import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, useNavigate, useParams, useLocation, Navigate, Link } from 'react-router-dom';
import './styles/afikim-ui.css';
import './styles/premium-editors.css';
import './styles/global.css';
import LoginPage from './components/LoginPage';
import PreloadScreen from './components/PreloadScreen';
// FileAttachmentsSidebar component is defined inline for sidebar display
import CustomerFormView from './components/CustomerFormView';
import { recordService } from './services/recordService';
// preloadService removed - using on-demand API loading
import { buildObjectListUrl, buildRecordUrl, buildSetupUrl } from './routes';
import config from './config/config';
import { LAB_OBJECT_CONFIG, normalizeObjectName } from './config/labObjectLabels';
import { LAB_REPAIR_RECORD_KEYS } from './config/labObjectLabels';
import { APP_LAUNCHER_PINNED_ITEMS } from './config/appLauncherItems';
import { reconcileInlineFilter } from './utils/inlineFilter';
import { summariseActivities } from './utils/activityView';
import SaAssignmentBanner from './components/records/SaAssignmentBanner';
import { isStrictIdentified, checkAccountGate } from './utils/accountCoords';
import { DiscountPendingBanner, DiscountLineChip, approvedUnitPrice, fetchOpportunityDiscounts, mayKeepApprovedPrice } from './components/discountRequests/discountRequestShared';
import AssetLifecyclePanel from './components/records/AssetLifecyclePanel';
import BarakActivitiesList from './components/records/BarakActivitiesList';
import { deleteHeaders, authHeaders } from './utils/session';
import { RECORD_SCREEN_EDIT_HEADERS, NO_RESTRICTIONS, fetchRecordAccess, lockedFieldSet, recordAccessErrorMessage, canEditOnScreen, canDeleteRecords, useHiddenObjects, isObjectHidden } from './utils/recordAccess';

import { ADMIN_ONLY_HIDDEN_BUTTONS, ADMIN_ONLY_OBJECT_MESSAGE, isAdminOnlyObject } from './utils/adminOnlyObjects';
import { formatIsraelDateTime } from './utils/israelTime';
import { changedFields } from './utils/recordDiff';
import { fetchMarketerContext, fetchDealerBootstrap } from './utils/marketer';
import MixedPricebookBadge from './components/MixedPricebookBadge';
import { showsDeliverySchedule, DELIVERY_SCHEDULE_BUTTON_ID, DELIVERY_SCHEDULE_LABEL } from './utils/businessDelivery';
import {
  BUSINESS_DELIVERY_WORK_TYPE_NAME, isBusinessDeliveryWorkType, deliverableLines, buildCloseBody,
} from './utils/businessDelivery';
import {
  relatedRecordsCache as _relatedRecordsCache,
  RELATED_CACHE_TTL_MS as _RELATED_CACHE_TTL_MS,
  invalidateRelatedCacheOnDelete as _invalidateRelatedCacheOnDelete,
  invalidateRelatedCacheForParent as _invalidateRelatedCacheForParent,
} from './utils/relatedCache';
// PR-G of user/profile audit (2026-05-29): hardcoded admin / accounting
// profile checks across this file (13 sites) replaced with calls to
// the canonical helpers in utils/permissions.js. Renamed on import to
// avoid shadowing the local `const isAdmin = ...` declarations inside
// per-function closures, which we leave in place (they now compute via
// the helper and keep their existing local-variable shape).
import { homeDashboardFor } from './utils/homeDashboards';
import { isAdmin as permIsAdmin, isAccounting as permIsAccounting, hasProfile as permHasProfile, isInstitutional as permIsInstitutional, isDistributor as permIsDistributor, canCreateAssetOrContract as permCanCreateAssetOrContract } from './utils/permissions';
import { VerifyPaymentButton as PaymentVerifyButton } from './components/accounting/PaymentVerifyControls';
import { setSentryUser, clearSentryUser } from './utils/sentry';
import { afSocket } from './utils/socket';
import { DynamicComponentRenderer } from './dynamic-components/DynamicComponentRenderer';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
// Import InternalChat directly (not lazy) to ensure it loads
import InternalChat from './components/chat/InternalChat';
// Import Afikim AI Chat - Business AI Assistant
import AfkimAIChat from './components/chat/AfkimAIChat';
import RecordWhatsAppChat from './components/omnichannel/RecordWhatsAppChat';
import WhatsAppOptInModal from './components/omnichannel/WhatsAppOptInModal';
import LookupField from './components/shared/LookupField';
import UnifiedFloatingHub from './components/UnifiedFloatingHub';
import AddressAutocompleteWithMap from './components/shared/AddressAutocompleteWithMap';
import { CallPopupProvider } from './components/telephony/CallPopup';
import { HEBREW_FIELD_LABELS, getHebrewFieldLabel, getHebrewObjectLabel, getHebrewPicklistValue } from './config/hebrewLabels';
import { SoftphoneProvider, SoftphoneUI, IncomingCallPopup, CallHighlightIndicator, useSoftphone } from './components/telephony/WebRTCSoftphone';
import TelephonyQueueManager from './components/telephony/TelephonyQueueManager';
import ThemeSelectorPanel from './components/theme/ThemeSelectorPanel';
import WhatsAppQueueManager from './components/omnichannel/WhatsAppQueueManager';
import AgentStatusManager from './components/omnichannel/AgentStatusManager';

// 2026-05-19: chat temporarily disabled for the 90-user load test —
// InternalChat polls /api/chat/conversations, /api/chat/unread,
// /api/chat/new, /api/chat/screen-share/check, /api/chat/remote-control/events
// at multi-second intervals which dominates the slow_request signal and
// generates 503s during cache warmup. Flip to true to re-enable.
const CHAT_ENABLED = false;

// 2026-05-26: The Afikim AI server is not deployed in prod — there is no
// systemd service for it and nothing listens on localhost:9500. Every
// click on the floating "Afikim AI" button surfaces a 503 ("AI server not
// available") and floods backend-error.log with httpx.ConnectError
// tracebacks (~1.5 GB accumulated by 2026-05-26). Hide the entry point
// until the AI server is provisioned + healthchecked. Flip to true once
// `systemctl is-active afikim-ai` reports running.
const AI_ENABLED = false;

// Phase 3.3.4: lazy-load setup/admin pages + heavy modals (all route-gated or conditional)
const SalesProcessWizard = lazy(() => import('./components/modals/SalesProcessWizard'));
const ServiceProcessWizard = lazy(() => import('./components/modals/ServiceProcessWizard'));
const CommunicationCenter = lazy(() => import('./components/telephony/CommunicationCenter'));
const WorkspaceShell = lazy(() => import('./workspace').then(m => ({ default: m.WorkspaceShell })));
const TelephonyDashboard = lazy(() => import('./components/telephony/TelephonyDashboard'));
const OwnerDashboardApp = lazy(() => import('./components/ownerDashboard/OwnerDashboardApp'));
const WebCallRoom = lazy(() => import('./components/telephony/WebCallRoom'));
const WhatsAppConnect = lazy(() => import('./components/omnichannel/WhatsAppConnect'));
const WhatsAppBotBuilder = lazy(() => import('./components/omnichannel/WhatsAppBotBuilder'));
const WhatsAppTriggers = lazy(() => import('./components/omnichannel/WhatsAppTriggers'));
const WhatsAppBotManagement = lazy(() => import('./components/omnichannel/WhatsAppBotManagement'));
const TelephonySetup = lazy(() => import('./components/telephony/TelephonySetup'));
const OtpSettings = lazy(() => import('./components/setup/OtpSettings'));
const ReminderSettings = lazy(() => import('./components/setup/ReminderSettings'));
const EmailToCaseSettings = lazy(() => import('./components/setup/EmailToCaseSettings'));
const WhatsAppTemplateSettings = lazy(() => import('./components/setup/WhatsAppTemplateSettings'));
const LabChecklistSettings = lazy(() => import('./components/setup/LabChecklistSettings'));
const TaskDashboardSettings = lazy(() => import('./components/setup/TaskDashboardSettings'));

// Lazy load heavy components for better initial load performance
const LegacySystemBackupBrowser = lazy(() => import('./components/setup/LegacySystemBackupBrowser'));
const TechnicianScheduler = lazy(() => import('./components/scheduler/TechnicianScheduler'));
const TechnicianSchedulerQuickAction = lazy(() => import('./components/scheduler/TechnicianSchedulerQuickAction'));
const AreaPlanner = lazy(() => import('./components/planner/AreaPlanner'));
// Use the new LWC Runtime version with proper Apex adapter
const TechnicianCapacityPlanner = lazy(() => import('./dynamic-components/ComponentRenderer').then(mod => ({ default: mod.TechnicianCapacityPlanner })));
const AddressValidation = lazy(() => import('./components/dynamic/AddressValidation'));
const PurchaseOrderCreator = lazy(() => import('./components/dynamic/PurchaseOrderCreator'));
const ObjectManagerSetup = lazy(() => import('./components/setup/ObjectManager'));
const AccountModal = lazy(() => import('./components/modals/AccountModal'));
const CreateAssetModal = lazy(() => import('./components/modals/CreateAssetModal'));
const CreateServiceContractModal = lazy(() => import('./components/modals/CreateServiceContractModal'));
const CreateCaseModal = lazy(() => import('./components/modals/CreateCaseModal'));
// 2026-05-17: case-classification update — opened from Asset detail page.
const UpdateCaseClassificationModal = lazy(() => import('./components/modals/UpdateCaseClassificationModal'));
const RefundModal = lazy(() => import('./components/modals/RefundModal'));
const PaymentTransferModal = lazy(() => import('./components/modals/PaymentTransferModal'));
const ChangeOwnerModal = lazy(() => import('./components/modals/ChangeOwnerModal'));
const StrictIdentificationModal = lazy(() => import('./components/modals/StrictIdentificationModal'));
const AddProductRequiredModal = lazy(() => import('./components/modals/AddProductRequiredModal'));
const OpportunityModal = lazy(() => import('./components/modals/OpportunityModal'));
const OpportunityPath = lazy(() => import('./components/modals/OpportunityModal').then(mod => ({ default: mod.OpportunityPath })));
const ProductsModal = lazy(() => import('./components/modals/ProductsModal'));
const PaymentModal = lazy(() => import('./components/modals/PaymentModal'));
const PriceQuoteModal = lazy(() => import('./components/modals/PriceQuoteModal'));
const UserManagement = lazy(() => import('./components/setup/UserManagement'));
const ServerManagement = lazy(() => import('./components/setup/ServerManagement'));
const ScriptManager = lazy(() => import('./components/setup/ScriptManager'));
const LayoutEditor = lazy(() => import('./components/setup/LayoutEditor'));
const AppLauncherEditor = lazy(() => import('./components/setup/AppLauncherEditor'));
const RecordAccessEditor = lazy(() => import('./components/setup/RecordAccessEditor'));
const ValidationExemptionsEditor = lazy(() => import('./components/setup/ValidationExemptionsEditor'));
const NavMenuEditor = lazy(() => import('./components/setup/NavMenuEditor'));
const SearchIndexEditor = lazy(() => import('./components/setup/SearchIndexEditor'));
// WP+WC integration PR #1 (2026-06-16) — admin UI for issuing the
// API keys external systems use to call /api/external/*.
const ExternalApiKeys = lazy(() => import('./components/setup/ExternalApiKeys'));
const SalesApiUsers = lazy(() => import('./components/setup/SalesApiUsers'));
const FeatureFlagsAdmin = lazy(() => import('./components/setup/FeatureFlagsAdmin'));
const WorkspaceSettings = lazy(() => import('./components/setup/WorkspaceSettings'));
const LoginPageDesigner = lazy(() => import('./components/setup/LoginPageDesigner'));
// 2026-10-06 — the מוקד sale / אולימפיק rules (admin only)
const MokedSaleRules = lazy(() => import('./components/setup/MokedSaleRules'));
const SalesRules = lazy(() => import('./components/setup/SalesRules'));
const VisitVoiceRetryHours = lazy(() => import('./components/setup/VisitVoiceRetryHours'));
const LabReturnAutoSchedule = lazy(() => import('./components/setup/LabReturnAutoSchedule'));
const MarketerCreditLimits = lazy(() => import('./components/setup/MarketerCreditLimits'));
const DiscountManagersSettings = lazy(() => import('./components/setup/DiscountManagersSettings'));
// WP+WC integration PR #3 — admin UI for the WC product → internal
// Product/Price Definition mapping table.
const WcProductMapping = lazy(() => import('./components/setup/WcProductMapping'));
// 2026-10-18 — תזכורים: allowed channels by number prefix (kosher → calls only), admin only
const ReminderChannelRules = lazy(() => import('./components/setup/ReminderChannelRules'));
// 2026-10-18 — תזכורים: the follow-up rules (רצף תזכורות), admin only
const ReminderSequence = lazy(() => import('./components/setup/ReminderSequence'));
const FlowBuilder = lazy(() => import('./components/setup/FlowBuilder'));
const DeveloperConsole = lazy(() => import('./components/devConsole').then(mod => ({ default: mod.DeveloperConsole })));
const FormTemplateBuilder = lazy(() => import('./components/setup/FormTemplateBuilder'));
const DataLoadingConfig = lazy(() => import('./components/setup/DataLoadingConfig'));
const RecycleBin = lazy(() => import('./components/setup/RecycleBin'));
const DataExport = lazy(() => import('./components/setup/DataExport'));
const ProductManagement = lazy(() => import('./components/setup/ProductManagement'));
const ProductRequiredRules = lazy(() => import('./components/setup/ProductRequiredRules'));
const TechnicianCashGate = lazy(() => import('./components/setup/TechnicianCashGate'));
const PasswordResetTool = lazy(() => import('./components/setup/PasswordResetTool'));
const TechnicianSlotRules = lazy(() => import('./components/setup/TechnicianSlotRules'));
const DeviceReplacementAllocationDashboard = lazy(() => import('./components/setup/DeviceReplacementAllocationDashboard'));
const IntegrationPortal = lazy(() => import('./components/integration-portal/IntegrationPortal'));
const IFormsProductMappings = lazy(() => import('./components/setup/IFormsProductMappings'));
const LoadTestDashboard = lazy(() => import('./components/LoadTestDashboard'));
const LoadTestDayDashboard = lazy(() => import('./components/LoadTestDayDashboard'));
const ManagerDashboards = lazy(() => import('./components/ManagerDashboards'));
const WarrantyRenewalDashboard = lazy(() => import('./components/warranty/WarrantyRenewalDashboard'));
const BouncedChecksDashboard = lazy(() => import('./components/accounting/BouncedChecksDashboard'));
const WarrantyMyLeadsDashboard = lazy(() => import('./components/warranty/WarrantyMyLeadsDashboard'));
const EmailToCaseCenter = lazy(() => import('./components/EmailToCaseCenter'));
// 2026-05-20: institutional Closed Won splitter — lazy because most
// opps have ≤1 serialized device and never trigger this path.
const MultiAddressCloseStep = lazy(() => import('./components/MultiAddressCloseStep'));
// 2026-10-09 (dealer P2-B): the marketer close — "מסירה ללקוח משווק".
const BusinessDeliveryCloseStep = lazy(() => import('./components/BusinessDeliveryCloseStep'));
const BusinessDeliveryResult = lazy(() => import('./components/BusinessDeliveryResult'));
const DocumentSendDialog = lazy(() => import('./components/DocumentSendDialog'));
// 2026-05-20: institutional bulk-scheduler — opens off the SA detail
// actions row, wraps the existing TechnicianSelector to schedule every
// SA of the same Opp in sequence + emit a printable HTML report.
const InstitutionalScheduler = lazy(() => import('./components/InstitutionalScheduler'));
const LegacyDataImporter = lazy(() => import('./components/setup/LegacyDataImporter'));
const OmnichannelConsole = lazy(() => import('./components/omnichannel/OmnichannelConsole'));
const WhatsAppReports = lazy(() => import('./components/omnichannel/WhatsAppReports'));
const TechnicianApp = lazy(() => import('./components/technician/TechnicianApp'));
const AppSelector = lazy(() => import('./components/AppSelector'));
const FormPreviewModal = lazy(() => import('./components/modals/FormPreviewModal'));
const AppointmentTracking = lazy(() => import('./components/public/AppointmentTracking'));
const TechnicianSelector = lazy(() => import('./components/TechnicianSelector'));
const TerritoryManager = lazy(() => import('./components/TerritoryManager'));
const RouteOptimizer = lazy(() => import('./components/route/RouteOptimizer'));
const BulkTransfer = lazy(() => import('./components/transfer/BulkTransfer'));
// 2026-10-29: the leads list as Lead.im lays it out (components/leads/leadim); the old
// file-based LeadsPage is retired with the rest of the CSV module (L-9).
const LeadsPage = lazy(() => import('./components/leads/leadim/LeadimLeadsPage'));
const LeadsSettingsPage = lazy(() => import('./components/leads/leadim/LeadimSettingsPage'));
const LeadsCampaignsPage = lazy(() => import('./components/leads/leadim/LeadimCampaignsPage'));
const LeadsUsersPage = lazy(() => import('./components/leads/leadim/LeadimUsersPage'));
const LeadDetailPage = lazy(() => import('./components/leads/LeadDetailPage'));
const UsersPage = lazy(() => import('./components/leads/UsersPage'));
const LeadsDashboard = lazy(() => import('./components/leads/LeadsDashboard'));
const TaskManagerDashboard = lazy(() => import('./components/TaskManagerDashboard'));
const SalesTechnicianDashboard = lazy(() => import('./components/SalesTechnicianDashboard'));
const PersonalDashboard = lazy(() => import("./components/dashboard/PersonalDashboard"));
const TaskTransferModal = lazy(() => import("./components/modals/TaskTransferModal"));
// ניהול הרשאות רשומות (PR 5): an object this user may not see — the list and
// record routes show this instead (UI hiding only, owner decision D2).
const NoObjectAccess = () => (
  <div dir="rtl" style={{ padding: '80px 24px', textAlign: 'center' }}>
    <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
    <h2 style={{ margin: '0 0 8px' }}>אין הרשאה</h2>
    <p style={{ color: '#54698d', margin: 0 }}>אין לך הרשאה לצפות ברשומות מסוג זה.</p>
  </div>
);
// Mirrors services/task_edit_lock.py LOCKED_MESSAGE.
const TASK_LOCKED_MESSAGE = 'לא ניתן לערוך משימה קיימת. אפשר להעביר אותה או לסמן כהושלמה.';
const TaskCompleteModal = lazy(() => import("./components/modals/TaskCompleteModal"));
const TaskCategoryPage = lazy(() => import("./components/dashboard/TaskCategoryPage"));
const PhoneLeadsPage = lazy(() => import('./components/leads/PhoneLeadsPage'));
const TasksPage = lazy(() => import('./components/leads/leadim/LeadimTasksPage'));   // the יומן on PG (2026-10-29s); the xlsx TasksPage goes with L-9
const SupplierLogin = lazy(() => import('./components/leads/SupplierLogin'));
const SupplierPortal = lazy(() => import('./components/leads/SupplierPortal'));
// The Lab moved to its own origin (PR #1, PWA Foundation). Its components now
// live in lab-frontend/, so there is exactly one Lab UI rather than two that
// drift. What stays here is a redirect, because bookmarks, the admin tile and
// muscle memory all still point at /lab.
const LAB_APP_URL = process.env.REACT_APP_LAB_URL || 'https://lab.afikim-water.org';
const ReportBuilder = lazy(() => import('./components/reports/ReportBuilder'));
const TechInventoryReport = lazy(() => import('./components/reports/TechInventoryReport'));
const TechInventoryReportV2 = lazy(() => import('./components/reports/TechInventoryReportV2'));
const WarehousePickingReport = lazy(() => import('./components/reports/WarehousePickingReport'));
const WarehouseHome = lazy(() => import('./components/warehouse/WarehouseHome'));
const WarehouseTour = lazy(() => import('./components/warehouse/WarehouseTour'));
const WarehouseModulePages = lazy(() => import('./components/warehouse/WarehouseModulePages').then(module => ({
  default: () => null // Placeholder, individual pages are imported below
})));
const GoodsReceiptPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.GoodsReceiptPage })));
const InventorySetupPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.InventorySetupPage })));
const InventoryCountPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.InventoryCountPage })));
const PickingPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.PickingPage })));
const ZoneTransferPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.ZoneTransferPage })));
const VehicleTransferPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.VehicleTransferPage })));
const SKUMappingPage = lazy(() => import('./components/warehouse/WarehouseModulePages').then(m => ({ default: m.SKUMappingPage })));
const BarcodeManagement = lazy(() => import('./components/warehouse/BarcodeManagement'));
const BinManager = lazy(() => import('./components/inventory/BinManager'));
const InventoryPage = lazy(() => import('./components/inventory/InventoryPage'));
const WarehouseApp = lazy(() => import('./components/inventory/warehouse/WarehouseApp'));
const WarehouseLogin = lazy(() => import('./components/inventory/warehouse/WarehouseLogin'));
const MobileTransfer = lazy(() => import('./components/inventory/mobile/MobileTransfer'));
const MobilePutaway = lazy(() => import('./components/inventory/mobile/MobilePutaway'));
const CountManager = lazy(() => import('./components/inventory/CountManager'));
const ExceptionQueue = lazy(() => import('./components/inventory/ExceptionQueue'));
const PurchaseOrders = lazy(() => import('./components/inventory/po/PurchaseOrders'));
const ReceiptDesk = lazy(() => import('./components/inventory/receiving/ReceiptDesk'));
const InventoryHome = lazy(() => import('./components/inventory/status/InventoryHome'));
const AdjustmentDesk = lazy(() => import('./components/inventory/status/AdjustmentDesk'));
const ReorderRules = lazy(() => import('./components/inventory/status/ReorderRules'));
const DeviceBook = lazy(() => import('./components/inventory/status/DeviceBook'));
const LabExitBins = lazy(() => import('./components/inventory/status/LabExitBins'));
const ProductSubstitutes = lazy(() => import('./components/inventory/status/ProductSubstitutes'));
const ShredApprovals = lazy(() => import('./components/inventory/status/ShredApprovals'));
const ProcurementHome = lazy(() => import('./components/procurement/ProcurementHome'));
const AccountingHome = lazy(() => import('./components/accounting/AccountingHome'));
const TechnicianSalesReport = lazy(() => import('./components/accounting/TechnicianSalesReport'));
const TechActivitiesPage = lazy(() => import('./components/accounting/TechActivitiesPage'));
const MonthlyRevenueDashboard = lazy(() => import('./components/accounting/MonthlyRevenueDashboard'));
const PaymentsVerification = lazy(() => import('./components/accounting/PaymentsVerification'));
const CreditApprovalsReport = lazy(() => import('./components/creditApprovals/CreditApprovalsReport'));
const LeadAttributionApprovals = lazy(() => import('./components/leadAttribution/LeadAttributionApprovals'));
const DiscountRequestsReport = lazy(() => import('./components/discountRequests/DiscountRequestsReport'));
const CreditApprovalRequestModal = lazy(() => import('./components/creditApprovals/CreditApprovalRequestModal'));
const DiscountRequestModal = lazy(() => import('./components/discountRequests/DiscountRequestModal'));
// ERN direct debit (הוראת קבע) — the pilot's list screen (§9.3). Accounting + admin.
const ErnDirectDebitList = lazy(() => import('./components/accounting/ErnDirectDebitList'));
const SalesRepReport = lazy(() => import('./components/accounting/SalesRepReport'));
// 2026-10-07 — revenue by team (route only, not on any menu yet; admin + accounting server-side)
const TeamRevenueDashboard = lazy(() => import('./components/accounting/TeamRevenueDashboard'));
const ProductDashboard = lazy(() => import('./components/productDashboard/ProductDashboard'));
const FileVaultPage = lazy(() => import('./components/fileVault/FileVaultPage'));

// 2026-07-02 PR-F — System Maintenance / Notification Center. Admin
// dashboard for connected users + broadcasting blocking notifications.
// Mounted at `/admin/system-notifications`, gated by is_admin in AppLauncher.
const SystemMaintenanceDashboard = lazy(() =>
  import('./components/system-notifications/SystemMaintenanceDashboard'));
// The blocking modal HOST is a normal (non-lazy) import — it must be
// available immediately on app boot so a notification pushed to the
// user while lazy chunks are still downloading doesn't slip through.
// eslint-disable-next-line import/first
import SystemNotificationHost from './components/system-notifications/SystemNotificationHost';
const ResellerReport = lazy(() => import('./components/accounting/ResellerReport'));
const CreateDocumentModal = lazy(() => import('./components/accounting/AccountingModals').then(mod => ({ default: mod.CreateDocumentModal })));
const ReturnedCheckModal = lazy(() => import('./components/accounting/AccountingModals').then(mod => ({ default: mod.ReturnedCheckModal })));
const AfkimInspector = lazy(() => import('./components/admin/AfkimInspector'));
const DataLoader = lazy(() => import('./components/admin/DataLoader'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '200px',
    color: '#0176d3'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div className="modern-spinner" style={{ margin: '0 auto 10px' }} />
      <style>{`
        .modern-spinner {
          width: 40px;
          height: 40px;
          position: relative;
        }
        .modern-spinner::before,
        .modern-spinner::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid transparent;
        }
        .modern-spinner::before {
          border-top-color: #0176d3;
          border-right-color: #0176d3;
          animation: modern-spin 1s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        }
        .modern-spinner::after {
          border-bottom-color: rgba(1, 118, 211, 0.3);
          border-left-color: rgba(1, 118, 211, 0.3);
          animation: modern-spin 1s cubic-bezier(0.5, 0, 0.5, 1) infinite reverse;
          animation-delay: -0.4s;
        }
        @keyframes modern-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <span>טוען...</span>
    </div>
  </div>
);

// ========================================
// API Configuration
// ========================================
const API_BASE = config.api.baseUrl;

// ========================================
// Frontend cache for /related responses, keyed by `objectName/recordId`.
// The /related endpoint can take 6-10s on first hit (resolve_refs +
// EntityHistory CSV scan); the backend has its own TTL cache, so the
// second hit is fast — but the *first* hit on each record still
// blanks the UI. This cache lets revisits paint instantly while a
// fresh fetch updates in the background, and removes the need for a
// "מחפש רשומות קשורות..." spinner above the related lists.
// Module-level so it survives RecordDetail unmounts inside a session.
// ========================================
// The related-records cache moved to utils/relatedCache.js on 2026-08-30 so
// the modals that mutate related records can invalidate it. Same Map, same
// TTL, same delete-time patch — see that file for why it is module-level.

// UX Performance Initiative PR #A+ (owner-spec 2026-06-09): module-
// level map of in-flight /related promises keyed by `${api_name}/${id}`.
// RecordDetailRoute kicks off /related at T=0 in parallel with
// /records/{id}; RecordPage's useEffect picks up the in-flight promise
// instead of starting its own fetch. Saves ~300ms by overlapping the
// network calls and removes the 'cleared then refilled' flash.
const _pendingRelatedFetches = new Map();

// ========================================
// Search Results Page Component
// ========================================
const SearchResultsPage = ({ getObjectConfig, objects, navigateToRecord }) => {
  const hiddenObjects = useHiddenObjects();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showAllRecords, setShowAllRecords] = useState({}); // Track which groups show all records

  useEffect(() => {
    if (query.length >= 2) {
      performSearch();
    } else {
      setResults([]);
      setLoading(false);
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}&limit=500`);
      const data = await res.json();

      const groupedResults = [];
      for (const group of (data.results || [])) {
        const objName = group.object_name || group.objectName;
        if (isObjectHidden(hiddenObjects, objName)) continue;
        const config = getObjectConfig(objName);
        groupedResults.push({
          objectName: objName,
          label: config.heLabel || objName,
          icon: config.icon,
          color: config.color,
          records: group.records || [],
          matchesCount: group.matches_count || (group.records || []).length
        });
      }

      const OBJECT_PRIORITY = { Account: 1, Contact: 2, Opportunity: 3, Case: 4, ServiceAppointment: 5, WorkOrder: 6, Lead: 7, Asset: 8 };
      groupedResults.sort((a, b) => (OBJECT_PRIORITY[a.objectName] || 100) - (OBJECT_PRIORITY[b.objectName] || 100) || b.matchesCount - a.matchesCount);
      setResults(groupedResults);
      setTotalResults(data.total || groupedResults.reduce((s, g) => s + g.matchesCount, 0));

      // Expand all groups by default
      const expanded = {};
      groupedResults.forEach(g => expanded[g.objectName] = true);
      setExpandedGroups(expanded);
    } catch (err) {
      console.error('Search error:', err);
    }
    setLoading(false);
  };

  const toggleGroup = (objName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [objName]: !prev[objName]
    }));
  };

  const getDisplayName = (record) => {
    if (record._display_name && record._display_name.trim() && record._display_name !== 'nan') {
      return record._display_name;
    }
    if (record.Name && record.Name.trim()) return record.Name;
    if (record.FirstName || record.LastName) {
      const fullName = `${record.FirstName || ''} ${record.LastName || ''}`.trim();
      if (fullName) return fullName;
    }
    if (record.Subject && record.Subject.trim()) return record.Subject;
    if (record.CaseNumber) return record.CaseNumber;
    if (record.WorkOrderNumber) {
      const num = parseFloat(record.WorkOrderNumber);
      return isNaN(num) ? record.WorkOrderNumber : String(Math.round(num)).padStart(8, '0');
    }
    if (record.AppointmentNumber) return record.AppointmentNumber;
    return 'ללא שם';
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const p = String(phone);
    return /^[2-9]\d{7,8}$/.test(p) ? '0' + p : p;
  };

  const getSubtitle = (record) => {
    if (record.Email) return record.Email;
    if (record.Phone) return formatPhoneDisplay(record.Phone);
    if (record.PersonMobilePhone) return formatPhoneDisplay(record.PersonMobilePhone);
    if (record.Customer_Number__c) return record.Customer_Number__c;
    if (record.City) return record.City;
    if (record.Status) return record.Status;
    return '';
  };

  return (
    <div className="search-results-page">
      <div className="search-results-header">
        <h1>תוצאות חיפוש עבור "{query}"</h1>
        <p className="search-results-count">{totalResults} תוצאות נמצאו</p>
      </div>

      {loading ? (
        <div className="search-results-loading">
          <div className="modern-spinner" style={{ margin: '40px auto' }} />
          <p>מחפש...</p>
        </div>
      ) : results.length === 0 ? (
        <div className="search-results-empty">
          <div className="empty-icon">🔍</div>
          <h2>לא נמצאו תוצאות</h2>
          <p>נסה לחפש עם מילים אחרות או בדוק את האיות</p>
        </div>
      ) : (
        <div className="search-results-content">
          {results.map((group) => (
            <div key={group.objectName} className="search-results-group">
              <div
                className="search-results-group-header"
                onClick={() => toggleGroup(group.objectName)}
              >
                <span className="group-icon" style={{ backgroundColor: group.color }}>
                  {group.icon}
                </span>
                <span className="group-name">{group.label}</span>
                <span className="group-count">{group.matchesCount} תוצאות</span>
                <span className="group-toggle">{expandedGroups[group.objectName] ? '▼' : '◀'}</span>
              </div>

              {expandedGroups[group.objectName] && (
                <div className="search-results-group-records">
                  <table className="slds-table slds-table_bordered">
                    <thead>
                      <tr>
                        <th>שם</th>
                        <th>פרטים נוספים</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllRecords[group.objectName] ? group.records : group.records.slice(0, 5)).map((record, idx) => (
                        <tr key={record.Id || idx}>
                          <td>
                            <a
                              href={`/object/${group.objectName}/${record.Id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                const obj = objects.find(o => o.api_name === group.objectName);
                                navigateToRecord(record, obj);
                              }}
                              className="search-result-link"
                            >
                              {getDisplayName(record)}
                            </a>
                          </td>
                          <td className="search-result-subtitle">{getSubtitle(record)}</td>
                          <td>
                            <button
                              className="slds-button slds-button_neutral slds-button_small"
                              onClick={() => {
                                const obj = objects.find(o => o.api_name === group.objectName);
                                navigateToRecord(record, obj);
                              }}
                            >
                              פתח
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {group.records.length > 5 && !showAllRecords[group.objectName] && (
                    <div className="show-more-container">
                      <button
                        className="show-more-button"
                        onClick={() => setShowAllRecords(prev => ({ ...prev, [group.objectName]: true }))}
                      >
                        הצג עוד {group.records.length - 5} תוצאות
                      </button>
                    </div>
                  )}
                  {showAllRecords[group.objectName] && group.records.length > 5 && (
                    <div className="show-more-container">
                      <button
                        className="show-more-button"
                        onClick={() => setShowAllRecords(prev => ({ ...prev, [group.objectName]: false }))}
                      >
                        הצג פחות
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .search-results-page {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .search-results-header {
          margin-bottom: 24px;
          border-bottom: 1px solid #e5e5e5;
          padding-bottom: 16px;
        }
        .search-results-header h1 {
          font-size: 24px;
          font-weight: 700;
          color: #080707;
          margin: 0 0 8px 0;
        }
        .search-results-count {
          color: #706e6b;
          font-size: 14px;
          margin: 0;
        }
        .search-results-loading {
          text-align: center;
          padding: 60px 20px;
          color: #706e6b;
        }
        .search-results-empty {
          text-align: center;
          padding: 60px 20px;
        }
        .search-results-empty .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }
        .search-results-empty h2 {
          font-size: 20px;
          color: #080707;
          margin: 0 0 8px 0;
        }
        .search-results-empty p {
          color: #706e6b;
          margin: 0;
        }
        .search-results-group {
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 4px;
          margin-bottom: 16px;
          overflow: hidden;
        }
        .search-results-group-header {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          background: #f3f3f3;
          cursor: pointer;
          gap: 12px;
        }
        .search-results-group-header:hover {
          background: #e5e5e5;
        }
        .group-icon {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: white;
        }
        .group-name {
          font-weight: 600;
          font-size: 16px;
          flex: 1;
        }
        .group-count {
          color: #706e6b;
          font-size: 14px;
        }
        .group-toggle {
          color: #706e6b;
          font-size: 12px;
        }
        .search-results-group-records {
          padding: 0;
        }
        .search-results-group-records table {
          margin: 0;
        }
        .search-result-link {
          color: #0176d3;
          text-decoration: none;
          font-weight: 500;
        }
        .search-result-link:hover {
          text-decoration: underline;
        }
        .search-result-subtitle {
          color: #706e6b;
          font-size: 13px;
        }
        .show-more-container {
          padding: 12px 16px;
          text-align: center;
          border-top: 1px solid #e5e5e5;
          background: #fafaf9;
        }
        .show-more-button {
          background: none;
          border: none;
          color: #0176d3;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 8px 16px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .show-more-button:hover {
          background: rgba(1, 118, 211, 0.1);
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

// ========================================
// Route Wrapper Components
// ========================================

// My Tasks Full Page - דף משימות מלא עם טאבים (משימות שלי / מעקב משימות)
const MyTasksFullPage = ({ currentUser, config, navigateToRecord, objects, navigate }) => {
  const [activeTab, setActiveTab] = useState('mine');
  const [myTasks, setMyTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const completedStatuses = ['Completed', 'סיום טיפול בשביעות רצון', 'סיום טיפול לא בשביעות רצון', 'הושלם'];
  const isCompleted = (s) => completedStatuses.includes(s);

  const statusLabel = (s) => {
    if (s === 'Not Started') return 'לא התחיל';
    if (s === 'In Progress') return 'בתהליך';
    if (s === 'Completed') return 'הושלם';
    if (s === 'Waiting on someone else') return 'ממתין';
    if (s === 'Deferred') return 'נדחה';
    return s || '';
  };

  useEffect(() => {
    if (!currentUser?.username) return;
    setLoading(true);
    Promise.all([
      fetch(`${config.api.baseUrl}/api/tasks/my?username=${encodeURIComponent(currentUser.username)}&status=all&limit=100`).then(r => r.ok ? r.json() : { tasks: [] }),
      fetch(`${config.api.baseUrl}/api/tasks/assigned-by-me?username=${encodeURIComponent(currentUser.username)}&limit=100`).then(r => r.ok ? r.json() : { tasks: [] })
    ]).then(([mine, assigned]) => {
      setMyTasks(mine.tasks || []);
      setAssignedTasks(assigned.tasks || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentUser?.username, config.api.baseUrl]);

  const sortTasks = (tasks) => [...tasks].sort((a, b) => {
    const ac = isCompleted(a.Status) ? 1 : 0;
    const bc = isCompleted(b.Status) ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return (b.ActivityDate || '').localeCompare(a.ActivityDate || '');
  });

  const filterTasks = (tasks) => {
    if (!searchTerm) return tasks;
    const term = searchTerm.toLowerCase();
    return tasks.filter(t =>
      (t.Subject || '').toLowerCase().includes(term) ||
      (t.What__Name || '').toLowerCase().includes(term) ||
      (t.Owner__Name || '').toLowerCase().includes(term)
    );
  };

  const currentTasks = activeTab === 'mine' ? myTasks : assignedTasks;
  const filtered = sortTasks(filterTasks(currentTasks));
  const taskObj = objects.find(o => o.api_name === 'Task');

  return (
    <div style={{ padding: '1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1b1b1b', margin: 0 }}>המשימות שלי</h1>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e5e5', marginBottom: '1rem' }}>
        <button onClick={() => setActiveTab('mine')} style={{
          padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
          background: activeTab === 'mine' ? '#fff' : '#f7f7f7',
          color: activeTab === 'mine' ? '#0176d3' : '#706e6b',
          borderBottom: activeTab === 'mine' ? '3px solid #0176d3' : '3px solid transparent',
          marginBottom: '-2px', borderRadius: '8px 8px 0 0'
        }}>
          משימות שלי
          <span style={{ marginRight: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: activeTab === 'mine' ? '#d4edfc' : '#e8e8e8' }}>
            {myTasks.filter(t => !isCompleted(t.Status)).length}
          </span>
        </button>
        <button onClick={() => setActiveTab('assigned')} style={{
          padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
          background: activeTab === 'assigned' ? '#fff' : '#f7f7f7',
          color: activeTab === 'assigned' ? '#0176d3' : '#706e6b',
          borderBottom: activeTab === 'assigned' ? '3px solid #0176d3' : '3px solid transparent',
          marginBottom: '-2px', borderRadius: '8px 8px 0 0'
        }}>
          מעקב משימות
          <span style={{ marginRight: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: activeTab === 'assigned' ? '#d4edfc' : '#e8e8e8' }}>
            {assignedTasks.filter(t => !isCompleted(t.Status)).length}
          </span>
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', fontSize: '13px', color: '#706e6b' }}>
          <span>פתוחות: <strong style={{ color: '#0176d3' }}>{currentTasks.filter(t => !isCompleted(t.Status)).length}</strong></span>
          <span>הושלמו: <strong style={{ color: '#2e844a' }}>{currentTasks.filter(t => isCompleted(t.Status)).length}</strong></span>
        </div>
        <input
          type="text"
          placeholder="חיפוש משימות..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid #c9c9c9', borderRadius: '6px', fontSize: '13px', width: '220px' }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#706e6b' }}>טוען...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#706e6b' }}>
          <span style={{ fontSize: '2.5rem' }}>✅</span>
          <p style={{ marginTop: '0.5rem' }}>{searchTerm ? 'לא נמצאו תוצאות' : 'אין משימות'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#f0f0f0', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e5e5' }}>
          {filtered.map((task, idx) => {
            const done = isCompleted(task.Status);
            const overdue = !done && task.ActivityDate && new Date(task.ActivityDate) < new Date();
            return (
              <div key={task.Id || idx} onClick={() => navigateToRecord(task, taskObj)} style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 18px',
                background: done ? '#fafcfa' : '#fff', cursor: 'pointer', opacity: done ? 0.7 : 1
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = done ? '#f0f7f0' : '#f3f7fb'}
              onMouseLeave={(e) => e.currentTarget.style.background = done ? '#fafcfa' : '#fff'}
              >
                <div style={{ paddingTop: '2px' }}>
                  {done ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', background: '#2e844a', borderRadius: '50%', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: '2px solid ' + (overdue ? '#ea001e' : '#b0adab'), borderRadius: '50%' }}></span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: done ? '#706e6b' : '#1b1b1b', textDecoration: done ? 'line-through' : 'none' }}>
                      {task.Subject || 'משימה'}
                    </div>
                    <span style={{
                      padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', flexShrink: 0,
                      background: done ? '#d4f7dc' : task.Status === 'In Progress' ? '#d4edfc' : task.Status === 'Not Started' ? '#fef3cd' : '#e8e8e8',
                      color: done ? '#2e844a' : task.Status === 'In Progress' ? '#0176d3' : task.Status === 'Not Started' ? '#9e6c00' : '#514f4d'
                    }}>{statusLabel(task.Status)}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '6px', fontSize: '12px', color: '#706e6b' }}>
                    {task.ActivityDate && (
                      <span style={{ color: overdue ? '#ea001e' : '#706e6b', fontWeight: overdue ? '600' : '400' }}>
                        תאריך: {new Date(task.ActivityDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                      </span>
                    )}
                    {task.Priority === 'High' && (
                      <span style={{ padding: '1px 8px', borderRadius: '3px', background: '#fde8e8', color: '#ea001e', fontWeight: '600', fontSize: '11px' }}>גבוהה</span>
                    )}
                    {activeTab === 'assigned' && task.Owner__Name && (
                      <span style={{ color: '#0176d3', fontWeight: '500' }}>מוקצה ל: {task.Owner__Name}</span>
                    )}
                    {activeTab === 'mine' && task.Owner__Name && (
                      <span>מוקצה: {task.Owner__Name}</span>
                    )}
                  </div>
                  {task.What__Name && (
                    <div style={{ fontSize: '12px', color: '#0176d3', marginTop: '4px' }}>{task.What__Name}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Object List Route - קורא את שם האובייקט מה-URL וטוען את הנתונים
const ObjectListRoute = ({
  objects,
  selectedObject,
  setSelectedObject,
  records,
  loading,
  loadRecords,
  navigateToRecord,
  getObjectConfig,
  getFieldLabel,
  currentListView,
  setCurrentListView,
  showListViewMenu,
  setShowListViewMenu,
  initialLoadComplete,
  currentUser,
  setRecords,
  setAllRecordsCache,
  addToCache,
  loadMoreRecords,
  recordsLoadingMore,
  recordsHasMore,
  recordsTotalCount
}) => {
  const { objectName } = useParams();
  const routeLocation = useLocation();
  const navigateForRedirect = useNavigate();

  // Owner-spec 2026-06-17: when the user clicks "משימות" in the nav,
  // route them to the Tasks dashboard (TaskCategoryPage) instead of
  // the generic list view. Same intent as the dashboard tile click —
  // owner explicitly compared the UX to "כשלוחצים על הדשבורד".
  // The deep-link /object/Task/:recordId path (single-record view) is
  // unchanged because this hook only fires on the listing route.
  //
  // 2026-06-21 PR follow-up to #1070: respect the user's pinned default
  // view from localStorage. Without this, clicking משימות in the nav
  // always took the user to tasks_mine_open regardless of what they
  // had pinned — owner-reported "אני קובע כברירת מחדל אבל אם אני יוצא
  // ונכנס הוא חוזר להמשימות הפתוחות שלי". Same storage key as
  // TaskCategoryPage:PINNED_VIEW_STORAGE_KEY.
  useEffect(() => {
    if (objectName === 'Task' && !routeLocation.search.includes('filter=')) {
      let pinned = '';
      try {
        pinned = localStorage.getItem('afikim:tasks_dashboard:pinned_view') || '';
      } catch (_) { /* private mode */ }
      const target = pinned || 'tasks_mine_open';
      navigateForRedirect(`/dashboard/tasks/${target}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName]);

  // Find the object from the URL parameter
  const currentObject = objects.find(o => o.api_name === objectName);

  // 2026-05-29: parent filter from ?filter=<field>:<value> — the
  // "📊 הצג בטבלה" button on related lists builds this URL so the
  // table view can be scoped to a specific parent record.
  const parentFilter = useMemo(() => {
    const raw = new URLSearchParams(routeLocation.search).get('filter');
    if (!raw || !raw.includes(':')) return null;
    const idx = raw.indexOf(':');
    return { field: raw.slice(0, idx), value: raw.slice(idx + 1) };
  }, [routeLocation.search]);
  const hiddenObjects = useHiddenObjects();

  useEffect(() => {
    if (currentObject) {
      // Always re-fetch when the URL filter changes (or when navigating
      // to a different object) — the cache key in loadRecords is just
      // objectName, so we have to force a fresh scoped fetch here.
      if (!selectedObject || selectedObject.api_name !== objectName) {
        setSelectedObject(currentObject);
      }
      loadRecords(objectName, null, !!parentFilter, parentFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, parentFilter?.field, parentFilter?.value]);

  if (isObjectHidden(hiddenObjects, objectName)) {
    return <NoObjectAccess />;
  }

  if (!currentObject) {
    return <div className="slds-loading">אובייקט לא נמצא...</div>;
  }

  if (!selectedObject || selectedObject.api_name !== objectName) {
    return <div className="slds-loading">טוען...</div>;
  }

  return (
    <ListView
      object={selectedObject}
      records={records}
      loading={loading}
      onRecordClick={navigateToRecord}
      onRefresh={() => loadRecords(selectedObject.api_name, null, true, parentFilter)}
      onSearch={(searchTerm) => loadRecords(selectedObject.api_name, searchTerm, false, parentFilter)}
      parentFilter={parentFilter}
      getObjectConfig={getObjectConfig}
      getFieldLabel={getFieldLabel}
      currentListView={currentListView}
      setCurrentListView={setCurrentListView}
      showListViewMenu={showListViewMenu}
      setShowListViewMenu={setShowListViewMenu}
      objects={objects}
      initialLoadComplete={initialLoadComplete}
      currentUser={currentUser}
      setRecords={setRecords}
      setAllRecordsCache={setAllRecordsCache}
      addToCache={addToCache}
      loadMoreRecords={loadMoreRecords}
      recordsLoadingMore={recordsLoadingMore}
      recordsHasMore={recordsHasMore}
      recordsTotalCount={recordsTotalCount}
    />
  );
};

// Record Detail Route - קורא את שם האובייקט ומזהה הרשומה מה-URL
const RecordDetailRoute = ({
  objects,
  selectedObject,
  setSelectedObject,
  selectedRecord,
  setSelectedRecord,
  getFromCache,
  addToCache,
  loadRecords,
  getObjectConfig,
  getFieldLabel,
  navigateToRecord,
  navigate,
  flexipageCache,
  setFlexipageCache,
  currentUser
}) => {
  const { objectName, recordId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Track which record we've loaded to avoid re-loading the same record
  const [loadedRecordKey, setLoadedRecordKey] = useState(null);

  // Create a unique key for the current route
  const currentRecordKey = `${objectName}/${recordId}`;
  const hiddenObjects = useHiddenObjects(currentUser && (currentUser.id || currentUser.username));

  useEffect(() => {
    const loadRecordData = async () => {
      console.log('[RecordDetailRoute] loadRecordData called', { objectName, recordId, objectsLen: objects.length, loadedRecordKey, currentRecordKey });

      // Wait for objects to load first
      if (objects.length === 0) {
        console.log('[RecordDetailRoute] Waiting for objects to load...');
        return;
      }

      // Already loaded this EXACT record
      if (loadedRecordKey === currentRecordKey && selectedRecord?.Id === recordId) {
        console.log('[RecordDetailRoute] Already loaded this record');
        return;
      }

      if (!objectName || !recordId) {
        setLoadError('חסרים פרמטרים');
        return;
      }

      // Tolerate brief race conditions where the URL points at an object
      // we haven't seen in the metadata list yet (Task in particular goes
      // through this path from the dashboard's clickable links). The
      // record-detail flow only needs api_name, so falling back to a
      // minimal stub keeps navigation working — recordService fetches by
      // object name regardless of whether the metadata entry has loaded.
      let obj = objects.find(o => o.api_name === objectName || o.api_name.toLowerCase() === objectName.toLowerCase());
      if (!obj) {
        console.warn(`[RecordDetailRoute] Object ${objectName} not in objects list — using stub`);
        obj = { api_name: objectName, label: objectName, has_data: true };
      }

      // Reset error state
      setLoadError(null);

      // Set object
      setSelectedObject(obj);

      // Check cache first
      const cachedRecord = getFromCache(objectName, recordId);
      if (cachedRecord) {
        console.log('[RecordDetailRoute] Found in cache:', cachedRecord.Id);
        setSelectedRecord(cachedRecord);
        setLoadedRecordKey(currentRecordKey);
        return;
      }

      // UX Performance Initiative — P0+ (owner-spec 2026-06-09):
      // Progressive record loading with parallel /related kick-off.
      //
      //   T=0   fire /records/{id}   ─┐
      //   T=0   fire /related        ─┤  (saved into _pendingRelatedFetches
      //                                so RecordPage's useEffect picks it
      //                                up instead of firing its own)
      //   T=200 setSelectedRecord    ┘  → RecordPage mounts; its useEffect
      //                                 sees the in-flight /related promise
      //                                 and awaits it (no second fetch)
      //   T=1500 /related resolves       → setRelatedRecords paints lists
      //
      // P0 (PR #815) made the header appear at T=300. P0+ further
      // overlaps /related so it finishes ~300ms earlier — and the
      // RecordPage no longer wipes the previous record's related
      // lists on cache miss (see useEffect change further down).
      console.log('[RecordDetailRoute] Loading record + related (parallel)…');
      setIsLoading(true);
      const cacheKey = `${objectName}/${recordId}`;
      // Kick off /related in parallel. Resolves silently into both
      // caches; failure is non-fatal (RecordPage will retry on its own).
      // Skip if a previous prefetch is already in flight for this key.
      if (!_pendingRelatedFetches.has(cacheKey)) {
        const relPromise = (async () => {
          try {
            const r = await fetch(`${API_BASE}/api/objects/${objectName}/records/${recordId}/related?limit=100&resolve_refs=true`);
            if (!r.ok) return null;
            const d = await r.json();
            const flat = {};
            const derivedCounts = {};
            for (const [objName, objData] of Object.entries(d.related_objects || {})) {
              const recs = (objData && objData.records) || [];
              flat[objName] = recs;
              // 2026-06-13 — /related doesn't emit a top-level `counts`
              // field (that's only on /related-counts). Each child carries
              // a `count` of its own; derive the map from those so the
              // cache-HIT branch in useEffect (line ~14797 + ~14831)
              // doesn't see `{}` → setRelatedCounts({}) → badges back to 0
              // for ~25s until /related-counts and loadAllRelatedRecords
              // overwrite (the symptom owner reported after PR #905).
              const beCount = (objData && typeof objData.count === 'number') ? objData.count : null;
              derivedCounts[objName] = beCount !== null ? beCount : recs.length;
            }
            _relatedRecordsCache.set(cacheKey, {
              data: flat,
              timestamp: Date.now(),
              counts: derivedCounts,
            });
            return flat;
          } catch (e) {
            console.warn('[RecordDetailRoute] /related parallel fetch failed:', e);
            return null;
          } finally {
            _pendingRelatedFetches.delete(cacheKey);
          }
        })();
        _pendingRelatedFetches.set(cacheKey, relPromise);
      }
      try {
        const res = await fetch(`${API_BASE}/api/objects/${objectName}/records/${recordId}`);
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'הרשומה לא נמצאה' : `שגיאת שרת: ${res.status}`);
        }
        const data = await res.json();
        const record = data?.record || data;  // server may wrap as {record:...} or return raw
        console.log('[RecordDetailRoute] Got record:', record?.Id);
        if (record && record.Id) {
          setSelectedRecord(record);
          addToCache(objectName, [record]);
          setLoadedRecordKey(currentRecordKey);
        } else {
          setLoadError('הרשומה לא נמצאה');
          setLoadedRecordKey(currentRecordKey); // Prevent retries
        }
      } catch (err) {
        console.error('[RecordDetailRoute] Failed to load record:', err);
        setLoadError(err.message || 'שגיאה בטעינת הרשומה');
        // Mark as loaded even on error to prevent infinite retries
        setLoadedRecordKey(currentRecordKey);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecordData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, recordId, objects.length, currentRecordKey, loadedRecordKey]);

  if (isObjectHidden(hiddenObjects, objectName)) {
    return <NoObjectAccess />;
  }

  // Show loading while objects are loading OR record is loading
  if (objects.length === 0) {
    return <div className="slds-loading">טוען אובייקטים...</div>;
  }

  if (isLoading) {
    return <div className="slds-loading">טוען רשומה...</div>;
  }

  if (loadError) {
    return <div className="slds-error" style={{padding: '20px', textAlign: 'center'}}>{loadError}</div>;
  }

  // Still waiting for data to load - check that the selected record matches the URL
  if (!selectedObject || !selectedRecord || selectedRecord.Id !== recordId) {
    return <div className="slds-loading">טוען נתונים...</div>;
  }

  return (
    <RecordPage
      object={selectedObject}
      record={selectedRecord}
      onBack={() => navigate(buildObjectListUrl(selectedObject.api_name))}
      getObjectConfig={getObjectConfig}
      getFieldLabel={getFieldLabel}
      onNavigateToRecord={navigateToRecord}
      objects={objects}
      addToCache={addToCache}
      flexipageCache={flexipageCache}
      setFlexipageCache={setFlexipageCache}
      currentUser={currentUser}
      onRecordUpdate={(updated) => {
        setSelectedRecord(updated);
        addToCache(selectedObject.api_name, [updated]);
      }}
      onRecordDelete={() => {
        loadRecords(selectedObject.api_name);
        navigate(buildObjectListUrl(selectedObject.api_name));
      }}
      onViewRelatedList={(targetObj, parentFieldName, parentId) => {
        // Navigate to list view of related object with filter
        setSelectedObject(targetObj);
        setSelectedRecord(null);
        // Navigate to the list view with a filter query parameter
        const filterParam = parentFieldName && parentId ? `?filter=${parentFieldName}:${parentId}` : '';
        navigate(buildObjectListUrl(targetObj.api_name) + filterParam);
      }}
    />
  );
};

// Setup Section Route - קורא את ה-section מה-URL
const SetupSectionRoute = ({
  setSetupSection,
  loadScriptClasses,
  loadFlows,
  objects,
  scriptClasses,
  flows,
  metadata,
  stats,
  navigateToObject,
  loadScriptClass,
  loadFlow,
  selectedScript,
  selectedFlow,
  getObjectConfig,
  navigate,
  setFlexipageCache,
  currentUser
}) => {
  const { section } = useParams();

  useEffect(() => {
    if (section) {
      setSetupSection(section);
      if (section === 'apex') loadScriptClasses();
      if (section === 'flows') loadFlows();
    }
  }, [section, setSetupSection, loadScriptClasses, loadFlows]);

  return (
    <SetupPage
      section={section || 'home'}
      setSection={(newSection) => {
        setSetupSection(newSection);
        if (newSection && newSection !== 'home') {
          navigate(buildSetupUrl(newSection));
        } else {
          navigate('/setup');
        }
      }}
      objects={objects}
      scriptClasses={scriptClasses}
      flows={flows}
      metadata={metadata}
      stats={stats}
      onObjectClick={navigateToObject}
      onScriptClick={loadScriptClass}
      onFlowClick={loadFlow}
      selectedScript={selectedScript}
      selectedFlow={selectedFlow}
      getObjectConfig={getObjectConfig}
      loadScriptClasses={loadScriptClasses}
      loadFlows={loadFlows}
      setFlexipageCache={setFlexipageCache}
      currentUser={currentUser}
    />
  );
};

// Setup Object Route - מטפל בניתוב לאובייקט ספציפי עם טאב
const SetupObjectRoute = ({ objects, setFlexipageCache }) => {
  const { objectName, tab } = useParams();
  const navigate = useNavigate();

  // Find the object from the list
  const object = objects.find(o => o.api_name === objectName);

  if (!object) {
    return (
      <div className="slds-setup">
        <div className="slds-setup__sidebar">
          <div className="slds-p-around_medium">
            <button className="slds-button slds-button_neutral" onClick={() => navigate('/setup/objects')}>
              ← חזרה לרשימת אובייקטים
            </button>
          </div>
        </div>
        <div className="slds-setup__content slds-p-around_large">
          <div className="slds-notify slds-notify_alert" role="alert">
            <h2>האובייקט {objectName} לא נמצא</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ObjectManagerSetup
        onBack={() => navigate('/setup/objects')}
        initialObject={object}
        initialTab={tab || 'details'}
        onTabChange={(newTab) => {
          navigate(buildSetupUrl('objects', objectName, newTab), { replace: true });
        }}
        onFlexipageCacheInvalidate={(objName) => {
          setFlexipageCache(prev => {
            const newCache = { ...prev };
            delete newCache[objName];
            return newCache;
          });
        }}
      />
    </Suspense>
  );
};

// ========================================
// LWC Component Renderer - renders custom components by name
// Supports both native React components and dynamic LWC loading via iframe
// ========================================
const DynamicComponentWrapper = ({ componentName, recordId, objectName, props = {}, targetObjectName = null, isRecordSpecific = true }) => {
  // State for resolved recordId (in case we need to fetch related record)
  const [resolvedRecordId, setResolvedRecordId] = useState(recordId);
  const [resolvedObjectName, setResolvedObjectName] = useState(objectName);
  const [loading, setLoading] = useState(false);

  // Check if we need to resolve a related record from a different object
  useEffect(() => {
    const resolveRecordId = async () => {
      // If component is not record-specific, don't resolve anything
      if (!isRecordSpecific) {
        setResolvedRecordId(null);
        setResolvedObjectName(objectName);
        setLoading(false);
        return;
      }

      // If we have a target object that's different from current object
      const targetObj = targetObjectName || objectName;

      // Check if we need to find a related record of different type
      if (targetObj && targetObj !== objectName && recordId) {
        setLoading(true);
        try {
          // Try to find related record of target type
          const response = await fetch(`${config.api.baseUrl}/api/objects/${objectName}/records/${recordId}/related?limit=100&resolve_refs=true`);
          if (response.ok) {
            const data = await response.json();
            const relatedRecords = data.related_objects?.[targetObj]?.records || [];

            if (relatedRecords.length > 0) {
              // Use the first (or most recent) related record
              const latestRecord = relatedRecords.sort((a, b) =>
                new Date(b.CreatedDate || 0) - new Date(a.CreatedDate || 0)
              )[0];
              console.log(`Found related ${targetObj}:`, latestRecord.Id);
              setResolvedRecordId(latestRecord.Id);
              setResolvedObjectName(targetObj);
            } else {
              console.warn(`No ${targetObj} found for`, objectName, recordId);
              // Keep original recordId - the component will handle the error
              setResolvedRecordId(recordId);
              setResolvedObjectName(objectName);
            }
          }
        } catch (error) {
          console.error(`Error resolving ${targetObj}:`, error);
          setResolvedRecordId(recordId);
          setResolvedObjectName(objectName);
        }
        setLoading(false);
      } else {
        // Use provided recordId directly
        setResolvedRecordId(recordId);
        setResolvedObjectName(targetObj || objectName);
      }
    };

    resolveRecordId();
  }, [componentName, recordId, objectName, targetObjectName, isRecordSpecific]);

  // Map of React implementations for specific LWC components
  // Components not in this map will be rendered via iframe from backend
  // Components with native React implementation (complex schedulers that need full React)
  // Other LWC components use iframe rendering with original legacy CRM styling
  const nativeComponentMap = {
    'technicianScheduler': TechnicianScheduler,
    'TechnicianScheduler': TechnicianScheduler,
    'technicianSchedulerQuickActionV4': TechnicianSchedulerQuickAction,
    'TechnicianSchedulerQuickActionV4': TechnicianSchedulerQuickAction,
    'areaPlanner': AreaPlanner,
    'AreaPlanner': AreaPlanner,
    'omnichannelConsole': OmnichannelConsole,
    'OmnichannelConsole': OmnichannelConsole,
    'technicianCapacityPlanner': TechnicianCapacityPlanner,
    'TechnicianCapacityPlanner': TechnicianCapacityPlanner,
    'addressValidation': AddressValidation,
    'AddressValidation': AddressValidation,
    'purchaseOrderCreator': PurchaseOrderCreator,
    'PurchaseOrderCreator': PurchaseOrderCreator,
  };

  const NativeComponent = nativeComponentMap[componentName];

  // Show loading while resolving recordId
  if (loading) {
    return null;
  }

  // If we have a native React implementation, use it
  if (NativeComponent) {
    return (
      <Suspense fallback={null}>
        <NativeComponent
          recordId={resolvedRecordId}
          objectName={resolvedObjectName}
          {...props}
        />
      </Suspense>
    );
  }

  // For all other dynamic components, render via DynamicComponentRenderer from dynamic-components
  // This handles loading component files and rendering with React
  return (
    <DynamicComponentRenderer
      componentName={componentName}
      recordId={resolvedRecordId}
      objectName={resolvedObjectName}
      {...props}
    />
  );
};

// ========================================
// Minimized Wizard Bubbles - קומפוננטות נפרדות לבועות ממוזערות
// ========================================
const MinimizedSalesWizardBubble = ({ wizardId, bubbleOffset, onRestore, onClose }) => {
  // קרא את הנתונים מ-localStorage
  const storageKey = `salesWizardData_${wizardId}`;
  const savedData = localStorage.getItem(storageKey);
  let customerName = 'לקוח חדש';
  let currentStep = 'account';

  if (savedData) {
    try {
      const data = JSON.parse(savedData);
      if (data.accountData?.FirstName || data.accountData?.LastName) {
        customerName = `${data.accountData.FirstName || ''} ${data.accountData.LastName || ''}`.trim();
      } else if (data.accountData?.Name) {
        customerName = data.accountData.Name;
      }
      currentStep = data.currentStep || 'account';
    } catch (e) {}
  }

  const stepConfig = {
    account: { icon: '👤', color: '#1976d2', waitingText: 'בחירת לקוח' },
    products: { icon: '📦', color: '#9c27b0', waitingText: 'בחירת מוצרים' },
    payment: { icon: '💳', color: '#2e7d32', waitingText: 'תשלום' },
    summary: { icon: '✅', color: '#4caf50', waitingText: 'סיכום' }
  };

  const config = stepConfig[currentStep] || stepConfig.account;

  return createPortal(
    <div
      className="sales-wizard-bubble"
      style={{
        position: 'fixed',
        bottom: `${20 + bubbleOffset}px`,
        left: '20px',
        minWidth: '140px',
        height: '80px',
        borderRadius: '20px',
        backgroundColor: config.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        zIndex: 10500,
        transition: 'all 0.3s',
        padding: '10px 16px',
        border: '2px solid rgba(255,255,255,0.3)'
      }}
      onClick={onRestore}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute',
          top: '-8px',
          right: '-8px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          border: '2px solid white',
          backgroundColor: '#dc3545',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
        }}
        title="סגור תהליך"
      >×</button>
      <div style={{ textAlign: 'center', color: 'white' }}>
        <div style={{ fontSize: '24px', marginBottom: '2px' }}>{config.icon}</div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customerName}
        </div>
        <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.9 }}>{config.waitingText}</div>
      </div>
    </div>,
    document.body
  );
};

const MinimizedServiceWizardBubble = ({ wizardId, bubbleOffset, onRestore, onClose }) => {
  const storageKey = `serviceWizardData_${wizardId}`;
  const savedData = localStorage.getItem(storageKey);
  let customerName = 'תהליך שירות';
  let currentStep = 'account';

  if (savedData) {
    try {
      const data = JSON.parse(savedData);
      if (data.accountData?.FirstName || data.accountData?.LastName) {
        customerName = `${data.accountData.FirstName || ''} ${data.accountData.LastName || ''}`.trim();
      } else if (data.accountData?.Name) {
        customerName = data.accountData.Name;
      }
      currentStep = data.currentStep || 'account';
    } catch (e) {}
  }

  const stepConfig = {
    account: { icon: '👤', waitingText: 'בחירת לקוח' },
    asset: { icon: '📦', waitingText: 'בחירת נכס' },
    warranty: { icon: '📋', waitingText: 'בדיקת אחריות' },
    history: { icon: '📜', waitingText: 'היסטוריה' },
    case: { icon: '🔧', waitingText: 'יצירת מקרה' },
    technician: { icon: '👷', waitingText: 'תיאום טכנאי' },
    summary: { icon: '✅', waitingText: 'סיכום' }
  };

  const config = stepConfig[currentStep] || stepConfig.account;

  return createPortal(
    <div
      className="service-wizard-bubble"
      style={{
        position: 'fixed',
        bottom: `${20 + bubbleOffset}px`,
        left: '20px',
        minWidth: '140px',
        height: '80px',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(156, 39, 176, 0.4)',
        zIndex: 10500,
        transition: 'all 0.3s',
        padding: '10px 16px',
        border: '2px solid rgba(255,255,255,0.3)'
      }}
      onClick={onRestore}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute',
          top: '-8px',
          right: '-8px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          border: '2px solid white',
          backgroundColor: '#dc3545',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
        }}
        title="סגור תהליך"
      >×</button>
      <div style={{ textAlign: 'center', color: 'white' }}>
        <div style={{ fontSize: '24px', marginBottom: '2px' }}>{config.icon}</div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customerName}
        </div>
        <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.9 }}>{config.waitingText}</div>
      </div>
    </div>,
    document.body
  );
};

// ========================================
// Main App
// ========================================
function App() {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-11-02 (owner: WhatsApp only): /workspace is a standalone screen — none of
  // the CRM chrome (header, tabs, search, softphone, agent status, hub, themes) is
  // mounted under it, and the CRM's background pollers below stand down there.
  const isWorkspaceRoute = location.pathname === '/workspace'
    || location.pathname.startsWith('/workspace/');

  // Authentication state - check for existing session immediately to prevent login flash
  const hasStoredSession = !!(localStorage.getItem('session_token') || sessionStorage.getItem('session_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(hasStoredSession);
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem('salesforce_user') || sessionStorage.getItem('salesforce_user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // Initialize recordService with stored user on app load
        recordService.setCurrentUser(parsedUser);
        return parsedUser;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [sessionValidated, setSessionValidated] = useState(false);

  // 2026-05-18: Password-change state. Originally a soft banner; owner
  // reported users were just dismissing it and never changing their
  // initial passwords. 2026-05-19 made BLOCKING: when the session says
  // mustChangePassword=true the modal auto-opens and cannot be closed
  // until the user actually changes the password. The legacy banner is
  // kept as a fallback render path for the rare case the modal is
  // bypassed (e.g. an inline edit cleared the flag while modal closed).
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [pwdBannerDismissed, setPwdBannerDismissed] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpSubmitting, setCpSubmitting] = useState(false);
  const [cpError, setCpError] = useState('');

  // 2026-05-19: BLOCKING force-password-change. As soon as we know the
  // current user has mustChangePassword=true (fresh login OR session
  // restore from storage), open the change-password modal and clear
  // any "dismissed" state so it can't be skipped. Closes only after
  // a successful change (the success branch flips mustChangePassword
  // to false in state + storage).
  useEffect(() => {
    if (currentUser?.mustChangePassword) {
      setShowChangePasswordModal(true);
      setPwdBannerDismissed(false);
      setCpCurrent('');
      setCpNew('');
      setCpConfirm('');
      setCpError('');
    }
  }, [currentUser?.mustChangePassword]);

  // Global authenticated fetch — adds token + handles 401/403
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    const headers = { ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!headers['Content-Type'] && options.body) headers['Content-Type'] = 'application/json';

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
      console.warn('[authFetch] 401 — session expired');
      // Don't force logout — the periodic refresh will try to re-login
    }
    return res;
  }, []);

  // Login-As banner state
  const [loginAsInfo, setLoginAsInfo] = useState(() => {
    try {
      const stored = localStorage.getItem('afikim_login_as');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.active) return parsed;
      }
    } catch (e) {}
    return null;
  });

  const handleLogoutAs = () => {
    // Restore original admin session
    const originalToken = localStorage.getItem('afikim_original_admin_token');
    const originalUser = localStorage.getItem('afikim_original_admin_user');
    if (originalToken) {
      localStorage.setItem('session_token', originalToken);
      if (originalUser) {
        localStorage.setItem('salesforce_user', originalUser);
      }
    }
    // Cleanup login-as data
    localStorage.removeItem('afikim_login_as');
    localStorage.removeItem('afikim_original_admin_token');
    localStorage.removeItem('afikim_original_admin_user');
    setLoginAsInfo(null);
    window.location.href = '/setup/users';
  };

  // PRELOAD STATE - טעינה ראשונית של כל הנתונים
  // Skip splash screen on refresh - show only on first login in session
  const isSessionReload = sessionStorage.getItem('afikim_preload_done') === '1';
  const [preloadComplete, setPreloadComplete] = useState(isSessionReload);
  const [preloadProgress, setPreloadProgress] = useState(isSessionReload ? 100 : 0);
  const [preloadMessage, setPreloadMessage] = useState('');
  const [preloadError, setPreloadError] = useState(null);
  const [preloadStats, setPreloadStats] = useState(null);

  const [objects, setObjects] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  // Infinite scroll state for the object list view: load 50 first, then
  // append 50 more each time the user scrolls near the bottom.
  const [recordsTotalCount, setRecordsTotalCount] = useState(0);
  const [recordsHasMore, setRecordsHasMore] = useState(false);
  const [recordsLoadingMore, setRecordsLoadingMore] = useState(false);

  // Global record cache - כל הרשומות נשמרות כאן לגישה מיידית
  const [recordCache, setRecordCache] = useState({});  // { objectName: { recordId: record } }
  const [allRecordsCache, setAllRecordsCache] = useState({}); // { objectName: [records] } - כל הרשומות לחיפוש
  const [flexipageCache, setFlexipageCache] = useState({}); // { objectName: record page } - Record Page cache for instant loading
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showAppLauncher, setShowAppLauncher] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [navMenuPosition, setNavMenuPosition] = useState({ top: 48, right: 200 });
  const navDropdownAnchorRef = React.useRef(null);
  const [showListViewMenu, setShowListViewMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  // Admin-only system status dot rendered next to the bell.
  // ready → green; building/initializing → orange; error/network → red.
  const [systemStatus, setSystemStatus] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [showCommunicationCenter, setShowCommunicationCenter] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showTelephonyDashboard, setShowTelephonyDashboard] = useState(false);
  const [telephonyDashboardInitialTab, setTelephonyDashboardInitialTab] = useState('dashboard');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [currentListView, setCurrentListView] = useState('הוצגו לאחרונה');
  const [tasks, setTasks] = useState([]);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksCount, setMyTasksCount] = useState(0);
  const [assignedByMeTasks, setAssignedByMeTasks] = useState([]);
  const [myTasksTab, setMyTasksTab] = useState('mine');
  // CRM Notification system (wallet transfers, discount requests)
  const [crmNotifications, setCrmNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showManagerWallet, setShowManagerWallet] = useState(false);
  const [showAccountingWallet, setShowAccountingWallet] = useState(false);
  const [notifTab, setNotifTab] = useState('all');
  const [transferModalLoading, setTransferModalLoading] = useState(false);
  const [discountModalLoading, setDiscountModalLoading] = useState(false);
  // P0.9 Task 6 — manager-editable approved price. Initialized from
  // notifData.requestedPrice when the modal opens (see effect below). Allowed
  // range [0, originalPrice]; UI blocks submit when out of range.
  const [approvedPriceInput, setApprovedPriceInput] = useState('');
  const [walletActionReason, setWalletActionReason] = useState('');
  const [walletEditAmount, setWalletEditAmount] = useState('');
  const [walletEditNotes, setWalletEditNotes] = useState('');
  const [showWalletEditForm, setShowWalletEditForm] = useState(false);
  const [showWalletRejectForm, setShowWalletRejectForm] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);
  const [managerWalletData, setManagerWalletData] = useState(null);
  const [accountingWalletData, setAccountingWalletData] = useState(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  // ניהול הרשאות רשומות (PR 5): objects hidden from this user — out of nav,
  // launcher, global search and related lists (UI only, D2).
  const hiddenObjects = useHiddenObjects(currentUser && (currentUser.id || currentUser.username));
  const [searchLoading, setSearchLoading] = useState(false);
  const globalSearchRef = React.useRef(null);

  // Recent searches (per-user, localStorage). Owner-spec 2026-05-31:
  // the "חיפושים אחרונים" section in the global-search dropdown was
  // empty — show each user their own most recent terms.
  const [recentSearches, setRecentSearches] = useState([]);
  const RECENT_SEARCHES_MAX = 8;

  // Setup state
  const [setupSection, setSetupSection] = useState(null);
  const [scriptClasses, setScriptClasses] = useState([]);
  const [flows, setFlows] = useState([]);
  const [selectedScript, setSelectedApex] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);

  // Context Menu (Right-click) state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, item: null, type: null });

  // Sales Process Wizards state - מערך לתמיכה במספר חלונות מכירה
  const [salesWizards, setSalesWizards] = useState(() => {
    const saved = localStorage.getItem('salesWizards');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    // תאימות לאחור - אם יש salesWizardOpen ישן
    const oldSaved = localStorage.getItem('salesWizardOpen');
    return oldSaved === 'true' ? [{ id: Date.now() }] : [];
  });

  // רשימת תהליכי מכירה (נשמרת בשרת)
  const [salesProcessesList, setSalesProcessesList] = useState([]);
  const [showSalesListModal, setShowSalesListModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [salesListFilter, setSalesListFilter] = useState({ date: '', status: '' });

  // Service Process Wizards state - מערך לתמיכה במספר חלונות שירות
  const [serviceWizards, setServiceWizards] = useState(() => {
    const saved = localStorage.getItem('serviceWizards');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // רשימת תהליכי שירות
  const [serviceProcessesList, setServiceProcessesList] = useState([]);
  const [showServiceListModal, setShowServiceListModal] = useState(false);
  const [serviceListFilter, setServiceListFilter] = useState({ date: '', status: '' });

  // טעינת תהליכי מכירה מהשרת
  const loadSalesProcesses = useCallback(async () => {
    try {
      let url = `${API_BASE}/api/sales-processes`;
      const params = new URLSearchParams();
      if (currentUser?.email) params.append('user_id', currentUser.email);
      if (salesListFilter.date) {
        params.append('date_from', salesListFilter.date);
        params.append('date_to', salesListFilter.date);
      }
      if (salesListFilter.status) params.append('status', salesListFilter.status);
      if (params.toString()) url += '?' + params.toString();

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setSalesProcessesList(data.processes || []);
      }
    } catch (err) {
      console.error('[Sales] Error loading processes:', err);
    }
  }, [currentUser?.email, salesListFilter]);

  // טעינת תהליכים בעת פתיחת המודל
  useEffect(() => {
    if (showSalesListModal) {
      loadSalesProcesses();
    }
  }, [showSalesListModal, loadSalesProcesses]);

  // Save wizards state to localStorage
  useEffect(() => {
    localStorage.setItem('salesWizards', JSON.stringify(salesWizards));
    // נקה את ה-localStorage הישן
    localStorage.removeItem('salesWizardOpen');
  }, [salesWizards]);

  // Admin-only system-status poller. Reads /api/health every 10 s so the
  // dot next to the bell stays in sync. Network errors collapse to
  // status='error' (treated as red — the backend is unreachable, which is
  // also what "during a restart" looks like to the client).
  useEffect(() => {
    // Not on /workspace: the dot it feeds is in the CRM header, which is not
    // mounted there, and every new status object re-rendered the whole App.
    if (!permIsAdmin(currentUser) || isWorkspaceRoute) { setSystemStatus(null); return; }

    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${config.api.baseUrl}/api/health`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSystemStatus(data);
      } catch (e) {
        if (!cancelled) setSystemStatus({ status: 'error', _network_error: true, error: String(e?.message || e) });
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [currentUser, isWorkspaceRoute]);

  // פונקציה לפתיחת wizard חדש
  const openNewSalesWizard = () => {
    const newId = Date.now();
    setSalesWizards(prev => [...prev, { id: newId, minimized: false }]);
    return newId;
  };

  // פונקציה לסגירת wizard
  const closeSalesWizard = (wizardId) => {
    setSalesWizards(prev => prev.filter(w => w.id !== wizardId));
  };

  // עדכון מצב מזעור של wizard
  const updateWizardMinimized = (wizardId, minimized) => {
    setSalesWizards(prev => prev.map(w =>
      w.id === wizardId ? { ...w, minimized } : w
    ));
  };

  // Service Process functions
  // Save service wizards state to localStorage
  useEffect(() => {
    localStorage.setItem('serviceWizards', JSON.stringify(serviceWizards));
  }, [serviceWizards]);

  // פונקציה לפתיחת service wizard חדש
  const openNewServiceWizard = () => {
    const newId = Date.now();
    setServiceWizards(prev => [...prev, { id: newId, minimized: false }]);
    return newId;
  };

  // פונקציה לסגירת service wizard
  const closeServiceWizard = (wizardId) => {
    setServiceWizards(prev => prev.filter(w => w.id !== wizardId));
  };

  // עדכון מצב מזעור של service wizard
  const updateServiceWizardMinimized = (wizardId, minimized) => {
    setServiceWizards(prev => prev.map(w =>
      w.id === wizardId ? { ...w, minimized } : w
    ));
  };

  // עדכון נתונים של wizard (שם לקוח, שלב וכו')
  const updateWizardData = (wizardId, data) => {
    setSalesWizards(prev => prev.map(w =>
      w.id === wizardId ? { ...w, ...data } : w
    ));
  };

  // Lightning Console Tabs - טאבים פתוחים עם תמיכה בתת-טאבים
  // מבנה חדש: כל טאב ראשי יכול להכיל subtabs
  // workspaces = טאבים ראשיים (לקוחות/חשבונות)
  // כל workspace יכול להכיל subtabs (הזדמנויות, הזמנות עבודה, וכו')
  // הטאב הראשון הוא "טאב נווט" שמשתנה לפי בחירת המשתמש
  const getInitialNavTab = () => {
    try {
      const savedNavTab = localStorage.getItem('navTab');
      if (savedNavTab) {
        return JSON.parse(savedNavTab);
      }
    } catch (e) {}
    return { id: 'home', type: 'home', label: 'בית', icon: '🏠', pinned: true, subtabs: [], isNavTab: true };
  };

  // Helper: get user-specific localStorage key
  const getUserStorageKey = (key) => {
    try {
      const savedUser = localStorage.getItem('salesforce_user') || sessionStorage.getItem('salesforce_user');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        const userId = user.username || user.email || user.id || '';
        if (userId) return `${key}_${userId}`;
      }
    } catch (e) {}
    return key;
  };

  const getInitialTabs = () => {
    try {
      const savedTabs = localStorage.getItem(getUserStorageKey('openTabs'));
      if (savedTabs) {
        const parsed = JSON.parse(savedTabs);
        // Filter out any old nav tabs and add current nav tab at start
        const nonNavTabs = parsed.filter(t => !t.isNavTab && t.id !== 'home');
        return [getInitialNavTab(), ...nonNavTabs];
      }
    } catch (e) {
      console.error('Error loading saved tabs:', e);
    }
    return [getInitialNavTab()];
  };

  const getInitialActiveTab = () => {
    try {
      const savedActiveTab = localStorage.getItem(getUserStorageKey('activeTabId'));
      if (savedActiveTab) return savedActiveTab;
    } catch (e) {}
    return 'home';
  };

  const getInitialActiveSubtab = () => {
    try {
      const savedActiveSubtab = localStorage.getItem(getUserStorageKey('activeSubtabId'));
      if (savedActiveSubtab && savedActiveSubtab !== 'null') return savedActiveSubtab;
    } catch (e) {}
    return null;
  };

  const [openTabs, setOpenTabs] = useState(getInitialTabs);
  const [activeTabId, setActiveTabId] = useState(getInitialActiveTab);
  const [activeSubtabId, setActiveSubtabId] = useState(getInitialActiveSubtab); // תת-טאב פעיל בתוך הטאב הראשי

  // Save tabs to localStorage whenever they change (per-user)
  useEffect(() => {
    try {
      localStorage.setItem(getUserStorageKey('openTabs'), JSON.stringify(openTabs));
    } catch (e) {
      console.error('Error saving tabs:', e);
    }
  }, [openTabs]);

  // Load recent searches from localStorage once we know who the user
  // is (waits for getUserStorageKey to resolve a real userId; if not,
  // we still read the un-suffixed legacy key as a safe fallback).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(getUserStorageKey('recentSearches'));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setRecentSearches(parsed.filter(t => typeof t === 'string' && t.trim()).slice(0, RECENT_SEARCHES_MAX));
        }
      }
    } catch (e) { /* ignore — fresh start is fine */ }
    // currentUser is the trigger — once login completes the per-user
    // key resolves correctly and we re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.username, currentUser?.email]);

  // Push a search term onto the per-user list (dedupe case-insensitively,
  // newest-first, capped). Persist immediately.
  const addRecentSearch = (term) => {
    const t = (term || '').toString().trim();
    if (!t || t.length < 2) return;
    setRecentSearches(prev => {
      const lower = t.toLowerCase();
      const dedup = prev.filter(x => x.toLowerCase() !== lower);
      const next = [t, ...dedup].slice(0, RECENT_SEARCHES_MAX);
      try {
        localStorage.setItem(getUserStorageKey('recentSearches'), JSON.stringify(next));
      } catch (e) { /* localStorage full / disabled — keep in-memory only */ }
      return next;
    });
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(getUserStorageKey('recentSearches'));
    } catch (e) { /* ignore */ }
  };

  // Save active tab IDs to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(getUserStorageKey('activeTabId'), activeTabId);
      localStorage.setItem(getUserStorageKey('activeSubtabId'), activeSubtabId || 'null');
    } catch (e) {
      console.error('Error saving active tab:', e);
    }
  }, [activeTabId, activeSubtabId]);

  // Validate session with backend (but don't block UI - user is already set from localStorage)
  useEffect(() => {
    const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');

    // If we have a stored session, validate it in background
    if (sessionToken && isAuthenticated) {
      console.log('[Session] Validating session with server (non-blocking)...');

      fetch(`${API_BASE}/api/auth/session`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      }).then(async response => {
        setSessionValidated(true);
        if (response.ok) {
          console.log('[Session] Validated successfully');
          // Update user info from server response if available
          try {
            const data = await response.json();
            if (data.user) {
              const updatedUser = {
                Id: data.user.id,
                id: data.user.id,
                username: data.user.username,
                name: data.user.name || `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim() || data.user.username,
                Name: data.user.name || `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim() || data.user.username,
                email: data.user.email,
                FirstName: data.user.firstName,
                LastName: data.user.lastName,
                profile: data.user.profile,
                isAdmin: data.user.isAdmin,
                service_resource_id: data.user.service_resource_id,
                loginTime: new Date().toISOString()
              };
              setCurrentUser(updatedUser);
              recordService.setCurrentUser(updatedUser);
              // Keep user in storage
              const storage = localStorage.getItem('session_token') ? localStorage : sessionStorage;
              storage.setItem('salesforce_user', JSON.stringify(updatedUser));
            }
          } catch (e) {
            // Response wasn't JSON or error - that's ok
          }
        } else {
          // Session validation failed - but keep user logged in if we have stored credentials
          // This prevents logout on page refresh when server restarts or session mismatch
          const storedUser = localStorage.getItem('salesforce_user') || sessionStorage.getItem('salesforce_user');
          if (storedUser) {
            console.log('[Session] Server validation failed but keeping cached session (user has stored credentials)');
            // Don't logout - just log the issue
          } else {
            // No stored user, clear session
            console.log('[Session] Invalid/expired and no stored user - clearing credentials');
            localStorage.removeItem('session_token');
            sessionStorage.removeItem('session_token');
            setIsAuthenticated(false);
            setCurrentUser(null);
          }
        }
      }).catch(err => {
        // Network error - keep user authenticated with cached credentials
        console.log('[Session] Network error during validation - keeping cached session:', err.message);
        setSessionValidated(true);
      });
    } else if (!sessionToken) {
      setSessionValidated(true);
    }

    // Periodic session refresh every 10 minutes (validates session; on 401 we
    // clear tokens and force re-login — never auto-login with an empty
    // password, which previously generated ~70 failed /api/auth/login per
    // hour from every office NAT and surfaced as the bulk of audit_log
    // login_failed events.
    const refreshInterval = setInterval(() => {
      const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (sessionToken) {
        fetch(`${API_BASE}/api/auth/session`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` },
        }).then(res => {
          if (res.ok) {
            console.log('[Session] Periodic refresh: session is valid');
          } else if (res.status === 401) {
            console.log('[Session] Expired — clearing tokens, prompting re-login');
            localStorage.removeItem('session_token');
            sessionStorage.removeItem('session_token');
            sessionStorage.setItem('session_expired_notice', '1');
            window.location.reload();
          }
        }).catch(err => {
          console.log('[Session] Periodic check failed (network):', err.message);
        });
      }
    }, 10 * 60 * 1000); // 10 minutes

    // Restore nav state from sessionStorage
    const savedNavState = sessionStorage.getItem('salesforce_nav_state');
    if (savedNavState) {
      try {
        const navState = JSON.parse(savedNavState);
        if (navState.setupSection) setSetupSection(navState.setupSection);
      } catch (err) {
        console.error('Error restoring nav state:', err);
      }
    }

    return () => clearInterval(refreshInterval);
  }, []);

  // Close global search on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(event.target)) {
        setShowGlobalSearch(false);
      }
    };

    if (showGlobalSearch) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showGlobalSearch]);

  // LIGHT LOAD - טעינה קלה של רשימת אובייקטים בלבד
  // הנתונים נטענים לפי דרישה מהשרת (on-demand) עם ה-DB החדש
  useEffect(() => {
    if (!isAuthenticated) return;

    const doLightLoad = async () => {
      try {
        if (!preloadComplete) {
          // First login - show splash screen with progress
          setPreloadProgress(10);
          setPreloadMessage('טוען רשימת אובייקטים...');
        }

        // Load objects list from API
        const response = await fetch(`${config.api.baseUrl}/api/objects`);
        if (!response.ok) throw new Error('Failed to load objects');

        const data = await response.json();
        const objectsList = data.objects || [];

        console.log(`[App] Loaded ${objectsList.length} objects`);

        // Set objects
        setObjects(objectsList);

        // Set stats
        setPreloadStats({
          totalObjects: objectsList.length,
          totalRecords: 0, // Records loaded on-demand
          loadTime: 0
        });

        if (!preloadComplete) {
          setPreloadProgress(100);
          setPreloadMessage('מוכן!');
          setPreloadComplete(true);
        }
        setInitialLoadComplete(true);
        sessionStorage.setItem('afikim_preload_done', '1');
        console.log('[App] Light load complete!');
      } catch (error) {
        console.error('[App] Light load failed:', error);
        if (!preloadComplete) {
          setPreloadError(error.message || 'Failed to load objects');
        }
      }
    };

    doLightLoad();
  }, [isAuthenticated]);

  // Load my tasks for header badge
  const myTasksIntervalRef = React.useRef(null);
  useEffect(() => {
    // The tasks badge lives in the CRM header — not mounted on /workspace.
    if (!isAuthenticated || !currentUser?.username || isWorkspaceRoute) return;

    const fetchMyTasks = async () => {
      // 2026-06-21 — Page Visibility guard. Skip background polls when the
      // tab isn't visible. Owner audit showed 87 polls/min/user against
      // these endpoints, far above the 60s interval × 1 tab — driven by
      // many tabs left open across the office overnight. Cuts background
      // load ~90% without changing foreground UX.
      if (typeof document !== 'undefined' && document.hidden) {
        if (typeof window !== 'undefined') {
          window.__pollSkipCount = (window.__pollSkipCount || 0) + 1;
        }
        return;
      }
      try {
        const res = await fetch(`${config.api.baseUrl}/api/tasks/my?username=${encodeURIComponent(currentUser.username)}&status=all&limit=20`);
        if (res.ok) {
          const data = await res.json();
          const tasks = data.tasks || [];
          setMyTasks(tasks);
          const completedStatuses = ['Completed', 'סיום טיפול בשביעות רצון', 'סיום טיפול לא בשביעות רצון', 'הושלם'];
          const openCount = tasks.filter(t => !completedStatuses.includes(t.Status)).length;
          setMyTasksCount(openCount);
        }
      } catch (err) {
        console.error('[MyTasks] Failed to load:', err);
      }
      // Also fetch tasks I assigned to others
      try {
        const res2 = await fetch(`${config.api.baseUrl}/api/tasks/assigned-by-me?username=${encodeURIComponent(currentUser.username)}&limit=20`);
        if (res2.ok) {
          const data2 = await res2.json();
          setAssignedByMeTasks(data2.tasks || []);
        }
      } catch (err) {
        console.error('[MyTasks] Failed to load assigned-by-me:', err);
      }
    };

    fetchMyTasks();
    // 2026-06-21 incident mitigation: bump from 60s → 300s. /api/tasks/my
    // + /api/tasks/assigned-by-me were the top consumers on prod (1.7k+
    // calls per 5 min, ~9.6 req/sec). 5× interval cuts that to ~2 req/sec
    // immediately, independent of page-visibility guard / browser cache
    // state. UX impact on the header badge is minimal — the badge updates
    // on user navigation too, and a 5-minute lag on a passive counter is
    // invisible. Reverse after the underlying tasks-pipeline is faster.
    myTasksIntervalRef.current = setInterval(fetchMyTasks, 300000); // refresh every 5 min
    return () => clearInterval(myTasksIntervalRef.current);
  }, [isAuthenticated, currentUser?.username, isWorkspaceRoute]);

  // CRM Notification polling + WebSocket (wallet transfers, discount requests)
  const crmNotifIntervalRef = React.useRef(null);
  useEffect(() => {
    const userId = currentUser?.Id || currentUser?.id;
    if (!isAuthenticated || !userId) return;
    // Owner 2026-11-02: no CRM notifications inside the WhatsApp Workspace. The
    // bell is not mounted there; close the socket rather than keep a second
    // connection per agent open for nothing.
    if (isWorkspaceRoute) {
      afSocket.disconnect('/notifications');
      return;
    }

    const pollNotifCount = async () => {
      // 2026-06-21 — Page Visibility guard (see fetchMyTasks above).
      if (typeof document !== 'undefined' && document.hidden) {
        if (typeof window !== 'undefined') {
          window.__pollSkipCount = (window.__pollSkipCount || 0) + 1;
        }
        return;
      }
      try {
        const res = await fetch(`${config.api.baseUrl}/api/notifications/${userId}/count`);
        if (res.ok) {
          const data = await res.json();
          setUnreadNotifCount(data.count || 0);
        }
      } catch (e) { /* silent */ }
    };

    // WebSocket: instant notification push
    const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    if (sessionToken) {
      afSocket.connect('/notifications', sessionToken);
      afSocket.on('/notifications', 'new_notification', (notif) => {
        setUnreadNotifCount(prev => prev + 1);
        setCrmNotifications(prev => [notif, ...prev].slice(0, 50));
      });
    }

    // HTTP polling fallback. Phase 3.1 Part B: only poll when WebSocket is
    // disconnected — eliminates redundant /api/notifications/count load when
    // Socket.IO is already pushing events. 60s when WS down (was 15s
    // unconditional). Per V2 plan §B Risk 4 (fallback should not saturate
    // backend if many users degrade simultaneously).
    pollNotifCount();
    crmNotifIntervalRef.current = setInterval(() => {
      if (!afSocket.isConnected('/notifications')) {
        pollNotifCount();
      }
    }, 60000);

    return () => {
      clearInterval(crmNotifIntervalRef.current);
      afSocket.off('/notifications', 'new_notification');
    };
  }, [isAuthenticated, currentUser?.Id, currentUser?.id, isWorkspaceRoute]);

  // Fetch full CRM notifications when dropdown opens
  const fetchCrmNotifications = useCallback(async () => {
    const userId = currentUser?.Id || currentUser?.id;
    if (!userId) return;
    try {
      const res = await fetch(`${config.api.baseUrl}/api/notifications/${userId}?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setCrmNotifications(data.notifications || []);
      }
    } catch (e) { console.error('[CRM Notif] Fetch error:', e); }
  }, [currentUser?.Id, currentUser?.id]);

  // Mark notification as read
  const markNotifRead = useCallback(async (notifId) => {
    try {
      await fetch(`${config.api.baseUrl}/api/notifications/${notifId}/read`, { method: 'POST' });
      setCrmNotifications(prev => prev.map(n => n.id === notifId ? { ...n, status: 'read' } : n));
      setUnreadNotifCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  }, []);

  // Mark all notifications as read
  const markAllNotifsRead = useCallback(async () => {
    const userId = currentUser?.Id || currentUser?.id;
    if (!userId) return;
    try {
      await fetch(`${config.api.baseUrl}/api/notifications/read-all/${userId}`, { method: 'POST' });
      setCrmNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadNotifCount(0);
    } catch (e) { /* silent */ }
  }, [currentUser?.Id, currentUser?.id]);

  // Handle notification click - open detail modal
  const handleNotifClick = useCallback(async (notif) => {
    if (notif.status === 'unread') markNotifRead(notif.id);
    setSelectedNotification(notif);
    const notifData = typeof notif.data === 'string' ? JSON.parse(notif.data) : (notif.data || {});

    if (notif.type?.includes('transfer') || notif.type === 'transfer_pending') {
      // Load transfer details
      setTransferModalLoading(true);
      setShowTransferModal(true);
      setShowNotifications(false);
      try {
        const res = await fetch(`${config.api.baseUrl}/api/wallet-transfer/${notifData.transferId}/details`);
        if (res.ok) {
          const data = await res.json();
          setTransferDetails(data);
        }
      } catch (e) { console.error('[Transfer] Detail load error:', e); }
      setTransferModalLoading(false);
    } else if (notif.type?.includes('discount')) {
      setShowDiscountModal(true);
      // P0.9 Task 6 — preload the editable price field with the requested price
      try {
        const _data = typeof notif.data === 'string' ? JSON.parse(notif.data) : (notif.data || {});
        const _initial = _data?.requestedPrice ?? '';
        setApprovedPriceInput(_initial !== '' && _initial != null ? String(_initial) : '');
      } catch {
        setApprovedPriceInput('');
      }
      setShowNotifications(false);
    } else if (notif.type === 'payroll_late_items' && typeof notifData.url === 'string' && notifData.url.startsWith('/accounting/')) {
      // 2026-10-06 payroll: deals added to a closed payroll month — open the report
      // of the month they will be paid in (services/payroll_late_check.py).
      setShowNotifications(false);
      navigate(notifData.url);
    } else if (notif.related_object_type && notif.related_record_id) {
      // 2026-06-13: generic record-page navigation for any notification
      // that carries (related_object_type, related_record_id). First user
      // is `sa_sent_to_lab` for איילת (תכנון: pres = Service Appointment),
      // but this branch also covers any future bell-notification that
      // names a target record. Close the dropdown so the route change
      // isn't hidden by the popover.
      setShowNotifications(false);
      try {
        navigate(`/record/${encodeURIComponent(notif.related_object_type)}/${encodeURIComponent(notif.related_record_id)}`);
      } catch (e) {
        console.warn('[Notif] navigate failed:', e);
      }
    }
  }, [markNotifRead, navigate]);

  // Transfer actions
  const handleTransferAction = useCallback(async (action, extraData = {}) => {
    const { transferIdOverride, ...passthroughExtra } = extraData;
    const notifData = typeof selectedNotification?.data === 'string' ? JSON.parse(selectedNotification.data) : (selectedNotification?.data || {});
    const transferId = transferIdOverride || notifData.transferId || transferDetails?.transfer?.Id;
    const userId = currentUser?.Id || currentUser?.id;
    if (!transferId || !userId) return;

    setTransferModalLoading(true);
    try {
      // userName included so backend audit_log + notifications carry actor name
      const userName = currentUser?.name || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username || '';
      const body = { transferId, userId, userName, ...passthroughExtra };
      const res = await fetch(`${config.api.baseUrl}/api/wallet-transfer/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setShowTransferModal(false);
        setSelectedNotification(null);
        setTransferDetails(null);
        setShowWalletEditForm(false);
        setShowWalletRejectForm(false);
        setWalletActionReason('');
        setWalletEditAmount('');
        setWalletEditNotes('');
        fetchCrmNotifications();
        // Reload manager wallet so inline-action callers see fresh state
        loadManagerWallet();
        // Show success toast
        const msgs = { approve: 'העברה אושרה בהצלחה', reject: 'העברה נדחתה', edit: 'העברה נשלחה בחזרה לטכנאי לעריכה' };
        alert(msgs[action] || 'הפעולה בוצעה בהצלחה');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'שגיאה בביצוע הפעולה');
      }
    } catch (e) {
      alert('שגיאה בחיבור לשרת');
    }
    setTransferModalLoading(false);
    // loadManagerWallet intentionally omitted — declared later in same component (TDZ);
    // called by name resolves at click time when both have rendered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNotification, transferDetails, currentUser, fetchCrmNotifications]);

  // P0.9 Task 6 — copyable approval code modal state. Replaces alert() (D7).
  // approvedCode and approvedPriceForDisplay populate the modal body.
  const [approvedCodeModal, setApprovedCodeModal] = useState(null); // {code, price}

  // Discount actions
  const handleDiscountAction = useCallback(async (action, extraData = {}) => {
    const notifData = typeof selectedNotification?.data === 'string' ? JSON.parse(selectedNotification.data) : (selectedNotification?.data || {});
    const requestId = notifData.requestId;
    const userId = currentUser?.Id || currentUser?.id;
    const userName = currentUser?.Name || currentUser?.name || `${currentUser?.FirstName || ''} ${currentUser?.LastName || ''}`.trim() || (currentUser?.email || '');
    if (!requestId || !userId) return;

    setDiscountModalLoading(true);
    try {
      const body = { requestId, managerId: userId, managerName: userName, ...extraData };
      const res = await fetch(`${config.api.baseUrl}/api/discount-request/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setShowDiscountModal(false);
        setSelectedNotification(null);
        setWalletActionReason('');
        fetchCrmNotifications();
        if (action === 'approve') {
          // P0.9 Task 6 (D7) — copyable modal instead of blocking alert.
          setApprovedCodeModal({
            code: data.code || '',
            price: extraData.approvedPrice ?? notifData.requestedPrice ?? null,
          });
        } else {
          // Reject: simple toast-style alert is fine; nothing to copy.
          alert('בקשת הנחה נדחתה');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'שגיאה בביצוע הפעולה');
      }
    } catch (e) {
      alert('שגיאה בחיבור לשרת');
    }
    setDiscountModalLoading(false);
  }, [selectedNotification, currentUser, fetchCrmNotifications]);

  // Load manager wallet
  const loadManagerWallet = useCallback(async () => {
    const userId = currentUser?.Id || currentUser?.id;
    if (!userId) return;
    try {
      const res = await fetch(`${config.api.baseUrl}/api/manager-wallet/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setManagerWalletData(data);
      }
    } catch (e) { console.error('[Wallet] Error:', e); }
  }, [currentUser]);

  // Load accounting wallet
  const loadAccountingWallet = useCallback(async () => {
    const userId = currentUser?.Id || currentUser?.id;
    if (!userId) return;
    try {
      const res = await fetch(`${config.api.baseUrl}/api/accounting-wallet/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setAccountingWalletData(data);
      }
    } catch (e) { console.error('[Wallet] Error:', e); }
  }, [currentUser]);

  // Retry load on error
  const handlePreloadRetry = useCallback(() => {
    setPreloadError(null);
    setPreloadProgress(0);
    setPreloadMessage('');
    setPreloadComplete(false); // This will trigger the useEffect above
  }, []);

  // Load essential data on mount when authenticated (fallback if preload disabled)
  const flexipagePreloadedRef = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && preloadComplete) {
      // Stats and metadata can load in background after preload
      const timer = setTimeout(() => {
        loadMetadata();
        loadStats();
      }, 500);

      // Preload FlexiPages for priority objects in background (for instant record page loading)
      // Only run once
      if (!flexipagePreloadedRef.current) {
        flexipagePreloadedRef.current = true;
        const preloadFlexipages = async () => {
          const priorityObjects = ['Account', 'Contact', 'Opportunity', 'Lead', 'Case', 'ServiceAppointment', 'WorkOrder', 'Asset'];
          console.log('[FlexiPage] Preloading FlexiPages in background...');

          for (const objName of priorityObjects) {
            try {
              const res = await fetch(`${API_BASE}/api/objects/${objName}/record-page`);
              if (res.ok) {
                const data = await res.json();
                if (data.flexipage) {
                  setFlexipageCache(prev => ({ ...prev, [objName]: data.flexipage }));
                }
              }
            } catch (e) {
              // Silently ignore - some objects don't have FlexiPages
            }
          }
          console.log('[FlexiPage] Background preload complete');
        };

        // Start preloading FlexiPages after a short delay (let UI render first)
        setTimeout(preloadFlexipages, 1000);
      }

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isAuthenticated, preloadComplete]);

  // Data is loaded on-demand from API when user navigates to object list

  // Save setup section to sessionStorage whenever it changes
  useEffect(() => {
    if (isAuthenticated && setupSection) {
      const navState = { setupSection };
      sessionStorage.setItem('salesforce_nav_state', JSON.stringify(navState));
    }
  }, [setupSection, isAuthenticated]);

  const handleLogin = (user) => {
    // Check if user is a technician - block access and redirect to technician app
    // 2026-10-09 (dealer REV-2 P5): drivers ('נהג' → 'driver') work only in the TechApp too.
    const technicianProfiles = ['technician', 'טכנאי', 'driver', 'נהג'];
    const technicianRoles = ['טכנאי שירות', 'טכנאי', 'מתקין', 'טכנאי בכיר', 'נהג'];

    const isTechnician = technicianProfiles.includes(user.profile) ||
                         technicianRoles.includes(user.role) ||
                         technicianRoles.includes(user.technical_type);

    if (isTechnician) {
      // Show message and redirect to external technician app
      alert('אין לך גישה למערכת זו.\nאנא השתמש באפליקציית הטכנאים בכתובת:\nhttps://technician.afikim-water.org/app');

      // Redirect to external technician app
      window.location.href = 'https://technician.afikim-water.org/app';
      return;
    }

    setCurrentUser(user);
    setIsAuthenticated(true);
    // Update recordService with current user
    recordService.setCurrentUser(user);
    setSentryUser(user);

    // PR #10 — owner-spec 2026-06-17: site@afikim-water.co.il is the
    // single account scoped to the developer integration portal. Land
    // them directly on /integration-portal on every login — they have
    // no other CRM access (sidebar is empty via nav_menu_permission).
    if ((user.profile || '').trim() === 'integration_developer') {
      try {
        // Use the same history hook the rest of the app uses to avoid
        // a full reload — but keep the fallback for cold links.
        window.history.pushState({}, '', '/integration-portal');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (e) {
        window.location.href = '/integration-portal';
      }
    }
  };

  const handleLogout = async () => {
    // Call backend logout API
    const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    if (sessionToken) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (err) {
        console.error('Logout error:', err);
      }
    }

    // Disconnect WebSockets
    afSocket.disconnectAll();

    // Clear local storage
    localStorage.removeItem('salesforce_user');
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('salesforce_user');
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('salesforce_nav_state');
    sessionStorage.removeItem('afikim_preload_done');
    // Clear local cache
    setAllRecordsCache({});
    setPreloadComplete(false);

    setCurrentUser(null);
    setIsAuthenticated(false);
    clearSentryUser();
    setSelectedObject(null);
    setSelectedRecord(null);
    setSetupSection(null);
    navigate('/');
  };

  const loadMetadata = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/metadata`);
      const data = await res.json();
      setMetadata(data);
    } catch (err) {
      console.error('שגיאה בטעינת מטא-דאטא:', err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('שגיאה בטעינת סטטיסטיקות:', err);
    }
  };

  const loadScriptClasses = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/service`);
      const data = await res.json();
      setScriptClasses(data.apex_classes || []);
    } catch (err) {
      console.error('שגיאה בטעינת מחלקות סקריפט:', err);
    }
  };

  const loadFlows = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/legacy-backup/flows`);
      const data = await res.json();
      setFlows(data.flows || []);
    } catch (err) {
      console.error('שגיאה בטעינה Flows:', err);
    }
  };

  // פונקציה להוספת רשומות ל-cache
  const addToCache = (objectName, recordsToAdd) => {
    if (!recordsToAdd || recordsToAdd.length === 0) return;

    setRecordCache(prev => {
      const objectCache = prev[objectName] || {};
      const newObjectCache = { ...objectCache };

      recordsToAdd.forEach(record => {
        const id = record.Id || record.id;
        if (id) {
          newObjectCache[id] = record;
        }
      });

      return { ...prev, [objectName]: newObjectCache };
    });
  };

  // פונקציה לקבלת רשומה מה-cache
  const getFromCache = (objectName, recordId) => {
    return recordCache[objectName]?.[recordId] || null;
  };

  // טעינה ראשונית של כל הנתונים - קריאה אחת לשרת שכבר טען את הנתונים
  const loadAllDataInitial = async () => {
    if (initialLoadComplete) return;

    try {
      // קריאה אחת לשרת - הנתונים כבר טעונים בזיכרון השרת!
      const res = await fetch(`${API_BASE}/api/preloaded-data`);
      const data = await res.json();

      const newAllRecordsCache = {};

      // עבור על כל האובייקטים שהשרת החזיר
      for (const [objName, objData] of Object.entries(data.objects || {})) {
        const records = objData.records || [];
        if (records.length > 0) {
          newAllRecordsCache[objName] = records;
          addToCache(objName, records);
        }
      }

      setAllRecordsCache(newAllRecordsCache);
      setInitialLoadComplete(true);

      console.log(`Initial data loaded: ${data.total_records} records from ${Object.keys(newAllRecordsCache).length} objects`);
    } catch (err) {
      console.error('Error loading preloaded data:', err);
      setInitialLoadComplete(true); // סמן שסיים גם בשגיאה
    }
  };

  const loadRecords = async (objectName, searchTerm = null, forceRefresh = false, parentFilter = null) => {
    const startTime = performance.now();

    // אם יש חיפוש - תמיד פנה לשרת כדי לחפש בכל הנתונים!
    if (searchTerm && searchTerm.length >= 2) {
      setLoading(true);
      console.log(`[App] Searching SERVER for "${searchTerm}" in ${objectName}...`);

      try {
        // Use dedicated search endpoint for better performance.
        // 2026-05-29: when the list view is scoped to a parent (the
        // "📊 הצג בטבלה" flow), keep the scope on search results so
        // typing into the filter chip doesn't break out of the parent
        // context.
        let url = `${API_BASE}/api/objects/${objectName}/search?q=${encodeURIComponent(searchTerm)}&limit=200`;
        if (parentFilter && parentFilter.field && parentFilter.value) {
          url += `&filter_field=${encodeURIComponent(parentFilter.field)}&filter_value=${encodeURIComponent(parentFilter.value)}`;
        }
        const res = await fetch(url);
        const data = await res.json();
        const searchResults = data.records || [];
        const duration = Math.round(performance.now() - startTime);

        console.log(`[App] Search results from SERVER: ${searchResults.length}/${data.total || searchResults.length} for "${searchTerm}" in ${objectName} (${duration}ms)`);
        setRecords(searchResults);
        setLoading(false);
        return;
      } catch (err) {
        console.error('[App] Search error:', err);
        // Fallback to local cache search if server fails
        if (allRecordsCache[objectName]) {
          const term = searchTerm.toLowerCase();
          const filtered = allRecordsCache[objectName].filter(record =>
            Object.values(record).some(value =>
              String(value).toLowerCase().includes(term)
            )
          );
          setRecords(filtered);
          console.log(`[App] Search fallback to cache: ${filtered.length} results`);
        }
        setLoading(false);
        return;
      }
    }

    // נסה מה-cache המקומי קודם (לטעינה מהירה) — אלא אם הקריאה מבקשת רענון.
    // 2026-05-17: forceRefresh נקרא מ-navigateToObject ו-setNavTab כי דווח
    // שלחיצה על אובייקט מהתפריט הראשי לא מציגה רשומות עד F5. הקריאה
    // המקומית מ-React Router ל-ObjectListRoute עדיין משתמשת ב-cache
    // הזה — רק לחיצות מפורשות של המשתמש מבקשות fetch חדש מהשרת.
    // 2026-05-29: skip the shared cache when a parentFilter is set — the
    // cache is keyed by objectName only and would otherwise overwrite the
    // unfiltered list with a scoped result (and vice-versa).
    if (!forceRefresh && !parentFilter && allRecordsCache[objectName] && allRecordsCache[objectName].length > 0) {
      setRecords(allRecordsCache[objectName]);
      console.log(`[App] Records from local cache: ${allRecordsCache[objectName].length} for ${objectName}`);
      return;
    }

    // אחרת - טען מהשרת (fallback)
    setLoading(true);
    console.log(`[App] Loading records from SERVER for ${objectName}...`);

    try {
      // First page: only 50 records. Owner asked for Salesforce-style
      // infinite scroll — load 50 more on scroll-near-bottom (see
      // `loadMoreRecords` below). 500 was too heavy for the first paint;
      // most users only look at the top of the list.
      const RECORDS_LIMIT = 50;
      let url = `${API_BASE}/api/objects/${objectName}/records?sort_order=desc&limit=${RECORDS_LIMIT}&offset=0`;
      if (parentFilter && parentFilter.field && parentFilter.value) {
        url += `&filter_field=${encodeURIComponent(parentFilter.field)}&filter_value=${encodeURIComponent(parentFilter.value)}`;
      }

      const res = await fetch(url);
      const data = await res.json();
      const loadedRecords = data.records || [];
      const totalCount = data.total_count || 0;
      const duration = Math.round(performance.now() - startTime);

      console.log(`[App] Loaded ${loadedRecords.length}/${totalCount} records for ${objectName} in ${duration}ms (from server)`);

      setRecords(loadedRecords);
      setRecordsTotalCount(totalCount);
      setRecordsHasMore(loadedRecords.length < totalCount);

      // שמור ב-cache — אבל לא כשהתוצאה מסוננת לפי אב (אחרת היא תחליף
      // את התוצאה הלא-מסוננת ותגרום לבלבול ברשימות הבאות).
      if (!searchTerm && !parentFilter) {
        setAllRecordsCache(prev => ({ ...prev, [objectName]: loadedRecords }));
      }
      addToCache(objectName, loadedRecords);
    } catch (err) {
      console.error('[App] Error loading records:', err);
      setRecords([]);
      setRecordsHasMore(false);
      setRecordsTotalCount(0);
    }
    setLoading(false);
  };

  // Append the next batch of 50 records to the list view. Triggered by
  // an onScroll handler on the table container (see ListView render).
  // Only runs when there's no active search — the search endpoint
  // returns its own bounded set and isn't paginated.
  const loadMoreRecords = useCallback(async () => {
    if (recordsLoadingMore || !recordsHasMore) return;
    if (!selectedObject) return;
    setRecordsLoadingMore(true);
    try {
      const offset = records.length;
      const url = `${API_BASE}/api/objects/${selectedObject}/records?sort_order=desc&limit=50&offset=${offset}`;
      const res = await fetch(url);
      const data = await res.json();
      const more = data.records || [];
      if (more.length > 0) {
        setRecords(prev => {
          const combined = [...prev, ...more];
          // refresh local cache so search-fallback uses the latest
          setAllRecordsCache(c => ({ ...c, [selectedObject]: combined }));
          return combined;
        });
      }
      const newLen = records.length + more.length;
      setRecordsHasMore(newLen < (data.total_count || recordsTotalCount));
    } catch (err) {
      console.error('[App] loadMoreRecords error:', err);
    } finally {
      setRecordsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsLoadingMore, recordsHasMore, selectedObject, records.length, recordsTotalCount]);

  const loadScriptClass = async (className) => {
    try {
      const res = await fetch(`${API_BASE}/api/service/${className}`);
      const data = await res.json();
      setSelectedApex(data);
    } catch (err) {
      console.error('שגיאה בטעינת מחלקת סקריפט:', err);
    }
  };

  const loadFlow = async (flowName) => {
    try {
      const res = await fetch(`${API_BASE}/api/legacy-backup/flows/${flowName}`);
      const data = await res.json();
      setSelectedFlow(data);
    } catch (err) {
      console.error('שגיאה בטעינה Flow:', err);
    }
  };

  // 2026-05-15: race-condition guard for the global search.
  // Two-layer protection so a stale response can never overwrite a fresher
  // result set:
  //   (a) AbortController — cancels the in-flight HTTP request on the next
  //       keystroke so older queries don't even resolve.
  //   (b) Sequence number — if an older response arrives anyway (e.g. the
  //       server already finished, browser is in the middle of decoding,
  //       abort signal raced), we drop it because reqSeqRef.current has
  //       moved past it. Owner-reported "results suddenly change to
  //       unrelated list" was a stale-response-overrides-fresh case.
  const globalSearchAbortRef = useRef(null);
  const globalSearchSeqRef = useRef(0);
  // 2026-05-26: debounce keystrokes so a rapid 10-char phone-number entry
  // fires 1 search instead of 10. Backend phone-prefix lookups still take
  // 4-22s per call until the BE phone-prefix index lands; debounce cuts
  // request volume immediately and keeps the uvicorn thread pool free for
  // other handlers (whatsapp polls, tasks, etc).
  const globalSearchDebounceRef = useRef(null);

  // חיפוש גלובלי - פנייה לשרת לחיפוש בכל הנתונים
  const performGlobalSearch = useCallback(async (term) => {
    // צריך לפחות 2 תווים לחיפוש
    if (!term || term.length < 2) {
      // Abort any in-flight request — keystroke that dropped below 2 chars
      // shouldn't see late results.
      if (globalSearchAbortRef.current) {
        try { globalSearchAbortRef.current.abort(); } catch {}
        globalSearchAbortRef.current = null;
      }
      setGlobalSearchResults([]);
      setSearchLoading(false);
      return;
    }

    // Cancel the previous request before firing a new one.
    if (globalSearchAbortRef.current) {
      try { globalSearchAbortRef.current.abort(); } catch {}
    }
    const ctrl = new AbortController();
    globalSearchAbortRef.current = ctrl;
    const mySeq = ++globalSearchSeqRef.current;

    setSearchLoading(true);
    const startTime = performance.now();

    try {
      // פנה לשרת לחיפוש גלובלי - מחפש בכל האובייקטים!
      const res = await fetch(
        `${API_BASE}/api/search?q=${encodeURIComponent(term)}&limit=100`,
        { signal: ctrl.signal }
      );
      // If a newer keystroke already fired (or finished) skip this stale
      // response — never call setGlobalSearchResults with old data.
      if (mySeq !== globalSearchSeqRef.current) return;
      const data = await res.json();
      if (mySeq !== globalSearchSeqRef.current) return;

      // ארגן את התוצאות לפי אובייקט
      // API returns: { results: [{object_name, records: [...]}, ...] }
      const groupedResults = {};
      for (const group of (data.results || [])) {
        const objName = group.object_name || group.objectName;
        if (isObjectHidden(hiddenObjects, objName)) continue;
        if (!groupedResults[objName]) {
          groupedResults[objName] = {
            objectName: objName,
            label: getObjectConfig(objName).heLabel || objName,
            matchesCount: 0,
            records: []
          };
        }
        // Records already contain _display_name from server
        const records = group.records || [];
        groupedResults[objName].records.push(...records);
        groupedResults[objName].matchesCount += records.length;
      }

      const results = Object.values(groupedResults);
      const OBJECT_PRIORITY = { Account: 1, Opportunity: 2, Payment__c: 3, Asset: 4, Case: 5, WorkOrder: 6, ServiceAppointment: 7 };
      results.sort((a, b) => (OBJECT_PRIORITY[a.objectName] || 100) - (OBJECT_PRIORITY[b.objectName] || 100) || b.matchesCount - a.matchesCount);
      setGlobalSearchResults(results);

      const duration = Math.round(performance.now() - startTime);
      console.log(`[App] Global search from SERVER: ${data.total || results.reduce((s, r) => s + r.matchesCount, 0)} results in ${duration}ms`);

    } catch (err) {
      // 2026-05-15: AbortError is expected on every keystroke that cancels
      // the previous request — quietly ignore so it doesn't trigger the
      // local-cache fallback (which would set unrelated stale results).
      if (err && (err.name === 'AbortError' || err.code === 20)) {
        return;
      }
      // Same stale-response guard as the success branch.
      if (mySeq !== globalSearchSeqRef.current) return;
      console.error('[App] Global search error:', err);

      // Fallback - חפש ב-cache מקומי אם השרת נכשל
      const searchTerm = term.toLowerCase();
      const results = [];

      // fallback ל-cache הישן
      for (const [objName, records] of Object.entries(allRecordsCache)) {
        if (!records || records.length === 0) continue;
        const matchingRecords = records.filter(record =>
          Object.values(record).some(value =>
            String(value).toLowerCase().includes(searchTerm)
          )
        );
        if (matchingRecords.length > 0) {
          results.push({
            objectName: objName,
            label: getObjectConfig(objName).heLabel || objName,
            matchesCount: matchingRecords.length,
            records: matchingRecords
          });
        }
      }
      results.sort((a, b) => b.matchesCount - a.matchesCount);
      if (mySeq === globalSearchSeqRef.current) {
        setGlobalSearchResults(results);
        console.log(`[App] Global search fallback to cache: ${results.reduce((s, r) => s + r.matchesCount, 0)} results`);
      }
    } finally {
      // Only clear the spinner if THIS request is still the latest. Otherwise
      // a slower-finishing earlier request would prematurely flip loading=false
      // while a fresher one is still in flight.
      if (mySeq === globalSearchSeqRef.current) {
        setSearchLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRecordsCache, hiddenObjects]);

  // Debounced entry point used by the search input's onChange.
  // Empty / too-short terms fire immediately so backspacing past the
  // threshold clears results without a 300ms lag.
  const triggerGlobalSearch = useCallback((term) => {
    if (globalSearchDebounceRef.current) {
      clearTimeout(globalSearchDebounceRef.current);
      globalSearchDebounceRef.current = null;
    }
    if (!term || term.length < 2) {
      performGlobalSearch(term);
      return;
    }
    globalSearchDebounceRef.current = setTimeout(() => {
      globalSearchDebounceRef.current = null;
      performGlobalSearch(term);
    }, 300);
  }, [performGlobalSearch]);

  const navigateToObject = (obj) => {
    setSelectedObject(obj);
    // 2026-05-17: force a fresh fetch on every explicit nav click so the
    // list always reflects the current DB. Cache short-circuit was leaving
    // the list empty when the user clicked an object from the nav dropdown
    // and the cache had been seeded with an empty array on a prior failed
    // first attempt. The cache still wins for non-click code paths (e.g.,
    // ObjectListRoute's useEffect on URL-direct entry).
    loadRecords(obj.api_name, null, true);
    setShowAppLauncher(false);
    setShowNavMenu(false);
    navigate(buildObjectListUrl(obj.api_name));
  };

  // Helper: קבל שם תצוגה לרשומה
  // Owner-flagged 2026-06-09: Person Accounts/Contacts whose backend
  // Name was built as `${FirstName} ${LastName}` with FirstName=null
  // serialize as the literal string "null משפחה". Strip those tokens
  // and fall back to a clean FN/LN combo. Always returns a TRIMMED
  // string or '' — the caller wraps with the per-object default.
  const _cleanPersonDisplayName = (record) => {
    const cleanField = (v) => {
      const s = (v ?? '').toString().trim();
      return (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') ? '' : s;
    };
    const cleanedName = (record?.Name || '')
      .toString()
      .replace(/^\s*(null|undefined)\s+/i, '')
      .replace(/\s+(null|undefined)\s*$/i, '')
      .replace(/\s+(null|undefined)\s+/gi, ' ')
      .trim();
    if (cleanedName) return cleanedName;
    const parts = [cleanField(record?.FirstName), cleanField(record?.LastName)].filter(Boolean);
    return parts.length ? parts.join(' ') : '';
  };

  const getRecordDisplayName = (record, objectName) => {
    if (objectName === 'ServiceAppointment') {
      return record?.AppointmentNumber || record?.Name || 'פגישה';
    } else if (objectName === 'WorkOrder') {
      const woNum = record?.WorkOrderNumber;
      if (woNum) {
        const num = parseFloat(woNum);
        return isNaN(num) ? woNum : String(Math.round(num)).padStart(8, '0');
      }
      return record?.Name || 'הזמנת עבודה';
    } else if (objectName === 'Case') {
      return record?.CaseNumber || record?.Subject || record?.Name || 'פנייה';
    } else if (objectName === 'Contact') {
      // Owner-flagged 2026-06-09 (alongside Account): same null-handling
      // — strip stringified 'null' tokens so tab labels never show the
      // literal word "null" next to a real name.
      return _cleanPersonDisplayName(record) || 'איש קשר';
    } else if (objectName === 'Account') {
      // Owner-flagged 2026-06-09: tab labels showed "null אנלאי מולונך"
      // because the Account.Name had been built as
      // `${FirstName} ${LastName}` with FirstName=null serializing as
      // the literal string "null". Also: the original expression had a
      // precedence bug — `Name || FN && LN ? `${FN} ${LN}` : 'חשבון'`
      // parses as `(Name || (FN && LN)) ? `${FN} ${LN}` : 'חשבון'`, so
      // when Name was set it returned the FN/LN concat instead of Name.
      return _cleanPersonDisplayName(record) || 'חשבון';
    } else if (objectName === 'Opportunity') {
      return record?.Name || 'הזדמנות';
    }
    return record?.Name || record?.Subject || record?.Title ||
           record?.CaseNumber || record?.AppointmentNumber || record?.WorkOrderNumber ||
           record?.FirstName || 'רשומה';
  };

  // Helper: מצא טאב אב - כל רשומה שקשורה ל-Account פתוח תהיה תת-טאב שלו
  const findParentTab = (record, objectName) => {
    // מצא את הטאב הפעיל הנוכחי
    const currentActiveTab = openTabs.find(t => t.id === activeTabId);

    console.log('[Tabs] findParentTab:', {
      objectName,
      recordId: record?.Id,
      activeTabId,
      currentActiveTab: currentActiveTab ? {
        id: currentActiveTab.id,
        type: currentActiveTab.type,
        objectName: currentActiveTab.objectName,
        subtabsCount: currentActiveTab.subtabs?.length || 0
      } : null
    });

    // אם פותחים Account
    if (objectName === 'Account') {
      // אם אנחנו בתוך workspace של Account (טאב ראשי או תת-טאב)
      if (currentActiveTab?.type === 'record' && currentActiveTab?.objectName === 'Account') {
        // בדוק אם הרשומה הנוכחית קשורה ל-Account שאנחנו פותחים
        if (selectedRecord) {
          const currentRecordAccountId = selectedRecord?.AccountId || selectedRecord?.Account__c;
          if (currentRecordAccountId === record?.Id) {
            // הרשומה הנוכחית שייכת לחשבון הזה - פתח את החשבון כתת-טאב
            console.log('[Tabs] Opening parent Account of current record - adding as subtab');
            return currentActiveTab;
          }
        }
        // Account אחר - יפתח כטאב ראשי
        console.log('[Tabs] Opening different Account - will be main tab');
        return null;
      }

      // אם אנחנו בתת-טאב של Account workspace
      if (activeSubtabId && currentActiveTab?.subtabs?.length > 0) {
        // בדוק אם הרשומה הנוכחית קשורה לחשבון שאנחנו פותחים
        if (selectedRecord) {
          const currentRecordAccountId = selectedRecord?.AccountId || selectedRecord?.Account__c;
          if (currentRecordAccountId === record?.Id) {
            console.log('[Tabs] Opening parent Account from subtab - adding as subtab');
            return currentActiveTab;
          }
        }
      }

      // חפש אם יש כבר טאב פתוח שקשור לחשבון הזה
      // (למשל, הזדמנות פתוחה שה-AccountId שלה הוא החשבון הזה)
      for (const tab of openTabs) {
        if (tab.type === 'record' && tab.objectName !== 'Account') {
          // בדוק אם לטאב הזה יש AccountId שתואם
          // נצטרך לשמור את ה-AccountId בטאב עצמו
        }
        // בדוק בתת-טאבים
        if (tab.subtabs?.length > 0) {
          for (const subtab of tab.subtabs) {
            if (subtab.accountId === record?.Id) {
              console.log('[Tabs] Found workspace with related subtab - adding Account as subtab');
              return tab;
            }
          }
        }
      }

      console.log('[Tabs] Opening Account - will be main tab');
      return null;
    }

    // לא Account - חפש טאב אב

    // אם הטאב הפעיל הוא Account - הרשומה תהיה תת-טאב שלו
    if (currentActiveTab?.type === 'record' && currentActiveTab?.objectName === 'Account') {
      console.log('[Tabs] Active tab is Account - adding as subtab');
      return currentActiveTab;
    }

    // אם אנחנו בתת-טאב של Account workspace - הוסף כאח
    if (activeSubtabId && currentActiveTab?.subtabs?.length > 0) {
      console.log('[Tabs] Currently in a subtab - adding as sibling subtab');
      return currentActiveTab;
    }

    // חפש AccountId ברשומה הנפתחת
    const accountId = record?.AccountId || record?.Account__c ||
                      record?.Account?.Id || record?.RelatedAccountId ||
                      record?.Account;

    if (accountId) {
      // חפש טאב קיים לאותו חשבון
      const accountTab = openTabs.find(tab =>
        tab.type === 'record' && tab.objectName === 'Account' && tab.recordId === accountId
      );
      if (accountTab) {
        console.log('[Tabs] Found Account tab by AccountId:', accountTab.id);
        return accountTab;
      }
    }

    // בדוק גם ברשומה הנוכחית - אם היא קשורה לחשבון שכבר פתוח
    if (selectedRecord && objectName !== 'Account') {
      const currentAccountId = selectedRecord?.AccountId || selectedRecord?.Account__c;
      if (currentAccountId) {
        const accountTab = openTabs.find(tab =>
          tab.type === 'record' && tab.objectName === 'Account' && tab.recordId === currentAccountId
        );
        if (accountTab) {
          console.log('[Tabs] Found Account tab from current record context:', accountTab.id);
          return accountTab;
        }
      }
    }

    console.log('[Tabs] No parent tab found - will be main tab');
    return null;
  };

  const navigateToRecord = async (record, obj = selectedObject) => {
    const recordId = record?.Id || record?.id;
    const objectName = obj?.api_name || obj?.name;

    // הגדר את האובייקט ועבור לתצוגת רשומה מיד
    setSelectedObject(obj);
    setShowGlobalSearch(false);

    if (recordId && objectName) {
      // נווט ל-URL החדש
      navigate(buildRecordUrl(objectName, recordId));

      const tabId = `${objectName}-${recordId}`;
      const config = getObjectConfig(objectName);
      const displayName = getRecordDisplayName(record, objectName);

      // בדוק אם יש טאב אב (חשבון)
      const parentTab = findParentTab(record, objectName);

      if (parentTab) {
        // יש טאב אב - הוסף כתת-טאב
        setOpenTabs(prev => {
          return prev.map(tab => {
            if (tab.id === parentTab.id) {
              // בדוק אם התת-טאב כבר קיים
              const existingSubtab = tab.subtabs?.find(st => st.id === tabId);
              if (existingSubtab) return tab;

              return {
                ...tab,
                subtabs: [...(tab.subtabs || []), {
                  id: tabId,
                  type: 'record',
                  objectName,
                  recordId,
                  label: displayName,
                  icon: config.icon,
                  color: config.color
                }]
              };
            }
            return tab;
          });
        });
        setActiveTabId(parentTab.id);
        setActiveSubtabId(tabId);
      } else {
        // אין טאב אב - צור טאב ראשי חדש
        setOpenTabs(prev => {
          // בדוק אם הטאב כבר קיים כטאב ראשי
          const existingTab = prev.find(t => t.id === tabId);
          if (existingTab) return prev;

          // בדוק אם הטאב קיים כתת-טאב באחד הטאבים
          for (const tab of prev) {
            if (tab.subtabs?.find(st => st.id === tabId)) {
              return prev; // כבר קיים כתת-טאב
            }
          }

          return [...prev, {
            id: tabId,
            type: 'record',
            objectName,
            recordId,
            label: displayName,
            icon: config.icon,
            color: config.color,
            subtabs: []
          }];
        });
        setActiveTabId(tabId);
        setActiveSubtabId(null);
      }

      // 1. קודם - בדוק אם הרשומה כבר ב-cache (מיידי!)
      const cachedRecord = getFromCache(objectName, recordId);
      if (cachedRecord) {
        setSelectedRecord(cachedRecord);
        return; // סיימנו - אין צורך לטעון מהשרת
      }

      // 2. הצג מה שיש מיד (אין flicker של loading) — תמיד.
      // 2026-05-17: חיפוש גלובלי מחזיר ~30 שדות, מה שעבר את הסף הקודם של
      // 5 שדות וגרם לקוד לדלג על fetch. אבל ה-detail page מצפה ל-100+
      // שדות (כולל custom __c) — השדות החסרים הוצגו כריקים והמשתמש היה
      // צריך לרענן. הפיתרון: להציג את החלקי מיד + תמיד לעדכן ברקע, כך
      // שאחרי ~200ms הרשומה המלאה מחליפה את החלקית.
      setSelectedRecord(record);
      addToCache(objectName, [record]);

      // 3. טען ברקע בלי לחסום את ה-UI — מחליף את הרשומה החלקית בתוצאה מלאה.
      recordService.getRecord(objectName, recordId)
        .then(fullRecord => {
          if (fullRecord) {
            setSelectedRecord(fullRecord);
            addToCache(objectName, [fullRecord]);
          }
        })
        .catch(err => console.error('Background record load failed:', err));
    } else {
      setSelectedRecord(record);
    }
  };

  const openSetup = (section = 'home', openInNewTab = true) => {
    setShowSettings(false);
    const url = buildSetupUrl(section === 'home' ? '' : section);
    if (openInNewTab) {
      // פתיחה בכרטיסייה חדשה
      window.open(url, '_blank');
    } else {
      setSetupSection(section);
      if (section === 'apex') loadScriptClasses();
      if (section === 'flows') loadFlows();
      navigate(url);
    }
  };

  // Context Menu (Right-click) handlers
  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      item,
      type // 'record', 'object', 'setup'
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, item: null, type: null });
  };

  const openInNewTab = () => {
    if (!contextMenu.item) return;

    let url = '';
    if (contextMenu.type === 'record') {
      const { record, object } = contextMenu.item;
      url = buildRecordUrl(object.api_name, record.Id);
    } else if (contextMenu.type === 'object') {
      url = buildObjectListUrl(contextMenu.item.api_name);
    } else if (contextMenu.type === 'setup') {
      url = buildSetupUrl(contextMenu.item.section || '');
    }

    if (url) {
      window.open(url, '_blank');
    }
    closeContextMenu();
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => closeContextMenu();
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.show]);

  // Lightning Console Tab Functions
  const closeTab = (tabId, e) => {
    if (e) e.stopPropagation();
    const tab = openTabs.find(t => t.id === tabId);
    if (tab?.pinned) return; // לא ניתן לסגור טאב מוצמד

    setOpenTabs(prev => prev.filter(t => t.id !== tabId));

    // אם הטאב הפעיל נסגר, עבור לטאב הקודם
    if (activeTabId === tabId) {
      const currentIndex = openTabs.findIndex(t => t.id === tabId);
      const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      const newActiveTab = openTabs[newIndex];
      if (newActiveTab) {
        switchToTab(newActiveTab);
      }
    }
  };

  // סגירת כל הכרטיסיות (חוץ מטאב הנווט)
  const closeAllTabs = () => {
    const navTab = openTabs.find(t => t.isNavTab) || { id: 'home', type: 'home', label: 'בית', icon: '🏠', pinned: true, subtabs: [], isNavTab: true };
    setOpenTabs([navTab]);
    setActiveTabId(navTab.id);
    setActiveSubtabId(null);
    if (navTab.type === 'home') {
      navigate('/');
      setSelectedObject(null);
    } else if (navTab.type === 'object') {
      navigate(buildObjectListUrl(navTab.objectName));
      const obj = objects.find(o => o.api_name === navTab.objectName);
      if (obj) setSelectedObject(obj);
    }
    setSelectedRecord(null);
  };

  // שינוי טאב הנווט - הטאב הראשון שמשתנה לפי בחירה מהנווט
  const setNavTab = (newNavTab) => {
    // Save to localStorage
    try {
      localStorage.setItem('navTab', JSON.stringify(newNavTab));
    } catch (e) {}

    // Update tabs - replace first tab (nav tab)
    setOpenTabs(prev => {
      const nonNavTabs = prev.filter(t => !t.isNavTab);
      return [newNavTab, ...nonNavTabs];
    });

    // Navigate and activate
    setActiveTabId(newNavTab.id);
    setActiveSubtabId(null);

    if (newNavTab.type === 'home') {
      navigate('/');
      setSelectedObject(null);
      setSelectedRecord(null);
    } else if (newNavTab.type === 'object') {
      navigate(buildObjectListUrl(newNavTab.objectName));
      const obj = objects.find(o => o.api_name === newNavTab.objectName);
      if (obj) setSelectedObject(obj);
      setSelectedRecord(null);
      // 2026-05-17: force=true so the nav-dropdown click always re-fetches.
      // Owner reported clicking "חשבונות" from the dropdown left the list
      // empty until F5; investigation showed the cache short-circuit was
      // returning before the records state ever got populated.
      loadRecords(newNavTab.objectName, null, true);
    }
  };

  const switchToTab = (tab, subtabId = null) => {
    console.log('[switchToTab] Called with:', { tabId: tab.id, tabType: tab.type, objectName: tab.objectName, recordId: tab.recordId, subtabId });
    setActiveTabId(tab.id);
    setActiveSubtabId(subtabId);

    if (subtabId) {
      // מעבר לתת-טאב
      const subtab = tab.subtabs?.find(st => st.id === subtabId);
      if (subtab) {
        navigate(buildRecordUrl(subtab.objectName, subtab.recordId));
        const obj = objects.find(o => o.api_name === subtab.objectName);
        if (obj) setSelectedObject(obj);
      }
    } else if (tab.type === 'home') {
      navigate('/');
      setSelectedObject(null);
      setSelectedRecord(null);
    } else if (tab.type === 'record') {
      navigate(buildRecordUrl(tab.objectName, tab.recordId));
      const obj = objects.find(o => o.api_name === tab.objectName);
      if (obj) setSelectedObject(obj);
    } else if (tab.type === 'object') {
      navigate(buildObjectListUrl(tab.objectName));
      const obj = objects.find(o => o.api_name === tab.objectName);
      if (obj) setSelectedObject(obj);
    }
  };

  // סגירת תת-טאב
  const closeSubtab = (tabId, subtabId, e) => {
    if (e) e.stopPropagation();

    setOpenTabs(prev => prev.map(tab => {
      if (tab.id === tabId) {
        return {
          ...tab,
          subtabs: tab.subtabs?.filter(st => st.id !== subtabId) || []
        };
      }
      return tab;
    }));

    // אם התת-טאב הפעיל נסגר, עבור לטאב הראשי
    if (activeSubtabId === subtabId) {
      setActiveSubtabId(null);
      const tab = openTabs.find(t => t.id === tabId);
      if (tab && tab.type === 'record') {
        navigate(buildRecordUrl(tab.objectName, tab.recordId));
      }
    }
  };

  const openObjectTab = (obj) => {
    const tabId = `object-${obj.api_name}`;
    const config = getObjectConfig(obj.api_name);

    setOpenTabs(prev => {
      const existingTab = prev.find(t => t.id === tabId);
      if (existingTab) return prev;
      return [...prev, {
        id: tabId,
        type: 'object',
        objectName: obj.api_name,
        label: config.heLabel || obj.label || obj.api_name,
        icon: config.icon,
        color: config.color
      }];
    });
    setActiveTabId(tabId);
    navigate(buildObjectListUrl(obj.api_name));
  };

  // Object icons and colors - modern UI framework standard blue color for all objects
  const objectConfig = {
    'Account': { icon: '🏢', color: '#0176d3', heLabel: 'חשבונות' },
    'Contact': { icon: '👤', color: '#0176d3', heLabel: 'אנשי קשר' },
    'Opportunity': { icon: '💰', color: '#0176d3', heLabel: 'הזדמנויות' },
    'OpportunityLineItem': { icon: '📋', color: '#0176d3', heLabel: 'פריטי הזדמנות' },
    'Lead': { icon: '📣', color: '#0176d3', heLabel: 'לידים' },
    'Case': { icon: '📁', color: '#0176d3', heLabel: 'מקרים' },
    'Task': { icon: '✅', color: '#0176d3', heLabel: 'משימות' },
    'Event': { icon: '📅', color: '#0176d3', heLabel: 'אירועים' },
    'Campaign': { icon: '📢', color: '#0176d3', heLabel: 'מבצעי קידום' },
    'User': { icon: '👤', color: '#0176d3', heLabel: 'משתמשים' },
    'Product2': { icon: '📦', color: '#0176d3', heLabel: 'מוצרים' },
    'Pricebook2': { icon: '💵', color: '#0176d3', heLabel: 'מחירונים' },
    'Order': { icon: '🛒', color: '#0176d3', heLabel: 'הזמנות' },
    'Contract': { icon: '📄', color: '#0176d3', heLabel: 'חוזים' },
    'Quote': { icon: '📝', color: '#0176d3', heLabel: 'הצעות מחיר' },
    'Asset': { icon: '📦', color: '#0176d3', heLabel: 'נכסים' },
    'WorkOrder': { icon: '🔧', color: '#0176d3', heLabel: 'הזמנות עבודה' },
    'ServiceAppointment': { icon: '📅', color: '#0176d3', heLabel: 'פעילות שירות' },
    'WorkOrderLineItem': { icon: '📋', color: '#0176d3', heLabel: 'פריטי הזמנת עבודה' },
    'ServiceResource': { icon: '👷', color: '#0176d3', heLabel: 'משאבי שירות' },
    'ServiceTerritory': { icon: '🗺️', color: '#0176d3', heLabel: 'אזורי שירות' },
    'ServiceTerritoryMember': { icon: '👥', color: '#0176d3', heLabel: 'חברי אזור שירות' },
    'AssignedResource': { icon: '👤', color: '#0176d3', heLabel: 'משאבים שהוקצו' },
    'ServiceContract': { icon: '📄', color: '#0176d3', heLabel: 'חוזי שירות' },
    'Attachment': { icon: '📎', color: '#0176d3', heLabel: 'קבצים מצורפים' },
    'ContentDocumentLink': { icon: '📎', color: '#0176d3', heLabel: 'קבצים' },
    'ResourceAbsence': { icon: '🚫', color: '#0176d3', heLabel: 'היעדרויות משאבים' },
    'OperatingHours': { icon: '🕐', color: '#0176d3', heLabel: 'שעות פעילות' },
    'TimeSlot': { icon: '⏰', color: '#0176d3', heLabel: 'משבצות זמן' },
    'WorkType': { icon: '🔨', color: '#0176d3', heLabel: 'סוגי עבודה' },
    'EntityHistory': { icon: '📜', color: '#0176d3', heLabel: 'היסטוריית פעילות' },
    'ProductRequired': { icon: '📦', color: '#0176d3', heLabel: 'מוצרים נדרשים' },
    'ProductRequest': { icon: '📦', color: '#0176d3', heLabel: 'בקשות מוצרים' },
    'ServiceAppointmentHistory': { icon: '📜', color: '#0176d3', heLabel: 'היסטוריית פעילות שירות' },
    'TechnicianWallet__c': { icon: '💰', color: '#0176d3', heLabel: 'ארנק טכנאי' },
    'Manufacturer__c': { icon: '🏭', color: '#0176d3', heLabel: 'יצרנים' },
    // Additional objects with Hebrew labels
    'Activity_Barak__c': { icon: '📊', color: '#0176d3', heLabel: 'פעילות ברק' },
    'Address_Change_Log__c': { icon: '📝', color: '#0176d3', heLabel: 'יומן שינוי כתובת' },
    'Auto_Order__c': { icon: '🔄', color: '#0176d3', heLabel: 'הזמנה אוטומטית' },
    'Bin_Location__c': { icon: '📍', color: '#0176d3', heLabel: 'מיקום מחסן' },
    'Bypass_Settings__c': { icon: '⚙️', color: '#0176d3', heLabel: 'הגדרות עקיפה' },
    'Location': { icon: '📍', color: '#0176d3', heLabel: 'מיקומים' },
    'ProductItem': { icon: '📦', color: '#0176d3', heLabel: 'פריטי מוצר' },
    'ProductItemTransaction': { icon: '📦', color: '#0176d3', heLabel: 'תנועות פריטי מוצר' },
    'ProductConsumed': { icon: '📦', color: '#0176d3', heLabel: 'מוצרים שנצרכו' },
    'ProductTransfer': { icon: '🔄', color: '#0176d3', heLabel: 'העברות מוצרים' },
    'SerializedProduct': { icon: '🏷️', color: '#0176d3', heLabel: 'מוצרים עם מספר סידורי' },
    'PricebookEntry': { icon: '💵', color: '#0176d3', heLabel: 'פריטי מחירון' },
    'Entitlement': { icon: '📜', color: '#0176d3', heLabel: 'זכאויות' },
    'ReturnOrder': { icon: '↩️', color: '#0176d3', heLabel: 'הזמנות החזרה' },
    'ContentVersion': { icon: '📄', color: '#0176d3', heLabel: 'גרסאות תוכן' },
    'ContentFolder': { icon: '📁', color: '#0176d3', heLabel: 'תיקיות תוכן' },
    'CampaignMember': { icon: '👥', color: '#0176d3', heLabel: 'חברי קמפיין' },
    'OpportunityContactRole': { icon: '👤', color: '#0176d3', heLabel: 'תפקידי אנשי קשר בהזדמנות' },
    'OpportunityHistory': { icon: '📜', color: '#0176d3', heLabel: 'היסטוריית הזדמנויות' },
    'UserRole': { icon: '👔', color: '#0176d3', heLabel: 'תפקידי משתמשים' },
    'OperatingHoursHoliday': { icon: '🎉', color: '#0176d3', heLabel: 'חגים בשעות פעילות' },
    'WorkCapacityLimit': { icon: '⏱️', color: '#0176d3', heLabel: 'מגבלות קיבולת עבודה' },
    // FSL Objects
    'FSL__Criteria__c': { icon: '📋', color: '#0176d3', heLabel: 'קריטריונים' },
    'FSL__FSL_Operation__c': { icon: '⚙️', color: '#0176d3', heLabel: 'פעולות FSL' },
    'FSL__Gantt_Filter__c': { icon: '📊', color: '#0176d3', heLabel: 'מסנני גאנט' },
    'FSL__Gantt_Palette__c': { icon: '🎨', color: '#0176d3', heLabel: 'פלטת גאנט' },
    'FSL__Optimization_Request__c': { icon: '🔧', color: '#0176d3', heLabel: 'בקשות אופטימיזציה' },
    'FSL__Polygon__c': { icon: '🗺️', color: '#0176d3', heLabel: 'פוליגונים' },
    'FSL__Scheduling_Policy__c': { icon: '📅', color: '#0176d3', heLabel: 'מדיניות תזמון' },
    'FSL__Scheduling_Policy_Goal__c': { icon: '🎯', color: '#0176d3', heLabel: 'יעדי מדיניות תזמון' },
    'FSL__Scheduling_Policy_Work_Rule__c': { icon: '📜', color: '#0176d3', heLabel: 'כללי עבודה במדיניות' },
    'FSL__Scheduling_Recipe__c': { icon: '📋', color: '#0176d3', heLabel: 'מתכוני תזמון' },
    'FSL__Service_Goal__c': { icon: '🎯', color: '#0176d3', heLabel: 'יעדי שירות' },
    'FSL__Territory_Optimization_Request__c': { icon: '🗺️', color: '#0176d3', heLabel: 'בקשות אופטימיזציית אזור' },
    'FSL__User_Setting__c': { icon: '⚙️', color: '#0176d3', heLabel: 'הגדרות משתמש FSL' },
    'FSL__User_Setting_Territory__c': { icon: '🗺️', color: '#0176d3', heLabel: 'הגדרות אזור משתמש' },
    'FSL__User_Territory__c': { icon: '🗺️', color: '#0176d3', heLabel: 'אזור משתמש' },
    'FSL__Work_Rule__c': { icon: '📜', color: '#0176d3', heLabel: 'כללי עבודה' },
    // Custom Objects
    'Goods_Receipt__c': { icon: '📥', color: '#0176d3', heLabel: 'קבלת סחורה' },
    'Goods_Receipt_Line__c': { icon: '📋', color: '#0176d3', heLabel: 'שורות קבלת סחורה' },
    'MGR__c': { icon: '📊', color: '#0176d3', heLabel: 'MGR' },
    'Mobile_Payment_Request__c': { icon: '📱', color: '#0176d3', heLabel: 'בקשות תשלום מובייל' },
    'Payment__c': { icon: '💳', color: '#0176d3', heLabel: 'תשלומים' },
    'Product_Movement__c': { icon: '🔄', color: '#0176d3', heLabel: 'תנועות מוצרים' },
    'Purchase_Order__c': { icon: '🛒', color: '#0176d3', heLabel: 'הזמנות רכש' },
    'Purchase_Order_Line__c': { icon: '📋', color: '#0176d3', heLabel: 'שורות הזמנת רכש' },
    'Sales_targets__c': { icon: '🎯', color: '#0176d3', heLabel: 'יעדי מכירות' },
    'Scheduler_Operation_History__c': { icon: '📜', color: '#0176d3', heLabel: 'היסטוריית פעולות מתזמן' },
    'Scheduling_Approval__c': { icon: '✅', color: '#0176d3', heLabel: 'אישורי תזמון' },
    'Service_Visit_Feedback__c': { icon: '⭐', color: '#0176d3', heLabel: 'משוב ביקור שירות' },
    'Stock_Transfer__c': { icon: '🔄', color: '#0176d3', heLabel: 'העברות מלאי' },
    'Stock_Transfer_Item__c': { icon: '📦', color: '#0176d3', heLabel: 'פריטי העברת מלאי' },
    'Technician_Capacity__c': { icon: '⏱️', color: '#0176d3', heLabel: 'קיבולת טכנאי' },
    'Technician_Pay_Scale__c': { icon: '💰', color: '#0176d3', heLabel: 'סולם שכר טכנאים' },
    'Technician_Transfer__c': { icon: '🔄', color: '#0176d3', heLabel: 'העברות טכנאים' },
    'Warehouse_Zone__c': { icon: '🏭', color: '#0176d3', heLabel: 'אזורי מחסן' },
    'Webhook_Data__c': { icon: '🔗', color: '#0176d3', heLabel: 'נתוני Webhook' },
    'Deviation__c': { icon: '⚠️', color: '#0176d3', heLabel: 'סטיות' },
    'Manufacturer_Product__c': { icon: '🏭', color: '#0176d3', heLabel: 'מוצרי יצרן' },
    'Dashboard_Rep_Preference__c': { icon: '📊', color: '#0176d3', heLabel: 'העדפות דוח לוח מחוונים' },
    // Messaging & Communication
    'MessagingSession': { icon: '💬', color: '#0176d3', heLabel: 'שיחות הודעות' },
    'ListEmail': { icon: '📧', color: '#0176d3', heLabel: 'רשימת דוא"ל' },
    'EmailRoutingAddress': { icon: '📧', color: '#0176d3', heLabel: 'כתובות ניתוב דוא"ל' },
    // Other standard objects
    'Period': { icon: '📅', color: '#0176d3', heLabel: 'תקופות' },
    'ForecastingQuota': { icon: '📊', color: '#0176d3', heLabel: 'מכסות תחזית' },
    'FlowRecord': { icon: '🔄', color: '#0176d3', heLabel: 'רשומות Flow' },
    'FlowOrchestration': { icon: '🎭', color: '#0176d3', heLabel: 'תזמורת Flow' },
  };

  // Field labels - imported from shared module (frontend/src/config/hebrewLabels.js)
  const fieldLabels = HEBREW_FIELD_LABELS;


  // Function to get Hebrew field label - uses shared module with readable fallback
  const getFieldLabel = (fieldName) => {
    if (!fieldName) return '';
    return getHebrewFieldLabel(fieldName) || fieldName
      .replace(/__c/g, '').replace(/__r/g, '').replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1').trim();
  };

  // Map from apiName (plural/relationship) to objectName (singular)
  const apiNameToObjectName = {
    // Standard plurals
    'Contacts': 'Contact',
    'Opportunities': 'Opportunity',
    'Cases': 'Case',
    'Tasks': 'Task',
    'Events': 'Event',
    'Assets': 'Asset',
    'Accounts': 'Account',
    'Leads': 'Lead',
    'OpportunityLineItems': 'OpportunityLineItem',
    'OpportunityContactRoles': 'OpportunityContactRole',
    'WorkOrders': 'WorkOrder',
    'WorkOrderLineItems': 'WorkOrderLineItem',
    'ServiceAppointments': 'ServiceAppointment',
    'AssignedResources': 'AssignedResource',
    'ServiceResources': 'ServiceResource',
    'ServiceTerritories': 'ServiceTerritory',
    'ServiceContracts': 'ServiceContract',
    'Quotes': 'Quote',
    'Payments': 'Payment__c',
    'ProductsRequired': 'ProductRequired',
    'ProductRequests': 'ProductRequest',
    'TechnicianWallets': 'TechnicianWallet__c',
    // Relationship names with __r suffix
    'Payments__r': 'Payment__c',
    'Work_Orders__r': 'WorkOrder',
    'WorkOrders__r': 'WorkOrder',
    'Service_Appointments__r': 'ServiceAppointment',
    'ServiceAppointments__r': 'ServiceAppointment',
    'Assigned_Resources__r': 'AssignedResource',
    'AssignedResources__r': 'AssignedResource',
    'Opportunity_Line_Items__r': 'OpportunityLineItem',
    'OpportunityLineItems__r': 'OpportunityLineItem',
    'Contacts__r': 'Contact',
    'Accounts__r': 'Account',
    'Cases__r': 'Case',
    'Tasks__r': 'Task',
    'Events__r': 'Event',
    'Assets__r': 'Asset',
    'Quotes__r': 'Quote',
    'TechnicianWallet__r': 'TechnicianWallet__c',
    'TechnicianWallets__r': 'TechnicianWallet__c',
    'Product_Required__r': 'ProductRequired',
    'ProductRequired__r': 'ProductRequired',
    'Product_Requests__r': 'ProductRequest',
    'ProductRequests__r': 'ProductRequest',
    'Work_Order_Line_Items__r': 'WorkOrderLineItem',
    'WorkOrderLineItems__r': 'WorkOrderLineItem',
    'AttachedContentDocuments': 'ContentDocumentLink',
  };

  // Auto-translation dictionary for common English terms
  const autoTranslateTerms = {
    'Activity': 'פעילות', 'Address': 'כתובת', 'Change': 'שינוי', 'Log': 'יומן',
    'Auto': 'אוטומטי', 'Order': 'הזמנה', 'Bin': 'תא', 'Location': 'מיקום',
    'Bypass': 'עקיפה', 'Settings': 'הגדרות', 'Product': 'מוצר', 'Item': 'פריט',
    'Transaction': 'תנועה', 'Transfer': 'העברה', 'Serialized': 'מסודר',
    'Price': 'מחיר', 'Book': 'ספר', 'Entry': 'ערך', 'Entitlement': 'זכאות',
    'Return': 'החזרה', 'Content': 'תוכן', 'Version': 'גרסה', 'Folder': 'תיקייה',
    'Campaign': 'קמפיין', 'Member': 'חבר', 'Role': 'תפקיד', 'History': 'היסטוריה',
    'User': 'משתמש', 'Operating': 'פעילות', 'Hours': 'שעות', 'Holiday': 'חג',
    'Work': 'עבודה', 'Capacity': 'קיבולת', 'Limit': 'מגבלה', 'Criteria': 'קריטריון',
    'Operation': 'פעולה', 'Gantt': 'גאנט', 'Filter': 'מסנן', 'Palette': 'פלטה',
    'Optimization': 'אופטימיזציה', 'Request': 'בקשה', 'Polygon': 'פוליגון',
    'Scheduling': 'תזמון', 'Policy': 'מדיניות', 'Goal': 'יעד', 'Rule': 'כלל',
    'Recipe': 'מתכון', 'Service': 'שירות', 'Territory': 'אזור', 'Goods': 'סחורה',
    'Receipt': 'קבלה', 'Line': 'שורה', 'Mobile': 'נייד', 'Payment': 'תשלום',
    'Movement': 'תנועה', 'Purchase': 'רכש', 'Sales': 'מכירות', 'Target': 'יעד',
    'Scheduler': 'מתזמן', 'Approval': 'אישור', 'Visit': 'ביקור', 'Feedback': 'משוב',
    'Stock': 'מלאי', 'Technician': 'טכנאי', 'Pay': 'שכר', 'Scale': 'סולם',
    'Warehouse': 'מחסן', 'Zone': 'אזור', 'Webhook': 'Webhook', 'Data': 'נתונים',
    'Deviation': 'סטייה', 'Manufacturer': 'יצרן',
    'Rep': 'נציג', 'Preference': 'העדפה', 'Messaging': 'הודעות', 'Session': 'שיחה',
    'List': 'רשימה', 'Email': 'דוא"ל', 'Routing': 'ניתוב', 'Period': 'תקופה',
    'Forecasting': 'תחזית', 'Quota': 'מכסה', 'Flow': 'זרימה', 'Record': 'רשומה',
    'Orchestration': 'תזמור', 'Resource': 'משאב', 'Absence': 'היעדרות',
    'Assigned': 'מוקצה', 'Contract': 'חוזה', 'Appointment': 'פגישה',
    'Asset': 'נכס', 'Case': 'פנייה', 'Contact': 'איש קשר', 'Account': 'חשבון',
    'Opportunity': 'הזדמנות', 'Lead': 'ליד', 'Task': 'משימה', 'Event': 'אירוע',
    'Quote': 'הצעת מחיר', 'Attachment': 'קובץ מצורף', 'Document': 'מסמך',
    'Note': 'הערה', 'Report': 'דוח', 'Dashboard': 'לוח בקרה', 'Calendar': 'לוח שנה',
    'Model': 'מודל', 'View': 'תצוגה', 'Type': 'סוג', 'Status': 'סטטוס',
    'New': 'חדש', 'Required': 'נדרש', 'Consumed': 'נצרך', 'State': 'מצב'
  };

  // Function to auto-translate object name
  const autoTranslateName = (name) => {
    // Split by underscore or camelCase
    const words = name
      .replace(/__c$/g, '')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .split('_')
      .filter(w => w.length > 0);

    const translatedWords = words.map(word => {
      const capitalWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      return autoTranslateTerms[capitalWord] || autoTranslateTerms[word] || word;
    });

    return translatedWords.join(' ');
  };

  const getObjectConfig = (apiName) => {
    if (objectConfig[apiName]) return objectConfig[apiName];

    // Try to map from plural API name to singular object name
    const singularName = apiNameToObjectName[apiName];
    if (singularName && objectConfig[singularName]) return objectConfig[singularName];

    // Lab objects, before the auto-translator gets them.
    //
    // autoTranslateName does word-level substitution, which is what produced
    // «Lab repair», «Lab repair part» and «Lab מלאי» — half-translated names
    // sitting next to «חשבונות» and «מקרים». The lookup is normalised so it
    // matches whichever spelling the caller holds: /api/objects returns
    // `Lab_repair`, the related-list resolver can produce `LabRepair`.
    const labCfg = LAB_OBJECT_CONFIG[normalizeObjectName(apiName)];
    if (labCfg) return labCfg;

    // Auto-translate for objects not in config
    const translatedName = autoTranslateName(apiName);
    return {
      icon: '📋',
      color: '#0176D3',
      heLabel: translatedName
    };
  };

  // Navigation items - default
  const defaultNavItems = [
    { id: 'home', label: 'בית', icon: '🏠', color: '#E176B3' },
    { id: 'Account', label: 'חשבונות', icon: '🏢', color: '#7F8DE1' },
    { id: 'Contact', label: 'אנשי קשר', icon: '👤', color: '#E176B3' },
    { id: 'Opportunity', label: 'הזדמנויות', icon: '💰', color: '#FCB95B' },
    { id: 'Campaign', label: 'מבצעי קידום', icon: '📢', color: '#F2CF5B' },
    { id: 'Task', label: 'משימות', icon: '✅', color: '#4BC076' },
    { id: 'Event', label: 'לוח שנה', icon: '📅', color: '#EB7092' },
  ];

  // Load saved nav items from localStorage or use defaults.
  // PR-R Phase 2 (2026-05-30): on top of localStorage, after the
  // current user is known we fetch from
  //   GET /api/nav-menu-permissions/effective/{userId}
  // If a per-user / per-profile record exists, that DB result wins
  // and overrides localStorage. If `unrestricted: true` (no record
  // applies), we keep whatever's in localStorage / defaults.
  const [navItems, setNavItems] = useState(() => {
    const saved = localStorage.getItem('salesforce_nav_items');
    return saved ? JSON.parse(saved) : defaultNavItems;
  });
  const [showNavEditor, setShowNavEditor] = useState(false);

  // Save nav items when changed
  useEffect(() => {
    localStorage.setItem('salesforce_nav_items', JSON.stringify(navItems));
  }, [navItems]);

  // PR-R Phase 2: pull DB-backed nav items for the current user on
  // login + whenever the user changes. Fail-open: any error keeps the
  // localStorage / defaults nav so the user never gets a blank nav bar.
  useEffect(() => {
    if (!currentUser) return;
    const uid = currentUser.id || currentUser.sf_user_id
      || currentUser.username || currentUser.email;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/nav-menu-permissions/effective/${encodeURIComponent(uid)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data && data.success && data.unrestricted === false
            && Array.isArray(data.nav_items) && data.nav_items.length > 0) {
          setNavItems(data.nav_items);
        }
        // else: unrestricted = keep current localStorage/defaults.
      } catch (e) {
        // Silent — keep current nav.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser && (currentUser.id || currentUser.username)]);

  // Public form route (accessible without authentication)
  if (location.pathname.startsWith('/form/')) {
    const token = location.pathname.split('/form/')[1];
    return <CustomerFormView token={token} />;
  }

  // Public appointment tracking route (accessible without authentication)
  if (location.pathname.startsWith('/appointment-tracking/')) {
    const token = location.pathname.split('/appointment-tracking/')[1];
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AppointmentTracking token={token} />
      </Suspense>
    );
  }

  // App Selector route - displayed when accessing /technician from Android TWA
  if (location.pathname === '/technician') {
    const handleAppSelect = (appType) => {
      if (appType === 'technician') {
        window.location.href = '/app';
      } else if (appType === 'afikim') {
        window.location.href = '/login';
      }
    };
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AppSelector onSelectApp={handleAppSelect} />
      </Suspense>
    );
  }

  // Technician App route (has its own authentication)
  if (location.pathname.startsWith('/app')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <TechnicianApp />
      </Suspense>
    );
  }

  // Supplier routes (have their own authentication)
  if (location.pathname === '/supplier-login' || location.pathname === '/supplier-portal') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/supplier-login" element={<SupplierLogin />} />
          <Route path="/supplier-portal" element={<SupplierPortal />} />
        </Routes>
      </Suspense>
    );
  }

  // Lab moved to its own origin and is now an installable PWA.
  //
  // A full-page redirect, not a <Route>: the Lab is a different origin, so
  // react-router cannot reach it. Anyone landing on an old /lab* URL is sent
  // to the equivalent path on the Lab host — the paths line up because the
  // Lab's router is mounted at "/" and the "/lab" prefix is now carried by
  // the hostname. /lab-login maps to /login for the same reason.
  if (location.pathname === '/lab-login' || location.pathname.startsWith('/lab')) {
    const suffix = location.pathname === '/lab-login'
      ? '/login'
      : location.pathname.replace(/^\/lab/, '') || '/';
    window.location.replace(`${LAB_APP_URL}${suffix}${location.search || ''}`);
    return <LoadingFallback />;
  }

  // The storekeepers' app (PWA): counts and van loading by pallet. Full screen, BEFORE the CRM
  // login and preload — it has its own sign-in (same account, same session) and a phone in the
  // warehouse loads only this. Behind inventory.enabled (server).
  if (location.pathname.startsWith('/warehouse-app')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        {isAuthenticated
          ? <WarehouseApp user={currentUser}
                          onLogout={async () => { await handleLogout(); navigate('/warehouse-app'); }} />
          : <WarehouseLogin onLogin={handleLogin} />}
      </Suspense>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
      </Routes>
    );
  }

  // WhatsApp Workspace — a standalone screen (owner 2026-11-02: "WhatsApp only").
  // After the login gate, BEFORE the preload screen and the CRM layout: nothing of
  // the CRM is mounted under it (it used to sit, invisible, on top of the whole
  // chrome — header, tabs, softphone + JsSIP socket, agent status, hub, themes —
  // all of which kept rendering). Calls and agent status are handled in a CRM tab.
  if (isWorkspaceRoute) {
    const workspaceShell = (
      <Suspense fallback={<LoadingFallback />}>
        <WorkspaceShell
          currentUserEmail={currentUser?.email || currentUser?.username || ''}
          currentUserProfile={currentUser?.profile || currentUser?.Profile || ''}
        />
      </Suspense>
    );
    return (
      <Routes>
        <Route path="/workspace" element={workspaceShell} />
        {/* E4.4 deep link per chat — `*` lets the JID's `@` and dots through. */}
        <Route path="/workspace/chat/*" element={workspaceShell} />
      </Routes>
    );
  }

  // Show preload screen while loading all data
  if (!preloadComplete) {
    return (
      <PreloadScreen
        progress={preloadProgress}
        message={preloadMessage}
        stats={preloadStats}
        error={preloadError}
        onRetry={handlePreloadRetry}
      />
    );
  }

  // Product Management Full-Screen Layout
  // 2026-06-01 owner-spec: route gate dropped to mirror the launcher
  // change (Shirael needs access without admin profile). Menu editor
  // is now the sole source of truth for visibility — anyone who reaches
  // this URL has already been allowed through there.
  if (location.pathname.startsWith('/products-management')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <ProductManagement currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // WhatsApp Bot Management Full-Screen Layout
  // 2026-06-12 owner-spec: AppLauncher tile bot-management used to land on
  // /setup/whatsapp-bots which dragged in the entire Setup sidebar (admin
  // settings leaked into non-admin views — שיראל saw משתמשים / פרופילים /
  // ערכות הרשאות). Mirror the products-management pattern: standalone
  // route with its own focused 3-tab sidebar. Visibility gate is the
  // AppLauncher tile permission (set in Setup → עורך תפריטים).
  if (location.pathname.startsWith('/whatsapp-bot-management')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <WhatsAppBotManagement currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Product Required Rules Full-Screen Layout
  // 2026-06-01 owner-spec: route gate dropped; menu editor is the
  // sole source of visibility (same treatment as products-management).
  if (location.pathname.startsWith('/product-required-rules')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <ProductRequiredRules currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Cash gate management — accounting decides who may keep working while holding cash.
  // Visibility is the menu editor's, like the screens above it; the API refuses anyone
  // who is not accounting regardless of what the menu shows.
  if (location.pathname.startsWith('/technician-cash-gate')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <TechnicianCashGate currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // איפוס סיסמאות — the tile is grant-required; the API refuses anyone the menu editor
  // has not granted, and resets only the four call-center profiles.
  if (location.pathname.startsWith('/password-reset')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <PasswordResetTool onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // כללי שיבוץ טכנאים — grant-required tile; the API refuses anyone the menu editor has
  // not granted (administrators always may).
  if (location.pathname.startsWith('/technician-slot-rules')) {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <TechnicianSlotRules onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Developer Integration Portal — PR #10
  // (integration_developer profile OR admin — backend re-validates)
  if (location.pathname.startsWith('/integration-portal')) {
    const isAdmin = permIsAdmin(currentUser);
    const isDeveloper = (currentUser?.profile || '').trim() === 'integration_developer';
    if (!isAdmin && !isDeveloper) {
      return <div dir="rtl" style={{ padding: '60px', textAlign: 'center', fontSize: '18px' }}>אין לך הרשאה לגשת לדף זה</div>;
    }
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <IntegrationPortal currentUser={currentUser} onBack={() => navigate('/')} onLogout={handleLogout} />
      </Suspense>
    );
  }

  // Device Replacement Allocation Dashboard Full-Screen Layout
  // (admin OR amitai@afikim-water.co.il — backend re-validates, this is UX only)
  if (location.pathname.startsWith('/device-replacement-allocation')) {
    const isAdmin = permIsAdmin(currentUser);
    const isAllocOwner = (currentUser?.email || '').toLowerCase() === 'amitai@afikim-water.co.il';
    if (!isAdmin && !isAllocOwner) {
      return <div dir="rtl" style={{ padding: '60px', textAlign: 'center', fontSize: '18px' }}>אין לך הרשאה לגשת לדף זה</div>;
    }
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <DeviceReplacementAllocationDashboard onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // iForms Product Mappings Full-Screen Layout (admin only)
  if (location.pathname.startsWith('/iforms-product-mappings')) {
    const isAdmin = permIsAdmin(currentUser);
    if (!isAdmin) {
      return <div dir="rtl" style={{ padding: '60px', textAlign: 'center', fontSize: '18px' }}>אין לך הרשאה לגשת לדף זה</div>;
    }
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <IFormsProductMappings currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Load-test live dashboard — direct URL only, admin-gated. Added for the
  // 2026-05-20 90-user load test. Powered by /api/load-test/* + the
  // system_event samplers running in the backend.
  // 2026-05-26: added /load-test/day — the full-day forensic dashboard.
  if (location.pathname.startsWith('/load-test')) {
    const isAdmin = permIsAdmin(currentUser);
    if (!isAdmin) {
      return <div dir="rtl" style={{ padding: '60px', textAlign: 'center', fontSize: '18px' }}>אין לך הרשאה לגשת לדף זה</div>;
    }
    if (location.pathname.startsWith('/load-test/day')) {
      return (
        <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
          <LoadTestDayDashboard currentUser={currentUser} onBack={() => navigate('/')} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <LoadTestDashboard currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Manager + Executive dashboards (2026-05-19).
  // Routes: /dashboard/sales-tech, /dashboard/service-tech,
  //         /dashboard/sales-center, /dashboard/init-center,
  //         /dashboard/executive.
  // Access is controlled by the owner via the menu editor; the page
  // itself is open to any authenticated user.
  // 2026-06-09: only intercept when the segment matches a known team;
  // otherwise fall through so React Router can match nested routes like
  // /dashboard/tasks/:category (PR #839). The previous behavior
  // unconditionally returned "דשבורד לא נמצא" for any unknown segment,
  // which shadowed every nested /dashboard/* route in the app.
  if (location.pathname.startsWith('/dashboard/')) {
    const team = location.pathname.replace('/dashboard/', '').replace(/\/$/, '');
    const validTeams = ['sales-tech', 'service-tech', 'sales-center', 'init-center', 'executive'];
    if (validTeams.includes(team)) {
      return (
        <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
          <ManagerDashboards team={team} currentUser={currentUser} onBack={() => navigate('/')} />
        </Suspense>
      );
    }
    // Unknown segment — fall through to React Router below.
  }

  // Warranty Renewal manager dashboard (2026-05-28). KPI cards + drilldown
  // + assignment modal + Excel export.
  if (location.pathname === '/warranty-renewal' || location.pathname === '/warranty-renewal/') {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <WarrantyRenewalDashboard currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Bounced-Checks dashboard (2026-05-29). One row per Returned check
  // across the whole org, with date / lawyer / search filters and a
  // "העברה לעו״ד" action that picks an attorney Account and (optionally)
  // bundles other open bounced checks on the same customer.
  if (location.pathname === '/bounced-checks' || location.pathname === '/bounced-checks/') {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <BouncedChecksDashboard currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Warranty Renewal — leads-assigned-to-me (user/center view, phase 4).
  // Filtered server-side by currentUser.sf_user_id.
  if (location.pathname === '/my-warranty-leads' || location.pathname === '/my-warranty-leads/') {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <WarrantyMyLeadsDashboard currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Email-to-Case manager inbox (2026-05-19). Lists cases that arrived
  // through the inbound email pipeline; supports multi-select bulk
  // assignment to team members.
  if (location.pathname === '/email-to-case' || location.pathname === '/email-to-case/') {
    return (
      <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center' }}>טוען...</div>}>
        <EmailToCaseCenter currentUser={currentUser} onBack={() => navigate('/')} />
      </Suspense>
    );
  }

  // Setup is for system admins (owner decision D6, 2026-10-14). The ⚙️ entry
  // was already admin-only, but typing /setup/... opened it for anyone. The
  // admin APIs behind each page still enforce on their own.
  if (location.pathname.startsWith('/setup') && currentUser && !permIsAdmin(currentUser)) {
    return (
      <div className="slds-scope" dir="rtl" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ margin: '0 0 8px' }}>אין הרשאה</h2>
        <p style={{ color: '#54698d', margin: '0 0 20px' }}>מסך ההגדרות זמין למנהלי מערכת בלבד.</p>
        <button className="slds-button slds-button_brand" onClick={() => navigate('/')}
          style={{ background: '#0176d3', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 4, cursor: 'pointer' }}>
          חזרה לדף הבית
        </button>
      </div>
    );
  }

  // Setup Full-Screen Layout (in a CRM Setup - dedicated full page)
  if (location.pathname.startsWith('/setup')) {
    return (
      <div className="slds-scope slds-setup-fullscreen" dir="rtl">
        {/* Login-As Banner in Setup */}
        {loginAsInfo && loginAsInfo.active && (
          <div style={{
            background: 'linear-gradient(90deg, #ff6b00, #ff8533)',
            color: '#fff',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 10000,
            position: 'relative',
          }}>
            <span>⚠️ הנך מחובר באמצעות המשתמש: {loginAsInfo.targetUser?.name || loginAsInfo.targetUser?.username || '—'}
              {loginAsInfo.targetUser?.email && <span style={{ opacity: 0.85, marginRight: 4 }}>({loginAsInfo.targetUser.email})</span>}
            </span>
            <button onClick={handleLogoutAs} style={{
              background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)',
              color: '#fff', borderRadius: 6, padding: '3px 14px', fontSize: 13,
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>🔙 חזור לאדמין</button>
          </div>
        )}
        {/* Setup Header with logo, search, tabs */}
        <div className="slds-setup-header">
          <div className="slds-setup-header__right">
            <span className="slds-setup-header__logo">אפיקים</span>
          </div>
          <div className="slds-setup-header__center">
            <SetupGlobalSearch navigate={navigate} setSetupSection={setSetupSection} />
          </div>
          <div className="slds-setup-header__left">
            <span className="slds-setup-header__user">{currentUser?.name || ''}</span>
          </div>
        </div>
        {/* Setup Nav Tabs */}
        <div className="slds-setup-nav">
          <div className="slds-setup-nav__tabs">
            <button className="slds-setup-nav__tab slds-setup-nav__tab--active" onClick={() => { navigate('/setup'); }}>
              הגדרות
            </button>
            <button className="slds-setup-nav__tab" onClick={() => navigate('/')}>
              דף הבית
            </button>
            <button className="slds-setup-nav__tab" onClick={() => {
              navigate('/setup/objects');
            }}>
              מנהל אובייקטים
            </button>
          </div>
          <div className="slds-setup-nav__right">
            <button className="slds-setup-nav__back" onClick={() => navigate('/')}>
              ← חזרה לאפליקציה
            </button>
          </div>
        </div>

        {/* Setup Body = SetupPage with sidebar + content */}
        <Routes>
          <Route path="/setup" element={
            <SetupPage
              section={setupSection || 'home'}
              setSection={(section) => {
                setSetupSection(section);
                if (section && section !== 'home') {
                  navigate(buildSetupUrl(section));
                }
              }}
              objects={objects}
              scriptClasses={scriptClasses}
              flows={flows}
              metadata={metadata}
              stats={stats}
              onObjectClick={navigateToObject}
              onScriptClick={loadScriptClass}
              onFlowClick={loadFlow}
              selectedScript={selectedScript}
              selectedFlow={selectedFlow}
              getObjectConfig={getObjectConfig}
              loadScriptClasses={loadScriptClasses}
              loadFlows={loadFlows}
              setFlexipageCache={setFlexipageCache}
              currentUser={currentUser}
            />
          } />

          <Route path="/setup/:section" element={
            <SetupSectionRoute
              setSetupSection={setSetupSection}
              loadScriptClasses={loadScriptClasses}
              loadFlows={loadFlows}
              objects={objects}
              scriptClasses={scriptClasses}
              flows={flows}
              metadata={metadata}
              stats={stats}
              navigateToObject={navigateToObject}
              loadScriptClass={loadScriptClass}
              loadFlow={loadFlow}
              selectedScript={selectedScript}
              selectedFlow={selectedFlow}
              getObjectConfig={getObjectConfig}
              navigate={navigate}
              setFlexipageCache={setFlexipageCache}
              currentUser={currentUser}
            />
          } />

          <Route path="/setup/objects/:objectName" element={
            <SetupObjectRoute
              objects={objects}
              setFlexipageCache={setFlexipageCache}
            />
          } />

          <Route path="/setup/objects/:objectName/:tab" element={
            <SetupObjectRoute
              objects={objects}
              setFlexipageCache={setFlexipageCache}
            />
          } />
        </Routes>
      </div>
    );
  }

  return (
    <div className="slds-scope slds-app slds-console-app" dir="rtl">
      {/* 2026-07-02 PR-F — System Notification blocking host. Mounted
          at the app root so a maintenance/emergency broadcast pops
          for EVERY user regardless of which route they're on. Only
          renders when there's a pending notification for the user. */}
      <SystemNotificationHost
        isAuthenticated={!!currentUser}
        sessionId={
          localStorage.getItem('session_token') ||
          sessionStorage.getItem('session_token')
        }
      />

      {/* Global Search Bar - Top with Logo and Actions */}
      <div className="slds-global-search-bar">
        {/* Logo - Right Side */}
        <div
          className="slds-global-search-bar__logo"
          onClick={() => navigate('/')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
          role="link"
          tabIndex={0}
          title="דף הבית"
          style={{ cursor: 'pointer' }}
        >
          <img src={`${API_BASE}/assets/logo.png`} alt="Afikim Water - דף הבית" />
        </div>

        {/* Search Container - Center */}
        <div className="slds-global-search-bar__container" ref={globalSearchRef}>
          <input
            type="search"
            className="slds-global-search-bar__input"
            placeholder="חיפוש באפיקים..."
            value={globalSearchTerm}
            onChange={(e) => {
              setGlobalSearchTerm(e.target.value);
              setShowGlobalSearch(true);
              triggerGlobalSearch(e.target.value);
            }}
            onClick={() => setShowGlobalSearch(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowGlobalSearch(false);
              } else if (e.key === 'Enter' && globalSearchTerm && globalSearchTerm.length >= 2) {
                addRecentSearch(globalSearchTerm);
                setShowGlobalSearch(false);
                navigate(`/search?q=${encodeURIComponent(globalSearchTerm)}`);
              }
            }}
          />

          {/* Global Search Results Dropdown */}
          {showGlobalSearch && (
            <div className="slds-global-search-dropdown">
              {/* Header */}
              <div className="slds-global-search__header">
                {globalSearchTerm ? (
                  <>תוצאות עבור: <span className="slds-global-search__header-term">"{globalSearchTerm}"</span></>
                ) : (
                  'חיפוש מהיר'
                )}
              </div>

              {/* Loading State */}
              {searchLoading && (
                <div className="slds-search-loading">
                  <div className="slds-search-loading__spinner"></div>
                  <span>מחפש בכל האובייקטים...</span>
                </div>
              )}

              {/* Recent Searches (when no search term) */}
              {!globalSearchTerm && !searchLoading && (
                <div className="slds-search-recent">
                  <div className="slds-search-recent__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>חיפושים אחרונים</span>
                    {recentSearches.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); clearRecentSearches(); }}
                        style={{ background: 'none', border: 'none', color: '#0176d3', fontSize: '12px', cursor: 'pointer', padding: 0 }}
                        title="ניקוי חיפושים אחרונים"
                      >
                        נקה
                      </button>
                    )}
                  </div>
                  {recentSearches.length > 0 ? (
                    <div className="slds-search-recent__items" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 12px' }}>
                      {recentSearches.map((term, i) => (
                        <button
                          key={`${term}-${i}`}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setGlobalSearchTerm(term);
                            triggerGlobalSearch(term);
                          }}
                          style={{
                            background: '#f3f3f3',
                            border: '1px solid #dddbda',
                            borderRadius: '14px',
                            padding: '4px 10px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            color: '#080707',
                          }}
                        >
                          🕘 {term}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '6px 12px 10px', fontSize: '12px', color: '#706e6b' }}>
                      אין חיפושים אחרונים — חפש משהו כדי להתחיל
                    </div>
                  )}
                  <div className="slds-search-instant">
                    <div className="slds-search-instant__label">חפש באובייקטים</div>
                    <div className="slds-search-instant__options">
                      {['Account', 'Contact', 'Opportunity', 'Case', 'Lead'].map(objName => {
                        const config = getObjectConfig(objName);
                        return (
                          <button
                            key={objName}
                            className="slds-search-instant__option"
                            onClick={() => {
                              const obj = objects.find(o => o.api_name === objName);
                              if (obj) {
                                navigateToObject(obj);
                                setShowGlobalSearch(false);
                              }
                            }}
                          >
                            <span className="slds-search-instant__option-icon">{config.icon}</span>
                            {config.heLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Search Results */}
              {globalSearchTerm && !searchLoading && globalSearchResults.length > 0 && (
                <div className="slds-global-search__results">
                  {globalSearchResults.map((group, idx) => {
                    const config = getObjectConfig(group.objectName);
                    return (
                      <div key={idx} className="slds-search-group">
                        <div
                          className="slds-search-group__header"
                          onClick={() => {
                            const obj = objects.find(o => o.api_name === group.objectName);
                            if (obj) {
                              navigateToObject(obj);
                              setShowGlobalSearch(false);
                            }
                          }}
                        >
                          <span className="slds-search-group__icon" style={{ backgroundColor: config.color }}>
                            {config.icon}
                          </span>
                          <span className="slds-search-group__name">{group.label}</span>
                          <span className="slds-search-group__count">{group.matchesCount}</span>
                          <span className="slds-search-group__expand">▼</span>
                        </div>
                        <div className="slds-search-results-list">
                          {group.records.map((record, ridx) => {
                            // חפש שם תצוגה - רק שם, לא מזהים
                            const getDisplayName = () => {
                              // FIRST: Check server-provided display name
                              if (record._display_name && record._display_name.trim() && record._display_name !== 'nan') {
                                return record._display_name;
                              }
                              // First try Name field
                              if (record.Name && record.Name.trim()) return record.Name;
                              // Then try combined FirstName + LastName
                              if (record.FirstName || record.LastName) {
                                const fullName = `${record.FirstName || ''} ${record.LastName || ''}`.trim();
                                if (fullName) return fullName;
                              }
                              // Other common name fields
                              if (record.Subject && record.Subject.trim()) return record.Subject;
                              if (record.Title && record.Title.trim()) return record.Title;
                              if (record.CaseNumber) return record.CaseNumber;
                              if (record.WorkOrderNumber) {
                                const num = parseFloat(record.WorkOrderNumber);
                                return isNaN(num) ? record.WorkOrderNumber : String(Math.round(num)).padStart(8, '0');
                              }
                              if (record.AppointmentNumber) return record.AppointmentNumber;
                              if (record.Username) return record.Username;
                              if (record.DeveloperName) return record.DeveloperName;
                              // Try display field from search index
                              if (record.display && record.display.trim()) return record.display;
                              return 'ללא שם';
                            };
                            const displayName = getDisplayName();

                            // חפש שדה משני לתצוגה - עם שם השדה שנמצאה בו ההתאמה
                            const getSubtitle = () => {
                              // If server returned matched field info (non-name fields), show it
                              if (record._matched_field && record._matched_value) {
                                // Filter out values that look in a CRM IDs
                                if (/^[a-zA-Z0-9]{15,18}$/.test(record._matched_value)) {
                                  // Don't show IDs as subtitle
                                } else {
                                  const fieldLabel = fieldLabels[record._matched_field] || record._matched_field.replace(/__c$/, '').replace(/_/g, ' ');
                                  return `${fieldLabel}: ${record._matched_value}`;
                                }
                              }
                              // Show useful secondary info — for accounts/contacts
                              // surface BOTH phone numbers (Phone + Another_phone__c)
                              // on separate lines so the dropdown reflects the
                              // full contact info, not just one number.
                              // Owner-spec 2026-06-16: append an address line
                              // (BillingStreet, BillingCity) under the phones
                              // so the operator can disambiguate two same-named
                              // accounts at a glance.
                              const phoneLines = [];
                              if (record.Phone) phoneLines.push(`טלפון: ${record.Phone}`);
                              if (record.Another_phone__c) phoneLines.push(`טלפון נוסף: ${record.Another_phone__c}`);
                              const addressParts = [record.BillingStreet, record.BillingCity]
                                .map(v => (v || '').trim()).filter(Boolean);
                              if (addressParts.length) phoneLines.push(`📍 ${addressParts.join(', ')}`);
                              if (phoneLines.length) return phoneLines;
                              if (record.Email) return record.Email;
                              // Show combined name for Person Accounts
                              const fullName = [record.FirstName, record.LastName].filter(Boolean).join(' ').trim();
                              if (fullName && fullName !== displayName) return `שם: ${fullName}`;
                              if (record.Account__Name || record.AccountName) {
                                const acctName = record.Account__Name || record.AccountName;
                                if (!/^[a-zA-Z0-9]{15,18}$/.test(acctName)) return `חשבון: ${acctName}`;
                              }
                              if (record.Company) return record.Company;
                              if (record.Type) return record.Type;
                              if (record.Status) return record.Status;
                              return '';
                            };
                            const subtitle = getSubtitle();

                            return (
                              <a
                                key={ridx}
                                href={`/object/${group.objectName}/${record.Id}`}
                                className="slds-search-result"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.preventDefault();
                                  addRecentSearch(globalSearchTerm);
                                  setShowGlobalSearch(false);
                                  const obj = objects.find(o => o.api_name === group.objectName);
                                  navigateToRecord(record, obj);
                                }}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                              >
                                <span className="slds-search-result__icon" style={{ backgroundColor: config.color }}>
                                  {config.icon}
                                </span>
                                <div className="slds-search-result__content">
                                  <span className="slds-search-result__name">
                                    {displayName}
                                    {/* Owner-spec 2026-06-16: Account results
                                        marked with 💰 (has Opp) / 📦 (has Asset).
                                        Icons swapped 2026-06-16 evening from
                                        💼/🔧 because owner read them as Work-
                                        Order/Service-Repair instead of Opp/Asset.
                                        💰 (money bag) signals a deal/Opp, 📦
                                        (package) signals an owned product. */}
                                    {record._has_opportunity && (
                                      <span title="קיימת הזדמנות מקושרת" style={{ marginInlineStart: 6, fontSize: 12 }}>💰</span>
                                    )}
                                    {record._has_asset && (
                                      <span title="קיים נכס מקושר" style={{ marginInlineStart: 4, fontSize: 12 }}>📦</span>
                                    )}
                                  </span>
                                  {subtitle && (Array.isArray(subtitle)
                                    ? subtitle.map((line, i) => (
                                        <span key={i} className="slds-search-result__subtitle" style={{ display: 'block' }}>{line}</span>
                                      ))
                                    : <span className="slds-search-result__subtitle">{subtitle}</span>
                                  )}
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty State */}
              {globalSearchTerm && !searchLoading && globalSearchResults.length === 0 && (
                <div className="slds-search-empty">
                  <div className="slds-search-empty__icon">🔔</div>
                  <div className="slds-search-empty__text">לא נמצאו תוצאות עבור "{globalSearchTerm}"</div>
                  <div className="slds-search-empty__hint">נסה לחפש עם מילים אחרות או בדוק את האיות</div>
                </div>
              )}

              {/* View All Results */}
              {globalSearchTerm && globalSearchResults.length > 0 && (
                <button
                  className="slds-search-view-all"
                  onClick={() => {
                    setShowGlobalSearch(false);
                    navigate(`/search?q=${encodeURIComponent(globalSearchTerm)}`);
                  }}
                >
                  הצג את כל התוצאות עבור "{globalSearchTerm}"
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions - Left Side */}
        <div className="slds-global-search-bar__actions">
          {/* AFIKIM Ledes Button */}
          <button
            className={`afikim-ledes-btn ${location.pathname === '/leads' || location.pathname.startsWith('/leads/') ? 'active' : ''}`}
            title="לידים"
            onClick={() => navigate('/leads')}
          >
            <img
              src={`${process.env.PUBLIC_URL}/afikim_ledes_logo.png`}
              alt=""
              className="afikim-ledes-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '💗';
              }}
            />
          </button>

          {/* 2026-05-15: Sync button — clears server cache + reloads the
              current view's data. Useful when records are edited in
              another tab/session or when search shows stale results. */}
          <button
            className="slds-global-search-bar__button"
            title="סנכרן נתונים"
            onClick={async (ev) => {
              const btn = ev.currentTarget;
              const origHTML = btn.innerHTML;
              btn.disabled = true;
              try {
                btn.innerHTML = '<span style="font-size:18px;line-height:1">⏳</span>';
                const r = await fetch(`${API_BASE}/api/cache/clear`, { method: 'POST' });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                btn.innerHTML = '<span style="font-size:18px;line-height:1">✓</span>';
                // Cheapest reliable refresh across every page (record,
                // list, setup, ...). Cache is cleared on the worker
                // that handled the click; the page reload re-fetches
                // fresh data from PG via the now-empty cache.
                setTimeout(() => { window.location.reload(); }, 400);
              } catch (e) {
                console.error('[Sync] failed:', e);
                btn.innerHTML = origHTML;
                btn.disabled = false;
                alert('סנכרון נכשל: ' + (e.message || e));
              }
            }}
          >
            <span style={{ fontSize: '20px', lineHeight: 1 }}>🔄</span>
          </button>

          {/* My Tasks Button */}
          <button
            className="slds-global-search-bar__button my-tasks-header-btn"
            title="המשימות שלי"
            onClick={() => { setShowMyTasks(!showMyTasks); setShowNotifications(false); setShowSettings(false); setShowUserMenu(false); }}
            style={{ position: 'relative' }}
          >
            <span style={{ fontSize: '20px', lineHeight: 1 }}>📋</span>
            {myTasksCount > 0 && (
              <span className="slds-notification-badge my-tasks-badge">{myTasksCount > 99 ? '99+' : myTasksCount}</span>
            )}
          </button>

          {/* Settings - Only for system admin */}
          {permIsAdmin(currentUser) && (
            <button
              className="slds-global-search-bar__button"
              title="הגדרות מערכת"
              onClick={() => setShowSettings(!showSettings)}
            >
              ⚙️
            </button>
          )}

          {/* Admin-only system status dot. Polled from /api/health every
              10 s. Red = backend unreachable / error; orange = cache still
              building or initializing; green = ready. Tooltip surfaces the
              build progress when applicable. */}
          {systemStatus && (() => {
            const s = systemStatus.status;
            const isError = s === 'error' || systemStatus._network_error;
            const isBuilding = s === 'building' || s === 'initializing';
            const color = isError ? '#c23934' : isBuilding ? '#fe9339' : '#2e844a';
            const label = isError
              ? (systemStatus._network_error ? 'השרת לא נגיש (Restart?)' : `שגיאה: ${systemStatus.error || 'unknown'}`)
              : isBuilding
                ? (() => {
                    const p = systemStatus.cache_progress || {};
                    const loaded = p.tables_loaded;
                    const total = p.tables_total;
                    const cur = p.current_table;
                    const counter = (loaded != null && total != null) ? ` (${loaded}/${total})` : '';
                    return `המערכת בונה אינדקס${counter}${cur ? ` — ${cur}` : ''}`;
                  })()
                : 'המערכת מוכנה לעבודה';
            return (
              <div
                title={label}
                aria-label={label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 6px',
                  cursor: 'help',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '11px',
                  height: '11px',
                  borderRadius: '50%',
                  background: color,
                  boxShadow: `0 0 6px ${color}cc, 0 0 2px ${color}`,
                  border: '1px solid rgba(0,0,0,0.08)',
                  transition: 'background 0.3s ease, box-shadow 0.3s ease',
                }} />
              </div>
            );
          })()}

          {/* Notifications - next to settings */}
          <button
            className="slds-global-search-bar__button"
            title="התראות"
            onClick={() => {
              const willShow = !showNotifications;
              setShowNotifications(willShow);
              if (willShow) fetchCrmNotifications();
            }}
          >
            🔔
            {(unreadNotifCount + tasks.length) > 0 && (
              <span className="slds-notification-badge">{unreadNotifCount + tasks.length}</span>
            )}
          </button>

          <button
            className="slds-global-search-bar__button slds-global-search-bar__avatar"
            title="פרופיל משתמש"
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0176d3 0%, #032d60 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Login-As Banner */}
      {loginAsInfo && loginAsInfo.active && (
        <div style={{
          background: 'linear-gradient(90deg, #ff6b00, #ff8533)',
          color: '#fff',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 10000,
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          direction: 'rtl',
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            הנך מחובר באמצעות המשתמש: {loginAsInfo.targetUser?.name || loginAsInfo.targetUser?.username || '—'}
            {loginAsInfo.targetUser?.email && <span style={{ opacity: 0.85, marginRight: 4 }}>({loginAsInfo.targetUser.email})</span>}
          </span>
          <button
            onClick={handleLogoutAs}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.5)',
              color: '#fff',
              borderRadius: 6,
              padding: '3px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginRight: 8,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.35)'}
            onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            🔙 חזור לאדמין
          </button>
        </div>
      )}

      {/* 2026-05-18: Non-blocking password-change reminder banner.
          Shows when the session says mustChangePassword=true. Dismiss is
          per-session (X button) — the underlying flag stays TRUE until
          the user actually changes the password, at which point the
          banner won't re-appear on next login either. */}
      {currentUser?.mustChangePassword && !pwdBannerDismissed && (
        <div
          dir="rtl"
          style={{
            background: '#fff4ce', borderBottom: '1px solid #f4d03f',
            color: '#7d6608', padding: '10px 18px', fontSize: '14px',
            fontWeight: 500, display: 'flex', alignItems: 'center', gap: '12px',
            justifyContent: 'space-between',
          }}
        >
          <div>
            🔑 הסיסמה שלך היא סיסמה ראשונית. מומלץ להחליף לסיסמה אישית.
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => { setCpCurrent(''); setCpNew(''); setCpConfirm(''); setCpError(''); setShowChangePasswordModal(true); }}
              style={{
                padding: '6px 16px', background: '#0176d3', color: '#fff',
                border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
              }}
            >
              החלף סיסמה
            </button>
            <button
              onClick={() => setPwdBannerDismissed(true)}
              title="הסתר עד התחברות הבאה"
              style={{
                padding: '4px 10px', background: 'transparent', color: '#7d6608',
                border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* 2026-05-18 → 2026-05-19: change-password modal. Auto-opens when
          currentUser.mustChangePassword is true. **BLOCKING** when the
          user logged in with an initial/bulk-reset password — no
          backdrop close, no ✕, no ביטול. Closes only after a
          successful change (success handler flips mustChangePassword
          to false in state + storage). When the modal is opened
          voluntarily (i.e. mustChangePassword is already false — user
          opens it themselves from a setting), all the dismiss
          affordances stay. */}
      {showChangePasswordModal && (() => {
        const blocking = !!currentUser?.mustChangePassword;
        return (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: blocking ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => { if (!blocking && !cpSubmitting) setShowChangePasswordModal(false); }}
        >
          <div
            dir="rtl"
            style={{
              background: '#fff', width: '420px', maxWidth: '94vw',
              borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              fontFamily: '"Assistant", "Heebo", system-ui, sans-serif',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e5ea', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#16325c' }}>
                🔑 {blocking ? 'החלפת סיסמה ראשונית — חובה' : 'החלפת סיסמה'}
              </h3>
              {!blocking && (
                <button
                  onClick={() => !cpSubmitting && setShowChangePasswordModal(false)}
                  style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#54698d' }}
                >
                  ✕
                </button>
              )}
            </div>
            {blocking && (
              <div style={{ padding: '10px 20px', background: '#fff4ce', borderBottom: '1px solid #f4d03f', color: '#7d6608', fontSize: '13px' }}>
                🔒 הסיסמה הנוכחית שלך היא סיסמה ראשונית מאיפוס מרוכז. יש לבחור סיסמה חדשה כדי להמשיך לעבוד במערכת.
              </div>
            )}
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#16325c', fontWeight: 500 }}>סיסמה נוכחית</label>
                <input
                  type="password" value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dddbda', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#16325c', fontWeight: 500 }}>סיסמה חדשה (לפחות 8 תווים)</label>
                <input
                  type="password" value={cpNew} onChange={(e) => setCpNew(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dddbda', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#16325c', fontWeight: 500 }}>אישור סיסמה חדשה</label>
                <input
                  type="password" value={cpConfirm} onChange={(e) => setCpConfirm(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #dddbda', borderRadius: '6px', boxSizing: 'border-box' }}
                />
              </div>
              {cpError && (
                <div style={{ background: '#feeded', color: '#ba0517', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', border: '1px solid #ffb3b3' }}>
                  {cpError}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e5ea', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {!blocking && (
                <button
                  onClick={() => setShowChangePasswordModal(false)}
                  disabled={cpSubmitting}
                  style={{ padding: '8px 16px', background: '#fff', color: '#181818', border: '1px solid #dddbda', borderRadius: '6px', cursor: 'pointer' }}
                >
                  ביטול
                </button>
              )}
              <button
                disabled={cpSubmitting || cpNew.length < 8 || cpNew !== cpConfirm || !cpCurrent}
                onClick={async () => {
                  setCpError('');
                  if (cpNew !== cpConfirm) { setCpError('הסיסמאות לא תואמות'); return; }
                  if (cpNew.length < 8)   { setCpError('הסיסמה החדשה חייבת להיות באורך 8 תווים לפחות'); return; }
                  setCpSubmitting(true);
                  try {
                    const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || '';
                    const r = await fetch(`${config.api.baseUrl}/api/auth/change-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}) },
                      body: JSON.stringify({ currentPassword: cpCurrent, newPassword: cpNew }),
                    });
                    const j = await r.json().catch(() => ({}));
                    if (!r.ok || j?.success === false) {
                      setCpError(j?.detail || j?.error || `שגיאה (${r.status})`);
                      return;
                    }
                    // Success — clear the flag locally so the banner disappears.
                    setCurrentUser((u) => u ? { ...u, mustChangePassword: false } : u);
                    // Mirror to localStorage/sessionStorage so a reload doesn't
                    // re-show the banner if the user got moved off this page.
                    try {
                      const storage = localStorage.getItem('salesforce_user') ? localStorage : sessionStorage;
                      const u = JSON.parse(storage.getItem('salesforce_user') || '{}');
                      u.mustChangePassword = false;
                      storage.setItem('salesforce_user', JSON.stringify(u));
                    } catch { /* swallow */ }
                    setShowChangePasswordModal(false);
                  } catch (e) {
                    setCpError('שגיאה: ' + (e.message || e));
                  } finally {
                    setCpSubmitting(false);
                  }
                }}
                style={{
                  padding: '8px 22px',
                  background: (cpSubmitting || cpNew.length < 8 || cpNew !== cpConfirm || !cpCurrent) ? '#c9c7c5' : '#0176d3',
                  color: '#fff', border: 'none', borderRadius: '6px',
                  cursor: cpSubmitting ? 'not-allowed' : 'pointer', fontWeight: 600,
                }}
              >
                {cpSubmitting ? 'משנה...' : 'שמור סיסמה'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Global Header - Lightning Console Style */}
      <header className="slds-global-header slds-console-header" style={{ background: '#ffffff', borderBottom: '1px solid #dddbda' }}>
        {/* App Launcher */}
        <button
          className="slds-global-header__button slds-global-header__waffle"
          onClick={() => setShowAppLauncher(!showAppLauncher)}
          title="משגר היישומים"
          style={{ background: 'transparent' }}
        >
          <div className="slds-waffle">
            {[...Array(9)].map((_, i) => <span key={i} style={{ background: '#3e3e3c' }}></span>)}
          </div>
        </button>

        {/* App Name */}
        <div
          className="slds-global-header__app-name"
          onClick={() => navigate('/')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
          role="link"
          tabIndex={0}
          title="דף הבית"
          style={{ color: '#181818', cursor: 'pointer' }}
        >
          <span className="slds-app-name">אפיקים מים</span>
        </div>

        {/* Lightning Console Tabs Bar - Right to Left */}
        <div className="slds-console-tabs-bar">
          {openTabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {/* טאב קבוע ראשון */}
              <a
                href={tab.type === 'record' && tab.objectName && tab.recordId ? `/object/${tab.objectName}/${tab.recordId}`
                    : tab.type === 'object' && tab.objectName ? `/object/${tab.objectName}` : '/'}
                className={`slds-console-tab ${activeTabId === tab.id ? 'slds-is-active' : ''} ${tab.subtabs?.length > 0 ? 'has-subtabs' : ''}`}
                onClick={(e) => { e.preventDefault(); switchToTab(tab); }}
              >
                <span className="slds-console-tab__icon">
                  {tab.icon}
                </span>
                <span className="slds-console-tab__label">{tab.label}</span>
                {!tab.pinned && (
                  <button
                    className="slds-console-tab__close"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeTab(tab.id, e); }}
                    title="סגור"
                  >
                    ✕
                  </button>
                )}
              </a>
              {/* אחרי הטאב הקבוע הראשון - קו, חץ, קו */}
              {index === 0 && (
                <div style={{ display: 'flex', alignItems: 'center' }} ref={navDropdownAnchorRef}>
                  {/* קו מפריד עבה מימין */}
                  <div style={{
                    width: '2px',
                    height: '24px',
                    background: '#706e6b'
                  }}></div>
                  {/* חץ */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!showNavMenu) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setNavMenuPosition({
                          top: rect.bottom + 2,
                          right: window.innerWidth - rect.right
                        });
                      }
                      setShowNavMenu(!showNavMenu);
                    }}
                    style={{
                      color: '#706e6b',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: showNavMenu ? '#f3f3f3' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '0'
                    }}
                    title="בחר אובייקט"
                  >
                    ▼
                  </button>
                  {/* קו מפריד עבה משמאל */}
                  <div style={{
                    width: '2px',
                    height: '24px',
                    background: '#706e6b'
                  }}></div>
                </div>
              )}
            </React.Fragment>
          ))}
          {openTabs.length > 1 && (
            <button
              className="slds-console-tab-close-all"
              onClick={closeAllTabs}
              title="סגור את כל הכרטיסיות"
            >
              ✕
            </button>
          )}
        </div>

      </header>

      {/* Object Selection Dropdown with Scroll */}
      {showNavMenu && createPortal(
        (() => {
          // Get current nav tab to highlight selected item
          const currentNavTab = openTabs.find(t => t.isNavTab);
          const currentObjectName = currentNavTab?.objectName || (currentNavTab?.type === 'home' ? 'home' : null);

          return (
            <>
            {/* Click-outside backdrop inside the portal for correct stacking context */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 8999, background: 'transparent' }} onClick={() => setShowNavMenu(false)} />
            <div
              className="sf-nav-dropdown"
              style={{
                position: 'fixed',
                top: `${navMenuPosition.top}px`,
                right: `${navMenuPosition.right}px`,
                zIndex: 9000,
                width: '220px',
                maxHeight: '400px',
                background: '#ffffff',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16)',
                border: '1px solid #dddbda',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Scrollable list with modern CRM UX scrollbar */}
              <div
                className="sf-nav-dropdown__list"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '4px 0',
                  direction: 'rtl'
                }}
              >
                {/* Home option - always first */}
                <a
                  href="/"
                  className="sf-nav-dropdown__item"
                  onClick={(e) => {
                    e.preventDefault();
                    setNavTab({ id: 'home', type: 'home', label: 'בית', icon: '🏠', pinned: true, subtabs: [], isNavTab: true });
                    setShowNavMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: currentObjectName === 'home' ? '#d8edff' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRight: currentObjectName === 'home' ? '3px solid #0176d3' : '3px solid transparent',
                    textDecoration: 'none', color: 'inherit'
                  }}
                  onMouseEnter={(e) => { if (currentObjectName !== 'home') e.currentTarget.style.background = '#f3f3f3'; }}
                  onMouseLeave={(e) => { if (currentObjectName !== 'home') e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '4px',
                    backgroundColor: '#0176d3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    flexShrink: 0
                  }}>
                    🏠
                  </span>
                  <span style={{ color: '#181818', fontSize: '13px', fontWeight: currentObjectName === 'home' ? '600' : '400', flex: 1, textAlign: 'right' }}>בית</span>
                </a>
                {/* Object options */}
                {navItems.filter(item => item.id !== 'home' && !isObjectHidden(hiddenObjects, item.id)).map((item) => {
                  const config = getObjectConfig(item.id);
                  const isSelected = currentObjectName === item.id;
                  return (
                    <a
                      key={item.id}
                      href={`/object/${item.id}`}
                      className="sf-nav-dropdown__item"
                      onClick={(e) => {
                        e.preventDefault();
                        const obj = objects.find(o => o.api_name === item.id);
                        if (obj) {
                          setNavTab({
                            id: `nav-${item.id}`,
                            type: 'object',
                            objectName: item.id,
                            label: item.label,
                            icon: config.icon,
                            color: config.color,
                            pinned: true,
                            subtabs: [],
                            isNavTab: true
                          });
                        }
                        setShowNavMenu(false);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: isSelected ? '#d8edff' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'right',
                        direction: 'rtl',
                        borderRight: isSelected ? '3px solid #0176d3' : '3px solid transparent',
                        textDecoration: 'none', color: 'inherit'
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f3f3f3'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '4px',
                        backgroundColor: config.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>
                        {config.icon}
                      </span>
                      <span style={{ color: '#181818', fontSize: '13px', fontWeight: isSelected ? '600' : '400', flex: 1, textAlign: 'right' }}>{item.label}</span>
                    </a>
                  );
                })}
              </div>
              {/* Footer with edit button */}
              <div style={{
                padding: '8px 12px',
                borderTop: '1px solid #dddbda',
                background: '#fafaf9'
              }}>
                <button
                  onClick={() => { setShowNavEditor(true); setShowNavMenu(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '6px 12px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#0176d3',
                    fontSize: '13px',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f3f3'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  עריכה
                </button>
              </div>
              {/* modern CRM UX scrollbar CSS */}
              <style>{`
                .sf-nav-dropdown__list::-webkit-scrollbar {
                  width: 8px;
                }
                .sf-nav-dropdown__list::-webkit-scrollbar-track {
                  background: #f3f3f3;
                }
                .sf-nav-dropdown__list::-webkit-scrollbar-thumb {
                  background: #0176d3;
                  border-radius: 4px;
                }
                .sf-nav-dropdown__list::-webkit-scrollbar-thumb:hover {
                  background: #014486;
                }
              `}</style>
            </div>
            </>
          );
        })(),
        document.body
      )}

      {/* Sub-Tabs Bar - מוצג מתחת ל-header כשיש טאב פעיל עם תת-טאבים */}
      {(() => {
        const activeTab = openTabs.find(t => t.id === activeTabId);
        if (!activeTab || !activeTab.subtabs?.length) return null;

        // חישוב המיקום של הכרטיסייה הפעילה
        const activeTabIndex = openTabs.findIndex(t => t.id === activeTabId);

        return (
          <div className="slds-console-subtabs-bar">
            {/* Spacer להתאמת תת-הכרטיסיות מתחת לכרטיסיית האב */}
            <div className="slds-subtabs-connector" style={{ display: 'flex', alignItems: 'center' }}>
              {/* קו מחבר שיורד מכרטיסיית האב */}
              <div className="slds-subtabs-connector-line"></div>
              {/* הכרטיסייה הראשית (parent) */}
              <div
                className={`slds-console-subtab slds-console-subtab--parent ${!activeSubtabId ? 'slds-is-active' : ''}`}
                onClick={() => switchToTab(activeTab, null)}
              >
                <span className="slds-console-subtab__icon">
                  {activeTab.icon}
                </span>
                <span className="slds-console-subtab__label">{activeTab.label}</span>
              </div>
              {/* תת-טאבים */}
              {activeTab.subtabs.map((subtab) => (
                <div
                  key={subtab.id}
                  className={`slds-console-subtab ${activeSubtabId === subtab.id ? 'slds-is-active' : ''}`}
                  onClick={() => switchToTab(activeTab, subtab.id)}
                >
                  <span className="slds-console-subtab__icon">
                    {subtab.icon}
                  </span>
                  <span className="slds-console-subtab__label">{subtab.label}</span>
                  <button
                    className="slds-console-subtab__close"
                    onClick={(e) => closeSubtab(activeTab.id, subtab.id, e)}
                    title="סגור"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="slds-notifications-dropdown" style={{ width: 420, maxHeight: 520 }}>
          <div className="slds-notifications__header">
            <h3>התראות</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {unreadNotifCount > 0 && (
                <button onClick={markAllNotifsRead} style={{ fontSize: 11, color: '#0176d3', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  סמן הכל כנקרא
                </button>
              )}
              <button onClick={() => setShowNotifications(false)}>✕</button>
            </div>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e5e5e5', padding: '0 12px', background: '#fafaf9' }}>
            {[
              { key: 'all', label: 'הכל' },
              { key: 'transfers', label: 'העברות' },
              { key: 'discounts', label: 'הנחות' },
              { key: 'tasks', label: 'משימות' }
            ].map(tab => (
              <button key={tab.key} onClick={() => setNotifTab(tab.key)} style={{
                padding: '8px 14px', fontSize: 12, fontWeight: notifTab === tab.key ? 700 : 400,
                color: notifTab === tab.key ? '#0176d3' : '#706e6b', background: 'none', border: 'none',
                borderBottom: notifTab === tab.key ? '2px solid #0176d3' : '2px solid transparent',
                marginBottom: -2, cursor: 'pointer', fontFamily: 'inherit'
              }}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="slds-notifications__content" style={{ maxHeight: 360, overflowY: 'auto' }}>
            {/* CRM Notifications (transfers + discounts) */}
            {(notifTab === 'all' || notifTab === 'transfers' || notifTab === 'discounts') && crmNotifications
              .filter(n => {
                if (notifTab === 'transfers') return n.type?.includes('transfer');
                if (notifTab === 'discounts') return n.type?.includes('discount');
                return true;
              })
              .map(notif => {
                const isTransfer = notif.type?.includes('transfer');
                const isDiscount = notif.type?.includes('discount');
                const icon = isTransfer ? '💰' : isDiscount ? '🏷️' : '📋';
                const isUnread = notif.status === 'unread';
                return (
                  <div key={`crm-${notif.id}`}
                    className={`slds-notification-item ${isUnread ? 'slds-notif-item--unread' : ''}`}
                    onClick={() => handleNotifClick(notif)}
                    style={{
                      background: isUnread ? '#f0f7ff' : 'transparent',
                      fontWeight: isUnread ? 600 : 400,
                      borderRight: isUnread ? '3px solid #0176d3' : '3px solid transparent',
                      cursor: 'pointer'
                    }}
                  >
                    <span className="slds-notification-icon" style={{ fontSize: 18 }}>{icon}</span>
                    <div className="slds-notification-text" style={{ flex: 1 }}>
                      <strong style={{ fontSize: 13 }}>{notif.title || 'התראה'}</strong>
                      <span style={{ fontSize: 11, color: '#706e6b', display: 'block' }}>{notif.message || ''}</span>
                      <span style={{ fontSize: 10, color: '#b0adab' }}>
                        {notif.created_at ? new Date(notif.created_at).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    {notif.status === 'actioned' && (
                      <span style={{ fontSize: 10, color: '#2e844a', fontWeight: 600 }}>
                        {notif.action_taken === 'approved' ? '✅' : notif.action_taken === 'rejected' ? '❌' : '✏️'}
                      </span>
                    )}
                  </div>
                );
              })}
            {/* Tasks tab */}
            {(notifTab === 'all' || notifTab === 'tasks') && tasks.slice(0, 10).map((task, idx) => (
              <div key={`task-${idx}`} className="slds-notification-item" onClick={() => {
                navigateToRecord(task, objects.find(o => o.api_name === 'Task'));
                setShowNotifications(false);
              }}>
                <span className="slds-notification-icon">✓</span>
                <div className="slds-notification-text">
                  <strong>{task.Subject || 'משימה'}</strong>
                  <span>{task.Status || 'לא הוקצה'}</span>
                </div>
              </div>
            ))}
            {/* Empty state */}
            {crmNotifications.length === 0 && tasks.length === 0 && (
              <div className="slds-notifications-empty">
                <span>📭</span>
                <p>אין התראות חדשות</p>
              </div>
            )}
          </div>
          <div className="slds-notifications__footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px' }}>
            <button onClick={() => {
              setShowManagerWallet(true);
              setShowNotifications(false);
              loadManagerWallet();
            }} style={{ fontSize: 12, color: '#0176d3', background: 'none', border: 'none', cursor: 'pointer' }}>
              ארנק מנהל
            </button>
            <button onClick={() => {
              const taskObj = objects.find(o => o.api_name === 'Task');
              if (taskObj) navigateToObject(taskObj);
              setShowNotifications(false);
            }} style={{ fontSize: 12, color: '#0176d3', background: 'none', border: 'none', cursor: 'pointer' }}>
              כל המשימות
            </button>
          </div>
        </div>
      )}

      {/* My Tasks Dropdown */}
      {showMyTasks && (
        <div className="my-tasks-dropdown">
          <div className="my-tasks-dropdown__header">
            <h3>{myTasksTab === 'mine' ? 'המשימות שלי' : 'מעקב משימות'}</h3>
            <button onClick={() => setShowMyTasks(false)}>✕</button>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e5e5e5' }}>
            <button onClick={() => setMyTasksTab('mine')} style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              background: myTasksTab === 'mine' ? '#fff' : '#f7f7f7',
              color: myTasksTab === 'mine' ? '#0176d3' : '#706e6b',
              borderBottom: myTasksTab === 'mine' ? '2px solid #0176d3' : '2px solid transparent',
              marginBottom: '-2px'
            }}>משימות שלי</button>
            <button onClick={() => setMyTasksTab('assigned')} style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              background: myTasksTab === 'assigned' ? '#fff' : '#f7f7f7',
              color: myTasksTab === 'assigned' ? '#0176d3' : '#706e6b',
              borderBottom: myTasksTab === 'assigned' ? '2px solid #0176d3' : '2px solid transparent',
              marginBottom: '-2px'
            }}>מעקב משימות</button>
          </div>
          <div className="my-tasks-dropdown__content">
            {myTasksTab === 'assigned' ? (
              /* === ASSIGNED BY ME TAB === */
              (() => {
                const isCompleted = (s) => s === 'Completed' || s === 'סיום טיפול בשביעות רצון' || s === 'סיום טיפול לא בשביעות רצון' || s === 'הושלם';
                const sorted = [...assignedByMeTasks].sort((a, b) => {
                  const aComp = isCompleted(a.Status) ? 1 : 0;
                  const bComp = isCompleted(b.Status) ? 1 : 0;
                  if (aComp !== bComp) return aComp - bComp;
                  return (b.ActivityDate || '').localeCompare(a.ActivityDate || '');
                });
                const statusLabel = (s) => {
                  if (s === 'Not Started') return 'לא התחיל';
                  if (s === 'In Progress') return 'בתהליך';
                  if (s === 'Completed') return 'הושלם';
                  if (s === 'Waiting on someone else') return 'ממתין';
                  if (s === 'Deferred') return 'נדחה';
                  return s || '';
                };
                return sorted.length > 0 ? sorted.slice(0, 15).map((task, idx) => {
                  const done = isCompleted(task.Status);
                  return (
                  <div key={task.Id || idx} className="my-tasks-dropdown__item" style={done ? { background: '#f6faf7', opacity: 0.75 } : {}} onClick={() => {
                    navigateToRecord(task, objects.find(o => o.api_name === 'Task'));
                    setShowMyTasks(false);
                  }}>
                    <div className="my-tasks-item__checkbox">
                      {done ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', background: '#2e844a', borderRadius: '50%', color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>✓</span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', border: '2px solid #b0adab', borderRadius: '50%' }}></span>
                      )}
                    </div>
                    <div className="my-tasks-item__content" style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div className="my-tasks-item__subject" style={done ? { textDecoration: 'line-through', color: '#706e6b' } : {}}>{task.Subject || 'משימה'}</div>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', flexShrink: 0, whiteSpace: 'nowrap',
                          background: done ? '#d4f7dc' : task.Status === 'In Progress' ? '#d4edfc' : task.Status === 'Not Started' ? '#fef3cd' : '#e8e8e8',
                          color: done ? '#2e844a' : task.Status === 'In Progress' ? '#0176d3' : task.Status === 'Not Started' ? '#9e6c00' : '#514f4d'
                        }}>{statusLabel(task.Status)}</span>
                      </div>
                      <div className="my-tasks-item__meta" style={{ marginTop: '4px' }}>
                        {task.Owner__Name && (
                          <span style={{ fontSize: '11px', color: '#0176d3', fontWeight: '600' }}>מוקצה ל: {task.Owner__Name}</span>
                        )}
                        {task.ActivityDate && (
                          <span style={{ fontSize: '11px', color: !done && new Date(task.ActivityDate) < new Date() ? '#ea001e' : '#706e6b', fontWeight: !done && new Date(task.ActivityDate) < new Date() ? '600' : '400' }}>
                            {new Date(task.ActivityDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                          </span>
                        )}
                        {task.Priority === 'High' && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: '#fde8e8', color: '#ea001e', fontWeight: '600' }}>גבוהה</span>}
                      </div>
                      {task.What__Name && (
                        <div style={{ fontSize: '11px', color: '#706e6b', marginTop: '2px' }}>{task.What__Name}</div>
                      )}
                    </div>
                  </div>
                );}) : (
                  <div className="my-tasks-dropdown__empty">
                    <span style={{ fontSize: '2rem' }}>✅</span>
                    <p>אין משימות שהקצית</p>
                  </div>
                );
              })()
            ) : (
            /* === MY TASKS TAB === */
            (() => {
              const isCompleted = (s) => s === 'Completed' || s === 'סיום טיפול בשביעות רצון' || s === 'סיום טיפול לא בשביעות רצון' || s === 'הושלם';
              const sorted = [...myTasks].sort((a, b) => {
                const aComp = isCompleted(a.Status) ? 1 : 0;
                const bComp = isCompleted(b.Status) ? 1 : 0;
                if (aComp !== bComp) return aComp - bComp;
                return (b.ActivityDate || '').localeCompare(a.ActivityDate || '');
              });
              const statusLabel = (s) => {
                if (s === 'Not Started') return 'לא התחיל';
                if (s === 'In Progress') return 'בתהליך';
                if (s === 'Completed') return 'הושלם';
                if (s === 'Waiting on someone else') return 'ממתין';
                if (s === 'Deferred') return 'נדחה';
                return s || '';
              };
              return sorted.length > 0 ? sorted.slice(0, 15).map((task, idx) => {
                const done = isCompleted(task.Status);
                return (
                <div key={task.Id || idx} className="my-tasks-dropdown__item" style={done ? { background: '#f6faf7', opacity: 0.75 } : {}} onClick={() => {
                  navigateToRecord(task, objects.find(o => o.api_name === 'Task'));
                  setShowMyTasks(false);
                }}>
                  <div className="my-tasks-item__checkbox">
                    {done ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', background: '#2e844a', borderRadius: '50%', color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>✓</span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', border: '2px solid #b0adab', borderRadius: '50%' }}></span>
                    )}
                  </div>
                  <div className="my-tasks-item__content" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <div className="my-tasks-item__subject" style={done ? { textDecoration: 'line-through', color: '#706e6b' } : {}}>{task.Subject || 'משימה'}</div>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', flexShrink: 0, whiteSpace: 'nowrap',
                        background: done ? '#d4f7dc' : task.Status === 'In Progress' ? '#d4edfc' : task.Status === 'Not Started' ? '#fef3cd' : '#e8e8e8',
                        color: done ? '#2e844a' : task.Status === 'In Progress' ? '#0176d3' : task.Status === 'Not Started' ? '#9e6c00' : '#514f4d'
                      }}>{statusLabel(task.Status)}</span>
                    </div>
                    <div className="my-tasks-item__meta" style={{ marginTop: '4px' }}>
                      {task.ActivityDate && (
                        <span style={{ fontSize: '11px', color: !done && new Date(task.ActivityDate) < new Date() ? '#ea001e' : '#706e6b', fontWeight: !done && new Date(task.ActivityDate) < new Date() ? '600' : '400' }}>
                          {new Date(task.ActivityDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                        </span>
                      )}
                      {task.Priority === 'High' && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: '#fde8e8', color: '#ea001e', fontWeight: '600' }}>גבוהה</span>}
                      {task.Owner__Name && (
                        <span style={{ fontSize: '11px', color: '#706e6b' }}>מוקצה: {task.Owner__Name}</span>
                      )}
                    </div>
                    {task.What__Name && (
                      <div style={{ fontSize: '11px', color: '#0176d3', marginTop: '2px' }}>{task.What__Name}</div>
                    )}
                  </div>
                </div>
              );}) : (
                <div className="my-tasks-dropdown__empty">
                  <span style={{ fontSize: '2rem' }}>✅</span>
                  <p>אין משימות</p>
                </div>
              );
            })()
            )}
          </div>
          <div className="my-tasks-dropdown__footer">
            <button onClick={() => {
              navigate('/my-tasks');
              setShowMyTasks(false);
            }}>
              הצג את כל המשימות
            </button>
          </div>
        </div>
      )}

      {/* Transfer Detail Modal */}
      {showTransferModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 560, maxHeight: '85vh', overflow: 'hidden', direction: 'rtl', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #0176d3, #032d60)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>
                💰 העברת כספים {selectedNotification?.source_name ? `מ${selectedNotification.source_name}` : ''}
              </h3>
              <button onClick={() => { setShowTransferModal(false); setSelectedNotification(null); setTransferDetails(null); setShowWalletEditForm(false); setShowWalletRejectForm(false); }}
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            {/* Body */}
            <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              {transferModalLoading && !transferDetails ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#706e6b' }}>טוען פרטים...</div>
              ) : transferDetails ? (
                <>
                  {/* Summary cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                    <div style={{ background: '#f0f9ff', borderRadius: 8, padding: 12, textAlign: 'center', border: '1px solid #d8edff' }}>
                      <div style={{ fontSize: 11, color: '#706e6b', marginBottom: 4 }}>מזומן</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#0176d3' }}>₪{transferDetails.transfer?.CashAmount__c || 0}</div>
                    </div>
                    <div style={{ background: '#fef7f0', borderRadius: 8, padding: 12, textAlign: 'center', border: '1px solid #ffe0c2' }}>
                      <div style={{ fontSize: 11, color: '#706e6b', marginBottom: 4 }}>שיקים</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#e67300' }}>₪{transferDetails.transfer?.ChecksAmount__c || 0}</div>
                    </div>
                    <div style={{ background: '#f0faf0', borderRadius: 8, padding: 12, textAlign: 'center', border: '1px solid #c8e6c9' }}>
                      <div style={{ fontSize: 11, color: '#706e6b', marginBottom: 4 }}>סה"כ</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#2e844a' }}>₪{transferDetails.transfer?.TotalAmount__c || 0}</div>
                    </div>
                  </div>
                  {/* Transfer info */}
                  <div style={{ marginBottom: 16, background: '#fafaf9', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>טכנאי:</span>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{transferDetails.transfer?.technician_name || transferDetails.technician_name || selectedNotification?.source_name || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>תאריך:</span>
                      <span style={{ fontSize: 13 }}>{transferDetails.transfer?.TransferDate__c ? new Date(transferDetails.transfer.TransferDate__c).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>שיטת תשלום:</span>
                      <span style={{ fontSize: 13 }}>{transferDetails.transfer?.PaymentMethod__c || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>סטטוס:</span>
                      <span style={{ fontSize: 13, color: '#0176d3', fontWeight: 600 }}>{transferDetails.transfer?.Status__c || '—'}</span>
                    </div>
                  </div>
                  {/* Check details */}
                  {transferDetails.checks && transferDetails.checks.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#3e3e3c' }}>פרטי שיקים ({transferDetails.checks.length})</h4>
                      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {transferDetails.checks.map((chk, ci) => (
                          <div key={ci} style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, marginBottom: 6, fontSize: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span>שיק #{chk.CheckNumber__c || ci + 1}</span>
                              <span style={{ fontWeight: 700, color: '#0176d3' }}>₪{chk.Amount__c || chk.Check_Amount__c || '—'}</span>
                            </div>
                            <div style={{ color: '#706e6b', fontSize: 11 }}>
                              {chk.BankName__c && <span>בנק: {chk.BankName__c} | </span>}
                              {chk.BranchNumber__c && <span>סניף: {chk.BranchNumber__c} | </span>}
                              {chk.AccountNumber__c && <span>חשבון: {chk.AccountNumber__c}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Edit form */}
                  {showWalletEditForm && (
                    <div style={{ background: '#f0f7ff', borderRadius: 8, padding: 14, marginBottom: 16, border: '1px solid #d8edff' }}>
                      <h4 style={{ fontSize: 13, marginBottom: 8, color: '#0176d3' }}>עריכת סכום מזומן</h4>
                      <input type="number" value={walletEditAmount} onChange={e => setWalletEditAmount(e.target.value)}
                        placeholder="סכום מזומן מעודכן" style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d8dde6', marginBottom: 8, direction: 'rtl', fontSize: 13 }} />
                      <textarea value={walletEditNotes} onChange={e => setWalletEditNotes(e.target.value)}
                        placeholder="הערות לטכנאי..." rows={2} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d8dde6', resize: 'none', direction: 'rtl', fontSize: 13 }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button onClick={() => handleTransferAction('edit', { editedCashAmount: walletEditAmount, editNotes: walletEditNotes })}
                          disabled={transferModalLoading}
                          style={{ flex: 1, padding: '8px', borderRadius: 6, background: '#0176d3', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                          שלח לעריכה
                        </button>
                        <button onClick={() => setShowWalletEditForm(false)}
                          style={{ padding: '8px 16px', borderRadius: 6, background: '#f3f2f2', border: '1px solid #d8dde6', cursor: 'pointer', fontSize: 13 }}>
                          ביטול
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Reject form */}
                  {showWalletRejectForm && (
                    <div style={{ background: '#fff5f5', borderRadius: 8, padding: 14, marginBottom: 16, border: '1px solid #ffd4d4' }}>
                      <h4 style={{ fontSize: 13, marginBottom: 8, color: '#c23934' }}>סיבת סירוב</h4>
                      <textarea value={walletActionReason} onChange={e => setWalletActionReason(e.target.value)}
                        placeholder="הזן סיבת סירוב..." rows={2} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d8dde6', resize: 'none', direction: 'rtl', fontSize: 13 }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button onClick={() => handleTransferAction('reject', { reason: walletActionReason })}
                          disabled={transferModalLoading || !walletActionReason.trim()}
                          style={{ flex: 1, padding: '8px', borderRadius: 6, background: '#c23934', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: !walletActionReason.trim() ? 0.5 : 1 }}>
                          אשר סירוב
                        </button>
                        <button onClick={() => setShowWalletRejectForm(false)}
                          style={{ padding: '8px 16px', borderRadius: 6, background: '#f3f2f2', border: '1px solid #d8dde6', cursor: 'pointer', fontSize: 13 }}>
                          ביטול
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#706e6b' }}>לא נמצאו פרטי העברה</div>
              )}
            </div>
            {/* Footer actions */}
            {transferDetails && transferDetails.transfer?.Status__c === 'ממתין לאישור' && !showWalletEditForm && !showWalletRejectForm && (
              <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 10, justifyContent: 'center', borderTop: '1px solid #e5e5e5' }}>
                <button onClick={() => handleTransferAction('approve')} disabled={transferModalLoading}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: '#2e844a', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                  ✅ אישור
                </button>
                <button onClick={() => { setShowWalletEditForm(true); setWalletEditAmount(String(transferDetails.transfer?.CashAmount__c || '')); }}
                  disabled={transferModalLoading}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: '#0176d3', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                  ✏️ עריכה
                </button>
                <button onClick={() => setShowWalletRejectForm(true)} disabled={transferModalLoading}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: '#c23934', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                  ❌ סירוב
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Discount Request Modal */}
      {showDiscountModal && selectedNotification && (() => {
        const notifData = typeof selectedNotification.data === 'string' ? JSON.parse(selectedNotification.data) : (selectedNotification.data || {});
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 480, direction: 'rtl', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: 'linear-gradient(135deg, #e67300, #ff8533)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  🏷️ בקשת הנחה {notifData.technicianName ? `מ${notifData.technicianName}` : ''}
                </h3>
                <button onClick={() => { setShowDiscountModal(false); setSelectedNotification(null); setWalletActionReason(''); }}
                  style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
              </div>
              {/* Body */}
              <div style={{ padding: 20 }}>
                <div style={{ background: '#fafaf9', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#706e6b', fontSize: 12 }}>לקוח:</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{notifData.accountName || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#706e6b', fontSize: 12 }}>מוצר:</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{notifData.productName || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#706e6b', fontSize: 12 }}>מחיר מקורי:</span>
                    <span style={{ fontSize: 13 }}>₪{notifData.originalPrice || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#706e6b', fontSize: 12 }}>מחיר מבוקש:</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#e67300' }}>₪{notifData.requestedPrice || '—'}</span>
                  </div>
                  {notifData.priceAfter && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>מחיר לאחר הנחה:</span>
                      <span style={{ fontSize: 13 }}>₪{notifData.priceAfter}</span>
                    </div>
                  )}
                  {notifData.originalPrice && notifData.requestedPrice && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#706e6b', fontSize: 12 }}>אחוז הנחה:</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#c23934' }}>
                        {Math.round((1 - notifData.requestedPrice / notifData.originalPrice) * 100)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* P0.9 Task 6 — editable approved price. Manager can override
                    the requested price (e.g. split-the-difference). Range
                    [0, originalPrice]. Required, 2-decimal, validated below. */}
                {!showWalletRejectForm && selectedNotification.status !== 'actioned' && (() => {
                  const orig = Number(notifData.originalPrice || 0);
                  const inputVal = approvedPriceInput.trim();
                  const parsed = inputVal === '' ? NaN : Number(inputVal);
                  const valid = !isNaN(parsed) && parsed >= 0 && (orig <= 0 || parsed <= orig);
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontSize: 12, color: '#706e6b', marginBottom: 6 }}>מחיר מאושר (ניתן לעדכן):</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#706e6b', fontWeight: 600 }}>₪</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max={orig > 0 ? orig : undefined}
                          value={approvedPriceInput}
                          onChange={e => setApprovedPriceInput(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px 10px 32px',
                            border: valid ? '1px solid #d8dde6' : '2px solid #c23934',
                            borderRadius: 6,
                            fontSize: 16,
                            fontWeight: 700,
                            direction: 'ltr',
                            textAlign: 'right',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      {!valid && inputVal !== '' && (
                        <div style={{ color: '#c23934', fontSize: 12, marginTop: 4 }}>
                          המחיר חייב להיות בין 0 ל-{orig.toLocaleString()}
                        </div>
                      )}
                      {!valid && inputVal === '' && (
                        <div style={{ color: '#c23934', fontSize: 12, marginTop: 4 }}>חובה להזין מחיר מאושר</div>
                      )}
                    </div>
                  );
                })()}

                {/* Reject reason */}
                {showWalletRejectForm && (
                  <div style={{ marginBottom: 16 }}>
                    <textarea value={walletActionReason} onChange={e => setWalletActionReason(e.target.value)}
                      placeholder="סיבת סירוב..." rows={2}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d8dde6', resize: 'none', direction: 'rtl', fontSize: 13 }} />
                  </div>
                )}
              </div>
              {/* Footer */}
              {selectedNotification.status !== 'actioned' && (
                <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 10, justifyContent: 'center', borderTop: '1px solid #e5e5e5' }}>
                  {!showWalletRejectForm ? (
                    <>
                      {(() => {
                        const orig = Number(notifData.originalPrice || 0);
                        const parsed = approvedPriceInput.trim() === '' ? NaN : Number(approvedPriceInput);
                        const validPrice = !isNaN(parsed) && parsed >= 0 && (orig <= 0 || parsed <= orig);
                        return (
                          <button onClick={() => handleDiscountAction('approve', { approvedPrice: parsed })}
                            disabled={discountModalLoading || !validPrice}
                            style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: validPrice ? '#2e844a' : '#bbb', color: '#fff', border: 'none', cursor: validPrice ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14 }}>
                            ✅ אישור
                          </button>
                        );
                      })()}
                      <button onClick={() => setShowWalletRejectForm(true)}
                        disabled={discountModalLoading}
                        style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: '#c23934', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                        ❌ סירוב
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleDiscountAction('reject', { reason: walletActionReason })}
                        disabled={discountModalLoading || !walletActionReason.trim()}
                        style={{ flex: 1, padding: '10px 20px', borderRadius: 8, background: '#c23934', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: !walletActionReason.trim() ? 0.5 : 1 }}>
                        אשר סירוב
                      </button>
                      <button onClick={() => { setShowWalletRejectForm(false); setWalletActionReason(''); }}
                        style={{ padding: '10px 20px', borderRadius: 8, background: '#f3f2f2', border: '1px solid #d8dde6', cursor: 'pointer', fontSize: 14 }}>
                        ביטול
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* P0.9 Task 6 (D7) — copyable approval code modal. Replaces the
          window.alert() that previously stopped the flow and could not be
          read aloud comfortably. */}
      {approvedCodeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setApprovedCodeModal(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 360, direction: 'rtl', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflow: 'hidden' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ background: 'linear-gradient(135deg, #2e844a, #1f6b3a)', color: '#fff', padding: '16px 20px' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>✓ בקשת ההנחה אושרה</h3>
            </div>
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: '#706e6b', marginBottom: 8 }}>קוד אישור (גם נשלח אוטומטית לטכנאי):</div>
              <div style={{
                fontSize: 36,
                fontWeight: 700,
                letterSpacing: 8,
                fontFamily: 'monospace',
                color: '#0176d3',
                background: '#f0f8ff',
                padding: '14px 8px',
                borderRadius: 8,
                marginBottom: 16,
                userSelect: 'all'
              }}>
                {approvedCodeModal.code || '—'}
              </div>
              {approvedCodeModal.price != null && (
                <div style={{ fontSize: 14, color: '#333', marginBottom: 16 }}>
                  מחיר מאושר: <strong>₪{Number(approvedCodeModal.price).toLocaleString()}</strong>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  onClick={async () => {
                    try {
                      if (navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(approvedCodeModal.code || '');
                      } else {
                        // Fallback for older / iOS Safari pre-13.4
                        const ta = document.createElement('textarea');
                        ta.value = approvedCodeModal.code || '';
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.focus(); ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                    } catch (e) {
                      console.warn('[Discount] copy failed:', e);
                    }
                  }}
                  style={{ flex: 1, padding: '10px 16px', borderRadius: 8, background: '#0176d3', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                  📋 העתק קוד
                </button>
                <button
                  onClick={() => setApprovedCodeModal(null)}
                  style={{ flex: 1, padding: '10px 16px', borderRadius: 8, background: '#f3f2f2', border: '1px solid #d8dde6', cursor: 'pointer', fontSize: 14 }}>
                  סגור
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 12 }}>
                הקוד יישלח אוטומטית לטכנאי דרך SSE — אין צורך לקרוא בעל-פה
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manager Wallet Modal */}
      {showManagerWallet && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '95%', maxWidth: 700, maxHeight: '90vh', overflow: 'hidden', direction: 'rtl', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'linear-gradient(135deg, #0176d3, #032d60)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>💼 ארנק מנהל</h3>
              <button onClick={() => setShowManagerWallet(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              {!managerWalletData ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#706e6b' }}>טוען...</div>
              ) : (
                <>
                  {/* Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                    <div style={{ background: '#f0f9ff', borderRadius: 8, padding: 14, textAlign: 'center', border: '1px solid #d8edff' }}>
                      <div style={{ fontSize: 11, color: '#706e6b' }}>מזומן</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#0176d3' }}>₪{managerWalletData?.cash || 0}</div>
                    </div>
                    <div style={{ background: '#fef7f0', borderRadius: 8, padding: 14, textAlign: 'center', border: '1px solid #ffe0c2' }}>
                      <div style={{ fontSize: 11, color: '#706e6b' }}>שיקים</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#e67300' }}>₪{managerWalletData?.checks || 0}</div>
                    </div>
                    <div style={{ background: '#f0faf0', borderRadius: 8, padding: 14, textAlign: 'center', border: '1px solid #c8e6c9' }}>
                      <div style={{ fontSize: 11, color: '#706e6b' }}>סה"כ</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#2e844a' }}>₪{(managerWalletData?.cash || 0) + (managerWalletData?.checks || 0)}</div>
                    </div>
                  </div>
                  {/* Pending transfers */}
                  {managerWalletData.pendingTransfers?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 700, color: '#e67300', marginBottom: 8 }}>ממתינים לאישור ({managerWalletData.pendingTransfers.length})</h4>
                      {managerWalletData.pendingTransfers.map((t, i) => (
                        <div key={i} style={{ background: '#fef7f0', border: '1px solid #ffe0c2', borderRadius: 6, padding: 10, marginBottom: 6, fontSize: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontWeight: 600 }}>{t.technician_name || 'טכנאי'}</div>
                              <div style={{ color: '#706e6b', fontSize: 11 }}>
                                {t.TransferDate__c ? new Date(t.TransferDate__c).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: '#e67300' }}>₪{t.TotalAmount__c || 0}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => {
                                if (!window.confirm(`לאשר העברה של ₪${t.TotalAmount__c || 0} מ${t.technician_name || 'טכנאי'}?`)) return;
                                handleTransferAction('approve', { transferIdOverride: t.Id });
                              }}
                              disabled={transferModalLoading}
                              style={{ background: '#2e844a', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                            >✓ אשר</button>
                            <button
                              onClick={() => {
                                const newAmount = window.prompt('סכום מתוקן (במזומן):', t.CashAmount__c || t.TotalAmount__c || '0');
                                if (newAmount === null) return;
                                const notes = window.prompt('הערה (אופציונלי):', '') || '';
                                handleTransferAction('edit', { transferIdOverride: t.Id, editedCashAmount: parseFloat(newAmount) || 0, editNotes: notes });
                              }}
                              disabled={transferModalLoading}
                              style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                            >✎ תקן</button>
                            <button
                              onClick={() => {
                                const reason = window.prompt('סיבת דחייה:', '');
                                if (!reason) return;
                                handleTransferAction('reject', { transferIdOverride: t.Id, reason });
                              }}
                              disabled={transferModalLoading}
                              style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                            >✕ סרב</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Approved transfers */}
                  {managerWalletData.transfers?.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2e844a', marginBottom: 8 }}>אושרו ({managerWalletData.transfers.length})</h4>
                      {managerWalletData.transfers.map((t, i) => (
                        <div key={i} style={{ background: '#f0faf0', border: '1px solid #c8e6c9', borderRadius: 6, padding: 10, marginBottom: 6, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{t.technician_name || 'טכנאי'}</div>
                            <div style={{ color: '#706e6b', fontSize: 11 }}>
                              {t.TransferDate__c ? new Date(t.TransferDate__c).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            </div>
                          </div>
                          <div style={{ fontWeight: 700, color: '#2e844a' }}>₪{t.TotalAmount__c || 0}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!managerWalletData.transfers?.length && !managerWalletData.pendingTransfers?.length) && (
                    <div style={{ textAlign: 'center', padding: 30, color: '#706e6b' }}>אין העברות בארנק</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Dropdown */}
      {showSettings && (
        <div className="slds-settings-dropdown">
          <div className="slds-settings__header">
            <h3>הגדרות</h3>
            <button onClick={() => setShowSettings(false)}>✕</button>
          </div>
          <div className="slds-settings__content">
            <button className="slds-settings-item" onClick={() => openSetup('home')}>
              <span>⚙️</span>
              <span>הגדרות מערכת</span>
            </button>
            <button className="slds-settings-item" onClick={() => openSetup('objects')}>
              <span>📦</span>
              <span>מנהל אובייקטים</span>
            </button>
            <button className="slds-settings-item" onClick={() => openSetup('apex')}>
              <span>📋</span>
              <span>מחלקות סקריפט</span>
            </button>
            <button className="slds-settings-item" onClick={() => openSetup('flows')}>
              <span>🔄</span>
              <span>תהליכים</span>
            </button>
            <button className="slds-settings-item" onClick={() => {
              setShowSettings(false);
              window.open('/developer-console', 'DevConsole', 'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no');
            }}>
              <span>💻</span>
              <span>קונסולת מפתח</span>
            </button>
            <div className="slds-settings-divider"></div>
            <button className="slds-settings-item">
              <span>👤</span>
              <span>הגדרות איכות</span>
            </button>
            <button className="slds-settings-item">
              <span>🔔</span>
              <span>הגדרוה התראות</span>
            </button>
            <div className="slds-settings-divider"></div>
            <div className="slds-settings-user-info">
              <span className="user-icon">👤</span>
              <span className="user-name">{currentUser?.name || currentUser?.username || 'משהמש'}</span>
            </div>
            <button className="slds-settings-item logout" onClick={handleLogout}>
              <span>🚪</span>
              <span>התנתק</span>
            </button>
          </div>
        </div>
      )}

      {/* User Menu Dropdown */}
      {showUserMenu && (
        <div className="slds-user-menu-dropdown">
          <div className="slds-user-menu__header">
            <div className="slds-user-menu__avatar">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="#0176d3">
                <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/>
              </svg>
            </div>
            <div className="slds-user-menu__info">
              <h3 className="slds-user-menu__name">{currentUser?.name || currentUser?.username || 'משתמש'}</h3>
              <p className="slds-user-menu__email">{currentUser?.email || ''}</p>
              <p className="slds-user-menu__role">{currentUser?.role === 'admin' ? 'מנהל מערכת' : 'משתמש'}</p>
            </div>
            <button className="slds-user-menu__close" onClick={() => setShowUserMenu(false)}>✕</button>
          </div>
          <div className="slds-user-menu__content">
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              navigate('/profile');
            }}>
              <span className="slds-user-menu-item__icon">👤</span>
              <span>הפרופיל שלי</span>
            </button>
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              navigate('/settings/personal');
            }}>
              <span className="slds-user-menu-item__icon">⚙️</span>
              <span>הגדרות אישיות</span>
            </button>
            {(permIsAdmin(currentUser)) && (
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              setShowThemePanel(true);
            }}>
              <span className="slds-user-menu-item__icon">🎨</span>
              <span>עיצוב</span>
            </button>
            )}
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              navigate('/settings/notifications');
            }}>
              <span className="slds-user-menu-item__icon">🔔</span>
              <span>הגדרות התראות</span>
            </button>
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              navigate('/settings/security');
            }}>
              <span className="slds-user-menu-item__icon">🔒</span>
              <span>אבטחה וסיסמה</span>
            </button>
            <div className="slds-user-menu-divider"></div>
            <button className="slds-user-menu-item" onClick={() => {
              setShowUserMenu(false);
              navigate('/help');
            }}>
              <span className="slds-user-menu-item__icon">❓</span>
              <span>עזרה ותמיכה</span>
            </button>
            <div className="slds-user-menu-divider"></div>
            <button className="slds-user-menu-item slds-user-menu-item--logout" onClick={() => {
              setShowUserMenu(false);
              handleLogout();
            }}>
              <span className="slds-user-menu-item__icon">🚪</span>
              <span>התנתקות</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="slds-main">
        <Routes>
          {/* Home Page */}
          <Route path="/" element={
            <HomePage
              objects={objects}
              onNavigate={navigateToObject}
              getObjectConfig={getObjectConfig}
              stats={stats}
              onOpenSalesWizard={openNewSalesWizard}
              onOpenSalesList={() => setShowSalesListModal(true)}
              onOpenServiceWizard={openNewServiceWizard}
              onOpenServiceList={() => setShowServiceListModal(true)}
              onOpenSerialModal={() => setShowSerialModal(true)}
              currentUser={currentUser}
            />
          } />

          {/* Object List View */}
          <Route path="/object/:objectName" element={
            <ObjectListRoute
              objects={objects}
              selectedObject={selectedObject}
              setSelectedObject={setSelectedObject}
              records={records}
              loading={loading}
              loadRecords={loadRecords}
              navigateToRecord={navigateToRecord}
              getObjectConfig={getObjectConfig}
              getFieldLabel={getFieldLabel}
              currentListView={currentListView}
              setCurrentListView={setCurrentListView}
              showListViewMenu={showListViewMenu}
              setShowListViewMenu={setShowListViewMenu}
              initialLoadComplete={initialLoadComplete}
              currentUser={currentUser}
              setRecords={setRecords}
              setAllRecordsCache={setAllRecordsCache}
              addToCache={addToCache}
              loadMoreRecords={loadMoreRecords}
              recordsLoadingMore={recordsLoadingMore}
              recordsHasMore={recordsHasMore}
              recordsTotalCount={recordsTotalCount}
            />
          } />

          {/* Record Detail View */}
          <Route path="/object/:objectName/:recordId" element={
            <RecordDetailRoute
              objects={objects}
              selectedObject={selectedObject}
              setSelectedObject={setSelectedObject}
              selectedRecord={selectedRecord}
              setSelectedRecord={setSelectedRecord}
              getFromCache={getFromCache}
              addToCache={addToCache}
              loadRecords={loadRecords}
              getObjectConfig={getObjectConfig}
              getFieldLabel={getFieldLabel}
              navigateToRecord={navigateToRecord}
              navigate={navigate}
              flexipageCache={flexipageCache}
              setFlexipageCache={setFlexipageCache}
              currentUser={currentUser}
            />
          } />

          {/* Setup routes handled by fullscreen layout above */}

          {/* Developer Console - Full Page */}
          <Route path="/developer-console" element={
            <Suspense fallback={<LoadingFallback />}>
              <DeveloperConsole onClose={() => navigate(-1)} />
            </Suspense>
          } />

          {/* Afikim Inspector - SQL Console for Admins */}
          <Route path="/inspector" element={
            <Suspense fallback={<LoadingFallback />}>
              <AfkimInspector />
            </Suspense>
          } />

          {/* Data Loader - Import/Export Tool */}
          <Route path="/data-loader" element={
            <Suspense fallback={<LoadingFallback />}>
              <DataLoader />
            </Suspense>
          } />

          {/* Communication Center - Full Page (Admin only, non-admin redirected) */}
          <Route path="/communication-center" element={
            (permIsAdmin(currentUser)) ? (
              <Suspense fallback={<LoadingFallback />}>
                <OmnichannelConsole currentUser={currentUser} />
              </Suspense>
            ) : <div style={{ textAlign: 'center', padding: '60px 20px', color: '#706e6b' }}><h2>אין הרשאה</h2><p>מרכז התקשורת זמין למנהלים בלבד</p></div>
          } />

          {/* Communication Workspace V2 (additive, dormant until CONVERSATION_API_ENABLED=1).
              Audit: docs/design/communication-center-2.0/20-workspace-reuse-audit.md §13.
              Legacy /communication-center above stays live. */}
          <Route path="/workspace" element={
            <Suspense fallback={<LoadingFallback />}>
              <WorkspaceShell
                currentUserEmail={currentUser?.email || currentUser?.username || ''}
                currentUserProfile={currentUser?.profile || currentUser?.Profile || ''}
              />
            </Suspense>
          } />
          {/* E4.4 — deep-link URL per chat. Pattern matches the WhatsApp JID
              format `972...@s.whatsapp.net`. React Router needs `*` to
              allow the `@` and dots through unescaped. */}
          <Route path="/workspace/chat/*" element={
            <Suspense fallback={<LoadingFallback />}>
              <WorkspaceShell
                currentUserEmail={currentUser?.email || currentUser?.username || ''}
                currentUserProfile={currentUser?.profile || currentUser?.Profile || ''}
              />
            </Suspense>
          } />

          {/* Owner Dashboard editor (#1474). Edits a SPECIFICATION only — the
              catalogue decides what may be asked, the RenderContext decides
              when, and the Engine decides whether it can honestly be drawn.
              Server-gated to OWNER_DASHBOARD_PROFILES; this route is a
              convenience, never the access control. */}
          <Route path="/owner-dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <OwnerDashboardApp />
            </Suspense>
          } />
          {/* A saved dashboard is a real, linkable thing. `?edit=1` on the same
              route turns on the Builder — same screen, same renderer, controls
              added. There is no separate preview route, because a preview that
              renders differently is a lie discovered after saving. */}
          <Route path="/owner-dashboard/:dashboardId" element={
            <Suspense fallback={<LoadingFallback />}>
              <OwnerDashboardApp />
            </Suspense>
          } />

          {/* WhatsApp Reports - Agent Performance Reports */}
          <Route path="/whatsapp-reports" element={
            <Suspense fallback={<LoadingFallback />}>
              <WhatsAppReports currentUser={currentUser} />
            </Suspense>
          } />

          {/* Report Builder - Advanced modern CRM UX Reports */}
          <Route path="/reports" element={
            <Suspense fallback={<LoadingFallback />}>
              <ReportBuilder />
            </Suspense>
          } />
          <Route path="/reports/*" element={
            <Suspense fallback={<LoadingFallback />}>
              <ReportBuilder />
            </Suspense>
          } />

          {/* Technician App - Mobile Field Service App */}
          <Route path="/app/*" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechnicianApp />
            </Suspense>
          } />

          {/* Phone Leads - from telephony system */}
          <Route path="/leads/phone" element={
            <Suspense fallback={<LoadingFallback />}>
              <PhoneLeadsPage />
            </Suspense>
          } />

          {/* Leads Dashboard - AFIKIM Ledes */}
          <Route path="/leads/dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadsDashboard />
            </Suspense>
          } />

          {/* Leads users (leads admins; the server checks) */}
          <Route path="/leads/users" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadsUsersPage />
            </Suspense>
          } />

          {/* Leads campaigns (every leads role reads; leads admins save) */}
          <Route path="/leads/campaigns" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadsCampaignsPage />
            </Suspense>
          } />

          {/* Leads settings (leads admins; the server checks) */}
          <Route path="/leads/settings" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadsSettingsPage />
            </Suspense>
          } />

          {/* Leads Page - AFIKIM Ledes */}
          <Route path="/leads" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadsPage currentUser={currentUser} />
            </Suspense>
          } />

          {/* Lead Detail Page */}
          <Route path="/leads/:leadId" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadDetailPage />
            </Suspense>
          } />

          {/* Users Page - AFIKIM Ledes */}
          <Route path="/ledes/users" element={
            <Suspense fallback={<LoadingFallback />}>
              <UsersPage />
            </Suspense>
          } />

          {/* Personal Dashboard — drill-down full page (per category) */}
          <Route path="/dashboard/tasks/:category" element={
            <Suspense fallback={<LoadingFallback />}>
              <TaskCategoryPage currentUser={currentUser} />
            </Suspense>
          } />

          {/* My Tasks Page */}
          <Route path="/my-tasks" element={
            <MyTasksFullPage
              currentUser={currentUser}
              config={config}
              navigateToRecord={navigateToRecord}
              objects={objects}
              navigate={navigate}
            />
          } />
<Route path="/task-dashboard" element={            <Suspense fallback={<LoadingFallback />}>              <TaskManagerDashboard currentUser={currentUser} config={config} navigateToRecord={navigateToRecord} objects={objects} />            </Suspense>          } />

          {/* Sales Technician dashboard — also embedded inside the
              manager-mobile via iframe (?openTargets=1 auto-opens the
              targets editor for the dedicated "הגדרת יעדים" tab) */}
          <Route path="/dashboard/sales-technicians" element={
            <Suspense fallback={<LoadingFallback />}>
              <SalesTechnicianDashboard currentUser={currentUser} />
            </Suspense>
          } />

          {/* יומן — the leads tasks on PG (leadim/LeadimTasksPage) */}
          <Route path="/ledes/tasks" element={
            <Suspense fallback={<LoadingFallback />}>
              <TasksPage />
            </Suspense>
          } />

          {/* Supplier Login Page - AFIKIM Leads */}
          <Route path="/supplier-login" element={
            <Suspense fallback={<LoadingFallback />}>
              <SupplierLogin />
            </Suspense>
          } />

          {/* Supplier Portal - AFIKIM Leads (restricted access) */}
          <Route path="/supplier-portal" element={
            <Suspense fallback={<LoadingFallback />}>
              <SupplierPortal />
            </Suspense>
          } />

          {/* Report Pages (replacements for legacy VF pages) */}
          <Route path="/apex/service_hitstaydut" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechInventoryReport />
            </Suspense>
          } />
          <Route path="/apex/HITSTAIDUTV2" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechInventoryReportV2 />
            </Suspense>
          } />
          <Route path="/apex/mahsan" element={
            <Suspense fallback={<LoadingFallback />}>
              <WarehousePickingReport />
            </Suspense>
          } />

          {/* Warehouse Home - Department Dashboard */}
          <Route path="/warehouse" element={
            <Suspense fallback={<LoadingFallback />}>
              <WarehouseHome />
            </Suspense>
          } />

          {/* Warehouse Virtual Tour - Street View Style */}
          <Route path="/warehouse/tour" element={
            <Suspense fallback={<LoadingFallback />}>
              <WarehouseTour />
            </Suspense>
          } />

          {/* Warehouse Module Pages - Open as separate pages */}
          <Route path="/warehouse/goods-receipt" element={
            <Suspense fallback={<LoadingFallback />}>
              <GoodsReceiptPage />
            </Suspense>
          } />
          <Route path="/warehouse/inventory-setup" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventorySetupPage />
            </Suspense>
          } />
          <Route path="/warehouse/inventory-count" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryCountPage />
            </Suspense>
          } />
          <Route path="/warehouse/picking" element={
            <Suspense fallback={<LoadingFallback />}>
              <PickingPage />
            </Suspense>
          } />
          <Route path="/warehouse/zone-transfer" element={
            <Suspense fallback={<LoadingFallback />}>
              <ZoneTransferPage />
            </Suspense>
          } />
          <Route path="/warehouse/vehicle-transfer" element={
            <Suspense fallback={<LoadingFallback />}>
              <VehicleTransferPage />
            </Suspense>
          } />
          <Route path="/warehouse/sku-mapping" element={
            <Suspense fallback={<LoadingFallback />}>
              <SKUMappingPage />
            </Suspense>
          } />

          {/* Barcode Management */}
          <Route path="/barcode-management" element={
            <Suspense fallback={<LoadingFallback />}>
              <BarcodeManagement />
            </Suspense>
          } />

          {/* Inventory — bin manager (new module; the server answers 404 while
              inventory.enabled is off, and the screen says so). No launcher tile yet. */}
          <Route path="/inventory/bins" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><BinManager /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — counting is done in the storekeepers' app, by pallet (every count from now on). */}
          <Route path="/inventory/m" element={<Navigate to="/warehouse-app" replace />} />

          {/* Inventory — the phone page for loading vans, returns, bin moves. Same flag rule. */}
          <Route path="/inventory/m/transfer" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><MobileTransfer /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — the phone page for putting received goods into bins. Same flag rule. */}
          <Route path="/inventory/m/putaway" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><MobilePutaway /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — open, review, approve and post counts. Same flag rule. */}
          <Route path="/inventory/counts" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><CountManager /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — the exception queue and the shadow measurement. Same flag rule. */}
          <Route path="/inventory/exceptions" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><ExceptionQueue /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — purchase orders (phase 3). Same flag rule; the SF-era PO screen is untouched. */}
          <Route path="/inventory/po" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><PurchaseOrders /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — goods receiving, warehouse work: models and quantities, never a price. */}
          <Route path="/inventory/receipts" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><ReceiptDesk /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — the module's home: stock status and the way to every inventory screen. */}
          <Route path="/inventory" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryHome />
            </Suspense>
          } />

          {/* Inventory — stock corrections, approved by another administrator. Same flag rule. */}
          <Route path="/inventory/adjustments" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><AdjustmentDesk /></InventoryPage>
            </Suspense>
          } />

          {/* Inventory — minimum stock per product and place (van standard included). Same flag rule. */}
          <Route path="/inventory/reorder" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage><ReorderRules /></InventoryPage>
            </Suspense>
          } />

          {/* ספר מכשיר — one device's whole story by serial (inventory.device_book; 404 → "not open yet"). */}
          <Route path="/inventory/device-book" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage title="ספר מכשיר"><DeviceBook /></InventoryPage>
            </Suspense>
          } />

          {/* The main-warehouse bins the lab's exit lands in — system administrator (lab.exit_to_warehouse). */}
          <Route path="/inventory/lab-exit-bins" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage title="תאי יציאה מהמעבדה"><LabExitBins /></InventoryPage>
            </Suspense>
          } />

          {/* החלפות מותרות — what may replace what in the van load (owner D4), system administrator. */}
          <Route path="/inventory/substitutes" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage title="החלפות מותרות"><ProductSubstitutes /></InventoryPage>
            </Suspense>
          } />

          {/* אישורי גריסה — the menu-editor tile inventory-condition-shred (owner 2026-10-24). 404 while inventory.shred is off. */}
          <Route path="/inventory/shred-approvals" element={
            <Suspense fallback={<LoadingFallback />}>
              <InventoryPage title="אישורי גריסה"><ShredApprovals /></InventoryPage>
            </Suspense>
          } />

          {/* Procurement Home - Department Dashboard */}
          <Route path="/procurement" element={
            <Suspense fallback={<LoadingFallback />}>
              <ProcurementHome />
            </Suspense>
          } />

          {/* Accounting Home - Department Dashboard */}
          <Route path="/accounting" element={
            <Suspense fallback={<LoadingFallback />}>
              <AccountingHome />
            </Suspense>
          } />

          {/* Technician Sales Report — 2026-05-14 owner spec: bonus calc for
              accounting + admin profiles. AppLauncher tile hides the link
              for other profiles; this route is reachable by deep-link too,
              but the report only exposes data the BE returns (admin-side
              gating can be tightened on the BE if needed). */}
          <Route path="/accounting/technician-sales-report" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechnicianSalesReport />
            </Suspense>
          } />

          {/* PR #1229 — dedicated route for Tech Activities so Sale Case
              can open as a modal on top without z-index fights. */}
          <Route path="/accounting/tech-activities/:resourceId" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechActivitiesPage />
            </Suspense>
          } />

          {/* 2026-06-01 — Monthly Revenue Dashboard. App Launcher tile
              routes to /monthly-revenue. */}
          <Route path="/monthly-revenue" element={
            <Suspense fallback={<LoadingFallback />}>
              <MonthlyRevenueDashboard />
            </Suspense>
          } />

          {/* 2026-10-23 — דשבורד מוצרים (App Launcher tile `product-dashboard`; the API answers 404 while
              dashboards.product_performance is off and 403 without the launcher grant). */}
          <Route path="/product-dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <ProductDashboard />
            </Suspense>
          } />

          {/* 2026-10-28 — מנהל קבצים (admin only; the API answers 404 while file_vault.enabled is off
              and 403 to anyone not a system administrator — the page says so). */}
          <Route path="/files" element={
            <Suspense fallback={<LoadingFallback />}>
              <FileVaultPage />
            </Suspense>
          } />

          {/* 2026-10-15 — credit approval requests report (אישורי זיכוי). */}
          <Route path="/credit-approvals" element={
            <Suspense fallback={<LoadingFallback />}>
              <CreditApprovalsReport />
            </Suspense>
          } />

          {/* 2026-11-02 — sale-to-lead attribution review (אישור שיוך מכירה לפי ליד). */}
          <Route path="/lead-attribution-approvals" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeadAttributionApprovals />
            </Suspense>
          } />

          {/* 2026-10-25 — manager discount requests (בקשות הנחה). */}
          <Route path="/discount-requests" element={
            <Suspense fallback={<LoadingFallback />}>
              <DiscountRequestsReport />
            </Suspense>
          } />

          {/* 2026-07-12 — Payments & payments-awaiting-verification (accounting+admin). */}
          <Route path="/payments-verification" element={
            <Suspense fallback={<LoadingFallback />}>
              <PaymentsVerification />
            </Suspense>
          } />

          {/* 2026-08-27 — ERN direct-debit requests (§9.3). This is what makes
              "leave and come back" true: a signature can take up to 7 days, so the
              modal that opened the request is not where the work finishes. Both gates
              (ERN_DIRECT_DEBIT_ENABLED + Admin/accounting) are enforced server-side;
              the screen explains itself if either says no. */}
          <Route path="/ern-direct-debit" element={
            <Suspense fallback={<LoadingFallback />}>
              <ErnDirectDebitList />
            </Suspense>
          } />

          {/* Sales-rep sales report — 2026-05-14. Sister of tech-sales-report
              for non-tech reps (Opportunity.OwnerId rollup). AppLauncher
              tile gates by admin + accounting profile. */}
          <Route path="/accounting/sales-rep-report" element={
            <Suspense fallback={<LoadingFallback />}>
              <SalesRepReport />
            </Suspense>
          } />

          {/* Revenue by team — 2026-10-07. Route only, no menu entry yet (owner).
              /api/dashboards/team-revenue answers admin + accounting only. */}
          <Route path="/accounting/team-revenue" element={
            <Suspense fallback={<LoadingFallback />}>
              <TeamRevenueDashboard />
            </Suspense>
          } />

          {/* Reseller report — 2026-05-14. Lists reseller-pricebook
              opportunities filtered by reseller / order # / order-date /
              install-date. Admin + accounting profile. */}
          <Route path="/accounting/reseller-report" element={
            <Suspense fallback={<LoadingFallback />}>
              <ResellerReport />
            </Suspense>
          } />

          {/* System Notification Center — 2026-07-02 PR-F. Admin surface
              for broadcasting blocking notifications + real-time
              connected-user snapshot. Gated by is_admin at the AppLauncher
              tile level. */}
          <Route path="/admin/system-notifications" element={
            <Suspense fallback={<LoadingFallback />}>
              <SystemMaintenanceDashboard sessionId={
                localStorage.getItem('session_token') ||
                sessionStorage.getItem('session_token')
              } />
            </Suspense>
          } />

          {/* Search Results Page */}
          <Route path="/search" element={
            <SearchResultsPage
              getObjectConfig={getObjectConfig}
              objects={objects}
              navigateToRecord={navigateToRecord}
            />
          } />

          {/* Afikim GO - Technician Scheduler */}
          <Route path="/afikim-go" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechnicianScheduler />
            </Suspense>
          } />
          <Route path="/afikim-go/:dateParam" element={
            <Suspense fallback={<LoadingFallback />}>
              <TechnicianScheduler />
            </Suspense>
          } />

          {/* WebRTC Call Room - Browser-based calling */}
          <Route path="/call/:roomId" element={<Suspense fallback={<LoadingFallback />}><WebCallRoom /></Suspense>} />

          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* App Launcher Modal */}
      {showAppLauncher && (
        <AppLauncherModal
          objects={objects}
          currentUser={currentUser}
          onSelect={(obj) => {
            setShowAppLauncher(false);
            openObjectTab(obj);
          }}
          onClose={() => setShowAppLauncher(false)}
          getObjectConfig={getObjectConfig}
          onSetup={() => { setShowAppLauncher(false); openSetup('home'); }}
          onNavigate={(path) => { setShowAppLauncher(false); navigate(path); }}
          onOpenComponent={(componentName) => {
            setShowAppLauncher(false);
            if (componentName === 'technicianScheduler') {
              navigate('/afikim-go');
            } else if (componentName === 'telephony') {
              setTelephonyDashboardInitialTab('dashboard');
              setShowTelephonyDashboard(true);
            } else if (componentName === 'telephony-daily') {
              setTelephonyDashboardInitialTab('daily-dashboard');
              setShowTelephonyDashboard(true);
            } else if (componentName === 'telephony-weekly') {
              setTelephonyDashboardInitialTab('weekly-dashboard');
              setShowTelephonyDashboard(true);
            }
          }}
        />
      )}

      {/* Click outside handler - don't show when NavEditor modal is open */}
      {!showNavEditor && (showNavMenu || showListViewMenu || showNotifications || showSettings || showGlobalSearch || showMyTasks) && (
        <div
          className="slds-backdrop"
          style={showGlobalSearch ? { zIndex: 9999 } : {}}
          onClick={() => {
            setShowNavMenu(false);
            setShowListViewMenu(false);
            setShowNotifications(false);
            setShowSettings(false);
            setShowGlobalSearch(false);
            setShowMyTasks(false);
            setGlobalSearchTerm('');
          }}
        />
      )}

      {/* Navigation Editor Modal - MUST be after backdrop to appear on top */}
      {showNavEditor && (
        <NavEditorModal
          navItems={navItems}
          objects={objects}
          getObjectConfig={getObjectConfig}
          onSave={(newItems) => {
            setNavItems(newItems);
            setShowNavEditor(false);
          }}
          onClose={() => setShowNavEditor(false)}
        />
      )}

      {/* Context Menu (Right-click) */}
      {contextMenu.show && (
        <div
          className="slds-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 99999,
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            minWidth: '160px',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={openInNewTab}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              textAlign: 'right',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#181818'
            }}
            onMouseOver={(e) => e.target.style.background = '#f3f3f3'}
            onMouseOut={(e) => e.target.style.background = 'none'}
          >
            <span>🔗</span>
            <span>פתח בכרטיסייה חדשה</span>
          </button>
          <button
            onClick={() => {
              if (contextMenu.item && contextMenu.type === 'record') {
                const url = buildRecordUrl(contextMenu.item.object.api_name, contextMenu.item.record.Id);
                navigator.clipboard.writeText(window.location.origin + url);
              } else if (contextMenu.item && contextMenu.type === 'object') {
                const url = buildObjectListUrl(contextMenu.item.api_name);
                navigator.clipboard.writeText(window.location.origin + url);
              }
              closeContextMenu();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              textAlign: 'right',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#181818'
            }}
            onMouseOver={(e) => e.target.style.background = '#f3f3f3'}
            onMouseOut={(e) => e.target.style.background = 'none'}
          >
            <span>📋</span>
            <span>העתק קישור</span>
          </button>
        </div>
      )}

      {/* Bottom Banner - Communication Center with Agent Status */}
      <AgentStatusManager
        currentUser={currentUser}
        onNavigate={navigate}
      />

      {/* Sales Process List Modal */}
      {showSalesListModal && (
        <SalesProcessListModal
          processes={salesProcessesList}
          onClose={() => setShowSalesListModal(false)}
          onRefresh={loadSalesProcesses}
          filter={salesListFilter}
          onFilterChange={setSalesListFilter}
          onNavigate={navigate}
          onOpenProcess={(process) => {
            // בדיקה אם כבר יש wizard פתוח לאותו תהליך
            const existingWizard = salesWizards.find(w => w.processId === process.id);
            if (existingWizard) {
              // פתיחת ה-wizard הקיים (ביטול מזעור)
              setSalesWizards(prev => prev.map(w =>
                w.id === existingWizard.id ? { ...w, minimized: false } : w
              ));
            } else {
              // יצירת wizard חדש
              const newId = Date.now();
              setSalesWizards(prev => [...prev, {
                id: newId,
                minimized: false,
                processId: process.id,
                accountId: process.account_id,
                opportunityId: process.opportunity_id,
                currentStep: process.current_step
              }]);
            }
            setShowSalesListModal(false);
          }}
          onRemoveProcess={async (processId) => {
            try {
              const response = await fetch(`${API_BASE}/api/sales-processes/${processId}`, {
                method: 'DELETE'
              });
              if (response.ok) {
                // הסר מהרשימה המקומית
                setSalesProcessesList(prev => prev.filter(p => p.id !== processId));
                // הסר wizard פתוח אם יש
                setSalesWizards(prev => prev.filter(w => w.processId !== processId));
              } else {
                alert('שגיאה בהסרת התהליך');
              }
            } catch (err) {
              console.error('Error removing process:', err);
              alert('שגיאה בהסרת התהליך');
            }
          }}
        />
      )}

      {/* Serial Products Modal - Admin only */}
      {showSerialModal && (
        <SerialProductsModal
          onClose={() => setShowSerialModal(false)}
          currentUser={currentUser}
        />
      )}

      {/* Sales Process Wizards - רק כשלא ממוזערים */}
      <Suspense fallback={<LoadingFallback />}>
        {salesWizards.filter(w => !w.minimized).map((wizard, index) => (
          <SalesProcessWizard
            key={wizard.id}
            isOpen={true}
            onClose={() => closeSalesWizard(wizard.id)}
            wizardId={wizard.id}
            zIndex={1000 + index}
            onMinimizeChange={(minimized) => updateWizardMinimized(wizard.id, minimized)}
            onDataChange={(data) => updateWizardData(wizard.id, data)}
            currentUser={currentUser}
            initialProcessId={wizard.processId}
            initialStep={wizard.currentStep}
            initialAccountId={wizard.accountId}
            initialOpportunityId={wizard.opportunityId}
          />
        ))}
      </Suspense>

      {/* Service Process Wizards - רק כשלא ממוזערים */}
      <Suspense fallback={<LoadingFallback />}>
        {serviceWizards.filter(w => !w.minimized).map((wizard, index) => (
          <ServiceProcessWizard
            key={wizard.id}
            isOpen={true}
            onClose={() => closeServiceWizard(wizard.id)}
            wizardId={wizard.id}
            zIndex={1100 + index}
            onMinimizeChange={(minimized) => updateServiceWizardMinimized(wizard.id, minimized)}
            currentUser={currentUser}
            bubbleOffset={salesWizards.filter(w => w.minimized).length * 90}
          />
        ))}
      </Suspense>

      {/* Service Process List Modal */}
      {showServiceListModal && (
        <ServiceProcessListModal
          onClose={() => setShowServiceListModal(false)}
          currentUser={currentUser}
          onOpenProcess={(process) => {
            // Open existing wizard with the saved ID
            setServiceWizards(prev => {
              // Check if wizard is already open
              const existingWizard = prev.find(w => w.id === parseInt(process.id) || w.id === process.id);
              if (existingWizard) {
                // Just un-minimize it
                return prev.map(w => w.id === existingWizard.id ? { ...w, minimized: false } : w);
              }
              // Open new wizard with the saved ID
              return [...prev, {
                id: parseInt(process.id) || process.id,
                minimized: false
              }];
            });
            setShowServiceListModal(false);
          }}
        />
      )}

      {/* Internal Chat - Controlled by UnifiedFloatingHub.
          Gated by CHAT_ENABLED — when false the component is unmounted
          entirely, which stops every chat poll. */}
      {CHAT_ENABLED && (
        <InternalChat
          currentUser={currentUser}
          externalOpen={showInternalChat}
          onOpenChange={setShowInternalChat}
          hideFloatingButton={true}
        />
      )}

      {/* Afikim AI - Controlled by UnifiedFloatingHub.
          Gated by AI_ENABLED — when false the component is unmounted so
          the lazy-imported chat module never runs, and the entry point in
          UnifiedFloatingHub is filtered out below. */}
      {AI_ENABLED && (
        <AfkimAIChat
          externalOpen={showAIChat}
          onOpenChange={setShowAIChat}
          hideFloatingButton={true}
          currentUser={currentUser?.name || currentUser?.firstName || currentUser?.username || ''}
          currentUserId={currentUser?.username || ''}
        />
      )}

      {/* WebRTC Softphone - Browser-Based Phone with JsSIP */}
      <SoftphoneProvider currentUser={currentUser}>
        {/* SoftphoneUI removed - replaced by CommunicationCenter */}
        <IncomingCallPopup />
        <CallHighlightIndicator />

        {/* Communication Center - Admin only */}
        {(permIsAdmin(currentUser)) && (
          <Suspense fallback={<LoadingFallback />}>
            <CommunicationCenter
              isOpen={showCommunicationCenter}
              onClose={() => setShowCommunicationCenter(false)}
              onMinimize={() => setShowCommunicationCenter(false)}
              currentUser={currentUser}
            />
          </Suspense>
        )}

        {/* Unified Floating Hub — owner directive 2026-06-29:
            phone tile removed; legacy Communication Center tile removed;
            WhatsApp tile now opens the new Workspace V2 (single entry point). */}
        <UnifiedFloatingHub
          phoneEnabled={false}
          workspaceEnabled={false}
          onOpenWhatsApp={() => navigate('/workspace')}
          onOpenInternalChat={() => setShowInternalChat(true)}
          chatEnabled={CHAT_ENABLED}
          aiEnabled={AI_ENABLED}
          onOpenAI={() => setShowAIChat(true)}
          phoneConnected={false}
          hasActiveCall={false}
          unreadMessages={{
            phone: 0,
            whatsapp: 0,
            chat: 0,
            ai: 0,
            workspace: 0
          }}
        />
      </SoftphoneProvider>

      {/* 2026-09-02 — WhatsAppNotificationPopup removed. It polled
          /api/whatsapp/status every 15s and /api/whatsapp/chats every 60s
          on every route for every signed-in user, and neither result had
          a consumer: the only thing that could set `notification` was
          showNotification(), which had no callers, so the render guard
          `if (!isConnected || !notification) return null` never opened
          and the component could not display anything. The `onOpenChat`
          prop it was given here was a console.log, so even a rendered
          popup led nowhere.
          Incoming WhatsApp is surfaced by AgentStatusManager's
          incoming-call popup (live) and by the Workspace conversation
          list. See docs/design/wa-extraction/64-pr-a-heartbeat.md. */}

      {/* Theme Selector Panel */}
      <ThemeSelectorPanel
        isOpen={showThemePanel}
        onClose={() => setShowThemePanel(false)}
      />

      {/* Telephony Dashboard - Full Management Interface */}
      {showTelephonyDashboard && (
        <Suspense fallback={<LoadingFallback />}>
          <TelephonyDashboard
            onClose={() => setShowTelephonyDashboard(false)}
            initialTab={telephonyDashboardInitialTab}
          />
        </Suspense>
      )}

      {/* Minimized Wizard Bubbles - רנדור בנפרד דרך Portal */}
      {salesWizards.filter(w => w.minimized).map((wizard, index) => (
        <MinimizedSalesWizardBubble
          key={wizard.id}
          wizardId={wizard.id}
          bubbleOffset={index * 90}
          onRestore={() => updateWizardMinimized(wizard.id, false)}
          onClose={() => closeSalesWizard(wizard.id)}
        />
      ))}
      {serviceWizards.filter(w => w.minimized).map((wizard, index) => (
        <MinimizedServiceWizardBubble
          key={wizard.id}
          wizardId={wizard.id}
          bubbleOffset={(salesWizards.filter(w => w.minimized).length + index) * 90}
          onRestore={() => updateServiceWizardMinimized(wizard.id, false)}
          onClose={() => closeServiceWizard(wizard.id)}
        />
      ))}
    </div>
  );
}

// ========================================
// App Launcher Modal
// ========================================
const AppLauncherModal = ({ objects, currentUser, onSelect, onClose, getObjectConfig, onSetup, onNavigate, onOpenComponent }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);
  const isAdmin = permIsAdmin(currentUser);
  // ארנק הנה"ח is gated STRICTLY to the 3 finance recipients (#147 allow-list).
  // Per owner (2026-05-05): admins (e.g., elroei) should NOT see this entry —
  // it's the finance team's tool, not an admin-debug surface. If an admin
  // needs to inspect the wallet, they can impersonate one of the finance users.
  const ACCOUNTING_EMAILS = ['finance2@afikim-water.co.il', 'finance3@afikim-water.co.il', 'finance4@afikim-water.co.il'];
  const _userKey = (currentUser?.email || currentUser?.username || '').toLowerCase();
  const isAccountingUser = ACCOUNTING_EMAILS.includes(_userKey);
  // 2026-05-14: broader accounting-profile check (vs. the strict 3-recipient
  // email allowlist above used only for ארנק הנה"ח). Anyone whose profile
  // is 'accounting' / 'הנהלת חשבונות' qualifies — used for tiles the
  // accounting team broadly needs (e.g. the tech-sales bonus report).
  const isAccountingProfile = ['accounting', 'הנהלת חשבונות'].includes(currentUser?.profile);

  // Pinned-items catalog now lives in frontend/src/config/appLauncherItems.js
  // (extracted 2026-05-14 so the Setup "עורך תפריטים" editor can share it).
  // === Saved Owner Dashboards in the launcher (2026-08-20) ===
  // The registry is the server's, not a list in this file. A dashboard appears
  // here because someone published it (`launcher_enabled`), and the endpoint is
  // behind the same 403 as every other owner-dashboard route — so an
  // unauthorised user receives no entries because they receive no response.
  // That keeps the launcher dynamic without turning it into a second
  // access-control surface.
  const [ownerDashboards, setOwnerDashboards] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Same header the rest of the app uses — the session is a bearer
        // token in localStorage, not a cookie, so `credentials: 'include'`
        // would arrive anonymous and be answered 403.
        const t = localStorage.getItem('session_token') ||
                  sessionStorage.getItem('session_token') || '';
        const r = await fetch(`${API_BASE}/api/owner-dashboard/dashboards/registry/launcher`,
                              { credentials: 'include',
                                headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (!r.ok) return;                       // 403 for anyone not permitted
        const d = await r.json();
        if (!cancelled) setOwnerDashboards(d.entries || []);
      } catch (e) {
        // Non-blocking: the launcher still works without its dashboard entries.
        console.debug('[AppLauncher] owner-dashboard registry unavailable:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  const hiddenObjects = useHiddenObjects(currentUser && (currentUser.id || currentUser.username));
  const allPinnedItems = useMemo(() => [
    ...APP_LAUNCHER_PINNED_ITEMS,
    // Ordinary launcher items. The registry endpoint that produced this list
    // already returned 403 to anyone not permitted, so an unauthorised user
    // never receives the entries in the first place.
    ...ownerDashboards.map(d => ({ ...d, type: 'link' })),
  ], [ownerDashboards]);

  // === AppLauncher per-user visibility (עורך תפריטים, 2026-05-14) ===
  // Fetch the effective allowed component_ids for the current user.
  // ⚠️ 2026-10-14 owner-spec: a user NO app_launcher_permission record covers
  // (`unrestricted: true`) sees NO app tiles — only listed users see any. Until
  // then such a user saw everything. Administrators keep the old behaviour (all
  // tiles when uncovered). Not loaded yet / failed to load = nothing for a
  // non-admin: an error must not open the launcher. Only 'component' / 'link'
  // tiles are filtered; 'object' tiles are always shown per owner spec.
  const [launcherPerm, setLauncherPerm] = useState({ loaded: false, unrestricted: false, allowed: new Set() });
  useEffect(() => {
    const uid = currentUser?.id || currentUser?.Id || currentUser?.email || currentUser?.username || '';
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/app-launcher-permissions/effective/${encodeURIComponent(uid)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setLauncherPerm({
          loaded: true,
          unrestricted: !!d.unrestricted,
          allowed: new Set(d.allowed_component_ids || []),
        });
      } catch (e) {
        // Non-blocking: on error a non-admin keeps seeing no app tiles (fail closed).
        console.debug('[AppLauncher] effective-permissions fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // 2026-05-18: device-replacement-allocation dashboard owner gate.
  // Hardcoded single extra email per owner spec — when a second
  // non-admin owner is requested, move this list into a config table.
  const _allocOwnerEmails = new Set(['amitai@afikim-water.co.il']);
  const isDeviceAllocOwner = _allocOwnerEmails.has((currentUser?.email || '').toLowerCase());

  // Filter role-gated AND per-user-visibility items
  const pinnedItems = allPinnedItems.filter(item => {
    // ניהול הרשאות רשומות (PR 5): an object tile for an object hidden from this user.
    if (item.type === 'object' && isObjectHidden(hiddenObjects, item.api_name || item.id)) return false;
    // 2026-07-29 owner-req: an EXPLICIT per-user grant via Setup → עורך תפריטים
    // overrides the *soft* profile gate (adminOrAccountingProfile). The admin
    // deliberately assigned this tile to the user, so honor it even if their
    // profile isn't accounting/admin. Only link/component tiles are grantable;
    // the hard adminOnly / accountingOnly gates are NOT overridable this way.
    const grantedViaEditor = !launcherPerm.unrestricted
      && (item.type === 'component' || item.type === 'link')
      && launcherPerm.allowed.has(item.id);
    if (item.adminOnly                  && !isAdmin) return false;
    if (item.accountingOnly             && !isAccountingUser) return false;
    if (item.adminOrAccountingProfile   && !(isAdmin || isAccountingProfile) && !grantedViaEditor) return false;
    if (item.deviceAllocationOwnerOrAdmin && !(isAdmin || isDeviceAllocOwner)) return false;
    // Grant-required tiles: silence in the menu editor means NO (the API says the same).
    if (item.requiresGrant              && !(isAdmin || grantedViaEditor)) return false;
    // Per-user visibility (managed via Setup → עורך תפריטים).
    // Only components + links are managed; objects always pass through.
    // Items with an explicit owner gate (e.g. deviceAllocationOwnerOrAdmin)
    // bypass the per-user filter — the gate IS the source of truth for them.
    if (!item.deviceAllocationOwnerOrAdmin
        && (item.type === 'component' || item.type === 'link')) {
      const listed = launcherPerm.loaded && !launcherPerm.unrestricted;
      if (listed && !launcherPerm.allowed.has(item.id)) return false;
      // Not listed in any record (or not loaded): nothing, unless administrator.
      if (!listed && !isAdmin) return false;
    }
    return true;
  });

  // הכן את כל האובייקטים עם הקונפיגורציה שלהם
  const allItems = objects.filter(obj => !isObjectHidden(hiddenObjects, obj.api_name)).map(obj => ({
    ...obj,
    ...getObjectConfig(obj.api_name),
    type: 'object'
  }));

  // סינון לפי חיפוש - כולל גם פריטים קבועים וגם אובייקטים
  const filteredPinned = searchTerm
    ? pinnedItems.filter(i =>
        i.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : pinnedItems;

  const filteredObjects = searchTerm
    ? allItems.filter(i =>
        i.heLabel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.api_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allItems;

  // מיון: אובייקטים עם נתונים קודם, אחר כך לפי שם
  const sortedObjects = [...filteredObjects].sort((a, b) => {
    if ((a.record_count || 0) > 0 && (b.record_count || 0) === 0) return -1;
    if ((b.record_count || 0) > 0 && (a.record_count || 0) === 0) return 1;
    return (a.heLabel || a.api_name).localeCompare(b.heLabel || b.api_name);
  });

  // הצג את כל האובייקטים רק בחיפוש או ב"הצג הכל"
  const displayObjects = showAll || searchTerm ? sortedObjects : [];

  const handleItemClick = (item) => {
    if (item.type === 'component') {
      onOpenComponent && onOpenComponent(item.component);
      onClose();
    } else if (item.type === 'link' && item.path) {
      window.location.href = item.path;
      onClose();
    } else {
      // אובייקט רגיל
      const obj = objects.find(o => o.api_name === item.api_name || o.api_name === item.id);
      if (obj) {
        onSelect({ ...obj, ...getObjectConfig(obj.api_name) });
      }
    }
  };

  return (
    <div className="slds-app-launcher-modal">
      {/* Backdrop first, then content */}
      <div className="slds-app-launcher__backdrop" onClick={onClose}></div>
      <div className="slds-app-launcher">
        <div className="slds-app-launcher__header">
          <button className="slds-app-launcher__close" onClick={onClose}>✕</button>
          <h2>משגר היישומים</h2>
        </div>

        <div className="slds-app-launcher__search">
          <input
            type="search"
            placeholder="חפש יישומים ופריטים..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <span className="slds-search-icon">🔍</span>
        </div>

        <div className="slds-app-launcher__content">
          {/* פריטים קבועים */}
          {filteredPinned.length > 0 && (
            <div className="slds-app-launcher__section">
              <h3>יישומים</h3>
              <div className="slds-app-launcher__items">
                {filteredPinned.map((item) => (
                  <a
                    key={item.id}
                    href={item.type === 'object' ? `/object/${item.api_name || item.id}` : item.type === 'link' ? (item.path || '#') : '#'}
                    className="slds-app-launcher__item"
                    onClick={(e) => { e.preventDefault(); handleItemClick(item); }}
                  >
                    <span className="slds-item-icon" style={{ backgroundColor: item.iconImage ? 'transparent' : item.color }}>
                      {item.iconImage ? (
                        <img src={item.iconImage} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                      ) : item.icon}
                    </span>
                    <span className="slds-item-name">{item.label}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* אובייקטים - רק בחיפוש או הצג הכל */}
          {(searchTerm || showAll) && displayObjects.length > 0 && (
            <div className="slds-app-launcher__section">
              <h3>פריטים ({filteredObjects.length})</h3>
              <div className="slds-app-launcher__items" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                {displayObjects.map((item) => (
                  <a
                    key={item.api_name}
                    href={`/object/${item.api_name}`}
                    className="slds-app-launcher__item"
                    onClick={(e) => { e.preventDefault(); handleItemClick(item); }}
                  >
                    <span className="slds-item-icon" style={{ backgroundColor: item.iconImage ? 'transparent' : item.color }}>
                      {item.iconImage ? (
                        <img src={item.iconImage} alt={item.heLabel} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                      ) : item.icon}
                    </span>
                    <span className="slds-item-name">{item.heLabel}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {!searchTerm && !showAll && (
            <button
              className="slds-app-launcher__view-all"
              onClick={() => setShowAll(true)}
            >
              הצג הכל ({allItems.length} פריטים)
            </button>
          )}
          {showAll && !searchTerm && (
            <button
              className="slds-app-launcher__view-all"
              onClick={() => setShowAll(false)}
            >
              הצג פחות
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ========================================
// Navigation Editor Modal
// ========================================
const NavEditorModal = ({ navItems, objects, getObjectConfig, onSave, onClose }) => {
  const [items, setItems] = useState([...navItems]);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Get all available objects that can be added
  const availableObjects = objects.filter(obj =>
    !items.some(item => item.id === obj.api_name)
  ).map(obj => ({
    id: obj.api_name,
    label: getObjectConfig(obj.api_name).heLabel,
    icon: getObjectConfig(obj.api_name).icon,
    color: getObjectConfig(obj.api_name).color
  }));

  // Filter by search
  const filteredAvailable = searchTerm
    ? availableObjects.filter(obj =>
        obj.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        obj.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableObjects;

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const addItem = (obj) => {
    setItems([...items, obj]);
  };

  const removeItem = (index) => {
    if (items[index].id === 'home') return; // Can't remove home
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const moveItem = (index, direction) => {
    if (index === 0 && direction === -1) return;
    if (index === items.length - 1 && direction === 1) return;

    const newItems = [...items];
    const item = newItems[index];
    newItems.splice(index, 1);
    newItems.splice(index + direction, 0, item);
    setItems(newItems);
  };

  return (
    <div className="slds-nav-editor-modal">
      <div className="slds-nav-editor">
        <div className="slds-nav-editor__header">
          <h2>ערוך פריטי ניווט</h2>
          <button className="slds-nav-editor__close" onClick={onClose}>✕</button>
        </div>

        <div className="slds-nav-editor__content">
          {/* Current Navigation Items */}
          <div className="slds-nav-editor__section">
            <h3>פריטים רניווט ({items.length})</h3>
            <p className="slds-nav-editor__hint">גרור כדי לשנות סדר, או השתמש בחצים</p>
            <div className="slds-nav-editor__items">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className={`slds-nav-editor__item ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="slds-nav-editor__drag-handle">⋮⋮</span>
                  <span className="slds-nav-editor__item-icon" style={{ backgroundColor: item.iconImage ? 'transparent' : item.color }}>
                    {item.iconImage ? (
                      <img src={item.iconImage} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                    ) : item.icon}
                  </span>
                  <span className="slds-nav-editor__item-label">{item.label}</span>
                  <div className="slds-nav-editor__item-actions">
                    <button
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                      title="הזז למעלה"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveItem(index, 1)}
                      disabled={index === items.length - 1}
                      title="הזז למטה"
                    >
                      ▼
                    </button>
                    {item.id !== 'home' && (
                      <button
                        className="slds-nav-editor__remove"
                        onClick={() => removeItem(index)}
                        title="הסר"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Available Objects to Add */}
          <div className="slds-nav-editor__section">
            <h3>הוסף פריטים</h3>
            <input
              type="search"
              className="slds-nav-editor__search"
              placeholder="חפש אובייקטים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="slds-nav-editor__available">
              {filteredAvailable.slice(0, 50).map((obj) => (
                <button
                  key={obj.id}
                  className="slds-nav-editor__add-item"
                  onClick={() => addItem(obj)}
                >
                  <span className="slds-nav-editor__item-icon" style={{ backgroundColor: obj.color }}>
                    {obj.icon}
                  </span>
                  <span>{obj.label}</span>
                  <span className="slds-nav-editor__add-icon">+</span>
                </button>
              ))}
              {filteredAvailable.length === 0 && (
                <p className="slds-nav-editor__empty">אין אובייקטים נוספים להוספה</p>
              )}
            </div>
          </div>
        </div>

        <div className="slds-nav-editor__footer">
          <button className="slds-btn slds-btn_neutral" onClick={onClose}>
            ביטול
          </button>
          <button className="slds-btn slds-btn_brand" onClick={() => onSave(items)}>
            שמור
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// Sales Process List Modal
// ========================================
const SalesProcessListModal = ({ processes, onClose, onRefresh, filter, onFilterChange, onOpenProcess, onNavigate, onRemoveProcess }) => {
  const STEPS = {
    'account': { label: 'חשבון', icon: '👤' },
    'opportunity': { label: 'הזדמנות', icon: '💼' },
    'products': { label: 'מוצרים', icon: '📦' },
    'summary': { label: 'טופס סיכום', icon: '📄' },
    'payment': { label: 'תשלום', icon: '💳' },
    'workorder': { label: 'הזמנת עבודה', icon: '🔧' },
    'service': { label: 'פעילות שירות', icon: '📅' }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#0176d3';
      case 'completed': return '#2e844a';
      case 'cancelled': return '#c23934';
      default: return '#706e6b';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'active': return 'פעיל';
      case 'completed': return 'הושלם';
      case 'cancelled': return 'בוטל';
      default: return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) + ' ' + date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="sales-list-modal-overlay">
      <div className="sales-list-modal">
        <div className="sales-list-header">
          <h2>📋 רשימת תהליכי מכירה</h2>
          <button className="sales-list-close" onClick={onClose}>✕</button>
        </div>

        <div className="sales-list-filters">
          <div className="filter-group">
            <label>תאריך:</label>
            <input
              type="date"
              value={filter.date}
              onChange={(e) => onFilterChange({ ...filter, date: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>סטטוס:</label>
            <select
              value={filter.status}
              onChange={(e) => onFilterChange({ ...filter, status: e.target.value })}
            >
              <option value="">הכל</option>
              <option value="active">פעיל</option>
              <option value="completed">הושלם</option>
              <option value="cancelled">בוטל</option>
            </select>
          </div>
          <button className="filter-refresh" onClick={onRefresh}>🔄 רענן</button>
        </div>

        <div className="sales-list-content">
          {processes.length === 0 ? (
            <div className="sales-list-empty">
              <span className="empty-icon">📭</span>
              <p>אין תהליכי מכירה להצגה</p>
            </div>
          ) : (
            <table className="sales-list-table">
              <thead>
                <tr>
                  <th>לקוח</th>
                  <th>הזדמנות</th>
                  <th>שלב נוכחי</th>
                  <th>חתימה</th>
                  <th>סטטוס</th>
                  <th>נוצר</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {processes.map(process => (
                  <tr key={process.id}>
                    <td>
                      {process.account_id ? (
                        <a
                          href={`/object/Account/${process.account_id}`}
                          className="record-link"
                          onClick={(e) => {
                            e.preventDefault();
                            onClose();
                            onNavigate && onNavigate(`/object/Account/${process.account_id}`);
                          }}
                        >
                          {process.account_name || process.account_id}
                        </a>
                      ) : (
                        process.account_name || '-'
                      )}
                    </td>
                    <td>
                      {process.opportunity_id ? (
                        <a
                          href={`/object/Opportunity/${process.opportunity_id}`}
                          className="record-link"
                          onClick={(e) => {
                            e.preventDefault();
                            onClose();
                            onNavigate && onNavigate(`/object/Opportunity/${process.opportunity_id}`);
                          }}
                        >
                          {process.opportunity_name || process.opportunity_id}
                        </a>
                      ) : (
                        process.opportunity_name || '-'
                      )}
                    </td>
                    <td>
                      <span className="step-badge">
                        {STEPS[process.current_step]?.icon} {STEPS[process.current_step]?.label || process.current_step}
                      </span>
                    </td>
                    <td>
                      {process.form_signed ? (
                        <span className="signed-badge">✅ נחתם</span>
                      ) : process.form_id ? (
                        <span className="pending-badge">⏳ ממתין</span>
                      ) : (
                        <span className="no-form-badge">-</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(process.status) }}
                      >
                        {getStatusLabel(process.status)}
                      </span>
                    </td>
                    <td>{formatDate(process.created_at)}</td>
                    <td className="actions-cell">
                      <button
                        className="open-process-btn"
                        onClick={() => onOpenProcess(process)}
                        title="פתח תהליך"
                      >
                        📂 פתח
                      </button>
                      <button
                        className="remove-process-btn"
                        onClick={() => {
                          if (window.confirm(`האם להסיר את התהליך של ${process.account_name || 'לקוח חדש'}?`)) {
                            onRemoveProcess && onRemoveProcess(process.id);
                          }
                        }}
                        title="הסר תהליך"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        .sales-list-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 15000;
          direction: rtl;
        }

        .sales-list-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 1100px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 50px rgba(0,0,0,0.3);
        }

        .sales-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e5e5;
          background: linear-gradient(135deg, #0176d3, #0b5cab);
          border-radius: 12px 12px 0 0;
          color: white;
        }

        .sales-list-header h2 {
          margin: 0;
          font-size: 1.4rem;
        }

        .sales-list-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          font-size: 1.2rem;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sales-list-close:hover {
          background: rgba(255,255,255,0.3);
        }

        .sales-list-filters {
          display: flex;
          gap: 20px;
          padding: 16px 24px;
          background: #f4f6f9;
          border-bottom: 1px solid #e5e5e5;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-group label {
          font-weight: 600;
          color: #444;
        }

        .filter-group input,
        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #d8dde6;
          border-radius: 6px;
          font-size: 14px;
        }

        .filter-refresh {
          padding: 8px 16px;
          background: #0176d3;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .filter-refresh:hover {
          background: #0b5cab;
        }

        .sales-list-content {
          flex: 1;
          overflow: auto;
          padding: 0;
        }

        .sales-list-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #706e6b;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 16px;
        }

        .sales-list-table {
          width: 100%;
          border-collapse: collapse;
        }

        .sales-list-table th,
        .sales-list-table td {
          padding: 14px 16px;
          text-align: right;
          border-bottom: 1px solid #e5e5e5;
        }

        .sales-list-table th {
          background: #fafaf9;
          font-weight: 700;
          color: #444;
          position: sticky;
          top: 0;
        }

        .sales-list-table tr:hover {
          background: #f4f6f9;
        }

        .step-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #e8f4fd;
          border-radius: 12px;
          font-size: 13px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          color: white;
          font-size: 12px;
          font-weight: 600;
        }

        .signed-badge {
          color: #2e844a;
          font-weight: 600;
        }

        .pending-badge {
          color: #ff9800;
          font-weight: 600;
        }

        .no-form-badge {
          color: #999;
        }

        .open-process-btn {
          padding: 6px 14px;
          background: #0176d3;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }

        .open-process-btn:hover {
          background: #0b5cab;
        }

        .actions-cell {
          display: flex;
          gap: 8px;
        }

        .remove-process-btn {
          padding: 6px 10px;
          background: #fff;
          color: #dc3545;
          border: 1px solid #dc3545;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }

        .remove-process-btn:hover {
          background: #dc3545;
          color: white;
        }

        .record-link {
          color: #0176d3;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s;
        }

        .record-link:hover {
          color: #0b5cab;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

// ========================================
// Service Process List Modal - מציג תהליכי שירות בטיפול מ-localStorage
// ========================================
const ServiceProcessListModal = ({ onClose, onOpenProcess, currentUser }) => {
  const [processes, setProcesses] = useState([]);

  // Load service wizard data from localStorage
  useEffect(() => {
    const loadProcesses = () => {
      const processesData = [];

      // Scan localStorage for service wizard data
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('serviceWizardData_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            const wizardId = key.replace('serviceWizardData_', '');

            // Only include processes that are NOT in account step (first step)
            if (data && data.currentStep && data.currentStep !== 'account') {
              processesData.push({
                id: wizardId,
                ...data,
                savedAt: data.savedAt || Date.now()
              });
            }
          } catch (e) {
            console.error('Error parsing service wizard data:', e);
          }
        }
      }

      // Sort by savedAt (newest first)
      processesData.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
      setProcesses(processesData);
    };

    loadProcesses();
  }, []);

  const getStepName = (step) => {
    const steps = {
      account: 'בחירת לקוח',
      asset: 'בחירת נכס',
      warranty: 'בדיקת אחריות',
      history: 'היסטוריית מקרים',
      case: 'יצירת מקרה',
      technician: 'תיאום טכנאי',
      summary: 'סיכום'
    };
    return steps[step] || step;
  };

  const getStepColor = (step) => {
    const colors = {
      account: '#1976d2',
      asset: '#9c27b0',
      warranty: '#ff9800',
      history: '#607d8b',
      case: '#f44336',
      technician: '#4caf50',
      summary: '#2e7d32'
    };
    return colors[step] || '#6b7280';
  };

  const getCustomerName = (data) => {
    if (data.accountData?.FirstName || data.accountData?.LastName) {
      return `${data.accountData.FirstName || ''} ${data.accountData.LastName || ''}`.trim();
    }
    if (data.accountData?.Name) {
      return data.accountData.Name;
    }
    return 'לא נבחר לקוח';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenProcess = (process) => {
    onOpenProcess({ id: process.id });
    onClose();
  };

  const handleDeleteProcess = (processId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק תהליך זה?')) {
      localStorage.removeItem(`serviceWizardData_${processId}`);
      localStorage.removeItem(`serviceWizardMinimized_${processId}`);
      setProcesses(prev => prev.filter(p => p.id !== processId));
    }
  };

  return (
    <div className="service-list-modal-overlay">
      <div className="service-list-modal">
        <div className="service-list-header">
          <h2>🔧 לקוחות שירות בטיפול: {currentUser?.name || currentUser?.username || 'משתמש'}</h2>
          <button className="service-list-close" onClick={onClose}>✕</button>
        </div>

        <div className="service-list-content">
          {processes.length === 0 ? (
            <div className="service-list-empty">
              <span className="empty-icon">📭</span>
              <p>אין תהליכי שירות בטיפול</p>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px' }}>
                תהליכים יופיעו כאן לאחר שתתחיל תהליך שירות ותעבור משלב בחירת הלקוח
              </p>
            </div>
          ) : (
            <table className="service-list-table">
              <thead>
                <tr>
                  <th>לקוח</th>
                  <th>נכס</th>
                  <th>שלב נוכחי</th>
                  <th>עודכן לאחרונה</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {processes.map(proc => (
                  <tr key={proc.id}>
                    <td>{getCustomerName(proc)}</td>
                    <td>{proc.assetData?.Name || proc.assetData?.Product2?.Name || '-'}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStepColor(proc.currentStep) }}
                      >
                        {getStepName(proc.currentStep)}
                      </span>
                    </td>
                    <td>{formatDate(proc.savedAt)}</td>
                    <td className="actions-cell">
                      <button
                        className="open-process-btn"
                        onClick={() => handleOpenProcess(proc)}
                        title="המשך תהליך"
                      >
                        ▶️ המשך
                      </button>
                      <button
                        className="delete-process-btn"
                        onClick={() => handleDeleteProcess(proc.id)}
                        title="מחק תהליך"
                        style={{
                          background: '#dc3545',
                          marginRight: '8px'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        .service-list-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 15000;
          direction: rtl;
        }

        .service-list-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 1100px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 50px rgba(0,0,0,0.3);
        }

        .service-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e5e5;
          background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
          border-radius: 12px 12px 0 0;
          color: white;
        }

        .service-list-header h2 {
          margin: 0;
          font-size: 1.4rem;
        }

        .service-list-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          font-size: 1.2rem;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }

        .service-list-close:hover {
          background: rgba(255,255,255,0.3);
        }

        .service-list-filters {
          display: flex;
          gap: 20px;
          padding: 16px 24px;
          background: #f4f6f9;
          border-bottom: 1px solid #e5e5e5;
          align-items: center;
          flex-wrap: wrap;
        }

        .service-list-content {
          flex: 1;
          overflow-y: auto;
          padding: 0;
        }

        .service-list-loading,
        .service-list-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #666;
        }

        .service-list-empty .empty-icon {
          font-size: 3rem;
          margin-bottom: 16px;
        }

        .service-list-table {
          width: 100%;
          border-collapse: collapse;
        }

        .service-list-table th {
          background: #f8f9fa;
          padding: 14px 16px;
          text-align: right;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #e5e5e5;
          position: sticky;
          top: 0;
        }

        .service-list-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .service-list-table tr:hover {
          background: #f5f0f7;
        }

        .service-list-table .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          color: white;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .service-list-table .actions-cell {
          white-space: nowrap;
        }

        .service-list-table .open-process-btn {
          background: #9c27b0;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: all 0.2s;
        }

        .service-list-table .open-process-btn:hover {
          background: #7b1fa2;
        }
      `}</style>
    </div>
  );
};

// ========================================
// ========================================
// Serial Products Modal (Admin only)
// ========================================
// 2026-06-21 PR-A: this top-level MAIN_WAREHOUSE_ID constant was dead code
// (never referenced inside SerialProductsModal). Removed. The two live
// usages in the WorkOrder cache loader fetch the value from
// /api/config/main-warehouse-id and use it locally.

function generateSerials(startSerial, count) {
  const match = startSerial.match(/^(.*?)(\d+)$/);
  if (!match) return [startSerial];
  const prefix = match[1];
  const numStr = match[2];
  const startNum = parseInt(numStr, 10);
  const padding = numStr.length;
  const serials = [];
  for (let i = 0; i < count; i++) {
    serials.push(prefix + String(startNum + i).padStart(padding, '0'));
  }
  return serials;
}

const SerialProductsModal = ({ onClose, currentUser }) => {
  const [step, setStep] = useState(1);
  const [productItems, setProductItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPI, setSelectedPI] = useState(null);
  const [serialCounts, setSerialCounts] = useState({ free: 0, saved: 0, total: 0 });
  const [startSerial, setStartSerial] = useState('');
  const [quantity, setQuantity] = useState('');
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Load product items from dedicated endpoint (DB join with product names + serial counts)
  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/warehouse-product-items`);
      const data = await res.json();
      if (data.success) {
        setProductItems(data.items || []);
      } else {
        setError(data.error || 'שגיאה בטעינת פריטי מוצר');
      }
    } catch (err) {
      setError('שגיאה בטעינת פריטי מוצר');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // When a product item is selected, use pre-loaded counts
  const selectProductItem = (pi) => {
    setSelectedPI(pi);
    setSerialCounts({ free: pi.freeCount || 0, saved: pi.savedCount || 0, total: pi.totalSerials || 0 });
    setStep(2);
    setError(null);
  };

  // Filter product items by search
  const filtered = productItems.filter(pi => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const prodName = (pi.productName || '').toLowerCase();
    const piNum = (pi.ProductItemNumber || '').toLowerCase();
    return piNum.includes(term) || prodName.includes(term);
  });

  // Generate preview
  const handlePreview = () => {
    const count = parseInt(quantity, 10);
    if (!startSerial || !count || count <= 0) {
      setError('יש להזין מספר סידורי ראשון וכמות');
      return;
    }
    if (count > 10000) {
      setError('מקסימום 10,000 יחידות');
      return;
    }
    if (!/\d+$/.test(startSerial)) {
      setError('מספר סידורי חייב להסתיים בספרות');
      return;
    }
    setError(null);
    const serials = generateSerials(startSerial, count);
    setPreview(serials);
  };

  // Create serials
  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/serialized-products/bulk-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productItemId: selectedPI.Id,
          product2Id: selectedPI.Product2Id,
          startSerial,
          count: parseInt(quantity, 10),
          userId: currentUser?.email || currentUser?.username,
          userName: currentUser?.name || currentUser?.firstName
        })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setPreview(null);
      } else {
        setError(data.error || 'שגיאה ביצירה');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Go back to step 2 after creation
  const handleBackToItem = async () => {
    setResult(null);
    setStartSerial('');
    setQuantity('');
    setPreview(null);
    // Reload fresh data from DB (counts changed after creation)
    try {
      const res = await fetch(`${config.api.baseUrl}/api/warehouse-product-items`);
      const data = await res.json();
      if (data.success) {
        setProductItems(data.items || []);
        const updatedPI = (data.items || []).find(i => i.Id === selectedPI.Id);
        if (updatedPI) {
          setSelectedPI(updatedPI);
          setSerialCounts({ free: updatedPI.freeCount || 0, saved: updatedPI.savedCount || 0, total: updatedPI.totalSerials || 0 });
        }
      }
    } catch (err) { /* ignore */ }
    setStep(2);
  };

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
  const modalStyle = {
    background: 'white', borderRadius: '12px', width: '700px', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column', direction: 'rtl', overflow: 'hidden'
  };
  const headerStyle = {
    padding: '16px 20px', background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)',
    color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  };
  const bodyStyle = { padding: '20px', flex: 1, overflowY: 'auto' };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>📦 הוספת מוצרים סיריאליים</h2>
            <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
              {step === 1 ? 'בחירת פריט מוצר' : step === 2 ? 'פרטי פריט מוצר' : 'הוספת סיריאליים'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={bodyStyle}>
          {error && (
            <div style={{ padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
              {error}
              <button onClick={() => setError(null)} style={{ float: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
          )}

          {/* Step 1: Select Product Item */}
          {step === 1 && (
            <>
              <input
                type="text"
                placeholder="חיפוש לפי מספר PI או שם מוצר..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }}
              />
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>טוען פריטי מוצר...</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>מספר PI</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>שם מוצר</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e0e0e0' }}>כמות במלאי</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(pi => (
                      <tr
                        key={pi.Id}
                        onClick={() => selectProductItem(pi)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fff3e0'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 12px', fontWeight: '600', color: '#e65100' }}>{pi.ProductItemNumber}</td>
                        <td style={{ padding: '10px 12px' }}>{pi.productName || '-'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>{pi.QuantityOnHand || 0}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>לא נמצאו פריטי מוצר</td></tr>
                    )}
                  </tbody>
                </table>
              )}
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                {filtered.length} פריטים במחסן הראשי
              </div>
            </>
          )}

          {/* Step 2: Product Item Details + Serial Counts */}
          {step === 2 && selectedPI && (
            <>
              <button onClick={() => { setStep(1); setSelectedPI(null); }} style={{ background: 'none', border: 'none', color: '#e65100', cursor: 'pointer', fontSize: '13px', marginBottom: '12px', padding: 0 }}>
                → חזרה לרשימה
              </button>
              <div style={{ background: '#fff8e1', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#e65100', marginBottom: '8px' }}>
                  {selectedPI.productName || 'מוצר'}
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>מספר: {selectedPI.ProductItemNumber}</div>
                <div style={{ fontSize: '13px', color: '#666' }}>כמות במלאי: {selectedPI.QuantityOnHand || 0}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ background: '#e8f5e9', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>{serialCounts.free}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>פנויים</div>
                    </div>
                    <div style={{ background: '#fff3e0', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#e65100' }}>{serialCounts.saved}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>שמורים</div>
                    </div>
                    <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1565c0' }}>{serialCounts.total}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>סה"כ</div>
                    </div>
                  </div>

                  <button
                    onClick={() => setStep(3)}
                    style={{
                      width: '100%', padding: '14px', background: 'linear-gradient(135deg, #e65100, #bf360c)',
                      color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px',
                      fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    + הוספת סיריאליים
                  </button>
            </>
          )}

          {/* Step 3: Add Serials */}
          {step === 3 && selectedPI && (
            <>
              <button onClick={() => { setStep(2); setPreview(null); setResult(null); }} style={{ background: 'none', border: 'none', color: '#e65100', cursor: 'pointer', fontSize: '13px', marginBottom: '12px', padding: 0 }}>
                → חזרה לפרטי מוצר
              </button>

              {result ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                  <h3 style={{ color: '#2e7d32', margin: '0 0 12px' }}>נוצרו {result.created} סיריאליים!</h3>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>ראשון: <strong>{result.firstSerial}</strong></div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>אחרון: <strong>{result.lastSerial}</strong></div>
                  <button onClick={handleBackToItem} style={{ padding: '10px 30px', background: '#e65100', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    חזרה לפריט מוצר
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ background: '#f5f5f5', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{selectedPI.productName || 'מוצר'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      פנויים: {serialCounts.free} | שמורים: {serialCounts.saved}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>מספר סידורי ראשון</label>
                      <input
                        type="text"
                        value={startSerial}
                        onChange={e => { setStartSerial(e.target.value); setPreview(null); }}
                        placeholder="למשל: 3308546001"
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>כמות יחידות</label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={e => { setQuantity(e.target.value); setPreview(null); }}
                        placeholder="למשל: 4000"
                        min="1"
                        max="10000"
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handlePreview}
                    disabled={!startSerial || !quantity}
                    style={{
                      width: '100%', padding: '12px', background: !startSerial || !quantity ? '#ccc' : '#1565c0',
                      color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', marginBottom: '16px'
                    }}
                  >
                    הצג רשימה
                  </button>

                  {preview && (
                    <>
                      <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                              <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', width: '60px' }}>#</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>מספר סידורי</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.length <= 30 ? (
                              preview.map((s, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                  <td style={{ padding: '6px 12px', textAlign: 'center', color: '#999' }}>{i + 1}</td>
                                  <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{s}</td>
                                </tr>
                              ))
                            ) : (
                              <>
                                {preview.slice(0, 10).map((s, i) => (
                                  <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                    <td style={{ padding: '6px 12px', textAlign: 'center', color: '#999' }}>{i + 1}</td>
                                    <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{s}</td>
                                  </tr>
                                ))}
                                <tr style={{ background: '#f9f9f9' }}>
                                  <td colSpan="2" style={{ padding: '8px 12px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                                    ... {preview.length - 20} פריטים נוספים ...
                                  </td>
                                </tr>
                                {preview.slice(-10).map((s, i) => (
                                  <tr key={`last-${i}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                    <td style={{ padding: '6px 12px', textAlign: 'center', color: '#999' }}>{preview.length - 10 + i + 1}</td>
                                    <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{s}</td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '13px' }}>
                        <strong>סיכום:</strong> {preview.length} סיריאליים | ראשון: {preview[0]} | אחרון: {preview[preview.length - 1]}
                      </div>

                      <button
                        onClick={handleCreate}
                        disabled={creating}
                        style={{
                          width: '100%', padding: '14px',
                          background: creating ? '#ccc' : 'linear-gradient(135deg, #2e7d32, #1b5e20)',
                          color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px',
                          fontWeight: '600', cursor: creating ? 'wait' : 'pointer'
                        }}
                      >
                        {creating ? 'יוצר סיריאליים...' : `אשר וצור ${preview.length} סיריאליים`}
                      </button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Home Page
// ========================================

const HomePage = ({ objects, onNavigate, getObjectConfig, stats, onOpenSalesWizard, onOpenSalesList, onOpenServiceWizard, onOpenServiceList, currentUser, onOpenSerialModal }) => {
  // Check if user should see sales dashboard (personal)
  const userProfile = currentUser?.profile || '';

  // Check if user should see team dashboard (managers)

  // Check if user should see technician dashboard (office management)

  // Owner 2026-10-12: for the users whose home page is the team revenue dashboard
  // (utils/homeDashboards.js) the home page is ONLY that dashboard — no welcome header, no
  // admin module tiles (מני ברזל is an admin and saw them above it).
  if (homeDashboardFor(currentUser) === 'team_revenue') {
    return (
      <div className="home-team-revenue-only" data-testid="home-team-revenue-only">
        <React.Suspense fallback={<div style={{ textAlign: "center", padding: 40, color: "#888" }}>טוען דשבורד...</div>}>
          <TeamRevenueDashboard />
        </React.Suspense>
      </div>
    );
  }

  return (
    <div className="slds-home slds-home--redesigned">
      {/* Header with welcome */}
      <div className="slds-home__header">
        <h1 className="slds-home__title">אפיקים מים</h1>
        <p className="slds-home__subtitle">שלום, {currentUser?.name || 'משתמש'}</p>
        {currentUser?.title && (
          <p className="slds-home__user-title" style={{ fontSize: '14px', color: '#0176d3', fontWeight: '500', margin: '4px 0 0' }}>
            {currentUser.title}
          </p>
        )}
      </div>

      {/* Admin Module Dashboard */}
      {(permIsAdmin(currentUser)) && (
        <div className="admin-dashboard-grid">
          <div className="admin-module-card" onClick={() => { window.location.href = '/procurement'; }}>
            <div className="icon">🛒</div>
            <div className="title">ניהול רכש</div>
            <div className="subtitle">הזמנות רכש, ספקים ומעקב</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = '/warehouse'; }}>
            <div className="icon">🏪</div>
            <div className="title">ניהול מחסן</div>
            <div className="subtitle">מלאי, סיריאליים ותנועות</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = '/accounting'; }}>
            <div className="icon">💳</div>
            <div className="title">הנהלת חשבונות</div>
            <div className="subtitle">חשבוניות, תשלומים ודוחות</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = '/afikim-go'; }}>
            <div className="icon"><img src="/favicon_AG.png" alt="AFIKIM GO" style={{ width: 48, height: 48, objectFit: 'contain' }} /></div>
            <div className="title">אפיקים GO</div>
            <div className="subtitle">ניהול רכב ולוגיסטיקה</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = `${LAB_APP_URL}/login`; }}>
            <div className="icon">🔬</div>
            <div className="title">מעבדה</div>
            <div className="subtitle">בדיקות מעבדה ותוצאות</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = '/reports'; }}>
            <div className="icon">📊</div>
            <div className="title">דוחות</div>
            <div className="subtitle">דוחות מערכת וניתוח נתונים</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.open('https://technician.afikim-water.org/app', '_blank'); }}>
            <div className="icon"><img src="/favicon_AG.png" alt="Technician App" style={{ width: 48, height: 48, objectFit: 'contain' }} /></div>
            <div className="title">אפליקציית טכנאי</div>
            <div className="subtitle">ניהול טכנאים ומשימות שטח</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.open('/supplier-login', '_blank'); }}>
            <div className="icon"><img src="/afikim_ledes_logo.png" alt="Supplier Portal" style={{ width: 48, height: 48, objectFit: 'contain' }} /></div>
            <div className="title">פורטל ספקים</div>
            <div className="subtitle">כניסת ספקים לאזור לידים</div>
          </div>
          <div className="admin-module-card" onClick={() => { window.location.href = '/barcode-management'; }}>
            <div className="icon">📦</div>
            <div className="title">ניהול ברקודים</div>
            <div className="subtitle">ברקודים, מוצרים ו-EAN</div>
          </div>
        </div>
      )}


{/* Home dashboard (utils/homeDashboards.js): Elroi sees the sales-technicians dashboard
    (owner 2026-05-06); everyone else the personal dashboard. The team revenue users never
    reach here — their home page is the dashboard alone (the early return above). */}
      <React.Suspense fallback={<div style={{ textAlign: "center", padding: 40, color: "#888" }}>טוען דשבורד...</div>}>
        {(() => {
          const home = homeDashboardFor(currentUser);
          if (home === 'sales_technicians') return <SalesTechnicianDashboard currentUser={currentUser} />;
          return <PersonalDashboard currentUser={currentUser} />;
        })()}
      </React.Suspense>


      <style>{`
        .slds-home--redesigned {
          padding: 1.5rem;
          background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
          min-height: 100%;
          position: relative;
        }

        .slds-home__header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .slds-home__title {
          font-size: 2rem;
          font-weight: 700;
          color: #0176d3;
          margin: 0 0 0.5rem;
        }

        .slds-home__subtitle {
          font-size: 1.1rem;
          color: #5e6e82;
          margin: 0;
        }

        /* Compact Action Buttons */
        .slds-home__actions {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .slds-home__action-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.5rem;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.25s ease;
          color: white;
        }

        .slds-home__action-btn.primary {
          background: linear-gradient(135deg, #0176d3 0%, #0b5cab 100%);
          box-shadow: 0 4px 16px rgba(1, 118, 211, 0.25);
        }

        .slds-home__action-btn.secondary {
          background: linear-gradient(135deg, #5e6e82 0%, #445261 100%);
          box-shadow: 0 4px 16px rgba(94, 110, 130, 0.25);
        }

        .slds-home__action-btn:hover {
          transform: translateY(-2px);
        }

        .slds-home__action-btn.primary:hover {
          box-shadow: 0 8px 24px rgba(1, 118, 211, 0.35);
        }

        .slds-home__action-btn.secondary:hover {
          box-shadow: 0 8px 24px rgba(94, 110, 130, 0.35);
        }

        .action-icon {
          font-size: 1.25rem;
        }

        .action-text {
          font-size: 0.95rem;
        }

        /* Dashboard Container */
        .slds-home__dashboard {
          margin-top: 0.5rem;
        }

        /* Loading State */
        .dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #5e6e82;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e5e5;
          border-top-color: #0176d3;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 600px) {
          .slds-home--redesigned {
            padding: 1rem;
          }

          .slds-home__title {
            font-size: 1.5rem;
          }

          .slds-home__actions {
            flex-direction: column;
            align-items: stretch;
          }

          .slds-home__action-btn {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

// ========================================
// Setup Page
// ========================================
const SetupPage = ({
  section,
  setSection,
  objects,
  scriptClasses,
  flows,
  metadata,
  stats,
  onObjectClick,
  onScriptClick,
  onFlowClick,
  selectedScript,
  selectedFlow,
  getObjectConfig,
  loadScriptClasses,
  loadFlows,
  setFlexipageCache,
  currentUser
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const setupMenuItems = [
    { id: 'home', label: 'בית', icon: '📋' },
    { id: 'objects', label: 'מנהל אובייקטים', icon: '📋' },
    { id: 'sfbackup', label: 'דפדפן גיבויים', icon: '📋' },
    { id: 'divider1', type: 'divider', label: 'אבטחה ומשתמשים' },
    { id: 'users', label: 'משתמשים', icon: '📋' },
    { id: 'profiles', label: 'פרופילים', icon: '📋' },
    { id: 'permission-sets', label: 'ערכות הרשאות', icon: '📋' },
    { id: 'roles', label: 'תפקידים', icon: '📋' },
    { id: 'record-access', label: 'ניהול הרשאות רשומות', icon: '🔐' },
    { id: 'data-loading-config', label: 'הגדרות טעינת נתונים', icon: '⚙️' },
    { id: 'app-launcher-editor', label: 'עורך תפריטים — משגר היישומים', icon: '🎛️' },
    { id: 'validation-exemptions', label: 'עורך וולידציות', icon: '🛡️' },
    { id: 'nav-menu-editor', label: 'עורך תפריטים — פריטי ניווט', icon: '🧭' },
    { id: 'search-index-editor', label: 'עורך חיפוש גלובלי', icon: '🔎' },
    { id: 'external-api-keys', label: 'מפתחות API חיצוניים', icon: '🔑' },
    { id: 'sales-api-users', label: 'API מכירות — סוכני AI', icon: '🤖' },
    { id: 'feature-flags', label: 'דגלי פיצ׳רים', icon: '🚩' },
    { id: 'wc-product-mapping', label: 'מיפוי מוצרי אתר (WC)', icon: '🛒' },
    { id: 'recycle-bin', label: 'סל מחזור', icon: '🗑️' },
    { id: 'data-export', label: 'ייצוא נתונים', icon: '📤' },
    { id: 'data-loader', label: 'טעינת נתונים', icon: '📥' },
    { id: 'server-management', label: 'ניהול שרתים', icon: '🖥️' },
    { id: 'divider2', type: 'divider', label: 'קוד ואוטומציה' },
    { id: 'apex', label: 'מחלקות סקריפט', icon: '📋' },
    { id: 'triggers', label: 'טריגרים', icon: '✅' },
    { id: 'vf', label: 'דפי תצוגה', icon: '📋' },
    { id: 'flows', label: 'תהליכים', icon: '📋' },
    { id: 'validation', label: 'כללי אימות', icon: '✅' },
    { id: 'divider3', type: 'divider', label: 'טפסים ומסמכים' },
    { id: 'form-templates', label: 'תבניות טפסים', icon: '📝' },
    { id: 'iforms-product-mappings', label: 'שיוך טפסי iForms', icon: '📄' },
    { id: 'otp-settings', label: 'הגדרות חתימה OTP', icon: '🔐' },
    { id: 'divider4', type: 'divider', label: 'WhatsApp ותקשורת' },
    { id: 'whatsapp-connect', label: 'חיבור WhatsApp & SMS', icon: '🔗' },
    { id: 'whatsapp-queues', label: 'ניהול תורים', icon: '📋' },
    { id: 'whatsapp-bots', label: 'בוט אוטומטי', icon: '🤖' },
    { id: 'whatsapp-triggers', label: 'טריגרים אוטומטיים', icon: '⚡' },
    { id: 'whatsapp-templates', label: 'תבניות הודעות', icon: '📝' },
    { id: 'workspace-settings', label: 'הגדרות מרכז התקשורת', icon: '🎨' },
    { id: 'login-design', label: 'עיצוב דף ההתחברות', icon: '🖼️' },
    { id: 'moked-sale-rules', label: 'כללי מכירה — מוקד', icon: '🧮' },
    { id: 'sales-rules', label: 'חוקי מכירה', icon: '⚖️' },
    { id: 'marketer-credit-limits', label: 'תקרות אשראי משווקים', icon: '💳' },
    { id: 'discount-managers', label: 'מנהלי הנחות', icon: '💲' },
    { id: 'reminder-channel-rules', label: 'ערוצי תזכורת לפי קידומת', icon: '📵' },
    { id: 'appointment-reminders', label: 'תזכורות פגישות', icon: '🔔' },
    { id: 'visit-voice-retry-hours', label: 'שעות שיחה חוזרת לתזכורים', icon: '🔁' },
    { id: 'reminder-sequence', label: 'רצף תזכורות', icon: '🔗' },
    { id: 'divider5', type: 'divider', label: 'טלפוניה ומייל' },
    { id: 'telephony', label: 'הגדרות טלפוניה', icon: '📞' },
    { id: 'email-to-case', label: 'Email-to-Case', icon: '📧' },
    { id: 'divider-lab', type: 'divider', label: 'מעבדה' },
    { id: 'lab-checklist', label: 'צ׳קליסט מעבדה', icon: '🔬' },
    { id: 'lab-return-auto-schedule', label: 'תיאום אוטומטי להחזר מעבדה', icon: '🔄' },
    { id: 'task-dashboard-settings', label: 'דשבורד משימות', icon: '📊' },
    { id: 'divider-products', type: 'divider', label: 'מוצרים ומחירונים' },
    { id: 'products-management', label: 'ניהול מוצרים', icon: '📦' },
    { id: 'product-required-rules', label: 'אוטומציות מוצרים נדרשים', icon: '⚙️' },
    { id: 'technician-cash-gate', label: 'חסימת מזומן לטכנאים', icon: '🔒' },
  ];

  return (
    <div className="slds-setup">
      {/* Setup Sidebar */}
      <div className="slds-setup__sidebar">
        <div className="slds-setup__sidebar-header">
          <h2>⚙️ הגדרות</h2>
        </div>
        <div className="slds-setup__search">
          <input
            type="search"
            placeholder="חיפוש מהיר..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <nav className="slds-setup__menu">
          {setupMenuItems.map(item => (
            item.type === 'divider' ? (
              <div key={item.id} className="slds-setup__menu-divider">
                <span>{item.label}</span>
              </div>
            ) : (
              <button
                key={item.id}
                className={`slds-setup__menu-item ${section === item.id ? 'slds-is-active' : ''}`}
                onClick={() => {
                  if (item.id === 'products-management') { navigate('/products-management'); return; }
        if (item.id === 'product-required-rules') { navigate('/product-required-rules'); return; }
        if (item.id === 'technician-cash-gate') { navigate('/technician-cash-gate'); return; }
        if (item.id === 'iforms-product-mappings') { navigate('/iforms-product-mappings'); return; }
                  setSection(item.id);
                  if (item.id === 'apex') loadScriptClasses();
                  if (item.id === 'flows') loadFlows();
                }}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            )
          ))}
        </nav>
      </div>

      {/* Setup Content */}
      <div className={`slds-setup__content ${section === 'form-templates' ? 'slds-setup__content--no-scroll' : ''}`}>
        {section === 'home' && (
          <SetupHome stats={stats} metadata={metadata} setSection={setSection} currentUser={currentUser} />
        )}

        {section === 'objects' && (
          <Suspense fallback={<LoadingFallback />}>
            <ObjectManagerSetup
              onBack={() => setSection('home')}
              onSelectObject={(obj) => {
                // Navigate to object detail page with URL
                navigate(buildSetupUrl('objects', obj.api_name, 'details'));
              }}
              onFlexipageCacheInvalidate={(objectName) => {
                // Clear cache for specific object when default page changes
                setFlexipageCache(prev => {
                  const newCache = { ...prev };
                  delete newCache[objectName];
                  return newCache;
                });
              }}
            />
          </Suspense>
        )}

        {section === 'sfbackup' && (
          <Suspense fallback={<LoadingFallback />}>
            <LegacySystemBackupBrowser />
          </Suspense>
        )}

        {section === 'scheduler' && (
          <Suspense fallback={<LoadingFallback />}>
            <TechnicianScheduler />
          </Suspense>
        )}

        {/* User Management Sections */}
        {section === 'users' && (
          <Suspense fallback={<LoadingFallback />}>
            <UserManagement section="users" />
          </Suspense>
        )}

        {section === 'profiles' && (
          <Suspense fallback={<LoadingFallback />}>
            <UserManagement section="profiles" />
          </Suspense>
        )}

        {section === 'permission-sets' && (
          <Suspense fallback={<LoadingFallback />}>
            <UserManagement section="permissionSets" />
          </Suspense>
        )}

        {section === 'roles' && (
          <Suspense fallback={<LoadingFallback />}>
            <UserManagement section="roles" />
          </Suspense>
        )}

        {section === 'app-launcher-editor' && (
          <Suspense fallback={<LoadingFallback />}>
            <AppLauncherEditor />
          </Suspense>
        )}

        {section === 'record-access' && (
          <Suspense fallback={<LoadingFallback />}>
            <RecordAccessEditor />
          </Suspense>
        )}

        {section === 'validation-exemptions' && (
          <Suspense fallback={<LoadingFallback />}>
            <ValidationExemptionsEditor />
          </Suspense>
        )}

        {/* PR after #807 owner-spec 2026-06-08: dual-server switchover UI. */}
        {section === 'server-management' && (
          <Suspense fallback={<LoadingFallback />}>
            <ServerManagement />
          </Suspense>
        )}

        {section === 'nav-menu-editor' && (
          <Suspense fallback={<LoadingFallback />}>
            <NavMenuEditor />
          </Suspense>
        )}

        {section === 'search-index-editor' && (
          <Suspense fallback={<LoadingFallback />}>
            <SearchIndexEditor />
          </Suspense>
        )}

        {/* WP+WC integration PR #1 — admin manages external API keys here */}
        {section === 'external-api-keys' && (
          <Suspense fallback={<LoadingFallback />}>
            <ExternalApiKeys />
          </Suspense>
        )}

        {/* Sales API for an AI agent — the API user, its key, the kill switch, its day */}
        {section === 'sales-api-users' && (
          <Suspense fallback={<LoadingFallback />}>
            <SalesApiUsers />
          </Suspense>
        )}

        {/* Governance Stage 8.2 — feature flag admin (kill switch in 6s) */}
        {section === 'feature-flags' && (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureFlagsAdmin />
          </Suspense>
        )}

        {/* 2026-10-06 — מוקד sale rules: which deals count, אולימפיק / שאר (admin only). */}
        {section === 'moked-sale-rules' && (
          <Suspense fallback={<LoadingFallback />}>
            <MokedSaleRules />
          </Suspense>
        )}

        {/* 2026-11-01 — sales rules per profile, report vs dashboard (admin only; flag sales_rules.screen). */}
        {section === 'sales-rules' && (
          <Suspense fallback={<LoadingFallback />}>
            <SalesRules />
          </Suspense>
        )}

        {/* 2026-10-09 — marketer credit limits: above the limit no delivery is scheduled (admin edits). */}
        {section === 'marketer-credit-limits' && (
          <Suspense fallback={<LoadingFallback />}>
            <MarketerCreditLimits />
          </Suspense>
        )}

        {/* 2026-10-25 — discount managers + monthly budgets (בקשת הנחה ממנהל; admin edits). */}
        {section === 'discount-managers' && (
          <Suspense fallback={<LoadingFallback />}>
            <DiscountManagersSettings />
          </Suspense>
        )}

        {/* 2026-06-30 — Workspace UI settings: background image, theme,
            default sort. Backed by typed feature flags. */}
        {section === 'workspace-settings' && (
          <Suspense fallback={<LoadingFallback />}>
            <WorkspaceSettings />
          </Suspense>
        )}

        {/* 2026-09-04 — login page appearance: hero image, layout,
            card position/opacity, branding. Live preview renders the
            real login markup through the shared buildLoginCss(). */}
        {section === 'login-design' && (
          <Suspense fallback={<LoadingFallback />}>
            <LoginPageDesigner />
          </Suspense>
        )}

        {/* WP+WC integration PR #3 — admin manages WC product mapping here */}
        {section === 'wc-product-mapping' && (
          <Suspense fallback={<LoadingFallback />}>
            <WcProductMapping />
          </Suspense>
        )}

        {section === 'apex' && (
          <Suspense fallback={<LoadingFallback />}>
            <ScriptManager />
          </Suspense>
        )}

        {section === 'flows' && (
          <FlowsView
            flows={flows}
            selectedFlow={selectedFlow}
            onFlowClick={onFlowClick}
            searchTerm={searchTerm}
            loadFlows={loadFlows}
          />
        )}

        {section === 'vf' && (
          <Suspense fallback={<LoadingFallback />}>
            <LayoutEditor onClose={() => setSection('home')} />
          </Suspense>
        )}

        {section === 'triggers' && (
          <div className="slds-setup__section">
            <h2>טריגרים</h2>
            {metadata?.triggers ? (
              <div className="slds-setup__list">
                {metadata.triggers.map((trigger, idx) => (
                  <div key={idx} className="slds-setup__list-item">
                    <span className="slds-setup__list-icon">💻</span>
                    <span className="slds-setup__list-name">{trigger}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p>לא נמצאו Triggers </p>
            )}
          </div>
        )}

        {section === 'validation' && (
          <div className="slds-setup__section">
            <h2>Validation Rules</h2>
            <div className="slds-setup__list">
              {objects.filter(o => o.validation_rules_count > 0).map((obj, idx) => (
                <div key={idx} className="slds-setup__list-item">
                  <span className="slds-setup__list-icon"></span>
                  <span className="slds-setup__list-name">{obj.api_name}</span>
                  <span className="slds-setup__list-badge">{obj.validation_rules_count} </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {section === 'form-templates' && (
          <Suspense fallback={<LoadingFallback />}>
            <FormTemplateBuilder onBack={() => setSection('home')} />
          </Suspense>
        )}

        {section === 'otp-settings' && (
          <Suspense fallback={<LoadingFallback />}>
            <OtpSettings currentUser={currentUser} />
          </Suspense>
        )}

        {/* 2026-10-18 — תזכורים: allowed channels by prefix (kosher → calls only), admin only. */}
        {section === 'reminder-channel-rules' && (
          <Suspense fallback={<LoadingFallback />}>
            <ReminderChannelRules />
          </Suspense>
        )}

        {/* 2026-10-18 — תזכורים: the follow-up rules (רצף תזכורות), admin only. */}
        {section === 'reminder-sequence' && (
          <Suspense fallback={<LoadingFallback />}>
            <ReminderSequence />
          </Suspense>
        )}

        {section === 'appointment-reminders' && (
          <Suspense fallback={<LoadingFallback />}>
            <ReminderSettings currentUser={currentUser} />
          </Suspense>
        )}

        {/* 2026-10-18 — תזכורים: the hours of the automatic retry call (admin only). */}
        {section === 'visit-voice-retry-hours' && (
          <Suspense fallback={<LoadingFallback />}>
            <VisitVoiceRetryHours />
          </Suspense>
        )}

        {/* 2026-10-20 — lab-return AS-2: the automatic return appointment's settings (admin only). */}
        {section === 'lab-return-auto-schedule' && (
          <Suspense fallback={<LoadingFallback />}>
            <LabReturnAutoSchedule />
          </Suspense>
        )}

        {section === 'email-to-case' && (
          <Suspense fallback={<LoadingFallback />}>
            <EmailToCaseSettings />
          </Suspense>
        )}

        {section === 'whatsapp-templates' && (
          <Suspense fallback={<LoadingFallback />}>
            <WhatsAppTemplateSettings />
          </Suspense>
        )}

        {section === 'lab-checklist' && (
          <Suspense fallback={<LoadingFallback />}>
            <LabChecklistSettings currentUser={currentUser} />
          </Suspense>
        )}
        {section === 'task-dashboard-settings' && (
          <Suspense fallback={<LoadingFallback />}>
            <TaskDashboardSettings currentUser={currentUser} />
          </Suspense>
        )}

        {section === 'data-loading-config' && (
          <Suspense fallback={<LoadingFallback />}>
            <DataLoadingConfig />
          </Suspense>
        )}

        {section === 'recycle-bin' && (
          <Suspense fallback={<LoadingFallback />}>
            <RecycleBin />
          </Suspense>
        )}

        {section === 'data-export' && (
          <Suspense fallback={<LoadingFallback />}>
            <DataExport />
          </Suspense>
        )}

        {section === 'data-loader' && (
          <Suspense fallback={<LoadingFallback />}>
            <LegacyDataImporter />
          </Suspense>
        )}

        {section === 'whatsapp-connect' && (
          <Suspense fallback={<LoadingFallback />}>
            <WhatsAppConnect currentUser={currentUser} />
          </Suspense>
        )}

        {section === 'whatsapp-queues' && (
          <WhatsAppQueueManager currentUser={currentUser} />
        )}

        {section === 'whatsapp-bots' && (
          <Suspense fallback={<LoadingFallback />}>
            <WhatsAppBotBuilder currentUser={currentUser} />
          </Suspense>
        )}

        {section === 'whatsapp-triggers' && (
          <Suspense fallback={<LoadingFallback />}>
            <WhatsAppTriggers currentUser={currentUser} />
          </Suspense>
        )}

        {section === 'telephony' && (
          <Suspense fallback={<LoadingFallback />}>
            <TelephonySetup currentUser={currentUser} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

// ========================================
// Setup Global Search
// ========================================
const SETUP_SEARCH_ITEMS = [
  { id: 'home', label: 'דף הבית להגדרה', section: 'home', category: 'ניווט' },
  { id: 'objects', label: 'מנהל אובייקטים', section: 'objects', category: 'נתונים' },
  { id: 'sfbackup', label: 'דפדפן גיבויים', section: 'sfbackup', category: 'נתונים' },
  { id: 'users', label: 'משתמשים', section: 'users', category: 'אבטחה ומשתמשים' },
  { id: 'profiles', label: 'פרופילים', section: 'profiles', category: 'אבטחה ומשתמשים' },
  { id: 'permission-sets', label: 'ערכות הרשאות', section: 'permission-sets', category: 'אבטחה ומשתמשים' },
  { id: 'roles', label: 'תפקידים', section: 'roles', category: 'אבטחה ומשתמשים' },
  { id: 'record-access', label: 'ניהול הרשאות רשומות', section: 'record-access', category: 'אבטחה ומשתמשים' },
  { id: 'data-loading-config', label: 'הגדרות טעינת נתונים', section: 'data-loading-config', category: 'נתונים' },
  { id: 'recycle-bin', label: 'סל מחזור', section: 'recycle-bin', category: 'אבטחה ומשתמשים' },
  { id: 'data-export', label: 'ייצוא נתונים / Data Export', section: 'data-export', category: 'נתונים' },
  { id: 'apex', label: 'מחלקות סקריפט', section: 'apex', category: 'קוד ואוטומציה' },
  { id: 'triggers', label: 'טריגרים', section: 'triggers', category: 'קוד ואוטומציה' },
  { id: 'vf', label: 'Visualforce / דפי תצוגה', section: 'vf', category: 'קוד ואוטומציה' },
  { id: 'flows', label: 'תהליכים / Flows', section: 'flows', category: 'קוד ואוטומציה' },
  { id: 'validation', label: 'כללי אימות / Validation Rules', section: 'validation', category: 'קוד ואוטומציה' },
  { id: 'form-templates', label: 'תבניות טפסים', section: 'form-templates', category: 'טפסים ומסמכים' },
  { id: 'whatsapp-connect', label: 'חיבור WhatsApp & SMS / Infobip', section: 'whatsapp-connect', category: 'WhatsApp ותקשורת' },
  { id: 'whatsapp-queues', label: 'ניהול תורים WhatsApp', section: 'whatsapp-queues', category: 'WhatsApp ותקשורת' },
  { id: 'whatsapp-bots', label: 'בוט אוטומטי WhatsApp', section: 'whatsapp-bots', category: 'WhatsApp ותקשורת' },
  { id: 'whatsapp-triggers', label: 'טריגרים אוטומטיים WhatsApp', section: 'whatsapp-triggers', category: 'WhatsApp ותקשורת' },
  { id: 'whatsapp-templates', label: 'תבניות הודעות WhatsApp', section: 'whatsapp-templates', category: 'WhatsApp ותקשורת' },
  { id: 'telephony', label: 'הגדרות טלפוניה', section: 'telephony', category: 'טלפוניה ומייל' },
  { id: 'email-to-case', label: 'Email-to-Case / מייל לפנייה', section: 'email-to-case', category: 'טלפוניה ומייל' },
];

const SetupGlobalSearch = ({ navigate, setSetupSection }) => {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const ref = useRef(null);

  const filtered = query.trim()
    ? SETUP_SEARCH_ITEMS.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.category.includes(query)
      )
    : [];

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (item) => {
    setSetupSection(item.section);
    if (item.section === 'home') {
      navigate('/setup');
    } else {
      navigate(`/setup/${item.section}`);
    }
    setQuery('');
    setShowResults(false);
  };

  return (
    <div className="slds-setup-header__search" ref={ref}>
      <input
        type="search"
        placeholder="חפש בהגדרות..."
        className="slds-setup-header__search-input"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
        onFocus={() => { if (query) setShowResults(true); }}
      />
      <span className="slds-setup-header__search-icon">&#128269;</span>
      {showResults && filtered.length > 0 && (
        <div className="slds-setup-search-results">
          {filtered.map(item => (
            <button key={item.id} className="slds-setup-search-results__item" onClick={() => handleSelect(item)}>
              <span className="slds-setup-search-results__label">{item.label}</span>
              <span className="slds-setup-search-results__category">{item.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================
// Setup Home (modern CRM UX)
// ========================================
const SetupHome = ({ stats, metadata, setSection, currentUser }) => {
  const navigate = useNavigate();

  const quickActions = [
    { id: 'users', label: 'משתמשים', icon: '👥', desc: 'ניהול משתמשים, הרשאות ופרופילים' },
    { id: 'objects', label: 'מנהל אובייקטים', icon: '📋', desc: 'שדות, פריסות ואימותים', count: stats?.objects?.csv || 0 },
    { id: 'apex', label: 'מחלקות סקריפט', icon: '💻', desc: 'מחלקות קוד ולוגיקה עסקית', count: stats?.code?.service_classes ?? stats?.code?.apex_classes ?? 0 },
    { id: 'flows', label: 'תהליכים', icon: '🔄', desc: 'זרימות עבודה ואוטומציה', count: stats?.code?.flows || 0 },
    { id: 'triggers', label: 'טריגרים', icon: '⚡', desc: 'טריגרים ואירועים אוטומטיים', count: stats?.code?.triggers || 0 },
    { id: 'form-templates', label: 'תבניות טפסים', icon: '📝', desc: 'יצירה וניהול תבניות' },
  ];

  const adminLinks = [
    { id: 'profiles', label: 'פרופילים', section: 'profiles' },
    { id: 'permission-sets', label: 'ערכות הרשאות', section: 'permission-sets' },
    { id: 'roles', label: 'תפקידים', section: 'roles' },
    { id: 'data-loading-config', label: 'הגדרות טעינת נתונים', section: 'data-loading-config' },
    { id: 'telephony', label: 'הגדרות טלפוניה', section: 'telephony' },
    { id: 'whatsapp-queues', label: 'ניהול תורי WhatsApp', section: 'whatsapp-queues' },
  ];

  return (
    <div className="slds-setup-home">
      {/* Welcome Banner */}
      <div className="slds-setup-home__welcome">
        <h1>Welcome, {currentUser?.name || currentUser?.firstName || 'Admin'}</h1>
        <p>ניהול והגדרת המערכת. עיון בהגדרות, סקירת תכונות ועוד.</p>
      </div>

      {/* Quick Access Cards */}
      <div className="slds-setup-home__cards">
        {quickActions.map(action => (
          <button
            key={action.id}
            className="slds-setup-home__card"
            onClick={() => setSection(action.id)}
          >
            <div className="slds-setup-home__card-body">
              <div className="slds-setup-home__card-title">
                {action.label}
                {action.count !== undefined && <span className="slds-setup-home__card-count">{action.count}</span>}
              </div>
              <div className="slds-setup-home__card-desc">{action.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* System Summary + Quick Links */}
      <div className="slds-setup-home__grid">
        {/* System Summary */}
        <div className="slds-setup-home__section">
          <div className="slds-setup-home__section-header">
            <h2>סיכום מערכת</h2>
          </div>
          <div className="slds-setup-home__summary">
            <div className="slds-setup-home__stat">
              <span className="slds-setup-home__stat-value">{stats?.data?.total_records?.toLocaleString() || 0}</span>
              <span className="slds-setup-home__stat-label">סה"כ רשומות</span>
            </div>
            <div className="slds-setup-home__stat">
              <span className="slds-setup-home__stat-value">{stats?.objects?.csv || 0}</span>
              <span className="slds-setup-home__stat-label">אובייקטים</span>
            </div>
            <div className="slds-setup-home__stat">
              <span className="slds-setup-home__stat-value">{stats?.code?.service_classes ?? stats?.code?.apex_classes ?? 0}</span>
              <span className="slds-setup-home__stat-label">מחלקות סקריפט</span>
            </div>
            <div className="slds-setup-home__stat">
              <span className="slds-setup-home__stat-value">{stats?.code?.flows || 0}</span>
              <span className="slds-setup-home__stat-label">תהליכים</span>
            </div>
            <div className="slds-setup-home__stat">
              <span className="slds-setup-home__stat-value">{stats?.data?.total_size_mb?.toFixed(1) || 0} MB</span>
              <span className="slds-setup-home__stat-label">נפח נתונים</span>
            </div>
          </div>
        </div>

        {/* Admin Quick Links */}
        <div className="slds-setup-home__section">
          <div className="slds-setup-home__section-header">
            <h2>קישורים מהירים</h2>
          </div>
          <div className="slds-setup-home__links">
            {adminLinks.map(link => (
              <button key={link.id} className="slds-setup-home__link" onClick={() => setSection(link.section)}>
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Data Sources Status */}
      <div className="slds-setup-home__section" style={{marginTop: '16px'}}>
        <div className="slds-setup-home__section-header">
          <h2>מקורות נתונים</h2>
        </div>
        <div className="slds-setup-home__sources">
          <div className={'slds-setup-home__source' + (stats?.sources?.metadata_json ? ' active' : '')}>
            <span className="slds-setup-home__source-icon">{stats?.sources?.metadata_json ? '✅' : '❌'}</span>
            <span>Metadata JSON</span>
          </div>
          <div className={'slds-setup-home__source' + (stats?.sources?.csv_directory ? ' active' : '')}>
            <span className="slds-setup-home__source-icon">{stats?.sources?.csv_directory ? '✅' : '❌'}</span>
            <span>CSV Files</span>
          </div>
          <div className={'slds-setup-home__source' + (stats?.sources?.database ? ' active' : '')}>
            <span className="slds-setup-home__source-icon">{stats?.sources?.database ? '✅' : '❌'}</span>
            <span>Database</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// Object Manager
// ========================================
// eslint-disable-next-line no-unused-vars
const ObjectManager = ({ objects, onObjectClick, getObjectConfig, searchTerm }) => {
  const [filter, setFilter] = useState('all');

  let filteredObjects = objects;

  if (filter === 'custom') {
    filteredObjects = objects.filter(o => o.api_name.endsWith('__c'));
  } else if (filter === 'standard') {
    filteredObjects = objects.filter(o => !o.api_name.endsWith('__c'));
  }

  if (searchTerm) {
    filteredObjects = filteredObjects.filter(o =>
      o.api_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getObjectConfig(o.api_name).heLabel.includes(searchTerm)
    );
  }

  return (
    <div className="slds-object-manager">
      <div className="slds-object-manager__header">
        <h2>Object Manager</h2>
        <p>נךצול של האובייקטים רמערשה ({objects.length})</p>
      </div>

      <div className="slds-object-manager__filters">
        <button
          className={`slds-filter-btn ${filter === 'all' ? 'slds-is-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          צשל ({objects.length})
        </button>
        <button
          className={`slds-filter-btn ${filter === 'standard' ? 'slds-is-active' : ''}`}
          onClick={() => setFilter('standard')}
        >
          Standard ({objects.filter(o => !o.api_name.endsWith('__c')).length})
        </button>
        <button
          className={`slds-filter-btn ${filter === 'custom' ? 'slds-is-active' : ''}`}
          onClick={() => setFilter('custom')}
        >
          Custom ({objects.filter(o => o.api_name.endsWith('__c')).length})
        </button>
      </div>

      <div className="slds-object-manager__table">
        <table className="slds-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>API Name</th>
              <th>שדות</th>
              <th>רשומות</th>
              <th>מקור</th>
            </tr>
          </thead>
          <tbody>
            {filteredObjects.map(obj => {
              const config = getObjectConfig(obj.api_name);
              return (
                <tr key={obj.api_name} onClick={() => onObjectClick(obj)}>
                  <td>
                    <span className="slds-object-row">
                      <span className="slds-object-icon-sm" style={{ backgroundColor: config.color }}>
                        {config.icon}
                      </span>
                      {config.heLabel}
                    </span>
                  </td>
                  <td className="slds-api-name">{obj.api_name}</td>
                  <td>{obj.fields_count || '-'}</td>
                  <td>{obj.record_count?.toLocaleString() || '-'}</td>
                  <td>
                    <span className={`slds-source-badge slds-source-${obj.source}`}>
                      {obj.source}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========================================
// Apex Classes View
// ========================================
// eslint-disable-next-line no-unused-vars
const ScriptClassesView = ({ scriptClasses, selectedScript, onScriptClick, searchTerm }) => {
  const filteredClasses = searchTerm
    ? scriptClasses.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : scriptClasses;

  return (
    <div className="slds-apex-view">
      <div className="slds-apex-view__header">
        <h2>מחלקות סקריפט ({scriptClasses.length})</h2>
        <p>קוד ולוגיקה עסקית</p>
      </div>

      <div className="slds-apex-view__content">
        <div className="slds-apex-list">
          {filteredClasses.map((cls, idx) => (
            <button
              key={idx}
              className={`slds-apex-item ${selectedScript?.name === cls.name ? 'slds-is-selected' : ''}`}
              onClick={() => onScriptClick(cls.name)}
            >
              <span className="slds-apex-icon">📋</span>
              <span className="slds-apex-name">{cls.name}</span>
            </button>
          ))}
        </div>

        {selectedScript && (
          <div className="slds-apex-detail">
            <div className="slds-apex-detail__header">
              <h3>{selectedScript.name || 'מחלקת סקריפט'}</h3>
            </div>
            <pre className="slds-code-block">
              {selectedScript.content || selectedScript.body || 'אין קוד זמין'}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// Flows View
// ========================================
const FlowsView = ({ flows, selectedFlow, onFlowClick, searchTerm, loadFlows }) => {
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [editingFlowId, setEditingFlowId] = useState(null);

  const filteredFlows = searchTerm
    ? flows.filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()) || f.label?.toLowerCase().includes(searchTerm.toLowerCase()))
    : flows;

  const handleNewFlow = () => {
    setEditingFlowId(null);
    setShowFlowBuilder(true);
  };

  const handleEditFlow = (flowName) => {
    setEditingFlowId(flowName);
    setShowFlowBuilder(true);
  };

  const handleFlowSave = async (flowData) => {
    if (loadFlows) loadFlows();
    setShowFlowBuilder(false);
    setEditingFlowId(null);
  };

  const handleCloseFlowBuilder = () => {
    setShowFlowBuilder(false);
    setEditingFlowId(null);
  };

  // If flow builder is open, show it full screen
  if (showFlowBuilder) {
    return (
      <FlowBuilder
        flowId={editingFlowId}
        onClose={handleCloseFlowBuilder}
        onSave={handleFlowSave}
      />
    );
  }

  return (
    <div className="slds-flows-view">
      <div className="slds-flows-view__header">
        <div className="slds-flows-view__header-content">
          <div>
            <h2>Flows ({flows.length})</h2>
            <p>תהליכי עבודה אוטומטיים</p>
          </div>
          <button
            className="slds-btn slds-btn_brand slds-btn_new-flow"
            onClick={handleNewFlow}
          >
            <span style={{ marginLeft: '6px' }}>➕</span>
            זרימה חדשה
          </button>
        </div>
      </div>

      <div className="slds-flows-view__content">
        <div className="slds-flows-list">
          {filteredFlows.length === 0 ? (
            <div className="slds-flows-empty">
              <span className="slds-flows-empty__icon">📋</span>
              <h3>אין זרימות</h3>
              <p>לחץ על "זרימה חדשה" כדי ליצור את הזרימה הראשונה שלך</p>
              <button
                className="slds-btn slds-btn_brand"
                onClick={handleNewFlow}
              >
                צור זרימה חדשה
              </button>
            </div>
          ) : (filteredFlows.map((flow, idx) => (
            <div
              key={idx}
              className={`slds-flow-item ${selectedFlow?.name === flow.name ? 'slds-is-selected' : ''}`}
            >
              <button
                className="slds-flow-item__main"
                onClick={() => onFlowClick(flow.name)}
              >
              <span className="slds-flow-icon">🔄</span>
              <div className="slds-flow-info">
                  <span className="slds-flow-name">{flow.label || flow.name}</span>
                  <span className="slds-flow-api">{flow.name}</span>
                </div>
                <span className={`slds-flow-status slds-flow-status--${(flow.status || 'active').toLowerCase()}`}>
                  {flow.status || 'Active'}
                </span>
              </button>
              <button
                className="slds-flow-item__edit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditFlow(flow.name);
                }}
                title="ערוך זרימה"
              >
                ✏️
              </button>
            </div>
          )))}
        </div>

        {selectedFlow && (
          <div className="slds-flow-detail">
            <div className="slds-flow-detail__header">
              <div className="slds-flow-detail__title">
                <h3>{selectedFlow.label || selectedFlow.name}</h3>
                <span className="slds-flow-type">{selectedFlow.type || 'Flow'}</span>
              </div>
              <button
                className="slds-btn slds-btn_neutral"
                onClick={() => handleEditFlow(selectedFlow.name)}
              >
                פתח בעורך
              </button>
            </div>
            <div className="slds-flow-detail__content">
              <div className="slds-flow-info-grid">
                <div className="slds-flow-info-item">
                  <span className="slds-info-label">API Name</span>
                  <span className="slds-info-value">{selectedFlow.name}</span>
                </div>
                <div className="slds-flow-info-item">
                  <span className="slds-info-label">Status</span>
                  <span className="slds-info-value">{selectedFlow.status || 'Active'}</span>
                </div>
                <div className="slds-flow-info-item">
                  <span className="slds-info-label">Type</span>
                  <span className="slds-info-value">{selectedFlow.type || 'Screen Flow'}</span>
                </div>
                {selectedFlow.createdDate && (
                  <div className="slds-flow-info-item">
                    <span className="slds-info-label">Created</span>
                    <span className="slds-info-value">{new Date(selectedFlow.createdDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}</span>
                  </div>
                )}
                {selectedFlow.lastModifiedDate && (
                  <div className="slds-flow-info-item">
                    <span className="slds-info-label">Last Modified</span>
                    <span className="slds-info-value">{new Date(selectedFlow.lastModifiedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })}</span>
                  </div>
                )}
              </div>
              {selectedFlow.description && (
                <div className="slds-flow-description">
                  <h4>תיאור</h4>
                  <p>{selectedFlow.description}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// Record Form Modal (Create/Edit) - Enhanced
// ========================================
const RecordFormModal = ({ object, record, onClose, onSave, fields, recordType, getObjectConfig }) => {
  const [formData, setFormData] = useState(record || {});
  // The record as the form opened: an edit sends only fields that differ from it.
  const openedAsRef = useRef(record || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [compactLayout, setCompactLayout] = useState(null);
  const [createFormLayout, setCreateFormLayout] = useState(null);
  const [loadingLayout, setLoadingLayout] = useState(true);

  const isEdit = !!record?.Id;
  const config = getObjectConfig ? getObjectConfig(object.api_name) : { icon: '📄', color: '#0176d3', heLabel: object.label };

  // Load compact layout and create form layout for the object
  useEffect(() => {
    const loadLayouts = async () => {
      try {
        const [compactRes, createFormRes] = await Promise.all([
          fetch(`${API_BASE}/api/objects/${object.api_name}/compact-layout`),
          fetch(`${API_BASE}/api/objects/${object.api_name}/create-form-layout`)
        ]);
        if (compactRes.ok) {
          const data = await compactRes.json();
          setCompactLayout(data.compact_layout);
        }
        if (createFormRes.ok) {
          const data = await createFormRes.json();
          if (data.create_form_layout) {
            setCreateFormLayout(data.create_form_layout);
            // Apply default values for new records
            if (!record?.Id && data.create_form_layout.defaultValues) {
              setFormData(prev => {
                const updated = { ...prev };
                Object.entries(data.create_form_layout.defaultValues).forEach(([k, v]) => {
                  if (!updated[k]) updated[k] = v;
                });
                return updated;
              });
            }
          }
        }
      } catch (err) {
        console.log('Error loading layouts:', err);
      } finally {
        setLoadingLayout(false);
      }
    };
    loadLayouts();
  }, [object.api_name, record?.Id]);

  // Get required fields - priority: create form layout > compact layout > defaults
  const getRequiredFields = () => {
    // Priority 1: Create form layout (for new records only)
    if (!isEdit && createFormLayout?.sections) {
      return createFormLayout.sections.flatMap(s =>
        (s.columns || []).flat().filter(f => f.required).map(f => f.fieldName)
      );
    }
    if (compactLayout?.required_fields) {
      return compactLayout.required_fields;
    }
    // Default required fields based on object type
    const defaultRequired = ['Name'];
    if (object.api_name === 'Contact') return ['LastName'];
    if (object.api_name === 'Account') return ['Name'];
    if (object.api_name === 'Opportunity') return ['Name', 'StageName', 'CloseDate'];
    if (object.api_name === 'Lead') return ['LastName', 'Company'];
    if (object.api_name === 'Case') return ['Subject'];
    return defaultRequired;
  };

  // Get fields to display - priority: create form layout > compact layout > defaults
  const getDisplayFields = () => {
    // Priority 1: Create form layout (for new records only)
    if (!isEdit && createFormLayout?.sections) {
      return createFormLayout.sections.flatMap(s =>
        (s.columns || []).flat().map(f => f.fieldName)
      );
    }
    if (compactLayout?.fields && compactLayout.fields.length > 0) {
      return compactLayout.fields;
    }
    // Default: show first 10 editable fields
    return fields
      .filter(f => !['Id', 'CreatedDate', 'LastModifiedDate', 'CreatedById', 'LastModifiedById', 'IsDeleted', 'SystemModstamp'].includes(f.name) &&
        !f.name.endsWith('__Target') && !f.name.endsWith('__Name'))
      .slice(0, 12)
      .map(f => f.name);
  };

  const requiredFields = getRequiredFields();
  const displayFieldNames = getDisplayFields();

  // Known field type hints for common fields
  const fieldTypeHints = {
    'Subject': 'Text', 'Status': 'Picklist', 'Priority': 'Picklist', 'StageName': 'Picklist',
    'CloseDate': 'Date', 'ActivityDate': 'Date', 'StartDate': 'Date', 'EndDate': 'Date',
    'DueDate': 'Date', 'SchedStartTime': 'DateTime', 'SchedEndTime': 'DateTime',
    'Amount': 'Currency', 'Quantity': 'Number', 'UnitPrice': 'Currency', 'TotalPrice': 'Currency',
    'Description': 'TextArea', 'Email': 'Email', 'Phone': 'Phone', 'MobilePhone': 'Phone',
    'Website': 'Url', 'Title': 'Text', 'Company': 'Text', 'Industry': 'Picklist',
    'Type': 'Picklist', 'Reason': 'Picklist', 'Origin': 'Picklist',
    'Collect_Old_Device__c': 'boolean',
  };
  // Known picklist values for common fields
  const commonPicklistValues = {
    'Status': [
      { value: 'Not Started', label: 'לא התחיל' }, { value: 'In Progress', label: 'בתהליך' },
      { value: 'Completed', label: 'הושלם' }, { value: 'Waiting on someone else', label: 'ממתין' },
      { value: 'Deferred', label: 'נדחה' }
    ],
    'Priority': [
      { value: 'High', label: 'גבוהה' }, { value: 'Normal', label: 'רגילה' }, { value: 'Low', label: 'נמוכה' }
    ],
    'StageName': [
      { value: 'Prospecting', label: 'חיפוש' }, { value: 'Qualification', label: 'הערכה' },
      { value: 'Needs Analysis', label: 'ניתוח צרכים' }, { value: 'Proposal', label: 'הצעת מחיר' },
      { value: 'Negotiation', label: 'משא ומתן' }, { value: 'Closed Won', label: 'נסגר-זכייה' },
      { value: 'Closed Lost', label: 'נסגר-הפסד' }
    ],
  };

  // Hebrew labels for common field names
  const fieldLabelMap = {
    'Subject': 'נושא', 'Status': 'סטטוס', 'Priority': 'עדיפות', 'StageName': 'שלב',
    'CloseDate': 'תאריך סגירה', 'ActivityDate': 'תאריך פעילות', 'DueDate': 'תאריך יעד',
    'StartDate': 'תאריך התחלה', 'EndDate': 'תאריך סיום',
    'Amount': 'סכום', 'Quantity': 'כמות', 'UnitPrice': 'מחיר ליחידה', 'TotalPrice': 'סה"כ',
    'Description': 'תיאור', 'Email': 'אימייל', 'Phone': 'טלפון', 'MobilePhone': 'נייד',
    'Website': 'אתר', 'Title': 'תפקיד', 'Company': 'חברה', 'Industry': 'ענף',
    'Type': 'סוג', 'Name': 'שם', 'FirstName': 'שם פרטי', 'LastName': 'שם משפחה',
    'AccountId': 'חשבון', 'ContactId': 'איש קשר', 'OwnerId': 'בעלים',
    'WhoId': 'קשור ל (איש קשר)', 'WhatId': 'קשור ל (רשומה)',
    'Account': 'חשבון', 'Contact': 'איש קשר',
    'SchedStartTime': 'שעת התחלה', 'SchedEndTime': 'שעת סיום',
    'Origin': 'מקור', 'Reason': 'סיבה',
    'Collect_Old_Device__c': 'לאסוף מכשיר ישן מהלקוח',
  };

  // Filter and order fields based on display list - create synthetic entries for missing fields
  const displayFields = displayFieldNames
    .map(fieldName => {
      const found = fields.find(f => f.name === fieldName);
      if (found) {
        // Add Hebrew label if missing
        if (!found.label || found.label === found.name) {
          found.label = fieldLabelMap[fieldName] || found.label;
        }
        return found;
      }
      // Create synthetic field definition for fields not in metadata
      const guessedType = fieldTypeHints[fieldName] || (fieldName.endsWith('Id') ? 'Reference' : 'Text');
      const label = fieldLabelMap[fieldName] || fieldName.replace(/__c$/g, '').replace(/([A-Z])/g, ' $1').trim();
      const synth = { name: fieldName, label, type: guessedType };
      if (commonPicklistValues[fieldName]) {
        synth.picklistValues = commonPicklistValues[fieldName];
      }
      return synth;
    });

  // Validate form
  const validateForm = () => {
    const errors = {};
    requiredFields.forEach(fieldName => {
      if (!formData[fieldName] || formData[fieldName].toString().trim() === '') {
        const field = fields.find(f => f.name === fieldName);
        errors[fieldName] = `${field?.label || fieldName} הוא שדה חובה`;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // An edit sends only what changed since the form opened (utils/recordDiff.js) — the
      // full record would write stale values over a change made meanwhile. A create
      // still sends everything.
      const dataToSave = isEdit ? changedFields(openedAsRef.current, formData) : { ...formData };
      if (isEdit && Object.keys(dataToSave).length === 0) {
        setSaving(false);
        onClose?.();
        return;
      }
      const currentUser = recordService.getCurrentUser();
      const now = new Date().toISOString();

      // Add system fields
      if (currentUser) {
        const userId = currentUser.id || currentUser.Id || currentUser.email;
        const userName = currentUser.name || currentUser.Name || currentUser.email;

        if (isEdit) {
          dataToSave.LastModifiedById = userId;
          dataToSave.LastModifiedByName = userName;
          dataToSave.LastModifiedDate = now;
        } else {
          dataToSave.CreatedById = userId;
          dataToSave.CreatedByName = userName;
          dataToSave.CreatedDate = now;
          dataToSave.LastModifiedById = userId;
          dataToSave.LastModifiedByName = userName;
          dataToSave.LastModifiedDate = now;
          dataToSave.OwnerId = userId;
          dataToSave.Owner__Name = userName;
        }
      }

      // Add record type if selected
      if (recordType) {
        dataToSave.RecordTypeId = recordType.id;
        dataToSave['RecordType.Name'] = recordType.label || recordType.name;
      }

      const url = isEdit
        ? `${API_BASE}/api/objects/${object.api_name}/records/${record.Id}`
        : `${API_BASE}/api/objects/${object.api_name}/records`;

      // 2026-06-12: include Authorization header so the backend gates
      // (field-edit permission, CreatedById, audit attribution) see the
      // caller's session. PR-B added a cookie fallback on the backend so
      // this is belt-and-suspenders, but every other authed PUT/POST in
      // the app sends the Bearer too — keeping it consistent here.
      const _sessTok = localStorage.getItem('session_token')
                    || sessionStorage.getItem('session_token');
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(_sessTok ? { 'Authorization': `Bearer ${_sessTok}` } : {}),
        },
        body: JSON.stringify(dataToSave)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const detail = errData?.detail;
        // 2026-05-19: validation errors — expand each error with the field
        // label (Hebrew) + the current value so the user sees WHY the rule
        // rejected the record (not a generic "Validation failed").
        if (detail && typeof detail === 'object' && detail.type === 'validation_error' && Array.isArray(detail.errors)) {
          const fieldsMeta = fields || [];
          const lines = detail.errors.map((e) => {
            let fieldName = e.error_field || '';
            if (!fieldName && e.rule_name) {
              const base = String(e.rule_name).replace(/_validation$/i, '').replace(/_rule$/i, '').toLowerCase();
              const match = fieldsMeta.find((f) => {
                const n = String(f.name || '').toLowerCase().replace(/__c$/, '');
                return n === base;
              });
              if (match) fieldName = match.name;
            }
            const fieldDef = fieldsMeta.find((f) => f.name === fieldName);
            const label = fieldDef?.label || fieldName || '';
            const currentValue = fieldName ? formData?.[fieldName] : undefined;
            const parts = [];
            if (label) parts.push(`שדה: ${label}`);
            if (currentValue !== undefined && currentValue !== '' && currentValue !== null) {
              parts.push(`הערך בשדה: ${currentValue}`);
            }
            if (e.error_message) parts.push(e.error_message);
            return parts.join('\n');
          });
          throw new Error('שגיאת אימות:\n\n' + lines.join('\n\n'));
        }
        // 2026-06-24: backend guards (e.g. PR #1092/#1097 Israeli ID checksum)
        // return { detail: { success:false, field, error, value } }. Older paths
        // use detail.message or a plain string detail. Surface whichever is
        // present so users see "תעודת זהות לא תקינה" instead of "שגיאה בשמירה".
        const _serverMsg = typeof detail === 'string'
          ? detail
          : (detail?.error || detail?.message || '');
        const _fieldPrefix = (detail && typeof detail === 'object' && detail.field)
          ? `שדה ${detail.field}: `
          : '';
        throw new Error((_fieldPrefix + _serverMsg) || 'שגיאה בשמירה');
      }

      const result = await response.json();
      onSave(result.record);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear validation error when field is modified
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const renderField = (field) => {
    const isRequired = requiredFields.includes(field.name);
    const hasError = validationErrors[field.name];
    const fieldType = field.type?.toLowerCase() || 'text';

    return (
      <div key={field.name} className={`slds-form-element ${hasError ? 'slds-has-error' : ''}`}>
        <label className="slds-form-element__label">
          {isRequired && <span className="slds-required" title="חובה">*</span>}
          {field.label || field.name}
        </label>
        <div className="slds-form-element__control">
          {fieldType === 'boolean' || fieldType === 'checkbox' ? (
            <label className="slds-checkbox">
              <input
                type="checkbox"
                checked={formData[field.name] === 'true' || formData[field.name] === true}
                onChange={(e) => handleChange(field.name, e.target.checked)}
              />
              <span className="slds-checkbox_faux"></span>
            </label>
          ) : fieldType === 'textarea' || fieldType === 'longtextarea' ? (
            <textarea
              className="slds-input slds-textarea"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              rows={3}
              required={isRequired}
            />
          ) : fieldType === 'picklist' && field.picklistValues ? (
            <select
              className="slds-input slds-select"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={isRequired}
            >
              <option value="">-- בחר --</option>
              {field.picklistValues.map((opt, i) => {
                const apiVal = opt.value || opt;
                const display = opt.label || getHebrewPicklistValue(object.api_name, field.name, apiVal);
                return <option key={i} value={apiVal}>{display}</option>;
              })}
            </select>
          ) : fieldType === 'reference' || fieldType === 'lookup' || fieldType === 'masterdetail' ? (
            (() => {
              // Resolve target object: prefer field.referenceTo (metadata),
              // then the backend-injected <field>__Target (only present for
              // resolved FKs), then a small conventional table for the standard
              // SF lookups that don't carry referenceTo in our schema (AccountId,
              // ContactId, Product2Id, OwnerId, …).
              const STD_FK = {
                AccountId: 'Account', ContactId: 'Contact', Product2Id: 'Product2',
                OwnerId: 'User', CreatedById: 'User', LastModifiedById: 'User',
                LocationId: 'Location', CampaignId: 'Campaign', CaseId: 'Case',
                LeadId: 'Lead', WorkOrderId: 'WorkOrder', AssetProvidedById: 'User',
                AssetServicedById: 'User', ParentId: object.api_name,
                RootAssetId: 'Asset', AccountRollupId: 'Account',
                ServiceContractId: 'ServiceContract', EntitlementId: 'Entitlement',
              };
              const targetObject =
                field.referenceTo ||
                formData[`${field.name}__Target`] ||
                STD_FK[field.name] ||
                null;
              // Display name lives under several keys depending on field shape:
              //   AccountId      → Account__Name      (strip "Id")
              //   Reseller__c    → Reseller__c__Name  (keep "__c")
              //   <anything>     → <field>__Name      (defensive fallback)
              const strippedId = field.name.endsWith('Id') && field.name !== 'Id'
                ? field.name.slice(0, -2) : null;
              const displayValue =
                (strippedId && formData[`${strippedId}__Name`]) ||
                formData[`${field.name}__Name`] ||
                '';
              return (
                <LookupField
                  object={targetObject}
                  value={formData[field.name] || ''}
                  displayValue={displayValue}
                  onChange={(id, name) => {
                    setFormData(prev => {
                      const next = { ...prev, [field.name]: id };
                      // Mirror the display name into the right __Name slot so
                      // a subsequent re-render keeps showing the picked name
                      // (until the parent re-fetches the record on save).
                      if (strippedId) next[`${strippedId}__Name`] = name;
                      else next[`${field.name}__Name`] = name;
                      return next;
                    });
                    if (validationErrors[field.name]) {
                      setValidationErrors(prev => ({ ...prev, [field.name]: null }));
                    }
                  }}
                  placeholder={`חפש ${field.label || field.name}...`}
                  disabled={!targetObject}
                />
              );
            })()
          ) : (
            <input
              type={
                fieldType === 'date' ? 'date' :
                fieldType === 'datetime' ? 'datetime-local' :
                fieldType === 'number' || fieldType === 'currency' || fieldType === 'double' || fieldType === 'int' ? 'number' :
                fieldType === 'email' ? 'email' :
                fieldType === 'phone' ? 'tel' :
                fieldType === 'url' ? 'url' :
                'text'
              }
              className="slds-input"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={isRequired}
              step={fieldType === 'currency' || fieldType === 'double' ? '0.01' : undefined}
            />
          )}
        </div>
        {hasError && <div className="slds-form-element__help">{hasError}</div>}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '800px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        {/* Header with object icon */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`, color: '#fff', borderRadius: '8px 8px 0 0' }}>
          <button style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '18px', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }} onClick={onClose}>✕</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', fontSize: '20px', background: 'rgba(255,255,255,0.2)' }}>
              {config.icon}
            </span>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#fff' }}>
                {isEdit ? 'עריכת' : 'יצירת'} {config.heLabel || object.label}
              </h2>
              {recordType && (
                <p style={{ fontSize: '13px', opacity: 0.9, margin: '2px 0 0' }}>סוג: {recordType.label || recordType.name}</p>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {loadingLayout ? (
              <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#706e6b' }}>טוען שדות...</div>
            ) : (
              <>
                {error && (
                  <div className="slds-notify slds-notify_alert slds-alert_error" role="alert" style={{ marginBottom: '16px' }}>
                    <span className="slds-icon_container slds-m-right_x-small">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                {Object.keys(validationErrors).length > 0 && (
                  <div className="slds-notify slds-notify_alert slds-alert_warning" style={{ marginBottom: '16px' }}>
                    <span>יש למלא את כל שדות החובה המסומנים ב-*</span>
                  </div>
                )}

                {displayFields.length === 0 ? (
                  <div className="slds-illustration slds-illustration_small" style={{ textAlign: 'center', padding: '32px' }}>
                    <p>לא נמצאו שדות להצגה</p>
                  </div>
                ) : !isEdit && createFormLayout?.sections ? (
                  /* Section-based layout from create form configuration */
                  <div className="slds-form">
                    {createFormLayout.sections.map((section, sIdx) => (
                      <fieldset key={sIdx} className="slds-form-element" style={{ marginBottom: '20px', border: 'none', padding: 0 }}>
                        {section.label && (
                          <legend style={{ fontSize: '14px', fontWeight: 600, color: '#181818', borderBottom: '1px solid #e5e5e5', paddingBottom: '8px', marginBottom: '12px', width: '100%' }}>
                            {section.label}
                          </legend>
                        )}
                        <div style={{ display: 'flex', gap: '24px' }}>
                          {(section.columns || []).map((column, cIdx) => (
                            <div key={cIdx} style={{ flex: 1 }}>
                              {column.map((fieldCfg) => {
                                const field = displayFields.find(f => f.name === fieldCfg.fieldName);
                                if (!field) return null;
                                const fieldWithLabel = fieldCfg.customLabel
                                  ? { ...field, label: fieldCfg.customLabel }
                                  : field;
                                return (
                                  <div key={fieldCfg.fieldName} style={{ marginBottom: '12px' }}>
                                    {renderField(fieldWithLabel)}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                ) : (
                  <div className="slds-form slds-form_compound">
                    <div className="slds-form__row">
                      {displayFields.map((field, idx) => (
                        <div key={field.name} className="slds-form__item" style={{ flex: '1 1 45%', minWidth: '250px' }}>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #e5e5e5', background: '#fafafa', flexShrink: 0 }}>
            <div>
              {!isEdit && <span style={{ fontSize: '12px', color: '#706e6b' }}>הרשומה תישמר עם הבעלים והמשתמש הנוכחי</span>}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={onClose} disabled={saving}
                style={{ padding: '8px 16px', border: '1px solid #d8dde6', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                ביטול
              </button>
              <button type="submit" disabled={saving || loadingLayout}
                style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', background: '#0176d3', color: '#fff', cursor: saving ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: (saving || loadingLayout) ? 0.6 : 1 }}>
                {saving ? 'שומר...' : (isEdit ? 'עדכן' : 'צור רשומה')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// New List View Modal
// ========================================
const NewListViewModal = ({ object, onClose, onSave, existingViews = [] }) => {
  const [viewName, setViewName] = useState('');
  const [viewNameEn, setViewNameEn] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!viewName.trim()) {
      setError('יש להזין שם לרשימת הצפייה');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/objects/${object.api_name}/list-views`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: viewName,
          label_en: viewNameEn || viewName,
          is_default: isDefault,
          is_public: isPublic,
          is_system: false,
          columns: [],
          filters: []
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'שגיאה ביצירת רשימת צפייה');
      }

      const result = await response.json();
      onSave(result.list_view || result);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '420px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
          <button style={{ background: 'transparent', border: 'none', fontSize: '18px', color: '#706e6b', cursor: 'pointer', padding: '4px 8px' }} onClick={onClose}>✕</button>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>רשימת צפייה חדשה</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="slds-modal__content" style={{ padding: '24px' }}>
            {error && (
              <div className="slds-notify slds-notify_alert slds-alert_error" style={{ marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <div className="slds-form-element" style={{ marginBottom: '16px' }}>
              <label className="slds-form-element__label">
                <span className="slds-required">*</span> שם הרשימה (עברית)
              </label>
              <div className="slds-form-element__control">
                <input
                  type="text"
                  className="slds-input"
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  placeholder="לדוגמה: הלקוחות שלי"
                  required
                />
              </div>
            </div>

            <div className="slds-form-element" style={{ marginBottom: '16px' }}>
              <label className="slds-form-element__label">שם הרשימה (אנגלית)</label>
              <div className="slds-form-element__control">
                <input
                  type="text"
                  className="slds-input"
                  value={viewNameEn}
                  onChange={(e) => setViewNameEn(e.target.value)}
                  placeholder="My Customers"
                />
              </div>
            </div>

            <div className="slds-form-element" style={{ marginBottom: '16px' }}>
              <div className="slds-checkbox">
                <input
                  type="checkbox"
                  id="is-default"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <label className="slds-checkbox__label" htmlFor="is-default">
                  <span className="slds-checkbox_faux"></span>
                  <span className="slds-form-element__label">הגדר כברירת מחדל</span>
                </label>
              </div>
            </div>

            <div className="slds-form-element">
              <div className="slds-checkbox">
                <input
                  type="checkbox"
                  id="is-public"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
                <label className="slds-checkbox__label" htmlFor="is-public">
                  <span className="slds-checkbox_faux"></span>
                  <span className="slds-form-element__label">רשימה ציבורית (נראית לכל המשתמשים)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="slds-modal__footer">
            <button type="button" className="slds-button slds-button_neutral" onClick={onClose}>
              ביטול
            </button>
            <button type="submit" className="slds-button slds-button_brand" disabled={saving}>
              {saving ? 'יוצר...' : 'צור רשימה'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// Manage List Views Modal
// ========================================
const ManageListViewsModal = ({ object, listViews, onClose, onUpdate, onDelete }) => {
  const [views, setViews] = useState(listViews);
  const [editingView, setEditingView] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSetDefault = async (view) => {
    setSaving(true);
    try {
      // Update all views - remove default from others, set on this one
      const updatedViews = views.map(v => ({
        ...v,
        is_default: v.id === view.id
      }));

      await fetch(`${API_BASE}/api/objects/${object.api_name}/list-views/${view.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...view, is_default: true })
      });

      setViews(updatedViews);
      if (onUpdate) onUpdate(updatedViews);
    } catch (err) {
      console.error('Error setting default:', err);
    }
    setSaving(false);
  };

  const handleDelete = async (view) => {
    if (view.is_system) {
      alert('לא ניתן למחוק רשימת מערכת');
      return;
    }
    if (!window.confirm(`האם למחוק את הרשימה "${view.label}"?`)) return;

    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/objects/${object.api_name}/list-views/${view.id}`, {
        method: 'DELETE'
      });

      const updatedViews = views.filter(v => v.id !== view.id);
      setViews(updatedViews);
      if (onDelete) onDelete(view);
    } catch (err) {
      console.error('Error deleting view:', err);
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '640px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
          <button style={{ background: 'transparent', border: 'none', fontSize: '18px', color: '#706e6b', cursor: 'pointer', padding: '4px 8px' }} onClick={onClose}>✕</button>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>ניהול רשימות צפייה</h2>
        </div>

        <div className="slds-modal__content" style={{ padding: '0', maxHeight: '400px', overflowY: 'auto' }}>
          <table className="slds-table slds-table_bordered slds-table_cell-buffer">
            <thead>
              <tr>
                <th>שם הרשימה</th>
                <th>סוג</th>
                <th>ברירת מחדל</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {views.map(view => (
                <tr key={view.id}>
                  <td>
                    <span>{view.label}</span>
                    {view.label_en && view.label_en !== view.label && (
                      <span className="slds-text-color_weak" style={{ marginRight: '8px', fontSize: '12px' }}>
                        ({view.label_en})
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`slds-badge ${view.is_system ? 'slds-badge_inverse' : ''}`}>
                      {view.is_system ? 'מערכת' : 'מותאם אישית'}
                    </span>
                  </td>
                  <td>
                    {view.is_default ? (
                      <span className="slds-badge slds-badge_success">✓ ברירת מחדל</span>
                    ) : (
                      <button
                        className="slds-button slds-button_outline-brand slds-button_x-small"
                        onClick={() => handleSetDefault(view)}
                        disabled={saving}
                      >
                        הגדר כברירת מחדל
                      </button>
                    )}
                  </td>
                  <td>
                    {!view.is_system && (
                      <button
                        className="slds-button slds-button_icon slds-button_icon-error"
                        onClick={() => handleDelete(view)}
                        disabled={saving}
                        title="מחק"
                      >
                        🗑️
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="slds-modal__footer">
          <button className="slds-button slds-button_neutral" onClick={onClose}>
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// Column Selector Modal (Enhanced)
// ========================================
const ColumnSelectorModal = ({ availableColumns, selectedColumns, onClose, onSave, formatFieldLabel }) => {
  const [selected, setSelected] = useState(selectedColumns.length > 0 ? selectedColumns : availableColumns.slice(0, 6));
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  const filteredColumns = availableColumns.filter(col =>
    col.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (formatFieldLabel && formatFieldLabel(col).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleColumn = (col) => {
    if (selected.includes(col)) {
      setSelected(selected.filter(c => c !== col));
    } else {
      setSelected([...selected, col]);
    }
  };

  const handleDragStart = (idx) => {
    setDraggedItem(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === idx) return;

    const newSelected = [...selected];
    const item = newSelected[draggedItem];
    newSelected.splice(draggedItem, 1);
    newSelected.splice(idx, 0, item);
    setSelected(newSelected);
    setDraggedItem(idx);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '640px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
          <button style={{ background: 'transparent', border: 'none', fontSize: '18px', color: '#706e6b', cursor: 'pointer', padding: '4px 8px' }} onClick={onClose}>✕</button>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>בחר שדות לתצוגה</h2>
        </div>

        <div className="slds-modal__content" style={{ padding: '16px', display: 'flex', gap: '16px' }}>
          {/* Available columns */}
          <div style={{ flex: 1 }}>
            <h4 style={{ marginBottom: '8px', fontWeight: '600' }}>שדות זמינים</h4>
            <input
              type="search"
              className="slds-input"
              placeholder="חיפוש שדות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: '8px' }}
            />
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e5e5', borderRadius: '4px' }}>
              {filteredColumns.map(col => (
                <label
                  key={col}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: selected.includes(col) ? '#f3f3f3' : 'transparent',
                    borderBottom: '1px solid #f3f3f3'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(col)}
                    onChange={() => toggleColumn(col)}
                    style={{ marginLeft: '8px' }}
                  />
                  <span>{formatFieldLabel ? formatFieldLabel(col) : col}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Selected columns with drag reorder */}
          <div style={{ flex: 1 }}>
            <h4 style={{ marginBottom: '8px', fontWeight: '600' }}>שדות נבחרים (גרור לשינוי סדר)</h4>
            <div style={{ maxHeight: '340px', overflowY: 'auto', border: '1px solid #e5e5e5', borderRadius: '4px' }}>
              {selected.map((col, idx) => (
                <div
                  key={col}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'grab',
                    background: draggedItem === idx ? '#e0f3ff' : '#fff',
                    borderBottom: '1px solid #f3f3f3'
                  }}
                >
                  <span style={{ marginLeft: '8px', color: '#706e6b' }}>⋮⋮</span>
                  <span style={{ flex: 1 }}>{formatFieldLabel ? formatFieldLabel(col) : col}</span>
                  <button
                    onClick={() => toggleColumn(col)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c23934' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {selected.length === 0 && (
                <div style={{ padding: '16px', textAlign: 'center', color: '#706e6b' }}>
                  לא נבחרו שדות
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="slds-modal__footer">
          <button className="slds-button slds-button_neutral" onClick={onClose}>
            ביטול
          </button>
          <button className="slds-button slds-button_brand" onClick={() => onSave(selected)}>
            שמור
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// Sort Modal
// ========================================
const SortModal = ({ columns, currentSort, currentDirection, onClose, onSave, formatFieldLabel }) => {
  const [sortColumn, setSortColumn] = useState(currentSort || '');
  const [sortDirection, setSortDirection] = useState(currentDirection || 'asc');

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '420px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
          <button style={{ background: 'transparent', border: 'none', fontSize: '18px', color: '#706e6b', cursor: 'pointer', padding: '4px 8px' }} onClick={onClose}>✕</button>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>מיון הרשימה</h2>
        </div>

        <div className="slds-modal__content" style={{ padding: '24px' }}>
          <div className="slds-form-element" style={{ marginBottom: '16px' }}>
            <label className="slds-form-element__label">מיין לפי</label>
            <div className="slds-form-element__control">
              <select
                className="slds-select"
                value={sortColumn}
                onChange={(e) => setSortColumn(e.target.value)}
              >
                <option value="">-- בחר שדה --</option>
                {columns.map(col => (
                  <option key={col} value={col}>
                    {formatFieldLabel ? formatFieldLabel(col) : col}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="slds-form-element">
            <label className="slds-form-element__label">כיוון המיון</label>
            <div className="slds-form-element__control">
              <div className="slds-radio_button-group">
                <span className={`slds-button ${sortDirection === 'asc' ? 'slds-button_brand' : 'slds-button_neutral'}`}
                  onClick={() => setSortDirection('asc')}>
                  ↑ עולה (א-ת)
                </span>
                <span className={`slds-button ${sortDirection === 'desc' ? 'slds-button_brand' : 'slds-button_neutral'}`}
                  onClick={() => setSortDirection('desc')}>
                  ↓ יורד (ת-א)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="slds-modal__footer">
          <button className="slds-button slds-button_neutral" onClick={onClose}>
            ביטול
          </button>
          <button
            className="slds-button slds-button_brand"
            onClick={() => onSave(sortColumn, sortDirection)}
            disabled={!sortColumn}
          >
            החל מיון
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// List View
// ========================================
const ListView = ({
  object,
  records,
  loading,
  onRecordClick,
  onRefresh,
  onSearch,
  getObjectConfig,
  getFieldLabel,
  currentListView,
  setCurrentListView,
  showListViewMenu,
  setShowListViewMenu,
  objects = [],
  initialLoadComplete = true,
  currentUser = null,
  setRecords = null,
  setAllRecordsCache = null,
  addToCache = null,
  loadMoreRecords = null,
  recordsLoadingMore = false,
  recordsHasMore = false,
  recordsTotalCount = 0,
  parentFilter = null
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const searchTimeoutRef = React.useRef(null);
  const [objectFields, setObjectFields] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // List Views state - from server
  const [listViews, setListViews] = useState([]);
  const [selectedListView, setSelectedListView] = useState(null);
  const [listViewsLoading, setListViewsLoading] = useState(true);
  const [listViewSearch, setListViewSearch] = useState('');
  const [showNewListViewModal, setShowNewListViewModal] = useState(false);
  const [showManageListViewsModal, setShowManageListViewsModal] = useState(false);
  const [showEnhancedColumnSelector, setShowEnhancedColumnSelector] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);

  // Record Types and New button state
  const [recordTypes, setRecordTypes] = useState([]);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [objectButtons, setObjectButtons] = useState([]);
  const [showTerritoryManager, setShowTerritoryManager] = useState(false);
  const [showAreaPlannerList, setShowAreaPlannerList] = useState(false);
  const [showRouteOptimizer, setShowRouteOptimizer] = useState(false);
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);

  // Permission gate for schedule management tools — admin + office_management.
  // canonicalize handles both 'office_management' and 'הנהלת משרד' uniformly.
  const canManageSchedule = permIsAdmin(currentUser) || permHasProfile(currentUser, 'office_management');

  const config = getObjectConfig(object.api_name);

  // Fetch list views from server
  useEffect(() => {
    const fetchListViews = async () => {
      try {
        setListViewsLoading(true);
        const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/list-views`);
        if (res.ok) {
          const data = await res.json();
          setListViews(data.list_views || []);
          // Set default view
          const defaultView = data.list_views?.find(v => v.is_default) || data.list_views?.[0];
          if (defaultView && !selectedListView) {
            setSelectedListView(defaultView);
            setCurrentListView(defaultView.label);
            // Set columns from list view
            if (defaultView.columns && defaultView.columns.length > 0) {
              setSelectedColumns(defaultView.columns);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching list views:', err);
        // Fallback to default views
        setListViews([
          { id: 'recent', label: 'נצפו לאחרונה', is_default: true },
          { id: 'all', label: 'כל הרשומות' },
          { id: 'my', label: 'הרשומות שלי' }
        ]);
      } finally {
        setListViewsLoading(false);
      }
    };
    fetchListViews();
  }, [object.api_name]);

  // Fetch object fields for create/edit modals
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/fields`);
        if (res.ok) {
          const data = await res.json();
          setObjectFields(data.fields || []);
        }
      } catch (err) {
        console.error('Error fetching fields:', err);
      }
    };
    fetchFields();
  }, [object.api_name]);

  // Fetch record types and buttons from server
  useEffect(() => {
    const fetchRecordTypesAndButtons = async () => {
      try {
        // Fetch record types
        const rtRes = await fetch(`${API_BASE}/api/objects/${object.api_name}/record-types`);
        if (rtRes.ok) {
          const rtData = await rtRes.json();
          setRecordTypes(rtData.record_types || []);
        }

        // Fetch buttons
        const btnRes = await fetch(`${API_BASE}/api/objects/${object.api_name}/buttons`);
        if (btnRes.ok) {
          const btnData = await btnRes.json();
          // Filter only list buttons (not detail buttons)
          const listButtons = (btnData.buttons || []).filter(b =>
            b.display_type === 'list' || b.display_type === 'both' || !b.display_type
          );
          setObjectButtons(listButtons);
        }
      } catch (err) {
        console.error('Error fetching record types/buttons:', err);
      }
    };
    fetchRecordTypesAndButtons();
  }, [object.api_name]);

  // Handle new record creation with optional record type
  const handleNewRecord = (recordType = null) => {
    setSelectedRecordType(recordType);
    setShowNewMenu(false);
    setShowCreateModal(true);
  };

  // Handle list view change
  const handleListViewChange = (view) => {
    setSelectedListView(view);
    setCurrentListView(view.label);
    setShowListViewMenu(false);

    // Update columns from list view
    if (view.columns && view.columns.length > 0) {
      setSelectedColumns(view.columns);
    } else {
      setSelectedColumns([]);
    }

    // Trigger refresh with new filters (optional)
    if (onRefresh) onRefresh();
  };

  // Filter list views by search
  const filteredListViews = listViews.filter(v =>
    !listViewSearch ||
    v.label?.toLowerCase().includes(listViewSearch.toLowerCase()) ||
    v.label_en?.toLowerCase().includes(listViewSearch.toLowerCase())
  );

  // פונקציה לבדיקה אם ערך נראה כמו מזהה ייחודי
  const looksLikeUniqueId = (value) => {
    if (!value || typeof value !== 'string') return false;
    // Pure alphanumeric IDs: 10-18 chars with both letters and digits
    if (/^[a-zA-Z0-9]{10,18}$/.test(value) && /[a-zA-Z]/.test(value) && /[0-9]/.test(value)) return true;
    // Hebrew-prefixed IDs: Hebrew chars + alphanumeric suffix (e.g., "הזדמנויותNZMM6LU5N6JC", "חשבונותAK2M0EG4VOUX")
    if (/^[\u0590-\u05FF]+[a-zA-Z0-9]{6,18}$/.test(value)) return true;
    // legacy CRM IDs: 15-18 alphanumeric starting with 0 (e.g., "006Hp000018Q6bRIAS")
    if (/^0[a-zA-Z0-9]{14,17}$/.test(value)) return true;
    return false;
  };

  // Get all available columns
  const getAllAvailableColumns = () => {
    if (records.length === 0) return [];

    const hiddenFields = ['Id', 'attributes', 'IsDeleted', 'MasterRecordId', 'SystemModstamp', 'SourceSystemIdentifier',
      'RecordTypeId', 'LastReferencedDate', 'LastViewedDate', 'LastActivityDate', 'PhotoUrl', 'SourceId', 'OwnerId',
      'CreatedById', 'LastModifiedById', 'ConnectionReceivedId', 'ConnectionSentId', 'IndividualId', 'Jigsaw',
      'JigsawContactId', 'EmailBouncedDate', 'EmailBouncedReason', 'LastCURequestDate', 'LastCUUpdateDate'];
    const allKeys = Object.keys(records[0]);

    return allKeys.filter(k => {
      if (hiddenFields.includes(k)) return false;
      if (k.endsWith('__Target')) return false;
      // הסתר שדות ID אם יש שדה __Name מתאים שמכיל שם אמיתי (לא ID)
      if (k.endsWith('Id') && k !== 'Id') {
        const nameField = k.replace('Id', '__Name');
        const nameValue = records[0][nameField];
        // אם יש שדה __Name שלא נראה כמו ID, הסתר את שדה ה-ID
        if (nameValue && !looksLikeUniqueId(nameValue)) return false;
      }
      // הסתר שדות שנראים כמו מזהים ייחודיים (כמו UUID)
      if (k.toLowerCase().includes('guid') || k.toLowerCase().includes('uuid')) return false;
      return true;
    });
  };

  const availableColumns = getAllAvailableColumns();

  // Get columns from first record - מסננים שדות פנימיים ומציגים עמודות עם נתונים
  const getColumnsToDisplay = () => {
    // אם נבחרו עמודות ידנית, השתמש בהן
    if (selectedColumns.length > 0) {
      return selectedColumns.filter(col => availableColumns.includes(col));
    }

    if (records.length === 0) return [];

    // שדות מועדפים שכדאי להציג אם קיימים - מעדיפים __Name על פני Id
    const preferredFields = ['Name', 'Subject', 'Title', 'Phone', 'Email', 'Type', 'Status', 'CreatedDate', 'Owner__Name', 'Account__Name', 'Contact__Name'];

    // בדוק שבכמה רשומות יש ערך לכל עמודה
    const columnScores = {};
    availableColumns.forEach(key => {
      let score = 0;
      // רונוס לשדות מועדפים
      if (preferredFields.includes(key)) score += 100;
      // רונוס ל-Name (צכך טשור)
      if (key === 'Name') score += 200;
      // סשור שמצ רשומות יש ערך
      records.slice(0, 20).forEach(r => {
        if (r[key] !== null && r[key] !== '' && r[key] !== undefined) {
          score += 1;
        }
      });
      columnScores[key] = score;
    });

    // מיין לפי הגיון ובחר אה 6 הטובים
    return availableColumns
      .sort((a, b) => columnScores[b] - columnScores[a])
      .slice(0, 6);
  };

  const columns = getColumnsToDisplay();

  // Toggle column selection
  const toggleColumn = (colName) => {
    setSelectedColumns(prev => {
      if (prev.includes(colName)) {
        return prev.filter(c => c !== colName);
      } else {
        return [...prev, colName];
      }
    });
  };

  // Reset columns to default
  const resetColumns = () => {
    setSelectedColumns([]);
    setShowColumnSelector(false);
  };

  // Helper לבדיקה אם עמודה היא reference ועבור איזה אובייקט
  const getReferenceInfo = (colName, record) => {
    if (colName.endsWith('__Name')) {
      const idField = colName.replace('__Name', 'Id');
      const targetObject = record[`${idField}__Target`];
      const refId = record[idField];
      return { isReference: true, targetObject, refId };
    }
    return { isReference: false };
  };

  // Format field label for display - use getFieldLabel if available
  const formatFieldLabel = (fieldName) => {
    // Clean up field name for lookup
    const cleanName = fieldName.replace(/__Name$/g, 'Id').replace(/__c$/g, '');
    // Try to get Hebrew label
    if (getFieldLabel) {
      const heLabel = getFieldLabel(cleanName);
      if (heLabel && heLabel !== cleanName) return heLabel;
    }
    // Fallback to formatting
    return fieldName
      .replace(/__c$/g, '')
      .replace(/__Name$/g, '')
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };

  // חיפוש לפי שם - מקומי מיידי + שרת עם debounce
  // חיפוש בשדה מופעל כשכבר 2+ תווים, אחרי השהייה של 500ms
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);

    // נקה timeout קודם
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // אם אין מונח חיפוש, טען רשומות בקלות
    if (!newSearchTerm || newSearchTerm.length < 2) {
      if (newSearchTerm === '' && onSearch) {
        // מאפס לטעינה בקלות
        setIsSearching(true);
        onSearch(null);
        setTimeout(() => setIsSearching(false), 500);
      }
      return;
    }

    // חיפוש בשדה עם debounce
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(newSearchTerm);
      }
      setIsSearching(false);
    }, 500);
  };

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort records
  const displayRecords = React.useMemo(() => {
    let result = records;

    // Apply list view filters (e.g., "My Records" filters by OwnerId)
    if (selectedListView?.filters?.length > 0) {
      const userId = currentUser?.Id || currentUser?.id || currentUser?.email || currentUser?.username || '';
      for (const filter of selectedListView.filters) {
        const field = filter.field;
        const operator = filter.operator;
        let value = filter.value;
        // Replace special tokens
        if (value === '$CurrentUser.Id' && userId) {
          value = userId;
        } else if (value === 'TODAY') {
          value = new Date().toISOString().slice(0, 10);
        } else if (value === '$CurrentUser.Id' && !userId) {
          continue; // Skip filter if no user ID available
        }
        if (!field) continue;
        // Apply filter
        if (operator === 'equals') {
          result = result.filter(r => {
            const fv = String(r[field] || '');
            // For OwnerId, also check email/username match
            if (field === 'OwnerId' || field === 'CreatedById') {
              const uEmail = currentUser?.email || '';
              const uUsername = currentUser?.username || '';
              const uId = currentUser?.Id || currentUser?.id || '';
              return fv === value || fv === uEmail || fv === uUsername || fv === uId;
            }
            return fv === value || fv.startsWith(value);
          });
        } else if (operator === 'not_equals') {
          result = result.filter(r => String(r[field] || '') !== value);
        } else if (operator === 'contains') {
          result = result.filter(r => String(r[field] || '').toLowerCase().includes(value.toLowerCase()));
        }
      }
    }

    // Apply search filter
    if (searchTerm && searchTerm.length >= 1) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r =>
        Object.values(r).some(v =>
          v !== null && v !== undefined && String(v).toLowerCase().includes(term)
        )
      );
    }

    // Apply sorting
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        // Handle null/undefined
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
        if (bVal == null) return sortDirection === 'asc' ? -1 : 1;

        // Compare values
        let comparison = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
          comparison = aVal === bVal ? 0 : aVal ? 1 : -1;
        } else {
          comparison = String(aVal).localeCompare(String(bVal), 'he');
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [records, searchTerm, sortColumn, sortDirection, selectedListView, currentUser]);

  const toggleSelectAll = () => {
    if (selectedRows.length === displayRecords.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(displayRecords.map((_, i) => i));
    }
  };

  const toggleRow = (idx) => {
    setSelectedRows(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="slds-list-view">
      {/* List View Header - Single Row */}
      <div className="slds-list-view__header">
        {/* Right side - List View Selector */}
        <div className="slds-list-view__header-right">
          <div className="slds-list-view__icon">
            <span style={{ backgroundColor: config.color }}>{config.icon}</span>
          </div>
          <div className="slds-list-view__title">
            <button
              className="slds-list-view__title-button"
              onClick={() => setShowListViewMenu(!showListViewMenu)}
            >
              <span>{currentListView}</span>
              <span className="slds-dropdown-arrow">▼</span>
            </button>
            <span className="slds-list-view__meta">
              {records.length.toLocaleString()} פריטים {initialLoadComplete ? '' : '(טוען...)'}
            </span>

          {/* List View Dropdown */}
          {showListViewMenu && (
            <div className="slds-listview-dropdown slds-listview-dropdown--enhanced">
              <div className="slds-listview-dropdown__header">
                <h3>רשימות צפייה</h3>
                <button
                  className="slds-listview-dropdown__new-btn"
                  onClick={() => setShowNewListViewModal(true)}
                  title="צור רשימת צפייה חדשה"
                >
                  +
                </button>
              </div>
              <div className="slds-listview-dropdown__search">
                <input
                  type="search"
                  placeholder="חיפוש ברשימות..."
                  value={listViewSearch}
                  onChange={(e) => setListViewSearch(e.target.value)}
                />
                <span>🔍</span>
              </div>

              {listViewsLoading ? (
                <div className="slds-listview-dropdown__loading">
                  <span>טוען רשימות...</span>
                </div>
              ) : (
                <>
                  {/* System Views */}
                  <div className="slds-listview-dropdown__section">
                    <h4>רשימות מערכת</h4>
                    {filteredListViews.filter(v => v.is_system).map((view) => (
                      <button
                        key={view.id}
                        className={`slds-listview-dropdown__item ${selectedListView?.id === view.id ? 'slds-is-selected' : ''}`}
                        onClick={() => handleListViewChange(view)}
                      >
                        <span className="slds-listview-item__icon">📋</span>
                        <span className="slds-listview-item__label">{view.label}</span>
                        {view.is_default && <span className="slds-badge slds-badge--default">ברירת מחדל</span>}
                        {selectedListView?.id === view.id && <span className="slds-listview-item__check">✓</span>}
                      </button>
                    ))}
                  </div>

                  {/* Custom Views */}
                  {filteredListViews.filter(v => !v.is_system).length > 0 && (
                    <div className="slds-listview-dropdown__section">
                      <h4>רשימות מותאמות אישית</h4>
                      {filteredListViews.filter(v => !v.is_system).map((view) => (
                        <button
                          key={view.id}
                          className={`slds-listview-dropdown__item ${selectedListView?.id === view.id ? 'slds-is-selected' : ''}`}
                          onClick={() => handleListViewChange(view)}
                        >
                          <span className="slds-listview-item__icon">⭐</span>
                          <span className="slds-listview-item__label">{view.label}</span>
                          {view.is_default && <span className="slds-badge slds-badge--default">ברירת מחדל</span>}
                          {selectedListView?.id === view.id && <span className="slds-listview-item__check">✓</span>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No results */}
                  {filteredListViews.length === 0 && (
                    <div className="slds-listview-dropdown__empty">
                      לא נמצאו רשימות תואמות
                    </div>
                  )}
                </>
              )}

              <div className="slds-listview-dropdown__footer">
                <button
                  className="slds-listview-dropdown__action"
                  onClick={() => { setShowNewListViewModal(true); setShowListViewMenu(false); }}
                >
                  <span>➕</span>
                  <span>רשימת צפייה חדשה</span>
                </button>
                <button
                  className="slds-listview-dropdown__action"
                  onClick={() => { setShowManageListViewsModal(true); setShowListViewMenu(false); }}
                >
                  <span>⚙️</span>
                  <span>ניהול רשימות</span>
                </button>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Left side - Action Buttons */}
        <div className="slds-list-view__header-left">
          {/* Custom Object Buttons */}
          {objectButtons.length > 0 && objectButtons.slice(0, 3).map((btn, idx) => (
            <button
              key={idx}
              className="slds-button slds-button_outline"
              onClick={() => {
                if (btn.url) window.open(btn.url, '_blank');
                else if (btn.onclick) eval(btn.onclick);
              }}
              title={btn.description || btn.label}
            >
              {btn.label}
            </button>
          ))}

          {/* New Button - with Record Type dropdown if available. Hidden on an admin-only object
              (work types, owner 2026-10-11) for anyone but a system admin — the server refuses it too. */}
          <div className="slds-new-button-container"
               style={{ position: 'relative',
                        display: isAdminOnlyObject(object?.api_name) && !permIsAdmin(currentUser) ? 'none' : undefined }}>
            {recordTypes.length > 1 ? (
              // Multiple record types - show dropdown
              <>
                <button
                  className="slds-button slds-button_brand slds-button_dropdown"
                  onClick={() => setShowNewMenu(!showNewMenu)}
                >
                  <span>חדש</span>
                  <span className="slds-dropdown-arrow" style={{ marginRight: '6px' }}>▼</span>
                </button>
                {showNewMenu && (
                  <>
                    <div className="slds-backdrop" onClick={() => setShowNewMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 8999 }}></div>
                    <div className="slds-new-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      background: 'white',
                      border: '1px solid #dddbda',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 9000,
                      minWidth: '200px'
                    }}>
                      <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e5e5', fontWeight: '600', fontSize: '12px', color: '#706e6b' }}>
                        בחר סוג רשומה
                      </div>
                      {recordTypes.map((rt, idx) => (
                        <button
                          key={idx}
                          className="slds-new-dropdown__item"
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '10px 12px',
                            textAlign: 'right',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                          onClick={() => handleNewRecord(rt)}
                          onMouseOver={(e) => e.currentTarget.style.background = '#f3f2f2'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          {rt.label || rt.name}
                          {rt.description && (
                            <span style={{ display: 'block', fontSize: '11px', color: '#706e6b', marginTop: '2px' }}>
                              {rt.description}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              // Single or no record types - simple button
              <button className="slds-button slds-button_brand" onClick={() => handleNewRecord(recordTypes[0] || null)}>
                חדש
              </button>
            )}
          </div>

          {/* Schedule Management Buttons - Only for ServiceAppointment + admin/office */}
          {object.api_name === 'ServiceAppointment' && canManageSchedule && (
            <>
              <button
                className="slds-button slds-button_outline"
                onClick={() => setShowTerritoryManager(true)}
                style={{
                  background: 'linear-gradient(135deg, #0070d2 0%, #1589ee 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                🗓️ ניהול יומנים ואזורים
              </button>
              <button
                className="slds-button slds-button_outline"
                onClick={() => setShowAreaPlannerList(true)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                🪄 משפך הקסם
              </button>
              <button
                className="slds-button slds-button_outline"
                onClick={() => setShowRouteOptimizer(true)}
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                ✨ שביט הקסם
              </button>
              <button
                className="slds-button slds-button_outline"
                onClick={() => setShowBulkTransfer(true)}
                style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                🔄 ניהול היעדרויות טכנאי
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="slds-list-view__toolbar">
        <div className="slds-toolbar__left">
          <button
            className="slds-toolbar-button"
            disabled={selectedRows.length === 0}
            title="מחיקה"
            onClick={() => {
              if (selectedRows.length === 0) return;
              if (!window.confirm(`האם למחוק ${selectedRows.length} רשומות?`)) return;
              // TODO: Implement batch delete
              alert('פונקציית מחיקה בפיתוח');
            }}
          >
            🗑️
          </button>
          <button
            className="slds-toolbar-button"
            title="עריכה מהירה"
            disabled={selectedRows.length !== 1}
            onClick={() => {
              if (selectedRows.length !== 1) return;
              const record = displayRecords[selectedRows[0]];
              if (record) {
                onRecordClick(record);
              }
            }}
          >
            ✏️
          </button>
          <button
            className="slds-toolbar-button"
            title="מיון"
            onClick={() => setShowSortModal(true)}
          >
            ↕️
          </button>
          <button className="slds-toolbar-button" onClick={onRefresh} title="רענון">🔄</button>
          <span className="slds-toolbar-divider"></span>
          <button className="slds-toolbar-button" title="הצגה גרפית">📊</button>

          {/* Settings Menu Button */}
          <div style={{ position: 'relative' }}>
            <button
              className="slds-toolbar-button"
              title="הגדרות"
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            >
              ⚙️
            </button>

            {/* Settings Dropdown Menu */}
            {showSettingsMenu && (
              <>
                <div className="slds-backdrop" onClick={() => setShowSettingsMenu(false)}></div>
                <div className="slds-settings-dropdown" style={{ top: '100%', right: 0, left: 'auto', position: 'absolute', marginTop: '4px' }}>
                  <div className="slds-settings__header">
                    <h3>הגדרות</h3>
                    <button onClick={() => setShowSettingsMenu(false)}>✕</button>
                  </div>
                  <div className="slds-settings__content">
                    <button className="slds-settings-item" onClick={() => { setShowEnhancedColumnSelector(true); setShowSettingsMenu(false); }}>
                      <span>📋</span>
                      <span>בחר שדות לתצוגה</span>
                    </button>
                    <button className="slds-settings-item" onClick={() => { resetColumns(); setShowSettingsMenu(false); }}>
                      <span>↩️</span>
                      <span>איפוס סדר עמודות</span>
                    </button>
                    <div className="slds-settings-divider"></div>
                    <button className="slds-settings-item" onClick={() => { setShowSortModal(true); setShowSettingsMenu(false); }}>
                      <span>↕️</span>
                      <span>מיון</span>
                    </button>
                    <button className="slds-settings-item" onClick={() => { onRefresh(); setShowSettingsMenu(false); }}>
                      <span>🔄</span>
                      <span>רענון</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="slds-toolbar__right">
          <div className="slds-list-search">
            <input
              type="search"
              placeholder={`חיפוש ב-${records.length} רשומות... (2+ תווים לחיפוש מתקדם)`}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <span>{isSearching ? '⏳' : '🔔'}</span>
          </div>
          {searchTerm && (
            <span style={{ fontSize: '12px', color: '#706e6b', marginRight: '8px' }}>
              {isSearching ? 'מחפש...' : `${displayRecords.length} תוצאות`}
            </span>
          )}
        </div>
      </div>

      {/* Column Selector Modal */}
      {showColumnSelector && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowColumnSelector(false)}>
          <div className="slds-column-selector" onClick={(e) => e.stopPropagation()}>
            <div className="slds-column-selector__header">
              <h3>בחר שדות לתצוגה</h3>
              <button onClick={() => setShowColumnSelector(false)}>✕</button>
            </div>
            <div className="slds-column-selector__content">
              <div className="slds-column-selector__search">
                <input type="search" placeholder="חיפוש שדות..." />
              </div>
              <div className="slds-column-selector__list">
                {availableColumns.map(col => (
                  <label key={col} className="slds-column-selector__item">
                    <input
                      type="checkbox"
                      checked={selectedColumns.length === 0 ? columns.includes(col) : selectedColumns.includes(col)}
                      onChange={() => {
                        if (selectedColumns.length === 0) {
                          // First time selecting - start with current columns
                          const newSelection = columns.includes(col)
                            ? columns.filter(c => c !== col)
                            : [...columns, col];
                          setSelectedColumns(newSelection);
                        } else {
                          toggleColumn(col);
                        }
                      }}
                    />
                    <span>{formatFieldLabel(col)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="slds-column-selector__footer">
              <button className="slds-button slds-button_outline" onClick={resetColumns}>
                איפוס לברירת מחדל
              </button>
              <button className="slds-button slds-button_brand" onClick={() => setShowColumnSelector(false)}>
                בטל
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="slds-list-view__table-container"
           onScroll={(e) => {
             // Salesforce-style infinite scroll: when the user scrolls
             // within ~200px of the bottom, fetch the next 50 records.
             if (!loadMoreRecords) return;
             const el = e.currentTarget;
             if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
               loadMoreRecords();
             }
           }}>
        {loading ? (
          <div className="slds-loading">
            <span>טוען...</span>
          </div>
        ) : records.length === 0 ? (
          <div className="slds-empty-state">
            <span className="slds-empty-icon">📭</span>
            <h3>אין רשומות להצגה</h3>
          </div>
        ) : (
          <table className="slds-table">
            <thead>
              <tr>
                <th className="slds-th-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === displayRecords.length && displayRecords.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="slds-th-row-number"></th>
                {columns.map(col => {
                  // פורמט שם עמודה לעברית
                  const colLabel = formatFieldLabel(col);
                  const isSorted = sortColumn === col;
                  const sortIcon = isSorted
                    ? (sortDirection === 'asc' ? '▲' : '▼')
                    : '▼';
                  return (
                    <th key={col} className={isSorted ? 'slds-th-sorted' : ''}>
                      <button
                        className="slds-th-button"
                        onClick={() => handleSort(col)}
                        title={`מיין לפי ${colLabel}`}
                      >
                        {colLabel}
                        <span className={`slds-sort-icon ${isSorted ? 'slds-sort-icon--active' : ''}`}>
                          {sortIcon}
                        </span>
                      </button>
                    </th>
                  );
                })}
                <th className="slds-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {displayRecords.map((record, idx) => (
                <tr
                  key={record.Id || idx}
                  className={selectedRows.includes(idx) ? 'slds-is-selected' : ''}
                >
                  <td className="slds-td-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(idx)}
                      onChange={() => toggleRow(idx)}
                    />
                  </td>
                  <td className="slds-td-row-number">{idx + 1}</td>
                  {columns.map((col, colIdx) => {
                    const refInfo = getReferenceInfo(col, record);
                    let cellValue = record[col];

                    // Special handling for Name field - combine FirstName + LastName for Person Accounts
                    if (col === 'Name' && (!cellValue || cellValue === '-' || cellValue === '')) {
                      const firstName = record.FirstName || record.PersonFirstName || '';
                      const lastName = record.LastName || record.PersonLastName || '';
                      if (firstName || lastName) {
                        cellValue = `${firstName} ${lastName}`.trim();
                      }
                    }

                    // Handle reference fields - show Name instead of ID
                    if (col.endsWith('Id') && col !== 'Id') {
                      const nameField = col.replace('Id', '__Name');
                      if (record[nameField] && !looksLikeUniqueId(record[nameField])) {
                        cellValue = record[nameField];
                      } else if (col === 'AccountId' && looksLikeUniqueId(cellValue)) {
                        // Person Account fallback: use FirstName + LastName
                        const first = record.FirstName || record.PersonFirstName || '';
                        const last = record.LastName || record.PersonLastName || '';
                        const combined = `${first} ${last}`.trim();
                        if (combined) cellValue = combined;
                      }
                    }

                    // Format date fields - DD/MM/YYYY HH:MM
                    if (col.includes('Date') || col.includes('Time') || col.endsWith('At') || col.endsWith('__c') && cellValue && typeof cellValue === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(cellValue)) {
                      try {
                        const dateVal = new Date(cellValue);
                        if (!isNaN(dateVal.getTime())) {
                          const day = String(dateVal.getDate()).padStart(2, '0');
                          const month = String(dateVal.getMonth() + 1).padStart(2, '0');
                          const year = dateVal.getFullYear();
                          const hours = String(dateVal.getHours()).padStart(2, '0');
                          const minutes = String(dateVal.getMinutes()).padStart(2, '0');
                          cellValue = `${day}/${month}/${year} ${hours}:${minutes}`;
                        }
                      } catch (e) { /* keep original value */ }
                    }

                    // אם הערך נראה כמו legacy CRM ID, הצג '-' במקום
                    const displayValue = (cellValue && looksLikeUniqueId(cellValue)) ? '-' : (cellValue || '-');

                    return (
                      <td key={col}>
                        {colIdx === 0 ? (
                          // עמודה ראשונה - לינק לרשומה עצמה (תג a אמיתי לתמיכה בלחיצה ימנית)
                          <a
                            href={`/object/${object.api_name}/${record.Id}`}
                            className="slds-cell-link"
                            onClick={(e) => {
                              e.preventDefault();
                              onRecordClick(record);
                            }}
                          >
                            {displayValue}
                          </a>
                        ) : refInfo.isReference && refInfo.targetObject && refInfo.refId && !looksLikeUniqueId(cellValue) ? (
                          // עמודה reference - לינק לרשומה הקשורה (תג a אמיתי לתמיכה בלחיצה ימנית)
                          <a
                            href={`/object/${refInfo.targetObject}/${refInfo.refId}`}
                            className="slds-cell-link"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // ניווט לרשומה הקשורה
                              const targetObj = objects.find(o => o.api_name === refInfo.targetObject);
                              if (targetObj) {
                                // יצירת אובייקט רשומה עם ה-ID
                                const refRecord = { Id: refInfo.refId, Name: cellValue };
                                onRecordClick(refRecord);
                              }
                            }}
                          >
                            {displayValue}
                          </a>
                        ) : (
                          <span className="slds-cell-text">{displayValue}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="slds-td-actions">
                    <button className="slds-row-action">▼</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Infinite-scroll feedback at the bottom of the list. */}
        {!loading && records.length > 0 && recordsLoadingMore && (
          <div style={{ textAlign: 'center', padding: 16, color: '#54698d', fontSize: 13 }}>
            טוען עוד 50…
          </div>
        )}
        {!loading && records.length > 0 && !recordsHasMore && recordsTotalCount > 50 && (
          <div style={{ textAlign: 'center', padding: 12, color: '#a0a0a0', fontSize: 12 }}>
            הוצגו {records.length} מתוך {recordsTotalCount} רשומות
          </div>
        )}
      </div>

      {/* Create Modal - Use AccountModal for Account, RecordFormModal for others */}
      {showCreateModal && object.api_name === 'Account' && (
        <React.Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>טוען טופס...</div>}>
          <AccountModal
            isOpen={showCreateModal}
            onClose={() => { setShowCreateModal(false); setSelectedRecordType(null); }}
            onSuccess={(newRecord) => {
              setShowCreateModal(false);
              setSelectedRecordType(null);
              // Optimistic update - add to records immediately
              if (newRecord && newRecord.Id && setRecords) {
                // Add to local records state (at the beginning)
                setRecords(prev => [newRecord, ...(prev || [])]);
                // Update cache with new record
                if (setAllRecordsCache) {
                  setAllRecordsCache(prev => {
                    const currentRecords = prev[object.api_name] || [];
                    return { ...prev, [object.api_name]: [newRecord, ...currentRecords] };
                  });
                }
                // Also add to record cache
                if (addToCache) {
                  addToCache(object.api_name, [newRecord]);
                }
              } else {
                // Fallback - reload from server
                onRefresh();
              }
            }}
            recordType={selectedRecordType}
            currentUser={currentUser}
          />
        </React.Suspense>
      )}
      {showCreateModal && object.api_name !== 'Account' && (
        <RecordFormModal
          object={object}
          record={selectedRecordType ? { RecordTypeId: selectedRecordType.id } : null}
          fields={objectFields.length > 0 ? objectFields : (records.length > 0 ? Object.keys(records[0]).map(key => ({
            name: key,
            label: key.replace(/__c$/g, '').replace(/_/g, ' '),
            type: 'Text'
          })) : [])}
          recordType={selectedRecordType}
          getObjectConfig={getObjectConfig}
          onClose={() => { setShowCreateModal(false); setSelectedRecordType(null); }}
          onSave={(newRecord) => {
            setShowCreateModal(false);
            setSelectedRecordType(null);
            // Optimistic update - add to records immediately
            if (newRecord && newRecord.Id && setRecords) {
              // Add to local records state (at the beginning)
              setRecords(prev => [newRecord, ...(prev || [])]);
              // Update cache with new record
              if (setAllRecordsCache) {
                setAllRecordsCache(prev => {
                  const currentRecords = prev[object.api_name] || [];
                  return { ...prev, [object.api_name]: [newRecord, ...currentRecords] };
                });
              }
              // Also add to record cache
              if (addToCache) {
                addToCache(object.api_name, [newRecord]);
              }
            } else {
              // Fallback - reload from server
              onRefresh();
            }
          }}
        />
      )}

      {/* New List View Modal */}
      {showNewListViewModal && (
        <NewListViewModal
          object={object}
          existingViews={listViews}
          onClose={() => setShowNewListViewModal(false)}
          onSave={(newView) => {
            setListViews([...listViews, newView]);
            setShowNewListViewModal(false);
            handleListViewChange(newView);
          }}
        />
      )}

      {/* Manage List Views Modal */}
      {showManageListViewsModal && (
        <ManageListViewsModal
          object={object}
          listViews={listViews}
          onClose={() => setShowManageListViewsModal(false)}
          onUpdate={(updatedViews) => setListViews(updatedViews)}
          onDelete={(deletedView) => {
            setListViews(listViews.filter(v => v.id !== deletedView.id));
            if (selectedListView?.id === deletedView.id) {
              const defaultView = listViews.find(v => v.is_default && v.id !== deletedView.id) || listViews[0];
              if (defaultView) handleListViewChange(defaultView);
            }
          }}
        />
      )}

      {/* Enhanced Column Selector Modal */}
      {showEnhancedColumnSelector && (
        <ColumnSelectorModal
          availableColumns={availableColumns}
          selectedColumns={selectedColumns.length > 0 ? selectedColumns : columns}
          formatFieldLabel={formatFieldLabel}
          onClose={() => setShowEnhancedColumnSelector(false)}
          onSave={(newColumns) => {
            setSelectedColumns(newColumns);
            setShowEnhancedColumnSelector(false);
          }}
        />
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <SortModal
          columns={columns}
          currentSort={sortColumn}
          currentDirection={sortDirection}
          formatFieldLabel={formatFieldLabel}
          onClose={() => setShowSortModal(false)}
          onSave={(col, dir) => {
            setSortColumn(col);
            setSortDirection(dir);
            setShowSortModal(false);
          }}
        />
      )}

      {/* Territory Manager Modal */}
      {showTerritoryManager && (
        <Suspense fallback={<LoadingFallback />}>
          <TerritoryManager
            onClose={() => setShowTerritoryManager(false)}
          />
        </Suspense>
      )}

      {/* Area Planner Modal (List View) */}
      {showAreaPlannerList && (
        <div className="slds-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '95vw', height: '90vh', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'white' }}>🪄 משפך הקסם</h2>
              <button onClick={() => setShowAreaPlannerList(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'white', padding: '4px 8px', borderRadius: '4px' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              <Suspense fallback={<LoadingFallback />}>
                <AreaPlanner />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Route Optimizer Modal (שביט הקסם) */}
      {showRouteOptimizer && (
        <Suspense fallback={<LoadingFallback />}>
          <RouteOptimizer onClose={() => setShowRouteOptimizer(false)} />
        </Suspense>
      )}

      {/* Bulk Transfer Modal (ניהול היעדרויות טכנאי) */}
      {showBulkTransfer && (
        <Suspense fallback={<LoadingFallback />}>
          <BulkTransfer onClose={() => setShowBulkTransfer(false)} />
        </Suspense>
      )}
    </div>
  );
};

// ========================================
// File Attachments Sidebar Component
// ========================================
const FileAttachmentsSidebar = ({ objectName, recordId, recordName }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  const loadAttachments = async () => {
    if (!objectName || !recordId) return;
    try {
      setLoading(true);
      const _t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const response = await fetch(`${API_BASE}/api/objects/${objectName}/records/${recordId}/attachments`,
        { headers: _t ? { 'Authorization': `Bearer ${_t}` } : {} });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data.attachments || []);
      }
    } catch (err) {
      console.error('[FileAttachmentsSidebar] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadAttachments();
  }, [objectName, recordId]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', '');
      const _t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const response = await fetch(`${API_BASE}/api/objects/${objectName}/records/${recordId}/attachments`, {
        method: 'POST',
        headers: _t ? { 'Authorization': `Bearer ${_t}` } : {},
        body: formData
      });
      if (response.ok) {
        await loadAttachments();
      }
    } catch (err) {
      console.error('[FileAttachmentsSidebar] Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // window.open() cannot send an Authorization header, so once the endpoint
  // enforces (#3.7) a plain link would 401. Fetch the blob with the token and
  // hand the browser an object URL instead.
  const downloadFile = async (attachment) => {
    const url = `${API_BASE}/api/objects/${objectName}/records/${recordId}/attachments/${attachment.id}/download`;
    const _t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    try {
      const res = await fetch(url, { headers: _t ? { 'Authorization': `Bearer ${_t}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = attachment.filename || 'download';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch (err) {
      console.error('[FileAttachmentsSidebar] Download error:', err);
    }
  };

  const deleteFile = async (attachment) => {
    if (!window.confirm(`למחוק את "${attachment.filename}"?`)) return;
    try {
      const _t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      await fetch(`${API_BASE}/api/objects/${objectName}/records/${recordId}/attachments/${attachment.id}`,
        { method: 'DELETE', headers: _t ? { 'Authorization': `Bearer ${_t}` } : {} });
      await loadAttachments();
    } catch (err) {
      console.error('[FileAttachmentsSidebar] Delete error:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📕';
    if (['doc', 'docx'].includes(ext)) return '📘';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📗';
    if (['ppt', 'pptx'].includes(ext)) return '📙';
    if (['zip', 'rar', '7z'].includes(ext)) return '📦';
    if (['mp4', 'avi', 'mov'].includes(ext)) return '🎬';
    if (['mp3', 'wav'].includes(ext)) return '🎵';
    return '📎';
  };

  return (
    <div className={`slds-related-list ${isExpanded ? 'expanded' : ''}`}>
      <div className="slds-related-list__header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="slds-collapse-icon">{isExpanded ? '▼' : '▶'}</span>
        <span className="slds-related-list__icon" style={{ backgroundColor: '#747474' }}>📎</span>
        <span className="slds-related-list__label">קבצים מצורפים ({attachments.length})</span>
      </div>

      {isExpanded && (
        <div
          className="slds-related-list__content"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            backgroundColor: dragOver ? '#EBF5FF' : 'transparent',
            border: dragOver ? '2px dashed #0176d3' : '2px dashed transparent',
            borderRadius: '4px',
            transition: 'all 0.2s ease'
          }}
        >
          {loading ? (
            <div className="slds-related-list__loading"><span>טוען...</span></div>
          ) : attachments.length === 0 ? (
            <div className="slds-related-list__empty" style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', opacity: 0.5, marginBottom: '8px' }}>📁</div>
              <p style={{ margin: 0, fontSize: '12px', color: '#706e6b' }}>אין קבצים מצורפים</p>
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#999' }}>גרור קבצים או לחץ להעלאה</p>
            </div>
          ) : (
            <div className="slds-related-list__records">
              {attachments.slice(0, 5).map((attachment, idx) => (
                <div key={idx} className="slds-related-record" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => downloadFile(attachment)}>
                  <span style={{ fontSize: '20px' }}>{getFileIcon(attachment.filename)}</span>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <span className="slds-related-record__name" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {attachment.filename}
                    </span>
                    <span style={{ fontSize: '11px', color: '#706e6b' }}>{attachment.size_formatted}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteFile(attachment); }}
                    style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: '4px' }}
                    title="מחק"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              {attachments.length > 5 && (
                <div style={{ textAlign: 'center', padding: '8px', color: '#0176d3', fontSize: '12px' }}>
                  +{attachments.length - 5} קבצים נוספים
                </div>
              )}
            </div>
          )}

          {/* Upload button */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e5e5' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '8px',
                background: '#f3f3f3',
                border: '1px solid #e5e5e5',
                borderRadius: '4px',
                color: '#0176d3',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              {uploading ? '⏳ מעלה...' : '+ העלאת קובץ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// Record Page
// ========================================
const RecordPage = ({ object, record, onBack, getObjectConfig, getFieldLabel, onNavigateToRecord, objects = [], addToCache = () => {}, flexipageCache = {}, setFlexipageCache = () => {}, onRecordUpdate, onRecordDelete, currentUser, onViewRelatedList }) => {
  // PR #821 follow-up: OpportunityModal needs navigate to do SPA
  // transitions instead of window.location.href. RecordPage is a
  // React component so useNavigate works here.
  // Hotfix on top of PR #821: build was failing with 'navigate'
  // is not defined no-undef — exactly the gotcha that
  // feedback_verify_react_hook_imports.md warns about (babel parser
  // passes, CRA's no-undef catches at build).
  const navigate = useNavigate();
  const config = getObjectConfig(object.api_name);
  // Persist active tab per record (so refresh keeps WhatsApp tab open)
  const recordTabKey = `record_tab_${record?.Id || ''}`;
  const [activeTab, setActiveTabRaw] = useState(() => {
    try {
      const saved = localStorage.getItem(recordTabKey);
      return saved || 'details';
    } catch { return 'details'; }
  });
  const setActiveTab = (tab) => {
    setActiveTabRaw(tab);
    try { localStorage.setItem(recordTabKey, tab); } catch {}
  };
  const [activeFlexiTab, setActiveFlexiTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(record);
  const [editedRecord, setEditedRecord] = useState(record);
  const editBaselineRef = useRef(null);   // the record when inline editing started
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [layout, setLayout] = useState(null);
  const [flexipage, setFlexipage] = useState(null);

  // 2026-05-18: button-permission editor — fetch the denial list for the
  // current user and use it to hide buttons in the action bar. Pattern
  // mirrors launcherPerm above. Backend returns
  // {denied: {object_name: [button_id, ...]}}; an empty map = no
  // restrictions, the default.
  const [buttonDeny, setButtonDeny] = useState({});
  useEffect(() => {
    const uid = currentUser?.id || currentUser?.Id || currentUser?.email || currentUser?.username || '';
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/button-permissions/effective/${encodeURIComponent(uid)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setButtonDeny(d.denied || {});
      } catch (e) {
        console.debug('[ButtonPerm] effective fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // dealer P4: who may schedule marketer deliveries — decided by the server once per user.
  const [dealerBoot, setDealerBoot] = useState(null);
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    fetchDealerBootstrap().then((b) => { if (!cancelled) setDealerBoot(b); });
    return () => { cancelled = true; };
  }, [currentUser]);

  // Update currentRecord when record prop changes (e.g., navigating to different record)
  useEffect(() => {
    if (record && record.Id) {
      setCurrentRecord(record);
      setEditedRecord(record);
    }
  }, [record, record?.Id]);

  // Hebrew labels for document title
  const objectLabelsForTitle = {
    'Account': 'לקוח',
    'Contact': 'איש קשר',
    'Opportunity': 'הזדמנות',
    'Lead': 'ליד',
    'Case': 'פנייה',
    'Task': 'משימה',
    'Event': 'אירוע',
    'ServiceAppointment': 'פעילות שירות',
    'WorkOrder': 'הזמנת עבודה',
    'Asset': 'נכס',
    'Product2': 'מוצר',
    'Order': 'הזמנה',
    'Quote': 'הצעת מחיר',
    'Contract': 'חוזה',
    'User': 'משתמש',
    'ServiceResource': 'טכנאי',
    'ServiceTerritory': 'אזור שירות',
    'OpportunityLineItem': 'פריט הזדמנות',
    'Payment__c': 'תשלום',
    'WorkOrderLineItem': 'פריט הזמנת עבודה',
    'Campaign': 'קמפיין',
    'CampaignMember': 'חבר קמפיין',
    'Pricebook2': 'מחירון',
    'PricebookEntry': 'ערך מחירון',
    'ServiceContract': 'חוזה שירות',
    'Entitlement': 'זכאות',
    'AssignedResource': 'משאב מוקצה',
    'ServiceTerritoryMember': 'חבר אזור שירות',
    'ResourceAbsence': 'היעדרות משאב',
    'ContentDocumentLink': 'קישור מסמך',
    'ProductRequired': 'מוצר נדרש',
    'ProductConsumed': 'מוצר שנצרך',
    'ProductRequest': 'בקשת מוצר',
    'TechnicianWallet__c': 'ארנק טכנאי',
    'Deviation__c': 'סטייה',
    'Activity_Barak__c': 'פעילות ברק',
    'Document': 'מסמך',
    'RecordType': 'סוג רשומה',
  };

  // Update document title when record loads
  useEffect(() => {
    if (currentRecord && object) {
      // For ServiceAppointment and WorkOrder, show the number instead of name
      let displayName;
      if (object.api_name === 'ServiceAppointment') {
        displayName = currentRecord.AppointmentNumber || currentRecord.Name || currentRecord.Id;
      } else if (object.api_name === 'WorkOrder') {
        const woNum = currentRecord.WorkOrderNumber;
        if (woNum) {
          const num = parseFloat(woNum);
          displayName = isNaN(num) ? woNum : String(Math.round(num)).padStart(8, '0');
        } else {
          displayName = currentRecord.Name || currentRecord.Id;
        }
      } else {
        displayName = currentRecord.Name || currentRecord.name || currentRecord.Id;
      }

      const hebrewObjectName = objectLabelsForTitle[object.api_name] || config?.heLabel || object.api_name;
      document.title = `Afikim | ${hebrewObjectName} | ${displayName}`;
    }

    // Cleanup - restore original title when component unmounts
    return () => {
      document.title = 'Afikim';
    };
  }, [currentRecord, object, config]);

  const [backendFields, setBackendFields] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [relatedRecords, setRelatedRecords] = useState({});
  // Fast-path counts populated by a separate /related-counts call so
  // the section headers can paint correct numbers without waiting for
  // the full /related fetch. Keyed by api_name → integer count.
  const [relatedCounts, setRelatedCounts] = useState({});
  const [expandedRelatedLists, setExpandedRelatedLists] = useState({});
  const [loadingRelated, setLoadingRelated] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showTechnicianScheduler, setShowTechnicianScheduler] = useState(false);
  const [showAreaPlanner, setShowAreaPlanner] = useState(false);
  const [showRouteOptimizer, setShowRouteOptimizer] = useState(false);
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTechnicianSelector, setShowTechnicianSelector] = useState(false);
  // 2026-05-20: institutional bulk-scheduler — opens from an SA's
  // action row. Targets the SA's parent Opportunity (resolved via the
  // SA → WorkOrder → Opportunity chain on click).
  const [institutionalSchedulerOppId, setInstitutionalSchedulerOppId] = useState(null);
  // dealer P3.4: "📤 שליחת מסמכים" on a closed marketer-delivery opportunity.
  const [docSendOppId, setDocSendOppId] = useState(null);
  // dealer P4: the same scheduler, titled for marketer deliveries.
  const [schedulerIsDelivery, setSchedulerIsDelivery] = useState(false);
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
  const [createAppointmentLoading, setCreateAppointmentLoading] = useState(false);
  const [createAppointmentResult, setCreateAppointmentResult] = useState(null);
  const [showCreateAssetModal, setShowCreateAssetModal] = useState(false);
  const [showCreateServiceContractModal, setShowCreateServiceContractModal] = useState(false);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  // Owner-spec 2026-06-09: Task record page — "העברת משימה" button opens
  // a modal that flips OwnerId, writes a `TaskTransfer` audit row, and
  // shows the full transfer history. New owner gets a bell ping.
  const [showTaskTransferModal, setShowTaskTransferModal] = useState(false);
  const [showTaskCompleteModal, setShowTaskCompleteModal] = useState(false);
  // Owner-spec 2026-10-14: an existing Task is edited only by a system
  // admin (flag `tasks.edit_lock`); others transfer it or press "הושלם".
  // The server decides — GET /api/tasks/edit-policy — and enforces it on
  // the PUT. Unknown / fetch failed = editable, the server still guards.
  // Fetched on every record page: the activity timeline's "סמן כהושלם"
  // checkboxes on Account/Opportunity pages edit tasks too.
  const [taskCanEdit, setTaskCanEdit] = useState(true);
  // ניהול הרשאות רשומות (record access PR 3): the caller's effective access to
  // this object. lockedFields = read-only on this screen for this user (empty
  // unless record_access.enforce is on; never for an admin). The server enforces
  // the same rule on the record screen's save.
  const [recordAccess, setRecordAccess] = useState(NO_RESTRICTIONS);
  const hiddenObjects = useHiddenObjects(currentUser && (currentUser.id || currentUser.username));
  useEffect(() => {
    let cancelled = false;
    setRecordAccess(NO_RESTRICTIONS);
    fetchRecordAccess(object?.api_name).then(a => { if (!cancelled) setRecordAccess(a); });
    return () => { cancelled = true; };
  }, [object?.api_name]);
  const lockedFields = useMemo(() => lockedFieldSet(recordAccess), [recordAccess]);
  // PR 4: whole-record edit from the screen, and delete (D1).
  const recordEditDenied = !canEditOnScreen(recordAccess);
  const recordDeleteDenied = !canDeleteRecords(recordAccess);
  // No ✏️ / double-click for this field: the field is read-only for me, or I may not edit at all.
  const noScreenEdit = (name) => recordEditDenied || lockedFields.has(name);
  const lockIcon = <span title="שדה לקריאה בלבד" style={{ marginInlineStart: 4, fontSize: 11 }}>🔒</span>;
  useEffect(() => {
    let cancelled = false;
    const _tok = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    fetch(`${API_BASE}/api/tasks/edit-policy`, { headers: _tok ? { 'Authorization': `Bearer ${_tok}` } : {} })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && typeof d.can_edit === 'boolean') setTaskCanEdit(d.can_edit); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [object?.api_name]);
  const taskEditLocked = object?.api_name === 'Task' && !taskCanEdit;
  // 2026-05-17: case-classification update modal — opened from Asset record page.
  // Lists all cases on this Asset with full WO/SA/PR/AR context; user can edit
  // Reason_1/2/Description and the change cascades through WO + SA + PR.
  // Locked when any SA is past Scheduled (Dispatched, In Progress, ...).
  const [showUpdateCaseClassificationModal, setShowUpdateCaseClassificationModal] = useState(false);
  // 2026-05-14: refund modal — opened from Payment__c detail by admin/accounting.
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  // 2026-05-14: change-owner modal — opened from the small icon next to
  // the OwnerId field. Available on any object whose detail page renders
  // OwnerId; saves OwnerId to the current record.
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  // 2026-05-15: strict-identification modal — opened from the Account
  // detail page button. On save it sets Strict_Identification__c=true
  // which unblocks Case creation from Asset records linked to this Account.
  const [showStrictIdentificationModal, setShowStrictIdentificationModal] = useState(false);
  // 2026-10-27 (owner): «הזדמנות חדשה» is blocked on an Account without coordinates.
  const [oppGateMsg, setOppGateMsg] = useState('');
  // 2026-05-18: AddProductRequiredModal — opened from the "+ הוסף מוצר נדרש"
  // button on a WorkOrder's ProductRequired related list. PR #2 of the
  // device-replacement-allocation feature.
  const [showAddProductRequiredModal, setShowAddProductRequiredModal] = useState(false);
  // 2026-05-17: WhatsApp opt-in modal — Account-page button. Customer
  // must reply "שלח" before we can hold a free-form conversation; this
  // modal lets the rep pick which Account phone to send the opt-in
  // prompt to, then POSTs it via /api/whatsapp/send.
  const [showWhatsAppOptInModal, setShowWhatsAppOptInModal] = useState(false);
  // 2026-05-17: Route-to-Agent modal — Account-page button. Mirrors the
  // SF flow Account- Route To Agent (retrieved-metadata/flows/
  // Account_Route_To_Agent.flow-meta.xml). 3 screens:
  //   1. Q1: pick Type_of_transfer_to_customer__c (הלקוח לא עונה / באמצע עסקה)
  //   2. yes/no (or change/no if an agent is already assigned)
  //   3. result (assigned / unassigned / error)
  // On Yes/Change: sets Account.Agent_ID__c=$User.Id, Agent_Name__c=$User
  // name, Transition_to_a_Agent__c=true, Type_of_transfer_to_customer__c
  // and POSTs the customer_not_available WhatsApp template.
  // On No: clears the agent assignment.
  const [showRouteToAgentModal, setShowRouteToAgentModal] = useState(false);
  const [showPriceQuoteModal, setShowPriceQuoteModal] = useState(false);
  const [rtaStep, setRtaStep] = useState(1);
  const [rtaTypeOfTransfer, setRtaTypeOfTransfer] = useState('');
  const [rtaChoice, setRtaChoice] = useState(null); // 'yes' | 'change' | 'no'
  const [rtaSubmitting, setRtaSubmitting] = useState(false);
  const [rtaResult, setRtaResult] = useState(null); // null | 'assigned' | 'unassigned' | 'error'
  const [rtaError, setRtaError] = useState('');
  // Accounting action modals (admin/accounting only)
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  // Account-level הפקת מסמכים — same CreateDocumentModal but seeded
  // with ALL the account's opportunities so the user can pick which
  // one to issue the document against. Kept as a separate state +
  // mount so the Opportunity-level modal logic stays untouched.
  const [showAccountDocumentModal, setShowAccountDocumentModal] = useState(false);
  const [accountDocumentOpps, setAccountDocumentOpps] = useState([]);
  const [loadingAccountOpps, setLoadingAccountOpps] = useState(false);
  const [showBouncedChecksPanel, setShowBouncedChecksPanel] = useState(false);
  const [accountChecks, setAccountChecks] = useState([]);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [selectedCheckForReturn, setSelectedCheckForReturn] = useState(null);
  const [showReturnedCheckModal, setShowReturnedCheckModal] = useState(false);
  const [editingPaymentStatusId, setEditingPaymentStatusId] = useState(null);
  const [savingPaymentStatusId, setSavingPaymentStatusId] = useState(null);
  const [showCustomerLedger, setShowCustomerLedger] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  // Email-send modal for a single customer document
  const [emailModalDoc, setEmailModalDoc] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ to: '', subject: '', body: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSendError, setEmailSendError] = useState(null);
  // Brief "הועתק" toast for copy-link action
  const [copyToast, setCopyToast] = useState(null);
  // WhatsApp confirmation modal — opens with prefilled text, user can
  // edit, send button POSTs to /api/whatsapp/send (Baileys, NOT wa.me)
  const [waDraftDoc, setWaDraftDoc] = useState(null);
  const [waDraftError, setWaDraftError] = useState(null);
  const [waSending, setWaSending] = useState(false);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [expandedLedgerOpps, setExpandedLedgerOpps] = useState({});
  // Rivhit customer balance (ledger cards)
  const [rivhitBalance, setRivhitBalance] = useState(null);
  const [rivhitBalanceLoading, setRivhitBalanceLoading] = useState(false);

  // Auto-load Rivhit balance when viewing Account with Rivhit customer ID
  const loadRivhitBalance = async (custId) => {
    if (!custId || custId === '0' || custId === '') return;
    setRivhitBalanceLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/rivhit/customer-balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: custId })
      });
      const data = await res.json();
      if (data.success) {
        setRivhitBalance(data);
        console.log('[RivhitBalance] Loaded:', data);
      } else {
        console.warn('[RivhitBalance] API error:', data.error);
        setRivhitBalance(null);
      }
    } catch (err) {
      console.error('[RivhitBalance] Fetch error:', err);
      setRivhitBalance(null);
    } finally {
      setRivhitBalanceLoading(false);
    }
  };

  // Auto-load Rivhit balance when Account with Rivhit_Customer_Id__c is displayed
  useEffect(() => {
    if (object?.api_name === 'Account' && currentRecord?.Rivhit_Customer_Id__c
        && currentRecord.Rivhit_Customer_Id__c !== '0' && currentRecord.Rivhit_Customer_Id__c !== '') {
      loadRivhitBalance(currentRecord.Rivhit_Customer_Id__c);
    } else {
      setRivhitBalance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRecord?.Id, currentRecord?.Rivhit_Customer_Id__c]);

  // Edit/Delete Line Item states
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  const [lineItemActionMenu, setLineItemActionMenu] = useState(null); // { recordId, x, y }
  const [lineItemProducts, setLineItemProducts] = useState([]); // Products for line item edit modal
  // 2026-06-01 owner-spec — per-user effective price-range permission
  // for the OLI edit modal. Shape mirrors ProductsModal:
  //   { is_admin, is_technician, product_ranges: { [pid]: {min, max} } }
  const [lineItemPricePermission, setLineItemPricePermission] = useState({
    is_admin: false, is_technician: false, is_institutional: false, is_accounting: false, product_ranges: {},
  });
  const [lineItemPriceError, setLineItemPriceError] = useState('');
  const [loadingLineItemProducts, setLoadingLineItemProducts] = useState(false);
  const [lineItemSaving, setLineItemSaving] = useState(false);
  const [customButtons, setCustomButtons] = useState([]);
  const [activeCustomAction, setActiveCustomAction] = useState(null);
  const [activeFormTemplate, setActiveFormTemplate] = useState(null);
  const [showMoreButtons, setShowMoreButtons] = useState(false);
  // Related list create modal state
  const [relatedCreateModal, setRelatedCreateModal] = useState(null); // { objectName, parentField, parentId, parentName }
  const [relatedCreateFields, setRelatedCreateFields] = useState([]);
  // History, Activity and Updates data
  const [recordHistory, setRecordHistory] = useState([]);
  const [recordActivities, setRecordActivities] = useState({ tasks: [], events: [] });
  const [recordUpdates, setRecordUpdates] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  // The activity tab renders an abbreviated view — 5 open tasks, 3 months, 10
  // items per month. This lifts all three caps at once.
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showNewActivityModal, setShowNewActivityModal] = useState(null);
  const [showCreditApprovalModal, setShowCreditApprovalModal] = useState(false);
  // 2026-10-25 — manager discount requests (בקשת הנחה ממנהל). null = feature off (flag → 404)
  // or not an Opportunity: no button, no banner, no line chips.
  const [showDiscountRequestModal, setShowDiscountRequestModal] = useState(false);
  const [discountSummary, setDiscountSummary] = useState(null);
  const isOpportunityPage = object?.api_name === 'Opportunity';
  const refreshDiscountSummary = useCallback(async () => {
    if (!isOpportunityPage || !currentRecord?.Id) { setDiscountSummary(null); return; }
    setDiscountSummary(await fetchOpportunityDiscounts(currentRecord.Id));
  }, [isOpportunityPage, currentRecord?.Id]);
  // Re-read when the lines change too: a deleted line cancels its pending request server-side.
  const discountLinesKey = isOpportunityPage
    ? (relatedRecords.OpportunityLineItem || []).filter((li) => !String(li.Id || '').startsWith('_opt_'))
        .map((li) => `${li.Id}:${li.TotalPrice}`).join('|')
    : '';
  useEffect(() => { refreshDiscountSummary(); }, [refreshDiscountSummary, discountLinesKey]);
  // 2026-06-17 owner-flagged: under workday load (BE creates ~2-3s) users
  // were clicking the submit button repeatedly out of frustration, and
  // each click fired a new POST. PG showed up to 41 identical Tasks
  // created within 22 seconds by the same user. Single state guards the
  // whole modal — covers task / event / call / email branches alike.
  const [savingActivity, setSavingActivity] = useState(false);
  const [activityMenuOpen, setActivityMenuOpen] = useState(null);
  // Hover card on related records — owner-spec 2026-05-25. Shows Account
  // name, Owner, CreatedBy, CreatedDate after a small hover delay so it
  // doesn't flash on accidental mouse-overs.
  const [relatedHover, setRelatedHover] = useState(null); // { rec, x, y, objectName }
  const _relatedHoverTimerRef = useRef(null);
  // Owner-spec 2026-06-16: single-click on a field value copies its
  // display text to clipboard; double-click still opens inline edit.
  // The 250 ms timer lets us distinguish single from double click — a
  // second click within the window cancels the pending copy and the
  // onDoubleClick handler takes over.
  const _fieldClickCopyTimerRef = useRef(null);
  const [activityUsers, setActivityUsers] = useState([]);
  const [activityUserSearch, setActivityUserSearch] = useState('');
  const [activitySelectedUser, setActivitySelectedUser] = useState(null);
  const [activityUserDropdownOpen, setActivityUserDropdownOpen] = useState(false);
  // PR after #806 owner-spec 2026-06-08: file attachments on the email
  // activity dialog. List of { name, size, type, content } where
  // content is base64. Resend caps total payload at ~40 MB; we
  // soft-cap each file at 10 MB to give headroom for HTML body etc.
  const [emailAttachments, setEmailAttachments] = useState([]);
  const isAdminUser = permIsAdmin(currentUser);
  const isAccountingUserForDelete = permIsAccounting(currentUser);
  // PR after #804 owner-spec 2026-06-08: institutional users get the
  // signed-doc Closed-Won bypass + price-edit + OLI delete carve-outs.
  const isInstitutionalUser = permIsInstitutional(currentUser);

  const handleDeleteActivity = async (activityId, activityType) => {
    setActivityMenuOpen(null);
    if (!window.confirm('האם למחוק פעילות זו?')) return;
    try {
      const objType = activityType === 'event' ? 'Event' : 'Task';
      const res = await fetch(`${API_BASE}/api/objects/${objType}/records/${activityId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        // הסר מהרשימה המקומית ישירות
        setRecordActivities(prev => ({
          tasks: prev.tasks.filter(t => t.Id !== activityId),
          events: prev.events.filter(e => e.Id !== activityId)
        }));
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(recordAccessErrorMessage(errData.detail) || ('שגיאה במחיקה: ' + (typeof errData.detail === 'string' ? errData.detail : res.status)));
      }
    } catch (e) {
      alert('שגיאה: ' + e.message);
    }
  };

  // Deal Summary - iForms Integration
  const [dealSummaryForm, setDealSummaryForm] = useState({
    status: 'pending', // pending, collecting, sent, signed
    iformsUrl: null,
    signedDocLink: null,
  });
  const [showDealSummaryModal, setShowDealSummaryModal] = useState(false);
  const [dealSummaryLoading, setDealSummaryLoading] = useState(false);
  // 2026-05-25: owner-spec "הסכם התקשרות" step 1 — collect track period
  // + number of payments before firing the iForms create. Sent to iForms
  // as c_period and payments; the backend computes m_price as the per-
  // installment price (opp.Amount / payments) so the form pre-fills
  // every per-payment row with a consistent value.
  const [engagementInputs, setEngagementInputs] = useState({ period: '', payments: '' });
  const [engagementForceNew, setEngagementForceNew] = useState(false);
  const [dealSummarySendMode, setDealSummarySendMode] = useState(null); // null|'choosing'|'confirm'|'editing'|'sending'|'sent'|'error'
  const [dealSummarySendChannel, setDealSummarySendChannel] = useState(null);
  const [dealSummarySendResult, setDealSummarySendResult] = useState(null);
  const [dealSummaryAccountData, setDealSummaryAccountData] = useState(null);
  const [dealSummaryContactValue, setDealSummaryContactValue] = useState(''); // phone or email to send to
  const [dealSummaryEditValue, setDealSummaryEditValue] = useState(''); // editing value
  // 2026-05-15: per-field picker for the deal-summary send flow.
  // dealSummaryContactField = which Account column the current
  // dealSummaryContactValue came from (e.g. 'PersonMobilePhone',
  // 'Phone', 'Another_Phone__c', 'PersonEmail'). The editing screen
  // uses this both as the default save-target and as the radio
  // selection when the user wants to write the new value to a
  // different slot. null = no Account column yet (user typed a fresh
  // number that doesn't match any field).
  const [dealSummaryContactField, setDealSummaryContactField] = useState(null);
  const [dealSummaryEditField, setDealSummaryEditField] = useState(null);

  // OTP Signing states
  const [otpStep, setOtpStep] = useState('choose');    // choose | sending | sent | verifying | verified | error
  const [otpMethod, setOtpMethod] = useState(null);     // 'whatsapp' | 'voice'
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpError, setOtpError] = useState('');
  const [otpSignature, setOtpSignature] = useState(null);

  // === WORK ORDER AUTOMATION STATES ===
  // Triggered when Opportunity is closed won with "התקנה (טכנאי מכירה)" work type
  const [showWorkTypeModal, setShowWorkTypeModal] = useState(false);
  const [workTypeModalStep, setWorkTypeModalStep] = useState('workType'); // 'workType', 'productItem', 'serialized', 'creating'
  // UX Performance Initiative (owner-spec 2026-06-09): live per-phase
  // progress for the WO/PR/SA creation step. Each phase walks through:
  //   pending → running → done | error
  const [workOrderProgress, setWorkOrderProgress] = useState({
    workOrder: 'pending', products: 'pending', sa: 'pending',
  });
  const [availableWorkTypes, setAvailableWorkTypes] = useState([]);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  // 2026-05-20: auto-recommended work type for the Closed Won flow.
  // Rules (per owner spec):
  //   Opp.Name contains 'שדרוג'/'שידרוג'     → 'שידרוג'
  //   any line-item product has IsSerialized → 'התקנה (טכנאי מכירה)'
  //   else                                    → 'שירות'
  // We pre-select this in the modal; if the user picks a different
  // type we ask to confirm.
  const [recommendedWorkType, setRecommendedWorkType] = useState(null);
  // 2026-05-24: no-tech-warranty precheck result (eligibility for Asset+SC
  // creation when user picks "no technician needed" on a warranty Opp).
  // Drives the noTechWarrantyConfirm / noTechWarrantyNoPayment modal steps.
  // The Asset for the new SC is picked from the Account's existing assets
  // (warranty-extension is sold against something the customer already owns).
  const [noTechWarrantyPrecheck, setNoTechWarrantyPrecheck] = useState(null);
  const [noTechWarrantyAssets, setNoTechWarrantyAssets] = useState([]);
  const [noTechWarrantySelectedAssetId, setNoTechWarrantySelectedAssetId] = useState('');
  const [noTechWarrantyAssetsLoading, setNoTechWarrantyAssetsLoading] = useState(false);
  // 2026-05-29: Task 3 — "no-tech שירות" close with verified payment + no
  // serialized OLI asks the user whether to issue a tax invoice. "No"
  // requires a free-text reason, both stored on the Opp.
  const [noTechInvoiceReason, setNoTechInvoiceReason] = useState('');
  const [noTechInvoiceDecisionNo, setNoTechInvoiceDecisionNo] = useState(false);
  const [noTechInvoiceSaving, setNoTechInvoiceSaving] = useState(false);
  // 2026-05-20: in-flight flag for the multi-address splitter submit
  // (POST /api/opportunity/{id}/close-multi-address). Disables the
  // confirm button + ביטול while the transactional create runs.
  const [multiAddressSubmitting, setMultiAddressSubmitting] = useState(false);
  // 2026-05-20: parent Account for the Closed Won multi-address step.
  // The Q1a sub-question ("האם הכתובת היא <BillingAddress>?") needs
  // the Account on hand. We fetch it lazily once when we route into
  // the multiAddressClose step.
  const [closeFlowAccount, setCloseFlowAccount] = useState(null);
  // 2026-05-20: success-result modal for the multi-address splitter
  // (replaces the previous bare alert). Holds the backend response so
  // the manager sees the WO + SA numbers + can click through.
  const [multiAddressResult, setMultiAddressResult] = useState(null);
  // 2026-10-09 (dealer P2-B): "מסירה ללקוח משווק". closeIsMarketer = the opportunity's account is a
  // marketer AND dealer_sales_enabled is on (decided by the server at close time).
  const [closeIsMarketer, setCloseIsMarketer] = useState(false);
  const [bdIssueInvoice, setBdIssueInvoice] = useState(null);       // true | false | null (not asked yet)
  const [bdInvoiceSkipReason, setBdInvoiceSkipReason] = useState('');
  const [bdIssueNotes, setBdIssueNotes] = useState(null);
  const [bdSubmitting, setBdSubmitting] = useState(false);
  const [bdResult, setBdResult] = useState(null);
  const [availableProductItems, setAvailableProductItems] = useState([]);
  const [selectedProductItem, setSelectedProductItem] = useState(null);
  const [availableSerializedProducts, setAvailableSerializedProducts] = useState({});
  const [selectedSerializedProducts, setSelectedSerializedProducts] = useState({});
  // Accumulates all (productItem + chosen serial) pairs across multiple selection rounds
  // so that opportunities with 2+ serialized products iterate through all of them
  // before creating the WorkOrder. Format: [{ productItem, serial, productId }]
  const [completedSerialSelections, setCompletedSerialSelections] = useState([]);
  const [workOrderCreating, setWorkOrderCreating] = useState(false);
  const [workOrderError, setWorkOrderError] = useState(null);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [workOrderResult, setWorkOrderResult] = useState(null); // { workOrderId, workOrderNumber, serviceAppointmentId, serviceAppointmentNumber }

  // Service flow states (שירות → נכס → מקרה → WO+SA)
  const SERVICE_REASON_1 = [
    'החלפת סננים','התקנה',
    'נזילה','רעשים','הבהוב / צפצוף ללא הפסקה','לא מחמם','לא מקרר','לא יוצא מים כלל',
    'אחר','מסך כבוי','כרטיס חכם','טיפול בטעם המים','מקצר','החלפת מכשיר','פלסטיקה',
    'מסירה','החלפת לנס','החזר מעבדה','מעבר דירה'
  ];
  const SERVICE_REASON_2 = {
    'נזילה': ['נזילה מהמכשיר','נזילה מהצינור','נזילה מהברז','נזילה מהחיבורים','אחר'],
    'רעשים': ['רעש מהמכשיר','רעש מהמנוע','רעש מהמשאבה','אחר'],
    'לא מחמם': ['לא מחמם בכלל','מחמם חלש','אחר'],
    'לא מקרר': ['לא מקרר בכלל','מקרר חלש','אחר'],
    'לא יוצא מים כלל': ['חם וקר','רק חם','רק קר','אחר'],
    'החלפת סננים': ['החלפה תקופתית','סנן פגום','אחר'],
    'הבהוב / צפצוף ללא הפסקה': ['הבהוב','צפצוף','שניהם','אחר'],
    'טיפול בטעם המים': ['טעם רע','ריח','צבע','אחר'],
  };
  const [serviceAssets, setServiceAssets] = useState([]);
  const [selectedServiceAsset, setSelectedServiceAsset] = useState(null);
  const [loadingServiceAssets, setLoadingServiceAssets] = useState(false);
  const [serviceCaseData, setServiceCaseData] = useState({ Reason_1__c: '', Reason_2__c: '', Description: '', DateOfLastFilterReplacement: '' });
  const [showCreateAssetInWOFlow, setShowCreateAssetInWOFlow] = useState(false);

  // Alias for objectFields
  const objectFields = backendFields;

  // טעינת של הרשומות הקשורות (פשוט - מהשרת)
  const loadAllRelatedRecords = async (cacheBust = false) => {
    if (!currentRecord.Id) {
      setLoadingRelated({});
      return;
    }

    setLoadingRelated({ all: true });
    try {
      // קריאה ל-API - cacheBust=true מוסיף _t לדלג על cache בשרת
      const cacheBustParam = cacheBust ? `&_t=${Date.now()}` : '';
      const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/records/${currentRecord.Id}/related?limit=100&resolve_refs=true${cacheBustParam}`);
      if (res.ok) {
        const data = await res.json();
        // המרה לפורמט שה-UI מצפה לו
        const newRelatedRecords = {};
        for (const [objName, objData] of Object.entries(data.related_objects || {})) {
          const records = objData.records || [];
          newRelatedRecords[objName] = records;

          // שמור אה של הרשומות הקשורות ב-cache לניווט מהיר
          if (records.length > 0) {
            addToCache(objName, records);
            setExpandedRelatedLists(prev => ({ ...prev, [objName]: true }));
          }
        }
        setRelatedRecords(newRelatedRecords);
        // 2026-06-13 owner-reported: after creating a child via a modal,
        // loadAllRelatedRecords(true) updated relatedRecords but the
        // sidebar badge counts ('הזדמנויות (0)') stayed stuck at the
        // values fetched by the initial /related-counts call. relatedCounts
        // is a separate state — fold the live counts in here so any reload
        // (modal-onSuccess, manual refresh, _t cache-bust) repaints the
        // badges in lockstep with the records list. Each
        // related_objects[X].count is the BE-authoritative number for that
        // child type; when missing, fall back to length-of-records (the
        // record list is the same parent-side data that feeds the count).
        const refreshedCounts = {};
        for (const [objName, objData] of Object.entries(data.related_objects || {})) {
          const beCount = (objData && typeof objData.count === 'number') ? objData.count : null;
          const records = (objData && objData.records) || [];
          refreshedCounts[objName] = beCount !== null ? beCount : records.length;
        }
        setRelatedCounts(refreshedCounts);
        console.log(`[loadAllRelatedRecords] Found ${Object.keys(newRelatedRecords).length} objects for ${object.api_name}/${currentRecord.Id}:`, Object.keys(newRelatedRecords));

        // For Case records - also fetch EmailMessage from dedicated endpoint
        if (object.api_name === 'Case') {
          try {
            const emailRes = await fetch(`${API_BASE}/api/objects/Case/records/${currentRecord.Id}/emails`);
            if (emailRes.ok) {
              const emailData = await emailRes.json();
              if (emailData.emails && emailData.emails.length > 0) {
                newRelatedRecords['EmailMessage'] = emailData.emails;
                setExpandedRelatedLists(prev => ({ ...prev, 'EmailMessage': true }));
              }
            }
          } catch (emailErr) {
            console.warn('Could not fetch Case emails:', emailErr);
          }
        }

        // For ServiceAppointment records - also fetch activity history
        if (object.api_name === 'ServiceAppointment') {
          try {
            const histRes = await fetch(`${API_BASE}/api/objects/ServiceAppointment/records/${currentRecord.Id}/history`);
            if (histRes.ok) {
              const histData = await histRes.json();
              if (histData.history && histData.history.length > 0) {
                newRelatedRecords['ServiceAppointmentHistory'] = histData.history.map((h, i) => ({
                  Id: `hist_${i}`,
                  _histType: h.type,
                  _histDate: h.date,
                  _histUser: h.user,
                  _histField: h.field,
                  _histOldValue: h.oldValue,
                  _histNewValue: h.newValue,
                  _histAction: h.action,
                }));
                setExpandedRelatedLists(prev => ({ ...prev, 'ServiceAppointmentHistory': true }));
              }
            }
          } catch (histErr) {
            console.warn('Could not fetch SA history:', histErr);
          }
        }

        // For Asset records - also fetch lab repair history
        if (object.api_name === 'Asset') {
          try {
            // Hotfix L-04 (2026-08-13): this call used to go out with no
            // Authorization header, which is why the endpoint had no auth
            // gate at all. The endpoint now requires a lab profile, so the
            // panel renders for lab staff + Admin only; the `labRes.ok`
            // check below already fails soft for everyone else.
            const labToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
            const labRes = await fetch(`${API_BASE}/api/lab/repairs/by-asset/${currentRecord.Id}`, {
              headers: labToken ? { 'Authorization': `Bearer ${labToken}` } : {}
            });
            if (labRes.ok) {
              const labData = await labRes.json();
              if (labData.success && labData.repairs && labData.repairs.length > 0) {
                const labRows = labData.repairs.map(r => ({
                  Id: r.id,
                  // «לפני מערכת מלאי» (owner 2026-10-19): a pilot/test-era
                  // record is labelled, never shown as real repair history.
                  Name: r.pre_inventory_system
                    ? `${r.repair_number} · לפני מערכת מלאי`
                    : r.repair_number,
                  ...r
                }));
                // Stored under every spelling the panel may be keyed by.
                //
                // The related list reads `relatedRecords[list.objectName]`, and
                // that objectName arrives from two places: the hardcoded Asset
                // map says `LabRepair`, while a list derived from /api/objects
                // says `Lab_repair` — the table name. Writing only one of them
                // left «תיקון מעבדה (0)» sitting above the repair's own PDF.
                //
                // One array, several keys. Cheaper and safer than normalising
                // four separate lookup sites, and the panel finds it whichever
                // definition rendered it.
                LAB_REPAIR_RECORD_KEYS.forEach(k => { newRelatedRecords[k] = labRows; });
                setExpandedRelatedLists(prev => {
                  const next = { ...prev };
                  LAB_REPAIR_RECORD_KEYS.forEach(k => { next[k] = true; });
                  return next;
                });
              }
            }
          } catch (labErr) {
            console.warn('Could not fetch lab repairs:', labErr);
          }
        }

        setRelatedRecords(newRelatedRecords);
        // Cache the assembled snapshot keyed by the parent record so a
        // revisit paints instantly. Includes the Case-emails / SA-history
        // / Asset-lab-repairs sidecars layered on above.
        // 2026-06-13 (#904 follow-up): also persist `counts` so the
        // cache-HIT branch in useEffect (line ~14787) can repaint the
        // sidebar badges with the live numbers instead of leaving them
        // at whatever value /related-counts wrote 60s earlier. Without
        // this, owner saw "OLI added → F5 → OLI (0) → wait ~60s → OLI
        // back to (N)" — the 60s being _RELATED_CACHE_TTL_MS.
        // refreshedCounts is the same map already fed to setRelatedCounts
        // a few lines up — reuse it directly so the cache write and the
        // immediate render agree.
        const cacheKey = `${object.api_name}/${currentRecord.Id}`;
        _relatedRecordsCache.set(cacheKey, {
          data: newRelatedRecords,
          timestamp: Date.now(),
          counts: refreshedCounts,
        });
        // Bound the cache to keep memory predictable on long sessions.
        if (_relatedRecordsCache.size > 200) {
          const oldestKey = _relatedRecordsCache.keys().next().value;
          if (oldestKey) _relatedRecordsCache.delete(oldestKey);
        }
      } else {
        console.warn('Related records API returned non-OK status:', res.status);
        setRelatedRecords({});
      }
    } catch (err) {
      console.error('Error loading related records:', err);
      setRelatedRecords({});
    } finally {
      setLoadingRelated({});
    }
  };

  // פונקציה ישנה לתאימות - לא עושה כלום כי הרשומות נטענות כבר
  const loadRelatedRecords = async (relatedObjectName, relationshipField) => {
    // כבר נטען ב-loadAllRelatedRecords
  };

  // === DEAL SUMMARY - iForms FUNCTIONS ===
  const handleDealSummary = async () => {
    if (!currentRecord?.Id) return;

    setDealSummaryLoading(true);
    setShowDealSummaryModal(true);
    // Reset send state
    setDealSummarySendMode(null);
    setDealSummarySendChannel(null);
    setDealSummarySendResult(null);
    // Reset OTP state
    resetOtpState();

    // Fetch Account data for phone/email
    const acctId = currentRecord.AccountId || currentRecord.Account__c || '';
    console.log('[DealSummary] AccountId:', acctId);
    if (acctId) {
      try {
        const acctRes = await fetch(`${API_BASE}/api/objects/Account/records/${acctId}`);
        if (acctRes.ok) {
          const acctJson = await acctRes.json();
          // backend wraps single-record responses as {"record": {...fields}};
          // older code read acctData.Phone etc. directly, getting undefined -> all phone slots empty
          const acctData = acctJson?.record || acctJson;
          console.log('[DealSummary] Account data:', { Phone: acctData?.Phone, PersonEmail: acctData?.PersonEmail, PersonMobilePhone: acctData?.PersonMobilePhone, Another_Phone__c: acctData?.Another_Phone__c });
          setDealSummaryAccountData(acctData);
        } else {
          console.warn('[DealSummary] Account fetch returned:', acctRes.status);
        }
      } catch (e) {
        console.warn('[DealSummary] Could not fetch account data:', e);
      }
    } else {
      console.warn('[DealSummary] No AccountId found on Opportunity record. Fields:', Object.keys(currentRecord).filter(k => k.toLowerCase().includes('account')));
    }

    try {
      // Check if there's already a pre-sign link on the Opportunity
      const existingLink = currentRecord.Pre_Sign_Document_Link_c__c;
      const signedLink = currentRecord.Signed_Document_Link_c__c;

      if (signedLink) {
        // Document already signed
        setDealSummaryForm({
          status: 'signed',
          iformsUrl: existingLink || null,
          signedDocLink: signedLink,
        });
        setDealSummaryLoading(false);
        return;
      }

      if (existingLink) {
        // Link already exists, reuse it
        setDealSummaryForm({
          status: 'sent',
          iformsUrl: existingLink,
          signedDocLink: null,
        });
        setDealSummaryLoading(false);
        return;
      }

      // No existing link — owner-spec 2026-05-25 inserts a Step 1 here
      // that collects track period + number of payments. The actual
      // iForms create is deferred to submitEngagementInputs() so the
      // 2 fields can be passed as c_period / payments and the backend
      // can compute m_price = Amount / payments.
      setEngagementInputs({ period: '', payments: '' });
      setEngagementForceNew(false);
      setDealSummaryForm({ status: 'collecting', iformsUrl: null, signedDocLink: null });

    } catch (err) {
      console.error('[DealSummary] Error:', err);
      alert('שגיאה: ' + err.message);
      setShowDealSummaryModal(false);
    } finally {
      setDealSummaryLoading(false);
    }
  };

  // Step-2 of the engagement-agreement flow: takes the 2 collected
  // inputs + an optional force_new flag (from "צור קישור חדש"), fires
  // the iForms backend, and routes the response into the existing
  // 'sent' state. Compatible with createNewDealSummaryLink — both
  // paths funnel through here so the period/payments inputs always
  // reach iForms.
  const submitEngagementInputs = async () => {
    if (!currentRecord?.Id) return;
    const period = String(engagementInputs.period || '').trim();
    const payments = parseInt(engagementInputs.payments, 10);
    if (!period) {
      alert('יש להזין תקופת מסלול');
      return;
    }
    if (!payments || payments < 1) {
      alert('מספר תשלומים חייב להיות לפחות 1');
      return;
    }
    setDealSummaryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/iforms/send-deal-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunity_id: currentRecord.Id,
          c_period: period,
          payments: payments,
          force_new: !!engagementForceNew,
        }),
      });
      const data = await res.json();
      if (data.success && data.url) {
        setDealSummaryForm({ status: 'sent', iformsUrl: data.url, signedDocLink: null });
        if (currentRecord) currentRecord.Pre_Sign_Document_Link_c__c = data.url;
      } else {
        throw new Error(data.error || 'שגיאה ביצירת ההסכם');
      }
    } catch (err) {
      console.error('[Engagement] submit failed:', err);
      alert('שגיאה: ' + err.message);
    } finally {
      setDealSummaryLoading(false);
    }
  };

  // Force-create a new iForms deal-summary link, replacing whatever is
  // currently on the Opportunity. Used by the "צור קישור חדש" button —
  // bypasses handleDealSummary's existing-link reuse so the customer
  // gets a fresh signing URL (e.g. after they lost the previous one).
  const createNewDealSummaryLink = async () => {
    if (!currentRecord?.Id) return;
    if (!window.confirm('ליצור קישור חדש? הקישור הקיים יוחלף.')) return;
    // Reset send/OTP state so the UI doesn't carry stale "sent to X" labels
    setDealSummarySendMode('choosing');
    setDealSummarySendResult(null);
    resetOtpState();
    // Owner-spec 2026-05-25: even the "צור קישור חדש" path collects the
    // period + payments inputs first. The actual iForms call fires from
    // submitEngagementInputs with force_new=true so the new instance
    // carries the up-to-date c_period / payments / m_price values
    // (the previous instance's values are no longer reusable).
    setEngagementInputs({ period: '', payments: '' });
    setEngagementForceNew(true);
    setDealSummaryForm({ status: 'collecting', iformsUrl: null, signedDocLink: null });
  };

  // Refresh deal summary status (re-fetch opportunity to check if signed)
  const refreshDealSummaryStatus = async () => {
    if (!currentRecord?.Id) return;
    try {
      const res = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`);
      if (res.ok) {
        const opp = await res.json();
        const signedLink = opp.Signed_Document_Link_c__c;
        const preSignLink = opp.Pre_Sign_Document_Link_c__c;

        if (signedLink) {
          setDealSummaryForm(prev => ({
            ...prev,
            status: 'signed',
            signedDocLink: signedLink,
            iformsUrl: preSignLink || prev.iformsUrl,
          }));
          // Update local record
          if (currentRecord) {
            currentRecord.Signed_Document_Link_c__c = signedLink;
            currentRecord.Document_Signed__c = 'true';
          }
        }
      }
    } catch (err) {
      console.error('[DealSummary] Error refreshing status:', err);
    }
  };

  // Helper: get customer contact info from Account data
  const getDealSummaryCustomerInfo = () => {
    const acct = dealSummaryAccountData;
    // Helper to filter out null/NaN/undefined/empty values
    const clean = (val) => (val && val !== 'nan' && val !== 'NaN' && val !== 'null' && val !== 'undefined') ? String(val).trim() : '';
    return {
      name: clean(acct?.Name) || `${clean(acct?.FirstName)} ${clean(acct?.LastName)}`.trim() || clean(currentRecord?.Account__Name) || clean(currentRecord?.AccountId__Name) || clean(currentRecord?.Name) || '',
      phone: clean(acct?.Phone) || clean(acct?.PersonMobilePhone) || clean(acct?.MobilePhone) || clean(acct?.Another_phone__c) || clean(currentRecord?.Phone) || clean(currentRecord?.Contact_Phone__c) || '',
      email: clean(acct?.PersonEmail) || clean(acct?.Email__c) || clean(acct?.Email) || clean(currentRecord?.Email) || clean(currentRecord?.Contact_Email__c) || '',
    };
  };

  // 2026-05-15: per-field list for the deal-summary send picker.
  // Owner-asked: when sending the link via SMS/WhatsApp/Email, show
  // ALL available phone / email columns on the Account as radio rows
  // so the rep can pick which one to send to (and which slot to
  // update when adding a new number). Empty fields still appear so
  // the rep has an explicit "save the new number into this slot"
  // choice. Account JSON sometimes camel-cases the column key and
  // sometimes returns the lowercase PG name, so each entry checks
  // both spellings.
  const getDealSummaryContactOptions = (channel) => {
    const acct = dealSummaryAccountData || {};
    const clean = (val) => (val && val !== 'nan' && val !== 'NaN' && val !== 'null' && val !== 'undefined') ? String(val).trim() : '';
    if (channel === 'email') {
      return [
        { field: 'PersonEmail', label: 'אימייל ראשי', value: clean(acct.PersonEmail) || clean(acct.personemail) },
      ];
    }
    // SMS / WhatsApp → phone columns
    return [
      { field: 'PersonMobilePhone', label: 'טלפון נייד',
        value: clean(acct.PersonMobilePhone) || clean(acct.personmobilephone) || clean(acct.MobilePhone) || clean(acct.mobilephone) },
      { field: 'Phone',             label: 'טלפון',
        value: clean(acct.Phone) || clean(acct.phone) },
      { field: 'Another_Phone__c',  label: 'טלפון נוסף',
        value: clean(acct.Another_Phone__c) || clean(acct.Another_phone__c) || clean(acct.another_phone__c) },
    ];
  };

  // Pick the first non-empty option (for the initial radio selection
  // when the channel is chosen).
  const pickInitialDealSummaryContact = (options) => {
    const firstNonEmpty = options.find(o => o.value);
    return firstNonEmpty || options[0] || { field: null, value: '' };
  };

  // Helper: normalize Israeli phone
  const normalizeDealSummaryPhone = (phone) => {
    if (!phone) return null;
    let cleaned = phone.replace(/[^0-9]/g, '');
    if (cleaned.startsWith('05') && cleaned.length === 10) cleaned = '972' + cleaned.substring(1);
    else if (cleaned.startsWith('0') && cleaned.length === 9) cleaned = '972' + cleaned.substring(1);
    else if (!cleaned.startsWith('972') && cleaned.length === 9) cleaned = '972' + cleaned;
    return cleaned;
  };

  // Step 1: User picks channel → show confirm screen with contact info.
  // 2026-05-15: now pre-selects the first non-empty Account contact
  // column so the radio-list in the confirm screen starts with a
  // sensible default. dealSummaryContactField tracks which Account
  // column the current dealSummaryContactValue came from — used as
  // the default save-target in the editing screen.
  const selectDealSummaryChannel = (channel) => {
    setDealSummarySendChannel(channel);
    setDealSummarySendResult(null);
    const options = getDealSummaryContactOptions(channel);
    const initial = pickInitialDealSummaryContact(options);
    setDealSummaryContactValue(initial.value || '');
    setDealSummaryContactField(initial.field || null);
    setDealSummarySendMode('confirm');
  };

  // 2026-05-15: save a contact value into a *specific* Account column.
  // Previously this hardcoded the slot to Phone / PersonEmail; now the
  // caller passes targetField so the rep can choose where the new
  // number lands (PersonMobilePhone, Phone, Another_Phone__c).
  // Falls back to the legacy targets if targetField is omitted.
  const saveDealSummaryContactToAccount = async (value, targetField) => {
    const acctId = currentRecord?.AccountId;
    if (!acctId || !value) return;
    const channel = dealSummarySendChannel;
    const fieldToUpdate = targetField
      || ((channel === 'email') ? 'PersonEmail' : 'PersonMobilePhone');
    try {
      const res = await fetch(`${API_BASE}/api/objects/Account/records/${acctId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [fieldToUpdate]: value })
      });
      if (res.ok) {
        // Keep the local Account cache in sync so the radio-list
        // immediately reflects the new value.
        setDealSummaryAccountData(prev => prev ? { ...prev, [fieldToUpdate]: value } : prev);
        console.log(`[DealSummary] Updated Account ${fieldToUpdate} to ${value}`);
      } else {
        const t = await res.text().catch(() => '');
        console.warn(`[DealSummary] Account PUT ${fieldToUpdate} failed: ${res.status} ${t}`);
      }
    } catch (err) {
      console.warn('[DealSummary] Failed to update account:', err);
    }
  };

  // Step 2: Actually send the link
  const sendDealSummaryLink = async () => {
    const channel = dealSummarySendChannel;
    const contactValue = dealSummaryContactValue;
    setDealSummarySendMode('sending');
    setDealSummarySendResult(null);

    const link = dealSummaryForm.iformsUrl;
    if (!link) {
      setDealSummarySendMode('error');
      setDealSummarySendResult({ success: false, message: 'אין קישור לשליחה' });
      return;
    }

    const info = getDealSummaryCustomerInfo();
    const customerName = info.name;
    const userId = currentUser?.email || currentUser?.id;
    const userName = currentUser?.name || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.email;

    try {
      if (channel === 'whatsapp' || channel === 'sms') {
        if (!contactValue) throw new Error('לא הוזן מספר טלפון');
        const normalizedPhone = normalizeDealSummaryPhone(contactValue);
        const message = `שלום ${customerName}\nמצורף לינק לחתימה על סיכום העסקה עם אפיקים מים:\n${link}`;
        const endpoint = channel === 'sms' ? '/api/sms/send' : '/api/whatsapp/send';

        const res = await fetch(`${API_BASE}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: normalizedPhone,
            message,
            userId,
            userName,
            source: 'record_tab',
            recordId: currentRecord?.Id,
            recordType: 'Opportunity'
          })
        });
        const data = await res.json();
        if (data.success) {
          setDealSummarySendMode('sent');
          setDealSummarySendResult({
            success: true,
            message: channel === 'whatsapp' ? `הלינק נשלח בווטסאפ ל-${contactValue}` : `הלינק נשלח ב-SMS ל-${contactValue}`,
            channel
          });
        } else {
          throw new Error(data.error || 'שגיאה בשליחה');
        }

      } else if (channel === 'email') {
        if (!contactValue) throw new Error('לא הוזן מייל');
        const agentName = userName || 'אפיקים מים';
        const html = `
          <div dir="rtl" style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #0176d3 0%, #014486 100%); padding: 24px; border-radius: 8px 8px 0 0;">
              <h2 style="color: white; margin: 0; font-size: 20px;">הסכם התקשרות - אפיקים מים</h2>
            </div>
            <div style="padding: 24px; background: #f9f9f9; border: 1px solid #e5e5e5;">
              <p style="font-size: 16px;">שלום ${customerName},</p>
              <p>מצורף לינק לחתימה על הסכם ההתקשרות עם אפיקים מים:</p>
              <div style="text-align: center; margin: 24px 0;">
                <a href="${link}" style="display: inline-block; padding: 12px 32px; background: #0176d3; color: white; text-decoration: none; border-radius: 6px; font-size: 16px; font-weight: bold;">לחתימה על המסמך</a>
              </div>
              <p style="color: #706e6b; font-size: 13px;">או העתק/י את הלינק הבא:</p>
              <p style="direction: ltr; word-break: break-all; background: #fff; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px;">${link}</p>
              <hr style="border: none; border-top: 1px solid #e5e5e5; margin: 24px 0;" />
              <p style="color: #706e6b; font-size: 13px;">בברכה,<br/>${agentName}<br/>אפיקים מים</p>
              <p style="color: #a8a8a8; font-size: 11px; margin-top: 16px; font-style: italic;">נא לא להשיב למייל זה.</p>
            </div>
          </div>
        `;

        // Owner-spec 2026-06-10: previously this returned
        // email_not_verified because we never told the backend who the
        // sender was. The /api/send-email gate resolves sender by Bearer
        // OR senderUserEmail; pass both replyTo + senderUserEmail like
        // the activity-panel email path so the gate finds the verified
        // user (and replies route to them).
        const currentUserEmail = currentUser?.email || currentUser?.username || '';
        const res = await fetch(`${API_BASE}/api/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: contactValue,
            subject: 'הסכם התקשרות - אפיקים מים',
            html,
            fromName: agentName,
            replyTo: currentUserEmail,
            senderUserEmail: currentUserEmail,
          })
        });
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          if (detail.error === 'email_not_verified') {
            throw new Error(detail.message || 'כתובת המייל שלך אינה מאומתת. בקש ממנהל לאמת את הכתובת בהגדרות > ניהול משתמשים.');
          }
          throw new Error(detail.error || 'שגיאה בשליחת המייל');
        }
        const data = await res.json();
        if (data.success) {
          setDealSummarySendMode('sent');
          setDealSummarySendResult({ success: true, message: `הלינק נשלח למייל ${contactValue}`, channel: 'email' });
        } else {
          throw new Error(data.error || 'שגיאה בשליחת המייל');
        }
      }
    } catch (err) {
      console.error('[DealSummary] Send error:', err);
      setDealSummarySendMode('error');
      setDealSummarySendResult({ success: false, message: err.message, channel });
    }
  };

  // Get button style based on form status
  const getDealSummaryButtonStyle = () => {
    const signedLink = currentRecord?.Signed_Document_Link_c__c;
    const preSignLink = currentRecord?.Pre_Sign_Document_Link_c__c;
    if (signedLink) {
      return { background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', color: 'white', border: 'none' };
    } else if (preSignLink) {
      return { background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white', border: 'none' };
    }
    return { background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)', color: 'white', border: 'none' };
  };

  // === OTP SIGNING FUNCTIONS ===

  const otpTimerRef = React.useRef(null);

  const startOtpTimer = (seconds) => {
    setOtpTimer(seconds);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      setOtpTimer(prev => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetOtpState = () => {
    setOtpStep('choose');
    setOtpMethod(null);
    setOtpCode('');
    setOtpError('');
    setOtpSignature(null);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    setOtpTimer(0);
  };

  const sendOtp = async (method) => {
    const info = getDealSummaryCustomerInfo();
    const phone = otpPhone || info.phone;
    if (!phone) {
      setOtpError('לא נמצא מספר טלפון ללקוח');
      return;
    }

    setOtpMethod(method);
    setOtpStep('sending');
    setOtpError('');
    setOtpCode('');

    const agentName = currentUser?.name || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.email || '';
    const customerInfo = getDealSummaryCustomerInfo();

    // Gather product details from opportunity line items
    const lineItems = relatedRecords['OpportunityLineItem'] || [];
    const products = lineItems.map(li => ({
      name: li.Product_Name__c || li.Name || li.Description || '',
      quantity: li.Quantity || '1',
      price: li.TotalPrice || li.UnitPrice || '',
    })).filter(p => p.name);
    const totalAmount = currentRecord?.Amount || '';

    try {
      const endpoint = method === 'whatsapp' ? '/api/otp/send-whatsapp' : '/api/otp/send-voice';
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizeDealSummaryPhone(phone),
          opportunityId: currentRecord?.Id || '',
          agentName,
          customerName: customerInfo.name,
          products,
          totalAmount,
        })
      });
      const data = await res.json();
      if (data.success) {
        setOtpStep('sent');
        startOtpTimer(data.expiresIn || 600);
      } else {
        setOtpStep('error');
        setOtpError(data.error || 'שגיאה בשליחת קוד');
      }
    } catch (err) {
      console.error('[OTP] Send error:', err);
      setOtpStep('error');
      setOtpError(err.message || 'שגיאת רשת');
    }
  };

  const verifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('יש להזין קוד בן 6 ספרות');
      return;
    }

    const info = getDealSummaryCustomerInfo();
    const phone = otpPhone || info.phone;

    setOtpStep('verifying');
    setOtpError('');

    try {
      const res = await fetch(`${API_BASE}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizeDealSummaryPhone(phone),
          code: otpCode,
          opportunityId: currentRecord?.Id || '',
          iformsUrl: dealSummaryForm.iformsUrl || '',
        })
      });
      const data = await res.json();
      if (data.success && data.verified) {
        setOtpStep('verified');
        setOtpSignature(data.signature);
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        // Update the form status
        setDealSummaryForm(prev => ({ ...prev, status: 'signed' }));
      } else {
        setOtpStep('sent');
        setOtpError(data.error || 'קוד שגוי');
        if (data.locked || data.expired) {
          setOtpStep('error');
        }
      }
    } catch (err) {
      console.error('[OTP] Verify error:', err);
      setOtpStep('sent');
      setOtpError(err.message || 'שגיאת רשת');
    }
  };

  const formatOtpTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // === WORK ORDER AUTOMATION FUNCTIONS ===
  // 2026-06-21 PR-A: was `const MAIN_WAREHOUSE_ID = '131Hp...'`. The ID is
  // now fetched from /api/config/main-warehouse-id (single source of truth
  // in shared/config.py). Cached on workOrderDataCache.current so we don't
  // re-fetch on every WorkOrder open.

  // Cache for preloaded data
  const workOrderDataCache = React.useRef({
    productItems: null,
    productNames: null,
    serializedProducts: null,
    lastLoaded: null,
    isLoading: false,
    loadPromise: null
  });

  // Trigger work order flow when opportunity is closed won
  // 2026-05-20: Helper for the auto-recommendation in the Closed Won flow.
  // Returns the WorkType.Name we'd pre-select for this Opp, given its
  // name + line items + the Product2.IsSerialized set we've cached.
  // The "name contains שדרוג/שידרוג" check trumps the serial check —
  // an upgrade can carry serialized items but the owner's rule is
  // explicit that name wins.
  const computeRecommendedWorkType = (oppName, lineItems, serializableProductIds, pbeToProduct) => {
    const name = oppName || '';
    if (name.includes('שדרוג') || name.includes('שידרוג')) {
      return 'שידרוג';
    }
    const hasSerial = (lineItems || []).some(li => {
      const p2id = li.Product2Id || pbeToProduct?.[li.PricebookEntryId];
      return p2id && serializableProductIds?.has?.(p2id);
    });
    return hasSerial ? 'התקנה (טכנאי מכירה)' : 'שירות';
  };

  const triggerWorkOrderFlow = async (newStage, opts = {}) => {
    console.log('[WorkOrder] Triggering flow for stage:', newStage);
    const marketer = !!opts.marketer;
    setCloseIsMarketer(marketer);

    // Store the pending stage change
    setPendingStageChange(newStage);

    // Reset state before loading
    setWorkTypeModalStep('loading'); // Show loading state immediately
    setSelectedWorkType(null);
    setSelectedProductItem(null);
    setAvailableProductItems([]);
    setAvailableSerializedProducts({});
    setSelectedSerializedProducts({});
    setCompletedSerialSelections([]);
    setWorkOrderError(null);
    setShowWorkTypeModal(true); // Show modal IMMEDIATELY with loading state

    // Best-effort instant recommendation based on the name alone — the
    // serial check has to wait for the background data load below, but
    // when the name has שדרוג/שידרוג we can already lock the answer in.
    const oppName = currentRecord?.Name || '';
    if (marketer) {
      // A marketer sale's natural close is the business delivery (owner 2026-10-08).
      setRecommendedWorkType(BUSINESS_DELIVERY_WORK_TYPE_NAME);
    } else if (oppName.includes('שדרוג') || oppName.includes('שידרוג')) {
      setRecommendedWorkType('שידרוג');
    } else {
      setRecommendedWorkType(null);
    }

    // 2026-05-20: 'התקנה חדשה' removed per owner — only שירות,
    // התקנה (טכנאי מכירה), שידרוג, and מעבר דירה are valid choices
    // from the Closed Won path-button work-order modal.
    const ALLOWED_WORK_TYPES = ['התקנה (טכנאי מכירה)', 'שידרוג', 'מעבר דירה', 'שירות'];

    try {
      // Load work types FIRST (fast, small dataset)
      const workTypesRes = await fetch(`${API_BASE}/api/objects/WorkType/records?limit=100`);
      let workTypes = [];
      if (workTypesRes.ok) {
        const data = await workTypesRes.json();
        const allWorkTypes = data.records || [];
        // "מסירה ללקוח משווק" is offered ONLY for a marketer sale (matched by Id — a service type
        // called "מסירה" also exists).
        workTypes = allWorkTypes.filter(wt => ALLOWED_WORK_TYPES.includes(wt.Name)
          || (marketer && isBusinessDeliveryWorkType(wt)));
        console.log('[WorkOrder] Loaded work types:', workTypes.length);
      }

      // Show work types immediately!
      setAvailableWorkTypes(workTypes);
      setWorkTypeModalStep('workType');

      // Load other data in BACKGROUND (don't block UI)
      loadWorkOrderDataInBackground();
    } catch (err) {
      console.error('[WorkOrder] Error loading work types:', err);
      setWorkTypeModalStep('workType');
      setWorkOrderError('שגיאה בטעינת סוגי העבודה');
    }
  };

  // Load heavy data in background - returns a promise that resolves when data is loaded
  const loadWorkOrderDataInBackground = async () => {
    // If already loading, return the existing promise
    if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
      return workOrderDataCache.current.loadPromise;
    }

    // If data was loaded recently (within last 30 seconds), return immediately
    if (workOrderDataCache.current.lastLoaded && Date.now() - workOrderDataCache.current.lastLoaded < 30000) {
      return Promise.resolve();
    }

    workOrderDataCache.current.isLoading = true;

    const loadPromise = (async () => {
      try {
        // 2026-05-16: the Serializedproduct fetch was the dominant cost
        // (~8s wall time — 42K rows × 35 columns ≈ 80 MB JSON; FE then
        // filtered down to ~14K unsaved-at-main-warehouse rows it actually
        // used). Replaced the generic /api/objects/Serializedproduct/records
        // with the dedicated /api/serialized-products/available endpoint
        // which does the Saved__c + warehouse filter server-side and
        // returns only 5 fields. Expected drop: ~8s → ~1s.
        // 2026-06-21 PR-A: fetch the main-warehouse Location.Id alongside
        // the other 4 fetches (single round trip). /serialized-products/available
        // now defaults to main warehouse server-side, so no warehouse_id
        // param needed on that call.
        const [productItemsRes, product2Res, serializedRes, pbeRes, mainWhRes] = await Promise.all([
          fetch(`${API_BASE}/api/objects/Productitem/records?limit=10000&fields=Id,Product2Id,LocationId,QuantityOnHand,ProductItemNumber`),
          fetch(`${API_BASE}/api/objects/Product2/records?limit=5000&fields=Id,Name`),
          fetch(`${API_BASE}/api/serialized-products/available`),
          fetch(`${API_BASE}/api/objects/PricebookEntry/records?limit=10000&fields=Id,Product2Id`),
          fetch(`${API_BASE}/api/config/main-warehouse-id`)
        ]);
        workOrderDataCache.current.mainWarehouseId = mainWhRes.ok
          ? ((await mainWhRes.json()).id || '')
          : '';

        workOrderDataCache.current.productItems = productItemsRes.ok ? (await productItemsRes.json()).records || [] : [];
        const _product2Records = product2Res.ok ? (await product2Res.json()).records || [] : [];
        workOrderDataCache.current.productNames = _product2Records.reduce((acc, p) => { acc[p.Id] = p.Name; return acc; }, {});
        // 2026-05-16: track which Product2 IDs are serializable (independent
        // of current stock). The שירות-flow check at line ~12370 previously
        // used the existence of any Serializedproduct record as a proxy for
        // "is serializable" — that worked when /api/objects/Serializedproduct
        // returned ALL rows. After we switched to the warehouse-filtered
        // /api/serialized-products/available endpoint, the proxy would give a
        // false negative when the warehouse is out of stock. Using
        // Product2.IsSerialized is the actual source of truth.
        workOrderDataCache.current.serializableProductIds = new Set(
          _product2Records
            .filter(p => p.IsSerialized === true || p.IsSerialized === 'true' || p.IsSerialized === '1' || p.IsSerialized === 1)
            .map(p => p.Id)
        );
        workOrderDataCache.current.serializedProducts = serializedRes.ok ? (await serializedRes.json()).records || [] : [];
        // Map PricebookEntryId -> Product2Id (OpportunityLineItem has PricebookEntryId, not Product2Id)
        workOrderDataCache.current.pbeToProduct = pbeRes.ok
          ? (await pbeRes.json()).records?.reduce((acc, p) => { acc[p.Id] = p.Product2Id; return acc; }, {}) || {}
          : {};
        workOrderDataCache.current.lastLoaded = Date.now();

        console.log('[WorkOrder] Background data loaded:', {
          productItems: workOrderDataCache.current.productItems.length,
          products: Object.keys(workOrderDataCache.current.productNames).length,
          serialized: workOrderDataCache.current.serializedProducts.length,
          pricebookEntries: Object.keys(workOrderDataCache.current.pbeToProduct).length
        });

        // 2026-05-20: now that we know which Product2 IDs are serial-
        // izable AND have the PBE→Product2 map, finalize the Closed
        // Won recommendation if it wasn't already locked by the name
        // check. setState skips the work when the value is unchanged
        // (e.g. name was 'שדרוג' so we already set 'שידרוג').
        setRecommendedWorkType(prev => {
          if (prev) return prev;
          const oppName = currentRecord?.Name || '';
          const lineItems = relatedRecords['OpportunityLineItem'] || [];
          return computeRecommendedWorkType(
            oppName,
            lineItems,
            workOrderDataCache.current.serializableProductIds,
            workOrderDataCache.current.pbeToProduct,
          );
        });
      } catch (err) {
        console.error('[WorkOrder] Error loading background data:', err);
      } finally {
        workOrderDataCache.current.isLoading = false;
        workOrderDataCache.current.loadPromise = null;
      }
    })();

    workOrderDataCache.current.loadPromise = loadPromise;
    return loadPromise;
  };

  // 2026-05-20: submit handler for the multi-address splitter
  // (MultiAddressCloseStep). Hits the transactional backend endpoint
  // POST /api/opportunity/{id}/close-multi-address (added in PR #410)
  // which creates Contacts + WorkOrders + ServiceAppointments + Product
  // Required + flips Saved__c + advances StageName/Type — all in one
  // PG transaction. We don't try to merge with the legacy single-serial
  // post-create logic; this path is self-contained.
  const handleMultiAddressSubmit = async ({ mode, addresses, cubes }) => {
    if (!selectedWorkType) {
      setWorkOrderError('סוג עבודה לא נבחר — נא לחזור לשלב הקודם');
      return;
    }
    setMultiAddressSubmitting(true);
    setWorkOrderError(null);
    try {
      const actor = recordService.getCurrentUser?.();
      const actorId = actor?.id || actor?.Id || actor?.email || '';
      const stage = pendingStageChange || 'Closed Won';
      const body = {
        work_type_id: selectedWorkType.Id,
        work_type_name: selectedWorkType.Name,
        stage_name: stage,
        mode,
        addresses: addresses.map(a => ({
          street: a.street || '',
          city: a.city || '',
          postal_code: a.postalCode || '',
          latitude: String(a.latitude || ''),
          longitude: String(a.longitude || ''),
          contact_name: a.contact_name || '',
          contact_phone: a.contact_phone || '',
        })),
        cubes: cubes.map(c => ({
          address_idx: c.addressIdx ?? 0,
          // 2026-05-20: lineItemQuantities is { liId: count } so we can
          // split a Quantity>1 line item across multiple cubes. Backend
          // expects [{ id, quantity }, ...].
          line_items: Object.entries(c.lineItemQuantities || {})
            .filter(([, q]) => q > 0)
            .map(([id, quantity]) => ({ id, quantity })),
          serial_id: c.serialId,
        })),
        actor_user_id: actorId,
      };
      const resp = await fetch(`${API_BASE}/api/opportunity/${currentRecord.Id}/close-multi-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = typeof data.detail === 'string'
          ? data.detail
          : (data.detail?.message || data.detail?.errors?.[0]?.message || 'שגיאה ביצירת הפעילויות');
        throw new Error(msg);
      }
      // Success — close the work-type modal, update local opp state,
      // then pop the success-result modal so the manager can click
      // through to any of the created WO / SA records.
      setShowWorkTypeModal(false);
      setCurrentRecord(prev => ({ ...prev, StageName: stage, Type: selectedWorkType.Name }));
      if (onRecordUpdate) {
        onRecordUpdate({ ...currentRecord, StageName: stage, Type: selectedWorkType.Name });
      }
      setMultiAddressResult(data);
    } catch (err) {
      console.error('[MultiAddressClose] submit failed:', err);
      setWorkOrderError(err.message || 'שגיאה ביצירת הפעילויות');
    } finally {
      setMultiAddressSubmitting(false);
    }
  };

  // 2026-10-09 (dealer P2-B): POST the marketer close. One transaction server-side; the documents
  // are issued in the background and shown live in BusinessDeliveryResult.
  const handleBusinessDeliverySubmit = async ({ mode, addresses, allocation }) => {
    setBdSubmitting(true);
    setWorkOrderError(null);
    try {
      const stage = pendingStageChange || 'סגירה בהצלחה';
      const lines = deliverableLines(relatedRecords['OpportunityLineItem'] || [],
        workOrderDataCache.current.productNames || {}, workOrderDataCache.current.pbeToProduct || {});
      const body = buildCloseBody({ stage, issueInvoice: bdIssueInvoice, invoiceSkipReason: bdInvoiceSkipReason,
                                    issueDeliveryNotes: bdIssueNotes, mode, addresses, allocation, lines });
      const resp = await fetch(`${API_BASE}/api/dealer/opportunity/${currentRecord.Id}/close-business-delivery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const d = data.detail;
        throw new Error(typeof d === 'string' ? d : (d?.[0]?.msg || 'שגיאה בסגירת ההזדמנות'));
      }
      setShowWorkTypeModal(false);
      setCurrentRecord(prev => ({ ...prev, StageName: stage, Type: BUSINESS_DELIVERY_WORK_TYPE_NAME }));
      if (onRecordUpdate) {
        onRecordUpdate({ ...currentRecord, StageName: stage, Type: BUSINESS_DELIVERY_WORK_TYPE_NAME });
      }
      setBdResult(data);
    } catch (err) {
      console.error('[BusinessDelivery] close failed:', err);
      setWorkOrderError(err.message || 'שגיאה בסגירת ההזדמנות');
    } finally {
      setBdSubmitting(false);
    }
  };

  // Handle work type selection - uses cached data for instant response
  // ALL work types go through product item + serialized product selection
  const handleWorkTypeSelect = async (workType) => {
    // 2026-05-20: if the user picks something other than the auto-
    // recommended type, make them confirm — the rules cover almost
    // every legitimate case so an override usually means a mistake.
    if (recommendedWorkType && workType.Name !== recommendedWorkType) {
      const ok = window.confirm(
        `סוג העבודה המומלץ לפי החוקים הוא "${recommendedWorkType}".\n` +
        `בחרת "${workType.Name}". האם להמשיך עם הבחירה הזו?`
      );
      if (!ok) return;
    }
    setSelectedWorkType(workType);
    console.log('[WorkOrder] Selected work type:', workType.Name);
    setWorkOrderError(null);

    // 2026-10-09 (dealer P2-B): "מסירה ללקוח משווק" → invoice? → delivery note? → addresses.
    if (isBusinessDeliveryWorkType(workType) && closeIsMarketer) {
      setBdIssueInvoice(null);
      setBdInvoiceSkipReason('');
      setBdIssueNotes(null);
      setWorkTypeModalStep('loading');
      if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
        await workOrderDataCache.current.loadPromise;
      } else if (!workOrderDataCache.current.productNames) {
        await loadWorkOrderDataInBackground();
      }
      const accountId = currentRecord?.AccountId;
      try {
        const accRes = accountId ? await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`) : null;
        const accData = accRes && accRes.ok ? await accRes.json() : null;
        setCloseFlowAccount(accData ? (accData.record || accData) : null);
      } catch (e) {
        setCloseFlowAccount(null);
      }
      setWorkTypeModalStep('bdInvoice');
      return;
    }

    // שירות → special flow: ask if technician needed
    if (workType.Name === 'שירות') {
      setWorkTypeModalStep('technicianRequired');
      return;
    }

    // Show loading while we ensure data is ready
    setWorkTypeModalStep('loading');

    // Wait for background data to finish loading if still in progress
    if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
      console.log('[WorkOrder] Waiting for background data to finish loading...');
      await workOrderDataCache.current.loadPromise;
    }

    // If data is not loaded yet, load it now
    if (!workOrderDataCache.current.productItems) {
      console.log('[WorkOrder] Data not cached, loading now...');
      await loadWorkOrderDataInBackground();
    }

    // Get opportunity line items (products) — used both by the legacy
    // single-serial path and (when count>=2) the new multi-address
    // splitter path below.
    const lineItems = relatedRecords['OpportunityLineItem'] || [];
    console.log('[WorkOrder] Opportunity line items:', lineItems);

    if (lineItems.length === 0) {
      setWorkTypeModalStep('productItem');
      setWorkOrderError('אין פריטי מוצר בהזדמנות');
      return;
    }

    // 2026-05-20: route the institutional multi-device case to the new
    // splitter UI (MultiAddressCloseStep). The check counts serialized
    // devices expanded by Quantity — one OpportunityLineItem with
    // Product2.IsSerialized=1 and Quantity=20 means 20 devices, even
    // though it's a single row.
    const _pbeToProduct = workOrderDataCache.current.pbeToProduct || {};
    const _serializableSet = workOrderDataCache.current.serializableProductIds || new Set();
    const totalSerialDeviceCount = lineItems.reduce((sum, li) => {
      const pid = li.Product2Id || _pbeToProduct[li.PricebookEntryId];
      if (!pid || !_serializableSet.has(pid)) return sum;
      const q = parseInt(li.Quantity, 10);
      return sum + (isNaN(q) || q < 1 ? 1 : q);
    }, 0);
    console.log('[WorkOrder] Serial device count:', totalSerialDeviceCount);

    if (totalSerialDeviceCount >= 2) {
      // Hand off to MultiAddressCloseStep. Modal stays open; the
      // component does Q1/Q1a/Q2 + cubes + POSTs to the backend
      // /api/opportunity/{id}/close-multi-address endpoint via
      // handleMultiAddressSubmit() below.
      // First fetch the parent Account so the splitter's Q1a can
      // display the existing BillingAddress.
      const accountId = currentRecord?.AccountId;
      if (accountId) {
        try {
          const accRes = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`);
          if (accRes.ok) {
            const accData = await accRes.json();
            setCloseFlowAccount(accData.record || accData);
          } else {
            setCloseFlowAccount(null);
          }
        } catch (e) {
          console.warn('[MultiAddressClose] account fetch failed:', e);
          setCloseFlowAccount(null);
        }
      } else {
        setCloseFlowAccount(null);
      }
      setWorkTypeModalStep('multiAddressClose');
      return;
    }

    setWorkTypeModalStep('productItem');

    // Use cached data - now guaranteed to be loaded!
    const allProductItems = workOrderDataCache.current.productItems || [];
    const productNames = workOrderDataCache.current.productNames || {};
    const pbeToProduct = workOrderDataCache.current.pbeToProduct || {};

    // 2026-06-21 PR-A: filter by main warehouse using the ID fetched from
    // /api/config/main-warehouse-id (cached on workOrderDataCache.current).
    // Empty string sentinel means the config fetch failed — in that case
    // the filter rejects everything (fail-closed) so we never silently
    // show items from non-main locations.
    const mainWarehouseId = workOrderDataCache.current.mainWarehouseId || '';
    const warehouseItems = mainWarehouseId
      ? allProductItems.filter(pi => pi.LocationId === mainWarehouseId)
      : [];
    console.log('[WorkOrder] Warehouse items:', warehouseItems.length);

    // Get Product2Ids from line items (resolve through PricebookEntry since OpportunityLineItem has PricebookEntryId, not Product2Id)
    const lineItemProductIds = new Set(lineItems.map(li => {
      // Direct Product2Id (if available), or resolve via PricebookEntryId
      return li.Product2Id || pbeToProduct[li.PricebookEntryId];
    }).filter(Boolean));
    console.log('[WorkOrder] Line item Product2Ids:', [...lineItemProductIds]);

    // Get cached serialized products to count available
    const allSerialized = workOrderDataCache.current.serializedProducts || [];

    // Filter and enrich in one pass - include available serialized count
    const enrichedItems = warehouseItems
      .filter(pi => lineItemProductIds.has(pi.Product2Id))
      .map(pi => {
        // Count available serialized products for this ProductItem
        const availableCount = allSerialized.filter(sp =>
          sp.ProductItemId === pi.Id &&
          (sp.Saved__c === '0' || sp.Saved__c === 0 || sp.Saved__c === false || sp.Saved__c === 'false' || !sp.Saved__c)
        ).length;
        return { ...pi, Product2Name: productNames[pi.Product2Id] || pi.Product2Id, availableSerializedCount: availableCount };
      });

    console.log('[WorkOrder] Matching product items:', enrichedItems.length);
    setAvailableProductItems(enrichedItems);

    if (enrichedItems.length === 0) {
      setWorkOrderError('אין פריטי מוצר במחסן הראשי התואמים את פריטי ההזדמנות');
    }
  };

  // Handle product item selection - uses cached data for instant response
  const handleProductItemSelect = async (productItem) => {
    setSelectedProductItem(productItem);
    setWorkOrderError(null);
    console.log('[WorkOrder] Selected product item:', productItem);

    // Show loading while we ensure data is ready
    setWorkTypeModalStep('loading');

    // Wait for background data to finish loading if still in progress
    if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
      console.log('[WorkOrder] Waiting for serialized data to finish loading...');
      await workOrderDataCache.current.loadPromise;
    }

    // If data is not loaded yet, load it now
    if (!workOrderDataCache.current.serializedProducts) {
      console.log('[WorkOrder] Serialized data not cached, loading now...');
      await loadWorkOrderDataInBackground();
    }

    setWorkTypeModalStep('serialized');

    // Use cached serialized products - now guaranteed to be loaded!
    const allSerialized = workOrderDataCache.current.serializedProducts || [];

    // Filter by ProductItemId and Saved__c = false/0
    const available = allSerialized.filter(sp =>
      sp.ProductItemId === productItem.Id &&
      (sp.Saved__c === '0' || sp.Saved__c === 0 || sp.Saved__c === false || sp.Saved__c === 'false' || !sp.Saved__c)
    );

    console.log('[WorkOrder] Available serialized products:', available);

    if (available.length === 0) {
      setWorkOrderError(`אין את המוצר: ${productItem.Product2Name || productItem.ProductItemNumber} במלאי כרגע נא לבחור מוצר אחר`);
      setWorkTypeModalStep('productItem');
      return;
    }

    // Group by Product2Id for display
    const grouped = {};
    for (const sp of available) {
      const productId = sp.Product2Id || 'unknown';
      if (!grouped[productId]) {
        grouped[productId] = [];
      }
      grouped[productId].push(sp);
    }

    setAvailableSerializedProducts(grouped);
  };

  // Handle serialized product selection
  const handleSerializedProductSelect = (productId, serializedProduct) => {
    setSelectedSerializedProducts(prev => ({
      ...prev,
      [productId]: serializedProduct
    }));
  };

  // Create work order, service appointment, and product required records
  const createWorkOrderAndServiceAppointment = async (workType, productItem, selectedSerials) => {
    setWorkOrderCreating(true);
    setWorkTypeModalStep('creating');
    // Reset progress state on each entry — owner-spec 2026-06-09.
    setWorkOrderProgress({
      workOrder: 'running', products: 'pending', sa: 'pending',
    });

    try {
      // Get account details from opportunity
      const accountId = currentRecord.AccountId;
      let account = null;

      if (accountId) {
        const accountRes = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`);
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          // API returns { record: {...} } for single record - extract it
          account = accountData.record || accountData;
          console.log('[WorkOrder] Loaded account:', account?.Name, 'Floor:', account?.Floor__c, 'Apt:', account?.Apartment_number__c);
        }
      }

      // selectedSerials is now an array of { productItem, serial, productId } (accumulated across
      // multiple productItem selections). For backwards compatibility, also accept the old
      // object format { [productId]: serial } and convert it on the fly.
      let serialEntries = [];
      if (Array.isArray(selectedSerials)) {
        serialEntries = selectedSerials.filter(e => e && e.serial);
      } else if (selectedSerials && typeof selectedSerials === 'object') {
        serialEntries = Object.entries(selectedSerials).map(([productId, serial]) => ({
          productItem,
          serial,
          productId,
        }));
      }

      const allSelectedSerials = serialEntries.map(e => e.serial).filter(Boolean);
      // Primary serialized product (used for the WorkOrder.Serialized_Product__c field,
      // which is single-valued in the legacy CRM schema).
      const serializedProduct = allSelectedSerials[0] || null;
      console.log('[WorkOrder] Total serials selected:', allSelectedSerials.length, allSelectedSerials.map(s => s?.SerialNumber || s?.Id));

      // Get line items for ProductRequired - fetch directly from API (relatedRecords might not be loaded yet)
      let lineItems = relatedRecords['OpportunityLineItem'] || [];
      if (lineItems.length === 0 && currentRecord.Id) {
        try {
          const liRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}/related?resolve_refs=true`);
          if (liRes.ok) {
            const liData = await liRes.json();
            lineItems = liData.related_objects?.OpportunityLineItem?.records || [];
            console.log('[WorkOrder] Fetched line items from API:', lineItems.length);
          }
        } catch (e) { console.error('[WorkOrder] Error fetching line items:', e); }
      }

      // AutoNumber generation moved to the backend (PRs #178/#179) — it
      // now uses an advisory-locked reserve_next_autonumber that's
      // multi-worker safe. The frontend no longer needs to pre-fetch
      // the max and pass an explicit number; omitting the field lets
      // the backend assign one inside the same INSERT transaction.
      // This removes 2 round trips per Work Order create (WO MAX query
      // + SA MAX query) — about 400ms cold each on prod.
      //
      // 2026-06-09 — SA-211357 duplicate-number fix: the backend is now
      // strictly server-authoritative for AutoNumbers. FE picks up the
      // assigned value from the POST response below.
      let workOrderNumber = '';
      let appointmentNumber = '';

      // Build full address string
      const addressParts = [];
      if (account?.BillingStreet) addressParts.push(account.BillingStreet);
      if (account?.BillingCity) addressParts.push(account.BillingCity);
      const fullAddress = addressParts.join(', ');

      // 0. Reserve the chosen devices BEFORE anything is created. The server
      // reserves only a device that is still free (or already held by this
      // opportunity), so a device another sale took in the meantime stops the
      // close here — no work order is left without its device.
      const reserveSerial = async (sp, workOrderId) => {
        let res;
        try {
          res = await fetch(`${API_BASE}/api/inventory/serials/${encodeURIComponent(sp.Id)}/reserve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ opportunity_id: currentRecord.Id, work_order_id: workOrderId || '' })
          });
        } catch (e) {
          console.error('[WorkOrder] Device reservation call failed:', sp.Id, e);
          return;
        }
        // Only a real conflict (409 — another sale holds the device) stops the
        // close, and the server answers 409 only while the reservation guard
        // flag is ON. Any other failure is logged, as the old PUT's was, so
        // nothing here can block a sale before the inventory system goes live.
        if (res.status === 409) {
          const err = await res.json().catch(() => ({}));
          throw new Error(typeof err.detail === 'string' ? err.detail
            : `המכשיר ${sp.SerialNumber || sp.Id} שמור למכירה אחרת — יש לבחור מכשיר אחר`);
        }
        if (!res.ok) {
          console.error('[WorkOrder] Device reservation call failed:', sp.Id, res.status);
        }
      };
      for (const sp of allSelectedSerials.filter(s => s?.Id)) {
        try {
          await reserveSerial(sp, '');
        } catch (e) {
          setWorkOrderProgress(p => ({ ...p, workOrder: 'error' }));
          throw e;
        }
      }

      // 1. Create Work Order (WorkOrderNumber assigned by backend HiLo)
      const workOrderData = {
        AccountId: accountId,
        WorkTypeId: workType.Id,
        Opportunity__c: currentRecord.Id,
        Serialized_Product__c: serializedProduct?.Id || '',
        Status: 'New',
        Subject: `${workType.Name} - ${currentRecord.Name || ''}`,
        Priority: 'Medium',
        Product__c: serializedProduct?.Product2Id || '',
        Duration: workType.EstimatedDuration || 60,
        DurationType: workType.DurationType || 'Minutes',
        DurationInMinutes: workType.DurationInMinutes || 60,
        // Address from account
        Street: account?.BillingStreet || '',
        City: account?.BillingCity || '',
        Latitude: account?.BillingLatitude || '',
        Longitude: account?.BillingLongitude || '',
        Address: fullAddress
      };

      console.log('[WorkOrder] Creating work order:', workOrderData);

      const woRes = await fetch(`${API_BASE}/api/objects/WorkOrder/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(workOrderData)
      });

      if (!woRes.ok) {
        setWorkOrderProgress(p => ({ ...p, workOrder: 'error' }));
        const errData = await woRes.json().catch(() => ({}));
        const errMsg = typeof errData.detail === 'string' ? errData.detail
          : errData.detail?.message || errData.detail?.errors?.[0]?.message || 'שגיאה ביצירת הזמנת עבודה';
        throw new Error(errMsg);
      }

      const workOrderResponse = await woRes.json();
      const workOrder = workOrderResponse.record || workOrderResponse;
      workOrderNumber = workOrder.WorkOrderNumber || '';
      console.log('[WorkOrder] Created work order:', workOrder, 'WorkOrderNumber=', workOrderNumber);
      // Owner-spec 2026-06-09: WO created → move to next phases.
      setWorkOrderProgress(p => ({
        ...p, workOrder: 'done', products: 'running', sa: 'running',
      }));

      // 2+3+4 — ProductRequired creates, ServiceAppointment create, and
      // SerializedProduct save-flags can ALL run concurrently. Each one
      // only depends on data that exists before this block:
      //   - ProductRequired: workOrder.Id + lineItem fields
      //   - ServiceAppointment: workOrder.Id + workType + account + appointmentNumber
      //   - SerializedProduct PUTs: sp.Id (independent of anything else)
      // Previously these ran sequentially (PR awaited → then SA awaited
      // → then serials awaited). Owner observed the modal lingering on
      // each step. Wall time collapses from sum(PR, SA, serials) to
      // max(PR, SA, serials). Per the post-B2 measurements that's
      // roughly 273ms → ~273ms (capped by the slowest, which is SA).

      const duration = parseInt(workType.EstimatedDuration) || 60;
      // 2026-06-09 — Subject no longer suffixes the AppointmentNumber.
      // The number lives on the AppointmentNumber field (assigned by
      // backend HiLo via the POST below). Previous "${name}: ${apptNumber}"
      // suffix was a duplicate of data already on the row + caused empty
      // ": " when the FE-supplied apptNumber was '' (pre-fix state).
      const saSubject = `${workType.Name} - ${currentRecord.Name || ''}`;
      const saData = {
        ParentRecordId: workOrder.Id,
        AccountId: accountId,
        WorkTypeId: workType.Id,
        Status: 'None',
        Subject: saSubject,
        EarliestStartTime: new Date().toISOString(),
        Duration: duration,
        DurationType: workType.DurationType || 'Minutes',
        DurationInMinutes: parseInt(workType.DurationInMinutes) || duration,
        // Address from account - all fields
        Street: account?.BillingStreet || '',
        City: account?.BillingCity || '',
        State: account?.BillingState || '',
        PostalCode: account?.BillingPostalCode || '',
        Country: account?.BillingCountry || '',
        Latitude: account?.BillingLatitude || '',
        Longitude: account?.BillingLongitude || '',
        Address: fullAddress,
        // Additional address fields
        Floor__c: account?.Floor__c || '',
        Entrance__c: account?.Entrance__c || '',
        Apartment_number__c: account?.Apartment_number__c || '',
        product__c: serializedProduct?.Product2Id || ''
      };

      const sealedSerials = allSelectedSerials.filter(sp => sp?.Id);

      // Owner-spec 2026-06-10: distributor pricebook OLIs flagged with
      // Device_At_Customer__c='1' should NOT spawn a ProductRequired —
      // the device is already at the customer, the warehouse must not
      // pick another one. Filter the list before the parallel POST.
      const lineItemsToFulfill = lineItems.filter(
        li => String(li.Device_At_Customer__c || '') !== '1'
      );
      const skippedDeviceAtCustomer = lineItems.length - lineItemsToFulfill.length;
      if (skippedDeviceAtCustomer > 0) {
        console.log(`[WorkOrder] Skipping ${skippedDeviceAtCustomer} ProductRequired creates (Device_At_Customer__c='1')`);
      }
      console.log(`[WorkOrder] Concurrent: ${lineItemsToFulfill.length} ProductRequired + 1 SA + ${sealedSerials.length} SerializedProduct PUTs`);

      const prPromise = Promise.all(lineItemsToFulfill.map(lineItem => {
        const prData = {
          ParentRecordId: workOrder.Id,
          Product2Id: lineItem.Product2Id || '',
          QuantityRequired: lineItem.Quantity || 1,
          Reason__c: 'חדש'
        };
        return fetch(`${API_BASE}/api/objects/ProductRequired/records`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(prData)
        }).catch(e => console.error('[WorkOrder] ProductRequired create failed:', e));
      }));

      const saPromise = fetch(`${API_BASE}/api/objects/ServiceAppointment/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(saData)
      });

      // The devices were reserved in step 0; this links them to the new work
      // order (same opportunity, so the server accepts it as the same holder).
      const serialsPromise = sealedSerials.length > 0
        ? Promise.all(sealedSerials.map(sp =>
            reserveSerial(sp, workOrder.Id)
              .then(() => console.log('[WorkOrder] Linked reserved device to work order:', sp.SerialNumber || sp.Id))
              .catch(e => console.error('[WorkOrder] Failed to link device to work order:', sp.Id, e))
          ))
        : Promise.resolve();

      // Owner-spec 2026-06-09: surface per-phase completion to the UI
      // so the checklist updates as each promise lands instead of all
      // three flipping to ✓ at once.
      prPromise
        .then(() => setWorkOrderProgress(p => ({ ...p, products: 'done' })))
        .catch(() => setWorkOrderProgress(p => ({ ...p, products: 'error' })));
      saPromise
        .then(r => setWorkOrderProgress(p => ({ ...p, sa: r && r.ok ? 'done' : 'error' })))
        .catch(() => setWorkOrderProgress(p => ({ ...p, sa: 'error' })));
      // Wait for all three groups to complete; only SA's response is
      // needed downstream (for AppointmentNumber on the success modal).
      const [, saRes] = await Promise.all([prPromise, saPromise, serialsPromise]);

      let serviceAppointment = null;
      if (!saRes.ok) {
        console.error('[WorkOrder] Error creating service appointment');
      } else {
        const saResponse = await saRes.json();
        console.log('[WorkOrder] Raw SA response:', JSON.stringify(saResponse));
        serviceAppointment = saResponse.record || saResponse;
        appointmentNumber = serviceAppointment?.AppointmentNumber || '';
        console.log('[WorkOrder] Extracted serviceAppointment:', serviceAppointment, 'AppointmentNumber=', appointmentNumber);
        console.log('[WorkOrder] serviceAppointment.Id:', serviceAppointment?.Id);
      }

      // 5. Update opportunity - stage AND Type field
      const oppUpdateData = {};
      if (pendingStageChange) {
        oppUpdateData.StageName = pendingStageChange;
      }
      // Update Type field with the selected work type name
      oppUpdateData.Type = workType.Name;

      const currentUser = recordService.getCurrentUser();
      if (currentUser) {
        const userId = currentUser.id || currentUser.Id || currentUser.email;
        const userName = currentUser.name || currentUser.Name || currentUser.email;
        oppUpdateData.LastModifiedById = userId;
        oppUpdateData.LastModifiedByName = userName;
      }

      // The backend re-runs the Closed-Won validation here (signed-doc +
      // payment + balance). The first frontend gate above already passed,
      // but the multi-worker / csv-cache path can still see a stale
      // Signed_Document_Link_c__c if the sign was processed by a
      // different worker. Pass the field through explicitly so the
      // server-side merge has the value we already verified.
      if (currentRecord.Signed_Document_Link_c__c) {
        oppUpdateData.Signed_Document_Link_c__c = currentRecord.Signed_Document_Link_c__c;
      }

      const oppUpdateRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(oppUpdateData)
      });
      if (!oppUpdateRes.ok) {
        const errData = await oppUpdateRes.json().catch(() => ({}));
        let errMsg = 'שגיאה בעדכון ההזדמנות';
        if (errData.detail?.errors) {
          errMsg = errData.detail.errors.map(e => e.error_message || e.message).join('\n');
        } else if (errData.detail?.message) {
          errMsg = errData.detail.message;
        } else if (typeof errData.detail === 'string') {
          errMsg = errData.detail;
        }
        throw new Error(errMsg);
      }

      setCurrentRecord(prev => ({ ...prev, StageName: pendingStageChange || prev.StageName, Type: workType.Name }));
      if (onRecordUpdate) {
        onRecordUpdate({ ...currentRecord, StageName: pendingStageChange || currentRecord.StageName, Type: workType.Name });
      }

      // Show success screen with results
      const resultData = {
        workOrderId: workOrder.Id,
        workOrderNumber: workOrder.WorkOrderNumber || workOrderNumber,
        serviceAppointmentId: serviceAppointment?.Id,
        serviceAppointmentNumber: serviceAppointment?.AppointmentNumber || appointmentNumber,
        productsRequired: lineItems.map(li => {
          // Resolve product name: direct field, or via PricebookEntry -> Product2 name lookup
          const pbeProductId = li.Product2Id || (workOrderDataCache.current.pbeToProduct || {})[li.PricebookEntryId];
          const resolvedName = pbeProductId ? (workOrderDataCache.current.productNames || {})[pbeProductId] : null;
          return {
            name: li.Product2__Name || resolvedName || li.Description || li.Name || 'מוצר',
            quantity: li.Quantity || 1
          };
        })
      };
      console.log('[WorkOrder] Setting workOrderResult:', resultData);
      setWorkOrderResult(resultData);
      setWorkTypeModalStep('success');

    } catch (err) {
      console.error('[WorkOrder] Error creating work order:', err);
      setWorkOrderError(err.message || 'שגיאה ביצירת הזמנת עבודה');
      setWorkTypeModalStep('workType');
    } finally {
      setWorkOrderCreating(false);
    }
  };

  // Handle confirmation of the serial(s) picked for the currently-selected ProductItem.
  // Accumulates into completedSerialSelections, then returns to the productItem step so
  // the user can pick a serial for the NEXT serialized product (if any remain).
  // The actual Work Order is only created when the user clicks "Create Work Order"
  // in the productItem step (see handleCreateWorkOrderFromAccumulated).
  const handleConfirmSerializedProducts = async () => {
    if (Object.keys(selectedSerializedProducts).length === 0) {
      setWorkOrderError('יש לבחור לפחות מוצר סיריאלי אחד');
      return;
    }

    // Build new accumulated list from current picks (one serial per productId under this productItem)
    const newEntries = Object.entries(selectedSerializedProducts).map(([productId, serial]) => ({
      productItem: selectedProductItem,
      serial,
      productId,
    }));

    const accumulated = [...completedSerialSelections, ...newEntries];
    setCompletedSerialSelections(accumulated);

    // Clear current pick state
    setSelectedProductItem(null);
    setAvailableSerializedProducts({});
    setSelectedSerializedProducts({});
    setWorkOrderError(null);

    // Figure out how many product items still need a serial picked
    const completedProductItemIds = new Set(accumulated.map(s => s.productItem?.Id).filter(Boolean));
    const remaining = (availableProductItems || []).filter(pi => !completedProductItemIds.has(pi.Id));

    if (remaining.length > 0) {
      // More serialized products to pick — go back to productItem step
      console.log('[WorkOrder] More productItems remain:', remaining.length, '— returning to productItem step');
      setWorkTypeModalStep('productItem');
    } else {
      // All items selected — create the work order now
      console.log('[WorkOrder] All productItems selected — creating Work Order with', accumulated.length, 'serials');
      await createWorkOrderAndServiceAppointment(selectedWorkType, null, accumulated);
    }
  };

  // Called from the "Create Work Order" button in the productItem step
  // when the user is done picking serials and wants to finalize.
  const handleCreateWorkOrderFromAccumulated = async () => {
    if (completedSerialSelections.length === 0) {
      setWorkOrderError('יש לבחור לפחות מוצר סיריאלי אחד');
      return;
    }
    await createWorkOrderAndServiceAppointment(selectedWorkType, null, completedSerialSelections);
  };

  // Handle 'שירות' technician required question
  const handleTechnicianRequired = async (needsTechnician) => {
    if (!needsTechnician) {
      // No-tech close. Before closing, run the no-tech-warranty precheck:
      //   - eligible + verified payment + no existing SC → ask the user
      //   - eligible + already has active SC            → close normally
      //   - eligible + NO verified payment              → BLOCK close, show msg
      //   - not eligible                                → close normally
      try {
        setWorkTypeModalStep('loading');
        const precheckRes = await fetch(
          `${API_BASE}/api/automation/no-tech-warranty-precheck/${currentRecord.Id}`
        );
        const precheck = precheckRes.ok ? await precheckRes.json() : { eligible: false };

        // 2026-05-29: store precheck regardless of eligibility — the Task 3
        // "issue tax invoice?" dialog uses has_line_items / has_serialized_product
        // / has_verified_payment even when the warranty-SC flow doesn't run.
        setNoTechWarrantyPrecheck(precheck);
        if (precheck.eligible && !precheck.has_verified_payment) {
          setWorkTypeModalStep('noTechWarrantyNoPayment');
          return;
        }
        if (precheck.eligible && precheck.has_verified_payment && !precheck.already_has_contract) {
          setNoTechWarrantySelectedAssetId('');
          // Load this Account's existing Assets — user picks which one the
          // new SC attaches to (no Asset is created in the no-tech flow).
          setNoTechWarrantyAssetsLoading(true);
          try {
            let acctId = currentRecord.AccountId;
            if (!acctId && currentRecord.Id) {
              try {
                const oppRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`);
                if (oppRes.ok) {
                  const oppData = await oppRes.json();
                  acctId = (oppData.record || oppData).AccountId;
                }
              } catch (e) {}
            }
            if (acctId) {
              const assetsRes = await fetch(`${API_BASE}/api/objects/Account/records/${acctId}/related?limit=200`);
              if (assetsRes.ok) {
                const relData = await assetsRes.json();
                const allRelated = relData.related_objects || relData;
                const assetRecords = allRelated.Asset?.records || allRelated.Asset || [];
                setNoTechWarrantyAssets(
                  assetRecords.filter(a => a.IsDeleted !== 'true' && a.IsDeleted !== '1' && a.IsDeleted !== true)
                );
              } else {
                setNoTechWarrantyAssets([]);
              }
            } else {
              setNoTechWarrantyAssets([]);
            }
          } catch (assetErr) {
            console.warn('[NoTechWarranty] asset load failed:', assetErr);
            setNoTechWarrantyAssets([]);
          }
          setNoTechWarrantyAssetsLoading(false);
          setWorkTypeModalStep('noTechWarrantyConfirm');
          return;
        }
        // Either not eligible OR eligible but already has active SC →
        // skip the warranty-SC step; Task 3 (2026-05-29) may still ask
        // the user whether to issue a tax invoice before closing.
        await proceedAfterNoTechDecisionOrClose();
        return;
      } catch (precheckErr) {
        console.warn('[NoTechWarranty] precheck failed (non-fatal):', precheckErr);
      }
      await closeWonWithoutWorkOrder(selectedWorkType);
      return;
    }

    // Yes — technician needed. Check for serial products in OLI
    setWorkTypeModalStep('loading');

    try {
      // Ensure background data is loaded
      if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
        await workOrderDataCache.current.loadPromise;
      }
      if (!workOrderDataCache.current.serializedProducts) {
        await loadWorkOrderDataInBackground();
        if (workOrderDataCache.current.isLoading && workOrderDataCache.current.loadPromise) {
          await workOrderDataCache.current.loadPromise;
        }
      }

      // Get Product2Ids from OpportunityLineItems
      const lineItems = relatedRecords['OpportunityLineItem'] || [];
      const pbeToProduct = workOrderDataCache.current.pbeToProduct || {};
      const oliProduct2Ids = new Set(
        lineItems.map(li => li.Product2Id || pbeToProduct[li.PricebookEntryId]).filter(Boolean)
      );

      // Check if any OLI product is serializable. Uses Product2.IsSerialized
      // (the actual flag) rather than the existence of Serializedproduct rows,
      // so this still works correctly when the warehouse is out of stock.
      const serializableIds = workOrderDataCache.current.serializableProductIds || new Set();
      const hasSerialProduct = [...oliProduct2Ids].some(pid => serializableIds.has(pid));

      if (hasSerialProduct) {
        console.log('[WorkOrder] שירות: serial product found in OLI, showing error');
        setWorkTypeModalStep('serialProductError');
        return;
      }

      // No serial products — proceed to asset selection
      console.log('[WorkOrder] שירות: loading assets for service flow');
      setWorkOrderError(null);
      setWorkTypeModalStep('loading');
      // Load account assets — first get AccountId from opportunity
      let accountId = currentRecord.AccountId;
      console.log('[ServiceFlow] currentRecord.AccountId:', accountId, 'currentRecord.Id:', currentRecord.Id);
      // If AccountId not on the record, fetch the opportunity fresh
      if (!accountId && currentRecord.Id) {
        try {
          const oppRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`);
          if (oppRes.ok) {
            const oppData = await oppRes.json();
            const opp = oppData.record || oppData;
            accountId = opp.AccountId;
            console.log('[ServiceFlow] Fetched AccountId from opp:', accountId);
          }
        } catch (e) { console.error('[ServiceFlow] Error fetching opp:', e); }
      }
      if (accountId) {
        try {
          setLoadingServiceAssets(true);
          // Use related records endpoint from account — guaranteed to return only account's assets
          const assetsRes = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}/related?limit=200`);
          if (assetsRes.ok) {
            const relData = await assetsRes.json();
            const allRelated = relData.related_objects || relData;
            const assetRecords = allRelated.Asset?.records || allRelated.Asset || [];
            const assets = assetRecords.filter(a => a.IsDeleted !== 'true' && a.IsDeleted !== '1' && a.IsDeleted !== true);
            console.log('[ServiceFlow] Loaded', assets.length, 'assets for account', accountId);
            setServiceAssets(assets);
          }
        } catch (e) { console.error('[ServiceFlow] Error loading assets:', e); }
        setLoadingServiceAssets(false);
      } else {
        console.error('[ServiceFlow] No accountId found!');
        setServiceAssets([]);
        setLoadingServiceAssets(false);
      }
      setSelectedServiceAsset(null);
      setServiceCaseData({ Reason_1__c: '', Reason_2__c: '', Description: '', DateOfLastFilterReplacement: new Date().toISOString().split('T')[0] });
      setWorkTypeModalStep('assetSelect');
    } catch (err) {
      console.error('[WorkOrder] Error in handleTechnicianRequired:', err);
      setWorkOrderError(err.message || 'שגיאה בבדיקת מוצרים');
      setWorkTypeModalStep('technicianRequired');
    }
  };

  // Close won without creating WO/SA (for שירות without technician)
  const closeWonWithoutWorkOrder = async (workType) => {
    setWorkOrderCreating(true);
    setWorkTypeModalStep('creating');
    try {
      const oppUpdateData = {
        StageName: pendingStageChange,
        Type: workType.Name,
      };
      const currentUser = recordService.getCurrentUser();
      if (currentUser) {
        const userId = currentUser.id || currentUser.Id || currentUser.email;
        const userName = currentUser.name || currentUser.Name || currentUser.email;
        oppUpdateData.LastModifiedById = userId;
        oppUpdateData.LastModifiedByName = userName;
      }

      const res = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(oppUpdateData)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail?.message || errData.detail || 'שגיאה בעדכון ההזדמנות');
      }

      setCurrentRecord(prev => ({ ...prev, StageName: pendingStageChange, Type: workType.Name }));
      if (onRecordUpdate) {
        onRecordUpdate({ ...currentRecord, StageName: pendingStageChange, Type: workType.Name });
      }

      // Close modal
      setShowWorkTypeModal(false);
      setPendingStageChange(null);
    } catch (err) {
      console.error('[WorkOrder] Error in closeWonWithoutWorkOrder:', err);
      setWorkOrderError(err.message);
      setWorkTypeModalStep('workType');
    } finally {
      setWorkOrderCreating(false);
    }
  };

  // 2026-05-24: Create a 36mo SC linked to a user-picked existing Asset,
  // then close. No Asset is created — warranty-extension attaches to an
  // asset the customer already owns.
  const confirmNoTechWarrantyAndClose = async () => {
    if (!currentRecord?.Id) return;
    if (!noTechWarrantySelectedAssetId) {
      setWorkOrderError('יש לבחור נכס שאליו ישוייך חוזה השירות');
      return;
    }
    setWorkOrderCreating(true);
    setWorkOrderError(null);
    setWorkTypeModalStep('creating');
    try {
      // Stamp the user closing the opp as the creator on the new SC,
      // otherwise the record renders 'נוצר על ידי' empty.
      const currentUser = recordService.getCurrentUser();
      const createdById = currentUser
        ? (currentUser.id || currentUser.Id || currentUser.email || '')
        : '';
      const createdByName = currentUser
        ? (currentUser.name || currentUser.Name || currentUser.email || '')
        : '';
      const createRes = await fetch(
        `${API_BASE}/api/automation/no-tech-warranty-create/${currentRecord.Id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            asset_id: noTechWarrantySelectedAssetId,
            created_by_id: createdById,
            created_by_name: createdByName,
          }),
        }
      );
      const createData = await createRes.json().catch(() => ({}));
      if (!createRes.ok || !createData.success) {
        throw new Error(createData.message || 'יצירת חוזה השירות נכשלה');
      }
      await proceedAfterNoTechDecisionOrClose();
    } catch (err) {
      console.error('[NoTechWarranty] create failed:', err);
      setWorkOrderError(err.message);
      setWorkTypeModalStep('noTechWarrantyConfirm');
    } finally {
      setWorkOrderCreating(false);
    }
  };

  // 2026-05-29: Task 3 — after the optional no-tech warranty SC decision is
  // resolved, check whether the Opp meets the tax-invoice prompt criteria
  // (has line items + verified payment + no serialized product). If so,
  // route to the Yes/No dialog; otherwise close normally.
  const proceedAfterNoTechDecisionOrClose = async () => {
    const pc = noTechWarrantyPrecheck;
    const meetsCriteria = pc
      && pc.has_verified_payment === true
      && pc.has_line_items === true
      && pc.has_serialized_product === false;
    if (meetsCriteria) {
      setNoTechInvoiceReason('');
      setNoTechInvoiceDecisionNo(false);
      setWorkOrderError(null);
      setWorkTypeModalStep('noTechInvoiceConfirm');
      return;
    }
    await closeWonWithoutWorkOrder(selectedWorkType);
  };

  // 2026-05-29: Task 3 — "Yes" branch: issue a tax invoice via Rivhit,
  // then close the Opp.
  const confirmNoTechIssueInvoiceAndClose = async () => {
    if (!currentRecord?.Id) return;
    setNoTechInvoiceSaving(true);
    setWorkOrderError(null);
    try {
      const invRes = await fetch(
        `${API_BASE}/api/automation/issue-tax-invoice-for-opp/${currentRecord.Id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const invData = await invRes.json().catch(() => ({}));
      if (!invRes.ok || !invData.success) {
        const msg = invData.message || invData.detail || 'הנפקת חשבונית מס נכשלה';
        throw new Error(msg);
      }
      await closeWonWithoutWorkOrder(selectedWorkType);
    } catch (err) {
      console.error('[NoTechInvoice] issue failed:', err);
      setWorkOrderError(err.message);
      setWorkTypeModalStep('noTechInvoiceConfirm');
    } finally {
      setNoTechInvoiceSaving(false);
    }
  };

  // 2026-05-29: Task 3 — "No" branch: persist the user's request that NO
  // tax invoice be issued (with a free-text reason), then close the Opp.
  const confirmNoTechSkipInvoiceAndClose = async () => {
    if (!currentRecord?.Id) return;
    const reason = (noTechInvoiceReason || '').trim();
    if (!reason) {
      setWorkOrderError('יש להזין סיבה לאי-הנפקת חשבונית מס');
      return;
    }
    setNoTechInvoiceSaving(true);
    setWorkOrderError(null);
    try {
      const currentUser = recordService.getCurrentUser();
      const payload = {
        Invoice_Not_Issued_By_Request__c: 'true',
        Invoice_Not_Issued_Reason__c: reason,
      };
      if (currentUser) {
        payload.LastModifiedById = currentUser.id || currentUser.Id || currentUser.email;
        payload.LastModifiedByName = currentUser.name || currentUser.Name || currentUser.email;
      }
      const res = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail?.message || errData.detail || 'שמירת השדות נכשלה');
      }
      await closeWonWithoutWorkOrder(selectedWorkType);
    } catch (err) {
      console.error('[NoTechInvoice] save failed:', err);
      setWorkOrderError(err.message);
      setWorkTypeModalStep('noTechInvoiceConfirm');
    } finally {
      setNoTechInvoiceSaving(false);
    }
  };

  // Service flow: create Case + WO + SA with asset and case data
  const createServiceCaseAndWorkOrder = async () => {
    if (!selectedServiceAsset || !serviceCaseData.Reason_1__c) return;
    setWorkOrderCreating(true);
    setWorkTypeModalStep('creating');

    try {
      const _flowToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const _flowHeaders = { 'Content-Type': 'application/json', ...(_flowToken ? { 'Authorization': `Bearer ${_flowToken}` } : {}) };
      const accountId = currentRecord.AccountId;
      let account = null;
      if (accountId) {
        const accountRes = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`);
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          account = accountData.record || accountData;
        }
      }

      // 0. Update Asset with filter replacement date (required by validation rule)
      const filterDate = serviceCaseData.DateOfLastFilterReplacement || new Date().toISOString().split('T')[0];
      if (selectedServiceAsset.Id) {
        await fetch(`${API_BASE}/api/objects/Asset/records/${selectedServiceAsset.Id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ DateOfLastFilterReplacement__c: filterDate })
        });
        console.log('[ServiceFlow] Updated asset filter date:', filterDate);
      }

      // 1. Create Case
      const casePayload = {
        Subject: `${serviceCaseData.Reason_1__c}${serviceCaseData.Reason_2__c ? ' - ' + serviceCaseData.Reason_2__c : ''}`,
        Description: serviceCaseData.Description,
        Status: 'New',
        Priority: 'Medium',
        Origin: 'Phone',
        Reason: 'ביקור טכנאי',
        Reason_1__c: serviceCaseData.Reason_1__c,
        Reason_2__c: serviceCaseData.Reason_2__c,
        AccountId: accountId,
        AssetId: selectedServiceAsset.Id,
        DateOfLastFilterReplacement__c: filterDate,
      };
      console.log('[ServiceFlow] Creating case with payload:', JSON.stringify(casePayload));
      const caseRes = await fetch(`${API_BASE}/api/objects/Case/records`, {
        method: 'POST',
        headers: _flowHeaders,
        body: JSON.stringify(casePayload)
      });
      if (!caseRes.ok) {
        const errText = await caseRes.text();
        console.error('[ServiceFlow] Case creation failed:', caseRes.status, errText);
        let errMsg = 'שגיאה ביצירת מקרה';
        try {
          const errData = JSON.parse(errText);
          const detail = errData.detail;
          if (typeof detail === 'string') { errMsg = detail; }
          else if (detail?.errors?.length > 0) { errMsg = detail.errors.map(e => e.error_message || e.message).join('\n'); }
          else if (detail?.message) { errMsg = detail.message; }
          else { errMsg = errText; }
        } catch (e) { errMsg = errText || errMsg; }
        throw new Error(errMsg);
      }
      const caseResult = await caseRes.json();
      const createdCase = caseResult.record || caseResult;
      console.log('[ServiceFlow] Case created:', createdCase.Id);

      // 2. AutoNumber values come back from the backend on POST response.
      //    (See SA-211357 duplicate incident 2026-06-09 — the old FE-side
      //    `getNextAutoNumber` used SELECT MAX+1, racing across concurrent
      //    creates. Server is now authoritative.)
      let workOrderNumber = '';
      let appointmentNumber = '';

      // 3. Find service territory
      const lat = account?.BillingLatitude;
      const lng = account?.BillingLongitude;
      let territoryId = '';
      if (lat && lng) {
        try {
          const tRes = await fetch(`${API_BASE}/api/objects/ServiceTerritory/find-by-coordinates?lat=${lat}&lng=${lng}&type=שירות`);
          if (tRes.ok) {
            const tData = await tRes.json();
            territoryId = tData.Id || tData.territory?.Id || '';
          }
        } catch (e) { console.error('[ServiceFlow] Territory lookup error:', e); }
      }

      // 4. Build address
      const addressParts = [];
      if (account?.BillingStreet) addressParts.push(account.BillingStreet);
      if (account?.BillingCity) addressParts.push(account.BillingCity);
      const fullAddress = addressParts.join(', ');

      // 5. Create WorkOrder (WorkOrderNumber assigned by backend HiLo)
      const woPayload = {
        AccountId: accountId,
        CaseId: createdCase.Id,
        AssetId: selectedServiceAsset.Id,
        DateOfLastFilterReplacement__c: filterDate,
        Opportunity__c: currentRecord.Id,
        WorkTypeId: selectedWorkType?.Id || '',
        Status: 'New',
        Subject: `שירות - ${serviceCaseData.Reason_1__c} - ${currentRecord.Name || ''}`,
        Priority: 'Medium',
        Duration: selectedWorkType?.EstimatedDuration || 60,
        DurationType: selectedWorkType?.DurationType || 'Minutes',
        Street: account?.BillingStreet || '',
        City: account?.BillingCity || '',
        Latitude: lat || '',
        Longitude: lng || '',
        Address: fullAddress,
        ServiceTerritoryId: territoryId,
      };

      const woRes = await fetch(`${API_BASE}/api/objects/WorkOrder/records`, {
        method: 'POST',
        headers: _flowHeaders,
        body: JSON.stringify(woPayload)
      });
      if (!woRes.ok) throw new Error('שגיאה ביצירת הזמנת עבודה');
      const woResult = await woRes.json();
      const workOrder = woResult.record || woResult;
      // Pick up backend-assigned WorkOrderNumber.
      workOrderNumber = workOrder.WorkOrderNumber || '';
      console.log('[ServiceFlow] WorkOrder created:', workOrder.Id, 'WorkOrderNumber=', workOrderNumber);

      // Update Case with Work_Order__c
      await fetch(`${API_BASE}/api/objects/Case/records/${createdCase.Id}`, {
        method: 'PUT',
        headers: _flowHeaders,
        body: JSON.stringify({ Work_Order__c: workOrder.Id })
      });

      // 5b + 6 — ProductRequired creates and ServiceAppointment create
      // both only depend on workOrder.Id; previously the PR loop was a
      // sequential for/await over N items, then SA was awaited after.
      // Restructured to run in parallel. Wall time collapses from
      // sum(N*PR, SA) to max(maxParallelPR, SA).
      const oliItems = relatedRecords['OpportunityLineItem'] || [];

      // Build SA payload up-front so we can issue its POST concurrently
      // with the PR batch.
      const collectOldDevice = (currentRecord.Name || '').includes('שדרוג') ? 'true' : '';
      // SA AppointmentNumber assigned by backend HiLo (omitted from payload).
      // Subject no longer suffixes the number — it's on the AppointmentNumber field anyway.
      const saData = {
        ParentRecordId: workOrder.Id,
        AccountId: accountId,
        WorkTypeId: selectedWorkType?.Id || '',
        ServiceTerritoryId: territoryId,
        Status: 'None',
        Subject: `שירות - ${serviceCaseData.Reason_1__c}`,
        Duration: selectedWorkType?.EstimatedDuration || 60,
        DurationType: selectedWorkType?.DurationType || 'Minutes',
        Collect_Old_Device__c: collectOldDevice,
        Street: account?.BillingStreet || '',
        City: account?.BillingCity || '',
        Latitude: lat || '',
        Longitude: lng || '',
        Address: fullAddress,
        Floor__c: account?.Floor__c || '',
        Entrance__c: account?.Entrance__c || '',
        Apartment_number__c: account?.Apartment_number__c || '',
      };

      // Owner-spec 2026-06-10: distributor pricebook OLIs flagged with
      // Device_At_Customer__c='1' do NOT spawn a ProductRequired —
      // device is already at the customer, warehouse must not pick.
      const oliItemsToFulfill = oliItems.filter(
        oli => String(oli.Device_At_Customer__c || '') !== '1'
      );
      const _skippedDeviceServiceFlow = oliItems.length - oliItemsToFulfill.length;
      if (_skippedDeviceServiceFlow > 0) {
        console.log(`[ServiceFlow] Skipping ${_skippedDeviceServiceFlow} ProductRequired creates (Device_At_Customer__c='1')`);
      }
      console.log(`[ServiceFlow] Concurrent: ${oliItemsToFulfill.length} ProductRequired + 1 SA`);

      const prPromise = oliItemsToFulfill.length > 0
        ? Promise.all(oliItemsToFulfill.map(async (oli) => {
            try {
              const prPayload = {
                ParentRecordId: workOrder.Id,
                Product2Id: oli.Product2Id || '',
                QuantityRequired: oli.Quantity || '1',
              };
              // Resolve Product2Id from PricebookEntryId if needed.
              // This per-OLI fetch stays inside the per-PR async closure
              // so PBE→PR remains sequential PER ITEM but runs in parallel
              // ACROSS items.
              if (!prPayload.Product2Id || prPayload.Product2Id === 'nan') {
                const pbeId = oli.PricebookEntryId;
                if (pbeId) {
                  try {
                    const pbeRes2 = await fetch(`${API_BASE}/api/objects/PricebookEntry/records/${pbeId}`);
                    if (pbeRes2.ok) {
                      const pbeData = await pbeRes2.json();
                      const pbeRec = pbeData.record || pbeData;
                      if (pbeRec.Product2Id) prPayload.Product2Id = pbeRec.Product2Id;
                    }
                  } catch (e) { console.warn('[ServiceFlow] PBE resolve error:', e); }
                }
              }
              const prRes = await fetch(`${API_BASE}/api/objects/ProductRequired/records`, {
                method: 'POST',
                headers: _flowHeaders,
                body: JSON.stringify(prPayload)
              });
              if (prRes.ok) {
                const prResult = await prRes.json();
                console.log('[ServiceFlow] ProductRequired created:', (prResult.record || prResult).Id);
              } else {
                console.warn('[ServiceFlow] ProductRequired creation failed:', prRes.status);
              }
            } catch (prErr) {
              console.warn('[ServiceFlow] ProductRequired error:', prErr);
            }
          }))
        : Promise.resolve();

      const saPromise = fetch(`${API_BASE}/api/objects/ServiceAppointment/records`, {
        method: 'POST',
        headers: _flowHeaders,
        body: JSON.stringify(saData)
      });

      const [, saRes] = await Promise.all([prPromise, saPromise]);

      let serviceAppointment = null;
      if (saRes.ok) {
        const saResponse = await saRes.json();
        serviceAppointment = saResponse.record || saResponse;
        console.log('[ServiceFlow] SA created:', serviceAppointment?.Id);
      }

      // 7. Update Opportunity - stage + Type + Work_Order__c
      const oppUpdateData = { Type: selectedWorkType?.Name || 'שירות', Work_Order__c: workOrder.Id };
      if (pendingStageChange) oppUpdateData.StageName = pendingStageChange;
      const curUser = recordService.getCurrentUser();
      if (curUser) {
        oppUpdateData.LastModifiedById = curUser.id || curUser.Id || curUser.email;
        oppUpdateData.LastModifiedByName = curUser.name || curUser.Name || curUser.email;
      }
      await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
        method: 'PUT',
        headers: _flowHeaders,
        body: JSON.stringify(oppUpdateData)
      });

      setCurrentRecord(prev => ({ ...prev, StageName: pendingStageChange || prev.StageName, Type: 'שירות' }));
      if (onRecordUpdate) onRecordUpdate({ ...currentRecord, StageName: pendingStageChange || currentRecord.StageName, Type: 'שירות' });

      // 8. Show success
      setWorkOrderResult({
        workOrderId: workOrder.Id,
        workOrderNumber: workOrder.WorkOrderNumber || workOrderNumber,
        serviceAppointmentId: serviceAppointment?.Id,
        serviceAppointmentNumber: serviceAppointment?.AppointmentNumber || appointmentNumber,
        caseId: createdCase.Id,
      });
      setWorkTypeModalStep('success');
      loadAllRelatedRecords(true);
    } catch (err) {
      console.error('[ServiceFlow] Error:', err);
      setWorkOrderError(typeof err === 'string' ? err : (err?.message || 'שגיאה ביצירת מקרה/הזמנת עבודה'));
      setWorkTypeModalStep('caseCreate');
    } finally {
      setWorkOrderCreating(false);
    }
  };

  // Cancel work order flow
  const handleCancelWorkOrderFlow = () => {
    setShowWorkTypeModal(false);
    setPendingStageChange(null);
    setSelectedWorkType(null);
    setSelectedProductItem(null);
    setAvailableProductItems([]);
    setAvailableSerializedProducts({});
    setSelectedSerializedProducts({});
    setCompletedSerialSelections([]);
    setWorkOrderError(null);
  };

  // Toggle related list expansion
  const toggleRelatedList = (listName, relatedObject) => {
    setExpandedRelatedLists(prev => {
      const isExpanded = !prev[listName];
      if (isExpanded && relatedObject) {
        loadRelatedRecords(relatedObject, listName);
      }
      return { ...prev, [listName]: isExpanded };
    });
  };

  // Toggle section collapse/expand
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // === LINE ITEM EDIT/DELETE FUNCTIONS ===
  // 2026-06-01 — fetch effective price-range permission. Fails open
  // so a backend hiccup doesn't strand the user.
  // 2026-10-14: plus the Validation Exemptions engine — a user exempt from
  // rule OpportunityLineItem_Price_Range (Setup → עורך וולידציות) edits the
  // price freely. Fail-closed on that part, like the closed-won gates.
  // 2026-10-14: extracted so the line-item menu's "עריכה" — the button that
  // actually opens the modal — calls it. It used to open the modal directly,
  // so this never ran: price ranges, "unrestricted" and the exemption were
  // all ignored and every non-free user stayed locked.
  const loadLineItemPricePermission = async () => {
    const userKey = currentUser?.username || currentUser?.email || currentUser?.id;
    if (!userKey) return;
    const exemptP = (async () => {
      try {
        const [regR, effR] = await Promise.all([
          fetch(`${API_BASE}/api/validations/registry`),
          fetch(`${API_BASE}/api/validations/exemptions/effective/${encodeURIComponent(userKey)}`),
        ]);
        const reg = regR.ok ? await regR.json() : {};
        const eff = effR.ok ? await effR.json() : {};
        return !!reg.enabled
          && (eff.exempt_rule_names || []).includes('OpportunityLineItem_Price_Range');
      } catch (e) { return false; /* fail-closed: keep the price gate */ }
    })();
    const rangeP = (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/pricebook/price-range-permissions/effective/${encodeURIComponent(userKey)}`);
        return r.ok ? await r.json() : null;
      } catch (e) { return null; /* fail open */ }
    })();
    const [isValidationExempt, d] = await Promise.all([exemptP, rangeP]);
    if (d) {
      setLineItemPricePermission({
        is_admin: !!d.is_admin,
        is_technician: !!d.is_technician,
        is_institutional: !!d.is_institutional,
        is_accounting: !!d.is_accounting,
        // Setup's "unrestricted" grant — the gates below read it.
        is_price_unrestricted: !!d.is_price_unrestricted,
        is_validation_exempt: isValidationExempt,
        product_ranges: d.product_ranges || {},
      });
    } else {
      setLineItemPricePermission(prev => ({ ...prev, is_validation_exempt: isValidationExempt }));
    }
    // A click on the price input before this landed showed the "no
    // permission" message; the input re-evaluates the lock from here on.
    setLineItemPriceError('');
  };

  // Handle edit line item
  const handleEditLineItem = (lineItem) => {
    setEditingLineItem({
      ...lineItem,
      newQuantity: lineItem.Quantity || 1,
      newUnitPrice: lineItem.UnitPrice || 0
    });
    setShowLineItemModal(true);
    setLineItemActionMenu(null);
    setLineItemPriceError('');
    loadLineItemPricePermission();
  };

  // Handle delete line item
  const handleDeleteLineItem = async (lineItem) => {
    setLineItemActionMenu(null);

    console.log('[Delete] Line item to delete:', lineItem);
    console.log('[Delete] Line item Id:', lineItem?.Id);

    if (!lineItem?.Id) {
      alert('שגיאה: לא נמצא מזהה לפריט');
      return;
    }

    try {
      // Optimistically remove line item from UI immediately
      const deletedPrice = parseFloat(lineItem.TotalPrice) || (parseFloat(lineItem.UnitPrice || 0) * parseFloat(lineItem.Quantity || 1));
      setRelatedRecords(prev => ({
        ...prev,
        OpportunityLineItem: (prev.OpportunityLineItem || []).filter(item => item.Id !== lineItem.Id)
      }));
      setRelatedCounts(prev => ({
        ...prev,
        OpportunityLineItem: Math.max(0, (prev.OpportunityLineItem ?? (relatedRecords?.OpportunityLineItem?.length || 1)) - 1)
      }));

      // Optimistically update amounts in UI
      const remainingItems = (relatedRecords?.OpportunityLineItem || []).filter(item => item.Id !== lineItem.Id);
      const newTotal = remainingItems.reduce((sum, item) => sum + (parseFloat(item.TotalPrice) || 0), 0);
      const roundedTotal = Math.round(newTotal * 100) / 100;
      setCurrentRecord(prev => ({
        ...prev,
        Amount: roundedTotal,
        Actual_Balance_To_Be_Paid__c: roundedTotal,
        Fictitious_Balance_Due__c: roundedTotal
      }));

      // Delete the line item from server
      // authHeaders(): DELETE carries no body, so the token is the only way the
      // server can name who is deleting. Without it recycle_bin.deleted_by
      // records 'unknown' and the history cannot say who removed the product.
      const deleteRes = await fetch(`${API_BASE}/api/objects/OpportunityLineItem/records/${lineItem.Id}`, {
        method: 'DELETE',
        headers: deleteHeaders('OLI_DELETE'),
      });

      if (!deleteRes.ok) {
        const errorData = await deleteRes.json().catch(() => ({}));
        console.error('[Delete] Error response:', errorData);
        throw new Error(errorData.detail || 'שגיאה במחיקת המוצר');
      }

      // 2026-06-17 owner-spec: bust the module-level related-records cache so
      // F5 or navigate-back doesn't resurrect the deleted OLI. Prior code only
      // patched component state — cache entry stayed stale for 60s.
      _invalidateRelatedCacheOnDelete(object.api_name, currentRecord?.Id, 'OpportunityLineItem', lineItem.Id);

      // 2026-08-30: reconcile the inline filter now that the item is gone.
      //
      // Deleting a device used to leave its filter behind — the count only
      // corrected itself on the NEXT add or edit, because those were the only
      // paths that reconciled. Removing the last device left a filter with
      // nothing to filter.
      //
      // The pricebook is loaded on demand: `lineItemProducts` is only
      // populated when the edit modal opens, and a delete usually happens
      // without ever opening it. An empty list would make the plan refuse
      // (fail-closed) and silently skip the reconciliation — safe, but it
      // would not fix the bug.
      try {
        const products = lineItemProducts.length
          ? lineItemProducts
          : await loadProductsForLineItemEdit();
        const rec = await reconcileInlineFilter({
          apiBase: API_BASE,
          opportunityId: currentRecord.Id,
          products,
          actor: {
            id: currentUser?.id || currentUser?.Id || currentUser?.email || '',
            name: currentUser?.name || currentUser?.Name || currentUser?.email || '',
          },
        });
        // The reconciliation writes filter rows straight to the API. The
        // delete patch above only knows about the row the user removed, so
        // without this the cached payload keeps the filter's old quantity
        // until the TTL runs out.
        if (rec?.changed) {
          _invalidateRelatedCacheForParent(object.api_name, currentRecord?.Id);
        }
      } catch (recErr) {
        // The deletion the user asked for has already happened. A filter that
        // could not be reconciled is a follow-up, not a failed delete.
        console.warn('[oppline-delete] inline-filter reconcile failed', recErr);
      }

      // Recalculate accurate amounts from server (includes payment deductions)
      await recalculateOpportunityAmounts();

    } catch (err) {
      console.error('Error deleting line item:', err);
      alert('שגיאה במחיקת המוצר: ' + err.message);
      // On error, reload to get correct state
      try {
        const relatedRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord?.Id}/related?limit=1000&resolve_refs=false&_t=${Date.now()}`);
        if (relatedRes.ok) {
          const relatedData = await relatedRes.json();
          setRelatedRecords(prev => ({
            ...prev,
            OpportunityLineItem: relatedData.related_objects?.OpportunityLineItem?.records || []
          }));
        }
      } catch (reloadErr) {
        console.error('[Delete] Error reloading:', reloadErr);
      }
    }
  };

  // Load products for line item edit modal
  const loadProductsForLineItemEdit = async () => {
    setLoadingLineItemProducts(true);
    try {
      // Get opportunity's pricebook
      const oppPricebookId = currentRecord.Pricebook2Id;
      if (!oppPricebookId) {
        // Load standard pricebook as fallback
        const query = 'SELECT Id, Name, IsActive FROM Pricebook2 WHERE IsActive = true ORDER BY Name LIMIT 1';
        const pbRes = await fetch(`${API_BASE}/api/platform/query?q=${encodeURIComponent(query)}`);
        if (pbRes.ok) {
          const pbData = await pbRes.json();
          const pbs = pbData.records || pbData.results || [];
          if (pbs.length > 0) {
            return await loadProductsFromPricebook(pbs[0].Id);
          }
        }
      } else {
        return await loadProductsFromPricebook(oppPricebookId);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoadingLineItemProducts(false);
    }
    return [];
  };

  // Returns the pricebook's products AND sets them on state. The return value
  // is what the delete path uses: it needs the list in the same tick, and a
  // setState is not readable until the next render.
  const loadProductsFromPricebook = async (pricebookId) => {
    // Get pricebook entries
    const entriesQuery = `SELECT Id, Product2Id, UnitPrice, IsActive FROM PricebookEntry WHERE Pricebook2Id = '${pricebookId}'`;
    const entriesRes = await fetch(`${API_BASE}/api/platform/query?q=${encodeURIComponent(entriesQuery)}`);
    if (!entriesRes.ok) return [];

    const entriesData = await entriesRes.json();
    const entries = (entriesData.records || entriesData.results || []).filter(e =>
      e.IsActive === true || e.IsActive === "true" || e.IsActive === "1" || e.IsActive === 1
    );

    // Get products.
    // 2026-08-30: IsSerialized and Is_System__c added — the inline-filter
    // reconciliation below counts devices and systems from this list, and
    // without IsSerialized every product read as non-serial. The platform
    // query layer drops unknown columns silently (csv_data_layer.py:421), so
    // asking for Is_System__c before the migration runs is safe: it comes back
    // absent and the reconciliation refuses rather than guessing.
    const productsQuery = `SELECT Id, Name, ProductCode, IsActive, IsSerialized, Is_System__c FROM Product2 ORDER BY Name LIMIT 1000`;
    const productsRes = await fetch(`${API_BASE}/api/platform/query?q=${encodeURIComponent(productsQuery)}`);
    if (!productsRes.ok) return [];

    const productsData = await productsRes.json();
    const allProducts = (productsData.records || productsData.results || []).filter(p =>
      p.IsActive === true || p.IsActive === "true" || p.IsActive === "1" || p.IsActive === 1
    );

    // Create price map
    const priceMap = {};
    entries.forEach(entry => {
      priceMap[entry.Product2Id] = {
        pricebookEntryId: entry.Id,
        unitPrice: parseFloat(entry.UnitPrice) || 0
      };
    });

    // Filter and map products
    const productsWithPrices = allProducts
      .filter(product => priceMap[product.Id])
      .map(product => ({
        ...product,
        pricebookEntryId: priceMap[product.Id].pricebookEntryId,
        unitPrice: priceMap[product.Id].unitPrice
      }));

    setLineItemProducts(productsWithPrices);
    return productsWithPrices;
  };

  // Handle save edited line item - with full recalculation
  const handleSaveLineItem = async () => {
    if (!editingLineItem) return;

    // 2026-06-01 owner-spec — defensive price-range gate at save time
    // so a stale FE state (or a clever user who edits the input via
    // devtools) can't bypass the lock that should already be in place.
    const productId = editingLineItem.selectedProductId
      || editingLineItem.Product2Id
      || editingLineItem.Product2?.Id
      || '';
    const perm = lineItemPricePermission;
    // PR after #804: institutional users have unrestricted price edit.
    // Owner-spec 2026-06-16: accounting joins the free-edit set too.
    // 2026-06-16: also trust currentUser direct flags — the async fetch
    // that fills `perm` can race the modal open, leaving an admin
    // briefly locked. Reading currentUser eliminates the race window.
    const currentUserIsFree = (
      currentUser?.is_admin === true ||
      ['Admin', 'accounting', 'הנהלת חשבונות', 'institutional', 'מוסדי'].includes(currentUser?.profile)
    );
    // 2026-10-25: a line a manager discounted keeps the approved price, below the range or not.
    const keepsApprovedPrice = mayKeepApprovedPrice(
      discountSummary, editingLineItem.Id, editingLineItem.editUnitPrice,
      !!(editingLineItem.selectedProductId && editingLineItem.selectedProductId !== editingLineItem.Product2Id));
    if (productId && !keepsApprovedPrice && !currentUserIsFree && !perm.is_admin && !perm.is_technician && !perm.is_institutional && !perm.is_accounting && !perm.is_price_unrestricted && !perm.is_validation_exempt) {
      const r = perm.product_ranges?.[productId];
      if (!r) {
        alert('אין לך הרשאה לשנות את המחיר. צור קשר עם מנהל המערכת.');
        return;
      }
      const v = parseFloat(editingLineItem.editUnitPrice) || 0;
      const minN = Number(r.min);
      const maxN = Number(r.max);
      if (v < minN || v > maxN) {
        alert('המחיר חייב להיות בטווח ₪' + minN + ' – ₪' + maxN);
        return;
      }
    }

    setLineItemSaving(true);
    try {
      const newQuantity = parseFloat(editingLineItem.editQuantity) || 1;
      const newUnitPrice = parseFloat(editingLineItem.editUnitPrice) || 0;
      const newTotalPrice = newUnitPrice * newQuantity;

      // Build update object
      const updateData = {
        Quantity: newQuantity,
        UnitPrice: newUnitPrice,
        TotalPrice: newTotalPrice
      };

      // If product was changed, update Product2Id and PricebookEntryId
      let productSwapMeta = null;
      if (editingLineItem.selectedProductId && editingLineItem.selectedProductId !== editingLineItem.Product2Id) {
        const selectedProduct = lineItemProducts.find(p => p.Id === editingLineItem.selectedProductId);
        if (selectedProduct) {
          updateData.Product2Id = selectedProduct.Id;
          updateData.PricebookEntryId = selectedProduct.pricebookEntryId;
          productSwapMeta = {
            Product2__Name: selectedProduct.Name,
            Product2__Family: selectedProduct.Family || selectedProduct.family || '',
            Product2__ProductCode: selectedProduct.ProductCode || '',
            Product2__IsSerialized: selectedProduct.isSerialized ? '1' : '0',
          };
        }
      }

      // Update the line item
      const updateRes = await fetch(`${API_BASE}/api/objects/OpportunityLineItem/records/${editingLineItem.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (!updateRes.ok) {
        throw new Error('שגיאה בעדכון פריט ההזדמנות');
      }

      // Owner-spec 2026-05-31: row was visually disappearing because
      // loadAllRelatedRecords sets `loadingRelated` → list renders
      // empty while the fetch resolves. Patch the local state
      // optimistically right after the PUT so the row updates in
      // place — no flicker, no "where did my line item go?".
      setRelatedRecords(prev => {
        const lis = (prev.OpportunityLineItem || []).map(li =>
          li.Id === editingLineItem.Id
            ? {
                ...li,
                Quantity: String(newQuantity),
                UnitPrice: String(newUnitPrice),
                TotalPrice: String(newTotalPrice),
                ...(productSwapMeta || {}),
              }
            : li
        );
        return { ...prev, OpportunityLineItem: lis };
      });

      // Hold the pricebook the reconciliation needs BEFORE the modal state is
      // cleared. The closure would keep it either way, but that is a subtle
      // thing to leave a future edit to rediscover.
      const productsForReconcile = lineItemProducts;

      // Close modal — UI already shows the new values.
      setShowLineItemModal(false);
      setEditingLineItem(null);
      setLineItemProducts([]);

      // 2026-08-30: inline-filter reconciliation on edit — one shared
      // implementation (utils/inlineFilter.js), also used by ProductsModal.
      //
      // What was here read `li.Product2__IsSerialized` and
      // `li.Product2__Name` off a `resolve_refs=false` response. Those fields
      // are generated at READ TIME by the reference resolver
      // (unified_server.py:14880) and are absent from that response, so every
      // item counted as neither device nor filter, desired and current were
      // both 0, and the block never did anything. It was dead from the day it
      // was written.
      try {
        await reconcileInlineFilter({
          apiBase: API_BASE,
          opportunityId: currentRecord.Id,
          products: productsForReconcile,
          actor: {
            id: currentUser?.id || currentUser?.Id || currentUser?.email || '',
            name: currentUser?.name || currentUser?.Name || currentUser?.email || '',
          },
        });
      } catch (recErr) {
        // The user's edit is already saved; the filter is a follow-up.
        console.warn('[oppline-edit] inline-filter reconcile failed', recErr);
      }

      // The edit changed the line item, and the reconciliation may have moved
      // the filter row too. Drop the parent's cached payload rather than guess
      // which rows moved — unconditional, because the edit itself always did.
      _invalidateRelatedCacheForParent(object.api_name, currentRecord?.Id);

      // Recompute Opportunity totals + sync the related-list from PG
      // in the background (no setLoading-driven flicker now that the
      // optimistic update already covers the visible state).
      await recalculateOpportunityAmounts();
      loadAllRelatedRecords(true).catch(() => {});

    } catch (err) {
      console.error('Error updating line item:', err);
      alert('שגיאה בעדכון המוצר: ' + err.message);
    } finally {
      setLineItemSaving(false);
    }
  };

  // Recalculate opportunity amounts based on ALL line items and existing payments
  const recalculateOpportunityAmounts = async () => {
    try {
      // 1. Get ALL OpportunityLineItems for this opportunity using the related API (works correctly)
      // _t parameter bypasses cache to get fresh data
      const relatedRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}/related?limit=1000&resolve_refs=false&_t=${Date.now()}`);
      let totalLineItemsAmount = 0;
      let totalSuccessPayments = 0;
      let totalFictitiousPayments = 0;

      if (relatedRes.ok) {
        const relatedData = await relatedRes.json();

        // Get line items total
        const lineItemsData = relatedData.related_objects?.OpportunityLineItem;
        if (lineItemsData?.records) {
          totalLineItemsAmount = lineItemsData.records.reduce((sum, item) => {
            const price = parseFloat(item.TotalPrice) || 0;
            return sum + price;
          }, 0);
        }

        // Get payments totals
        const paymentsData = relatedData.related_objects?.Payment__c;
        if (paymentsData?.records) {
          // Actual balance: only Success payments
          const successPayments = paymentsData.records.filter(p =>
            ['SUCCESS', 'Success', 'Paid', 'שולם', 'הצלחה'].includes(p.Payment_Status__c)
          );
          totalSuccessPayments = successPayments.reduce((sum, p) => sum + (parseFloat(p.Amount__c) || 0), 0);

          // Fictitious balance: Success + Need To Be Paid only
          const fictitiousStatuses = ['SUCCESS', 'Success', 'Paid', 'שולם', 'הצלחה', 'Need To Be Paid'];
          const fictitiousPayments = paymentsData.records.filter(p =>
            fictitiousStatuses.includes(p.Payment_Status__c)
          );
          totalFictitiousPayments = fictitiousPayments.reduce((sum, p) => sum + (parseFloat(p.Amount__c) || 0), 0);
        }
      }

      // 2. Calculate new amounts
      const newAmount = Math.round(totalLineItemsAmount * 100) / 100;
      const newActualBalance = Math.round(Math.max(0, totalLineItemsAmount - totalSuccessPayments) * 100) / 100;
      const newFictitiousBalance = Math.round(Math.max(0, totalLineItemsAmount - totalFictitiousPayments) * 100) / 100;

      // 3. Update opportunity
      const putRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Amount: newAmount,
          Actual_Balance_To_Be_Paid__c: newActualBalance,
          Fictitious_Balance_Due__c: newFictitiousBalance
        })
      });

      // 4. Update local state. The server keeps Amount = Σ line items and
      // may correct ours (the list above can predate a concurrent delete),
      // so show what it saved when the response carries it.
      const saved = putRes.ok ? (await putRes.json().catch(() => null))?.record : null;
      setCurrentRecord(prev => ({
        ...prev,
        Amount: saved?.Amount ?? newAmount,
        Actual_Balance_To_Be_Paid__c: saved?.Actual_Balance_To_Be_Paid__c ?? newActualBalance,
        Fictitious_Balance_Due__c: saved?.Fictitious_Balance_Due__c ?? newFictitiousBalance
      }));

      console.log(`[Recalculate] Total line items: ${totalLineItemsAmount}, Success payments: ${totalSuccessPayments}, New balance: ${newActualBalance}`);

    } catch (err) {
      console.error('Error recalculating amounts:', err);
    }
  };

  // Fetch object fields from backend
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/fields`);
        if (res.ok) {
          const data = await res.json();
          setBackendFields(data.fields || []);
        }
      } catch (err) {
        console.error('Error fetching fields:', err);
      }
    };
    fetchFields();
  }, [object.api_name]);

  // טעינת ה-Record Page של האובייקט (או fallback ל-Layout) - WITH CACHE
  useEffect(() => {
    const loadFlexipage = async () => {
      // Check cache first for INSTANT loading
      if (flexipageCache[object.api_name]) {
        console.log(`[FlexiPage] Using cached FlexiPage for ${object.api_name}`);
        setFlexipage(flexipageCache[object.api_name]);
        setLayout(null);
        return;
      }

      try {
        // Load from server
        const flexiRes = await fetch(`${API_BASE}/api/objects/${object.api_name}/record-page`);
        if (flexiRes.ok) {
          const data = await flexiRes.json();
          setFlexipage(data.flexipage);
          setLayout(null);
          // Cache the Record Page for future use
          if (data.flexipage) {
            setFlexipageCache(prev => ({ ...prev, [object.api_name]: data.flexipage }));
          }
        } else {
          // Fallback to Layout
          const layoutRes = await fetch(`${API_BASE}/api/objects/${object.api_name}/layouts/default`);
          if (layoutRes.ok) {
            const data = await layoutRes.json();
            setLayout(data.layout);
          }
        }
      } catch (err) {
        console.error('Error loading flexipage/layout:', err);
      }
    };
    loadFlexipage();
  }, [object.api_name, flexipageCache, setFlexipageCache]);

  // טעינה אוטומטית של הרשומות הקשורות
  useEffect(() => {
    if (currentRecord?.Id) {
      // Show cached related-records immediately so the lists paint on
      // entry. Then refresh in the background so any changes since the
      // last visit show up. If we have nothing cached we just leave the
      // previous state in place (no flash of the spinner — which we've
      // also removed visually).
      const cacheKey = `${object.api_name}/${currentRecord.Id}`;
      const cached = _relatedRecordsCache.get(cacheKey);
      if (cached) {
        setRelatedRecords(cached.data);
        // Auto-expand non-empty related lists when painting from cache.
        // Without this, the page lands with every section collapsed
        // (the initial state of `expandedRelatedLists`) — owner asked
        // for non-empty lists to default to OPEN. The slower `loadAll`
        // path already does this; the cached path was missing it.
        const expandFromCache = {};
        for (const [objName, records] of Object.entries(cached.data || {})) {
          if (Array.isArray(records) && records.length > 0) {
            expandFromCache[objName] = true;
          }
        }
        if (Object.keys(expandFromCache).length > 0) {
          setExpandedRelatedLists(prev => ({ ...expandFromCache, ...prev }));
        }
        // Use cached counts too so the section headers match
        if (cached.counts) setRelatedCounts(cached.counts);
        const fresh = (Date.now() - cached.timestamp) < _RELATED_CACHE_TTL_MS;
        if (fresh) return;       // cache is fresh enough — skip the network round-trip
      } else {
        // PR #A+ Keep Previous Data (owner-spec 2026-06-09):
        // intentionally NOT calling setRelatedRecords({}) here. When
        // the user navigates A → B, leaving A's related lists visible
        // until B's data arrives is far less jarring than the
        // "everything blanks then refills" flash.
        // Counts ARE cleared because the badges have correct numbers
        // from /related-counts in ~100ms — short enough to not feel
        // wrong, and a (5) of A → (12) of B with no transition would
        // look like a calculation error.
        setRelatedCounts({});
      }
      // PR #A+ parallel pickup: if RecordDetailRoute kicked off
      // /related at T=0, the promise is in _pendingRelatedFetches. Wait
      // for it to land — much faster than firing our own fetch fresh.
      const pending = _pendingRelatedFetches.get(cacheKey);
      if (pending) {
        pending.then((data) => {
          if (data) {
            setRelatedRecords(data);
            const expand = {};
            for (const [objName, records] of Object.entries(data || {})) {
              if (Array.isArray(records) && records.length > 0) {
                expand[objName] = true;
              }
            }
            if (Object.keys(expand).length > 0) {
              setExpandedRelatedLists(prev => ({ ...expand, ...prev }));
            }
            // also pull counts from the same payload, see _relatedRecordsCache write above
            const fresh = _relatedRecordsCache.get(cacheKey);
            if (fresh && fresh.counts) setRelatedCounts(fresh.counts);
          }
        });
        return; // skip the /related-counts + loadAllRelatedRecords below
      }
      // Fast-path: hit /related-counts in parallel. This endpoint runs
      // a tiny COUNT(*) per related table and returns in ~50-150ms even
      // when the heavy /related is still scanning EntityHistory. The
      // section headers ('הזדמנויות (5)') therefore show correct numbers
      // immediately while the row contents fill in seconds later.
      (async () => {
        try {
          const r = await fetch(`${API_BASE}/api/objects/${object.api_name}/records/${currentRecord.Id}/related-counts`);
          if (r.ok) {
            const d = await r.json();
            if (d && d.counts) setRelatedCounts(d.counts);
          }
        } catch (e) {
          // Non-blocking — full /related load below will populate counts too.
          console.debug('[related-counts] skipped:', e);
        }
      })();
      loadAllRelatedRecords();
    } else {
      setRelatedRecords({});
      setRelatedCounts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object.api_name, currentRecord?.Id]);

  const [historyMeta, setHistoryMeta] = useState({});

  // Load record history when History tab is active
  const loadRecordHistory = async () => {
    if (!currentRecord.Id || loadingHistory) return;
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/records/${currentRecord.Id}/history`);
      if (res.ok) {
        const data = await res.json();
        setRecordHistory(data.history || []);
        setHistoryMeta({
          created_by: data.created_by,
          created_date: data.created_date,
          last_modified_by: data.last_modified_by,
          last_modified_date: data.last_modified_date
        });
      } else {
        const basicHistory = [];
        if (currentRecord.CreatedDate) {
          basicHistory.push({ type: 'created', date: currentRecord.CreatedDate, user: currentRecord.CreatedById, field: null, oldValue: null, newValue: null });
        }
        if (currentRecord.LastModifiedDate && currentRecord.LastModifiedDate !== currentRecord.CreatedDate) {
          basicHistory.push({ type: 'modified', date: currentRecord.LastModifiedDate, user: currentRecord.LastModifiedById, field: null, oldValue: null, newValue: null });
        }
        setRecordHistory(basicHistory);
      }
    } catch (err) {
      console.error('Error loading history:', err);
      setRecordHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load activities (tasks and events) related to THIS specific record ONLY
  const loadRecordActivities = async () => {
    if (!currentRecord.Id || loadingActivities) return;
    setLoadingActivities(true);
    try {
      let tasks = [];
      let events = [];

      const isAccount = object.api_name === 'Account';
      const isContact = ['Contact', 'Lead'].includes(object.api_name);

      if (isAccount) {
        // Account: חפש כל הפעילויות — כולל מהזדמנויות, הזמנות עבודה, נכסים, אנשי קשר
        const allRes = await fetch(`${API_BASE}/api/objects/Account/records/${currentRecord.Id}/all-activities?limit=200`);
        if (allRes.ok) {
          const data = await allRes.json();
          tasks = data.tasks || [];
          events = data.events || [];
        }
      } else if (isContact) {
        // Contact/Lead: חפש לפי WhoId בלבד
        const tasksRes = await fetch(`${API_BASE}/api/objects/Task/records?WhoId=${currentRecord.Id}&limit=100`);
        if (tasksRes.ok) { const data = await tasksRes.json(); tasks = data.records || []; }
        const eventsRes = await fetch(`${API_BASE}/api/objects/Event/records?WhoId=${currentRecord.Id}&limit=100`);
        if (eventsRes.ok) { const data = await eventsRes.json(); events = data.records || []; }
      } else {
        // Opportunity, Case, WorkOrder etc: חפש רק לפי WhatId של הרשומה הספציפית
        const tasksRes = await fetch(`${API_BASE}/api/objects/Task/records?WhatId=${currentRecord.Id}&limit=100`);
        if (tasksRes.ok) { const data = await tasksRes.json(); tasks = data.records || []; }
        const eventsRes = await fetch(`${API_BASE}/api/objects/Event/records?WhatId=${currentRecord.Id}&limit=100`);
        if (eventsRes.ok) { const data = await eventsRes.json(); events = data.records || []; }
        // 2026-10-15: credit-approval tasks hang on the opportunity (WhatId) — a
        // payment shows its opportunity's ones too, so the request is visible on
        // the account, the opportunity and the payment.
        if (object.api_name === 'Payment__c' && currentRecord.Opportunity__c) {
          const oppRes = await fetch(`${API_BASE}/api/objects/Task/records?WhatId=${currentRecord.Opportunity__c}&limit=100`);
          if (oppRes.ok) {
            const data = await oppRes.json();
            tasks = tasks.concat((data.records || []).filter(t => t.Type === 'Credit Approval' || t.Type === 'Credit Log'));
          }
        }
      }

      // הסר כפילויות
      const uniqueTasks = {};
      tasks.forEach(t => { if (t.Id) uniqueTasks[t.Id] = t; });
      tasks = Object.values(uniqueTasks);
      const uniqueEvents = {};
      events.forEach(e => { if (e.Id) uniqueEvents[e.Id] = e; });
      events = Object.values(uniqueEvents);

      // מיין לפי תאריך (החדש ביותר קודם)
      tasks.sort((a, b) => new Date(b.ActivityDate || b.CreatedDate || 0) - new Date(a.ActivityDate || a.CreatedDate || 0));
      events.sort((a, b) => new Date(b.StartDateTime || b.CreatedDate || 0) - new Date(a.StartDateTime || a.CreatedDate || 0));

      setRecordActivities({ tasks, events });
    } catch (err) {
      console.error('Error loading activities:', err);
      setRecordActivities({ tasks: [], events: [] });
    } finally {
      setLoadingActivities(false);
    }
  };

  // What the abbreviated activity view leaves out — the "show all" button sits
  // outside the blocks that apply the caps and cannot see past them on its own.
  // Works entirely on what the fetch already returned: expanding is a pure
  // render change, no second request and no pagination.
  const activityView = useMemo(() => summariseActivities(recordActivities), [recordActivities]);

  // Expanding applies to the record in front of you, not as a sticky
  // preference — opening another record starts collapsed again.
  useEffect(() => {
    setShowAllActivities(false);
  }, [currentRecord.Id]);

  // Load data when tabs change
  useEffect(() => {
    if (activeTab === 'history' && recordHistory.length === 0 && !loadingHistory) {
      loadRecordHistory();
    } else if (activeTab === 'activity' && recordActivities.tasks.length === 0 && recordActivities.events.length === 0 && !loadingActivities) {
      loadRecordActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentRecord.Id]);

  // Load users for activity "Assigned To" field
  useEffect(() => {
    if (showNewActivityModal && activityUsers.length === 0) {
      fetch(`${API_BASE}/api/auth/users/all`)
        .then(r => r.ok ? r.json() : { users: [] })
        .then(d => setActivityUsers((d.users || []).filter(u => u.isActive !== false)))
        .catch(() => {});
    }
    if (showNewActivityModal && currentUser) {
      // Default "Assigned To" to current user. Owner-reported 2026-06-09:
      // the default was showing the raw username (meni@afikim.my) for the
      // logged-in user because first_name + last_name on `currentUser`
      // are sometimes empty even when a display name IS available via
      // `currentUser.name` / `.Name` (set by /api/auth/login). Cross-
      // reference activityUsers (the same /api/auth/users/all list the
      // dropdown shows) so we get the same Hebrew display name as the
      // manual-pick path.
      const _activityUserLookup = activityUsers.find(u => (
        u.username === currentUser.username ||
        u.email === currentUser.email ||
        u.username === currentUser.email
      ));
      const cuName = (
        currentUser.name ||
        currentUser.Name ||
        (_activityUserLookup && (_activityUserLookup.name || `${_activityUserLookup.first_name || ''} ${_activityUserLookup.last_name || ''}`.trim())) ||
        `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() ||
        currentUser.username ||
        ''
      );
      setActivitySelectedUser({
        id: currentUser.id || currentUser.username,
        name: cuName,
        email: currentUser.email || currentUser.username,
        username: currentUser.username,
      });
    }
    if (!showNewActivityModal) {
      setActivityUserSearch('');
      setActivitySelectedUser(null);
      setActivityUserDropdownOpen(false);
      // PR after #806: drop any selected email attachments so they don't
      // leak across separate open-and-close cycles of the dialog.
      setEmailAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewActivityModal, activityUsers.length]);

  // When activityUsers loads AFTER the modal-open default was set,
  // re-resolve the display name for the current selection. Without this
  // the first paint sometimes lands while activityUsers is still empty
  // and the default name falls back to the username. Skipped once the
  // user picks someone else (the `currentUser`-match check).
  useEffect(() => {
    if (!showNewActivityModal || !currentUser || activityUsers.length === 0) return;
    setActivitySelectedUser(prev => {
      if (!prev) return prev;
      const isStillDefault = (
        prev.username === currentUser.username ||
        prev.email === (currentUser.email || currentUser.username)
      );
      if (!isStillDefault) return prev;
      const u = activityUsers.find(x => (
        x.username === currentUser.username ||
        x.email === currentUser.email ||
        x.username === currentUser.email
      ));
      const looked = u && (u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim());
      if (!looked || looked === prev.name) return prev;
      return { ...prev, name: looked };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityUsers.length, showNewActivityModal]);

  // טעינת כפתורים מותאמים אישית לאובייקט
  useEffect(() => {
    const fetchCustomButtons = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/objects/${object.api_name}/buttons`);
        if (res.ok) {
          const data = await res.json();
          setCustomButtons(data.buttons || []);
        }
      } catch (err) {
        console.error('Error fetching custom buttons:', err);
      }
    };
    fetchCustomButtons();
  }, [object.api_name]);

  // Get display name - prefer specific number fields for different object types
  // Helper to format WorkOrderNumber with leading zeros
  const formatWorkOrderNumber = (num) => {
    if (!num) return null;
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return num;
    return String(Math.round(numValue)).padStart(8, '0');
  };

  // For Person Accounts, Name may be empty - use FirstName + LastName
  const getDisplayName = () => {
    if (currentRecord.AppointmentNumber) return currentRecord.AppointmentNumber;
    if (currentRecord.WorkOrderNumber) return formatWorkOrderNumber(currentRecord.WorkOrderNumber);
    if (currentRecord.CaseNumber) return currentRecord.CaseNumber;
    if (currentRecord.Name && currentRecord.Name.trim()) return currentRecord.Name;
    // Person Account: combine FirstName + LastName
    const firstName = currentRecord.FirstName || currentRecord.PersonFirstName || '';
    const lastName = currentRecord.LastName || currentRecord.PersonLastName || '';
    if (firstName || lastName) {
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    if (currentRecord.Subject) return currentRecord.Subject;
    if (currentRecord.Title) return currentRecord.Title;
    // Don't show ID as name - show generic label
    return 'רשומה';
  };
  const displayName = getDisplayName();

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את הרשומה "${displayName}"?`)) return;

    setDeleting(true);
    try {
      const _delToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const response = await fetch(
        `${API_BASE}/api/objects/${object.api_name}/records/${currentRecord.Id}`,
        { method: 'DELETE', headers: _delToken ? { 'Authorization': `Bearer ${_delToken}` } : {} }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(recordAccessErrorMessage(errData.detail)
          || (typeof errData.detail === 'string' ? errData.detail : '') || 'שגיאה במחיקה');
      }

      // 2026-06-17 owner-spec: when the user deletes the record itself
      // (not just a child row), every parent that listed this record in
      // its related lists is now stale. We don't know every parent
      // trivially (lookup fields vary by object), so drop the whole
      // related-records cache. It's a 50-150ms refetch on the user's
      // next navigation — acceptable for a delete-record action.
      _relatedRecordsCache.clear();
      // Also any in-flight related fetch for this record is irrelevant
      // since the record no longer exists.
      _pendingRelatedFetches.delete(`${object.api_name}/${currentRecord.Id}`);

      onRecordDelete?.();
      onBack();
    } catch (err) {
      alert(err.message);
      setDeleting(false);
    }
  };

  // === CREATE APPOINTMENT FROM WORK ORDER ===
  // Helper function to get next AutoNumber
  const getNextAutoNumber = async (objectName, fieldName, prefix, digits) => {
    try {
      const res = await fetch(`${API_BASE}/api/objects/${objectName}/records?limit=1&sortField=${fieldName}&sortOrder=desc&fields=${fieldName}`);
      if (res.ok) {
        const data = await res.json();
        const records = data.records || [];
        if (records.length > 0 && records[0][fieldName]) {
          const lastNum = records[0][fieldName];
          const numPart = lastNum.replace(prefix, '');
          const num = parseInt(numPart) + 1;
          return `${prefix}${String(num).padStart(digits, '0')}`;
        }
      }
      return `${prefix}${'1'.padStart(digits, '0')}`;
    } catch (error) {
      console.error('Error getting auto number:', error);
      return `${prefix}${Date.now().toString().slice(-digits)}`;
    }
  };

  // Find service territory by coordinates using polygon (GeoJSON)
  const findServiceTerritoryByCoordinates = async (lat, lng, workTypeId) => {
    if (!lat || !lng) return null;

    try {
      // Get all service territories with GeoJSON
      const response = await fetch(`${API_BASE}/api/objects/ServiceTerritory/records?limit=500`);
      if (!response.ok) return null;

      const data = await response.json();
      const territories = data.records || [];

      // Filter territories that have GeoJSON and check if point is inside polygon
      for (const territory of territories) {
        const geoJson = territory.GeoJSON__c || territory.geojson__c;
        if (!geoJson) continue;

        try {
          const geo = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson;
          if (geo.type === 'Polygon' && geo.coordinates) {
            const polygon = geo.coordinates[0]; // First ring (outer boundary)
            if (isPointInPolygon(lat, lng, polygon)) {
              console.log('[CreateAppointment] Found territory by polygon:', territory.Name);
              return territory;
            }
          }
        } catch (e) {
          console.warn('[CreateAppointment] Error parsing GeoJSON for territory:', territory.Name, e);
        }
      }

      return null;
    } catch (error) {
      console.error('[CreateAppointment] Error finding territory:', error);
      return null;
    }
  };

  // Helper: Check if point is inside polygon (ray casting algorithm)
  const isPointInPolygon = (lat, lng, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];

      const intersect = ((yi > lat) !== (yj > lat)) &&
        (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Handle create appointment from WorkOrder button click
  const handleCreateAppointmentFromWorkOrder = async () => {
    if (!currentRecord?.Id) return;

    setCreateAppointmentLoading(true);
    setCreateAppointmentResult(null);

    try {
      const _woFlowToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const _woFlowHeaders = { 'Content-Type': 'application/json', ...(_woFlowToken ? { 'Authorization': `Bearer ${_woFlowToken}` } : {}) };
      const workOrder = currentRecord;
      console.log('[CreateAppointment] Creating from WorkOrder:', workOrder.WorkOrderNumber || workOrder.Id);

      // Get account data if we have AccountId
      let account = null;
      if (workOrder.AccountId) {
        const accountRes = await fetch(`${API_BASE}/api/objects/Account/records/${workOrder.AccountId}`);
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          account = accountData.record || accountData;
        }
      }

      // Get WorkType for duration
      let workType = null;
      if (workOrder.WorkTypeId) {
        const wtRes = await fetch(`${API_BASE}/api/objects/WorkType/records/${workOrder.WorkTypeId}`);
        if (wtRes.ok) {
          const wtData = await wtRes.json();
          workType = wtData.record || wtData;
        }
      }

      // Get coordinates from WorkOrder or Account
      const lat = workOrder.Latitude || workOrder.latitude || account?.BillingLatitude;
      const lng = workOrder.Longitude || workOrder.longitude || account?.BillingLongitude;

      // Find service territory by polygon
      const territory = await findServiceTerritoryByCoordinates(lat, lng, workOrder.WorkTypeId);
      console.log('[CreateAppointment] Found territory:', territory?.Name);

      // 2026-06-09 — AppointmentNumber assigned by backend HiLo on POST
      // response. The previous FE-side getNextAutoNumber was racy
      // (SELECT MAX+1 across concurrent calls produced duplicates).
      let appointmentNumber = '';

      // Build address
      const addressParts = [];
      if (workOrder.Street || account?.BillingStreet) addressParts.push(workOrder.Street || account?.BillingStreet);
      if (workOrder.City || account?.BillingCity) addressParts.push(workOrder.City || account?.BillingCity);
      const fullAddress = addressParts.join(', ');

      // Calculate duration
      const duration = parseInt(workType?.EstimatedDuration) || 60;

      // Build subject from WorkOrder — no number suffix (it's on the field).
      const subject = workOrder.Subject || workType?.Name || 'פעילות שירות';

      // Create ServiceAppointment payload (AppointmentNumber omitted)
      const saPayload = {
        ParentRecordId: workOrder.Id,
        AccountId: workOrder.AccountId || '',
        WorkTypeId: workOrder.WorkTypeId || '',
        Status: 'None',
        Subject: subject,
        EarliestStartTime: new Date().toISOString(),
        Duration: duration,
        DurationType: workType?.DurationType || 'Minutes',
        DurationInMinutes: parseInt(workType?.DurationInMinutes) || duration,
        Street: workOrder.Street || account?.BillingStreet || '',
        City: workOrder.City || account?.BillingCity || '',
        State: workOrder.State || account?.BillingState || '',
        PostalCode: workOrder.PostalCode || account?.BillingPostalCode || '',
        Country: workOrder.Country || account?.BillingCountry || 'Israel',
        Latitude: lat || '',
        Longitude: lng || '',
        Address: fullAddress,
        Floor__c: workOrder.Floor__c || account?.Floor__c || '',
        Entrance__c: workOrder.Entrance__c || account?.Entrance__c || '',
        Apartment_number__c: workOrder.Apartment_number__c || account?.Apartment_number__c || '',
        ServiceTerritoryId: territory?.Id || '',
      };

      console.log('[CreateAppointment] Creating ServiceAppointment:', saPayload);

      const saRes = await fetch(`${API_BASE}/api/objects/ServiceAppointment/records`, {
        method: 'POST',
        headers: _woFlowHeaders,
        body: JSON.stringify(saPayload)
      });

      if (!saRes.ok) {
        const errorText = await saRes.text();
        throw new Error(`שגיאה ביצירת פעילות שירות: ${errorText}`);
      }

      const saResult = await saRes.json();
      const serviceAppointment = saResult.record || saResult;
      // Backend assigned AppointmentNumber via HiLo — pick it up for the success screen.
      appointmentNumber = serviceAppointment.AppointmentNumber || '';
      console.log('[CreateAppointment] ServiceAppointment created:', serviceAppointment.Id, 'AppointmentNumber=', appointmentNumber);

      // Show success result
      setCreateAppointmentResult({
        success: true,
        appointmentId: serviceAppointment.Id,
        appointmentNumber: appointmentNumber,
        territoryName: territory?.Name || 'לא נמצא',
        message: `פעילות שירות ${appointmentNumber} נוצרה בהצלחה!`
      });

      setShowCreateAppointmentModal(true);

    } catch (error) {
      console.error('[CreateAppointment] Error:', error);
      setCreateAppointmentResult({
        success: false,
        message: error.message || 'שגיאה ביצירת פעילות שירות'
      });
      setShowCreateAppointmentModal(true);
    } finally {
      setCreateAppointmentLoading(false);
    }
  };

  // Start editing — and remember the record as it was, so save sends only what the user
  // changed (utils/recordDiff.js), never stale values over a change made meanwhile.
  const handleStartEdit = () => {
    // 2026-10-11 (owner): work types are edited by a system admin only (the server refuses the rest)
    if (isAdminOnlyObject(object?.api_name) && !permIsAdmin(currentUser)) {
      alert(ADMIN_ONLY_OBJECT_MESSAGE);
      return;
    }
    if (taskEditLocked) {
      alert(TASK_LOCKED_MESSAGE);
      return;
    }
    if (recordEditDenied) {
      alert('אין הרשאה לערוך רשומות מסוג זה מתוך המסך');
      return;
    }
    editBaselineRef.current = { ...currentRecord };
    setEditedRecord({...currentRecord});
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditedRecord({...currentRecord});
    setIsEditing(false);
  };

  // Save inline edits
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // Only the fields the user changed since editing started. Sending the whole record
      // overwrote changes made meanwhile — scheduling undone by a stale Status / technician
      // (SA-769715, SA-800115; utils/recordDiff.js).
      const dataToSave = changedFields(editBaselineRef.current || currentRecord, editedRecord);
      if (Object.keys(dataToSave).length === 0) {
        setIsEditing(false);
        setSaving(false);
        return;
      }
      const currentUser = recordService.getCurrentUser();
      if (currentUser) {
        const userId = currentUser.id || currentUser.Id || currentUser.email;
        const userName = currentUser.name || currentUser.Name || currentUser.email;
        dataToSave.LastModifiedById = userId;
        dataToSave.LastModifiedByName = userName;
      }

      const response = await fetch(
        `${API_BASE}/api/objects/${object.api_name}/records/${currentRecord.Id}`,
        {
          method: 'PUT',
          // the session, so the server's per-object gates (e.g. admin-only work types) see the caller;
          // the edit-source header scopes "read-only on the record screen" to this save
          headers: { 'Content-Type': 'application/json', ...authHeaders(), ...RECORD_SCREEN_EDIT_HEADERS },
          body: JSON.stringify(dataToSave)
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        // 2026-05-14: surface the BE field-edit gate message (P0.9 /
        // field-permissions) cleanly when 403 with structured detail.
        const detail = errData?.detail;
        const accessMsg = recordAccessErrorMessage(detail, formatFieldLabel);
        if (accessMsg) throw new Error(accessMsg);
        // 2026-05-19: validation errors — expand each error with the field
        // label (Hebrew) + the actual value the user has in that field, so
        // they see WHY the rule rejected the record (not just a generic
        // "Validation failed"). Field name is taken from error_field when
        // present, otherwise derived from rule_name like floor_validation
        // → Floor__c via a case-insensitive lookup on object.fields.
        if (detail && typeof detail === 'object' && detail.type === 'validation_error' && Array.isArray(detail.errors)) {
          const fieldsMeta = object?.fields || [];
          const lines = detail.errors.map((e) => {
            let fieldName = e.error_field || '';
            if (!fieldName && e.rule_name) {
              const base = String(e.rule_name).replace(/_validation$/i, '').replace(/_rule$/i, '').toLowerCase();
              const match = fieldsMeta.find((f) => {
                const n = String(f.name || '').toLowerCase().replace(/__c$/, '');
                return n === base;
              });
              if (match) fieldName = match.name;
            }
            const fieldDef = fieldsMeta.find((f) => f.name === fieldName);
            const label = fieldDef?.label || fieldName || '';
            const currentValue = fieldName ? editedRecord?.[fieldName] : undefined;
            const parts = [];
            if (label) parts.push(`שדה: ${label}`);
            if (currentValue !== undefined && currentValue !== '' && currentValue !== null) {
              parts.push(`הערך בשדה: ${currentValue}`);
            }
            if (e.error_message) parts.push(e.error_message);
            return parts.join('\n');
          });
          throw new Error('שגיאת אימות:\n\n' + lines.join('\n\n'));
        }
        // 2026-06-24: backend guards (e.g. PR #1092/#1097 Israeli ID checksum)
        // return { detail: { success:false, field, error, value } }. Older paths
        // use detail.message or a plain string detail. Surface whichever is
        // present so users see "תעודת זהות לא תקינה" instead of "שגיאה בשמירה".
        const _serverMsg = typeof detail === 'string'
          ? detail
          : (detail?.error || detail?.message || '');
        const _fieldPrefix = (detail && typeof detail === 'object' && detail.field)
          ? `שדה ${detail.field}: `
          : '';
        throw new Error((_fieldPrefix + _serverMsg) || 'שגיאה בשמירה');
      }

      const result = await response.json();
      // The server's record is the truth; without one, apply only what was sent on top of
      // the current record — not the whole edit buffer, which may be stale.
      const updatedRecord = result.record || { ...currentRecord, ...dataToSave };
      setCurrentRecord(updatedRecord);
      setEditedRecord(updatedRecord);
      setIsEditing(false);
      onRecordUpdate?.(updatedRecord);
    } catch (err) {
      alert(err.message);
    }
    setSaving(false);
  };

  // Handle field change during inline edit
  const handleFieldChange = (fieldName, value) => {
    setEditedRecord(prev => ({ ...prev, [fieldName]: value }));
  };

  // Handle custom button click
  const handleCustomButtonClick = (button) => {
    switch (button.type) {
      case 'link':
        // Open URL in new window or current based on behavior
        const url = button.url?.replace('{!Id}', currentRecord.Id)
          .replace('{!Name}', encodeURIComponent(currentRecord.Name || ''))
          .replace('{!AccountId}', currentRecord.AccountId || '');
        if (button.behavior === 'new_window') {
          window.open(url, '_blank');
        } else {
          window.location.href = url;
        }
        break;
      case 'button':
      case 'action':
        // Show modal or execute action
        if (button.behavior === 'modal' || button.behavior === 'inline') {
          setActiveCustomAction(button);
        } else if (button.behavior === 'javascript') {
          // Execute JavaScript (safely)
          try {
            // eslint-disable-next-line no-new-func
            const fn = new Function('record', 'API_BASE', button.javascript_code || '');
            fn(currentRecord, API_BASE);
          } catch (err) {
            console.error('Error executing button JavaScript:', err);
            alert('שגיאה בהרצת הפעולה');
          }
        }
        break;
      case 'lwc':
      case 'vf':
      case 'component':
        // Show LWC, Custom Page or custom component in modal
        setActiveCustomAction(button);
        break;
      case 'form_template':
        // Show form template modal
        if (button.form_template_id) {
          setActiveFormTemplate({
            templateId: button.form_template_id,
            objectName: object?.api_name,
            recordId: currentRecord.Id,
            recordName: currentRecord.Name || currentRecord.Subject || currentRecord.Id
          });
        } else {
          console.warn('Form template button missing form_template_id');
          alert('כפתור זה לא מוגדר כראוי - חסר תבנית טופס');
        }
        break;
      default:
        console.warn('Unknown button type:', button.type);
    }
  };

  // Get button style based on variant
  const getButtonStyle = (button) => {
    const baseStyle = {};
    switch (button.variant) {
      case 'brand':
        return { ...baseStyle, background: 'linear-gradient(135deg, #0070d2 0%, #1b5297 100%)', color: 'white', border: 'none' };
      case 'success':
        return { ...baseStyle, background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', color: 'white', border: 'none' };
      case 'destructive':
        return { ...baseStyle, background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)', color: 'white', border: 'none' };
      case 'outline':
        return { ...baseStyle, background: 'transparent', color: '#0070d2', border: '1px solid #0070d2' };
      default:
        return baseStyle;
    }
  };

  // Evaluate visibility rule for Record Page components
  const evaluateVisibilityRule = (rule, record) => {
    if (!rule || !rule.criteria || rule.criteria.length === 0) return true;

    const evaluateCriterion = (crit) => {
      let leftValue = crit.leftValue || '';

      // החלף {!Record.FieldName} בערך האמיתי
      if (leftValue.startsWith('{!Record.')) {
        const fieldName = leftValue.replace('{!Record.', '').replace('}', '');
        leftValue = record[fieldName];
      } else if (leftValue.startsWith('{!$User.')) {
        // ערכי משתמש - נחזיר null בינתיים (צריך להטמיע)
        leftValue = null;
      }

      const rightValue = crit.rightValue;
      const operator = crit.operator;

      switch (operator) {
        case 'EQUAL':
          if (typeof leftValue === 'boolean') {
            return leftValue === (rightValue === 'true');
          }
          return String(leftValue || '') === String(rightValue || '');
        case 'NOT_EQUAL':
          return String(leftValue || '') !== String(rightValue || '');
        case 'CONTAINS':
          return String(leftValue || '').includes(String(rightValue || ''));
        case 'STARTS_WITH':
          return String(leftValue || '').startsWith(String(rightValue || ''));
        case 'IS_NULL':
          return leftValue === null || leftValue === undefined || leftValue === '';
        case 'IS_NOT_NULL':
          return leftValue !== null && leftValue !== undefined && leftValue !== '';
        default:
          return true;
      }
    };

    // אם יש booleanFilter, השתמש בו
    if (rule.booleanFilter) {
      const results = rule.criteria.map((_, idx) => evaluateCriterion(rule.criteria[idx]));
      // פרסר פשוט ל-booleanFilter כמו "1 OR 2 OR 3" או "1 AND 2"
      let expression = rule.booleanFilter;
      results.forEach((result, idx) => {
        expression = expression.replace(new RegExp(`\\b${idx + 1}\\b`, 'g'), result ? 'true' : 'false');
      });
      expression = expression.replace(/\bOR\b/g, '||').replace(/\bAND\b/g, '&&');
      try {
        // eslint-disable-next-line no-eval
        return eval(expression);
      } catch {
        return true;
      }
    }

    // ברירת מחדל - AND בין כל הקריטריונים
    return rule.criteria.every(evaluateCriterion);
  };

  // Render custom Record Page component
  const renderCustomComponent = (comp, record) => {
    const componentName = comp.componentName || '';
    const props = comp.properties || {};

    // Toast/PopUp component (S360_LN:PopUp)
    if (componentName === 'S360_LN:PopUp' || componentName.includes('PopUp')) {
      const toastType = props.toastType || 'Info';
      const title = props.title || '';
      const message = props.message || '';
      const toastMode = props.toastMode || 'Dismissible';

      const toastStyles = {
        Error: { bg: '#fee2e2', border: '#f87171', icon: '❌' },
        Warning: { bg: '#fef3c7', border: '#fbbf24', icon: '⚠️' },
        Success: { bg: '#d1fae5', border: '#34d399', icon: '✅' },
        Info: { bg: '#dbeafe', border: '#60a5fa', icon: 'ℹ️' }
      };
      const style = toastStyles[toastType] || toastStyles.Info;

      return (
        <div style={{
          background: style.bg,
          border: `2px solid ${style.border}`,
          borderRadius: '8px',
          padding: '16px',
          margin: '8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>{style.icon}</span>
          <div>
            {title && <strong style={{ display: 'block', marginBottom: '4px' }}>{title}</strong>}
            {message && <span>{message}</span>}
          </div>
          {toastMode === 'Dismissible' && (
            <button style={{ marginRight: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>×</button>
          )}
        </div>
      );
    }

    // Activity Panel - already handled by built-in tabs, suppress duplicate
    if (componentName === 'runtime_sales_activities:activityPanel') {
      return null;
    }

    // internal chat Feed - already handled by built-in tabs, suppress duplicate
    if (componentName === 'forceChatter:recordFeedContainer') {
      return null;
    }

    // Merge Candidates Preview
    if (componentName === 'runtime_sales_merge:mergeCandidatesPreviewCard') {
      return null;
    }

    // Related List - already handled by sidebar, suppress duplicate
    if (componentName.includes('relatedList')) {
      return null;
    }

    // Unknown components - suppress to avoid showing raw component names
    return null;
  };

  // State for lookup search
  const [lookupSearch, setLookupSearch] = useState({});
  const [lookupResults, setLookupResults] = useState({});
  const [lookupLoading, setLookupLoading] = useState({});
  const [showLookupDropdown, setShowLookupDropdown] = useState({});

  // Search for lookup records - filter by related Account/Contact if applicable
  const searchLookupRecords = async (fieldName, targetObject, searchTerm) => {
    if (!searchTerm || searchTerm.length < 1) {
      setLookupResults(prev => ({ ...prev, [fieldName]: [] }));
      return;
    }

    setLookupLoading(prev => ({ ...prev, [fieldName]: true }));
    try {
      // Build the base URL
      let url = `${API_BASE}/api/objects/${targetObject}/records?search=${encodeURIComponent(searchTerm)}&limit=20`;

      // If we're looking for related records and have an Account/Contact context, filter by it
      const accountId = currentRecord?.AccountId || currentRecord?.Account__c;
      const contactId = currentRecord?.ContactId || currentRecord?.Contact__c;

      // For objects that should be filtered by Account
      const accountFilterObjects = ['Contact', 'Opportunity', 'Case', 'Asset', 'ServiceAppointment', 'WorkOrder', 'ServiceContract'];
      // For objects that should be filtered by Contact
      const contactFilterObjects = ['Case', 'Opportunity'];

      // Add Account filter if applicable
      if (accountId && accountFilterObjects.includes(targetObject)) {
        url += `&AccountId=${accountId}`;
      }
      // Add Contact filter if applicable
      else if (contactId && contactFilterObjects.includes(targetObject)) {
        url += `&ContactId=${contactId}`;
      }

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        let results = data.records || [];

        // If we got filtered results, show them first, then add unfiltered results
        if ((accountId || contactId) && results.length < 5) {
          // Get additional unfiltered results
          const unfilteredRes = await fetch(`${API_BASE}/api/objects/${targetObject}/records?search=${encodeURIComponent(searchTerm)}&limit=10`);
          if (unfilteredRes.ok) {
            const unfilteredData = await unfilteredRes.json();
            const unfilteredRecords = (unfilteredData.records || []).filter(
              rec => !results.some(r => r.Id === rec.Id)
            );
            results = [...results, ...unfilteredRecords].slice(0, 15);
          }
        }

        setLookupResults(prev => ({ ...prev, [fieldName]: results }));
      }
    } catch (err) {
      console.error('Error searching lookup:', err);
    }
    setLookupLoading(prev => ({ ...prev, [fieldName]: false }));
  };

  // Select a lookup record
  const selectLookupRecord = (fieldName, record, targetObject) => {
    // Update the ID field
    handleFieldChange(fieldName, record.Id);
    // Also update the Name field if it exists
    const nameField = fieldName.replace('Id', '__Name');
    if (nameField !== fieldName) {
      handleFieldChange(nameField, record.Name || record.Id);
    }
    // Update the __Target field if needed
    const targetField = fieldName.replace('Id', 'Id__Target');
    handleFieldChange(targetField, targetObject);

    setShowLookupDropdown(prev => ({ ...prev, [fieldName]: false }));
    setLookupSearch(prev => ({ ...prev, [fieldName]: record.Name || record.Id }));
  };

  // Render edit field based on field type
  const renderEditField = (fieldName, editValue, fieldMeta) => {
    // Address fields: render the shared <AddressAutocompleteWithMap> when the
    // primary billing-street field is being edited so users get the same
    // autocomplete + Google Map experience as the create-account flow
    // (Task 2 fix, 2026-04-27). Selecting an address atomically updates 7
    // billing fields. Other address sub-fields (BillingCity, BillingPostalCode,
    // etc.) continue to render as plain text inputs below and remain
    // manually editable.
    if (fieldName === 'BillingStreet' || fieldName === 'BillingAddress') {
      return (
        <div>
          <AddressAutocompleteWithMap
            initialLat={editedRecord?.BillingLatitude}
            initialLng={editedRecord?.BillingLongitude}
            onAddressSelected={(addr) => {
              setEditedRecord(prev => ({
                ...prev,
                // Primary billing
                BillingStreet: addr.street || prev.BillingStreet,
                BillingCity: addr.city || prev.BillingCity,
                BillingState: addr.state || prev.BillingState,
                BillingPostalCode: addr.postalCode || prev.BillingPostalCode,
                BillingCountry: addr.country || prev.BillingCountry,
                BillingLatitude: addr.lat,
                BillingLongitude: addr.lng,
                // Shipping mirrors billing on a fresh pick (only when source has a value)
                ShippingStreet: addr.street || prev.ShippingStreet,
                ShippingCity: addr.city || prev.ShippingCity,
                ShippingLatitude: addr.lat ?? prev.ShippingLatitude,
                ShippingLongitude: addr.lng ?? prev.ShippingLongitude,
                // Invoice fields (adresse_fi__c, city_fi__c) are intentionally
                // NOT auto-filled here. They live behind the
                // "כתובת לחשבונית שונה מכתובת הלקוח" checkbox and only the
                // user should populate them when they actually want a
                // different invoice address.
              }));
            }}
            height={200}
          />
          <input
            type="text"
            className="slds-input slds-inline-edit-input"
            value={editValue}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            style={{ marginTop: '8px' }}
            placeholder="או ערוך כתובת ידנית"
          />
        </div>
      );
    }

    // ------------------------------------------------------------------
    // Generic lookup detection (fires BEFORE the !fieldMeta early-return
    // and BEFORE the metadata-driven Lookup branch below).
    //
    // The original Lookup branch required `fieldMeta.referenceTo`, but
    // standard SF FKs (AccountId, Product2Id, ContactId, OwnerId, …)
    // aren't in our XML metadata at all (fieldMeta missing) and the few
    // that are don't carry referenceTo. Result: those fields fell
    // through to a plain text input and the user saw raw IDs like
    // '0015EZVQAU11AVG' / '02iPBHAQUWTXDEK'.
    //
    // Resolution order:
    //   1) fieldMeta.referenceTo            (XML metadata, when present)
    //   2) currentRecord[<field>__Target]   (backend resolve_refs hint)
    //   3) STD_FK[fieldName]                (well-known std SF FKs)
    //   4) CUSTOM_FK[fieldName]             (common __c lookups in this org)
    // ------------------------------------------------------------------
    const STD_FK = {
      AccountId: 'Account', ContactId: 'Contact', Product2Id: 'Product2',
      OwnerId: 'User', CreatedById: 'User', LastModifiedById: 'User',
      LocationId: 'Location', CampaignId: 'Campaign', CaseId: 'Case',
      LeadId: 'Lead', WorkOrderId: 'WorkOrder', AssetProvidedById: 'User',
      AssetServicedById: 'User', ParentId: object?.api_name,
      RootAssetId: 'Asset', AccountRollupId: 'Account',
      ServiceContractId: 'ServiceContract', EntitlementId: 'Entitlement',
      ParentRecordId: 'WorkOrder', ServiceAppointmentId: 'ServiceAppointment',
      AssetId: 'Asset',
    };
    const CUSTOM_FK = {
      Asset__c: 'Asset',
      Opportunity__c: 'Opportunity',
      Service_Contract__c: 'ServiceContract',
      Service_Appointment__c: 'ServiceAppointment',
      Work_Order__c: 'WorkOrder',
      Reseller__c: 'Account',
      Supplier__c: 'Account',
      Supplier_New__c: 'Account',
      Installed_Model__c: 'Product2',
      Contact_Lab__c: 'Contact',
      product__c: 'Product2',
      Parent_Opportunity__c: 'Opportunity',
    };
    const looksLikeLookup =
      (fieldName.endsWith('Id') && fieldName !== 'Id') ||
      fieldName.endsWith('__c');
    if (looksLikeLookup) {
      const lookupTarget =
        fieldMeta?.referenceTo ||
        currentRecord?.[`${fieldName}__Target`] ||
        STD_FK[fieldName] ||
        CUSTOM_FK[fieldName] ||
        null;
      if (lookupTarget) {
        const strippedId =
          fieldName.endsWith('Id') && fieldName !== 'Id'
            ? fieldName.slice(0, -2)
            : null;
        const displayValue =
          (strippedId && currentRecord?.[`${strippedId}__Name`]) ||
          currentRecord?.[`${fieldName}__Name`] ||
          '';
        return (
          <LookupField
            object={lookupTarget}
            value={editValue || ''}
            displayValue={displayValue}
            onChange={(id, name) => {
              handleFieldChange(fieldName, id);
              if (strippedId) {
                handleFieldChange(`${strippedId}__Name`, name);
              } else {
                handleFieldChange(`${fieldName}__Name`, name);
              }
            }}
            placeholder={`חפש ${fieldMeta?.label || fieldName}...`}
          />
        );
      }
    }

    if (!fieldMeta) {
      // No metadata - render simple text input
      return (
        <input
          type="text"
          className="slds-input slds-inline-edit-input"
          value={editValue}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        />
      );
    }

    const fieldType = fieldMeta.type;

    // Picklist field - render select dropdown
    if (fieldType === 'Picklist' && fieldMeta.picklistValues && fieldMeta.picklistValues.length > 0) {
      // Translation map for picklist values
      const picklistTranslations = {
        'Installed': 'הותקן',
        'Not Active': 'לא פעיל',
        'On the Lab': 'בטיפול במעבדה',
        'Send to Lab': 'נשלח למעבדה',
        'Ready for Resource': 'מוכן לטכנאי',
        'Customer': 'לקוח',
        'Partner': 'שותף',
        'Prospect': 'פוטנציאלי',
        'Active': 'פעיל',
        'Inactive': 'לא פעיל',
        'Pending': 'ממתין',
        'New': 'חדש',
        'Open': 'פתוח',
        'Closed': 'סגור',
        'High': 'גבוה',
        'Medium': 'בינוני',
        'Low': 'נמוך',
        'Yes': 'כן',
        'No': 'לא',
      };
      return (
        <select
          className="slds-select slds-inline-edit-input"
          value={editValue}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        >
          <option value="">-- בחר --</option>
          {fieldMeta.picklistValues.map((val, idx) => {
            const apiVal = typeof val === 'object' ? (val.value || val) : val;
            const display = (typeof val === 'object' && val.label)
              || getHebrewPicklistValue(object.api_name, fieldName, apiVal)
              || picklistTranslations[apiVal]
              || apiVal;
            return <option key={idx} value={apiVal}>{display}</option>;
          })}
        </select>
      );
    }

    // Lookup field - render search input with dropdown
    if (fieldType === 'Lookup' && fieldMeta.referenceTo) {
      const targetObject = fieldMeta.referenceTo;

      // Get display name from __Name field instead of showing ID
      let displayName = editValue;
      if (editValue && looksLikeUniqueId(editValue)) {
        // Try to get name from __Name field in the record
        const nameField = fieldName.replace(/Id$/, '__Name');
        const nameFromRecord = currentRecord?.[nameField];
        if (nameFromRecord && !looksLikeUniqueId(nameFromRecord)) {
          displayName = nameFromRecord;
        } else {
          // Try Person Account fallback: use FirstName + LastName
          const first = currentRecord?.FirstName || '';
          const last = currentRecord?.LastName || '';
          const combined = `${first} ${last}`.trim();
          if (combined) {
            displayName = combined;
          } else {
            // Never show raw IDs - show empty placeholder
            displayName = '';
          }
        }
      }

      const searchValue = lookupSearch[fieldName] !== undefined ? lookupSearch[fieldName] : displayName;
      const results = lookupResults[fieldName] || [];
      const isLoading = lookupLoading[fieldName];
      const showDropdown = showLookupDropdown[fieldName];

      return (
        <div className="slds-lookup-container" style={{ position: 'relative' }}>
          <div className="slds-lookup-input-container" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="text"
              className="slds-input slds-inline-edit-input"
              value={searchValue}
              placeholder={`חפש ב-${targetObject}...`}
              onChange={(e) => {
                setLookupSearch(prev => ({ ...prev, [fieldName]: e.target.value }));
                setShowLookupDropdown(prev => ({ ...prev, [fieldName]: true }));
                searchLookupRecords(fieldName, targetObject, e.target.value);
              }}
              onFocus={() => {
                setShowLookupDropdown(prev => ({ ...prev, [fieldName]: true }));
                if (searchValue) {
                  searchLookupRecords(fieldName, targetObject, searchValue);
                }
              }}
              onBlur={() => {
                // Delay hiding dropdown to allow click on results
                setTimeout(() => {
                  setShowLookupDropdown(prev => ({ ...prev, [fieldName]: false }));
                }, 200);
              }}
            />
            <span style={{ color: '#706e6b', fontSize: '11px' }}>🔔 {targetObject}</span>
          </div>
          {showDropdown && (results.length > 0 || isLoading) && (
            <div className="slds-lookup-dropdown" style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #dddbda',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {isLoading && (
                <div style={{ padding: '8px', color: '#706e6b', textAlign: 'center' }}>טוען...</div>
              )}
              {!isLoading && results.map((rec, idx) => (
                <div
                  key={idx}
                  className="slds-lookup-option"
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f2f2'
                  }}
                  onMouseDown={() => selectLookupRecord(fieldName, rec, targetObject)}
                  onMouseOver={(e) => e.currentTarget.style.background = '#f3f2f2'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: '500' }}>{rec.Name || rec.Id}</div>
                  {rec.Id && <div style={{ fontSize: '11px', color: '#706e6b' }}>{rec.Id}</div>}
                </div>
              ))}
              {!isLoading && results.length === 0 && searchValue && (
                <div style={{ padding: '8px', color: '#706e6b', textAlign: 'center' }}>לא נמצאו תוצאות</div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Checkbox/Boolean field
    if (fieldType === 'Checkbox' || fieldType === 'boolean') {
      return (
        <input
          type="checkbox"
          className="slds-checkbox"
          checked={editValue === true || editValue === 'true'}
          onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
        />
      );
    }

    // Date field - also check by field name pattern for dates that might be typed as text
    const dateFieldNames = ['InstallDate', 'PurchaseDate', 'UsageEndDate', 'LifecycleStartDate', 'LifecycleEndDate',
                            'StartDate', 'EndDate', 'CloseDate', 'BirthDate', 'ContractStartDate', 'ContractEndDate',
                            'ActivationDate', 'ExpirationDate', 'WarrantyStartDate', 'WarrantyEndDate'];
    const isDateByName = dateFieldNames.includes(fieldName) ||
                         fieldName?.toLowerCase().includes('date') && !fieldName?.toLowerCase().includes('datetime');
    if (fieldType === 'Date' || isDateByName) {
      return (
        <input
          type="date"
          className="slds-input slds-inline-edit-input"
          value={editValue || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        />
      );
    }

    // DateTime field
    if (fieldType === 'DateTime') {
      return (
        <input
          type="datetime-local"
          className="slds-input slds-inline-edit-input"
          value={editValue || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        />
      );
    }

    // Number/Currency field
    if (fieldType === 'Number' || fieldType === 'Currency' || fieldType === 'Percent') {
      return (
        <input
          type="number"
          className="slds-input slds-inline-edit-input"
          value={editValue || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        />
      );
    }

    // TextArea field
    if (fieldType === 'TextArea' || fieldType === 'LongTextArea') {
      return (
        <textarea
          className="slds-textarea slds-inline-edit-input"
          value={editValue || ''}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          rows={3}
        />
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        className="slds-input slds-inline-edit-input"
        value={editValue}
        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
      />
    );
  };

  // Handle save from edit modal
  const handleEditSave = async (updatedRecord) => {
    setCurrentRecord(updatedRecord);
    setEditedRecord(updatedRecord);
    setShowEditModal(false);
    onRecordUpdate?.(updatedRecord);
  };

  // Helper לפורמט שם שדה - מעדיף תרגומים עבריים מהמילון שלנו
  const formatFieldLabel = (fieldName) => {
    if (!fieldName) return '';
    // Clean up field name for lookup
    const cleanName = fieldName
      .replace(/__Name$/g, '')
      .replace(/__Target$/g, '')
      .replace(/__c$/g, '');

    // 1. Try Hebrew labels from getFieldLabel FIRST (our static dictionary)
    if (getFieldLabel) {
      // Try exact match first
      let heLabel = getFieldLabel(fieldName);
      if (heLabel && heLabel !== fieldName) return heLabel;
      // Try clean name
      heLabel = getFieldLabel(cleanName);
      if (heLabel && heLabel !== cleanName) return heLabel;
      // Try UPPERCASE version (for labels like "APARTMENT NUMBER")
      heLabel = getFieldLabel(fieldName.toUpperCase());
      if (heLabel && heLabel !== fieldName.toUpperCase()) return heLabel;
      // Try with spaces replaced by underscores
      heLabel = getFieldLabel(fieldName.replace(/ /g, '_'));
      if (heLabel && heLabel !== fieldName.replace(/ /g, '_')) return heLabel;
      // Try Title Case to CamelCase (e.g., "Apartment Number" -> "ApartmentNumber__c")
      const camelCase = fieldName.replace(/ /g, '') + '__c';
      heLabel = getFieldLabel(camelCase);
      if (heLabel && heLabel !== camelCase) return heLabel;
      // Try with Id suffix for reference fields
      if (fieldName.endsWith('__Name')) {
        heLabel = getFieldLabel(fieldName.replace('__Name', 'Id'));
        if (heLabel && heLabel !== fieldName.replace('__Name', 'Id')) return heLabel;
      }
      // Try with __c suffix
      heLabel = getFieldLabel(cleanName + '__c');
      if (heLabel && heLabel !== cleanName + '__c') return heLabel;
    }

    // 2. Try field metadata label (from legacy CRM XML) - but also check if we have translation for the label
    if (objectFields && objectFields.length > 0) {
      // Try exact match
      let fieldMeta = objectFields.find(f => f.name === fieldName);
      if (!fieldMeta) {
        // Try with __Name suffix removed and Id added
        const idFieldName = fieldName.replace('__Name', 'Id');
        fieldMeta = objectFields.find(f => f.name === idFieldName);
      }
      if (!fieldMeta) {
        // Try clean name with __c
        fieldMeta = objectFields.find(f => f.name === cleanName + '__c');
      }
      if (!fieldMeta) {
        // Try clean name
        fieldMeta = objectFields.find(f => f.name === cleanName);
      }
      // Use label if it exists
      if (fieldMeta?.label && fieldMeta.label !== fieldMeta.name) {
        // Check if the label itself has a Hebrew translation in our dictionary
        const labelTranslation = getFieldLabel ? getFieldLabel(fieldMeta.label) : null;
        if (labelTranslation && labelTranslation !== fieldMeta.label) {
          return labelTranslation;
        }
        // If label is already in Hebrew, return it
        if (/[\u0590-\u05FF]/.test(fieldMeta.label)) {
          return fieldMeta.label;
        }
        // Otherwise return the label (will be English)
        return fieldMeta.label;
      }
    }

    // 3. Fallback - format field name but keep Hebrew characters if present
    const formatted = fieldName
      .replace(/__c$/g, '')
      .replace(/__Name$/g, '')
      .replace(/__Target$/g, '')
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim();

    // If the result contains Hebrew, return as-is
    if (/[\u0590-\u05FF]/.test(formatted)) {
      return formatted;
    }

    return formatted;
  };

  // Get fields based on Layout or fallback
  const getFieldsToDisplay = () => {
    if (layout && layout.sections && layout.sections.length > 0) {
      // סדר שדות לפי Layout
      const orderedFields = [];
      for (const section of layout.sections) {
        for (const column of (section.columns || [])) {
          for (const item of column) {
            if (item.field && currentRecord.hasOwnProperty(item.field)) {
              orderedFields.push([item.field, currentRecord[item.field], item.behavior]);
            }
          }
        }
      }
      return orderedFields.length > 0 ? orderedFields : getFallbackFields();
    }
    return getFallbackFields();
  };

  const getFallbackFields = () => {
    // סינון שדות - העדפה לכמות על פני IDs
    return Object.entries(currentRecord).filter(([key, value]) => {
      // הסתר שדות מערכת
      if (['Id', 'attributes', 'IsDeleted'].includes(key)) return false;
      // הסתר שדות __Target
      if (key.endsWith('__Target')) return false;
      // הסתר שדות ID אם יש שדה __Name מתאים
      if (key.endsWith('Id') && key !== 'Id') {
        const nameField = key.replace('Id', '__Name');
        if (currentRecord[nameField]) return false;
      }
      // הסתר ערכים ריקים
      if (value === null || value === undefined || value === '') return false;
      return true;
    }).map(([key, value]) => [key, value, 'Edit']);
  };

  const fields = getFieldsToDisplay();

  // Split into two columns
  const leftFields = fields.filter((_, i) => i % 2 === 0);
  const rightFields = fields.filter((_, i) => i % 2 === 1);

  // מיפוי של אובייקטים קשורים
  const relatedObjectsMap = {
    'Account': [
      // משימות, אנשי קשר, הזדמנויות, נכסים, חוזים, הזמנות עבודה, קבצים
      { label: 'משימות', objectName: 'Task', icon: '✅', color: '#4BC076' },
      { label: 'אנשי קשר', objectName: 'Contact', icon: '👤', color: '#A094ED' },
      { label: 'הזדמנויות', objectName: 'Opportunity', icon: '💰', color: '#FCB95B' },
      { label: 'נכסים', objectName: 'Asset', icon: '📦', color: '#54698D' },
      { label: 'חוזים', objectName: 'Contract', icon: '📝', color: '#0176D3' },
      { label: 'הזמנות עבודה', objectName: 'WorkOrder', icon: '🔧', color: '#F49756' },
      { label: 'קבצים', objectName: 'ContentDocumentLink', icon: '📎', color: '#747474' },
    ],
    'Contact': [
      { label: 'משימות', objectName: 'Task', icon: '✅', color: '#4BC076' },
      { label: 'אירועים', objectName: 'Event', icon: '📅', color: '#F49756' },
      { label: 'מקרים', objectName: 'Case', icon: '📋', color: '#E770A2' },
      { label: 'הזדמנויות', objectName: 'Opportunity', icon: '💰', color: '#FCB95B' },
    ],
    'Opportunity': [
      // פריטי הזדמנות, הצעות מחיר, משימות, חוזים, הזמנות עבודה, תשלומים, קבצים
      { label: 'פריטי הזדמנות', objectName: 'OpportunityLineItem', icon: '📦', color: '#54698D' },
      { label: 'הצעות מחיר', objectName: 'Quote', icon: '📄', color: '#0176D3' },
      { label: 'משימות', objectName: 'Task', icon: '✅', color: '#4BC076' },
      { label: 'חוזים', objectName: 'Contract', icon: '📝', color: '#0176D3' },
      { label: 'הזמנות עבודה', objectName: 'WorkOrder', icon: '🔧', color: '#F49756' },
      { label: 'תשלומים', objectName: 'Payment__c', icon: '💳', color: '#4BC076' },
      { label: 'קבצים', objectName: 'ContentDocumentLink', icon: '📎', color: '#747474' },
    ],
    'Asset': [
      // משימות, הזמנות עבודה, היסטוריית מעבדה
      { label: 'משימות', objectName: 'Task', icon: '✅', color: '#4BC076' },
      { label: 'הזמנות עבודה', objectName: 'WorkOrder', icon: '🔧', color: '#F49756' },
      { label: 'היסטוריית מעבדה', objectName: 'LabRepair', icon: '🔬', color: '#0d7377' },
    ],
    'Case': [
      { label: 'משימות', objectName: 'Task', icon: '✅', color: '#4BC076' },
      { label: 'תגובות', objectName: 'CaseComment', icon: '💬', color: '#0176D3' },
      { label: 'אימיילים', objectName: 'EmailMessage', icon: '✉', color: '#F49756' },
    ],
    'WorkOrder': [
      { label: 'פריטי עבודה', objectName: 'WorkOrderLineItem', icon: '📋', color: '#0176D3' },
      { label: 'פעילות שירות', objectName: 'ServiceAppointment', icon: '📅', color: '#F49756' },
    ],
  };

  // Related lists - ONLY from Record Page definition + Attachments
  const getRelatedLists = () => {
    const lists = [];
    const addedObjects = new Set();

    // Get allowed related lists from Record Page - maintain their ORDER
    const flexipageRelatedLists = flexipage?.relatedLists || [];

    // Local mapping from apiName (plural/relationship) to objectName (singular)
    const apiToObjMap = {
      'OpportunityLineItems': 'OpportunityLineItem',
      'OpportunityContactRoles': 'OpportunityContactRole',
      'Payments__r': 'Payment__c',
      'Work_Orders__r': 'WorkOrder',
      'WorkOrders__r': 'WorkOrder',
      'Quotes': 'Quote',
      'Contacts': 'Contact',
      'Opportunities': 'Opportunity',
      'Cases': 'Case',
      'Tasks': 'Task',
      'Events': 'Event',
      'Assets': 'Asset',
      'Accounts': 'Account',
      'WorkOrderLineItems': 'WorkOrderLineItem',
      'ServiceAppointments': 'ServiceAppointment',
      'ServiceAppointments__r': 'ServiceAppointment',
      'Service_Appointments__r': 'ServiceAppointment',
      'AssignedResources': 'AssignedResource',
      'AssignedResources__r': 'AssignedResource',
      'Assigned_Resources__r': 'AssignedResource',
      'AttachedContentDocuments': 'ContentDocumentLink',
      'TechnicianWallets__r': 'TechnicianWallet__c',
      'Activity_Barak__r': 'Activity_Barak__c',
    };

    // Helper to get object name from related list config
    const getObjNameFromRL = (rl) => {
      // Try direct object name first
      if (rl.objectName) return rl.objectName;
      if (rl.relatedObject) return rl.relatedObject;
      // Then try to map from apiName
      if (rl.apiName) {
        return apiToObjMap[rl.apiName] || rl.apiName;
      }
      return null;
    };

    // Create order map from Record Page
    const flexipageOrder = new Map();
    flexipageRelatedLists.forEach((rl, index) => {
      const objName = getObjNameFromRL(rl);
      if (objName) flexipageOrder.set(objName, index);
    });

    const allowedObjects = new Set(flexipageRelatedLists.map(rl => getObjNameFromRL(rl)).filter(Boolean));

    // Always allow attachments
    allowedObjects.add('Attachment');
    allowedObjects.add('ContentDocumentLink');

    // 1. First add all Record Page related lists in ORDER (with or without records)
    flexipageRelatedLists.forEach(rl => {
      const objName = getObjNameFromRL(rl);
      if (objName && !addedObjects.has(objName)) {
        const config = getObjectConfig(objName);
        const records = relatedRecords[objName] || [];
        lists.push({
          label: config.heLabel || objName,
          objectName: objName,
          icon: config.icon || '📋',
          color: config.color || '#0176D3',
          recordCount: records?.length || 0,
          type: 'children',
          order: flexipageOrder.get(objName) ?? 999
        });
        addedObjects.add(objName);
      }
    });

    // 2. Add attachments if they exist (always allowed)
    ['Attachment', 'ContentDocumentLink'].forEach(objName => {
      const records = relatedRecords[objName];
      if (records?.length > 0 && !addedObjects.has(objName)) {
        const config = getObjectConfig(objName);
        lists.push({
          label: config.heLabel || objName,
          objectName: objName,
          icon: config.icon || '📋',
          color: config.color || '#0176D3',
          recordCount: records?.length || 0,
          type: 'children',
          order: 999
        });
        addedObjects.add(objName);
      }
    });

    // 2b. Add ServiceAppointmentHistory if it exists (always shown for SA records)
    if (relatedRecords['ServiceAppointmentHistory']?.length > 0 && !addedObjects.has('ServiceAppointmentHistory')) {
      const config = getObjectConfig('ServiceAppointmentHistory');
      lists.push({
        label: config.heLabel || 'היסטוריית פעילות שירות',
        objectName: 'ServiceAppointmentHistory',
        icon: config.icon || '📜',
        color: config.color || '#0176d3',
        recordCount: relatedRecords['ServiceAppointmentHistory'].length,
        type: 'children',
        order: 998
      });
      addedObjects.add('ServiceAppointmentHistory');
    }

    // 2c. «פעילויות ברק» — the previous system's activities on the customer (activity_barak_c); no Record Page lists them
    if (object?.api_name === 'Account' && relatedRecords['Activity_Barak__c']?.length > 0 && !addedObjects.has('Activity_Barak__c')) {
      lists.push({
        label: 'פעילויות ברק', objectName: 'Activity_Barak__c', icon: '🗂', color: '#9a3412',
        recordCount: relatedRecords['Activity_Barak__c'].length, type: 'children', order: 997,
      });
      addedObjects.add('Activity_Barak__c');
    }

    // 3. If no Record Page related lists configured, use relatedObjectsMap as fallback
    if (flexipageRelatedLists.length === 0) {
      // Check if we have a mapping for this object type
      const mappedRelatedObjects = relatedObjectsMap[object.api_name] || [];

      if (mappedRelatedObjects.length > 0) {
        // Use only the configured related objects from the map
        mappedRelatedObjects.forEach((mapping, index) => {
          const objName = mapping.objectName;
          const records = relatedRecords[objName];
          if (records?.length > 0 && !addedObjects.has(objName)) {
            lists.push({
              label: mapping.label,
              objectName: objName,
              icon: mapping.icon || '📋',
              color: mapping.color || '#0176D3',
              recordCount: records?.length || 0,
              type: 'children',
              order: index
            });
            addedObjects.add(objName);
          }
        });
      } else {
        // No mapping defined - show all related records (for objects not in the map)
        Object.entries(relatedRecords).forEach(([objName, records]) => {
          if (records?.length > 0 && !addedObjects.has(objName)) {
            const config = getObjectConfig(objName);
            lists.push({
              label: config.heLabel || objName,
              objectName: objName,
              icon: config.icon || '📋',
              color: config.color || '#0176D3',
              recordCount: records?.length || 0,
              type: 'children',
              order: 999
            });
            addedObjects.add(objName);
          }
        });
      }
    }

    // Sort by Record Page order
    lists.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

    return lists;
  };
  // ניהול הרשאות רשומות (PR 5): no related list for an object hidden from this user.
  const relatedLists = getRelatedLists().filter(l => !isObjectHidden(hiddenObjects, l.objectName));

  // פונקציה למציאת שם השדה שמקשר אובייקט קשור לאובייקט האב
  const getParentFieldName = (childObjectName, parentObjectName) => {
    // מיפוי של שדות קשר לפי סוג האובייקט
    const relationshipFields = {
      'Task': { 'Account': 'WhatId', 'Contact': 'WhoId', 'Opportunity': 'WhatId', 'Asset': 'WhatId', 'Case': 'WhatId' },
      'Contact': { 'Account': 'AccountId' },
      'Opportunity': { 'Account': 'AccountId' },
      'Asset': { 'Account': 'AccountId' },
      'Contract': { 'Account': 'AccountId', 'Opportunity': 'Opportunity__c' },
      'WorkOrder': { 'Account': 'AccountId', 'Opportunity': 'Opportunity__c', 'Asset': 'AssetId' },
      'OpportunityLineItem': { 'Opportunity': 'OpportunityId' },
      'Quote': { 'Opportunity': 'OpportunityId' },
      'Payment__c': { 'Opportunity': 'Opportunity__c' },
      'ContentDocumentLink': { 'Account': 'LinkedEntityId', 'Opportunity': 'LinkedEntityId', 'Asset': 'LinkedEntityId' },
      'Case': { 'Account': 'AccountId', 'Contact': 'ContactId' },
      'Event': { 'Account': 'WhatId', 'Contact': 'WhoId' },
      'WorkOrderLineItem': { 'WorkOrder': 'WorkOrderId' },
      'ServiceAppointment': { 'WorkOrder': 'ParentRecordId' },
      'EmailMessage': { 'Case': 'ParentId' },
    };

    const childRelations = relationshipFields[childObjectName];
    if (childRelations && childRelations[parentObjectName]) {
      return childRelations[parentObjectName];
    }

    // ברירת מחדל - נסה שם אובייקט + Id
    return `${parentObjectName}Id`;
  };

  // Open create modal for related list - auto-fills linked field
  const handleCreateRelatedRecord = async (childObjectName) => {
    const parentField = getParentFieldName(childObjectName, object.api_name);
    const parentId = currentRecord.Id;
    const parentName = currentRecord.Name || currentRecord.Subject || currentRecord.CaseNumber || currentRecord.WorkOrderNumber || currentRecord.AppointmentNumber || '';

    // Load fields for child object
    try {
      const res = await fetch(`${API_BASE}/api/objects/${childObjectName}/fields`);
      if (res.ok) {
        const data = await res.json();
        setRelatedCreateFields(data.fields || []);
      }
    } catch (err) {
      console.log('Error loading fields for related create:', err);
      setRelatedCreateFields([]);
    }

    setRelatedCreateModal({
      objectName: childObjectName,
      parentField,
      parentId,
      parentName,
    });
  };

  // Save handler for related create modal
  const handleRelatedCreateSave = (newRecord) => {
    if (newRecord && newRecord.Id && relatedCreateModal) {
      // Add to related records state
      setRelatedRecords(prev => {
        const current = prev[relatedCreateModal.objectName] || [];
        return { ...prev, [relatedCreateModal.objectName]: [newRecord, ...current] };
      });
    }
    setRelatedCreateModal(null);
    setRelatedCreateFields([]);
  };

  // פונקציה לבדיקה אם ערך נראה כמו מזהה ייחודי
  const looksLikeUniqueId = (value) => {
    if (!value || typeof value !== 'string') return false;
    // Pure alphanumeric IDs: 10-18 chars with both letters and digits
    if (/^[a-zA-Z0-9]{10,18}$/.test(value) && /[a-zA-Z]/.test(value) && /[0-9]/.test(value)) return true;
    // Hebrew-prefixed IDs: Hebrew chars + alphanumeric suffix (e.g., "הזדמנויותNZMM6LU5N6JC")
    if (/^[\u0590-\u05FF]+[a-zA-Z0-9]{6,18}$/.test(value)) return true;
    // legacy CRM IDs: 15-18 alphanumeric starting with 0
    if (/^0[a-zA-Z0-9]{14,17}$/.test(value)) return true;
    return false;
  };

  // פונקציה לרנדר ערך שדה - כולל לינקים לרשומות קשורות
  // displayAs: 'name' (default) or 'id' - for lookup fields, determines whether to show Name or ID
  const renderFieldValue = (fieldName, value, record, displayAs = 'name', noMap = false) => {
    // Save original ID value for lookup fields before any modifications
    const originalValue = value;

    // A date-time value is shown in Israel time, whatever shape the server stored it in
    // (utils/israelTime.js). A date alone, or anything that is not a date-time, falls
    // through unchanged.
    if (fieldName && /Date|Time|Modstamp/.test(fieldName)) {
      const _il = formatIsraelDateTime(value);
      if (_il) return _il;
    }

    // Special handling for OwnerId - show owner name
    if (fieldName === 'OwnerId' && value) {
      // 2026-05-14: small reassign icon next to the owner name —
      // clickable on EVERY object's detail page that surfaces OwnerId.
      // Opens the ChangeOwnerModal mounted at the bottom of the dynamic
      // record view; the modal handles user search + PATCH.
      // Record access (owner, 2026-10-14: "הכפתור של שינוי הבעלים לא הופך להיות
      // אפור"): when OwnerId is read-only on this screen for me, or I may not
      // edit this record here, the icon stays — greyed out and disabled.
      const _ownerLocked = noScreenEdit('OwnerId');
      const _ownerIcon = (
        <button
          type="button"
          disabled={_ownerLocked}
          data-testid="change-owner-icon"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!_ownerLocked) setShowChangeOwnerModal(true); }}
          title={_ownerLocked ? 'שדה לקריאה בלבד — אין הרשאה לשנות בעלים' : 'שינוי בעלים'}
          style={{
            marginRight: 6, marginLeft: 0, padding: '2px 6px',
            background: _ownerLocked ? '#f3f3f3' : 'transparent', border: '1px solid #c9c9c9',
            borderRadius: 4, cursor: _ownerLocked ? 'not-allowed' : 'pointer', fontSize: 12,
            color: _ownerLocked ? '#9ca3af' : '#0176d3', filter: _ownerLocked ? 'grayscale(1)' : 'none',
            opacity: _ownerLocked ? 0.55 : 1,
            display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
          }}
        >🔄</button>
      );

      const ownerName = record?.['Owner__Name'] || record?.['OwnerName'] || record?.['OwnerId__Name'] || record?.['Owner_Name'];
      if (ownerName && ownerName !== value && !looksLikeUniqueId(ownerName)) {
        return <span>{ownerName}{_ownerIcon}</span>;
      }
      // Try to resolve from current user
      const cu = recordService.getCurrentUser();
      if (cu && (value === cu.email || value === cu.id || value === cu.Id || value === cu.username)) {
        const resolvedName = cu.name || cu.Name || `${cu.firstName || ''} ${cu.lastName || ''}`.trim();
        if (resolvedName) return <span>{resolvedName}{_ownerIcon}</span>;
      }
      // If value looks like email, show it directly (better than SF ID)
      if (value.includes('@')) return <span>{value}{_ownerIcon}</span>;
    }

    // Special handling for CreatedById and LastModifiedById - show name + date
    if (fieldName === 'CreatedById' || fieldName === 'LastModifiedById') {
      // Get the user name - try multiple sources
      const baseName = fieldName.replace(/Id$/, '');
      let userName = record?.[`${baseName}Name`] || record?.[`${baseName}__Name`];
      // If no name found and value looks like email/username, try to resolve from current user
      if (!userName || userName === value) {
        const cu = recordService.getCurrentUser();
        if (cu && (value === cu.email || value === cu.id || value === cu.Id || value === cu.username)) {
          userName = cu.name || cu.Name || value;
        } else {
          userName = value;
        }
      }

      // Get the corresponding date
      const dateFieldName = fieldName === 'CreatedById' ? 'CreatedDate' : 'LastModifiedDate';
      const dateValue = record?.[dateFieldName];

      let formattedDate = formatIsraelDateTime(dateValue) || '';
      if (dateValue && !formattedDate) {
        try {
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            formattedDate = `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        } catch (e) { /* ignore */ }
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span>{userName && !looksLikeUniqueId(userName) ? userName : '-'}</span>
          {formattedDate && <span style={{ fontSize: '12px', color: '#706e6b' }}>{formattedDate}</span>}
        </div>
      );
    }

    // Special handling for Name field - combine FirstName + LastName for Person Accounts
    if (fieldName === 'Name' && (!value || value === '-' || value === '')) {
      const firstName = record?.FirstName || record?.PersonFirstName || '';
      const lastName = record?.LastName || record?.PersonLastName || '';
      if (firstName || lastName) {
        value = `${firstName} ${lastName}`.trim();
      }
    }

    // Special handling for WorkOrderNumber - format as 8-digit string with leading zeros
    if (fieldName === 'WorkOrderNumber' && value) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        value = String(Math.round(numValue)).padStart(8, '0');
      }
    }

    // Handle reference fields ending with Id - show name from __Name field or .Name field
    // Unless displayAs is 'id', in which case show the ID directly
    if (fieldName?.endsWith('Id') && fieldName !== 'Id' && displayAs !== 'id') {
      // Try multiple naming conventions for the name field
      const baseName = fieldName.replace(/Id$/, '');
      const possibleNameFields = [
        `${baseName}__Name`,           // AccountId -> Account__Name
        `${baseName}.Name`,            // AccountId -> Account.Name
        `${baseName}Name`,             // OwnerId -> OwnerName
        `${fieldName}__Name`,          // RecordTypeId -> RecordTypeId__Name
      ];

      let nameValue = null;
      for (const nameField of possibleNameFields) {
        if (record?.[nameField] && !looksLikeUniqueId(record[nameField])) {
          nameValue = record[nameField];
          break;
        }
      }

      if (nameValue) {
        // Translate known RecordType names to Hebrew
        if (fieldName === 'RecordTypeId') {
          const rtTranslations = {
            'Person Account': 'לקוח פרטי',
            'Person Accounts': 'לקוח פרטי',
            'Business Account': 'לקוח עסקי',
            'Business Accounts': 'לקוח עסקי',
            'Business_Accounts': 'לקוח עסקי',
          };
          value = rtTranslations[nameValue] || nameValue;
        } else {
          value = nameValue;
        }
      } else if (fieldName === 'OwnerId') {
        // OwnerId can contain email/username - try to resolve to name
        // Check if it matches current user
        const cu = recordService.getCurrentUser();
        if (cu && (value === cu.email || value === cu.id || value === cu.Id || value === cu.username)) {
          value = cu.name || cu.Name || value;
        }
        // Otherwise show value as-is (email/username)
      } else if (looksLikeUniqueId(value)) {
        // Person Account fallback: for AccountId, use Contact's own name
        if (fieldName === 'AccountId') {
          const first = record?.FirstName || record?.PersonFirstName || '';
          const last = record?.LastName || record?.PersonLastName || '';
          const combined = `${first} ${last}`.trim();
          if (combined) {
            value = combined;
          } else {
            return <span></span>;
          }
        } else {
          // No __Name field found - show empty for ID-only fields
          return <span></span>;
        }
      }
    }

    // Handle custom lookup fields ending with __c (like Parent_Opportunity__c)
    // These store legacy CRM IDs and have a corresponding __r.Name field
    if (fieldName?.endsWith('__c') && displayAs !== 'id' && looksLikeUniqueId(value)) {
      // Try to find the relationship name field
      const relationshipName = fieldName.replace('__c', '__r');
      const possibleNameFields = [
        `${relationshipName}__Name`,      // Parent_Opportunity__r__Name
        `${relationshipName}.Name`,       // Parent_Opportunity__r.Name
        `${fieldName}__Name`,             // Parent_Opportunity__c__Name
      ];

      // Special handling for product__c field - look for Product2Id__Name or Product__Name
      if (fieldName.toLowerCase() === 'product__c') {
        possibleNameFields.push('Product2Id__Name', 'Product__Name', 'ProductName', 'Product2__Name');
      }

      let nameValue = null;
      for (const nameField of possibleNameFields) {
        if (record?.[nameField] && !looksLikeUniqueId(record[nameField])) {
          nameValue = record[nameField];
          break;
        }
      }

      if (nameValue) {
        value = nameValue;
      }
    }

    // אם אין ערך או זה legacy CRM null ID - הצג ריק
    if (value === null || value === undefined || value === '' || value === '000000000000000AAA') {
      return <span></span>;
    }

    // Helper לנחש אובייקט יעד לפי שם שדה
    const guessTargetObject = (fName) => {
      const fieldMappings = {
        'AccountId': 'Account',
        'ContactId': 'Contact',
        'ServiceTerritoryId': 'ServiceTerritory',
        'ServiceResourceId': 'ServiceResource',
        'WorkOrderId': 'WorkOrder',
        'ServiceAppointmentId': 'ServiceAppointment',
        'OpportunityId': 'Opportunity',
        'ParentRecordId': 'WorkOrder', // Usually WorkOrder for ServiceAppointment
        'OwnerId': 'User',
        'CreatedById': 'User',
        'LastModifiedById': 'User',
        'CaseId': 'Case',
        'AssetId': 'Asset',
        'Product2Id': 'Product2',
        'WorkTypeId': 'WorkType',
        'ServiceContractId': 'ServiceContract',
      };
      // קודם בדוק מיפוי ישיר
      if (fieldMappings[fName]) return fieldMappings[fName];
      // נסה לנחש לפי שם השדה
      const baseName = fName.replace('Id', '').replace('__c', '');
      // בדוק אם יש אובייקט בשם הזה
      const possibleObj = objects.find(o =>
        o.api_name === baseName ||
        o.api_name === `${baseName}__c` ||
        o.api_name.toLowerCase() === baseName.toLowerCase()
      );
      return possibleObj?.api_name || null;
    };

    // בדיקה אם זה שדה __Name (reference field)
    const isNameField = fieldName?.endsWith('__Name');
    if (isNameField) {
      const idField = fieldName.replace('__Name', 'Id');
      const targetObject = record[`${idField}__Target`] || guessTargetObject(idField);
      const refId = record[idField];

      if (refId && targetObject) {
        const url = buildRecordUrl(targetObject, refId);
        return (
          <Link
            to={url}
            className="slds-cell-link"
            style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}
          >
            {value}
          </Link>
        );
      }
      // אם אין target אבל יש ערך - הצג כלינק אם יש ID
      if (refId) {
        return <span style={{ color: '#0176D3' }}>{value}</span>;
      }
    }

    // בדיקה אם זה שדה ID ויש שדה __Name מתאים
    if (fieldName?.endsWith('Id') && fieldName !== 'Id') {
      const nameField = fieldName.replace('Id', '__Name');
      const nameValue = record[nameField];
      const targetObject = record[`${fieldName}__Target`] || guessTargetObject(fieldName);
      // Use originalValue (the actual ID) for the link, not value (which may be the name)
      const refId = originalValue;

      // אם יש שם - הצג את השם כלינק
      if (nameValue && !looksLikeUniqueId(nameValue)) {
        if (targetObject && refId) {
          const url = buildRecordUrl(targetObject, refId);
          return (
            <Link
              to={url}
              className="slds-cell-link"
              style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}
            >
              {nameValue}
            </Link>
          );
        }
        return <span>{nameValue}</span>;
      }

      // אם הערך נראה כמו ID - הצג ריק
      if (looksLikeUniqueId(value)) {
        return <span></span>;
      }
    }

    // Handle custom lookup fields ending with __c (like Parent_Opportunity__c, salesResource__c)
    // Create clickable link if we have a name and ID
    if (fieldName?.endsWith('__c') && looksLikeUniqueId(originalValue)) {
      const relationshipName = fieldName.replace('__c', '__r');
      const nameValue = record?.[`${relationshipName}__Name`] || record?.[`${fieldName}__Name`];

      // Use backend-provided __Target first, then guess from field name
      let targetObject = record?.[`${fieldName}__Target`];
      if (!targetObject) {
        const lookupPrefixes = ['Parent_', 'Related_', 'Primary_', 'Secondary_', 'Master_', 'sales'];
        let baseName = fieldName.replace('__c', '');
        for (const prefix of lookupPrefixes) {
          if (baseName.toLowerCase().startsWith(prefix.toLowerCase())) {
            baseName = baseName.substring(prefix.length);
            break;
          }
        }

        const objectSuffixMappings = {
          'Resource': 'ServiceResource',
          'Territory': 'ServiceTerritory',
          'Appointment': 'ServiceAppointment',
        };

        for (const [suffix, objName] of Object.entries(objectSuffixMappings)) {
          if (baseName.endsWith(suffix)) {
            targetObject = objName;
            break;
          }
        }

        if (!targetObject) {
          targetObject = guessTargetObject(baseName + 'Id') || baseName;
        }
      }

      if (displayAs === 'id') {
        // User wants to see ID
        if (targetObject && originalValue) {
          const url = buildRecordUrl(targetObject, originalValue);
          return (
            <Link
              to={url}
              className="slds-cell-link"
              style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}
            >
              {originalValue}
            </Link>
          );
        }
        return <span>{originalValue}</span>;
      } else if (nameValue && !looksLikeUniqueId(nameValue)) {
        // Show name with link
        if (targetObject && originalValue) {
          const url = buildRecordUrl(targetObject, originalValue);
          return (
            <Link
              to={url}
              className="slds-cell-link"
              style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}
            >
              {nameValue}
            </Link>
          );
        }
        return <span>{nameValue}</span>;
      }
      // No name found - hide Hebrew-prefixed IDs
      if (looksLikeUniqueId(value)) {
        return <span></span>;
      }
    }

    // שדות מיוחדים - טלפון, אימייל, אתר, URL
    if (fieldName === 'Phone' || fieldName?.includes('Phone') || fieldName?.includes('phone') || fieldName?.includes('Mobile')) {
      // Add leading 0 for Israeli numbers missing it (e.g. 523115002 → 0523115002)
      let displayPhone = String(value);
      if (/^[2-9]\d{7,8}$/.test(displayPhone)) {
        displayPhone = '0' + displayPhone;
      }
      return <a href={`tel:${displayPhone}`} style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}>{displayPhone}</a>;
    }
    if (fieldName === 'Email' || fieldName?.includes('Email')) {
      return <a href={`mailto:${value}`} style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'none' }}>{value}</a>;
    }
    // Handle Website and URL fields
    if (fieldName === 'Website' || fieldName?.includes('Url') || fieldName?.includes('URL') || fieldName?.endsWith('__url') || fieldName?.endsWith('_url')) {
      const url = value.startsWith('http') ? value : `https://${value}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );
    }
    // Check if value looks like a URL
    if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('www.'))) {
      const url = value.startsWith('www.') ? `https://${value}` : value;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0176D3', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );
    }

    // Handle Address fields (compound address or Street field with coordinates)
    // Also check by label in Hebrew
    const addressLabels = ['כתובת', 'כתובת לחיוב', 'כתובת למשלוח', 'רחוב', 'רחוב לחיוב'];
    const isAddressField = fieldName === 'Address' || fieldName === 'Street' ||
                           fieldName?.endsWith('Address') || fieldName?.includes('Address') ||
                           fieldName?.endsWith('Street') || fieldName === 'BillingAddress' ||
                           fieldName === 'ShippingAddress' || fieldName === 'MailingAddress' ||
                           fieldName === 'BillingStreet' || fieldName === 'ShippingStreet' ||
                           addressLabels.includes(fieldName);

    if (isAddressField || fieldName === 'BillingAddress' || fieldName === 'ShippingAddress') {
      // Get coordinates from record - try multiple sources (check all possible field names)
      const lat = record?.Latitude || record?.FSL__InternalSLRGeolocation__Latitude__s ||
                  record?.BillingLatitude || record?.ShippingLatitude ||
                  record?.PersonMailingLatitude || record?.Geolocation__Latitude__s ||
                  record?.['BillingAddress.latitude'] || record?.lat;
      const lng = record?.Longitude || record?.FSL__InternalSLRGeolocation__Longitude__s ||
                  record?.BillingLongitude || record?.ShippingLongitude ||
                  record?.PersonMailingLongitude || record?.Geolocation__Longitude__s ||
                  record?.['BillingAddress.longitude'] || record?.lng;

      // Check if it's a compound address object
      if (typeof value === 'object' && value !== null) {
        const { street, city, state, postalCode, country, latitude, longitude } = value;
        const addressParts = [street, city, state, postalCode, country].filter(Boolean);
        const fullAddress = addressParts.join(', ');
        const finalLat = latitude || lat;
        const finalLng = longitude || lng;

        return (
          <div className="address-field-with-map">
            <div className="address-text" style={{ marginBottom: !noMap && finalLat && finalLng ? '8px' : 0 }}>
              {fullAddress || '-'}
            </div>
            {!noMap && finalLat && finalLng && (
              <div className="address-map" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e5e5' }}>
                <iframe
                  src={`https://maps.google.com/maps?q=${finalLat},${finalLng}&z=15&output=embed`}
                  width="100%"
                  height="150"
                  style={{ border: 'none' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="מפה"
                />
              </div>
            )}
          </div>
        );
      }

      // Simple string address (like Street field) or Address field with coordinates
      // Try multiple address field patterns
      const street = record?.Street || record?.BillingStreet || record?.ShippingStreet ||
                     record?.PersonMailingStreet || record?.MailingStreet;
      const city = record?.City || record?.BillingCity || record?.ShippingCity ||
                   record?.PersonMailingCity || record?.MailingCity;
      const country = record?.Country || record?.BillingCountry || record?.ShippingCountry ||
                      record?.PersonMailingCountry || record?.MailingCountry;
      const state = record?.State || record?.BillingState || record?.ShippingState ||
                    record?.PersonMailingState || record?.MailingState;
      const postalCode = record?.PostalCode || record?.BillingPostalCode || record?.ShippingPostalCode ||
                         record?.PersonMailingPostalCode || record?.MailingPostalCode;

      // Build full address from parts if value is empty
      let addressValue = value;
      if (!addressValue || addressValue === '-') {
        const parts = [street, city, state, postalCode, country].filter(Boolean);
        addressValue = parts.length > 0 ? parts.join(', ') : null;
      }

      if (lat && lng) {
        return (
          <div className="address-field-with-map">
            <div className="address-text" style={{ marginBottom: noMap ? 0 : '8px', lineHeight: '1.5' }}>
              {street && <div>{street}</div>}
              {(city || country) && <div style={{ color: '#706e6b' }}>{[city, state, country].filter(Boolean).join(', ')}</div>}
            </div>
            {!noMap && (
              <div className="address-map" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e5e5' }}>
                <iframe
                  src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
                  width="100%"
                  height="150"
                  style={{ border: 'none' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="מפה"
                />
              </div>
            )}
          </div>
        );
      }

      // If no coordinates but have address text (or we can compose it), show map using address query
      // For BillingAddress / ShippingAddress always try to compose if empty
      if (!addressValue && (fieldName === 'BillingAddress' || fieldName === 'ShippingAddress' || fieldName === 'MailingAddress')) {
        const parts = [street, city, state, postalCode, country].filter(Boolean);
        addressValue = parts.length > 0 ? parts.join(', ') : null;
      }
      if (addressValue) {
        const addressQuery = encodeURIComponent([street, city, country].filter(Boolean).join(', ') || addressValue);
        return (
          <div className="address-field-with-map">
            <div className="address-text" style={{ marginBottom: noMap ? 0 : '8px', lineHeight: '1.5' }}>
              {street && <div>{street}</div>}
              {(city || country) && <div style={{ color: '#706e6b' }}>{[city, state, country].filter(Boolean).join(', ')}</div>}
              {!street && !city && <div>{addressValue}</div>}
            </div>
            {!noMap && (
              <div className="address-map" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e5e5' }}>
                <iframe
                  src={`https://maps.google.com/maps?q=${addressQuery}&z=15&output=embed`}
                  width="100%"
                  height="150"
                  style={{ border: 'none' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="מפה"
                />
              </div>
            )}
          </div>
        );
      }
    }

    // Handle boolean values - show checkbox instead of TRUE/FALSE
    // Known boolean __c fields (verified from DB - only fields that have exclusively 0/1/true/false values)
    const knownBooleanFields = new Set([
      'Barak_Migration__c', 'Change_Country__c', 'Create_Messaging_user__c',
      'Customer_in_Debt__c', 'Deal_cancelled__c', 'Insured_client__c',
      'InvoiceDetails__c', 'Passed_Warranty__c', 'Service_Contract__c',
      'Transition_to_a_Agent__c', 'WhatsApp_Service_Confirmation__c',
      'satodifferntadress__c', 'no_tech_needed__c', 'checks_not_allowed__c',
      'Budget_Confirmed__c', 'Discovery_Completed__c', 'ROI_Analysis_Completed__c',
      'Send_customer_email__c', 'Created_By_Flow__c', 'FSL__Pinned__c',
      'Payment_Cancellation__c', 'FSL__IsMultiDay__c', 'Collect_Old_Device__c',
    ]);
    const isBooleanField = fieldName && (
      /^(Is|Has|Can|Do|Was|Should|Enable|Allow|Disable|Active)/.test(fieldName) ||
      fieldName === 'IsActive' || fieldName === 'IsDeleted' ||
      knownBooleanFields.has(fieldName) ||
      (fieldName.endsWith('__c') && (value === true || value === 'true' || value === 'TRUE' || value === false || value === 'false' || value === 'FALSE'))
    );
    const isTrueBoolean = value === true || value === 'true' || value === 'TRUE';
    const isFalseBoolean = value === false || value === 'false' || value === 'FALSE';
    const isNumericBool = (value === '1' || value === 1 || value === '0' || value === 0) && isBooleanField;

    if (isTrueBoolean || (isNumericBool && (value === '1' || value === 1))) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          backgroundColor: '#04844b',
          borderRadius: '4px',
          color: 'white',
          fontSize: '14px'
        }}>✓</span>
      );
    }
    if (isFalseBoolean || (isNumericBool && (value === '0' || value === 0))) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          backgroundColor: '#e5e5e5',
          borderRadius: '4px',
          border: '1px solid #d8dde6'
        }}></span>
      );
    }

    // Handle RecordTypeId - show readable name instead of legacy CRM ID
    if (fieldName === 'RecordTypeId') {
      const recordTypeMap = {
        '012Hp000001mZBN': 'לקוח פרטי',
        '012Hp000001mZBI': 'לקוח עסקי',
        '012Hp000001mZBNIA2': 'לקוח פרטי',
        '012Hp000001mZBIIA2': 'לקוח עסקי',
      };
      // Try exact match, then prefix match
      const rtName = recordTypeMap[value] || recordTypeMap[String(value).substring(0, 15)] || record?.RecordTypeId__Name;
      if (rtName) {
        return <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: rtName.includes('עסקי') ? '#dbeafe' : '#f0fdf4', color: rtName.includes('עסקי') ? '#1e40af' : '#166534', border: `1px solid ${rtName.includes('עסקי') ? '#93c5fd' : '#86efac'}` }}>{rtName}</span>;
      }
    }

    // Translate common picklist values to Hebrew
    const picklistTranslations = {
      'Customer': 'לקוח',
      'Partner': 'שותף',
      'Prospect': 'פוטנציאלי',
      'Competitor': 'מתחרה',
      'Reseller': 'משווק',
      'Other': 'אחר',
      'Lead': 'ליד',
      'New': 'חדש',
      'Active': 'פעיל',
      'Inactive': 'לא פעיל',
      'Pending': 'ממתין',
      'Closed': 'סגור',
      'Open': 'פתוח',
      'High': 'גבוה',
      'Medium': 'בינוני',
      'Low': 'נמוך',
      'Hot': 'חם',
      'Warm': 'חמים',
      'Cold': 'קר',
      'Qualification': 'הסמכה',
      'Needs Analysis': 'ניתוח צרכים',
      'Value Proposition': 'הצעת ערך',
      'Proposal/Price Quote': 'הצעת מחיר',
      'Negotiation/Review': 'משא ומתן',
      'Closed Won': 'נסגר בהצלחה',
      'Closed Lost': 'נסגר בהפסד',
      'Yes': 'כן',
      'No': 'לא',
      'Male': 'זכר',
      'Female': 'נקבה',
      'Mr.': 'מר',
      'Ms.': 'גב\'',
      'Mrs.': 'גב\'',
      'Dr.': 'ד"ר',
      'Prof.': 'פרופ\'',
      'Web': 'אתר',
      'Phone Inquiry': 'פנייה טלפונית',
      'Partner Referral': 'הפניה משותף',
      'Purchased List': 'רשימה שנרכשה',
      'Advertisement': 'פרסומת',
      'Employee Referral': 'הפניה מעובד',
      'Trade Show': 'תערוכה',
      'Word of mouth': 'מפה לאוזן',
      'Public Relations': 'יחסי ציבור',
      'Direct Mail': 'דיוור ישיר',
      'Conference': 'כנס',
      'Seminar': 'סמינר',
      'Email': 'דוא"ל',
      'Social': 'רשתות חברתיות',
      'Existing Customer': 'לקוח קיים',
      // ServiceAppointment Status values
      'Dispatched': 'תואמה התקנה',
      'Scheduled': 'פגישה מתוזמנת',
      'Cannot Complete': 'לא ניתן להשלים',
      'Canceled': 'פגישה בוטלה',
      // Asset Status values
      'Installed': 'הותקן',
      'Not Active': 'לא פעיל',
      'On the Lab': 'בטיפול במעבדה',
      'Send to Lab': 'נשלח למעבדה',
      'Ready for Resource': 'מוכן לטכנאי',
      'הותקן': 'הותקן',
    };

    // Check if value is a translatable picklist value:
    // try centralized HEBREW_PICKLIST_VALUES first (precise per-Object.Field),
    // fall back to the legacy generic map below for values not yet migrated.
    if (typeof value === 'string') {
      const centralized = getHebrewPicklistValue(object.api_name, fieldName, value);
      if (centralized !== value) return <span>{centralized}</span>;
      if (picklistTranslations[value]) return <span>{picklistTranslations[value]}</span>;
    }

    // Format date values - check by field name or ISO date pattern
    const dateFieldNames = ['InstallDate', 'PurchaseDate', 'UsageEndDate', 'LifecycleStartDate', 'LifecycleEndDate',
                            'StartDate', 'EndDate', 'CloseDate', 'BirthDate', 'ContractStartDate', 'ContractEndDate',
                            'ActivationDate', 'ExpirationDate', 'WarrantyStartDate', 'WarrantyEndDate', 'ActivityDate'];
    const fnLower = fieldName?.toLowerCase() || '';
    const isDateField = dateFieldNames.includes(fieldName) ||
                        (fnLower.includes('date') && !fnLower.includes('datetime'));
    const isTimeField = fnLower.includes('time') || fnLower.endsWith('at');
    const isISODate = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T| |$)/.test(value);

    if ((isDateField || isTimeField || isISODate) && typeof value === 'string' && value.length >= 10) {
      try {
        // Normalize "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SSZ" (DB stores UTC)
        let dateStr = value;
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(dateStr)) {
          dateStr = dateStr.replace(' ', 'T') + 'Z';
        }
        const dateObj = new Date(dateStr);
        const TZ = 'Asia/Jerusalem';
        if (!isNaN(dateObj.getTime())) {
          // Check if it has time component
          const hasTime = value.includes('T') || /\d{2}:\d{2}/.test(value);
          if (hasTime && value.length > 10) {
            return <span>{dateObj.toLocaleDateString('he-IL', { timeZone: TZ })} {dateObj.toLocaleTimeString('he-IL', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}</span>;
          }
          return <span>{dateObj.toLocaleDateString('he-IL', { timeZone: TZ })}</span>;
        }
      } catch (e) { /* ignore */ }
    }

    // ערך רגיל
    return <span>{String(value)}</span>;
  };

  // Get highlight fields (first 4 important fields)
  const getHighlightFields = () => {
    // Special handling for Task
    if (object.api_name === 'Task') {
      const highlights = [];
      if (currentRecord.Status) {
        highlights.push({ label: 'סטטוס', value: getHebrewPicklistValue('Task', 'Status', currentRecord.Status), fieldName: 'Status' });
      }
      if (currentRecord.ActivityDate) {
        highlights.push({ label: 'תאריך יעד', value: currentRecord.ActivityDate, fieldName: 'ActivityDate' });
      }
      if (currentRecord.Priority) {
        highlights.push({ label: 'עדיפות', value: getHebrewPicklistValue('Task', 'Priority', currentRecord.Priority), fieldName: 'Priority' });
      }
      const ownerName = currentRecord.Owner__Name || currentRecord.OwnerId__Name || '';
      if (ownerName) {
        highlights.push({ label: 'מוקצה ל', value: ownerName, fieldName: 'Owner__Name' });
      }
      return highlights;
    }

    // Special handling for ServiceAppointment
    if (object.api_name === 'ServiceAppointment') {
      const highlights = [];

      // חשבון
      const accountName = currentRecord.AccountId__Name || currentRecord.Account__Name || currentRecord.AccountName;
      if (accountName) {
        highlights.push({ label: 'חשבון', value: accountName, fieldName: 'AccountId__Name' });
      }

      // מספר פגישה
      if (currentRecord.AppointmentNumber) {
        highlights.push({ label: 'מספר פגישה', value: currentRecord.AppointmentNumber, fieldName: 'AppointmentNumber' });
      }

      // אזור שירות
      const territoryName = currentRecord.ServiceTerritoryId__Name || currentRecord.ServiceTerritory__Name || currentRecord.ServiceTerritoryName;
      if (territoryName) {
        highlights.push({ label: 'אזור שירות', value: territoryName, fieldName: 'ServiceTerritoryId__Name' });
      }

      // סטטוס - תרגום לעברית
      if (currentRecord.Status) {
        highlights.push({ label: 'סטטוס', value: getHebrewPicklistValue('ServiceAppointment', 'Status', currentRecord.Status), fieldName: 'Status' });
      }

      return highlights;
    }

    // שדות חשוב להציג בראש - עדיפות לכמות ולא IDs
    const priorityFields = ['Phone', 'Email', 'Industry', 'AccountNumber', 'Type', 'Website', 'Owner__Name', 'Account__Name', 'Contact__Name'];
    const highlights = [];

    // Try to get from backend fields - first check for __Name fields
    for (const fieldName of priorityFields) {
      const value = currentRecord[fieldName];
      // דלג על ערכים שנראים כמו IDs
      if (value !== undefined && value !== null && value !== '' && !looksLikeUniqueId(value)) {
        // קבל תווית יפה - הסר __Name מהתווית
        let label = formatFieldLabel(fieldName);
        if (fieldName === 'Owner__Name') label = 'בעלים';
        else if (fieldName === 'Account__Name') label = 'חשבון';
        else if (fieldName === 'Contact__Name') label = 'איש קשר';

        highlights.push({
          label: label,
          value: value,
          fieldName
        });
      }
      if (highlights.length >= 4) break;
    }

    // If not enough, add from record (excluding IDs and __Target fields)
    if (highlights.length < 4) {
      const excludeFields = ['Id', 'Name', 'Subject', 'Title', 'attributes', 'IsDeleted', ...priorityFields];
      for (const [key, value] of Object.entries(currentRecord)) {
        // סנן שדות שלא צריך להציג
        if (excludeFields.includes(key)) continue;
        if (!value) continue;
        if (key.endsWith('__Target')) continue;
        if (key.endsWith('__Name')) continue; // כבר טיפלנו ב-__Name למעלה
        // דלג על ערכים שנראים כמו IDs
        if (looksLikeUniqueId(value)) continue;
        // אל תציג ID-ים אם יש שדה __Name מתאים
        if (key.endsWith('Id')) {
          const nameField = key.replace('Id', '__Name');
          if (currentRecord[nameField]) continue; // יש שם - דלג על ה-ID
        }

        highlights.push({
          label: formatFieldLabel(key),
          value: String(value),
          fieldName: key
        });
        if (highlights.length >= 4) break;
      }
    }

    return highlights;
  };

  const highlightFields = getHighlightFields();

  return (
    <div className="slds-record-page">
      {object.api_name === 'ServiceAppointment' && <SaAssignmentBanner record={currentRecord} />}
      {/* 2026-10-25: «ממתין להנחת מנהל על סך: ₪X» — the products stay at the pricebook price until approved. */}
      {isOpportunityPage && <DiscountPendingBanner summary={discountSummary} />}
      {/* LC-4b: «לא פעיל · שודרג ל־…», the condition at collection, «מחזור חיים של המכשיר». */}
      {object.api_name === 'Asset' && currentRecord?.Id && <AssetLifecyclePanel assetId={currentRecord.Id} />}

      {/* Alert Banner for Account - Not Insured */}
      {object.api_name === 'Account' && (currentRecord.Insured_client__c === false || currentRecord.Insured_client__c === 'false' || currentRecord.Insured_client__c === '' || !currentRecord.Insured_client__c) && (
        <div style={{
          background: '#d32f2f',
          borderRadius: '4px',
          padding: '10px 20px',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(211,47,47,0.3)',
        }}>
          <span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>
            הלקוח אינו מבוטח
          </span>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>|</span>
          <span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>
            אין לבצע הזמנת עבודה ללא תשלום
          </span>
        </div>
      )}

      {/* Debtor Alert Banner */}
      {object.api_name === 'Account' && parseFloat(currentRecord.Total_Account_Debt__c || 0) > 0 && (
        <div style={{
          background: '#dc2626', color: '#fff', padding: '14px 24px',
          borderRadius: '8px', marginBottom: '12px', textAlign: 'center',
          fontSize: '16px', fontWeight: '700', boxShadow: '0 2px 8px rgba(220,38,38,0.3)'
        }}>
          ⚠️ לקוח חייב — אין לבצע שום פעולה בחשבון ללא תשלום
          (יתרה: ₪{parseFloat(currentRecord.Total_Account_Debt__c || 0).toLocaleString()})
        </div>
      )}

      {/* Rivhit Customer Ledger Cards — Debit / Credit */}
      {object?.api_name === 'Account' && (
        <div style={{ marginBottom: '12px' }}>
          {!currentRecord?.Rivhit_Customer_Id__c || currentRecord.Rivhit_Customer_Id__c === '0' || currentRecord.Rivhit_Customer_Id__c === '' ? (
            <div style={{
              background: '#f3f4f6', padding: '10px 16px', borderRadius: '8px',
              textAlign: 'center', color: '#9ca3af', fontSize: '13px'
            }}>
              ללקוח אין מספר לקוח ברווחית
            </div>
          ) : rivhitBalanceLoading ? (
            <div style={{
              background: '#f3f4f6', padding: '10px 16px', borderRadius: '8px',
              textAlign: 'center', color: '#6b7280', fontSize: '13px'
            }}>
              טוען יתרה מרווחית...
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>
              {/* Debit Card (לקוח חייב) */}
              <div style={{
                flex: 1, borderRadius: '6px', padding: '5px 12px',
                background: (() => {
                  const bal = rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0;
                  return parseFloat(bal) > 0
                    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    : '#f3f4f6';
                })(),
                color: parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0) > 0 ? '#fff' : '#9ca3af',
                boxShadow: parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0) > 0
                  ? '0 2px 6px rgba(220,38,38,0.3)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600' }}>לקוח חייב</div>
                <div style={{ fontSize: '14px', fontWeight: '700' }}>
                  ₪{Math.max(0, parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0)).toLocaleString()}
                </div>
              </div>

              {/* Credit Card (לקוח בזכות) */}
              <div style={{
                flex: 1, borderRadius: '6px', padding: '5px 12px',
                background: (() => {
                  const bal = rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0;
                  return parseFloat(bal) < 0
                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                    : '#f3f4f6';
                })(),
                color: parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0) < 0 ? '#fff' : '#9ca3af',
                boxShadow: parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0) < 0
                  ? '0 2px 6px rgba(22,163,74,0.3)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600' }}>לקוח בזכות</div>
                <div style={{ fontSize: '14px', fontWeight: '700' }}>
                  ₪{Math.abs(Math.min(0, parseFloat(rivhitBalance?.balance?.balance ?? rivhitBalance?.balance?.total_balance ?? 0))).toLocaleString()}
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => loadRivhitBalance(currentRecord.Rivhit_Customer_Id__c)}
                disabled={rivhitBalanceLoading}
                style={{
                  width: '28px', minWidth: '28px', height: 'auto',
                  borderRadius: '6px', border: '1px solid #e5e7eb',
                  background: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', color: '#6b7280',
                }}
                title="רענון יתרה מרווחית"
              >
                🔄
              </button>
            </div>
          )}
        </div>
      )}

      {/* Record Header - Afikim Style */}
      <div className="slds-record-header">
        {/* Top Row - Breadcrumb */}
        <div className="slds-record-header__top">
          <button className="slds-back-button" onClick={onBack}>
            <span>←</span>
            <span>{config.heLabel}</span>
          </button>
        </div>

        {/* Main Row - Icon, Name, and Actions */}
        <div className="slds-record-header__main">
          <span className="slds-record-icon" style={{ backgroundColor: config.color }}>
            {config.icon}
          </span>
          <div className="slds-record-header__info">
            <div className="slds-record-type-label">{config.heLabel}</div>
            <h1 className="slds-record-name">{displayName}</h1>
          </div>

          {/* Action Buttons - Right Side */}
          <div className="slds-record-actions">
            {/* PR-4: Payment verify — the SECOND entry point, same shared component +
                same endpoint as the Payments Verification queue (no duplicated logic).
                Accounting/Admin only, shown only for a verifiable Payment; on success it
                replaces itself with the verified marker. */}
            {object.api_name === 'Payment__c' && permIsAccounting(currentUser) && currentRecord?.Id && (
              <span style={{ marginInlineEnd: 8 }}>
                <PaymentVerifyButton
                  payment={{ id: currentRecord.Id, payment_status: currentRecord.Payment_Status__c,
                    issuance_failure_reason: currentRecord.Issuance_Failure_Reason__c }}
                  onDone={() => window.location.reload()} />
              </span>
            )}
            {!isEditing ? (
              (() => {
                // Get visibleButtonCount from record page settings (default 4)
                // Check multiple possible locations: highlights.numVisibleActions, settings.visibleButtonCount, visibleButtonCount
                let visibleButtonCount = flexipage?.highlights?.numVisibleActions || flexipage?.settings?.visibleButtonCount || flexipage?.visibleButtonCount || 4;

                // Build all buttons array with order support from record page
                // Get buttons from multiple possible sources: buttons array, actions array, or highlights.actionNames
                let flexipageButtons = flexipage?.buttons || flexipage?.actions || [];
                if (flexipageButtons.length === 0 && flexipage?.highlights?.actionNames) {
                  flexipageButtons = flexipage.highlights.actionNames.map((action, idx) => {
                    const actionName = typeof action === 'string' ? action : action.value || action;
                    return { id: actionName, apiName: actionName, label: actionName, order: idx };
                  });
                }
                const buttonOrderMap = new Map();
                flexipageButtons.forEach((btn, idx) => {
                  buttonOrderMap.set(btn.id || btn.apiName, { order: btn.order ?? idx, ...btn });
                });

                // Standard buttons - different for Account, Opportunity, and others
                let allButtons = [];
                const isAdminOrAccounting = permIsAccounting(currentUser);
                // Separate from the line above ON PURPOSE. `isAdminOrAccounting` also gates
                // «מסמכי לקוח» and both «הפקת מסמכים» buttons, which are financial; only the
                // two CREATION buttons widen to institutional and management.
                const canCreateAssetOrContract = permCanCreateAssetOrContract(currentUser);

                if (object.api_name === 'Account') {
                  // Account: עריכה, הזדמנות חדשה, יצירת נכס, זיהוי מחמיר
                  // 2026-10-27: done only with coordinates — 22 Accounts were marked before the
                  // modal required them; the button stays active so they are identified again.
                  const isStrictIdDone = isStrictIdentified(currentRecord);
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: 0 },
                    { id: 'newOpportunity', label: 'הזדמנות חדשה', icon: '💰', variant: 'brand', order: 1, onClick: async () => {
                      // 2026-10-27 (owner): no opportunity on an Account without coordinates.
                      const gate = await checkAccountGate(API_BASE, currentRecord?.Id, { requireStrictId: false });
                      if (!gate.ok) { setOppGateMsg(gate.message); return; }
                      setShowOpportunityModal(true);
                    } },
                    // Owner, 2026-09-02: creating an asset is accounting's job, so the button
                    // is theirs. Admin is a superset of accounting in `isAccounting`.
                    // ⚠️ Display only. /api/objects/Asset/records has no accounting-only gate,
                    // so a direct call still works — see the PR for why that was left alone.
                    ...(canCreateAssetOrContract ? [{ id: 'newAsset', label: 'יצירת נכס', icon: '📦', variant: 'brand', onClick: () => setShowCreateAssetModal(true), order: 2 }] : []),
                    {
                      id: 'strictIdentification',
                      label: isStrictIdDone ? 'זיהוי מחמיר ✓' : 'זיהוי מחמיר',
                      icon: '🏠',
                      variant: isStrictIdDone ? 'neutral' : 'brand',
                      onClick: () => setShowStrictIdentificationModal(true),
                      order: 3,
                    },
                    {
                      id: 'whatsappOptIn',
                      label: 'הצטרפות לשירות הוואטסאפ',
                      icon: '💬',
                      variant: 'brand',
                      onClick: () => setShowWhatsAppOptInModal(true),
                      order: 4,
                    },
                    {
                      id: 'routeToAgent',
                      label: 'העברת שיחות ישירות לנציגה',
                      icon: '📞',
                      variant: 'brand',
                      onClick: () => {
                        setRtaStep(1);
                        setRtaTypeOfTransfer('');
                        setRtaChoice(null);
                        setRtaSubmitting(false);
                        setRtaResult(null);
                        setRtaError('');
                        setShowRouteToAgentModal(true);
                      },
                      order: 5,
                    },
                    {
                      id: 'priceQuote',
                      label: 'הצעת מחיר',
                      icon: '📑',
                      variant: 'brand',
                      onClick: () => setShowPriceQuoteModal(true),
                      order: 6,
                    },
                  ];
                  // Admin/Accounting: מסמכי לקוח
                  // Renamed from "כרטסת לקוח" + reduced to documents-only view.
                  // Hits /api/accounts/{id}/documents which aggregates every
                  // receipt/invoice link + PDF/image attachment from the
                  // account itself and every related record (Opp / SA / WO /
                  // Case / Asset). The old summary stats + per-opp accordion
                  // were dropped per owner spec.
                  if (isAdminOrAccounting) {
                    allButtons.push({ id: 'customerDocuments', label: 'מסמכי לקוח', icon: '📑', variant: 'brand', onClick: async () => {
                      setShowCustomerLedger(true);
                      setIsLoadingLedger(true);
                      setLedgerData(null);
                      try {
                        const docsRes = await fetch(`${API_BASE}/api/accounts/${currentRecord.Id}/documents?_t=${Date.now()}`);
                        if (!docsRes.ok) throw new Error(`HTTP ${docsRes.status}`);
                        const docsData = await docsRes.json();
                        setLedgerData(docsData);
                      } catch (err) {
                        console.error('[CustomerDocs] load error:', err);
                        setLedgerData({ error: err.message, account: { name: currentRecord?.Name || '' }, documents: [] });
                      } finally {
                        setIsLoadingLedger(false);
                      }
                    }, order: 5 });
                  }
                  // Admin/Accounting: הפקת מסמכים — Account-wide.
                  // Loads ALL opps for this account so the modal's
                  // step-1 picker shows the full set (same component
                  // as the Opportunity-level button; just a different
                  // seed list).
                  if (isAdminOrAccounting) {
                    allButtons.push({ id: 'createDocumentAccount', label: 'הפקת מסמכים', icon: '🧾', variant: 'brand', onClick: async () => {
                      if (loadingAccountOpps) return;
                      setLoadingAccountOpps(true);
                      try {
                        const relRes = await fetch(`${API_BASE}/api/objects/Account/records/${currentRecord.Id}/related?resolve_refs=true&_t=${Date.now()}`);
                        if (!relRes.ok) throw new Error('Failed to load account opportunities');
                        const relData = await relRes.json();
                        const opps = relData.related_objects?.Opportunity?.records || [];
                        if (opps.length === 0) {
                          alert('אין הזדמנויות בחשבון זה');
                          return;
                        }
                        setAccountDocumentOpps(opps);
                        setShowAccountDocumentModal(true);
                      } catch (err) {
                        console.error('[AccountDoc] load opps failed:', err);
                        alert('שגיאה בטעינת הזדמנויות החשבון');
                      } finally {
                        setLoadingAccountOpps(false);
                      }
                    }, order: 7 });
                  }
                  // (Old 'customerLedger' button removed 2026-05-19 — superseded
                  // by 'customerDocuments' above which is documents-only.)
                } else if (object.api_name === 'Opportunity') {
                  // Opportunity: עריכה, הוספת מוצרים, תשלום, הסכם התקשרות
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: 0 },
                    { id: 'addProducts', label: 'הוספת מוצרים', icon: '📦', variant: 'brand', onClick: () => setShowProductsModal(true), order: 1 },
                    { id: 'payment', label: 'תשלום', icon: '💳', variant: 'brand', onClick: () => setShowPaymentModal(true), order: 2 },
                    { id: 'summary', label: dealSummaryLoading ? 'יוצר טופס...' : 'הסכם התקשרות', icon: dealSummaryLoading ? '⏳' : '📋', variant: 'brand', onClick: handleDealSummary, disabled: dealSummaryLoading, order: 3 }
                  ];
                  // 2026-10-25: בקשת הנחה ממנהל — only while the feature is on and the opp has products.
                  if (discountSummary && (relatedRecords.OpportunityLineItem || []).length > 0) {
                    allButtons.push({ id: 'discountRequest', label: 'בקש הנחה ממנהל', icon: '💲', variant: 'brand', onClick: () => setShowDiscountRequestModal(true), order: 1.5 });
                  }
                  if (dealerBoot?.enabled && currentRecord?.Type === BUSINESS_DELIVERY_WORK_TYPE_NAME) {
                    allButtons.push({ id: 'dealerSendDocuments', label: 'שליחת מסמכים', icon: '📤', variant: 'brand', onClick: () => setDocSendOppId(currentRecord.Id), order: 3.6 });
                  }
                  if (showsDeliverySchedule(dealerBoot, 'Opportunity', currentRecord)) {
                    allButtons.push({ id: DELIVERY_SCHEDULE_BUTTON_ID, label: DELIVERY_SCHEDULE_LABEL, icon: '🚚', variant: 'brand', onClick: () => { setSchedulerIsDelivery(true); setInstitutionalSchedulerOppId(currentRecord.Id); }, order: 3.5 });
                  }
                  // Admin/Accounting: הפקת מסמכים
                  // 2026-05-19: when triggered from an Opportunity, load
                  // ALL of the parent Account's opportunities so the
                  // modal's step-1 picker shows every opp + its products
                  // + its payments — not just the one we're standing on.
                  // Matches the Account-level "הפקת מסמכים" flow.
                  if (isAdminOrAccounting) {
                    allButtons.push({ id: 'createDocument', label: 'הפקת מסמכים', icon: '🧾', variant: 'brand', onClick: async () => {
                      if (loadingAccountOpps) return;
                      const accountId = currentRecord?.AccountId;
                      if (!accountId) {
                        // No parent account — fall back to single-opp scope.
                        setAccountDocumentOpps([]);
                        setShowDocumentModal(true);
                        return;
                      }
                      setLoadingAccountOpps(true);
                      try {
                        const relRes = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}/related?resolve_refs=true&_t=${Date.now()}`);
                        if (!relRes.ok) throw new Error('Failed to load account opportunities');
                        const relData = await relRes.json();
                        const opps = relData.related_objects?.Opportunity?.records || [];
                        // Make sure the CURRENT opp is in the list — guards
                        // against /related being slightly stale right after
                        // a brand-new opp was created (the cache rebuild
                        // can lag the writer by a couple of seconds).
                        if (currentRecord && !opps.find(o => o.Id === currentRecord.Id)) {
                          opps.unshift(currentRecord);
                        }
                        setAccountDocumentOpps(opps);
                        setShowDocumentModal(true);
                      } catch (err) {
                        console.error('[OppDoc] load account opps failed:', err);
                        // Soft-fail to single-opp mode so the button still
                        // works when /related glitches.
                        setAccountDocumentOpps([]);
                        setShowDocumentModal(true);
                      } finally {
                        setLoadingAccountOpps(false);
                      }
                    }, order: 4 });
                    // שיקים חוזרים - only show if there are check payments
                    const payments = relatedRecords?.Payment__c || [];
                    const hasChecks = payments.some(p => p.Payment_Type__c === 'שיק' || p.Payment_Type__c === 'check' || p.Payment_Type__c === 'Check');
                    if (hasChecks) {
                      allButtons.push({ id: 'bouncedChecks', label: 'שיקים חוזרים', icon: '📝', variant: 'brand', onClick: () => {
                        const checks = payments.filter(p => p.Payment_Type__c === 'שיק' || p.Payment_Type__c === 'check' || p.Payment_Type__c === 'Check');
                        setAccountChecks(checks);
                        setShowBouncedChecksPanel(true);
                      }, order: 5 });
                    }
                  }
                } else if (object.api_name === 'ServiceAppointment') {
                  // ServiceAppointment: עריכה + תיאום פגישה + תזכורת + תיאום מוסדי
                  // 2026-05-20: institutional bulk-scheduler opens via the SA →
                  // WorkOrder → Opportunity chain. We resolve the parent Opp
                  // lazily on click (fetch the WO record) rather than on every
                  // SA detail render to keep the page fast.
                  const handleOpenInstitutionalScheduler = async (asDelivery) => {
                    const woId = currentRecord?.ParentRecordId;
                    if (!woId) { alert('לא נמצאה הזמנת עבודה משויכת לפעילות זו'); return; }
                    try {
                      const woRes = await fetch(`${API_BASE}/api/objects/WorkOrder/records/${woId}`);
                      if (!woRes.ok) throw new Error(`HTTP ${woRes.status}`);
                      const woData = await woRes.json();
                      const wo = woData.record || woData;
                      const oppId = wo?.Opportunity__c;
                      if (!oppId) {
                        alert('פעילות זו אינה מקושרת להזדמנות — תיאום מוסדי מבוסס על שיוך לאותה הזדמנות');
                        return;
                      }
                      setSchedulerIsDelivery(asDelivery === true);
                      setInstitutionalSchedulerOppId(oppId);
                    } catch (err) {
                      console.error('[InstScheduler] failed to resolve Opp:', err);
                      alert('שגיאה באיתור ההזדמנות: ' + err.message);
                    }
                  };
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: buttonOrderMap.get('edit')?.order ?? 0 },
                    { id: 'scheduleAppointment', label: 'תיאום פגישה', icon: '📅', variant: 'brand', onClick: () => setShowTechnicianSelector(true), order: buttonOrderMap.get('scheduleAppointment')?.order ?? 1 },
                    { id: 'institutionalSchedule', label: 'תיאום פגישה מוסדי', icon: '🏢', variant: 'brand', onClick: () => handleOpenInstitutionalScheduler(false), order: buttonOrderMap.get('institutionalSchedule')?.order ?? 3 },
                    // 'תזכורת פגישה' (legacy reminder: robocall + WhatsApp) removed 2026-10-14 —
                    // the legacy reminder is off; visit confirmations go through 📲 תזכורים in אפיקים GO.
                  ];
                  if (showsDeliverySchedule(dealerBoot, 'ServiceAppointment', currentRecord)) {
                    allButtons.push({ id: DELIVERY_SCHEDULE_BUTTON_ID, label: DELIVERY_SCHEDULE_LABEL, icon: '🚚', variant: 'brand', onClick: () => handleOpenInstitutionalScheduler(true), order: buttonOrderMap.get(DELIVERY_SCHEDULE_BUTTON_ID)?.order ?? 3.5 });
                  }
                  // Admin/office management schedule tools
                  const canManageScheduleDetail = permIsAdmin(currentUser) || permHasProfile(currentUser, 'office_management');
                  if (canManageScheduleDetail) {
                    allButtons.push(
                      { id: 'areaPlanner', label: 'משפך הקסם', icon: '🪄', variant: 'brand', onClick: () => setShowAreaPlanner(true), order: 10 },
                      { id: 'routeOptimizer', label: 'שביט הקסם', icon: '✨', variant: 'brand', onClick: () => setShowRouteOptimizer(true), order: 11 },
                      { id: 'bulkTransfer', label: 'ניהול היעדרויות טכנאי', icon: '🔄', variant: 'brand', onClick: () => setShowBulkTransfer(true), order: 12 },
                    );
                  }
                } else if (object.api_name === 'Task') {
                  // Owner-spec 2026-06-15: collapse the two satisfaction
                  // buttons into a single "הושלם" that opens a modal which
                  // captures outcome + activity_response + treatment_done
                  // + optional assigner-notification. The modal calls
                  // POST /api/tasks/{id}/complete which atomically updates
                  // Status / IsClosed / Comments__c / CallResult__c and
                  // optionally fires a bell notification.
                  const taskIsClosed = (currentRecord?.IsClosed === '1' || currentRecord?.IsClosed === 1 || currentRecord?.IsClosed === true);
                  allButtons = [
                    ...(taskEditLocked ? [] : [
                      { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: 0 },
                    ]),
                    { id: 'completeTask', label: taskIsClosed ? 'משימה הושלמה' : 'הושלם', icon: '✓', variant: 'success', onClick: () => setShowTaskCompleteModal(true), order: 1,
                      style: taskIsClosed ? { background: '#4caf50', color: '#fff', borderColor: '#388e3c', fontWeight: 700 } : {} },
                    { id: 'transferTask', label: 'העברת משימה', icon: '🔁', variant: 'neutral', onClick: () => setShowTaskTransferModal(true), order: 2 },
                  ];
                } else if (object.api_name === 'WorkOrder') {
                  // WorkOrder: עריכה + יצירת פגישה + דוחות הצטיידות/ליקוט.
                  // Owner-spec 2026-06-10: hide "יצירת פגישה" from
                  // sales-center + service-center reps — they shouldn't
                  // create appointments directly off a WO. Admin and other
                  // profiles keep the button.
                  const hideCreateAppointment =
                    permHasProfile(currentUser, 'sales_center') ||
                    permHasProfile(currentUser, 'service_center');
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: buttonOrderMap.get('Edit')?.order ?? buttonOrderMap.get('edit')?.order ?? 0 },
                    ...(hideCreateAppointment ? [] : [
                      { id: 'createAppointment', label: createAppointmentLoading ? 'יוצר פגישה...' : 'יצירת פגישה', icon: createAppointmentLoading ? '⏳' : '📅', variant: 'brand', onClick: handleCreateAppointmentFromWorkOrder, disabled: createAppointmentLoading, order: buttonOrderMap.get('createAppointment')?.order ?? 1 },
                    ]),
                    { id: 'techInventory', label: 'הדפסת הצטיידות', icon: '📋', variant: 'neutral', onClick: () => window.open('/apex/service_hitstaydut', '_blank'), order: 2 },
                    { id: 'techInventoryV2', label: 'דוח הצטיידות', icon: '📦', variant: 'neutral', onClick: () => window.open('/apex/HITSTAIDUTV2', '_blank'), order: 3 },
                    { id: 'warehousePicking', label: 'ליקוט', icon: '🏭', variant: 'neutral', onClick: () => window.open('/apex/mahsan', '_blank'), order: 4 },
                  ];
                } else if (object.api_name === 'Asset') {
                  // Asset: עריכה, יצירת מקרה, עדכון סיווג מקרה, יצירת חוזה שירות
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: buttonOrderMap.get('edit')?.order ?? 0 },
                    { id: 'newCase', label: 'יצירת מקרה', icon: '📋', variant: 'brand', onClick: () => setShowCreateCaseModal(true), order: 1 },
                    { id: 'updateCaseClassification', label: 'עדכון סיווג מקרה', icon: '🔄', variant: 'brand', onClick: () => setShowUpdateCaseClassificationModal(true), order: 2 },
                    // Owner, 2026-09-02: same rule for the service contract — accounting and
                    // admin only. Display only, as above.
                    ...(canCreateAssetOrContract ? [{ id: 'newServiceContract', label: 'יצירת חוזה שירות', icon: '📄', variant: 'brand', onClick: () => setShowCreateServiceContractModal(true), order: 3 }] : []),
                  ];
                } else if (object.api_name === 'ServiceContract' || object.api_name === 'Case') {
                  // ServiceContract, Case: עריכה בלבד (ללא מחיקה ושכפול)
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: buttonOrderMap.get('edit')?.order ?? 0 },
                  ];
                } else if (object.api_name === 'Payment__c') {
                  // Payment__c: עריכה, בדיקת סטטוס תשלום, יצירת זיכוי (admin/accounting only)
                  const handleCheckPaymentStatus = async () => {
                    try {
                      const btn = document.getElementById('btn-check-payment');
                      if (btn) { btn.disabled = true; btn.textContent = '⏳ בודק...'; }
                      const res = await fetch(`${API_BASE}/api/icredit/verify-payment/${currentRecord.Id}`, { method: 'POST' });
                      const result = await res.json();
                      if (btn) { btn.disabled = false; btn.textContent = '🔍 בדיקת סטטוס תשלום'; }
                      if (result.success) {
                        // Refresh the record
                        const refreshRes = await fetch(`${API_BASE}/api/objects/Payment__c/records/${currentRecord.Id}`);
                        if (refreshRes.ok) {
                          const refreshData = await refreshRes.json();
                          setCurrentRecord(refreshData.record || refreshData);
                        }
                        alert(`בדיקת סטטוס הושלמה\n\nסטטוס: ${result.status}\n${result.authNum ? 'מספר אישור: ' + result.authNum + '\n' : ''}${result.cardLast4 ? 'כרטיס: ****' + result.cardLast4 + '\n' : ''}${result.receiptUrl ? 'קישור למסמך: ' + result.receiptUrl : ''}`);
                      } else {
                        alert(result.message || 'שגיאה בבדיקת סטטוס');
                      }
                    } catch (err) {
                      console.error('[Payment] Check status error:', err);
                      alert('שגיאה בבדיקת סטטוס תשלום');
                    }
                  };
                  // Profile gate for the refund button — admin + accounting only.
                  // Reads the same salesforce_user key the other gates use.
                  const _refundUserObj = (() => {
                    try {
                      return JSON.parse(localStorage.getItem('salesforce_user') || sessionStorage.getItem('salesforce_user') || '{}') || {};
                    } catch { return {}; }
                  })();
                  // permIsAccounting() returns true for admin too (admin is a
                  // superset of accounting), so this single call replaces the
                  // old two-list union.
                  const _canRefund = permIsAccounting(_refundUserObj);
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: 0 },
                    { id: 'checkPayment', label: 'בדיקת סטטוס תשלום', icon: '🔍', variant: 'brand', onClick: handleCheckPaymentStatus, order: 1 },
                    ...(_canRefund ? [{
                      id: 'createRefund', label: 'יצירת זיכוי לנציג מכירה', icon: '💸',
                      variant: 'brand',
                      onClick: () => setShowRefundModal(true),
                      order: 2,
                    }] : []),
                    ...(_canRefund ? [{
                      id: 'transferPayment', label: 'העברת תשלום', icon: '↪️',
                      variant: 'brand',
                      onClick: () => setShowTransferModal(true),
                      order: 2.5,
                    }] : []),
                    { id: 'delete', label: deleting ? 'מוחק...' : 'מחיקה', icon: deleting ? '⏳' : '🗑️', onClick: handleDelete, disabled: deleting, order: 3 },
                  ];
                } else {
                  // Other objects: עריכה, מחיקה, שכפול
                  allButtons = [
                    { id: 'edit', label: 'עריכה', icon: '✏️', variant: 'brand', onClick: handleStartEdit, order: buttonOrderMap.get('edit')?.order ?? 0 },
                    { id: 'delete', label: deleting ? 'מוחק...' : 'מחיקה', icon: deleting ? '⏳' : '🗑️', onClick: handleDelete, disabled: deleting, order: buttonOrderMap.get('delete')?.order ?? 1 },
                    { id: 'clone', label: 'שכפול', icon: '+', onClick: () => {}, order: buttonOrderMap.get('clone')?.order ?? 2 },
                  ];
                }

                // Add custom buttons
                customButtons
                  .filter(btn => btn.show_in_header && btn.is_active)
                  .forEach((btn, idx) => {
                    allButtons.push({
                      id: btn.id || `custom-${idx}`,
                      label: btn.label,
                      icon: btn.icon,
                      variant: btn.variant,
                      style: getButtonStyle(btn),
                      onClick: () => handleCustomButtonClick(btn),
                      title: btn.description || btn.label,
                      order: buttonOrderMap.get(btn.id)?.order ?? (100 + idx)
                    });
                  });

                // 2026-05-18: button-permission gate. Hide any button whose
                // (object, id) appears in the user's deny list (computed
                // server-side at /api/button-permissions/effective). Default
                // is unrestricted — no row = no entry in denyList.
                const _denyList = buttonDeny[object.api_name] || [];
                if (_denyList.length > 0) {
                  allButtons = allButtons.filter((b) => !_denyList.includes(b.id));
                }
                // 2026-10-11 (owner): work types — edit / delete / clone for a system admin only
                if (isAdminOnlyObject(object.api_name) && !permIsAdmin(currentUser)) {
                  allButtons = allButtons.filter((b) => !ADMIN_ONLY_HIDDEN_BUTTONS.includes(b.id));
                }
                // ניהול הרשאות רשומות (PR 4): edit-from-screen and delete, per object.
                if (recordEditDenied) allButtons = allButtons.filter((b) => b.id !== 'edit');
                if (recordDeleteDenied) allButtons = allButtons.filter((b) => b.id !== 'delete');

                // Sort buttons by order
                allButtons.sort((a, b) => a.order - b.order);

                // For WorkOrder/Task/Account, ensure all action buttons are visible
                // (not hidden in dropdown). Account was added 2026-05-17 after the
                // WhatsApp-opt-in + Route-to-Agent buttons pushed the total past the
                // flexipage's visibleButtonCount of 5, hiding Route-to-Agent in the
                // overflow menu.
                if (object.api_name === 'WorkOrder' || object.api_name === 'Task' || object.api_name === 'Account') {
                  visibleButtonCount = Math.max(visibleButtonCount, allButtons.length);
                }

                // Split into visible and hidden buttons
                const visibleButtons = allButtons.slice(0, visibleButtonCount);
                const hiddenButtons = allButtons.slice(visibleButtonCount);

                return (
                  <>
                    {visibleButtons.map(btn => (
                      <button
                        key={btn.id}
                        className={`slds-action-button ${btn.variant === 'brand' ? 'slds-action-button--brand' : ''}`}
                        style={btn.style}
                        onClick={btn.onClick}
                        disabled={btn.disabled}
                        title={btn.title}
                      >
                        {btn.icon && <span className="slds-action-button__icon">{btn.icon}</span>}
                        {btn.label}
                      </button>
                    ))}
                    {hiddenButtons.length > 0 && (
                      // 2026-05-20: removed className="slds-dropdown-trigger" —
                      // the global rule in afikim-ui.css (font-size 10px +
                      // opacity 0.7 !important) was intended for the
                      // global-header arrow but polluted every chevron in
                      // the app, including this one. The chevron rendered
                      // so tiny+faded that owners couldn't reliably click it.
                      // Replaced with a self-contained inline style + z-index
                      // so nothing on the page can cover it.
                      <div style={{ position: 'relative', zIndex: 5, display: 'inline-flex' }}>
                        <button
                          type="button"
                          className="slds-action-button slds-action-button__more"
                          onClick={(e) => { e.stopPropagation(); setShowMoreButtons(!showMoreButtons); }}
                          style={{ fontSize: '14px' }}
                        >
                          ▼ ({hiddenButtons.length})
                        </button>
                        {showMoreButtons && (
                          // 2026-05-20: in RTL the chevron sits at the
                          // visual LEFT edge of the row. To keep the menu
                          // on-screen it has to open RIGHTWARD physically
                          // (= leftward visually, toward the page center).
                          // Use inset-inline-end: 0 → maps to `left: 0` in
                          // RTL, which aligns the menu's LEFT edge with
                          // the trigger's LEFT edge and lets it extend
                          // rightward into the visible area. Adding a
                          // physical right fallback keeps LTR sensible too.
                          <div className="slds-dropdown slds-dropdown_right" style={{
                            position: 'absolute',
                            top: '100%',
                            insetInlineEnd: 0,
                            background: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            borderRadius: '4px',
                            zIndex: 9999,
                            minWidth: '220px',
                            padding: '4px 0',
                            marginTop: '4px',
                          }}>
                            {hiddenButtons.map(btn => (
                              <button
                                key={btn.id}
                                type="button"
                                className="slds-dropdown-item"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'right',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowMoreButtons(false);
                                  btn.onClick();
                                }}
                                disabled={btn.disabled}
                                title={btn.title}
                              >
                                {btn.icon && <span>{btn.icon}</span>}
                                {btn.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <>
                <button
                  className="slds-action-button slds-action-button--brand"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? '⏳ שומר...' : '✓ שמור'}
                </button>
                <button
                  className="slds-action-button"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  ✕ ביטול
                </button>
              </>
            )}
          </div>
        </div>

        {/* Highlights Panel - Golden Background */}
        <div className="slds-highlights-panel">
          {highlightFields.map((item, idx) => {
            // Use renderFieldValue for consistent ID-to-name resolution
            return (
              <div key={idx} className="slds-highlight-item">
                <span className="slds-highlight-label">{item.label}</span>
                <span className="slds-highlight-value">
                  {item.fieldName === 'Phone' || item.fieldName?.includes('Phone') || item.fieldName?.includes('phone') || item.fieldName?.includes('Mobile') ? (
                    <a href={`tel:${/^[2-9]\d{7,8}$/.test(String(item.value)) ? '0' + item.value : item.value}`}>{/^[2-9]\d{7,8}$/.test(String(item.value)) ? '0' + item.value : item.value}</a>
                  ) : item.fieldName === 'Email' || item.fieldName?.includes('Email') ? (
                    <a href={`mailto:${item.value}`}>{item.value}</a>
                  ) : item.fieldName === 'Website' ? (
                    <a href={item.value.startsWith('http') ? item.value : `https://${item.value}`} target="_blank" rel="noopener noreferrer">{item.value}</a>
                  ) : (
                    renderFieldValue(item.fieldName, item.value, currentRecord, 'name', true)
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Opportunity Path - Only for Opportunity records */}
      {object.api_name === 'Opportunity' && currentRecord?.StageName && (
        <div style={{ padding: '0 24px', marginBottom: '16px' }}>
          <MixedPricebookBadge
            opportunityId={currentRecord.Id}
            refreshKey={`${currentRecord.Amount ?? ''}|${currentRecord.Pricebook2Id ?? ''}|${(relatedRecords['OpportunityLineItem'] || []).length}`}
          />
          <Suspense fallback={<div style={{height: '50px'}} />}>
            <OpportunityPath
              currentStage={currentRecord.StageName}
              stages={(() => {
                // Get StageName picklist values from object fields
                const stageField = object.fields?.find(f => f.name === 'StageName');
                if (stageField?.picklistValues && stageField.picklistValues.length > 0) {
                  return stageField.picklistValues.map((pv, idx) => {
                    const apiVal = typeof pv === 'object' ? (pv.value || '') : pv;
                    const display = (typeof pv === 'object' && pv.label)
                      || getHebrewPicklistValue(object.api_name, 'StageName', apiVal);
                    return {
                      value: apiVal,
                      label: display,
                      probability: 10 + (idx * 10),
                      order: idx + 1
                    };
                  });
                }
                return undefined; // Use default stages
              })()}
              onMarkComplete={async (newStage) => {
                // Save the stage change to the server
                console.log('Saving stage:', newStage, 'for record:', currentRecord.Id);

                // If moving to Closed Won (check both English and Hebrew), validate first then trigger the work order flow
                if (newStage === 'Closed Won' || newStage === 'סגירה בהצלחה') {
                  console.log('[OpportunityPath] Closed Won detected - validating before work order flow');

                  // 2026-07-30: the legacy 15-username hardcoded exempt list
                  // for the payment / line-item gates was REMOVED. Exemptions
                  // now come from the server-side Validation Exemptions engine
                  // (rules Require_Payment_For_Closed_Won /
                  // Require_Line_Items_for_Closed_Won), resolved into
                  // `feExemptRules` below and editable per user/profile in
                  // Setup → עורך וולידציות. This was requested because the old
                  // list silently exempted whole sales reps (e.g. service13 =
                  // אפרת גמליאל) from the payment requirement, letting them
                  // close a device on ₪0 while a new rep on the same profile
                  // was blocked. Default now: NO ONE is exempt from payment /
                  // line-items until explicitly granted in the editor.

                  // Re-fetch opportunity from server for fresh data before validation
                  // (signed-document check needs the latest value).
                  // 2026-05-15: removed the upstream `/api/opportunity/.../recalc-balance`
                  // call — it existed only to support the balance-validation that's now
                  // removed below (Validation 3). Other balance recalculations still happen
                  // via the unified "💰 תשלום" button flow.
                  let freshRecord = currentRecord;
                  try {
                    const freshRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}?_t=${Date.now()}`);
                    if (freshRes.ok) {
                      const freshData = await freshRes.json();
                      // API returns { record: {...} } for single record — extract it
                      freshRecord = freshData.record || freshData;
                      // Update local state with fresh data
                      setCurrentRecord(prev => ({ ...prev, ...freshRecord }));
                    }
                  } catch (e) {
                    console.warn('[Validation] Could not refresh record, using cached:', e);
                  }

                  // Validation 1: Require signed engagement contract.
                  //
                  // Owner-spec 2026-06-02: re-enable for ALL users except
                  // Admin / Accounting profiles. Replaces the legacy 15-username
                  // hardcoded exempt list with a role-based check via
                  // permission_service. Backend mirror in
                  // unified_server.py:evaluate_validation_rules.
                  //
                  // History: disabled 2026-05-19 for a 90-user load test by
                  // commenting out the FE block + setting the BE env
                  // DISABLE_CLOSED_WON_SIGNED_DOC_VALIDATION=1. Both removed
                  // 2026-06-02.
                  // PR after #804 owner-spec 2026-06-08: institutional
                  // profile is also exempt — B2B bulk orders close
                  // without individual signed engagement contracts.
                  // Owner-spec 2026-06-10: distributors (משווקים) are also
                  // exempt — distributor sales close without per-end-
                  // customer engagement contracts (the contract is with the
                  // distributor, not their downstream customer).
                  // 2026-07-30 PR-A3: consult the server-side Validation
                  // Exemptions engine (single source of truth) so the client
                  // mirror matches the backend instead of duplicating hardcoded
                  // role lists. If the flag is ON and the acting user is exempt
                  // from a rule, skip the client-side block — the backend is
                  // authoritative either way. Fail-closed: on any error we keep
                  // the client validation.
                  // 2026-10-20: the opportunity id lets the engine judge pricebook-scoped
                  // exemptions (e.g. exempt only on RESELLER) from this opportunity's lines.
                  // With the engine ON it also owns the admin / accounting / institutional /
                  // distributor carve-outs below (moved to the editor).
                  let feExemptRules = new Set();
                  let feEngineOn = false;
                  let feMixedPricebooks = false;
                  try {
                    const _uid = currentUser?.id || currentUser?.email || currentUser?.username || '';
                    if (_uid) {
                      const _effR = await fetch(`${API_BASE}/api/validations/exemptions/effective/${encodeURIComponent(_uid)}?opportunity_id=${encodeURIComponent(currentRecord.Id)}`);
                      const _eff = _effR.ok ? await _effR.json() : {};
                      feEngineOn = !!_eff.enabled;
                      feMixedPricebooks = !!_eff.opportunity_mixed_pricebooks;
                      if (feEngineOn) feExemptRules = new Set(_eff.exempt_rule_names || []);
                    }
                  } catch (e) { /* fail-closed: keep client validation */ }
                  const _mixedNote = feMixedPricebooks
                    ? '\n\nשים לב: בהזדמנות יש מוצרים מכמה מחירונים (הזדמנות מעורבת), ולכן החרגה לפי מחירון לא חלה עליה.'
                    : '';

                  // 2026-10-09 (dealer P2-B, D2 + D20): a marketer sale (account Type = Reseller,
                  // flag on) pays after delivery and has no end-customer engagement document — the
                  // signed-document, payment and balance gates do not apply to it.
                  const _mkt = await fetchMarketerContext(currentRecord.Id);
                  const isMarketerClose = !!_mkt.active;

                  const _legacyProfileExempt = !feEngineOn && (permIsAdmin(currentUser) || permIsAccounting(currentUser) || permIsInstitutional(currentUser) || permIsDistributor(currentUser));
                  const isSignedDocExempt = _legacyProfileExempt
                    || feExemptRules.has('Require_Signed_Document_for_Closed_Won') || isMarketerClose;
                  if (!isSignedDocExempt) {
                    const signedDoc = freshRecord.Signed_Document_Link_c__c || '';
                    if (!signedDoc.trim()) {
                      alert('חובה לשלוח הסכם התקשרות ללקוח ולקבל חתימה.\nלאחר שהלקוח חתם ניתן לסגור את ההזדמנות בהצלחה.' + _mixedNote);
                      return;
                    }
                  }

                  // Re-fetch related records for payment check
                  let freshPayments = relatedRecords['Payment__c'] || [];
                  try {
                    const relRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}/related?limit=100&_t=${Date.now()}`);
                    if (relRes.ok) {
                      const relData = await relRes.json();
                      freshPayments = relData.related_objects?.Payment__c?.records || relData.related_objects?.Payment__c || [];
                    }
                  } catch (e) {
                    console.warn('[Validation] Could not refresh related records:', e);
                  }

                  // Validation 2: Require payment.
                  // A closed-won opportunity must have at least one Payment__c
                  // record — this blocks closing a device on ₪0. Exemption is
                  // resolved by the server-side engine (rule
                  // Require_Payment_For_Closed_Won); default = enforced for all.
                  if (freshPayments.length === 0 && !feExemptRules.has('Require_Payment_For_Closed_Won') && !isMarketerClose) {
                    alert('אין אפשרות לסגור הזדמנות בהצלחה ללא תשלום.\nיש ליצור תשלום לפני סגירת ההזדמנות.' + _mixedNote);
                    return;
                  }

                  // Validation 3: Balance must be 0 on at least one side
                  // (mirrors backend unified_server.py:14236 Balance_Close_Validation).
                  //
                  // History (2026-05-15 → 2026-06-02):
                  //   Removed on 2026-05-15 on the assumption that the
                  //   "balance gate at start of flow" (via the 💰 תשלום
                  //   button) covered it. It doesn't — when a user clicks
                  //   the path's Closed-Won straight from an unpaid opp,
                  //   the work-order modal opens, then the SUBMIT path:
                  //     1. Creates WorkOrder + ServiceAppointment + ProductRequired
                  //     2. THEN PUTs Opportunity with Stage='Closed Won'
                  //     3. Backend validation rejects step 2.
                  //   Step 1's records are NOT rolled back — service
                  //   appointment is left orphaned on an unpaid opp.
                  //   Owner-reported 2026-06-02.
                  //
                  // No exempt bypass here: even owner-spec exempt users
                  // shouldn't be allowed to create orphan WO/SA. The
                  // backend rule (no exempt) is the source of truth.
                  //
                  // Uses freshRecord from the GET above, not stale
                  // currentRecord, since balance fields are recalculated
                  // asynchronously by Payment writes via outbox events.
                  const _actualBalance = parseFloat(freshRecord?.Actual_Balance_To_Be_Paid__c) || 0;
                  const _fictBalance   = parseFloat(freshRecord?.Fictitious_Balance_Due__c)   || 0;
                  // 2026-07-30 PR-A3: honor a server-side exemption for this rule
                  // too. Safe re: the orphan-WO concern — if the user is exempt
                  // the backend won't reject the Closed-Won PUT, so no orphan.
                  if (_actualBalance > 0 && _fictBalance > 0 && !feExemptRules.has('Balance_Close_Validation') && !isMarketerClose) {
                    alert(
                      'אין אפשרות לסגור הזדמנות בהצלחה.\n' +
                      `יתרה בפועל: ₪${_actualBalance.toLocaleString()}\n` +
                      `יתרה פיקטיבית: ₪${_fictBalance.toLocaleString()}\n` +
                      'לפחות אחת מהיתרות חייבת להיות על 0.' + _mixedNote
                    );
                    return;
                  }

                  // Check if Type (work type) is already set - if so, skip work order modal
                  const existingType = (currentRecord.Type || '').trim();
                  if (existingType) {
                    console.log('[OpportunityPath] Type already set:', existingType, '- skipping work order flow');
                    // Type is filled - close directly without work order modal
                    // Fall through to the normal stage update below
                  } else {
                    // 2026-05-20: hard guard — no line items, no close.
                    // The work-order flow downstream needs at least one
                    // OpportunityLineItem to derive Type / pick a serial.
                    const lineItems = relatedRecords['OpportunityLineItem'] || [];
                    if (lineItems.length === 0 && !feExemptRules.has('Require_Line_Items_for_Closed_Won')) {
                      alert('אין אפשרות לסגור הזדמנות בהצלחה ללא פריטי הזדמנות.\nיש להוסיף לפחות פריט אחד לפני סגירה.');
                      return;
                    }
                    // Type is empty - trigger work order modal (which now
                    // pre-selects the recommended Type based on the rules
                    // in computeRecommendedWorkType()).
                    await triggerWorkOrderFlow(newStage, { marketer: isMarketerClose });
                    return; // The work order flow will handle the stage change
                  }
                }

                try {
                  // Add user tracking fields - get from recordService
                  const updateData = { StageName: newStage };
                  const currentUser = recordService.getCurrentUser();
                  if (currentUser) {
                    const userId = currentUser.id || currentUser.Id || currentUser.email;
                    const userName = currentUser.name || currentUser.Name || currentUser.email;
                    updateData.LastModifiedById = userId;
                    updateData.LastModifiedByName = userName;
                  }

                  const response = await fetch(`${API_BASE}/api/objects/Opportunity/records/${currentRecord.Id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData)
                  });
                  console.log('Response status:', response.status);
                  if (response.ok) {
                    // Update local state
                    setCurrentRecord(prev => ({ ...prev, StageName: newStage }));
                    if (onRecordUpdate) {
                      onRecordUpdate({ ...currentRecord, StageName: newStage });
                    }
                  } else {
                    const errorData = await response.json();
                    console.error('Error response:', errorData);
                    // Handle validation error - detail can be string or object with errors array
                    let errorMessage = 'שגיאה לא ידועה';
                    if (errorData.detail) {
                      if (typeof errorData.detail === 'string') {
                        errorMessage = errorData.detail;
                      } else if (errorData.detail.errors && Array.isArray(errorData.detail.errors)) {
                        // Validation errors - extract messages from errors array
                        errorMessage = errorData.detail.errors.map(e => e.error_message || e.message || e).join('\n');
                      } else if (errorData.detail.message) {
                        errorMessage = errorData.detail.message;
                      } else if (errorData.detail.error_message) {
                        errorMessage = errorData.detail.error_message;
                      }
                    }
                    alert(errorMessage);
                  }
                } catch (error) {
                  console.error('Error updating stage:', error);
                  alert('שגיאה בעדכון השלב');
                }
              }}
            />
          </Suspense>
        </div>
      )}

      {/* Content */}
      <div className="slds-record-content">
        {/* Related Lists (Left) - Clickable */}
        <div className="slds-record-related">
          {/* Loading indicator removed by request — lists render directly
              from the related-records frontend cache so revisits are instant
              and first-load doesn't flash a spinner above empty sections. */}
          {relatedLists.map((list, idx) => {
            const isExpanded = expandedRelatedLists[list.objectName || list.name];
            const records = list.type === 'parent' && list.linkedRecord
              ? [list.linkedRecord]
              : [...(relatedRecords[list.objectName] || [])].sort((a, b) => {
                  const dateA = a.CreatedDate || a.LastModifiedDate || '';
                  const dateB = b.CreatedDate || b.LastModifiedDate || '';
                  return dateB.localeCompare(dateA);
                });
            // 2026-05-14: Payment__c on Opportunity — split into verified /
            // unverified / documents sections. Verified credit-card =
            // Verified_With_iCredit__c='1'; all other payment types =
            // Payment_Status__c='Success'. Documents (2026-06-17 owner-spec)
            // are pure accounting records carved out so they don't get mixed
            // with cash flow — credit_invoice / negative_receipt.
            const _isPaymentOnOpp = list.objectName === 'Payment__c' && object?.api_name === 'Opportunity';
            const _isDocumentPayment = (p) => {
              const dt = (p.Document_Type__c || '').trim();
              return ['credit_invoice', 'negative_receipt', 'חשבונית זיכוי', 'קבלה במינוס'].includes(dt);
            };
            const _isVerifiedPayment = (p) => {
              if ((p.Payment_Type__c || '') === 'כרטיס אשראי') return p.Verified_With_iCredit__c === '1';
              return p.Payment_Status__c === 'Success';
            };
            const _dRec = _isPaymentOnOpp ? records.filter(_isDocumentPayment) : [];
            const _vRec = _isPaymentOnOpp ? records.filter(r => !_isDocumentPayment(r) && _isVerifiedPayment(r)) : [];
            const _uRec = _isPaymentOnOpp ? records.filter(r => !_isDocumentPayment(r) && !_isVerifiedPayment(r)) : [];
            const _displayRecords = _isPaymentOnOpp ? [..._vRec, ..._uRec, ..._dRec] : records;
            const _verifiedCount = _vRec.length;
            const _unverifiedCount = _uRec.length;
            const _documentCount = _dRec.length;
            const isLoading = loadingRelated[list.objectName] || loadingRelated.all;
            // Prefer the fast-path count when we have one — it lands in
            // ~100ms while the full record set is still being assembled.
            // Falls back to the explicit list.recordCount or the local
            // records.length for objects the count endpoint doesn't cover.
            const fastCount = relatedCounts[list.objectName];
            const recordCount = (fastCount !== undefined)
              ? fastCount
              : (list.recordCount !== undefined ? list.recordCount : records.length);
            const haveCount = (fastCount !== undefined) || !isLoading;

            return (
              <div key={idx} className={`slds-related-list ${isExpanded ? 'expanded' : ''} ${list.type === 'parent' ? 'slds-related-list--parent' : ''}`}>
                <div
                  className="slds-related-list__header"
                  onClick={() => toggleRelatedList(list.objectName || list.name, list.objectName)}
                >
                  <span className="slds-collapse-icon">{isExpanded ? '▼' : '►'}</span>
                  <span className="slds-related-list__icon" style={{ backgroundColor: list.color }}>
                    {list.icon}
                  </span>
                  <span className="slds-related-list__label">
                    {list.label}
                    {list.type !== 'parent' && (
                      // Show the count as soon as we have one — fast-path
                      // /related-counts lands in ~100ms so this normally
                      // beats the heavier /related fetch and the operator
                      // sees real numbers immediately. Only show the '(…)'
                      // placeholder when we genuinely don't know yet.
                      haveCount
                        ? ` (${recordCount})`
                        : <span className="slds-related-list__count-loading"> (…)</span>
                    )}
                  </span>
                  {list.type === 'parent' && <span className="slds-related-list__badge">קישור</span>}
                  {/* 2026-05-18: "+ הוסף מוצר נדרש" button on the WorkOrder's
                      ProductRequired related list. Opens AddProductRequiredModal,
                      which calls the manual-add endpoint (quota-gated). Other
                      objects' related lists don't get this button yet. */}
                  {list.objectName === 'ProductRequired' && object?.api_name === 'WorkOrder' && (
                    <button
                      className="slds-related-list__add-button"
                      style={{
                        marginInlineStart: 'auto',
                        padding: '4px 12px',
                        background: '#0176d3',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      onClick={(e) => { e.stopPropagation(); setShowAddProductRequiredModal(true); }}
                    >
                      + הוסף מוצר נדרש
                    </button>
                  )}
                </div>

                {/* Related Records Content */}
                {isExpanded && (
                  <div className="slds-related-list__content">
                    {isLoading && list.type !== 'parent' ? (
                      <div className="slds-related-list__loading">
                        <span>טוען...</span>
                      </div>
                    ) : records.length === 0 ? (
                      <div className="slds-related-list__empty">
                        אין רשומות קשורות
                      </div>
                    ) : list.objectName === 'Activity_Barak__c' ? (
                      <BarakActivitiesList records={records} total={recordCount} />
                    ) : list.objectName === 'ServiceAppointmentHistory' ? (
                      /* Special rendering for Service Appointment History */
                      <div className="sa-history-container">
                        {!showAllHistory ? (
                          /* Compact card view - 3 entries */
                          <div className="sa-history-compact">
                            {records.slice(0, 3).map((rec, recIdx) => {
                              const formatDT = (dateStr) => {
                                if (!dateStr) return '';
                                let ds = dateStr;
                                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
                                const d = new Date(ds);
                                return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                              };
                              const fieldLabels = {
                                'SchedStartTime': 'התחלה מתוזמנת', 'SchedEndTime': 'סיום מתוזמן',
                                'Status': 'מצב', 'StatusCategory': 'קטגוריית מצב',
                                'ServiceTerritoryId': 'אזור שירות', 'ServiceResourceId': 'משאב שירות',
                                'Service_Resource__c': 'משאב שירות', 'Collect_Old_Device__c': 'לאסוף מכשיר ישן',
                                'Latitude': 'קו רוחב', 'Longitude': 'קו אורך',
                                'DueDate': 'תאריך יעד', 'EarliestStartTime': 'התחלה מוקדמת',
                                'ArrivalWindowStartTime': 'חלון הגעה - התחלה', 'ArrivalWindowEndTime': 'חלון הגעה - סיום',
                                'ActualStartTime': 'התחלה בפועל', 'ActualEndTime': 'סיום בפועל',
                                'Duration': 'משך', 'DurationType': 'סוג משך',
                                'WorkTypeId': 'סוג עבודה', 'ParentRecordId': 'רשומת אב',
                                'Subject': 'נושא', 'Description': 'תיאור',
                                'Street': 'רחוב', 'City': 'עיר', 'PostalCode': 'מיקוד',
                                'AccountId': 'חשבון', 'ContactId': 'איש קשר',
                                'GeoJSON__c': 'דיוק קוד גיאוגרפי',
                                'FSL__Pinned__c': 'נעוץ', 'Resource_Description__c': 'תיאור משאב',
                                'OwnerId': 'בעלים', 'Priority': 'עדיפות',
                              };
                              const fieldLabel = fieldLabels[rec._histField] || rec._histField || '';
                              const formatValue = (val, field) => {
                                if (!val) return '';
                                if (['SchedStartTime','SchedEndTime','DueDate','EarliestStartTime','ArrivalWindowStartTime','ArrivalWindowEndTime','ActualStartTime','ActualEndTime'].includes(field)) {
                                  return formatDT(val);
                                }
                                if (field === 'Status') return getHebrewPicklistValue('ServiceAppointment', 'Status', val);
                                return val;
                              };
                              return (
                                <div key={recIdx} className="sa-history-entry" style={{
                                  padding: '10px 14px', borderBottom: recIdx < 2 ? '1px solid #e5e5e5' : 'none',
                                  direction: 'rtl', fontSize: '13px', lineHeight: '1.7',
                                }}>
                                  <div><span style={{color:'#706e6b', fontWeight:'500'}}>תאריך:</span> <span>{formatDT(rec._histDate)}</span></div>
                                  {rec._histType === 'created' ? (
                                    <>
                                      <div><span style={{color:'#706e6b', fontWeight:'500'}}>פעולה:</span> <span style={{color:'#4caf50', fontWeight:'600'}}>רשומה נוצרה</span></div>
                                      <div><span style={{color:'#706e6b', fontWeight:'500'}}>משתמש:</span> <span style={{color:'#0176d3'}}>{rec._histUser}</span></div>
                                    </>
                                  ) : (
                                    <>
                                      <div><span style={{color:'#706e6b', fontWeight:'500'}}>שדה:</span> <span>{fieldLabel}</span></div>
                                      <div><span style={{color:'#706e6b', fontWeight:'500'}}>משתמש:</span> <span style={{color:'#0176d3'}}>{rec._histUser}</span></div>
                                      {rec._histOldValue && <div><span style={{color:'#706e6b', fontWeight:'500'}}>ערך מקורי:</span> <span>{formatValue(rec._histOldValue, rec._histField)}</span></div>}
                                      <div><span style={{color:'#706e6b', fontWeight:'500'}}>ערך חדש:</span> <span>{formatValue(rec._histNewValue, rec._histField)}</span></div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            {records.length > 3 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowAllHistory(true); }}
                                style={{
                                  display: 'block', width: '100%', padding: '10px', textAlign: 'center',
                                  background: 'none', border: 'none', color: '#0176d3', cursor: 'pointer',
                                  fontWeight: '600', fontSize: '13px',
                                }}
                              >
                                הצג הכול
                              </button>
                            )}
                          </div>
                        ) : (
                          /* Full table view */
                          <div className="sa-history-table-wrap" style={{ overflowX: 'auto' }}>
                            <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e5e5' }}>
                              <span style={{ fontSize: '12px', color: '#706e6b' }}>{records.length} פריטים · ממויין לפי תאריך · עודכן לפני מספר שניות</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowAllHistory(false); }}
                                style={{ background: 'none', border: 'none', color: '#0176d3', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                              >
                                חזור לתצוגה מקוצרת
                              </button>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ background: '#fafaf9', borderBottom: '2px solid #e5e5e5' }}>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d', width: '30px' }}>#</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d' }}>תאריך</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d' }}>שדה</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d' }}>משתמש</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d' }}>ערך מקורי</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#514f4d' }}>ערך חדש</th>
                                </tr>
                              </thead>
                              <tbody>
                                {records.map((rec, recIdx) => {
                                  const formatDT = (dateStr) => {
                                    if (!dateStr) return '';
                                    let ds = dateStr;
                                    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
                                    const d = new Date(ds);
                                    return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                  };
                                  const fieldLabels = {
                                    'SchedStartTime': 'התחלה מתוזמנת', 'SchedEndTime': 'סיום מתוזמן',
                                    'Status': 'מצב', 'StatusCategory': 'קטגוריית מצב',
                                    'ServiceTerritoryId': 'אזור שירות', 'ServiceResourceId': 'משאב שירות',
                                    'Service_Resource__c': 'משאב שירות', 'Collect_Old_Device__c': 'לאסוף מכשיר ישן',
                                    'Latitude': 'קו רוחב', 'Longitude': 'קו אורך',
                                    'DueDate': 'תאריך יעד', 'EarliestStartTime': 'התחלה מוקדמת',
                                    'ArrivalWindowStartTime': 'חלון הגעה - התחלה', 'ArrivalWindowEndTime': 'חלון הגעה - סיום',
                                    'ActualStartTime': 'התחלה בפועל', 'ActualEndTime': 'סיום בפועל',
                                    'Duration': 'משך', 'DurationType': 'סוג משך',
                                    'WorkTypeId': 'סוג עבודה', 'ParentRecordId': 'רשומת אב',
                                    'Subject': 'נושא', 'Description': 'תיאור',
                                    'Street': 'רחוב', 'City': 'עיר', 'PostalCode': 'מיקוד',
                                    'AccountId': 'חשבון', 'ContactId': 'איש קשר',
                                    'GeoJSON__c': 'דיוק קוד גיאוגרפי',
                                    'FSL__Pinned__c': 'נעוץ', 'Resource_Description__c': 'תיאור משאב',
                                    'OwnerId': 'בעלים', 'Priority': 'עדיפות',
                                  };
                                  const fieldLabel = fieldLabels[rec._histField] || rec._histField || '';
                                  const formatValue = (val, field) => {
                                    if (!val) return '';
                                    if (['SchedStartTime','SchedEndTime','DueDate','EarliestStartTime','ArrivalWindowStartTime','ArrivalWindowEndTime','ActualStartTime','ActualEndTime'].includes(field)) {
                                      return formatDT(val);
                                    }
                                    if (field === 'Status') return getHebrewPicklistValue('ServiceAppointment', 'Status', val);
                                    return val;
                                  };
                                  return (
                                    <tr key={recIdx} style={{ borderBottom: '1px solid #e5e5e5' }}>
                                      <td style={{ padding: '7px 10px', color: '#706e6b' }}>{recIdx + 1}</td>
                                      <td style={{ padding: '7px 10px' }}>{formatDT(rec._histDate)}</td>
                                      <td style={{ padding: '7px 10px' }}>{rec._histType === 'created' ? <span style={{color:'#4caf50',fontWeight:600}}>רשומה נוצרה</span> : fieldLabel}</td>
                                      <td style={{ padding: '7px 10px', color: '#0176d3' }}>{rec._histUser}</td>
                                      <td style={{ padding: '7px 10px' }}>{formatValue(rec._histOldValue, rec._histField)}</td>
                                      <td style={{ padding: '7px 10px' }}>{formatValue(rec._histNewValue, rec._histField)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="slds-related-list__records">
                        {_displayRecords.slice(0, list.type === 'parent' ? 1 : (expandedRelatedLists[`${list.objectName}_all`] ? _displayRecords.length : 3)).map((rec, recIdx) => {
                          // 2026-05-14: section dividers for Payment__c on Opp.
                          // Render the 'verified' header before the very first
                          // record (when there are any verified payments), and
                          // the 'unverified' header at the boundary between
                          // groups. When verifiedCount=0, only the unverified
                          // header renders (above the first record).
                          let _paymentSectionHeader = null;
                          if (_isPaymentOnOpp) {
                            // 2026-06-17 owner-spec: 3 sections — verified, unverified, documents.
                            // _displayRecords order = [verified..., unverified..., documents...].
                            // Render the header at each section's first index. The header for an
                            // empty section is suppressed.
                            const _vHeader = (
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: '#0f5132',
                                background: '#d1f2dd', borderTop: '1px solid #badbcc',
                                borderBottom: '1px solid #badbcc',
                                padding: '5px 10px', margin: '4px -8px',
                                letterSpacing: '0.02em',
                              }}>
                                ✅ תשלומים מאומתים ({_verifiedCount})
                              </div>
                            );
                            const _uHeader = (
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: '#856404',
                                background: '#fff3cd', borderTop: '1px solid #ffe69c',
                                borderBottom: '1px solid #ffe69c',
                                padding: '5px 10px', margin: '8px -8px 4px',
                                letterSpacing: '0.02em',
                              }}>
                                ⚠ תשלומים לא מאומתים ({_unverifiedCount})
                              </div>
                            );
                            const _dHeader = (
                              <div style={{
                                fontSize: 11, fontWeight: 700, color: '#1e40af',
                                background: '#dbeafe', borderTop: '1px solid #93c5fd',
                                borderBottom: '1px solid #93c5fd',
                                padding: '5px 10px', margin: '8px -8px 4px',
                                letterSpacing: '0.02em',
                              }}>
                                📄 מסמכים ({_documentCount})
                              </div>
                            );
                            const _uStart = _verifiedCount;
                            const _dStart = _verifiedCount + _unverifiedCount;
                            if (recIdx === 0 && _verifiedCount > 0) {
                              _paymentSectionHeader = _vHeader;
                            } else if (recIdx === _uStart && _unverifiedCount > 0) {
                              _paymentSectionHeader = _uHeader;
                            } else if (recIdx === _dStart && _documentCount > 0) {
                              _paymentSectionHeader = _dHeader;
                            }
                          }
                          // Get display name - prioritize human-readable identifiers over IDs
                          const getDisplayName = () => {
                            // For ProductRequired - show Product name
                            if (list.objectName === 'ProductRequired') {
                              return rec.Product_Name__c || rec.Product2Id__Name || rec.ProductName || rec.Name || 'מוצר נדרש';
                            }
                            // For OpportunityLineItem - show Product name
                            if (list.objectName === 'OpportunityLineItem') {
                              return rec.Product2__Name || rec.Product2Id__Name || rec.ProductName || rec.Name || rec.Description || 'מוצר';
                            }
                            // For AssignedResource - show ServiceResource name
                            if (list.objectName === 'AssignedResource') {
                              return rec.ServiceResourceId__Name || rec.ServiceResource__Name || rec.Name || 'משאב';
                            }
                            // For ServiceTerritory - show territory name
                            if (list.objectName === 'ServiceTerritory') {
                              return rec.Name || rec.Description || 'אזור שירות';
                            }
                            // For ServiceTerritoryMember - show territory name
                            if (list.objectName === 'ServiceTerritoryMember') {
                              return rec.ServiceTerritoryId__Name || rec.ServiceTerritory__Name || rec.Name || 'חברות באזור';
                            }
                            // For Asset - show asset name
                            if (list.objectName === 'Asset') {
                              return rec.Name || rec.SerialNumber || rec.Product2Id__Name || 'נכס';
                            }
                            // For ServiceAppointment - show AppointmentNumber
                            if (list.objectName === 'ServiceAppointment') {
                              return rec.AppointmentNumber || rec.Name || rec.Subject || 'פגישת שירות';
                            }
                            // For WorkOrder - show WorkOrderNumber
                            if (list.objectName === 'WorkOrder') {
                              return rec.WorkOrderNumber || rec.Name || rec.Subject || 'הזמנת עבודה';
                            }
                            // For Case - show CaseNumber
                            if (list.objectName === 'Case') {
                              return rec.CaseNumber || rec.Subject || rec.Name || 'תיק';
                            }
                            // For Contact - show full name
                            if (list.objectName === 'Contact') {
                              if (rec.FirstName || rec.LastName) {
                                return `${rec.FirstName || ''} ${rec.LastName || ''}`.trim();
                              }
                              return rec.Name || rec.Email || 'איש קשר';
                            }
                            // For User - show name
                            if (list.objectName === 'User') {
                              return rec.Name || rec.Username || 'משתמש';
                            }
                            // For ServiceResource - show name
                            if (list.objectName === 'ServiceResource') {
                              return rec.Name || rec.RelatedRecordId__Name || 'משאב שירות';
                            }
                            // For ServiceContract - show name/number
                            if (list.objectName === 'ServiceContract') {
                              return rec.Name || rec.ContractNumber || 'חוזה שירות';
                            }
                            // For Product2 - show name
                            if (list.objectName === 'Product2') {
                              return rec.Name || rec.ProductCode || 'מוצר';
                            }
                            // For LabRepair - show repair number
                            if (list.objectName === 'LabRepair') {
                              return rec.repair_number || rec.Name || 'תיקון מעבדה';
                            }
                            // For EmailMessage - show subject with direction indicator
                            if (list.objectName === 'EmailMessage') {
                              const direction = rec.Incoming ? '📥' : '📤';
                              return `${direction} ${rec.Subject || 'ללא נושא'}`;
                            }
                            // For EntityHistory - show field name and change
                            if (list.objectName === 'EntityHistory') {
                              const fieldName = rec.FieldName || '';
                              const date = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              return `${fieldName} - ${date}`;
                            }
                            // For Payment__c - show payment type
                            if (list.objectName === 'Payment__c') {
                              const type = rec.Payment_Type__c || '';
                              return getHebrewPicklistValue('Payment__c', 'Payment_Type__c', type) || 'תשלום';
                            }
                            // Default order - prefer Name, then other identifiers
                            return rec.Name || rec.Subject || rec.Title || rec.CaseNumber || rec.WorkOrderNumber || rec.AppointmentNumber || 'ללא שם';
                          };
                          const displayName = getDisplayName();

                          // פרטים נוספים לפי סוג האובייקט
                          const getRecordDetails = () => {
                            if (list.objectName === 'ProductRequired') {
                              const productName = rec.Product2Id__Name || rec.Product2__Name || rec.Product2?.Name || '';
                              return (
                                <div className="slds-related-record__details">
                                  {productName && <span className="slds-related-record__field" style={{ fontWeight: '500' }}>{productName}</span>}
                                  {rec.QuantityRequired && <span className="slds-related-record__field">כמות: {rec.QuantityRequired}</span>}
                                  {rec.Reason__c && <span className="slds-related-record__field">סיבה: {rec.Reason__c}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'OpportunityLineItem') {
                              const productCode = rec.ProductCode || rec.Product2__ProductCode || '';
                              const unitP = parseFloat(rec.UnitPrice) || 0;
                              const totalP = parseFloat(rec.TotalPrice) || 0;
                              // Owner-spec 2026-06-10: distributor pricebook OLIs
                              // can have Device_At_Customer__c='1' meaning the
                              // device is already at the customer's home and
                              // shouldn't be delivered. Show a bold red banner
                              // so reps can't miss it when reviewing the opp.
                              const deviceAtCustomer = String(rec.Device_At_Customer__c || '') === '1';
                              return (
                                <div className="slds-related-record__details">
                                  {productCode && <span className="slds-related-record__field" style={{ color: '#0176d3', fontWeight: 500 }}>קוד: {productCode}</span>}
                                  {rec.Quantity && <span className="slds-related-record__field">כמות: {rec.Quantity}</span>}
                                  {unitP > 0 && <span className="slds-related-record__field">מחיר: ₪{unitP.toLocaleString()}</span>}
                                  {totalP > 0 && <span className="slds-related-record__field">סה"כ: ₪{totalP.toLocaleString()}</span>}
                                  <DiscountLineChip summary={discountSummary} lineId={rec.Id} />
                                  {deviceAtCustomer && (
                                    <div style={{
                                      flexBasis: '100%',
                                      marginTop: '6px',
                                      padding: '8px 12px',
                                      background: '#fef2f2',
                                      border: '2px solid #c23934',
                                      borderRadius: '6px',
                                      color: '#c23934',
                                      fontSize: '15px',
                                      fontWeight: 700,
                                      textAlign: 'center',
                                      letterSpacing: '0.02em',
                                    }}>
                                      ⚠ המכשיר בבית לקוח — לא לספק
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            if (list.objectName === 'Payment__c') {
                              const status = rec.Payment_Status__c || '';
                              // 2026-06-17 owner-spec: full status color matrix.
                              //   green  → completed / paid
                              //   orange → in-flight / awaiting action
                              //   red    → failed / cancelled / returned
                              // Case-insensitive trim because real values arrive
                              // mixed-case ('Success', 'Error', 'In process').
                              const _sNorm = status.toLowerCase().trim();
                              const _GREEN = ['success', 'paid', 'שולם', 'הצלחה'];
                              const _RED   = ['error', 'failed', 'נכשל', 'returned', 'שיק חוזר',
                                              'תשלום בוטל', 'תשלום בוטל חלקית',
                                              'תשלום בוטל בחברת האשראי'];
                              const _ORANGE = ['in process', 'pending', 'ממתין', 'ממתין לתשלום',
                                               'need to be paid', 'create receipt', 'create invoice',
                                               'verify', 'אימות נדרש', 'יצירת קבלה', 'יצירת חשבונית'];
                              const statusColor = _GREEN.includes(_sNorm) ? '#16a34a'
                                                : _RED.some(v => _sNorm === v.toLowerCase()) ? '#dc2626'
                                                : _ORANGE.some(v => _sNorm === v.toLowerCase()) ? '#ea580c'
                                                : '#737373';
                              // 2026-06-01 owner-spec: also render the
                              // per-check breakdown (number / due date /
                              // bank / branch / account) on returned
                              // checks — they mirror the original check's
                              // metadata so the rep can see what bounced
                              // without opening the record. The "mark
                              // returned" button gate stays on positive
                              // checks only (returned checks already
                              // have their status set).
                              const isPositiveCheck = rec.Payment_Type__c === 'שיק' || rec.Payment_Type__c === 'check' || rec.Payment_Type__c === 'Check';
                              const isReturnedCheck = rec.Payment_Type__c === 'שיק חוזר';
                              const isCheck = isPositiveCheck || isReturnedCheck;
                              // 2026-10-09 owner-spec: a standing order has bank details
                              // too, and until today the card hid them — these three
                              // fields were written by the cheque path only, so the gate
                              // below was `isCheck` and nothing else ever filled them.
                              // The ERN path now writes them on every approved mandate.
                              const isStandingOrder = rec.Payment_Type__c === 'הוראת קבע';
                              const canMarkReturned = isPositiveCheck && status !== 'Returned' &&
                                (permIsAccounting(currentUser));
                              return (
                                <div
                                  className="slds-related-record__details"
                                  style={isReturnedCheck ? {
                                    /* 2026-06-01 owner-spec: returned-check rows must be
                                       visually unmistakable. Soft red wash + red left rail +
                                       red text so a rep scanning the related list spots a
                                       bounce instantly. */
                                    background: '#fef2f2',
                                    borderRight: '4px solid #dc2626',
                                    color: '#991b1b',
                                    borderRadius: 4,
                                    padding: '6px 10px',
                                  } : undefined}
                                >
                                  {/* 2026-05-31 owner-spec: for check payments
                                      show the per-check breakdown — מס' שיק,
                                      תאריך פירעון, סכום, בנק, סניף, מספר חשבון
                                      — instead of just sum + message. Falls
                                      back to the legacy summary line for
                                      non-check payments. */}
                                  {isCheck && rec.Check_Number__c && (
                                    <span className="slds-related-record__field">מס׳ שיק: {rec.Check_Number__c}</span>
                                  )}
                                  {isCheck && rec.Check_Due_Date__c && (
                                    <span className="slds-related-record__field">פירעון: {rec.Check_Due_Date__c}</span>
                                  )}
                                  {rec.Amount__c && <span className="slds-related-record__field">סכום: ₪{Number(rec.Amount__c).toLocaleString()}</span>}
                                  {/* 2026-06-18 owner-spec: name line under amount
                                      (replaced the Description__c line that was
                                      added 2026-06-17). Owner wants the Payment
                                      record Name shown here instead of the
                                      free-text description — Name is the
                                      human-readable identifier of the payment. */}
                                  {rec.Name && (
                                    <span className="slds-related-record__field" style={{ flexBasis: '100%', fontSize: '11px', color: '#475569', fontStyle: 'italic', marginTop: '2px' }}>
                                      שם: {rec.Name}
                                    </span>
                                  )}
                                  {(isCheck || isStandingOrder) && rec.Bank_Name__c && (
                                    <span className="slds-related-record__field">בנק: {rec.Bank_Name__c}</span>
                                  )}
                                  {(isCheck || isStandingOrder) && rec.Bank_Branch__c && (
                                    <span className="slds-related-record__field">סניף: {rec.Bank_Branch__c}</span>
                                  )}
                                  {(isCheck || isStandingOrder) && rec.Bank_Account_Number__c && (
                                    <span className="slds-related-record__field">חשבון: {rec.Bank_Account_Number__c}</span>
                                  )}
                                  {isStandingOrder && rec.Standing_Order_First_Debit__c && (
                                    <span className="slds-related-record__field">חיוב ראשון: {rec.Standing_Order_First_Debit__c}</span>
                                  )}
                                  {/* The number the signing request was sent to. Audit, not
                                      a figure to scan — dimmed like the name line above. */}
                                  {isStandingOrder && rec.Standing_Order_Phone__c && (
                                    <span className="slds-related-record__field" style={{ flexBasis: '100%', fontSize: '11px', color: '#475569' }}>
                                      נשלח לאישור ל-{rec.Standing_Order_Phone__c}
                                    </span>
                                  )}
                                  {/* 2026-06-17 owner-spec: admin / accounting can override the
                                      payment status — primarily for setting "תשלום בוטל" /
                                      "תשלום בוטל חלקית" on payments that were reversed in the
                                      credit-card company. Inline <select> avoids opening a modal. */}
                                  {status && (() => {
                                    const _canEditStatus = permIsAdmin(currentUser) || permIsAccounting(currentUser);
                                    const _isEditing = editingPaymentStatusId === rec.Id;
                                    const _isSaving = savingPaymentStatusId === rec.Id;
                                    if (_isEditing && _canEditStatus) {
                                      return (
                                        <span className="slds-related-record__status" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                          <select
                                            autoFocus
                                            disabled={_isSaving}
                                            defaultValue={status}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={() => { if (!_isSaving) setEditingPaymentStatusId(null); }}
                                            onChange={async (e) => {
                                              e.stopPropagation();
                                              const newStatus = e.target.value;
                                              if (newStatus === status) { setEditingPaymentStatusId(null); return; }
                                              setSavingPaymentStatusId(rec.Id);
                                              try {
                                                const r = await fetch(`${API_BASE}/api/objects/Payment__c/records/${rec.Id}`, {
                                                  method: 'PUT',
                                                  headers: { 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ data: { Payment_Status__c: newStatus } })
                                                });
                                                if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'שגיאה');
                                                setRelatedRecords(prev => ({
                                                  ...prev,
                                                  Payment__c: (prev.Payment__c || []).map(p =>
                                                    p.Id === rec.Id ? { ...p, Payment_Status__c: newStatus } : p
                                                  )
                                                }));
                                              } catch (err) {
                                                alert('שגיאה בעדכון סטטוס: ' + (err.message || err));
                                              } finally {
                                                setSavingPaymentStatusId(null);
                                                setEditingPaymentStatusId(null);
                                              }
                                            }}
                                            style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid #cbd5e1' }}
                                          >
                                            <option value="In process">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'In process')}</option>
                                            <option value="Verify">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Verify')}</option>
                                            <option value="Success">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Success')}</option>
                                            <option value="Error">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Error')}</option>
                                            <option value="Need To Be Paid">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Need To Be Paid')}</option>
                                            <option value="Create Receipt">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Create Receipt')}</option>
                                            <option value="Create Invoice">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Create Invoice')}</option>
                                            <option value="Returned">{getHebrewPicklistValue('Payment__c', 'Payment_Status__c', 'Returned')}</option>
                                            <option value="תשלום בוטל">תשלום בוטל</option>
                                            <option value="תשלום בוטל חלקית">תשלום בוטל חלקית</option>
                                          </select>
                                          {_isSaving && <span style={{ fontSize: 10, color: '#6b7280' }}>שומר…</span>}
                                        </span>
                                      );
                                    }
                                    return (
                                      <span
                                        className="slds-related-record__status"
                                        onClick={_canEditStatus ? (e) => { e.preventDefault(); e.stopPropagation(); setEditingPaymentStatusId(rec.Id); } : undefined}
                                        title={_canEditStatus ? 'לחץ לעריכת סטטוס' : undefined}
                                        style={{ color: statusColor, fontWeight: '600', cursor: _canEditStatus ? 'pointer' : 'default' }}
                                      >
                                        {getHebrewPicklistValue('Payment__c', 'Payment_Status__c', status)}
                                        {_canEditStatus && <span style={{ marginInlineStart: 4, opacity: 0.5, fontSize: 10 }}>✏️</span>}
                                      </span>
                                    );
                                  })()}
                                  {/* Payment_Message__c: non-check + returned-check (which carries
                                      "קבלה זו מזכה ..."). Positive checks rarely have a meaningful
                                      message — the per-check fields above already cover it. */}
                                  {rec.Payment_Message__c && (!isPositiveCheck || isReturnedCheck) && <span className="slds-related-record__field" style={{ fontSize: '11px', opacity: 0.8 }}>{rec.Payment_Message__c}</span>}
                                  {canMarkReturned && (
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedCheckForReturn(rec); setShowReturnedCheckModal(true); }}
                                      title="סמן את השיק כחוזר ופתח מודאל עיבוד"
                                      style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginInlineStart: 6 }}>
                                      ↩ שיק חוזר
                                    </button>
                                  )}
                                </div>
                              );
                            }
                            if (list.objectName === 'EmailMessage') {
                              const formatDT = (dateStr) => {
                                if (!dateStr) return '';
                                let ds = dateStr;
                                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
                                const d = new Date(ds);
                                return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                              };
                              return (
                                <div className="slds-related-record__details">
                                  <span className="slds-related-record__field" style={{ fontWeight: '500' }}>
                                    {rec.Incoming ? 'מאת' : 'אל'}: {rec.Incoming ? (rec.FromName || rec.FromAddress) : rec.ToAddress}
                                  </span>
                                  {rec.MessageDate && <span className="slds-related-record__field">{formatDT(rec.MessageDate)}</span>}
                                  {rec.TextBody && <span className="slds-related-record__field" style={{ fontSize: '11px', opacity: 0.8 }}>{rec.TextBody.substring(0, 80)}{rec.TextBody.length > 80 ? '...' : ''}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'AssignedResource') {
                              const parentSA = record; // parent ServiceAppointment
                              const formatDT = (dateStr) => {
                                if (!dateStr) return '';
                                let ds = dateStr;
                                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
                                const d = new Date(ds);
                                return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                              };
                              return (
                                <div className="slds-related-record__details">
                                  {parentSA?.SchedStartTime && <span className="slds-related-record__field">מועד: {formatDT(parentSA.SchedStartTime)}</span>}
                                  {(parentSA?.WorkType__Name || parentSA?.WorkTypeId__Name) && <span className="slds-related-record__field">סוג: {parentSA.WorkType__Name || parentSA.WorkTypeId__Name}</span>}
                                  {rec.EstimatedTravelTime && <span className="slds-related-record__field">נסיעה: {rec.EstimatedTravelTime} דק'</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'Asset') {
                              return (
                                <div className="slds-related-record__details">
                                  {rec.SerialNumber && <span className="slds-related-record__field">מספר סידורי: {rec.SerialNumber}</span>}
                                  {rec.Status && <span className="slds-related-record__status">{rec.Status}</span>}
                                  {rec.Product2Id__Name && <span className="slds-related-record__field">מוצר: {rec.Product2Id__Name}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'ServiceTerritory') {
                              return (
                                <div className="slds-related-record__details">
                                  {rec.IsActive && <span className="slds-related-record__status">פעיל</span>}
                                  {rec.OperatingHoursId__Name && <span className="slds-related-record__field">שעות: {rec.OperatingHoursId__Name}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'ServiceAppointment') {
                              const formatDateTime = (dateStr) => {
                                if (!dateStr) return '';
                                let ds = dateStr;
                                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
                                const date = new Date(ds);
                                return date.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                              };
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Status && <span className="slds-related-record__status">{getHebrewPicklistValue('ServiceAppointment', 'Status', rec.Status)}</span>}
                                  {rec.SchedStartTime && <span className="slds-related-record__field">התחלה: {formatDateTime(rec.SchedStartTime)}</span>}
                                  {rec.SchedEndTime && <span className="slds-related-record__field">סיום: {formatDateTime(rec.SchedEndTime)}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'WorkOrder') {
                              const woDateTime = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                              const woStatusHe = rec.Status ? getHebrewPicklistValue('WorkOrder', 'Status', rec.Status) : '';
                              const woStatusColor = ['Completed', 'Closed', 'הושלם', 'סגור'].includes(rec.Status) ? '#4caf50' : ['Canceled', 'Cancelled', 'Cannot Complete'].includes(rec.Status) ? '#f44336' : rec.Status === 'New' ? '#2196f3' : '#ff9800';
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Subject && <span className="slds-related-record__field" style={{ fontWeight: '500' }}>{rec.Subject}</span>}
                                  {woStatusHe && <span className="slds-related-record__status" style={{ color: woStatusColor, fontWeight: '600' }}>{woStatusHe}</span>}
                                  {woDateTime && <span className="slds-related-record__field" style={{ fontSize: '11px', color: '#706e6b' }}>{woDateTime}</span>}
                                  {rec.WorkTypeId__Name && <span className="slds-related-record__field">סוג: {rec.WorkTypeId__Name}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'LabRepair') {
                              const stageNames = { 1: 'קבלה', 2: 'אבחון', 3: 'חלקי חילוף', 4: "צ'קליסט", 5: 'סיכום ואישור' };
                              const stage = rec.current_stage || 1;
                              const stageName = stageNames[stage] || `שלב ${stage}`;
                              const isCompleted = rec.status === 'completed';
                              const statusText = isCompleted ? 'הושלם' : 'בתהליך';
                              const statusColor = isCompleted ? '#166534' : '#1e40af';
                              const statusBg = isCompleted ? '#dcfce7' : '#dbeafe';
                              const receptionDate = rec.reception_date ? new Date(rec.reception_date).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              const completionDate = rec.completion_date ? new Date(rec.completion_date).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              return (
                                <div className="slds-related-record__details">
                                  <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: statusColor, background: statusBg }}>{statusText}</span>
                                  {!isCompleted && <span className="slds-related-record__field" style={{ color: '#0d7377', fontWeight: 500 }}>שלב {stage}/5: {stageName}</span>}
                                  {receptionDate && <span className="slds-related-record__field">קבלה: {receptionDate}</span>}
                                  {completionDate && <span className="slds-related-record__field">השלמה: {completionDate}</span>}
                                  {rec.lab_diagnosis && <span className="slds-related-record__field" style={{ fontSize: 11, opacity: 0.8 }}>{rec.lab_diagnosis.substring(0, 60)}{rec.lab_diagnosis.length > 60 ? '...' : ''}</span>}
                                  {rec.assigned_tech_name && <span className="slds-related-record__field">טכנאי: {rec.assigned_tech_name}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'Opportunity') {
                              const oppDate = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              return (
                                <div className="slds-related-record__details">
                                  {rec.StageName && <span className="slds-related-record__field">שלב: {rec.StageName}</span>}
                                  {rec.Amount && <span className="slds-related-record__field">סכום: ₪{Number(rec.Amount).toLocaleString()}</span>}
                                  {oppDate && <span className="slds-related-record__field" style={{ fontSize: '11px', color: '#706e6b' }}>{oppDate}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'Task') {
                              const taskDate = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Status && <span className="slds-related-record__status">{rec.Status}</span>}
                                  {rec.ActivityDate && <span className="slds-related-record__field">{rec.ActivityDate}</span>}
                                  {taskDate && <span className="slds-related-record__field" style={{ fontSize: '11px', color: '#706e6b' }}>נוצר: {taskDate}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'Case') {
                              const caseDate = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Status && <span className="slds-related-record__status">{rec.Status}</span>}
                                  {rec.Priority && <span className="slds-related-record__field">עדיפות: {rec.Priority}</span>}
                                  {caseDate && <span className="slds-related-record__field" style={{ fontSize: '11px', color: '#706e6b' }}>{caseDate}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'Contact') {
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Email && <span className="slds-related-record__field">{rec.Email}</span>}
                                  {rec.Phone && <span className="slds-related-record__field">{rec.Phone}</span>}
                                </div>
                              );
                            }
                            // For EntityHistory - show old/new values
                            if (list.objectName === 'EntityHistory') {
                              return (
                                <div className="slds-related-record__details">
                                  {rec.OldvalString && <span className="slds-related-record__field" style={{color: '#c23934'}}>קודם: {rec.OldvalString}</span>}
                                  {rec.NewvalString && <span className="slds-related-record__field" style={{color: '#2e844a'}}>חדש: {rec.NewvalString}</span>}
                                  {rec.CreatedBy__Name && <span className="slds-related-record__field">ע"י: {rec.CreatedBy__Name}</span>}
                                </div>
                              );
                            }
                            if (list.objectName === 'ServiceContract') {
                              return (
                                <div className="slds-related-record__details">
                                  {rec.Status && <span className="slds-related-record__status">{rec.Status}</span>}
                                  {rec.EndDate && <span className="slds-related-record__field">תוקף: {rec.EndDate}</span>}
                                </div>
                              );
                            }
                            // ברירת מחדל
                            const defaultDate = rec.CreatedDate ? new Date(rec.CreatedDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }) : '';
                            return (
                              <div className="slds-related-record__details">
                                {rec.Status && <span className="slds-related-record__status">{rec.Status}</span>}
                                {defaultDate && <span className="slds-related-record__field" style={{ fontSize: '11px', color: '#706e6b' }}>{defaultDate}</span>}
                              </div>
                            );
                          };

                          // 2026-06-15 owner-spec: OpportunityLineItem rows must NOT
                          // navigate — the OLI record page exposes Amount/UnitPrice
                          // editing which bypasses the Opportunity-side validations
                          // (kit pricing, warranty fees, etc.). The row stays in the
                          // related-list view-only.
                          const _isReadOnlyRow = list.objectName === 'OpportunityLineItem';

                          // Navigate to related record using onNavigateToRecord for proper tab handling
                          const handleRecordClick = (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (_isReadOnlyRow) return;
                            // LabRepair - open in lab app
                            if (list.objectName === 'LabRepair') {
                              window.location.href = `/lab?repair=${rec.id || rec.Id}`;
                              return;
                            }
                            const targetObj = objects.find(o => o.api_name === list.objectName);
                            if (targetObj && onNavigateToRecord) {
                              onNavigateToRecord(rec, targetObj);
                            }
                          };

                          return (
                            <React.Fragment key={`row-${recIdx}`}>
                              {_paymentSectionHeader}
                            <a
                              key={recIdx}
                              href={_isReadOnlyRow ? undefined : `/object/${list.objectName}/${rec.Id}`}
                              className="slds-related-record"
                              onClick={handleRecordClick}
                              onMouseEnter={(e) => {
                                if (_relatedHoverTimerRef.current) clearTimeout(_relatedHoverTimerRef.current);
                                const x = e.clientX;
                                const y = e.clientY;
                                _relatedHoverTimerRef.current = setTimeout(() => {
                                  setRelatedHover({ rec, x, y, objectName: list.objectName });
                                }, 350);
                              }}
                              onMouseLeave={() => {
                                if (_relatedHoverTimerRef.current) clearTimeout(_relatedHoverTimerRef.current);
                                _relatedHoverTimerRef.current = setTimeout(() => setRelatedHover(null), 120);
                              }}
                              style={{ cursor: _isReadOnlyRow ? 'default' : 'pointer', position: 'relative', textDecoration: 'none', color: 'inherit', display: 'block' }}
                            >
                              <span className="slds-related-record__name">{displayName}</span>
                              {getRecordDetails()}
                              {/* Action button for editable related records */}
                              {/* Delete button for AssignedResource - unschedules the SA */}
                              {list.objectName === 'AssignedResource' && object.api_name === 'ServiceAppointment' && (
                                <button
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!window.confirm('האם לבטל את התיאום ולהסיר את המשאב?')) return;
                                    try {
                                      const _unTk = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
                                      const res = await fetch(`${API_BASE}/api/scheduler/service-appointment/${record.Id}/unschedule`, { method: 'POST', headers: _unTk ? {'Authorization': `Bearer ${_unTk}`} : {} });
                                      if (!res.ok) throw new Error('שגיאה בביטול');
                                      // Refresh record and related records
                                      const recRes = await fetch(`${API_BASE}/api/objects/ServiceAppointment/records/${encodeURIComponent(record.Id)}`);
                                      if (recRes.ok) {
                                        const recData = await recRes.json();
                                        if (recData.record) setCurrentRecord(recData.record);
                                      }
                                      loadAllRelatedRecords(true);
                                    } catch (err) {
                                      alert(err.message || 'שגיאה בביטול התיאום');
                                    }
                                  }}
                                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                  title="ביטול תיאום והסרת משאב"
                                  style={{
                                    position: 'relative',
                                    zIndex: 2,
                                    background: '#fff',
                                    border: '1px solid #d8dde6',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    color: '#c23934',
                                    flexShrink: 0,
                                  }}
                                  onMouseOver={(e) => { e.currentTarget.style.background = '#fef0ef'; }}
                                  onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
                                >
                                  X
                                </button>
                              )}
                              {/* Action button for ALL related records */}
                              <button
                                className="line-item-action-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setLineItemActionMenu({
                                    record: rec,
                                    objectName: list.objectName,
                                    x: rect.left,
                                    y: rect.bottom + 5
                                  });
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                title="פעולות"
                                style={{
                                  position: 'relative',
                                  zIndex: 2,
                                  background: '#fff',
                                  border: '1px solid #d8dde6',
                                  borderRadius: '50%',
                                  width: '28px',
                                  height: '28px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  color: '#0176d3',
                                  flexShrink: 0,
                                }}
                              >
                                ▼
                              </button>
                            </a>
                            </React.Fragment>
                          );
                        })}
                        {_displayRecords.length > 3 && !expandedRelatedLists[`${list.objectName}_all`] && (
                          <button
                            className="slds-related-list__view-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRelatedLists(prev => ({ ...prev, [`${list.objectName}_all`]: true }));
                            }}
                          >
                            הצג הכל ({_displayRecords.length})
                          </button>
                        )}
                        {/* 2026-05-29 owner-spec: "הצג בטבלה" sibling button on
                            every related list — navigates to the standard
                            object list view filtered to this parent so the
                            user gets the full table layout (sortable columns,
                            inline edit, etc.) instead of the compact stack. */}
                        {_displayRecords.length > 0 && onViewRelatedList && (() => {
                          const targetObj = objects.find(o =>
                            o.api_name === list.objectName || o.name === list.objectName
                          );
                          if (!targetObj) return null;
                          // Resolve the child→parent linking field using the
                          // same map handleCreateRelatedRecord uses, so the
                          // list view's ?filter= URL is correct for every
                          // standard relationship (Payment__c↔Opportunity,
                          // OLI↔Opportunity, WO↔Account/Opp, etc.).
                          const parentField = getParentFieldName(list.objectName, object.api_name);
                          return (
                            <button
                              className="slds-related-list__view-all"
                              style={{ marginInlineStart: 8 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewRelatedList(targetObj, parentField, currentRecord.Id);
                              }}
                              title="פתח את הרשומות בתצוגת טבלה מלאה (מסוננת לרשומה הנוכחית)"
                            >
                              📊 הצג בטבלה
                            </button>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* File Attachments in Sidebar - Compact Style */}
          <FileAttachmentsSidebar
            objectName={object.api_name}
            recordId={currentRecord.Id}
            recordName={displayName}
          />
        </div>

        {/* Details (Main Content Area) */}
        <div className="slds-record-details">
          {/* Tabs Card */}
          <div className="slds-record-tabs-card">
            {/* Tabs - RTL: פרטים (right), היסטוריה (middle), עדכונים (left) */}
            <div className="slds-record-tabs">
              <button
                className={`slds-record-tab ${activeTab === 'details' ? 'slds-is-active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                פרטים
              </button>
              <button
                className={`slds-record-tab ${activeTab === 'activity' ? 'slds-is-active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                פעילות
              </button>
              <button
                className={`slds-record-tab ${activeTab === 'history' ? 'slds-is-active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                היסטוריה
              </button>
              <button
                className={`slds-record-tab ${activeTab === 'updates' ? 'slds-is-active' : ''}`}
                onClick={() => setActiveTab('updates')}
              >
                עדכונים
              </button>
              <button
                className={`slds-record-tab ${activeTab === 'whatsapp' ? 'slds-is-active' : ''}`}
                onClick={() => setActiveTab('whatsapp')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{ fontSize: '16px' }}>💬</span>
                וואטסאפ
              </button>
            </div>

            {/* Details Content */}
            {activeTab === 'details' && (
            <div className="slds-details-content">
              {/* Section label translation helper (frontend safety net) */}
              {(() => {
                const sectionTranslations = {
                  'Contact Information': 'פרטי יצירת קשר',
                  'Additional Information': 'מידע נוסף',
                  'Address Information': 'כתובת',
                  'System Information': 'מידע מערכת',
                  'Description Information': 'תיאור',
                  'Other Information': 'מידע אחר',
                  'Details': 'פרטים',
                  'Detail': 'פרטים',
                  'Description': 'תיאור',
                  'General': 'כללי',
                  'General Information': 'מידע כללי',
                  'Summary': 'סיכום',
                  'Account Information': 'פרטי חשבון',
                  'Account Summary': 'סיכום חשבון',
                  'Opportunity Information': 'פרטי הזדמנות',
                  'Lead Information': 'פרטי ליד',
                  'Case Information': 'פרטי פנייה',
                  'Asset Information': 'פרטי נכס',
                  'Work Order Information': 'פרטי הזמנת עבודה',
                  'Service Appointment Information': 'פרטי פעילות שירות',
                  'Product Information': 'פרטי מוצר',
                  'Price Information': 'פרטי מחיר',
                  'Billing Address': 'כתובת חיוב',
                  'Shipping Address': 'כתובת משלוח',
                  'Mailing Address': 'כתובת למשלוח דואר',
                  'Other Address': 'כתובת נוספת',
                  'Communication': 'תקשורת',
                  'Finance': 'כספים',
                  'Financial Information': 'מידע כספי',
                  'Payment Information': 'פרטי תשלום',
                  'Notes': 'הערות',
                  'Scheduling Information': 'פרטי תזמון',
                  'IS PERSON ACCOUNT': 'חשבון אישי',
                  'Other': 'אחר',
                  'Marketing Cloud': 'מרקטינג',
                  'Marketing': 'מרקטינג',
                  'Social': 'חברתי',
                  'History': 'היסטוריה',
                  'Service Information': 'פרטי שירות',
                  'Resource Information': 'פרטי משאב',
                  'Territory Information': 'פרטי אזור',
                };
                window._translateSectionLabel = (label) => {
                  if (!label) return label;
                  if (sectionTranslations[label]) return sectionTranslations[label];
                  // Case-insensitive fallback
                  const lower = label.toLowerCase();
                  for (const [eng, heb] of Object.entries(sectionTranslations)) {
                    if (eng.toLowerCase() === lower) return heb;
                  }
                  return label;
                };
                return null;
              })()}
              {/* Task-specific Layout */}
              {object.api_name === 'Task' && (() => {
                const taskSections = [
                  {
                    label: 'פרטי משימה',
                    // Owner-spec 2026-06-17:
                    //   1. Move "תגובה לפעילות" + "טיפול שבוצע" directly
                    //      under "תיאור" (they used to live in dedicated
                    //      bottom sections, which felt orphaned).
                    //   2. Use the REAL columns Activity_Outcome__c and
                    //      Description__c — the prior rows referenced
                    //      ActivityResponse__c / Treatment_Done__c which
                    //      don't exist on the task table (same dead-column
                    //      bug as the original CallResult__c / Comments__c
                    //      caught on 2026-06-15).
                    //   3. Drop the duplicate Activity_Outcome__c row from
                    //      "פרטי שיחה" and the standalone "טיפול שבוצע"
                    //      section — both are now consolidated here.
                    // 2026-07-01 owner-spec moved Description (base SF) +
                    // Action_Taken__c to a collapsed "מידע היסטורי
                    // מסיילספורס" section; 2026-10-14 owner-spec reversed
                    // that — they sit in this section again (row below).
                    fields: [
                      [['Subject', 'נושא'], ['OwnerId', 'מוקצה ל']],
                      [['Status', 'מצב'], ['Who__Name', 'שם']],
                      [['Type', 'סוג משנה של משימה'], ['', '']],
                      [['Activity_Outcome__c', 'תגובה לפעילות'], ['Description__c', 'טיפול שבוצע']],
                      // 2026-10-14 owner-spec: the two transfer fields — one
                      // stamped line appended per transfer, never overwritten.
                      [['Transfer_Reason__c', 'סיבה להעברת משימה'], ['Transfer_Notes__c', 'הערות ממעביר המשימה']],
                      // 2026-10-14 owner-spec: the legacy Salesforce fields
                      // (Description, Action_Taken__c) live here too, not in a
                      // separate collapsed section.
                      [['Description', 'תיאור מקורי'], ['Action_Taken__c', 'פעולה ישנה']],
                      [['ActivityDate', 'תאריך יעד'], ['What__Name', 'קשור ל']],
                      // 2026-10-19 owner-spec: directly under "תאריך יעד".
                      [['Postpone_Reason__c', 'סיבה לדחיית משימה'], ['', '']],
                      [['Closing_Employee__c', 'עובד סגירה'], ['Filter_Type__c', 'סוג הפילטר']],
                      [['Device_Name__c', 'שם המכשיר'], ['Color__c', 'צבע']],
                      [['Barak_System_Owner__c', 'בעלות מערכת ברק'], ['Barak_Assigned__c', 'משוייך ל במערכת ברק']],
                    ]
                  },
                  {
                    label: 'פרטי שיחה',
                    defaultCollapsed: true,
                    fields: [
                      [['CallIdentifier__c', 'מספר מזהה שיחה'], ['CallerPhone__c', 'טלפון מתקשר']],
                      [['CallRecording__c', 'רשומת שיחה'], ['CallStartTime__c', 'תחילת שיחה']],
                      [['CallDurationInSeconds', 'משך זמן שיחה'], ['CallEndTime__c', 'סיום שיחה']],
                      // Activity_Outcome__c moved up to "פרטי משימה"
                      // (owner-spec 2026-06-17). Leave CallObject + CallType
                      // here — they're the actual call-trace fields.
                      [['CallObject', 'מזהה אובייקט שיחה'], ['CallType', 'סוג שיחה']],
                    ]
                  },
                  // (Standalone "טיפול שבוצע" section removed — Description__c
                  // is now in "פרטי משימה" right under "תיאור" per owner-spec.)
                  {
                    label: 'פרטים נוספים',
                    defaultCollapsed: true,
                    fields: [
                      [['Priority', 'עדיפות'], ['', '']],
                    ]
                  },
                  {
                    label: 'מידע אחר',
                    defaultCollapsed: true,
                    fields: [
                      [['IsReminderSet', 'תזכורת הוגדרה'], ['IsRecurrence', 'צור סידרה חוזרת של משימות']],
                    ]
                  },
                  {
                    label: 'פרטי מערכת',
                    // Owner-spec 2026-06-17: expanded by default — user
                    // expects to see "מי יצר / מתי / שונה לאחרונה ע״י"
                    // without an extra click. The whole point of this
                    // section is at-a-glance audit info.
                    defaultCollapsed: false,
                    fields: [
                      [['CreatedById', 'נוצר על-ידי'], ['LastModifiedById', 'שונה לאחרונה על-ידי']],
                      [['CreatedDate', 'תאריך יצירה'], ['LastModifiedDate', 'תאריך עדכון']],
                    ]
                  },
                ];

                const renderTaskFieldValue = (fieldName, label) => {
                  if (!fieldName) return <span style={{ color: '#706e6b' }}>-</span>;
                  const val = currentRecord[fieldName];

                  // Reference links
                  // 2026-06-09 — owner-reported: clicking the "קשור ל" link on
                  // a Task showed "טוען נתונים..." and never navigated. Root
                  // cause: the call signature mismatched the consumer.
                  //   navigateToRecord(record, obj) expects:
                  //     record.Id      (the record's primary key)
                  //     obj.api_name   (the object type for buildRecordUrl)
                  // But this code was passing (target_string, WhatId_string),
                  // so both record.Id and obj.api_name resolved to undefined
                  // — the `if (recordId && objectName)` guard inside
                  // navigateToRecord then skipped the navigate() call, leaving
                  // the screen on the prior "loading" state. Same bug on
                  // Who__Name below. Build the object-shaped args properly.
                  if (fieldName === 'What__Name' && currentRecord.WhatId) {
                    // No resolved target means the WhatId matched no record the
                    // resolver knows. The old 'Opportunity' fallback opened an
                    // empty Opportunity page for it — show the raw id, unlinked.
                    const target = currentRecord.What__Target || currentRecord.WhatId__Target;
                    if (!target) return <span>{val || currentRecord.WhatId}</span>;
                    return (
                      <span style={{ color: '#0176d3', cursor: 'pointer' }}
                        onClick={() => {
                          if (!onNavigateToRecord) return;
                          const targetObj = objects.find(o => o.api_name === target) || { api_name: target };
                          onNavigateToRecord({ Id: currentRecord.WhatId }, targetObj);
                        }}>
                        {val || currentRecord.WhatId}
                      </span>
                    );
                  }
                  if (fieldName === 'Who__Name' && currentRecord.WhoId) {
                    return (
                      <span style={{ color: '#0176d3', cursor: 'pointer' }}
                        onClick={() => {
                          if (!onNavigateToRecord) return;
                          const targetObj = objects.find(o => o.api_name === 'Contact') || { api_name: 'Contact' };
                          onNavigateToRecord({ Id: currentRecord.WhoId }, targetObj);
                        }}>
                        {val || currentRecord.WhoId}
                      </span>
                    );
                  }
                  if (fieldName === 'OwnerId') {
                    return <span style={{ color: '#0176d3' }}>{currentRecord.Owner__Name || val || '-'}</span>;
                  }
                  // Owner-spec 2026-06-17: dynamic record page (System
                  // Information section) was rendering CreatedById /
                  // LastModifiedById as raw email/SF ID because this
                  // renderer fell through to `return val || '-'` for
                  // them. The denormalized name fields ARE in the API
                  // response (CreatedBy__Name / CreatedByName from
                  // _sql_resolve_references at unified_server.py:13230)
                  // — surface them properly instead of the raw id.
                  if (fieldName === 'CreatedById') {
                    return (
                      <span>
                        {currentRecord.CreatedBy__Name
                         || currentRecord.CreatedByName
                         || val
                         || '-'}
                      </span>
                    );
                  }
                  if (fieldName === 'LastModifiedById') {
                    return (
                      <span>
                        {currentRecord.LastModifiedBy__Name
                         || currentRecord.LastModifiedByName
                         || val
                         || '-'}
                      </span>
                    );
                  }
                  // Date fields
                  if (fieldName.includes('Date') || fieldName.includes('Time') || fieldName === 'CreatedDate' || fieldName === 'LastModifiedDate') {
                    if (val) {
                      try {
                        return formatIsraelDateTime(val) || new Date(val).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' });
                      } catch { return val; }
                    }
                    return '-';
                  }
                  // Boolean
                  if (typeof val === 'boolean' || val === 'true' || val === 'false' || val === '1' || val === '0' || val === 1 || val === 0) {
                    const isTrue = val === true || val === 'true' || val === '1' || val === 1;
                    return (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '20px', height: '20px',
                        backgroundColor: isTrue ? '#04844b' : '#e5e5e5',
                        borderRadius: '4px',
                        color: 'white', fontSize: '14px',
                        border: isTrue ? 'none' : '1px solid #d8dde6'
                      }}>{isTrue ? '✓' : ''}</span>
                    );
                  }
                  // Status with color
                  if (fieldName === 'Status') {
                    const statusColors = {
                      'Not Started': '#706e6b',
                      'In Progress': '#ff9800',
                      'Completed': '#4caf50',
                      'Waiting on someone else': '#2196f3',
                      'Deferred': '#9e9e9e',
                      'סיום טיפול בשביעות רצון': '#4caf50',
                      'None': '#9E9E9E',
                      'Scheduled': '#FF9800',
                      'Dispatched': '#2196F3',
                      'Cannot Complete': '#F44336',
                      'Canceled': '#9E9E9E',
                    };
                    return (
                      <span style={{ color: statusColors[val] || '#333', fontWeight: 500 }}>
                        {getHebrewPicklistValue(object.api_name, 'Status', val) || '-'}
                      </span>
                    );
                  }
                  if (fieldName === 'Priority') {
                    return getHebrewPicklistValue(object.api_name, 'Priority', val) || '-';
                  }
                  // One stamped line per transfer — keep the line breaks.
                  if (fieldName === 'Transfer_Reason__c' || fieldName === 'Transfer_Notes__c') {
                    return val ? <span style={{ whiteSpace: 'pre-wrap' }}>{val}</span> : '-';
                  }

                  return val || '-';
                };

                return taskSections.map((section, sIdx) => {
                  const sectionId = `task-section-${sIdx}`;
                  const isCollapsed = collapsedSections[sectionId] !== undefined
                    ? collapsedSections[sectionId]
                    : section.defaultCollapsed || false;

                  return (
                    <div key={sIdx} className={`slds-details-section ${isCollapsed ? 'collapsed' : ''}`}>
                      <h3 className="slds-details-section__title" onClick={() => toggleSection(sectionId)} style={{ cursor: 'pointer' }}>
                        <span className="slds-collapse-icon">{isCollapsed ? '►' : '▼'}</span>
                        {section.label}
                      </h3>
                      {!isCollapsed && (
                        <div className="slds-details-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                          {section.fields.map((row, rIdx) => (
                            <React.Fragment key={rIdx}>
                              {row.map(([fieldName, label], fIdx) => {
                                // 2026-06-08 — owner-flagged: double-click to
                                // edit works on every record type EXCEPT Task,
                                // because the Task-specific layout (this
                                // block) was missing the onDoubleClick handler
                                // that the standard flexipage layout has at
                                // line ~19071. handleStartEdit toggles the
                                // whole record into edit mode (same behavior
                                // as other record types). Excluded fields
                                // match the inner ternary's exclusion list
                                // (reference + system fields).
                                const canDblClickEdit = !isEditing
                                  && !taskEditLocked
                                  && !recordEditDenied
                                  && fieldName
                                  && !['What__Name', 'Who__Name', 'CreatedDate', 'LastModifiedDate', 'CreatedById', 'LastModifiedById'].includes(fieldName);
                                return (
                                <div key={fIdx} className="slds-form-field">
                                  <label className="slds-field-label">
                                    {isEditing && fieldName && !lockedFields.has(fieldName) ? (
                                      <span style={{ cursor: 'pointer', marginLeft: '4px' }}>✏️</span>
                                    ) : null}
                                    {label}
                                    {fieldName && lockedFields.has(fieldName) && lockIcon}
                                  </label>
                                  <div
                                    className="slds-field-value"
                                    onDoubleClick={canDblClickEdit ? handleStartEdit : undefined}
                                    style={canDblClickEdit ? { cursor: 'text' } : undefined}
                                    title={canDblClickEdit ? 'דאבל-קליק לעריכה' : undefined}
                                  >
                                    {isEditing && fieldName && !lockedFields.has(fieldName) && !['What__Name', 'Who__Name', 'CreatedDate', 'LastModifiedDate', 'CreatedById', 'LastModifiedById'].includes(fieldName) ? (
                                      fieldName === 'Status' ? (
                                        <select value={editedRecord[fieldName] || ''} onChange={e => handleFieldChange(fieldName, e.target.value)}
                                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #dddbda', borderRadius: '4px' }}>
                                          <option value="Not Started">טרם התחיל</option>
                                          <option value="In Progress">בתהליך</option>
                                          <option value="Completed">הושלם</option>
                                          <option value="Waiting on someone else">ממתין למישהו אחר</option>
                                          <option value="Deferred">נדחה</option>
                                          <option value="סיום טיפול בשביעות רצון">סיום טיפול בשביעות רצון</option>
                                        </select>
                                      ) : fieldName === 'Priority' ? (
                                        <select value={editedRecord[fieldName] || 'Normal'} onChange={e => handleFieldChange(fieldName, e.target.value)}
                                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #dddbda', borderRadius: '4px' }}>
                                          <option value="Normal">Normal</option>
                                          <option value="High">High</option>
                                          <option value="Low">Low</option>
                                        </select>
                                      ) : fieldName === 'Description' || fieldName === 'Comments__c' ? (
                                        <textarea value={editedRecord[fieldName] || ''} onChange={e => handleFieldChange(fieldName, e.target.value)}
                                          rows={3} style={{ width: '100%', padding: '4px 8px', border: '1px solid #dddbda', borderRadius: '4px', resize: 'vertical' }} />
                                      ) : fieldName === 'ActivityDate' ? (
                                        <input type="date" value={editedRecord[fieldName] || ''} onChange={e => handleFieldChange(fieldName, e.target.value)}
                                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #dddbda', borderRadius: '4px' }} />
                                      ) : (
                                        // 2026-07-01 owner-spec: cap Task Subject at 40 chars.
                                        // Long subjects push the highlights-panel content off-screen
                                        // and hide the שמור/ביטול actions on smaller layouts.
                                        // Overflow now belongs in the "תיאור" textarea.
                                        <input type="text" value={editedRecord[fieldName] || ''} onChange={e => handleFieldChange(fieldName, e.target.value)}
                                          maxLength={fieldName === 'Subject' ? 40 : undefined}
                                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #dddbda', borderRadius: '4px' }} />
                                      )
                                    ) : renderTaskFieldValue(fieldName, label)}
                                  </div>
                                </div>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}

              {/* Record Page Sections - הצוקה sections מ-Record Page */}
              {/* Check for sections directly in Record Page, or extract from Details tab */}
              {object.api_name !== 'Task' && (() => {
                // Get sections to display: either direct sections or from the Details tab
                let sectionsToShow = flexipage?.sections || [];
                let tabsToShow = flexipage?.tabs || [];

                // IMPORTANT: If there are tabs with a detail tab, use tabs content ONLY (not root sections)
                // This prevents duplication when Record Page has both sections and tabs with same content
                if (tabsToShow.length > 0) {
                  const detailTab = tabsToShow.find(t =>
                    t.originalTitle === 'Standard.Tab.detail' ||
                    t.title === 'Standard.Tab.detail' ||
                    t.title?.toLowerCase().includes('detail') ||
                    t.title?.includes('פרטים')
                  ) || tabsToShow[0];

                  if (detailTab?.body) {
                    // Extract fieldSections from the detail tab - REPLACE root sections
                    sectionsToShow = detailTab.body.filter(item => item.type === 'fieldSection');
                    // Remove the detail tab from tabs to show (it's now shown as main content)
                    tabsToShow = tabsToShow.filter(t => t !== detailTab);
                  }
                }

                return (
                  <>
                    {sectionsToShow.length > 0 ? (
                      <>
                        {/* Render each field section from Record Page */}
                        {sectionsToShow.map((section, sectionIdx) => {
                          const sectionId = `flexipage-${sectionIdx}`;
                          // Sections that should be collapsed by default
                          const defaultCollapsedLabels = ['מידע נוסף', 'הנהלת חשבונות', 'מערכת ברק', 'מרקטינג', 'Additional Information', 'Barak System', 'Marketing'];
                          const shouldBeCollapsedByDefault = defaultCollapsedLabels.some(label =>
                            section.label?.includes(label) || section.label?.toLowerCase().includes(label.toLowerCase())
                          );
                          // Use explicit state if set, otherwise use default
                          const isCollapsed = collapsedSections[sectionId] !== undefined
                            ? collapsedSections[sectionId]
                            : shouldBeCollapsedByDefault;
                          return (
                    <div key={sectionIdx} className={`slds-details-section ${isCollapsed ? 'collapsed' : ''}`}>
                      <h3
                        className="slds-details-section__title"
                        onClick={() => toggleSection(sectionId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="slds-collapse-icon">{isCollapsed ? '►' : '▼'}</span>
                        {window._translateSectionLabel ? window._translateSectionLabel(section.label) : section.label || `Section ${sectionIdx + 1}`}
                      </h3>
                      {!isCollapsed && <div className="slds-details-grid" style={{
                        gridTemplateColumns: section.columns?.length === 1 ? '1fr' :
                                            `repeat(${section.columns?.length || 2}, 1fr)`
                      }}>
                        {(section.columns || []).map((column, colIdx) => (
                          <div key={colIdx} className="slds-details-column">
                            {column.map((field, fieldIdx) => {
                              const fieldName = field.fieldName;
                              const displayValue = currentRecord[fieldName];
                              const editValue = editedRecord[fieldName] || '';
                              const uiBehavior = field.uiBehavior || 'none';
                              const isRequired = uiBehavior === 'required';
                              const isLocked = lockedFields.has(fieldName);
                              const isReadonly = uiBehavior === 'readonly';
                              // record access: read-only for me / no screen edit — still click-to-copy
                              const editBlocked = isReadonly || noScreenEdit(fieldName);
                              const isSystemField = ['Id', 'CreatedDate', 'LastModifiedDate', 'CreatedById', 'LastModifiedById', 'IsDeleted'].includes(fieldName);
                              const canEdit = isEditing && !editBlocked && !isSystemField && !fieldName?.endsWith('__Name') && !fieldName?.endsWith('__Target');

                              // Get field metadata for smart rendering
                              const fieldMeta = objectFields.find(f => f.name === fieldName);

                              return (
                                <div key={fieldIdx} className={`slds-form-field ${isRequired ? 'slds-is-required' : ''} ${editBlocked ? 'slds-is-readonly' : ''} ${canEdit ? 'slds-is-editing' : ''}`}>
                                  <label className="slds-field-label">
                                    {field.customLabel || formatFieldLabel(fieldName)}
                                    {isRequired && <span className="slds-required-marker">*</span>}
                                    {isLocked && lockIcon}
                                  </label>
                                  <div
                                    className="slds-field-value"
                                    // Owner-spec 2026-06-16: single click on
                                    // the value text copies its display value
                                    // to clipboard; double-click still opens
                                    // inline edit (the timer dance below
                                    // distinguishes the two). The dedicated
                                    // pencil button was removed because users
                                    // were copying it together with the value.
                                    title={!isReadonly && !isSystemField && !isEditing
                                      ? (editBlocked ? 'קליק להעתקה' : 'קליק להעתקה · דאבל-קליק לעריכה')
                                      : undefined}
                                    style={!isReadonly && !isSystemField && !isEditing
                                      ? { cursor: 'text' } : undefined}
                                    onClick={(e) => {
                                      if (isReadonly || isSystemField || isEditing || canEdit) return;
                                      if (e.target && (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest?.('a,button'))) return;
                                      if (_fieldClickCopyTimerRef.current) {
                                        clearTimeout(_fieldClickCopyTimerRef.current);
                                      }
                                      const valueToCopy = String(displayValue == null ? '' : displayValue);
                                      _fieldClickCopyTimerRef.current = setTimeout(async () => {
                                        _fieldClickCopyTimerRef.current = null;
                                        if (!valueToCopy) return;
                                        let ok = false;
                                        try {
                                          if (navigator?.clipboard?.writeText) {
                                            await navigator.clipboard.writeText(valueToCopy);
                                            ok = true;
                                          } else {
                                            const ta = document.createElement('textarea');
                                            ta.value = valueToCopy;
                                            ta.setAttribute('readonly', '');
                                            ta.style.position = 'fixed';
                                            ta.style.top = '-1000px';
                                            ta.style.opacity = '0';
                                            document.body.appendChild(ta);
                                            ta.select();
                                            ok = document.execCommand('copy');
                                            document.body.removeChild(ta);
                                          }
                                        } catch (err) { ok = false; }
                                        if (ok) {
                                          setCopyToast({ idx: -2, text: 'הועתק' });
                                          setTimeout(() => setCopyToast(null), 1200);
                                        }
                                      }, 250);
                                    }}
                                    onDoubleClick={!editBlocked && !isSystemField && !isEditing
                                      ? (() => {
                                          if (_fieldClickCopyTimerRef.current) {
                                            clearTimeout(_fieldClickCopyTimerRef.current);
                                            _fieldClickCopyTimerRef.current = null;
                                          }
                                          handleStartEdit();
                                        })
                                      : undefined}
                                  >
                                    {canEdit ? (
                                      renderEditField(fieldName, editValue, fieldMeta)
                                    ) : (
                                      renderFieldValue(fieldName, displayValue, currentRecord, field.displayAs)
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>}
                    </div>
                  );
                  })}

                  {/* Record Page Tabs - suppressed (duplicate of built-in tabs: פרטים/היסטוריה/עדכונים) */}
                  {false && tabsToShow && tabsToShow.length > 0 && (
                    <div className="slds-flexipage-tabs">
                      <div className="slds-tabs-wrapper">
                        {tabsToShow.map((tab, tabIdx) => (
                          <button
                            key={tabIdx}
                            className={`slds-flexipage-tab ${activeFlexiTab === tabIdx ? 'slds-is-active' : ''}`}
                            onClick={() => setActiveFlexiTab(tabIdx)}
                          >
                            {tab.title}
                          </button>
                        ))}
                      </div>
                      <div className="slds-tab-content">
                        {tabsToShow[activeFlexiTab]?.body?.map((item, itemIdx) => {
                          if (item.type === 'fieldSection') {
                            const tabSectionId = `tab-${activeFlexiTab}-section-${itemIdx}`;
                            // Sections that should be collapsed by default
                            const defaultCollapsedLabels = ['מידע נוסף', 'הנהלת חשבונות', 'מערכת ברק', 'מרקטינג', 'Additional Information', 'Barak System', 'Marketing'];
                            const shouldBeCollapsedByDefault = defaultCollapsedLabels.some(label =>
                              item.label?.includes(label) || item.label?.toLowerCase().includes(label.toLowerCase())
                            );
                            // Use explicit state if set, otherwise use default
                            const isTabSectionCollapsed = collapsedSections[tabSectionId] !== undefined
                              ? collapsedSections[tabSectionId]
                              : shouldBeCollapsedByDefault;
                            return (
                              <div key={itemIdx} className={`slds-details-section slds-details-section--tab ${isTabSectionCollapsed ? 'collapsed' : ''}`}>
                                <h3
                                  className="slds-details-section__title"
                                  onClick={() => toggleSection(tabSectionId)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span className="slds-collapse-icon">{isTabSectionCollapsed ? '►' : '▼'}</span>
                                  {item.label}
                                </h3>
                                {!isTabSectionCollapsed && <div className="slds-details-grid" style={{
                                  gridTemplateColumns: item.columns?.length === 1 ? '1fr' :
                                                      `repeat(${item.columns?.length || 2}, 1fr)`
                                }}>
                                  {(item.columns || []).map((column, colIdx) => (
                                    <div key={colIdx} className="slds-details-column">
                                      {column.map((field, fieldIdx) => {
                                        const fieldName = field.fieldName;
                                        const value = currentRecord[fieldName];
                                        const uiBehavior = field.uiBehavior || 'none';
                                        const isRequired = uiBehavior === 'required';
                                        const isReadonly = uiBehavior === 'readonly' || noScreenEdit(fieldName);

                                        return (
                                          <div key={fieldIdx} className={`slds-form-field ${isRequired ? 'slds-is-required' : ''} ${isReadonly ? 'slds-is-readonly' : ''}`}>
                                            <label className="slds-field-label">
                                              {formatFieldLabel(fieldName)}
                                              {isRequired && <span className="slds-required-marker">*</span>}
                                            </label>
                                            <div
                                              className="slds-field-value"
                                              onDoubleClick={!isReadonly ? handleStartEdit : undefined}
                                            >
                                              {!isReadonly && <span className="slds-field-edit" onClick={handleStartEdit} title="לחץ לעריכה">✏️</span>}
                                              {renderFieldValue(fieldName, value, currentRecord, field.displayAs)}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>}
                              </div>
                            );
                          }
                          if (item.type === 'field') {
                            const fieldName = item.fieldName;
                            const value = currentRecord[fieldName];
                            return (
                              <div key={itemIdx} className="slds-form-field">
                                <label className="slds-field-label">{formatFieldLabel(fieldName)}</label>
                                <div className="slds-field-value">
                                  {renderFieldValue(fieldName, value, currentRecord, item.displayAs)}
                                </div>
                              </div>
                            );
                          }
                          if (item.type === 'component') {
                            // Suppress legacy CRM-specific components that are already handled natively
                            const compName = item.componentName || '';
                            if (compName.includes('relatedList') ||
                                compName.includes('activityPanel') ||
                                compName.includes('recordFeedContainer') ||
                                compName.includes('chatter') ||
                                compName.includes('mergeCandidates')) {
                              return null;
                            }
                            return (
                              <div key={itemIdx} className="slds-flexipage-component">
                                <span className="slds-component-placeholder">
                                  {item.componentName}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Rich Text and Custom Components from Record Page - suppressed (handled natively) */}
                      </>
                    ) : null}
                  </>
                );
              })()}
              {/* Layout-based rendering (fallback when no Record Page) */}
              {!flexipage && layout && layout.sections && layout.sections.length > 0 ? (
                /* Layout-based rendering (fallback) */
                layout.sections.map((section, sectionIdx) => {
                  const layoutSectionId = `layout-${sectionIdx}`;
                  // Sections that should be collapsed by default
                  const defaultCollapsedLabels = ['מידע נוסף', 'הנהלת חשבונות', 'מערכת ברק', 'מרקטינג', 'Additional Information', 'Barak System', 'Marketing'];
                  const shouldBeCollapsedByDefault = defaultCollapsedLabels.some(label =>
                    section.label?.includes(label) || section.label?.toLowerCase().includes(label.toLowerCase())
                  );
                  // Use explicit state if set, otherwise use default
                  const isLayoutSectionCollapsed = collapsedSections[layoutSectionId] !== undefined
                    ? collapsedSections[layoutSectionId]
                    : shouldBeCollapsedByDefault;
                  return (
                  <div key={sectionIdx} className={`slds-details-section ${isLayoutSectionCollapsed ? 'collapsed' : ''}`}>
                    <h3
                      className="slds-details-section__title"
                      onClick={() => toggleSection(layoutSectionId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="slds-collapse-icon">{isLayoutSectionCollapsed ? '►' : '▼'}</span>
                      {window._translateSectionLabel ? window._translateSectionLabel(section.label) : section.label || `Section ${sectionIdx + 1}`}
                    </h3>
                    {!isLayoutSectionCollapsed && <div className="slds-details-grid" style={{
                      gridTemplateColumns: section.style === 'OneColumn' ? '1fr' :
                                          section.style === 'TwoColumnsTopToBottom' || section.style === 'TwoColumnsLeftToRight' ? '1fr 1fr' :
                                          '1fr 1fr'
                    }}>
                      {(section.columns || []).map((column, colIdx) => (
                        <div key={colIdx} className="slds-details-column">
                          {column.map((item) => {
                            const fieldName = item.field;
                            const value = currentRecord[fieldName];
                            const behavior = item.behavior;
                            const isRequired = behavior === 'Required';
                            const isReadonly = behavior === 'Readonly' || noScreenEdit(fieldName);

                            return (
                              <div key={fieldName} className={`slds-form-field ${isRequired ? 'slds-is-required' : ''} ${isReadonly ? 'slds-is-readonly' : ''}`}>
                                <label className="slds-field-label">
                                  {formatFieldLabel(fieldName)}
                                  {isRequired && <span className="slds-required-marker">*</span>}
                                </label>
                                <div
                                  className="slds-field-value"
                                  onDoubleClick={!isReadonly ? handleStartEdit : undefined}
                                >
                                  {!isReadonly && <span className="slds-field-edit" onClick={handleStartEdit} title="לחץ לעריכה">✏️</span>}
                                  {renderFieldValue(fieldName, value, currentRecord)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>}
                  </div>
                  );
                })
              ) : null}
              {/* Fallback - תצוגה בסיסית של שדות מהרשומה */}
              {/* מוצג כאשר אין Record Page ו-Layout, או שכבר אין sections */}
              {(() => {
                // בדיקה אם יש sections להציג מ-Record Page או Layout
                const hasFlexipageSections = flexipage?.sections?.length > 0 ||
                  flexipage?.tabs?.some(t => t.body?.some(item => item.type === 'fieldSection'));
                const hasLayoutSections = layout?.sections?.length > 0;

                // אם יש sections - לא מציגים את ה-fallback
                if (hasFlexipageSections || hasLayoutSections) return null;

                // מציגים את כל השדות מהרשומה
                const fallbackSectionId = 'fallback-main';
                const isFallbackCollapsed = collapsedSections[fallbackSectionId];
                return (
                  <div className={`slds-details-section ${isFallbackCollapsed ? 'collapsed' : ''}`}>
                    <h3
                      className="slds-details-section__title"
                      onClick={() => toggleSection(fallbackSectionId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="slds-collapse-icon">{isFallbackCollapsed ? '►' : '▼'}</span>
                      פרטי {config.heLabel}
                    </h3>
                    {!isFallbackCollapsed && <div className="slds-details-grid">
                      <div className="slds-details-column">
                        {leftFields.map(([key, value]) => {
                          const fieldMeta = objectFields.find(f => f.name === key);

                          return (
                            <div key={key} className="slds-form-field">
                              <label className="slds-field-label">{formatFieldLabel(key)}{lockedFields.has(key) && lockIcon}</label>
                              <div
                                className="slds-field-value"
                                onDoubleClick={!isEditing && !noScreenEdit(key) ? handleStartEdit : undefined}
                              >
                                {!isEditing && !noScreenEdit(key) && <span className="slds-field-edit" onClick={handleStartEdit} title="לחץ לעריכה">✏️</span>}
                                {isEditing && !lockedFields.has(key) ? (
                                  renderEditField(key, editedRecord[key] || '', fieldMeta)
                                ) : (
                                  renderFieldValue(key, value, currentRecord)
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="slds-details-column">
                        {rightFields.map(([key, value]) => {
                          const fieldMeta = objectFields.find(f => f.name === key);

                          return (
                            <div key={key} className="slds-form-field">
                              <label className="slds-field-label">{formatFieldLabel(key)}{lockedFields.has(key) && lockIcon}</label>
                              <div
                                className="slds-field-value"
                                onDoubleClick={!isEditing && !noScreenEdit(key) ? handleStartEdit : undefined}
                              >
                                {!isEditing && !noScreenEdit(key) && <span className="slds-field-edit" onClick={handleStartEdit} title="לחץ לעריכה">✏️</span>}
                                {isEditing && !lockedFields.has(key) ? (
                                  renderEditField(key, editedRecord[key] || '', fieldMeta)
                                ) : (
                                  renderFieldValue(key, value, currentRecord)
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Activity Tab Content */}
          {activeTab === 'activity' && (
            <div className="slds-activity-panel">
              {/* Activity Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', borderBottom: '1px solid #e5e5e5', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    setShowNewActivityModal({ type: 'call', objectName: object.api_name, recordId: currentRecord.Id, recordName: currentRecord.Name || currentRecord.Subject || '' });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#0176d3', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  📞 רישום שיחה
                </button>
                <button
                  onClick={() => {
                    setShowNewActivityModal({ type: 'task', objectName: object.api_name, recordId: currentRecord.Id, recordName: currentRecord.Name || currentRecord.Subject || '' });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#04844b', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  ✅ משימה חדשה
                </button>
                <button
                  onClick={() => {
                    setShowNewActivityModal({ type: 'event', objectName: object.api_name, recordId: currentRecord.Id, recordName: currentRecord.Name || currentRecord.Subject || '' });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#f0983a', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  📅 אירוע חדש
                </button>
                <button
                  onClick={() => {
                    setShowNewActivityModal({ type: 'email', objectName: object.api_name, recordId: currentRecord.Id, recordName: currentRecord.Name || currentRecord.Subject || '' });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '4px', border: '1px solid #d8dde6', background: '#fff', color: '#181818', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  ✉️ דואר אלקטרוני
                </button>
                {['Account', 'Opportunity', 'Payment__c'].includes(object.api_name) && (
                  <button
                    onClick={() => setShowCreditApprovalModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    💸 בקש אישור ממנהל לזיכוי לקוח
                  </button>
                )}
              </div>

              {/* Activity Filters Bar */}
              <div className="slds-activity-filters">
                <button className="slds-activity-filter-btn" title="הגדרות">
                  <span>⚙️</span>
                </button>
                <span className="slds-activity-filter-text">
                  מסננים: כל הזמן • כל הפעילויות • כל הסוגים
                </span>
                <div className="slds-activity-filter-links">
                  <button className="slds-activity-link">רענן</button>
                  <span>•</span>
                  <button className="slds-activity-link">הרחב הכול</button>
                  <span>•</span>
                  <button className="slds-activity-link">הצג הכול</button>
                </div>
              </div>

              {/* Upcoming Section */}
              {/* 2026-06-09 — owner-spec: list Tasks with Status='Open' (the
                  consolidated "not done" status after the 15K Not Started →
                  Open migration earlier today). The previous filter `!t.IsClosed`
                  was broken because IsClosed is a TEXT column whose values
                  '0' and 'false' are JavaScript-truthy — only Tasks with
                  IsClosed in ('', NULL) were ever shown. Switching to a
                  Status-based check is unambiguous and matches the migration.
                  Completed / סיום טיפול statuses stay hidden. */}
              <div className="slds-activity-upcoming">
                <div className="slds-activity-upcoming-header">
                  <span>בקרוב ותאריך היעד חלף</span>
                  <span className="slds-activity-collapse">▼</span>
                </div>
                <div className="slds-activity-upcoming-content">
                  {(() => {
                    const _openTasks = activityView.openTasks;
                    return loadingActivities ? (
                    <div className="slds-activity-loading">טוען...</div>
                  ) : _openTasks.length === 0 ? (
                    <div className="slds-activity-empty">
                      <p>אין פעילויות להצגה.</p>
                      <p className="slds-activity-empty-hint">התחל על-ידי שליחת דוא"ל, תזמון משימה ועוד.</p>
                    </div>
                  ) : (
                    <div className="slds-activity-items">
                      {(showAllActivities ? _openTasks : _openTasks.slice(0, 5)).map((task, idx) => {
                        const taskKey = task.Id || `upcoming-${idx}`;
                        // Prefer CreatedDate for time display. ActivityDate
                        // is a date-only string (YYYY-MM-DD) — parsing it
                        // produces UTC midnight, which renders as 03:00 in
                        // Asia/Jerusalem for every row (owner-reported).
                        // Owner-spec 2026-05-25: show actual creation time.
                        const taskDate = task.CreatedDate || task.ActivityDate;
                        const isCall = task.TaskSubtype === 'Call';
                        const defaultSubject = isCall ? 'שיחה' : 'משימה';
                        return (
                        <div key={taskKey} className="slds-activity-task-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {taskCanEdit && <input type="checkbox" className="slds-activity-checkbox" title="סמן כהושלם" style={{ cursor: 'pointer' }} onChange={async () => {
                            try {
                              await fetch(`${API_BASE}/api/objects/Task/records/${task.Id}`, {
                                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ Status: 'Completed', IsClosed: true })
                              });
                              setRecordActivities({ tasks: [], events: [] });
                              loadRecordActivities();
                            } catch(e) { console.error(e); }
                          }} />}
                          <div className="slds-activity-task-content" style={{ flex: 1, cursor: 'pointer' }} onClick={() => {
                            const taskObj = objects.find(o => o.api_name === 'Task');
                            if (taskObj && onNavigateToRecord) onNavigateToRecord(task, taskObj);
                          }}>
                            <div className="slds-activity-task-subject" style={{ color: '#0176d3' }}>{task.Subject || defaultSubject}</div>
                            {/* Owner-spec 2026-07-07: mirror the "יש ל / משימה
                                צפויה עם" meta line from the monthly timeline
                                (~line 20526) into the upcoming/past-due card
                                so the assignee + assigner are visible without
                                clicking through. Same fallback ('משתמש') when
                                OwnerId__Name is missing; assigner ("עם")
                                only renders when CreatedById__Name is set. */}
                            <div className="slds-activity-task-meta" style={{ fontSize: 12, color: '#54698d', marginTop: 2 }}>
                              יש ל: <strong>{task.OwnerId__Name || task.Owner__Name || 'משתמש'}</strong>
                              {(task.CreatedById__Name || task.CreatedBy__Name) && (
                                <>
                                  {' · משימה צפויה עם: '}
                                  <strong>{task.CreatedById__Name || task.CreatedBy__Name}</strong>
                                </>
                              )}
                            </div>
                            {taskDate && (
                              <div className="slds-activity-task-date">
                                {new Date(taskDate).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })} {new Date(taskDate).toLocaleTimeString('he-IL', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                          <div style={{ position: 'relative' }}>
                            <button onClick={() => setActivityMenuOpen(activityMenuOpen === taskKey ? null : taskKey)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', color: '#706e6b' }}>▼</button>
                            {activityMenuOpen === taskKey && (
                              <div style={{ position: 'absolute', left: 0, top: '100%', background: '#fff', border: '1px solid #d8dde6', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '140px', direction: 'rtl' }}>
                                <button onClick={() => { setActivityMenuOpen(null); const taskObj = objects.find(o => o.api_name === 'Task'); if (taskObj && onNavigateToRecord) onNavigateToRecord(task, taskObj); }} style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f3f3' }}
                                  onMouseEnter={e => e.target.style.background='#f3f3f3'} onMouseLeave={e => e.target.style.background='none'}>עריכה</button>
                                {isAdminUser && (
                                  <button onClick={() => handleDeleteActivity(task.Id, 'task')} style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '13px', color: '#c23934' }}
                                    onMouseEnter={e => e.target.style.background='#fef0ef'} onMouseLeave={e => e.target.style.background='none'}>מחיקה</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  );
                  })()}
                </div>
              </div>

              {/* Monthly Timeline */}
              <div className="slds-activity-timeline">
                {(() => {
                  const allActivities = [
                    // Owner-spec 2026-05-25: use CreatedDate as the timeline
                    // anchor so the "HH:MM" badge shows the actual creation
                    // moment, not midnight UTC (which renders as 03:00 in
                    // Asia/Jerusalem because ActivityDate is date-only).
                    // Calls are stored as Tasks with TaskSubtype='Call' —
                    // tag them as type:'call' so the icon/label render
                    // distinguishes them from generic tasks.
                    ...recordActivities.tasks.map(t => ({
                      ...t,
                      // 2026-10-19: "רישום זיכוי" — the credit-approval flow's closed
                      // notices (Type 'Credit Log'); a log line, not a task to tick.
                      // 2026-10-25: "רישום הנחה" — the discount flow's closed notices (Type 'Discount Log').
                      type: t.Type === 'Credit Log' ? 'credit' : t.Type === 'Discount Log' ? 'discount' : t.TaskSubtype === 'Call' ? 'call' : 'task',
                      date: t.CreatedDate || t.ActivityDate,
                    })),
                    ...recordActivities.events.map(e => ({
                      ...e,
                      type: 'event',
                      date: e.StartDateTime || e.CreatedDate || e.ActivityDate,
                    })),
                  ].filter(a => a.date).sort((a, b) => new Date(b.date) - new Date(a.date));

                  // Group by month
                  const months = {};
                  allActivities.forEach(activity => {
                    const date = new Date(activity.date);
                    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                    const monthName = date.toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem', month: 'long', year: 'numeric' });
                    if (!months[monthKey]) {
                      months[monthKey] = { name: monthName, items: [] };
                    }
                    months[monthKey].items.push(activity);
                  });

                  const _monthsAll = Object.entries(months);
                  const monthEntries = showAllActivities ? _monthsAll : _monthsAll.slice(0, 3);

                  if (monthEntries.length === 0) {
                    return (
                      <div className="slds-activity-no-history">
                        <p>אין היסטוריית פעילויות</p>
                      </div>
                    );
                  }

                  return monthEntries.map(([key, month]) => (
                    <div key={key} className="slds-activity-month">
                      <div className="slds-activity-month-header">
                        <span className="slds-activity-month-label">חודש זה</span>
                        <span className="slds-activity-month-name">{month.name}</span>
                      </div>
                      <div className="slds-activity-month-items">
                        {(showAllActivities ? month.items : month.items.slice(0, 10)).map((item, idx) => {
                          const itemKey = item.Id || `${key}-${idx}`;
                          return (
                          <div key={itemKey} className="slds-activity-timeline-item">
                            <div className="slds-activity-timeline-icon">
                              <span className={`slds-activity-icon-circle ${item.type === 'event' ? 'slds-icon-event' : item.type === 'call' ? 'slds-icon-call' : 'slds-icon-task'}`}>
                                {item.type === 'event' ? '📅' : item.type === 'call' ? '📞' : item.type === 'credit' ? '💸' : item.type === 'discount' ? '💲' : '✅'}
                              </span>
                            </div>
                            <div className="slds-activity-timeline-content" style={{ flex: 1 }}>
                              <div className="slds-activity-timeline-header">
                                <span className="slds-activity-timeline-title" style={{ cursor: 'pointer', color: '#0176d3' }}
                                  onClick={() => { const objName = item.type === 'event' ? 'Event' : 'Task'; const targetObj = objects.find(o => o.api_name === objName); if (targetObj && onNavigateToRecord) onNavigateToRecord(item, targetObj); }}>
                                  {item.Subject || (item.type === 'event' ? 'אירוע' : item.type === 'call' ? 'שיחה' : item.type === 'credit' ? 'רישום זיכוי' : item.type === 'discount' ? 'רישום הנחה' : 'משימה')}
                                </span>
                                <span className="slds-activity-timeline-type">
                                  {item.type === 'event' ? 'אירוע' : item.type === 'call' ? 'שיחה' : item.type === 'credit' ? 'רישום זיכוי' : item.type === 'discount' ? 'רישום הנחה' : 'משימה'}
                                </span>
                              </div>
                              {/* Owner-spec 2026-06-15: meta line reformat.
                                  New shape — "יש ל: <Owner> משימה צפויה עם:
                                  <CreatedBy> בתאריך: <date + time>". The
                                  prior format ("היה ל-<contact> משימה עם
                                  <owner>") put the contact (WhoId) on the
                                  left and the owner (assignee) on the right,
                                  which mixed two different relationships and
                                  hid the assigner entirely. New shape:
                                    - "יש ל"     → the assignee (Owner)
                                    - "עם"       → who assigned it (CreatedBy)
                                    - "בתאריך"   → ActivityDate + time (same
                                                  format as before — was a
                                                  separate span at the end)
                                  WhoId / Description / Subject all stay as
                                  optional supporting bits on a second row so
                                  the user still sees the related contact
                                  and topic when present. */}
                              <div className="slds-activity-timeline-meta">
                                <span>
                                  יש ל: <strong>{item.OwnerId__Name || item.Owner__Name || 'משתמש'}</strong>
                                  {(item.CreatedById__Name || item.CreatedBy__Name) && (
                                    <>
                                      {' משימה צפויה עם: '}
                                      <strong>{item.CreatedById__Name || item.CreatedBy__Name}</strong>
                                    </>
                                  )}
                                  {' בתאריך: '}
                                  <span className="slds-activity-timeline-date">
                                    {new Date(item.date).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })} {new Date(item.date).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </span>
                                {(item.WhoId__Name || item.Description) && (
                                  <div style={{ fontSize: 12, color: '#54698d', marginTop: 2 }}>
                                    {item.WhoId__Name && (
                                      <span>קשור ל-<span style={{ color: '#0176d3', cursor: 'pointer' }}
                                        onClick={() => { const contactObj = objects.find(o => o.api_name === 'Contact'); if (contactObj && onNavigateToRecord) onNavigateToRecord({ Id: item.WhoId }, contactObj); }}>{item.WhoId__Name}</span></span>
                                    )}
                                    {item.WhoId__Name && item.Description && <span> · </span>}
                                    {item.Description && (
                                      <span>{item.Description.substring(0, 40)}{item.Description.length > 40 ? '...' : ''}</span>
                                    )}
                                  </div>
                                )}
                                {/* Owner-spec 2026-06-17: PR #941 writes the
                                    completion modal's two free-text inputs to
                                    Activity_Outcome__c ("תגובה לפעילות") and
                                    Description__c ("טיפול שבוצע"). PR #942
                                    reformatted the meta line but never
                                    surfaced these values — so the user filled
                                    them in but couldn't see them. Render
                                    them inline on the timeline when present.
                                    Standard SF Description (no __c suffix) is
                                    a separate field already handled above. */}
                                {(item.Activity_Outcome__c || item.Description__c) && (
                                  <div style={{ fontSize: 12, color: '#3e3e3c', marginTop: 4,
                                                background: '#f7f9fb', border: '1px solid #e6ecf3',
                                                borderRadius: 4, padding: '6px 8px' }}>
                                    {item.Activity_Outcome__c && (
                                      <div><strong>תגובה לפעילות:</strong> {item.Activity_Outcome__c}</div>
                                    )}
                                    {item.Description__c && (
                                      <div style={{ marginTop: item.Activity_Outcome__c ? 2 : 0 }}>
                                        <strong>טיפול שבוצע:</strong> {item.Description__c}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {taskCanEdit && item.type === 'task' && !item.IsClosed && (
                                <input type="checkbox" title="סמן כהושלם" style={{ cursor: 'pointer' }} onChange={async () => {
                                  try {
                                    await fetch(`${API_BASE}/api/objects/Task/records/${item.Id}`, {
                                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ Status: 'Completed', IsClosed: true })
                                    });
                                    setRecordActivities({ tasks: [], events: [] });
                                    loadRecordActivities();
                                  } catch(e) { console.error(e); }
                                }} />
                              )}
                              <button
                                className="slds-activity-timeline-expand"
                                onClick={() => setActivityMenuOpen(activityMenuOpen === itemKey ? null : itemKey)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', color: '#706e6b' }}
                              >▼</button>
                              {activityMenuOpen === itemKey && (
                                <div style={{ position: 'absolute', left: 0, top: '100%', background: '#fff', border: '1px solid #d8dde6', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '140px', direction: 'rtl' }}>
                                  <button onClick={() => { setActivityMenuOpen(null); const objName = item.type === 'event' ? 'Event' : 'Task'; const targetObj = objects.find(o => o.api_name === objName); if (targetObj && onNavigateToRecord) onNavigateToRecord(item, targetObj); }}
                                    style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f3f3' }}
                                    onMouseEnter={e => e.target.style.background='#f3f3f3'} onMouseLeave={e => e.target.style.background='none'}>עריכה</button>
                                  {isAdminUser && (
                                    <button onClick={() => handleDeleteActivity(item.Id, item.type)} style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '13px', color: '#c23934' }}
                                      onMouseEnter={e => e.target.style.background='#fef0ef'} onMouseLeave={e => e.target.style.background='none'}>מחיקה</button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* View All Button.

                  It had no onClick at all — a static element that looked
                  clickable and did nothing, below a timeline capped at 10 items
                  per month. It is now also hidden when there is nothing behind
                  it, so a record with three activities stops offering to show
                  all of them. */}
              {(activityView.hasMore || showAllActivities) && (
                <div className="slds-activity-view-all">
                  <button
                    className="slds-button slds-button_brand"
                    onClick={() => setShowAllActivities(v => !v)}
                  >
                    {showAllActivities
                      ? 'הצג פחות'
                      : activityView.hiddenCount > 0
                        ? `הצג את כל הפעילויות (עוד ${activityView.hiddenCount})`
                        : 'הצג את כל הפעילויות'}
                  </button>
                </div>
              )}

              {/* Info Banner */}
              <div className="slds-activity-info-banner">
                <span className="slds-info-icon">ℹ️</span>
                <span>כדי לשנות את הפריטים המוצגים, נסה לשנות את המסננים שלך.</span>
              </div>
            </div>
          )}

          {/* Updates Tab Content (internal chat) */}
          {activeTab === 'updates' && (
            <div className="slds-details-content slds-updates-content">
              <div className="slds-chatter-container">
                <div className="slds-chatter-post-box">
                  <textarea
                    placeholder="שתף עדכון..."
                    className="slds-chatter-input"
                    disabled
                  />
                  <button className="slds-button slds-button_brand" disabled>פרסם</button>
                </div>
                <div className="slds-chatter-feed">
                  {recordUpdates.length > 0 ? (
                    recordUpdates.map((update, idx) => (
                      <div key={idx} className="slds-chatter-item">
                        <div className="slds-chatter-avatar">👤</div>
                        <div className="slds-chatter-content">
                          <div className="slds-chatter-user">{update.user || 'משתמש'}</div>
                          <div className="slds-chatter-text">{update.text}</div>
                          <div className="slds-chatter-time">{update.date}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="slds-empty-state">
                      <span className="slds-empty-icon">💬</span>
                      <p>אין עדכונים עדיין</p>
                      <p className="slds-empty-subtitle">היה הראשון לשתף עדכון על רשומה זו</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* History Tab Content */}
          {activeTab === 'history' && (
            <div className="slds-details-content slds-history-content" style={{ padding: '16px' }}>
              {/* Created/Modified Summary */}
              <div style={{ display: 'flex', gap: '24px', padding: '12px 16px', background: '#f4f6f9', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#444', flexWrap: 'wrap' }}>
                <div>
                  <strong>נוצר על ידי:</strong>{' '}
                  {historyMeta.created_by || currentRecord.CreatedByName || currentRecord.CreatedById || '—'}
                  {(historyMeta.created_date || currentRecord.CreatedDate) && (
                    <span style={{ color: '#706e6b', marginRight: '8px' }}>
                      {' '}({formatIsraelDateTime(historyMeta.created_date || currentRecord.CreatedDate) || new Date(historyMeta.created_date || currentRecord.CreatedDate).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })})
                    </span>
                  )}
                </div>
                <div>
                  <strong>עודכן לאחרונה על ידי:</strong>{' '}
                  {historyMeta.last_modified_by || currentRecord.LastModifiedByName || currentRecord.LastModifiedById || '—'}
                  {(historyMeta.last_modified_date || currentRecord.LastModifiedDate) && (
                    <span style={{ color: '#706e6b', marginRight: '8px' }}>
                      {' '}({formatIsraelDateTime(historyMeta.last_modified_date || currentRecord.LastModifiedDate) || new Date(historyMeta.last_modified_date || currentRecord.LastModifiedDate).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })})
                    </span>
                  )}
                </div>
              </div>

              {loadingHistory ? (
                <div className="slds-loading-state">טוען היסטוריה...</div>
              ) : recordHistory.length > 0 ? (
                <div>
                  {/* Group changes by timestamp (same second = same update) */}
                  {(() => {
                    const groups = [];
                    let currentGroup = null;
                    recordHistory.forEach((item) => {
                      const ts = (item.date || '').substring(0, 19);
                      const user = item.user || '';
                      const groupKey = `${ts}_${user}`;
                      if (!currentGroup || currentGroup.key !== groupKey) {
                        currentGroup = { key: groupKey, date: item.date, user, type: item.type, items: [] };
                        groups.push(currentGroup);
                      }
                      if (item.type === 'field_change' && item.field) {
                        currentGroup.items.push(item);
                      }
                    });

                    return groups.map((group, gIdx) => (
                      <div key={gIdx} style={{ marginBottom: '16px', borderBottom: '1px solid #e5e5e5', paddingBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '18px' }}>
                            {group.type === 'created' ? '🆕' : group.type === 'deleted' ? '🗑️' : '✏️'}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#181818' }}>
                              {group.type === 'created' ? 'רשומה נוצרה' : group.type === 'deleted' ? 'רשומה נמחקה' : 'עדכון שדות'}
                              {group.user && <span style={{ fontWeight: 400, color: '#0176d3', marginRight: '6px' }}> על ידי {group.user}</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#706e6b' }}>
                              {group.date ? new Date(group.date).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' }) : ''}
                            </div>
                          </div>
                        </div>
                        {group.items.length > 0 && (
                          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '13px', direction: 'rtl' }}>
                            <thead>
                              <tr style={{ background: '#f4f6f9' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #d8dde6', width: '28%' }}>שדה</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #d8dde6', width: '36%' }}>ערך קודם</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid #d8dde6', width: '36%' }}>ערך חדש</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.items.map((change, cIdx) => {
                                const _saFieldLabels = {
                                  'SchedStartTime': 'התחלה מתוזמנת', 'SchedEndTime': 'סיום מתוזמן',
                                  'Status': 'מצב', 'StatusCategory': 'קטגוריית מצב',
                                  'ServiceTerritoryId': 'אזור שירות', 'ServiceResourceId': 'משאב שירות',
                                  'Service_Resource__c': 'משאב שירות', 'Collect_Old_Device__c': 'לאסוף מכשיר ישן',
                                  'DueDate': 'תאריך יעד', 'EarliestStartTime': 'התחלה מוקדמת',
                                  'ArrivalWindowStartTime': 'חלון הגעה - התחלה', 'ArrivalWindowEndTime': 'חלון הגעה - סיום',
                                  'ActualStartTime': 'התחלה בפועל', 'ActualEndTime': 'סיום בפועל',
                                  'Duration': 'משך', 'DurationType': 'סוג משך',
                                  'WorkTypeId': 'סוג עבודה', 'ParentRecordId': 'רשומת אב',
                                  'Subject': 'נושא', 'Description': 'תיאור',
                                  'Street': 'רחוב', 'City': 'עיר', 'PostalCode': 'מיקוד',
                                  'AccountId': 'חשבון', 'ContactId': 'איש קשר',
                                  'FSL__Pinned__c': 'נעוץ', 'Resource_Description__c': 'תיאור משאב',
                                  'OwnerId': 'בעלים', 'Priority': 'עדיפות',
                                  'Latitude': 'קו רוחב', 'Longitude': 'קו אורך',
                                };
                                const _fLabel = _saFieldLabels[change.field] || (formatFieldLabel ? formatFieldLabel(change.field) : change.field);
                                return (
                                <tr key={cIdx} style={{ borderBottom: '1px solid #e5e5e5' }}>
                                  <td style={{ padding: '8px 12px', fontWeight: 500, color: '#181818', wordBreak: 'break-word' }}>
                                    {_fLabel}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: change.oldValue ? '#c23934' : '#706e6b', wordBreak: 'break-word' }}>
                                    {change.oldValue || '(ריק)'}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: '#04844b', fontWeight: 500, wordBreak: 'break-word' }}>
                                    {change.newValue || '(ריק)'}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <div className="slds-empty-state">
                  <span className="slds-empty-icon">📜</span>
                  <p>אין היסטוריית שינויים</p>
                  <p style={{ fontSize: '13px', color: '#706e6b' }}>שינויים שיבוצעו מעכשיו יוצגו כאן</p>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Tab Content */}
          {activeTab === 'whatsapp' && (
            <div className="slds-details-content slds-whatsapp-content" style={{ padding: '16px' }}>
              <RecordWhatsAppChat
                currentRecord={currentRecord}
                objectName={object?.api_name}
                currentUser={currentUser}
              />
            </div>
          )}
          </div> {/* End slds-record-tabs-card */}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <RecordFormModal
          object={object}
          record={currentRecord}
          fields={objectFields}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
        />
      )}

      {/* 2026-10-25 — manager discount request on one product line (בקשת הנחה ממנהל) */}
      {showDiscountRequestModal && isOpportunityPage && (
        <Suspense fallback={null}>
          <DiscountRequestModal
            opportunityId={currentRecord.Id}
            onClose={() => setShowDiscountRequestModal(false)}
            onCreated={() => { refreshDiscountSummary(); loadRecordActivities(); }}
          />
        </Suspense>
      )}

      {/* 2026-10-15 — credit approval request (בקש אישור ממנהל לזיכוי לקוח) */}
      {showCreditApprovalModal && (
        <Suspense fallback={null}>
          <CreditApprovalRequestModal
            sourceObject={object.api_name}
            recordId={currentRecord.Id}
            onClose={() => setShowCreditApprovalModal(false)}
            onCreated={(req) => {
              alert(`בקשת הזיכוי נשלחה לאישור (מס׳ ${req.id}) ונפתחה משימה ל${req.approver_name || req.approver_username}`);
              loadRecordActivities();
            }}
          />
        </Suspense>
      )}

      {/* New Activity Modal */}
      {showNewActivityModal && (() => {
        const activityConfig = {
          call: { title: 'רישום שיחה', icon: '📞', color: '#0176d3', subjectDefault: 'שיחה' },
          task: { title: 'משימה חדשה', icon: '✅', color: '#04844b', subjectDefault: '' },
          event: { title: 'אירוע חדש', icon: '📅', color: '#f0983a', subjectDefault: '' },
          email: { title: 'דואר אלקטרוני', icon: '✉️', color: '#7b83eb', subjectDefault: '' },
        };
        const cfg = activityConfig[showNewActivityModal.type] || activityConfig.task;
        const isContact = ['Contact', 'Lead'].includes(object.api_name);

        const filteredActivityUsers = activityUserSearch.trim()
          ? activityUsers.filter(u => {
              const s = activityUserSearch.toLowerCase();
              return (u.name && u.name.toLowerCase().includes(s)) ||
                     (u.email && u.email.toLowerCase().includes(s)) ||
                     (u.username && u.username.toLowerCase().includes(s));
            })
          : activityUsers;

        // PR #793 (#9) — owner-spec: the email button on a record now
        // actually sends an email via the existing /api/send-email
        // (Resend) endpoint, in addition to logging a Task for the
        // record-activity timeline. Defaults: TO = the account email,
        // FROM = the current logged-in user's email. No email login
        // session required.
        //
        // PR #798 (#9 followup 2026-06-08) — owner-flagged: Account
        // emails are stored on `PersonEmail` (person-account schema),
        // not Email/Email__c, so the אל field stayed empty even on
        // records with a valid customer email. Added PersonEmail to
        // the front of the fallback chain.
        const accountEmailDefault =
          currentRecord?.PersonEmail
          || currentRecord?.Email
          || currentRecord?.Account?.PersonEmail
          || currentRecord?.Account?.Email
          || currentRecord?.Email__c
          || '';
        const currentUserEmail = currentUser?.email || currentUser?.username || '';

        const handleActivitySave = async (e) => {
          e.preventDefault();
          // 2026-06-17 owner-flagged duplicate-task root cause: under workday
          // load the BE create takes 2-3s, users impatiently re-click, every
          // click fires another POST. Bail immediately on a re-entry while
          // the previous submit is still in flight.
          if (savingActivity) return;
          const form = e.target;
          const subject = form.subject.value.trim();
          if (!subject) { alert('נושא הוא שדה חובה'); return; }
          setSavingActivity(true);
          try {

          // ----- Email branch -----
          if (showNewActivityModal.type === 'email') {
            const toEmail = (form.emailTo?.value || '').trim();
            if (!toEmail || !toEmail.includes('@')) {
              alert('יש להזין כתובת מייל תקינה של נמען');
              return;
            }
            const bodyText = (form.description?.value || '').trim();
            // PR #798 (#9 followup) — body is now required (owner-flagged
            // empty emails were getting through to customers).
            if (!bodyText) {
              alert('תוכן ההודעה הוא שדה חובה');
              return;
            }
            try {
              // PR after #806 owner-spec 2026-06-08: pass attachments
              // through to /api/send-email → send_email_resend, which
              // forwards them as Resend's [{filename, content}] shape.
              // Each `content` is already base64-encoded at pick time
              // (FileReader.readAsDataURL → strip the data:...;base64,
              // prefix).
              const sendRes = await fetch(`${API_BASE}/api/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  to: toEmail,
                  subject: subject,
                  html: bodyText.replace(/\n/g, '<br/>'),
                  fromName: currentUser?.name || currentUserEmail || 'Afikim',
                  // PR #798 — owner-flagged: previously the recipient saw
                  // the system info@ as the From address with no link
                  // back to the user. Pass replyTo so reply lands in the
                  // user's inbox AND so /api/send-email can surface the
                  // user's address in the From header (server-side adds
                  // it to the display name).
                  replyTo: currentUserEmail,
                  senderUserEmail: currentUserEmail,
                  attachments: emailAttachments.length
                    ? emailAttachments.map(a => ({ filename: a.name, content: a.content }))
                    : undefined,
                }),
              });
              if (!sendRes.ok) {
                const detail = await sendRes.json().catch(() => ({}));
                // PR #799 (#9 v3 2026-06-08): the new verification gate
                // returns 403 + error='email_not_verified'. Show the
                // Hebrew message the backend provides — it tells the user
                // where to click for verification.
                if (detail.error === 'email_not_verified') {
                  alert(detail.message || 'כתובת המייל שלך אינה מאומתת. בקש ממנהל לאמת את הכתובת בהגדרות > ניהול משתמשים.');
                } else {
                  alert('שגיאה בשליחת מייל: ' + (detail.error || detail.detail || sendRes.status));
                }
                return;
              }
              const sendOut = await sendRes.json().catch(() => ({}));
              if (sendOut?.success === false) {
                if (sendOut.error === 'email_not_verified') {
                  alert(sendOut.message || 'כתובת המייל שלך אינה מאומתת.');
                } else {
                  alert('שגיאה בשליחת מייל: ' + (sendOut.error || 'unknown'));
                }
                return;
              }
            } catch (sendErr) {
              alert('שגיאה בשליחת מייל: ' + sendErr.message);
              return;
            }
            // Email sent — also log a Task so it appears in the activity
            // timeline. Marked Completed (the email has already been sent).
            const emailLogData = {
              Subject: `📧 ${subject}`,
              Description: `אל: ${toEmail}\nמאת: ${currentUserEmail}\n\n${bodyText}`,
              Status: 'Completed',
              Priority: 'Normal',
              ActivityDate: new Date().toISOString().split('T')[0],
              TaskSubtype: 'Email',
              IsClosed: true,
            };
            if (isContact) {
              emailLogData.WhoId = currentRecord.Id;
            } else if (object.api_name === 'Task' || object.api_name === 'Event') {
              // A Task/Event is NOT a valid WhatId target — the "related to"
              // resolver only knows Account/Opportunity/Case/WorkOrder, so a
              // WhatId pointing to a Task renders an empty related record.
              // Inherit the viewed activity's own WhatId/WhoId so the log links
              // to the real record, not to the activity itself. (2026-07-16)
              if (currentRecord.WhatId) emailLogData.WhatId = currentRecord.WhatId;
              if (currentRecord.WhoId) emailLogData.WhoId = currentRecord.WhoId;
            } else {
              emailLogData.WhatId = currentRecord.Id;
            }
            if (currentRecord.AccountId) {
              emailLogData.AccountId = currentRecord.AccountId;
            } else if (object.api_name === 'Account') {
              emailLogData.AccountId = currentRecord.Id;
            }
            if (activitySelectedUser) {
              emailLogData.OwnerId = activitySelectedUser.email || activitySelectedUser.username;
              emailLogData.Owner__Name = activitySelectedUser.name || '';
            } else if (currentUserEmail) {
              emailLogData.OwnerId = currentUserEmail;
              emailLogData.Owner__Name = currentUser?.name || '';
            }
            // Owner-spec 2026-06-09: stamp creator + assigner so the
            // record shows "נוצר על ידי: <user>" instead of "system" when
            // the backend session lookup misses (e.g. token expired
            // mid-session). Also send the session token so the backend
            // can do its own auto-stamp as a defence-in-depth fallback.
            {
              const _creatorId = currentUser?.username || currentUser?.email || currentUserEmail || '';
              const _creatorName = currentUser?.name || currentUser?.Name || _creatorId;
              if (_creatorId && !emailLogData.CreatedById)       emailLogData.CreatedById = _creatorId;
              if (_creatorName && !emailLogData.CreatedByName)   emailLogData.CreatedByName = _creatorName;
              if (_creatorId && !emailLogData.LastModifiedById)  emailLogData.LastModifiedById = _creatorId;
              if (_creatorName && !emailLogData.LastModifiedByName) emailLogData.LastModifiedByName = _creatorName;
              if (_creatorId && !emailLogData.OwnerId)           emailLogData.OwnerId = _creatorId;
            }
            const _sessTok = localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || '';
            try {
              const logRes = await fetch(`${API_BASE}/api/objects/Task/records`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(_sessTok ? { 'Authorization': `Bearer ${_sessTok}` } : {}),
                },
                body: JSON.stringify(emailLogData),
              });
              if (logRes.ok) {
                const created = await logRes.json().catch(() => ({}));
                const serverRecord = created?.record || created || {};
                const nowIso = new Date().toISOString();
                const newId = serverRecord.Id || created?.id || `temp-${Date.now()}`;
                const optimistic = {
                  ...emailLogData,
                  ...serverRecord,
                  Id: newId,
                  CreatedDate: serverRecord.CreatedDate || nowIso,
                  LastModifiedDate: serverRecord.LastModifiedDate || nowIso,
                  Owner__Name: serverRecord.Owner__Name || emailLogData.Owner__Name || '',
                  IsClosed: true,
                };
                setRecordActivities(prev => {
                  const list = prev?.tasks || [];
                  if (list.some(x => x.Id === newId)) return prev;
                  return { ...(prev || { tasks: [], events: [] }), tasks: [optimistic, ...list] };
                });
              }
            } catch (logErr) {
              // Logging is best-effort; the email itself already went out.
              console.error('[email] activity log failed:', logErr);
            }
            setShowNewActivityModal(null);
            return;
          }
          // ----- End email branch -----

          const activityData = {
            Subject: subject,
            Description: form.description?.value || '',
            Status: showNewActivityModal.type === 'call' ? 'Completed' : (form.status?.value || 'Not Started'),
            Priority: form.priority?.value || 'Normal',
            ActivityDate: form.activityDate?.value || new Date().toISOString().split('T')[0],
          };

          // Link to record
          if (isContact) {
            activityData.WhoId = currentRecord.Id;
          } else if (object.api_name === 'Task' || object.api_name === 'Event') {
            // A Task/Event is NOT a valid WhatId target — the "related to"
            // resolver only knows Account/Opportunity/Case/WorkOrder, so a
            // WhatId pointing to a Task renders an empty related record.
            // Inherit the viewed activity's own WhatId/WhoId so the follow-up
            // links to the real record, not to the activity itself. (2026-07-16)
            if (currentRecord.WhatId) activityData.WhatId = currentRecord.WhatId;
            if (currentRecord.WhoId) activityData.WhoId = currentRecord.WhoId;
          } else {
            activityData.WhatId = currentRecord.Id;
          }
          // Also link to Account if available
          if (currentRecord.AccountId) {
            activityData.AccountId = currentRecord.AccountId;
          } else if (object.api_name === 'Account') {
            activityData.AccountId = currentRecord.Id;
          }

          // Assigned To user
          if (activitySelectedUser) {
            activityData.OwnerId = activitySelectedUser.email || activitySelectedUser.username;
            activityData.Owner__Name = activitySelectedUser.name || '';
          }

          // Owner-spec 2026-06-09: stamp creator + last-modifier so
          // the new task's history/audit shows the real user, not
          // "system". Defence-in-depth: also send Authorization so the
          // backend's session-based auto-stamp works.
          {
            const _creatorId = currentUser?.username || currentUser?.email || currentUserEmail || '';
            const _creatorName = currentUser?.name || currentUser?.Name || _creatorId;
            if (_creatorId && !activityData.CreatedById)         activityData.CreatedById = _creatorId;
            if (_creatorName && !activityData.CreatedByName)     activityData.CreatedByName = _creatorName;
            if (_creatorId && !activityData.LastModifiedById)    activityData.LastModifiedById = _creatorId;
            if (_creatorName && !activityData.LastModifiedByName) activityData.LastModifiedByName = _creatorName;
            // Default OwnerId to the creator when the user didn't pick
            // an assignee. Without this, the backend's OwnerId default
            // chain (added in PR #840) takes over — and on PRs not yet
            // deployed it would leave Owner empty.
            if (_creatorId && !activityData.OwnerId)             activityData.OwnerId = _creatorId;
          }

          const objectType = showNewActivityModal.type === 'event' ? 'Event' : 'Task';
          if (objectType === 'Event') {
            activityData.StartDateTime = `${activityData.ActivityDate}T${form.startTime?.value || '09:00'}:00`;
            activityData.EndDateTime = `${activityData.ActivityDate}T${form.endTime?.value || '10:00'}:00`;
            delete activityData.Status;
            delete activityData.Priority;
          }
          if (showNewActivityModal.type === 'call') {
            activityData.TaskSubtype = 'Call';
            activityData.IsClosed = true;
          }

          const _sessTokAct = localStorage.getItem('session_token') || sessionStorage.getItem('session_token') || '';
          try {
            const res = await fetch(`${API_BASE}/api/objects/${objectType}/records`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(_sessTokAct ? { 'Authorization': `Bearer ${_sessTokAct}` } : {}),
              },
              body: JSON.stringify(activityData)
            });
            if (res.ok) {
              // Task is in AFIKIM_ASYNC_CACHE_MUTATION_OBJECTS — a re-fetch
              // right after POST often misses the new row because the
              // worker's data_cache hasn't received the cross-worker PG
              // NOTIFY yet (can take minutes under load). The previous
              // code did `setRecordActivities({tasks:[], events:[]})` then
              // loadRecordActivities() — wiping the list and then refilling
              // from a stale cache, so the user-created activity was
              // invisible until cache propagation caught up.
              //
              // New behavior: insert optimistically from the POST response
              // + form data into recordActivities, skip the immediate
              // refetch. Next mount reconciles. Owner-reported timing
              // ("eventually they all appear at once") confirms the
              // refetch was the source of the disappearing rows.
              let created = {};
              try { created = await res.json(); } catch { /* ignore */ }
              const serverRecord = created?.record || created || {};
              const nowIso = new Date().toISOString();
              const newId = serverRecord.Id || created?.id || `temp-${Date.now()}`;
              const optimistic = {
                ...activityData,
                ...serverRecord,
                Id: newId,
                CreatedDate: serverRecord.CreatedDate || nowIso,
                LastModifiedDate: serverRecord.LastModifiedDate || nowIso,
                Owner__Name: serverRecord.Owner__Name || activityData.Owner__Name || '',
                IsClosed: serverRecord.IsClosed ?? activityData.IsClosed ?? false,
              };
              setRecordActivities(prev => {
                const key = objectType === 'Event' ? 'events' : 'tasks';
                const list = prev?.[key] || [];
                if (list.some(x => x.Id === newId)) return prev;
                return { ...(prev || { tasks: [], events: [] }), [key]: [optimistic, ...list] };
              });
              setShowNewActivityModal(null);
            } else {
              const err = await res.json();
              alert(err.detail || 'שגיאה ביצירת הפעילות');
            }
          } catch (err) {
            alert('שגיאה: ' + err.message);
          }
          } finally {
            // 2026-06-17 — always release the lock whether we succeeded,
            // failed, or hit an early `return` in any branch. Without
            // the finally, an early return in the email branch (e.g.
            // toEmail validation) would leave the modal frozen.
            setSavingActivity(false);
          }
        };

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
            <div style={{ background: '#fff', borderRadius: '8px', width: '550px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: cfg.color, color: '#fff', borderRadius: '8px 8px 0 0' }}>
                <button onClick={() => setShowNewActivityModal(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>{cfg.icon}</span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{cfg.title}</h3>
                </div>
              </div>
              <form onSubmit={handleActivitySave} onClick={() => setActivityUserDropdownOpen(false)} style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ background: '#f0f7ff', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', color: '#0176d3', direction: 'rtl' }}>
                    מקושר ל: <strong>{showNewActivityModal.recordName || currentRecord.Name || currentRecord.Subject || currentRecord.Id}</strong>
                  </div>
                  {/* PR #793 (#9) — Email-specific recipient + sender fields,
                      rendered only for the email button. The sender is
                      read-only (current logged-in user). The recipient
                      defaults to the account email but stays editable so
                      the user can override on a record that lacks an email
                      or to send to a different address. */}
                  {showNewActivityModal.type === 'email' && (
                    <>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>אל <span style={{ color: '#c23934' }}>*</span></label>
                        <input name="emailTo" type="email" defaultValue={accountEmailDefault} required placeholder="customer@example.com" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>מאת</label>
                        <input type="text" value={currentUserEmail} readOnly style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e5e5', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', background: '#f7f7f7', color: '#444' }} />
                      </div>
                    </>
                  )}
                  {/* Assigned To - User search */}
                  <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>מוקצה ל</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={activitySelectedUser ? activitySelectedUser.name : activityUserSearch}
                        onChange={(e) => {
                          setActivityUserSearch(e.target.value);
                          setActivitySelectedUser(null);
                          setActivityUserDropdownOpen(true);
                        }}
                        onFocus={() => setActivityUserDropdownOpen(true)}
                        placeholder="חפש משתמש..."
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      {activitySelectedUser && (
                        <button type="button" onClick={() => { setActivitySelectedUser(null); setActivityUserSearch(''); }}
                          style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#706e6b' }}>✕</button>
                      )}
                    </div>
                    {activityUserDropdownOpen && !activitySelectedUser && filteredActivityUsers.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d8dde6', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, maxHeight: '180px', overflowY: 'auto', direction: 'rtl' }}>
                        {filteredActivityUsers.slice(0, 20).map(u => (
                          <div key={u.id || u.username}
                            onClick={() => { setActivitySelectedUser(u); setActivityUserSearch(''); setActivityUserDropdownOpen(false); }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f3f3', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f3f3f3'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <span style={{ fontWeight: 500 }}>{u.name || u.username}</span>
                            <span style={{ fontSize: '11px', color: '#706e6b' }}>{u.email || ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>
                      נושא <span style={{ color: '#c23934' }}>*</span>
                      {(showNewActivityModal.type === 'task' || showNewActivityModal.type === 'call') && (
                        <span style={{ color: '#888', fontWeight: 400, marginRight: '6px' }}>(עד 40 תווים — טקסט ארוך יותר בשדה "תיאור")</span>
                      )}
                    </label>
                    {/* 2026-07-01 owner-spec: task Subject capped at 40 chars.
                        Long subjects push actions off the header. Not applied
                        to event/email — those have their own natural limits. */}
                    <input name="subject" type="text" defaultValue={cfg.subjectDefault} required
                      maxLength={(showNewActivityModal.type === 'task' || showNewActivityModal.type === 'call') ? 40 : undefined}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} autoFocus />
                  </div>
                  {showNewActivityModal.type !== 'email' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>תאריך</label>
                      <input name="activityDate" type="date" defaultValue={new Date().toISOString().split('T')[0]} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {showNewActivityModal.type === 'event' && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>שעת התחלה</label>
                        <input name="startTime" type="time" defaultValue="09:00" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>שעת סיום</label>
                        <input name="endTime" type="time" defaultValue="10:00" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  )}
                  {showNewActivityModal.type !== 'event' && showNewActivityModal.type !== 'call' && showNewActivityModal.type !== 'email' && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>סטטוס</label>
                        <select name="status" defaultValue="Not Started" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}>
                          <option value="Not Started">לא התחיל</option>
                          <option value="In Progress">בתהליך</option>
                          <option value="Completed">הושלם</option>
                          <option value="Waiting on someone else">ממתין</option>
                          <option value="Deferred">נדחה</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>עדיפות</label>
                        <select name="priority" defaultValue="Normal" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}>
                          <option value="High">גבוהה</option>
                          <option value="Normal">רגילה</option>
                          <option value="Low">נמוכה</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>
                      {/* 2026-07-01 owner-spec: rename "הערות" to "תיאור"
                          for task/call/event — matches the record page's
                          "תיאור" field, so users see the same label they
                          see on the record after save. Email keeps "תוכן
                          ההודעה". */}
                      {showNewActivityModal.type === 'email' ? 'תוכן ההודעה' : 'תיאור'}
                      {showNewActivityModal.type === 'email' && <span style={{ color: '#c23934' }}> *</span>}
                    </label>
                    <textarea name="description" rows={showNewActivityModal.type === 'email' ? 6 : 3} required={showNewActivityModal.type === 'email'} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d8dde6', borderRadius: '4px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                  {/* PR after #806 owner-spec 2026-06-08: file/image
                      attachments for the email dialog. Reads each file
                      as base64 (strips the data: prefix so Resend's
                      attachments[].content gets the raw payload).
                      Soft caps: 10 MB per file, 20 MB total. */}
                  {showNewActivityModal.type === 'email' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px', fontWeight: 600 }}>קבצים מצורפים</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label htmlFor="emailAttachmentsInput" style={{ cursor: 'pointer', padding: '6px 12px', border: '1px dashed #7b83eb', borderRadius: 4, fontSize: 13, color: '#7b83eb', background: '#f6f7ff', userSelect: 'none' }}>
                          📎 הוסף קבצים / תמונות
                        </label>
                        <input
                          id="emailAttachmentsInput"
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const picked = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (!picked.length) return;
                            const MAX_PER = 10 * 1024 * 1024;
                            const MAX_TOTAL = 20 * 1024 * 1024;
                            const currentTotal = emailAttachments.reduce((n, a) => n + (a.size || 0), 0);
                            const next = [...emailAttachments];
                            const tooBig = [];
                            for (const f of picked) {
                              if (f.size > MAX_PER) { tooBig.push(`${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)`); continue; }
                              if (currentTotal + next.reduce((n, a) => n + (a.size || 0), 0) - currentTotal + f.size > MAX_TOTAL) {
                                tooBig.push(`${f.name} (גורם לחריגה מ-20MB סה"כ)`);
                                continue;
                              }
                              const dataUrl = await new Promise((resolve, reject) => {
                                const r = new FileReader();
                                r.onload = () => resolve(r.result);
                                r.onerror = () => reject(r.error || new Error('read error'));
                                r.readAsDataURL(f);
                              }).catch(() => null);
                              if (!dataUrl || typeof dataUrl !== 'string') continue;
                              const idx = dataUrl.indexOf(',');
                              const content = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
                              next.push({ name: f.name, size: f.size, type: f.type, content });
                            }
                            setEmailAttachments(next);
                            if (tooBig.length) alert('קבצים גדולים מדי לא צורפו:\n' + tooBig.join('\n'));
                          }}
                        />
                        <span style={{ fontSize: 12, color: '#706e6b' }}>
                          {emailAttachments.length
                            ? `${emailAttachments.length} קבצים, ${(emailAttachments.reduce((n, a) => n + (a.size || 0), 0) / 1024 / 1024).toFixed(2)} MB`
                            : 'מקסימום 10MB לקובץ / 20MB סה"כ'}
                        </span>
                      </div>
                      {emailAttachments.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                          {emailAttachments.map((a, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#f7f7f7', borderRadius: 4, fontSize: 12 }}>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.name}>
                                📎 {a.name}
                              </span>
                              <span style={{ color: '#706e6b' }}>{(a.size / 1024).toFixed(0)} KB</span>
                              <button type="button" onClick={() => setEmailAttachments(prev => prev.filter((_, i) => i !== idx))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c23934', fontSize: 14, padding: 0 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', padding: '12px 20px', borderTop: '1px solid #e5e5e5', background: '#fafafa' }}>
                  <button type="button" disabled={savingActivity} onClick={() => setShowNewActivityModal(null)} style={{ padding: '8px 16px', border: '1px solid #d8dde6', borderRadius: '4px', background: '#fff', cursor: savingActivity ? 'not-allowed' : 'pointer', opacity: savingActivity ? 0.6 : 1, fontSize: '13px' }}>ביטול</button>
                  <button
                    type="submit"
                    disabled={savingActivity}
                    style={{
                      padding: '8px 16px', border: 'none', borderRadius: '4px',
                      background: cfg.color, color: '#fff',
                      cursor: savingActivity ? 'not-allowed' : 'pointer',
                      opacity: savingActivity ? 0.6 : 1,
                      fontSize: '13px', fontWeight: 600,
                    }}
                  >
                    {savingActivity
                      ? (showNewActivityModal.type === 'email' ? 'שולח...' : 'שומר...')
                      : (showNewActivityModal.type === 'email' ? '✉️ שלח' : 'שמור')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Related List Create Modal */}
      {relatedCreateModal && (
        <RecordFormModal
          object={{ api_name: relatedCreateModal.objectName, label: relatedCreateModal.objectName }}
          record={{
            [relatedCreateModal.parentField]: relatedCreateModal.parentId,
            [`${relatedCreateModal.parentField}__Name`]: relatedCreateModal.parentName,
          }}
          fields={relatedCreateFields}
          getObjectConfig={getObjectConfig}
          onClose={() => { setRelatedCreateModal(null); setRelatedCreateFields([]); }}
          onSave={handleRelatedCreateSave}
        />
      )}

      {/* Technician Scheduler Modal */}
      {showTechnicianScheduler && (
        <div className="slds-modal-overlay">
          <div
            className="slds-scheduler-modal"
            style={{
              position: 'fixed',
              top: '5%',
              left: '5%',
              right: '5%',
              bottom: '5%',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e5e5',
              backgroundColor: '#f3f3f3'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#181818' }}>
                📅 יומן טכנאים - {displayName}
              </h2>
              <button
                onClick={() => setShowTechnicianScheduler(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#706e6b',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            {/* Modal Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              <Suspense fallback={<LoadingFallback />}>
                <TechnicianScheduler />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Area Planner Modal - מפגשי אזור */}
      {showAreaPlanner && (
        <div className="slds-modal-overlay">
          <div
            className="slds-scheduler-modal"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95vw',
              height: '90vh',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 10000,
              overflow: 'hidden'
            }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e5e5',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'white' }}>
                🗺️ מפגשי אזור - {displayName}
              </h2>
              <button
                onClick={() => setShowAreaPlanner(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            {/* Modal Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              <Suspense fallback={<LoadingFallback />}>
                <AreaPlanner />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Route Optimizer Modal (שביט הקסם) - Record Detail */}
      {showRouteOptimizer && (
        <Suspense fallback={<LoadingFallback />}>
          <RouteOptimizer onClose={() => setShowRouteOptimizer(false)} />
        </Suspense>
      )}

      {/* Bulk Transfer Modal (ניהול היעדרויות טכנאי) - Record Detail */}
      {showBulkTransfer && (
        <Suspense fallback={<LoadingFallback />}>
          <BulkTransfer onClose={() => setShowBulkTransfer(false)} />
        </Suspense>
      )}

      {/* Price Quote Modal (הצעת מחיר) — Account record page */}
      {showPriceQuoteModal && currentRecord && object?.api_name === 'Account' && (
        <Suspense fallback={<LoadingFallback />}>
          <PriceQuoteModal
            account={currentRecord}
            currentUser={currentUser}
            onClose={() => setShowPriceQuoteModal(false)}
          />
        </Suspense>
      )}

      {/* Opportunity Modal - הזדמנות חדשה */}
      {showOpportunityModal && (
        <Suspense fallback={<LoadingFallback />}>
          <OpportunityModal
          isOpen={showOpportunityModal}
          onClose={() => setShowOpportunityModal(false)}
            onSuccess={(newOpp) => {
              setShowOpportunityModal(false);
              loadAllRelatedRecords(true);
            }}
            // Owner-spec 2026-06-09: warm the record + related caches
            // while the user is on the success screen, so that the
            // "מעבר להזדמנות" click paints instantly via the cache-hit
            // path in RecordDetailRoute.
            onPrefetch={(oppId) => {
              if (!oppId) return;
              const cacheKey = `Opportunity/${oppId}`;
              // 1) basic record → addToCache so getFromCache hits
              fetch(`${API_BASE}/api/objects/Opportunity/records/${oppId}`)
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                  const rec = d?.record || d;
                  if (rec && rec.Id) addToCache('Opportunity', [rec]);
                })
                .catch(() => {});
              // 2) related → same pattern as PR #816 parallel kickoff
              if (!_pendingRelatedFetches.has(cacheKey)) {
                const p = (async () => {
                  try {
                    const r = await fetch(`${API_BASE}/api/objects/Opportunity/records/${oppId}/related?limit=100&resolve_refs=true`);
                    if (!r.ok) return null;
                    const d = await r.json();
                    const flat = {};
                    const derivedCounts = {};
                    for (const [k, v] of Object.entries(d.related_objects || {})) {
                      const recs = (v && v.records) || [];
                      flat[k] = recs;
                      // 2026-06-13 — same fix as RecordDetailRoute prefetch
                      // above. /related doesn't return a top-level d.counts.
                      const beCount = (v && typeof v.count === 'number') ? v.count : null;
                      derivedCounts[k] = beCount !== null ? beCount : recs.length;
                    }
                    _relatedRecordsCache.set(cacheKey, {
                      data: flat, timestamp: Date.now(), counts: derivedCounts,
                    });
                    return flat;
                  } catch (e) {
                    console.warn('[OpportunityModal prefetch] /related failed:', e);
                    return null;
                  } finally {
                    _pendingRelatedFetches.delete(cacheKey);
                  }
                })();
                _pendingRelatedFetches.set(cacheKey, p);
              }
            }}
            navigate={navigate}
            account={currentRecord}
          />
        </Suspense>
      )}

      {/* Create Asset Modal - יצירת נכס */}
      {showCreateAssetModal && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateAssetModal
            isOpen={showCreateAssetModal}
            onClose={() => setShowCreateAssetModal(false)}
            onSuccess={(newAsset, meta = {}) => {
              if (meta.optimistic) {
                // Close modal immediately + show the new asset in the list right away
                setShowCreateAssetModal(false);
                if (newAsset) {
                  setRelatedRecords(prev => ({
                    ...prev,
                    Asset: [newAsset, ...(prev.Asset || [])],
                  }));
                }
                return;
              }
              // Background POST resolved — swap the temp record for the real one
              if (meta.error) {
                if (meta.tempId) {
                  setRelatedRecords(prev => ({
                    ...prev,
                    Asset: (prev.Asset || []).filter(a => a.Id !== meta.tempId),
                  }));
                }
                return;
              }
              if (newAsset && meta.tempId) {
                setRelatedRecords(prev => ({
                  ...prev,
                  Asset: (prev.Asset || []).map(a => (a.Id === meta.tempId ? { ...a, ...newAsset, _optimistic: false } : a)),
                }));
              }
            }}
            accountId={currentRecord?.Id}
            accountName={currentRecord?.Name || currentRecord?.FirstName && `${currentRecord.FirstName} ${currentRecord.LastName || ''}`.trim()}
            accountData={currentRecord}
          />
        </Suspense>
      )}

      {/* Create Asset in Service WO Flow */}
      {showCreateAssetInWOFlow && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateAssetModal
            isOpen={showCreateAssetInWOFlow}
            onClose={() => setShowCreateAssetInWOFlow(false)}
            onSuccess={(newAsset, meta = {}) => {
              if (meta.optimistic) {
                // Close immediately, show the asset right away in the service-flow picker
                setShowCreateAssetInWOFlow(false);
                if (newAsset) {
                  setServiceAssets(prev => [newAsset, ...(prev || [])]);
                  setSelectedServiceAsset(newAsset);
                }
                return;
              }
              if (meta.error) {
                if (meta.tempId) {
                  setServiceAssets(prev => (prev || []).filter(a => a.Id !== meta.tempId));
                }
                return;
              }
              if (newAsset && meta.tempId) {
                setServiceAssets(prev => (prev || []).map(a => (a.Id === meta.tempId ? { ...a, ...newAsset, _optimistic: false } : a)));
                setSelectedServiceAsset(prev => (prev && prev.Id === meta.tempId ? { ...prev, ...newAsset, _optimistic: false } : prev));
              }
            }}
            accountId={currentRecord?.AccountId}
            accountName={currentRecord?.Account?.Name || currentRecord?.Name}
            accountData={currentRecord}
          />
        </Suspense>
      )}

      {/* Create Service Contract Modal - יצירת חוזה שירות */}
      {showCreateServiceContractModal && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateServiceContractModal
            isOpen={showCreateServiceContractModal}
            onClose={() => setShowCreateServiceContractModal(false)}
            onSuccess={(newContract) => {
              setShowCreateServiceContractModal(false);
              loadAllRelatedRecords(true);
            }}
            accountId={object?.api_name === 'Asset' ? (currentRecord?.AccountId || '') : currentRecord?.Id}
            accountName={object?.api_name === 'Asset' ? (currentRecord?.AccountId__Name || currentRecord?.AccountName || '') : (currentRecord?.Name || (currentRecord?.FirstName && `${currentRecord.FirstName} ${currentRecord.LastName || ''}`.trim()))}
            accountData={currentRecord}
            assetId={object?.api_name === 'Asset' ? currentRecord?.Id : null}
            assetName={object?.api_name === 'Asset' ? currentRecord?.Name : null}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Create Case Modal - יצירת מקרה */}
      {showCreateCaseModal && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateCaseModal
            isOpen={showCreateCaseModal}
            onClose={() => setShowCreateCaseModal(false)}
            onSuccess={(createdRecords) => {
              setShowCreateCaseModal(false);
              loadAllRelatedRecords(true);
            }}
            onCreateOpportunity={({ accountId: oppAccountId } = {}) => {
              // 2026-05-15: invoked when the rep picks "צור הזדמנות" from the
              // no-contract / expired-contract block screen. Hard-navigate to
              // the Account record where the "הזדמנות חדשה" button is mounted.
              // RecordPage doesn't have useNavigate in scope here — using
              // window.location.href keeps this self-contained.
              if (oppAccountId) {
                window.location.href = buildRecordUrl('Account', oppAccountId);
              }
            }}
            assetId={currentRecord?.Id}
            assetName={currentRecord?.Name}
            assetData={currentRecord}
            accountId={currentRecord?.AccountId}
            accountName={currentRecord?.Account__Name || currentRecord?.AccountId__Name}
            accountData={null}
          />
        </Suspense>
      )}

      {/* Task Transfer Modal — העברת משימה.
          Opened from Task detail. Transfers OwnerId to a chosen user, writes
          a TaskTransfer audit row + bell notification, and shows the full
          history of previous transfers on this task. */}
      {showTaskTransferModal && object?.api_name === 'Task' && currentRecord?.Id && (
        <Suspense fallback={<LoadingFallback />}>
          <TaskTransferModal
            task={currentRecord}
            currentUser={currentUser}
            onClose={() => setShowTaskTransferModal(false)}
            onTransferred={(payload) => {
              setCurrentRecord(prev => ({
                ...prev,
                OwnerId: payload.to_user_id || prev.OwnerId,
                Owner__Name: payload.to_user_name || prev.Owner__Name,
                // Appended reason/notes lines (2026-10-14), echoed by the endpoint.
                Transfer_Reason__c: payload.Transfer_Reason__c ?? prev.Transfer_Reason__c,
                Transfer_Notes__c: payload.Transfer_Notes__c ?? prev.Transfer_Notes__c,
              }));
            }}
          />
        </Suspense>
      )}

      {/* Owner-spec 2026-06-15: complete-task modal. Replaces the old
          two stand-alone satisfaction buttons. Captures outcome + the
          two free-text fields + an opt-in assigner-notification. */}
      {showTaskCompleteModal && object?.api_name === 'Task' && currentRecord?.Id && (
        <Suspense fallback={<LoadingFallback />}>
          <TaskCompleteModal
            task={currentRecord}
            currentUser={currentUser}
            onClose={() => setShowTaskCompleteModal(false)}
            onCompleted={(payload) => {
              // 2026-07-01 — previously only Status + IsClosed were merged in,
              // so the record page kept showing "-" for "תגובה לפעילות" and
              // "טיפול שבוצע" until a full page refresh. Owner-flagged as the
              // reason agents stopped filling the modal — they submit, see
              // no evidence anything was saved, conclude the fields don't
              // matter. Now spread the SF-column-named fields the endpoint
              // echoes back so the details section updates in place.
              setCurrentRecord(prev => ({
                ...prev,
                Status:              payload.status               || prev.Status,
                IsClosed:            payload.IsClosed             || '1',
                Activity_Outcome__c: payload.Activity_Outcome__c  ?? prev.Activity_Outcome__c,
                Description__c:      payload.Description__c       ?? prev.Description__c,
                LastModifiedDate:    payload.LastModifiedDate     || prev.LastModifiedDate,
                LastModifiedById:    payload.LastModifiedById     || prev.LastModifiedById,
              }));
            }}
          />
        </Suspense>
      )}

      {/* Update Case Classification Modal — עדכון סיווג מקרה.
          Opened from Asset detail. Lists all Cases on this Asset and lets the
          user re-classify each (Reason_1/2/Description); the change cascades
          through the WO + SA + ProductRequired automation. Locked when any SA
          is past Scheduled. */}
      {showUpdateCaseClassificationModal && currentRecord?.Id && (
        <Suspense fallback={<LoadingFallback />}>
          <UpdateCaseClassificationModal
            assetId={currentRecord.Id}
            assetName={currentRecord.Name || ''}
            onClose={() => setShowUpdateCaseClassificationModal(false)}
            onSaved={() => {
              setShowUpdateCaseClassificationModal(false);
              loadAllRelatedRecords(true);
            }}
          />
        </Suspense>
      )}

      {/* Refund Modal — יצירת זיכוי לנציג מכירה. Opens from Payment__c
          detail page (admin/accounting only). Writes the 6 refund fields
          on the linked Opportunity. */}
      {showTransferModal && currentRecord?.Id && (
        <Suspense fallback={<LoadingFallback />}>
          <PaymentTransferModal
            paymentId={currentRecord.Id}
            onClose={() => setShowTransferModal(false)}
            onSubmitted={() => {
              setShowTransferModal(false);
              // Re-fetch the payment + related records so the moved/reduced
              // amount and updated opportunity balances render immediately.
              loadAllRelatedRecords(true);
            }}
          />
        </Suspense>
      )}
      {showRefundModal && currentRecord?.Id && (
        <Suspense fallback={<LoadingFallback />}>
          <RefundModal
            paymentId={currentRecord.Id}
            onClose={() => setShowRefundModal(false)}
            onSubmitted={() => {
              setShowRefundModal(false);
              // Refresh related records so the linked Opp's refund fields
              // appear in any related-list views.
              loadAllRelatedRecords(true);
            }}
          />
        </Suspense>
      )}

      {/* Change-Owner modal — opened from the 🔄 icon next to the OwnerId
          field. Searches the User object, picks one, PATCHes the current
          record, then refreshes. */}
      {showChangeOwnerModal && currentRecord?.Id && object?.api_name && (
        <Suspense fallback={<LoadingFallback />}>
        <ChangeOwnerModal
          objectApiName={object.api_name}
          recordId={currentRecord.Id}
          currentOwnerId={currentRecord.OwnerId}
          currentOwnerName={
            currentRecord.Owner__Name || currentRecord.OwnerName ||
            currentRecord.OwnerId__Name || currentRecord.Owner_Name || ''
          }
          onClose={() => setShowChangeOwnerModal(false)}
          onChanged={async (newOwnerId, newOwnerName) => {
            setShowChangeOwnerModal(false);
            // Refresh the current record so the new owner shows.
            try {
              const r = await fetch(`${API_BASE}/api/objects/${object.api_name}/records/${encodeURIComponent(currentRecord.Id)}`);
              if (r.ok) {
                const j = await r.json();
                if (j?.record) setCurrentRecord(j.record);
              } else {
                // Optimistic local update if refetch fails.
                setCurrentRecord(prev => prev ? { ...prev, OwnerId: newOwnerId, Owner__Name: newOwnerName } : prev);
              }
            } catch {
              setCurrentRecord(prev => prev ? { ...prev, OwnerId: newOwnerId, Owner__Name: newOwnerName } : prev);
            }
          }}
        />
        </Suspense>
      )}

      {/* Strict-identification modal — opened from the 'זיהוי מחמיר' button on
          Account detail. Save updates the address + apartment/floor/entrance
          and sets Strict_Identification__c=true. */}
      {oppGateMsg && object?.api_name === 'Account' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}
          onClick={() => setOppGateMsg('')}>
          <div style={{ background: '#fff', width: 460, maxWidth: '92vw', borderRadius: 8, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ background: '#b91c1c', color: '#fff', padding: '10px 16px', fontWeight: 600, fontSize: 15 }}>⚠ לא ניתן ליצור הזדמנות</div>
            <div style={{ padding: '16px 20px', background: '#fef2f2', color: '#7f1d1d', fontSize: 14, lineHeight: 1.7 }}>{oppGateMsg}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', padding: '12px 16px', borderTop: '1px solid #fecaca' }}>
              <button type="button" onClick={() => { setOppGateMsg(''); setShowStrictIdentificationModal(true); }}
                style={{ padding: '8px 16px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                🏠 זיהוי מחמיר
              </button>
              <button type="button" onClick={() => setOppGateMsg('')}
                style={{ padding: '8px 16px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      {showStrictIdentificationModal && currentRecord?.Id && object?.api_name === 'Account' && (
        <Suspense fallback={<LoadingFallback />}>
          <StrictIdentificationModal
            account={currentRecord}
            onClose={() => setShowStrictIdentificationModal(false)}
            onSuccess={(updated) => {
              setShowStrictIdentificationModal(false);
              setCurrentRecord(updated);
            }}
          />
        </Suspense>
      )}

      {/* 2026-05-18: AddProductRequiredModal — manual ProductRequired add
          on a WorkOrder. Posts via /api/device-allocation/manual-add-product-required
          which re-validates the per-user-per-month quota server-side. */}
      {showAddProductRequiredModal && currentRecord?.Id && object?.api_name === 'WorkOrder' && (
        <Suspense fallback={<LoadingFallback />}>
          <AddProductRequiredModal
            workOrderId={currentRecord.Id}
            onClose={() => setShowAddProductRequiredModal(false)}
            onCreated={(payload, meta = {}) => {
              if (meta.optimistic) {
                // Modal will close itself; insert the row immediately
                if (payload) {
                  setRelatedRecords(prev => ({
                    ...prev,
                    ProductRequired: [payload, ...(prev.ProductRequired || [])],
                  }));
                }
                return;
              }
              if (meta.error) {
                // Roll back the optimistic insert
                if (meta.tempId) {
                  setRelatedRecords(prev => ({
                    ...prev,
                    ProductRequired: (prev.ProductRequired || []).filter(p => p.Id !== meta.tempId),
                  }));
                }
                return;
              }
              // Background POST resolved — swap temp record for the real one
              const realRecord = payload?.record || payload?.product_required || payload;
              if (realRecord && meta.tempId) {
                setRelatedRecords(prev => ({
                  ...prev,
                  ProductRequired: (prev.ProductRequired || []).map(p =>
                    p.Id === meta.tempId ? { ...p, ...realRecord, _optimistic: false } : p
                  ),
                }));
              }
            }}
          />
        </Suspense>
      )}

      {/* WhatsApp opt-in modal — Account-page button "הצטרפות לשירות הוואטסאפ".
          Lets the rep pick which Account phone to send the opt-in prompt to. The modal is
          components/omnichannel/WhatsAppOptInModal (shared with the leads panel, 2026-10-29). */}
      {showWhatsAppOptInModal && currentRecord?.Id && object?.api_name === 'Account' && (() => {
        // PG column names from prod (verified 2026-05-17 against information_schema):
        //   Phone (CamelCase) and Another_phone__c (lowercase 'p'). The
        //   backend echoes PG names AS-IS, so we read both common casings.
        const clean = (v) => (v && v !== 'nan' && v !== 'null' && v !== 'undefined') ? String(v).trim() : '';
        const phoneOptions = [
          { field: 'Phone', label: 'טלפון ראשי',
            value: clean(currentRecord.Phone) || clean(currentRecord.phone) },
          { field: 'Another_phone__c', label: 'טלפון נוסף',
            value: clean(currentRecord.Another_phone__c) || clean(currentRecord.Another_Phone__c) || clean(currentRecord.another_phone__c) },
        ];
        const firstName = clean(currentRecord?.FirstName)
            || clean(currentRecord?.first_name)
            || clean(currentRecord?.Name).split(/\s+/)[0]
            || clean(currentRecord?.name).split(/\s+/)[0];
        return (
          <WhatsAppOptInModal phones={phoneOptions} firstName={firstName} currentUser={currentUser}
                              onClose={() => setShowWhatsAppOptInModal(false)} />
        );
      })()}

      {/* Route-to-Agent modal — Account-page button "העברת שיחות ישירות לנציגה".
          Mirrors retrieved-metadata/flows/Account_Route_To_Agent.flow-meta.xml:
          Q1 (type of transfer) -> yes/no/change -> result + WA send. */}
      {showRouteToAgentModal && currentRecord?.Id && object?.api_name === 'Account' && (() => {
        const currentAgentId = currentRecord.Agent_ID__c || currentRecord.agent_id__c || '';
        const currentAgentName = currentRecord.Agent_Name__c || currentRecord.agent_name__c || '';
        const hasExistingAgent = !!String(currentAgentId).trim();
        const myUserId = currentUser?.id || '';
        const myUserName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim()
                            || currentUser?.username || '';
        const clean = (v) => (v && v !== 'nan' && v !== 'null' && v !== 'undefined') ? String(v).trim() : '';
        const accountPhone = clean(currentRecord.Phone) || clean(currentRecord.Another_phone__c)
                              || clean(currentRecord.Another_Phone__c) || clean(currentRecord.person_mobile_phone);
        const normalize = (p) => {
          if (!p) return null;
          let c = p.replace(/[^0-9]/g, '');
          if (c.startsWith('05') && c.length === 10) c = '972' + c.substring(1);
          else if (c.startsWith('0') && c.length === 9) c = '972' + c.substring(1);
          else if (!c.startsWith('972') && c.length === 9) c = '972' + c;
          return c;
        };

        const submit = async (decision) => {
          setRtaSubmitting(true);
          setRtaError('');
          try {
            const assigning = (decision === 'yes' || decision === 'change');
            const updates = assigning
              ? {
                  Agent_ID__c: myUserId,
                  Agent_Name__c: myUserName,
                  Transition_to_a_Agent__c: true,
                  Type_of_transfer_to_customer__c: rtaTypeOfTransfer,
                }
              : {
                  Agent_ID__c: '',
                  Agent_Name__c: '',
                  Transition_to_a_Agent__c: false,
                  Type_of_transfer_to_customer__c: rtaTypeOfTransfer,
                };
            const res = await fetch(`${API_BASE}/api/objects/Account/records/${currentRecord.Id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updates),
            });
            if (!res.ok) {
              const t = await res.text().catch(() => '');
              throw new Error(`עדכון נכשל: ${res.status} ${(t || '').substring(0, 120)}`);
            }
            setCurrentRecord(prev => prev ? { ...prev, ...updates } : prev);

            // 2026-06-02 owner-spec: when un-assigning (decision='no'),
            // also reset the customer's bot session — pop lastAgent*
            // + routedToAgent so the next inbound message falls through
            // to the normal bot welcome flow. Without this the
            // smart-routing in _process_bot_message keeps notifying the
            // just-released agent for up to 3h after the last outbound
            // template, and the bot never sees the message.
            if (!assigning && accountPhone) {
              try {
                const resetPhone = normalize(accountPhone);
                await fetch(`${API_BASE}/api/whatsapp/bot-session/reset`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ phone: resetPhone }),
                });
              } catch (resetErr) {
                console.warn('[RouteToAgent] bot-session reset failed (non-fatal):', resetErr);
              }
            }

            // Send WhatsApp opt-in template only when assigning (mirrors flow's Send_Message step)
            if (assigning && accountPhone) {
              const phone = normalize(accountPhone);
              const message = 'לקוח יקר, בכדי שנוכל להתחיל בשיחה, הגב לי את המילה ״שלח״ 😊';
              try {
                await fetch(`${API_BASE}/api/whatsapp/send`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ phone, message, userId: myUserId, userName: myUserName }),
                });
              } catch (waErr) {
                console.warn('[RouteToAgent] WA send failed (non-fatal):', waErr);
              }
            }

            setRtaResult(assigning ? 'assigned' : 'unassigned');
            setRtaStep(3);
          } catch (e) {
            setRtaResult('error');
            setRtaError(e.message || String(e));
            setRtaStep(3);
          } finally {
            setRtaSubmitting(false);
          }
        };

        const btn = (selected, accent, children, onClick) => (
          <button onClick={onClick} style={{
            padding: '22px 12px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#16325c',
            background: selected ? (accent === 'green' ? '#e8f5e9' : accent === 'red' ? '#fef1ed' : '#eef4fb') : '#fff',
            border: `2px solid ${selected ? (accent === 'green' ? '#2e844a' : accent === 'red' ? '#a9001f' : '#0176d3') : '#d8dde6'}`,
            borderRadius: '8px', transition: 'all 0.15s', textAlign: 'center',
          }}>{children}</button>
        );

        return (
          <div className="slds-backdrop slds-backdrop_open" style={{ zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '8px', minWidth: '420px', maxWidth: '560px', padding: '24px', direction: 'rtl', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e8eaed', paddingBottom: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#16325c' }}>📞 העברת שיחות ישירות לנציגה</h2>
                <button onClick={() => setShowRouteToAgentModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#54698d' }}>✕</button>
              </div>

              {/* Step 1: Type of transfer (Q1) */}
              {rtaStep === 1 && (
                <>
                  <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 'bold', color: '#16325c', textAlign: 'center' }}>בחר/י באפשרות המתאימה</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '22px' }}>
                    {btn(rtaTypeOfTransfer === 'הלקוח לא עונה', 'blue',
                      (<><div style={{ fontSize: '28px', marginBottom: '6px' }}>📞</div>הלקוח לא עונה</>),
                      () => setRtaTypeOfTransfer('הלקוח לא עונה'))}
                    {btn(rtaTypeOfTransfer === 'הלקוח באמצע עסקה', 'blue',
                      (<><div style={{ fontSize: '28px', marginBottom: '6px' }}>💼</div>הלקוח באמצע עסקה</>),
                      () => setRtaTypeOfTransfer('הלקוח באמצע עסקה'))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowRouteToAgentModal(false)}
                      style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#16325c' }}>ביטול</button>
                    <button onClick={() => setRtaStep(2)} disabled={!rtaTypeOfTransfer}
                      style={{ padding: '8px 22px', background: !rtaTypeOfTransfer ? '#ccc' : '#0176d3', color: '#fff', border: 'none', borderRadius: '4px', cursor: !rtaTypeOfTransfer ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>הבא</button>
                  </div>
                </>
              )}

              {/* Step 2: yes/no (or change/no if already-assigned) */}
              {rtaStep === 2 && (
                <>
                  {hasExistingAgent && (
                    <div style={{ marginBottom: '16px', padding: '12px', background: '#fef1ed', border: '1px solid #ffb6a0', borderRadius: '6px', textAlign: 'center', color: '#a9001f', fontSize: '14px', fontWeight: 500 }}>
                      ללקוח זה יש כבר מעבר ישיר לנציגה <strong>{currentAgentName}</strong>
                    </div>
                  )}
                  <div style={{ marginBottom: '14px', fontSize: '14px', fontWeight: 'bold', color: '#16325c', textAlign: 'center' }}>בחר/י באפשרות:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                    {btn(rtaChoice === 'yes' || rtaChoice === 'change', 'green',
                      (<><div style={{ fontSize: '24px', marginBottom: '6px' }}>✅</div>{hasExistingAgent ? 'החלפת מעבר ישיר לנציגה' : 'כן'}</>),
                      () => setRtaChoice(hasExistingAgent ? 'change' : 'yes'))}
                    {btn(rtaChoice === 'no', 'red',
                      (<><div style={{ fontSize: '24px', marginBottom: '6px' }}>❌</div>לא</>),
                      () => setRtaChoice('no'))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setRtaStep(1)} disabled={rtaSubmitting}
                      style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d8dde6', borderRadius: '4px', cursor: rtaSubmitting ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#16325c' }}>חזרה</button>
                    <button onClick={() => submit(rtaChoice)} disabled={!rtaChoice || rtaSubmitting}
                      style={{ padding: '8px 22px', background: (!rtaChoice || rtaSubmitting) ? '#ccc' : '#0176d3', color: '#fff', border: 'none', borderRadius: '4px', cursor: (!rtaChoice || rtaSubmitting) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                      {rtaSubmitting ? 'שומר...' : 'אישור'}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: result */}
              {rtaStep === 3 && rtaResult === 'assigned' && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: '52px', marginBottom: '14px' }}>✅</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2e844a', marginBottom: '24px' }}>
                    הלקוח מועבר ישירות אל: {myUserName}
                  </div>
                  <button onClick={() => setShowRouteToAgentModal(false)}
                    style={{ padding: '10px 28px', background: '#0176d3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>סיום</button>
                </div>
              )}
              {rtaStep === 3 && rtaResult === 'unassigned' && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: '52px', marginBottom: '14px' }}>✓</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2e844a', marginBottom: '24px' }}>
                    הלקוח ימשיך לקבל שירות רגיל, ללא העברה ישירה לנציגה
                  </div>
                  <button onClick={() => setShowRouteToAgentModal(false)}
                    style={{ padding: '10px 28px', background: '#0176d3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>סיום</button>
                </div>
              )}
              {rtaStep === 3 && rtaResult === 'error' && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
                  <div style={{ fontSize: '15px', color: '#c23934', marginBottom: '14px' }}>שגיאה בעדכון החשבון</div>
                  <div style={{ fontSize: '13px', color: '#54698d', marginBottom: '20px', padding: '8px', background: '#fef1ed', borderRadius: '4px', textAlign: 'right' }}>{rtaError}</div>
                  <button onClick={() => setShowRouteToAgentModal(false)}
                    style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>סגור</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Create Appointment Result Modal - הודעת הצלחה ליצירת פגישה מהזמנת עבודה */}
      {showCreateAppointmentModal && createAppointmentResult && (
        <>
          <div
            className="slds-backdrop slds-backdrop_open"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9000,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
            onClick={() => setShowCreateAppointmentModal(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95vw',
              maxWidth: '500px',
              zIndex: 9001,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              background: 'white',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            <div style={{
              padding: '24px',
              textAlign: 'center',
              direction: 'rtl'
            }}>
              {createAppointmentResult.success ? (
                <>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #04844b 0%, #2e844a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '40px'
                  }}>
                    ✓
                  </div>
                  <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#080707' }}>
                    פעילות שירות נוצרה בהצלחה!
                  </h2>
                  <div style={{
                    background: '#f3f3f3',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    textAlign: 'right'
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#706e6b', fontSize: '14px' }}>מספר פגישה:</span>
                      <span style={{ fontWeight: '700', fontSize: '16px', marginRight: '8px', color: '#0176d3' }}>
                        {createAppointmentResult.appointmentNumber}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#706e6b', fontSize: '14px' }}>אזור שירות:</span>
                      <span style={{ fontWeight: '600', fontSize: '14px', marginRight: '8px' }}>
                        {createAppointmentResult.territoryName}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button
                      onClick={() => {
                        setShowCreateAppointmentModal(false);
                        // Navigate to the new ServiceAppointment
                        if (createAppointmentResult.appointmentId && onNavigateToRecord) {
                          const saObj = objects.find(o => o.api_name === 'ServiceAppointment');
                          if (saObj) {
                            onNavigateToRecord({ Id: createAppointmentResult.appointmentId }, saObj);
                          }
                        }
                      }}
                      style={{
                        padding: '10px 24px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)',
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      📅 פתח פגישה
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateAppointmentModal(false);
                        loadAllRelatedRecords(true); // Refresh related lists
                      }}
                      style={{
                        padding: '10px 24px',
                        borderRadius: '6px',
                        border: '1px solid #dddbda',
                        background: 'white',
                        color: '#080707',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      סגור
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #c23934 0%, #ba0517 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '40px',
                    color: 'white'
                  }}>
                    ✕
                  </div>
                  <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '16px', color: '#c23934' }}>
                    שגיאה ביצירת פעילות שירות
                  </h2>
                  <p style={{ color: '#706e6b', marginBottom: '20px' }}>
                    {createAppointmentResult.message}
                  </p>
                  <button
                    onClick={() => setShowCreateAppointmentModal(false)}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '6px',
                      border: '1px solid #dddbda',
                      background: 'white',
                      color: '#080707',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    סגור
                  </button>
                </>
              )}
            </div>
          </div>
          <style>{`
            @keyframes modalSlideIn {
              from {
                opacity: 0;
                transform: translate(-50%, -48%);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%);
              }
            }
          `}</style>
        </>
      )}

      {/* Products Modal - הוספת מוצרים */}
      {showProductsModal && (
        <Suspense fallback={<LoadingFallback />}>
          <ProductsModal
            isOpen={showProductsModal}
            onClose={() => setShowProductsModal(false)}
            onOptimisticAdd={(products) => {
              // UX Performance Initiative P1 follow-up (owner-spec 2026-06-09):
              // append the chosen products to the related list IMMEDIATELY
              // with synthetic Ids so the user sees them the instant the
              // modal closes — instead of staring at the old list while
              // ~2-4s of POST/refresh runs in the background.
              // The real records replace these when loadAllRelatedRecords
              // returns from the parent's onSuccess below; setRelatedRecords
              // there overwrites the optimistic Ids with the server's.
              if (!products || products.length === 0) return;
              const now = new Date().toISOString();
              const userId = currentUser?.id || currentUser?.email || '';
              const userName = currentUser?.name || currentUser?.email || '';
              const stamp = Date.now();
              const optimistic = products.map((p, idx) => ({
                Id: `_opt_${stamp}_${idx}`,
                _optimistic: true,
                OpportunityId: currentRecord?.Id || '',
                Product2Id: p.Id,
                Product2__Name: p.Name,
                Product2Id__Name: p.Name,
                Name: p.Name,
                ProductName: p.Name,
                ProductCode: p.ProductCode || '',
                Quantity: p.quantity,
                UnitPrice: p.customPrice,
                TotalPrice: p.customPrice * p.quantity,
                CreatedById: userId,
                CreatedByName: userName,
                OwnerId: userId,
                Owner__Name: userName,
                CreatedDate: now,
                LastModifiedDate: now,
              }));
              setRelatedRecords(prev => ({
                ...prev,
                OpportunityLineItem: [...(prev.OpportunityLineItem || []), ...optimistic],
              }));
              setExpandedRelatedLists(prev => ({ ...prev, OpportunityLineItem: true }));

              // P1 follow-up #2 (owner-spec 2026-06-09): also update the
              // header amounts immediately. Replicates the math in
              // ProductsModal.handleSubmit so the optimistic numbers
              // match what the server will write a moment later:
              //   Amount        = Σ TotalPrice over all OLI (existing + new)
              //   ActualBalance = max(0, Amount - Σ success Payment.Amount__c)
              //   FictitiousBal = same formula (per existing handler).
              try {
                const existingLines = relatedRecords?.OpportunityLineItem || [];
                const allLines = [...existingLines, ...optimistic];
                const newAmount = allLines.reduce(
                  (s, li) => s + (parseFloat(li.TotalPrice) || 0), 0
                );
                const existingPayments = relatedRecords?.Payment__c || [];
                const successAmount = existingPayments
                  .filter(p => (
                    p.Payment_Status__c === 'SUCCESS'
                    || p.Payment_Status__c === 'הצלחה'
                    || p.Payment_Status__c === 'Paid'
                  ))
                  .reduce((s, p) => s + (parseFloat(p.Amount__c) || 0), 0);
                const newBalance = Math.max(0, newAmount - successAmount);
                setCurrentRecord(prev => ({
                  ...prev,
                  Amount: Math.round(newAmount * 100) / 100,
                  Actual_Balance_To_Be_Paid__c: Math.round(newBalance * 100) / 100,
                  Fictitious_Balance_Due__c: Math.round(newBalance * 100) / 100,
                }));
              } catch (e) {
                console.warn('[ProductsModal] optimistic amount calc failed:', e);
              }
            }}
            onSuccess={async (result) => {
              // Targeted re-fetch instead of window.location.reload() which
              // forced full page rebuild + 20+s wait. Owner-reported issue
              // 2026-04-27 — added products didn't appear in related list
              // until manual refresh because reload was the entire flow.
              //
              // The 300ms artificial sleep that previously sat here is gone
              // (2026-06-02): async-cache-mutation now broadcasts cache
              // updates cross-worker via PG NOTIFY → Redis pub/sub before
              // the POST response returns, so any worker handling the next
              // /related already sees the new OLI rows. The cache-bust _t=
              // param also forces /related to bypass its own RELATED_CACHE
              // and read fresh from PG.
              //
              // We also reuse the PUT response.record that ProductsModal
              // already produced (passed in as result.updatedOpportunity)
              // instead of re-fetching the parent Opportunity here.
              console.log('[ProductsModal] onSuccess — targeted refresh');
              try {
                await loadAllRelatedRecords(true);
                if (result?.updatedOpportunity) {
                  setCurrentRecord(result.updatedOpportunity);
                } else {
                  // Fallback if PUT path failed in the modal — go fetch fresh.
                  const recRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${encodeURIComponent(currentRecord.Id)}?_t=${Date.now()}`);
                  if (recRes.ok) {
                    const recData = await recRes.json();
                    if (recData.record) setCurrentRecord(recData.record);
                  }
                }
              } catch (e) {
                console.error('[ProductsModal] Refresh error, falling back to reload:', e);
                window.location.reload();
              }
            }}
            opportunityId={currentRecord?.Id}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Payment Modal - תשלום */}
      {showPaymentModal && (
        <Suspense fallback={<LoadingFallback />}>
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            // Owner-spec 2026-06-09: optimistic balance deduct so the
            // header updates the moment the modal closes — before the
            // server's recalc-balance + record refetch finish.
            //   isActual=true  → reduce BOTH Actual + Fictitious (real
            //                    payment, money actually came in)
            //   isActual=false → reduce ONLY Fictitious (תשלום פיקטיבי)
            // Server values overwrite these ~1-2s later via onSuccess.
            onOptimisticDeduct={({ amount, isActual }) => {
              if (!amount || amount <= 0) return;
              setCurrentRecord(prev => {
                const curFic = parseFloat(prev?.Fictitious_Balance_Due__c || 0) || 0;
                const next = {
                  ...prev,
                  Fictitious_Balance_Due__c: Math.round(Math.max(0, curFic - amount) * 100) / 100,
                };
                if (isActual) {
                  const curAct = parseFloat(prev?.Actual_Balance_To_Be_Paid__c || 0) || 0;
                  next.Actual_Balance_To_Be_Paid__c = Math.round(Math.max(0, curAct - amount) * 100) / 100;
                }
                return next;
              });
            }}
            onSuccess={async () => {
              console.log('[PaymentModal] onSuccess called, instant refresh');
              try {
                // 500ms artificial sleep removed 2026-06-02: with
                // Payment__c now on the async-cache-mutation allowlist
                // (.env + systemd unit), the create_record handler
                // already broadcasts the cache update via PG NOTIFY →
                // Redis pub/sub before returning, so the next /related
                // sees the new Payment row on whichever worker handles it.
                // The cache-bust _t= param on /related ensures we bypass
                // its in-memory RELATED_CACHE too.
                // Force authoritative recalc on the backend
                try {
                  const recalcRes = await fetch(`${API_BASE}/api/opportunity/${encodeURIComponent(currentRecord.Id)}/recalc-balance`, { method: 'POST' });
                  if (recalcRes.ok) {
                    const recalcData = await recalcRes.json();
                    console.log('[PaymentModal] Balance recalc:', recalcData);
                  }
                } catch (e) { console.warn('[PaymentModal] Recalc call failed:', e); }
                // Refresh related records (includes Payment__c) with cache-bust
                await loadAllRelatedRecords(true);
                // Also re-fetch opportunity record for updated balance fields (cache-bust)
                const recRes = await fetch(`${API_BASE}/api/objects/Opportunity/records/${encodeURIComponent(currentRecord.Id)}?_t=${Date.now()}`);
                if (recRes.ok) {
                  const recData = await recRes.json();
                  if (recData.record) setCurrentRecord(recData.record);
                }
              } catch (e) {
                console.error('[PaymentModal] Refresh error:', e);
                window.location.reload();
              }
            }}
            opportunity={currentRecord}
            products={relatedRecords?.OpportunityLineItem || []}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Document Generation Modal - הפקת מסמכים (admin/accounting only).
          Triggered from the Opportunity record. Seeded with ALL of the
          parent Account's opportunities (loaded by the button's onClick
          above) so the user sees every opp + its products + its payments
          in the picker — not just the one we came from. Falls back to
          just the current opp if the account load failed. */}
      {showDocumentModal && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateDocumentModal
            isOpen={showDocumentModal}
            onClose={() => { setShowDocumentModal(false); setAccountDocumentOpps([]); }}
            onSuccess={() => {
              setShowDocumentModal(false);
              setAccountDocumentOpps([]);
              loadAllRelatedRecords(true);
            }}
            account={{ Id: currentRecord?.AccountId, Name: currentRecord?.AccountId__Name || currentRecord?.Account__Name || currentRecord?.Account_Name__c || '' }}
            opportunities={accountDocumentOpps.length > 0 ? accountDocumentOpps : [currentRecord]}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Account-level Document Generation Modal - הפקת מסמכים בחשבון
          (admin/accounting only). Same modal, seeded with ALL of the
          account's opportunities so the user can pick which one to
          issue the document against. */}
      {showAccountDocumentModal && (
        <Suspense fallback={<LoadingFallback />}>
          <CreateDocumentModal
            isOpen={showAccountDocumentModal}
            onClose={() => { setShowAccountDocumentModal(false); setAccountDocumentOpps([]); }}
            onSuccess={() => {
              setShowAccountDocumentModal(false);
              setAccountDocumentOpps([]);
              loadAllRelatedRecords(true);
            }}
            account={currentRecord || {}}
            opportunities={accountDocumentOpps}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Bounced Checks Panel - שיקים חוזרים (admin/accounting only) */}
      {showBouncedChecksPanel && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95vw', maxWidth: '800px', maxHeight: '85vh', zIndex: 9001, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', background: 'white', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', direction: 'rtl' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>שיקים חוזרים</h2>
              <button onClick={() => { setShowBouncedChecksPanel(false); setAccountChecks([]); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '20px', overflow: 'auto', flex: 1, direction: 'rtl' }}>
              {accountChecks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  <p>לא נמצאו שיקים</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>מס׳ שיק</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>בנק</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>סניף</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>חשבון</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>סכום</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>תאריך פירעון</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>סטטוס</th>
                      <th style={{ padding: '10px 14px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>פעולה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountChecks.map((check, idx) => (
                      <tr key={check.Id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px' }}>{check.Check_Number__c || '-'}</td>
                        <td style={{ padding: '10px 14px' }}>{check.Bank_Name__c || '-'}</td>
                        <td style={{ padding: '10px 14px' }}>{check.Bank_Branch__c || '-'}</td>
                        <td style={{ padding: '10px 14px' }}>{check.Bank_Account_Number__c || '-'}</td>
                        <td style={{ padding: '10px 14px', fontWeight: '600' }}>{check.Amount__c ? `₪${parseFloat(check.Amount__c).toLocaleString()}` : '-'}</td>
                        <td style={{ padding: '10px 14px' }}>{check.Check_Due_Date__c || check.CreatedDate?.split('T')[0] || '-'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                            background: check.Payment_Status__c === 'Returned' ? '#fecaca' : check.Payment_Status__c === 'Success' ? '#dcfce7' : '#fef3c7',
                            color: check.Payment_Status__c === 'Returned' ? '#dc2626' : check.Payment_Status__c === 'Success' ? '#16a34a' : '#d97706'
                          }}>{check.Payment_Status__c === 'Returned' ? 'חזר' : check.Payment_Status__c === 'Success' ? 'שולם' : check.Payment_Status__c || 'ממתין'}</span>
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          {check.Payment_Status__c !== 'Returned' ? (
                            <button onClick={() => { setSelectedCheckForReturn(check); setShowReturnedCheckModal(true); }}
                              style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                              שיק חוזר
                            </button>
                          ) : <span style={{ color: '#dc2626', fontSize: '12px', fontWeight: '600' }}>חזר</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Returned Check Modal - שיק חוזר */}
      {showReturnedCheckModal && selectedCheckForReturn && (
        <Suspense fallback={<LoadingFallback />}>
          <ReturnedCheckModal
            isOpen={showReturnedCheckModal}
            onClose={() => { setShowReturnedCheckModal(false); setSelectedCheckForReturn(null); }}
            onSuccess={() => {
              setShowReturnedCheckModal(false);
              setSelectedCheckForReturn(null);
              setShowBouncedChecksPanel(false);
              loadAllRelatedRecords(true);
            }}
            check={selectedCheckForReturn}
            currentUser={currentUser}
          />
        </Suspense>
      )}

      {/* Customer Documents Modal - מסמכי לקוח (admin/accounting only) */}
      {showCustomerLedger && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95vw', maxWidth: '900px', maxHeight: '90vh', zIndex: 9001, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', background: 'white', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: 'white', direction: 'rtl' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>מסמכי לקוח — {ledgerData?.account?.name || currentRecord?.Name || `${currentRecord?.FirstName || ''} ${currentRecord?.LastName || ''}`.trim() || ''}</h2>
              <button onClick={() => { setShowCustomerLedger(false); setLedgerData(null); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'white' }}>✕</button>
            </div>
            <div style={{ padding: '20px', overflow: 'auto', flex: 1, direction: 'rtl' }}>
              {isLoadingLedger ? (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                  <div style={{ border: '3px solid #e2e8f0', borderTopColor: '#0176d3', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                  <p style={{ color: '#64748b' }}>טוען מסמכים...</p>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : ledgerData ? (
                <>
                  {ledgerData.error && (
                    <div style={{ padding: '12px 16px', background: '#fef0f0', border: '1px solid #ea6e6c', borderRadius: '8px', color: '#c23934', marginBottom: '16px', fontSize: '14px' }}>
                      שגיאה בטעינת נתונים: {ledgerData.error}
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                    סה"כ {(ledgerData.documents || []).length} מסמכים
                  </div>
                  {(ledgerData.documents || []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>אין מסמכים מקושרים לחשבון זה</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(ledgerData.documents || []).map((doc, idx) => {
                        const isImage = doc.is_image;
                        const isPdf = (doc.mime_type || '').includes('pdf');
                        const icon = isImage ? '🖼️' : isPdf ? '📄' : doc.doc_type === 'קבלה' ? '🧾' : '📋';
                        const dateStr = doc.created_at ? (doc.created_at.split('T')[0] || doc.created_at) : '';
                        const sourceLabel = doc.source_object === 'Payment__c' ? 'תשלום'
                          : doc.source_object === 'ServiceAppointment' ? 'פעילות שירות'
                          : doc.source_object === 'WorkOrder' ? 'הזמנת עבודה'
                          : doc.source_object === 'Opportunity' ? 'הזדמנות'
                          : doc.source_object === 'Case' ? 'פנייה'
                          : doc.source_object === 'Asset' ? 'נכס'
                          : doc.source_object === 'Account' ? 'חשבון'
                          : doc.source_object;
                        const docKey = doc.url + '_' + idx;
                        return (
                          <div key={docKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '240px' }}>
                              <span style={{ fontSize: '24px' }}>{icon}</span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                  style={{ color: '#0176d3', fontSize: '14px', fontWeight: '600', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={doc.label}>
                                  {doc.label}
                                </a>
                                <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: '#64748b' }}>
                                  {sourceLabel && <span>{sourceLabel}{doc.source_label ? ` • ${doc.source_label}` : ''}</span>}
                                  {dateStr && <span>📅 {dateStr}</span>}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                title="העתק לינק"
                                onClick={async () => {
                                  // navigator.clipboard is undefined on
                                  // non-HTTPS origins (Chrome ships this as
                                  // a "secure context" API). Owner browses
                                  // via http://45.130.104.8 in some flows,
                                  // so we need a fallback for that.
                                  const text = doc.url;
                                  let ok = false;
                                  try {
                                    if (navigator?.clipboard?.writeText) {
                                      await navigator.clipboard.writeText(text);
                                      ok = true;
                                    } else {
                                      const ta = document.createElement('textarea');
                                      ta.value = text;
                                      ta.setAttribute('readonly', '');
                                      ta.style.position = 'fixed';
                                      ta.style.top = '-1000px';
                                      ta.style.opacity = '0';
                                      document.body.appendChild(ta);
                                      ta.select();
                                      ok = document.execCommand('copy');
                                      document.body.removeChild(ta);
                                    }
                                  } catch (e) { ok = false; }
                                  if (ok) {
                                    setCopyToast({ idx, text: 'הועתק' });
                                    setTimeout(() => setCopyToast(null), 1500);
                                  } else {
                                    window.prompt('העתק ידנית (Ctrl+C):', text);
                                  }
                                }}
                                style={{ padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {copyToast?.idx === idx ? '✓ הועתק' : '📋 העתק'}
                              </button>
                              <button
                                title="שליחה בוואטסאפ"
                                disabled={!ledgerData?.account?.phone}
                                onClick={() => {
                                  // In-app WhatsApp send through Afikim's
                                  // Baileys integration (NOT WhatsApp Web).
                                  // The message lands in the customer's
                                  // record-WA panel and is delivered via
                                  // the company number. Owner explicitly
                                  // asked us NOT to use wa.me / WhatsApp
                                  // Web here.
                                  const text = `לקוח יקר לבקשתך מצורף המסמך : ${doc.label}\n${doc.url}`;
                                  setWaDraftDoc({ doc, text });
                                  setWaDraftError(null);
                                }}
                                style={{ padding: '6px 12px', background: '#25D366', border: 'none', borderRadius: '6px', color: '#fff', cursor: ledgerData?.account?.phone ? 'pointer' : 'not-allowed', opacity: ledgerData?.account?.phone ? 1 : 0.5, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                💬 וואטסאפ
                              </button>
                              <button
                                title="שליחה במייל"
                                disabled={!ledgerData?.account?.email}
                                onClick={() => {
                                  const customerName = ledgerData?.account?.name || '';
                                  const userName = (currentUser?.first_name || '') + (currentUser?.last_name ? ` ${currentUser.last_name}` : '') || currentUser?.name || currentUser?.username || '';
                                  const body =
                                    `שלום ${customerName},\n\n` +
                                    `לבקשתך מצורף המסמך : ${doc.label}\n` +
                                    `${doc.url}\n\n` +
                                    `בברכה,\n` +
                                    `${userName}\n` +
                                    `אפיקים מים`;
                                  setEmailDraft({
                                    to: ledgerData?.account?.email || '',
                                    subject: `מסמך מצורף — ${doc.label}`,
                                    body,
                                  });
                                  setEmailModalDoc(doc);
                                  setEmailSendError(null);
                                }}
                                style={{ padding: '6px 12px', background: '#0176d3', border: 'none', borderRadius: '6px', color: '#fff', cursor: ledgerData?.account?.email ? 'pointer' : 'not-allowed', opacity: ledgerData?.account?.email ? 1 : 0.5, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                📧 מייל
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Per-document Email send modal */}
      {emailModalDoc && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9100 }} onClick={() => !emailSending && setEmailModalDoc(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95vw', maxWidth: '560px', zIndex: 9101, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', background: 'white', direction: 'rtl' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0176d3', color: 'white' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>שליחה במייל</h3>
              <button onClick={() => !emailSending && setEmailModalDoc(null)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: emailSending ? 'wait' : 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {emailSendError && (
                <div style={{ padding: '8px 12px', background: '#fef0f0', border: '1px solid #ea6e6c', borderRadius: '6px', color: '#c23934', fontSize: '13px' }}>{emailSendError}</div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475569', fontWeight: 600, marginBottom: '4px' }}>אל</label>
                <input type="email" value={emailDraft.to} onChange={e => setEmailDraft(d => ({ ...d, to: e.target.value }))}
                  dir="ltr"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', textAlign: 'left' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475569', fontWeight: 600, marginBottom: '4px' }}>נושא</label>
                <input type="text" value={emailDraft.subject} onChange={e => setEmailDraft(d => ({ ...d, subject: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475569', fontWeight: 600, marginBottom: '4px' }}>תוכן</label>
                <textarea value={emailDraft.body} onChange={e => setEmailDraft(d => ({ ...d, body: e.target.value }))} rows={9}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: '4px' }}>
                <button onClick={() => !emailSending && setEmailModalDoc(null)} disabled={emailSending}
                  style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: emailSending ? 'wait' : 'pointer', fontSize: '13px' }}>ביטול</button>
                <button
                  disabled={emailSending || !emailDraft.to || !emailDraft.subject}
                  onClick={async () => {
                    setEmailSending(true);
                    setEmailSendError(null);
                    try {
                      const html = emailDraft.body
                        .split('\n')
                        .map(line => line.includes('http') ? line.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1">$1</a>') : line)
                        .join('<br>');
                      // Owner-spec 2026-06-10: pass replyTo + senderUserEmail
                      // so /api/send-email's verification gate finds the
                      // sender (otherwise it 403's with email_not_verified).
                      const currentUserEmail = currentUser?.email || currentUser?.username || '';
                      const res = await fetch(`${API_BASE}/api/send-email`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          to: emailDraft.to,
                          subject: emailDraft.subject,
                          html,
                          fromName: (currentUser?.first_name || currentUser?.name || 'Afikim'),
                          replyTo: currentUserEmail,
                          senderUserEmail: currentUserEmail,
                        }),
                      });
                      if (!res.ok) {
                        const detail = await res.json().catch(() => ({}));
                        if (detail.error === 'email_not_verified') {
                          setEmailSendError(detail.message || 'כתובת המייל שלך אינה מאומתת. בקש ממנהל לאמת את הכתובת בהגדרות > ניהול משתמשים.');
                        } else {
                          setEmailSendError(detail.error || 'שגיאה בשליחה');
                        }
                        return;
                      }
                      const data = await res.json().catch(() => ({ success: false, error: 'Bad response' }));
                      if (data.success) {
                        setEmailModalDoc(null);
                        alert('המייל נשלח בהצלחה');
                      } else {
                        setEmailSendError(data.error || 'שגיאה בשליחה');
                      }
                    } catch (e) {
                      setEmailSendError('שגיאת תקשורת: ' + e.message);
                    } finally {
                      setEmailSending(false);
                    }
                  }}
                  style={{ padding: '8px 22px', background: '#0176d3', border: 'none', borderRadius: '6px', color: '#fff', cursor: emailSending ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  {emailSending ? 'שולחת...' : 'שלח'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Per-document WhatsApp send modal — sends via /api/whatsapp/send
          (Afikim's Baileys integration → company number → customer).
          Owner explicitly asked NOT to use wa.me / WhatsApp Web here.
          The message will then appear in the customer's RecordWhatsAppChat
          panel like any other outbound message. */}
      {waDraftDoc && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9100 }} onClick={() => !waSending && setWaDraftDoc(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95vw', maxWidth: '520px', zIndex: 9101, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', background: 'white', direction: 'rtl' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#25D366', color: 'white' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>שליחה בוואטסאפ</h3>
              <button onClick={() => !waSending && setWaDraftDoc(null)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: waSending ? 'wait' : 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {waDraftError && (
                <div style={{ padding: '8px 12px', background: '#fef0f0', border: '1px solid #ea6e6c', borderRadius: '6px', color: '#c23934', fontSize: '13px' }}>{waDraftError}</div>
              )}
              <div style={{ fontSize: '12px', color: '#475569' }}>
                אל: <strong dir="ltr" style={{ direction: 'ltr', display: 'inline-block' }}>{ledgerData?.account?.phone || ''}</strong>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475569', fontWeight: 600, marginBottom: '4px' }}>תוכן ההודעה</label>
                <textarea value={waDraftDoc.text} onChange={e => setWaDraftDoc(d => ({ ...d, text: e.target.value }))} rows={5}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px' }}>
                <button onClick={() => !waSending && setWaDraftDoc(null)} disabled={waSending}
                  style={{ padding: '8px 18px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: waSending ? 'wait' : 'pointer', fontSize: '13px' }}>ביטול</button>
                <button
                  disabled={waSending || !waDraftDoc.text}
                  onClick={async () => {
                    setWaSending(true);
                    setWaDraftError(null);
                    try {
                      const rawPhone = ledgerData?.account?.phone || '';
                      let p = (rawPhone || '').replace(/[^0-9]/g, '');
                      if (p.startsWith('05') && p.length === 10) p = '972' + p.substring(1);
                      else if (p.startsWith('0') && p.length === 9) p = '972' + p.substring(1);
                      else if (!p.startsWith('972') && p.length === 9) p = '972' + p;
                      const res = await fetch(`${API_BASE}/api/whatsapp/send`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          phone: p,
                          message: waDraftDoc.text,
                          userId: currentUser?.id || '',
                          userName: (currentUser?.first_name || '') + (currentUser?.last_name ? ` ${currentUser.last_name}` : '') || currentUser?.name || currentUser?.username || '',
                        }),
                      });
                      const data = await res.json().catch(() => ({ success: false, error: 'תגובה לא חוקית מהשרת' }));
                      if (data.success) {
                        setWaDraftDoc(null);
                        setCopyToast({ idx: -1, text: 'נשלח בוואטסאפ' });
                        setTimeout(() => setCopyToast(null), 2000);
                      } else {
                        setWaDraftError(data.error || 'שגיאה בשליחה');
                      }
                    } catch (e) {
                      setWaDraftError('שגיאת תקשורת: ' + e.message);
                    } finally {
                      setWaSending(false);
                    }
                  }}
                  style={{ padding: '8px 22px', background: '#25D366', border: 'none', borderRadius: '6px', color: '#fff', cursor: waSending ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  {waSending ? 'שולחת...' : 'שלח'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Technician Selector Modal - תיאום פגישה */}
      {showTechnicianSelector && (
        <>
          {/* Premium Backdrop with blur effect */}
          <div
            className="slds-backdrop slds-backdrop_open"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9000,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
          />
          {/* Modal Container */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '95vw',
              maxWidth: '1400px',
              height: '90vh',
              maxHeight: '900px',
              zIndex: 9001,
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 10px 30px rgba(0, 0, 0, 0.3)',
              animation: 'techSelectorModalSlideIn 0.3s ease-out'
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <TechnicianSelector
                serviceAppointmentId={currentRecord?.Id}
                onClose={() => setShowTechnicianSelector(false)}
                onSuccess={async () => {
                  setShowTechnicianSelector(false);
                  // Targeted refresh — same pattern ProductsModal uses.
                  // Previous version read `relData.related` but the endpoint
                  // returns `related_objects`, so setRelatedRecords never fired
                  // and AssignedResource only appeared after a manual reload.
                  try {
                    await new Promise(r => setTimeout(r, 300));
                    const recRes = await fetch(`${API_BASE}/api/objects/ServiceAppointment/records/${encodeURIComponent(currentRecord.Id)}?_t=${Date.now()}`);
                    if (recRes.ok) {
                      const recData = await recRes.json();
                      if (recData.record) setCurrentRecord(recData.record);
                    }
                    await loadAllRelatedRecords(true);
                  } catch (e) {
                    console.error('[TechSelector] Refresh error:', e);
                    window.location.reload();
                  }
                }}
                currentUser={currentUser}
              />
            </Suspense>
          </div>
          <style>{`
            @keyframes techSelectorModalSlideIn {
              from {
                opacity: 0;
                transform: translate(-50%, -48%);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%);
              }
            }
          `}</style>
        </>
      )}

      {/* Institutional bulk-scheduler (2026-05-20) — opens via the
          תיאום פגישה מוסדי button on a ServiceAppointment. The
          component handles its own overlay/modal, so we just mount
          it conditionally. */}
      {institutionalSchedulerOppId && (
        <Suspense fallback={<LoadingFallback />}>
          <InstitutionalScheduler
            opportunityId={institutionalSchedulerOppId}
            onClose={() => { setInstitutionalSchedulerOppId(null); setSchedulerIsDelivery(false); }}
            currentUser={currentUser}
            {...(schedulerIsDelivery ? { title: DELIVERY_SCHEDULE_LABEL, icon: '🚚' } : {})}
          />
        </Suspense>
      )}

      {/* Multi-address splitter — success modal. Replaces the bare
          alert; lists every created WorkOrder + ServiceAppointment
          with click-through to /object/.../{id}. */}
      {docSendOppId && (
        <Suspense fallback={null}>
          <DocumentSendDialog opportunityId={docSendOppId} onClose={() => setDocSendOppId(null)} />
        </Suspense>
      )}

      {bdResult && (
        <Suspense fallback={null}>
          <BusinessDeliveryResult opportunityId={currentRecord?.Id} result={bdResult}
                                  onClose={() => setBdResult(null)}
                                  onSchedule={dealerBoot?.canScheduleDeliveries && currentRecord?.Id ? () => {
                                    const oppId = currentRecord.Id;
                                    setBdResult(null);
                                    setSchedulerIsDelivery(true);
                                    setInstitutionalSchedulerOppId(oppId);
                                  } : undefined} />
        </Suspense>
      )}

      {multiAddressResult && (
        <div
          dir="rtl"
          onClick={(e) => { if (e.target === e.currentTarget) setMultiAddressResult(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px',
              width: '100%', maxWidth: '640px', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden',
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Arial',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff', padding: '16px 22px',
            }}>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>✓ ההזדמנות נסגרה בהצלחה</div>
              <div style={{ fontSize: '13px', opacity: 0.92, marginTop: '4px' }}>
                {(multiAddressResult.work_orders || []).length} הזמנות עבודה ו־
                {(multiAddressResult.service_appointments || []).length} פעילויות שירות נוצרו
                {multiAddressResult.contacts?.length ? ` (+ ${multiAddressResult.contacts.length} אנשי קשר חדשים)` : ''}
              </div>
            </div>
            <div style={{ padding: '16px 22px', overflowY: 'auto', flex: 1 }}>
              {(multiAddressResult.work_orders || []).length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                    🛠️ הזמנות עבודה ({multiAddressResult.work_orders.length})
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    {multiAddressResult.work_orders.map(wo => (
                      <a
                        key={wo.id}
                        href={`/object/WorkOrder/${wo.id}`}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px', textDecoration: 'none',
                          borderBottom: '1px solid #e2e8f0', color: '#0176d3',
                        }}
                      >
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {wo.number || wo.id}
                        </span>
                        <span style={{ color: '#475569', fontSize: '12px', flex: 1, marginInlineStart: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {wo.subject}
                        </span>
                        <span style={{ color: '#0176d3', fontSize: '16px', marginInlineStart: '8px' }}>↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {(multiAddressResult.service_appointments || []).length > 0 && (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                    📅 פעילויות שירות ({multiAddressResult.service_appointments.length})
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    {multiAddressResult.service_appointments.map(sa => (
                      <a
                        key={sa.id}
                        href={`/object/ServiceAppointment/${sa.id}`}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px', textDecoration: 'none',
                          borderBottom: '1px solid #e2e8f0', color: '#0176d3',
                        }}
                      >
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {sa.number || sa.id}
                        </span>
                        <span style={{ color: '#0176d3', fontSize: '16px' }}>↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{
              padding: '12px 22px', borderTop: '1px solid #e2e8f0',
              background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '8px',
            }}>
              <button
                type="button"
                onClick={() => setMultiAddressResult(null)}
                style={{
                  padding: '8px 22px', background: '#0176d3', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >סגור</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Action Modal - LWC/VF/Actions */}
      {activeCustomAction && (
        <>
          {/* Backdrop - must come first */}
          <div
            className="slds-backdrop slds-backdrop_open"
            onClick={() => setActiveCustomAction(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9000 }}
          />
          {/* Modal */}
          <div
            className="slds-modal slds-fade-in-open"
            style={{
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9001,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              className="slds-modal__container"
              style={{
                maxWidth: activeCustomAction.modal_width || '95vw',
                width: activeCustomAction.modal_width ? '90%' : 'auto',
                minWidth: '900px',
                maxHeight: '90vh',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <header className="slds-modal__header" style={{
                padding: '16px 24px',
                borderBottom: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f3f3f3',
                borderRadius: '8px 8px 0 0'
              }}>
                <h2 className="slds-modal__title" style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                  {activeCustomAction.icon && <span style={{ marginLeft: '8px' }}>{activeCustomAction.icon}</span>}
                  {activeCustomAction.label}
                </h2>
                <button
                  className="slds-button slds-button_icon"
                  onClick={() => setActiveCustomAction(null)}
                  style={{ fontSize: '24px', cursor: 'pointer', background: 'none', border: 'none' }}
                >
                  ×
                </button>
              </header>
              <div className="slds-modal__content" style={{
                padding: '0',
                flex: 1,
                overflow: 'auto',
                minHeight: 'auto',
                maxHeight: '80vh'
              }}>
                {activeCustomAction.type === 'lwc' && activeCustomAction.lwc_component && (
                  <div className="lwc-component-container" style={{ height: '100%' }}>
                    {/* Render dynamic component inline instead of iframe */}
                    <DynamicComponentWrapper
                      componentName={activeCustomAction.lwc_component}
                      recordId={activeCustomAction.is_record_specific !== false ? currentRecord?.Id : null}
                      objectName={object?.api_name}
                      props={{
                        ...(activeCustomAction.props || {}),
                        onClose: () => setActiveCustomAction(null)
                      }}
                      isRecordSpecific={activeCustomAction.is_record_specific !== false}
                      targetObjectName={activeCustomAction.target_object || null}
                    />
                  </div>
                )}
                {activeCustomAction.type === 'component' && (activeCustomAction.component_name || activeCustomAction.component_component) && (
                  <div className="custom-component-container" style={{ height: '100%' }}>
                    <DynamicComponentWrapper
                      componentName={activeCustomAction.component_name || activeCustomAction.component_component}
                      recordId={activeCustomAction.is_record_specific !== false ? currentRecord?.Id : null}
                      objectName={object?.api_name}
                      props={{
                        ...(activeCustomAction.props || {}),
                        onClose: () => setActiveCustomAction(null)
                      }}
                      isRecordSpecific={activeCustomAction.is_record_specific !== false}
                      targetObjectName={activeCustomAction.target_object || null}
                    />
                  </div>
                )}
                {activeCustomAction.type === 'vf' && activeCustomAction.vf_page && (
                  <div className="vf-page-container" style={{ height: '100%' }}>
                    <iframe
                      src={`${config.api.baseUrl}/api/visualforce/${activeCustomAction.vf_page}?id=${currentRecord?.Id}`}
                      style={{ width: '100%', height: '100%', border: 'none', minHeight: '300px' }}
                      title={activeCustomAction.label}
                    />
                  </div>
                )}
                {(activeCustomAction.type === 'button' || activeCustomAction.type === 'action') && (
                  <div className="action-content">
                    {activeCustomAction.description && (
                      <p style={{ marginBottom: '16px', color: '#54698d' }}>{activeCustomAction.description}</p>
                    )}
                    {activeCustomAction.action_content ? (
                      <div dangerouslySetInnerHTML={{ __html: activeCustomAction.action_content }} />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#706e6b' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
                        <p>פעולה מותאמת: {activeCustomAction.label}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Form Template Modal */}
      {activeFormTemplate && (
        <Suspense fallback={<LoadingFallback />}>
          <FormPreviewModal
            isOpen={!!activeFormTemplate}
            onClose={() => setActiveFormTemplate(null)}
            templateId={activeFormTemplate.templateId}
            objectName={activeFormTemplate.objectName}
            recordId={activeFormTemplate.recordId}
            recordName={activeFormTemplate.recordName}
          />
        </Suspense>
      )}

      {/* Deal Summary Modal - iForms */}
      {showDealSummaryModal && (
        <>
          <div
            className="slds-backdrop slds-backdrop_open"
            onClick={() => !dealSummaryLoading && setShowDealSummaryModal(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9000 }}
          />
          <div
            style={{
              display: 'flex',
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 9001,
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent'
            }}
          >
            <div
              style={{
                width: '700px',
                maxWidth: '95vw',
                maxHeight: '85vh',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <header style={{
                padding: '16px 24px',
                borderBottom: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: dealSummaryForm.status === 'signed'
                  ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)'
                  : dealSummaryForm.status === 'sent'
                    ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                    : 'linear-gradient(135deg, #0176d3 0%, #014486 100%)',
                color: 'white',
                borderRadius: '8px 8px 0 0',
                flexShrink: 0
              }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                  הסכם התקשרות
                </h2>
                <button
                  onClick={() => setShowDealSummaryModal(false)}
                  disabled={dealSummaryLoading}
                  style={{ fontSize: '24px', cursor: 'pointer', background: 'none', border: 'none', color: 'white' }}
                >
                  x
                </button>
              </header>

              <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                {dealSummaryLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>...</div>
                    <p style={{ color: '#706e6b' }}>יוצר הסכם התקשרות ב-iForms...</p>
                  </div>
                ) : dealSummaryForm.status === 'collecting' ? (
                  // Owner-spec 2026-05-25: Step 1 — collect track period
                  // + number of payments. These two go to iForms as
                  // c_period / payments; the backend computes m_price
                  // (per-installment amount) from opp.Amount / payments.
                  (() => {
                    const oppAmount = parseFloat(currentRecord?.Amount) || 0;
                    const paymentsNum = parseInt(engagementInputs.payments, 10);
                    const previewPrice =
                      paymentsNum > 0 && oppAmount > 0
                        ? Math.round((oppAmount / paymentsNum) * 100) / 100
                        : null;
                    return (
                      <div>
                        <div style={{ marginBottom: '20px', fontSize: '14px', color: '#181818' }}>
                          לפני שליחת ההסכם, מלא את פרטי המסלול:
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: '#16325c' }}>
                              תקופת המסלול <span style={{ color: '#ba0517' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={engagementInputs.period}
                              onChange={(e) => setEngagementInputs(p => ({ ...p, period: e.target.value }))}
                              placeholder="לדוגמה: 36 חודשים"
                              autoFocus
                              style={{
                                width: '100%', padding: '10px 12px',
                                border: '1px solid #d8dde6', borderRadius: '6px',
                                fontSize: '14px', boxSizing: 'border-box',
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: '#16325c' }}>
                              מספר תשלומים <span style={{ color: '#ba0517' }}>*</span>
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={engagementInputs.payments}
                              onChange={(e) => setEngagementInputs(p => ({ ...p, payments: e.target.value }))}
                              placeholder="לדוגמה: 12"
                              style={{
                                width: '100%', padding: '10px 12px',
                                border: '1px solid #d8dde6', borderRadius: '6px',
                                fontSize: '14px', boxSizing: 'border-box',
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && engagementInputs.period && parseInt(engagementInputs.payments, 10) > 0) {
                                  submitEngagementInputs();
                                }
                              }}
                            />
                          </div>
                        </div>
                        {/* Preview of computed m_price so the rep sees
                            what value will populate the iForm before
                            sending. */}
                        <div style={{
                          background: '#f3f6fa',
                          border: '1px solid #d8dde6',
                          borderRadius: '6px',
                          padding: '12px 14px',
                          fontSize: '13px',
                          color: '#16325c',
                          marginBottom: '20px',
                        }}>
                          <div>סכום ההזדמנות: <strong>{oppAmount.toLocaleString('he-IL')} ₪</strong></div>
                          <div style={{ marginTop: '4px' }}>
                            תשלום חודשי (יישלח כ-m_price): {' '}
                            <strong>{previewPrice != null ? `${previewPrice.toLocaleString('he-IL')} ₪` : '—'}</strong>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px' }}>
                          <button
                            onClick={() => setShowDealSummaryModal(false)}
                            style={{
                              padding: '10px 18px', background: '#fff',
                              border: '1px solid #d8dde6', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '14px',
                            }}
                          >
                            ביטול
                          </button>
                          <button
                            onClick={submitEngagementInputs}
                            disabled={!engagementInputs.period || !(parseInt(engagementInputs.payments, 10) > 0)}
                            style={{
                              padding: '10px 22px',
                              background: '#0176d3', color: '#fff',
                              border: 'none', borderRadius: '6px',
                              cursor: (!engagementInputs.period || !(parseInt(engagementInputs.payments, 10) > 0)) ? 'not-allowed' : 'pointer',
                              opacity: (!engagementInputs.period || !(parseInt(engagementInputs.payments, 10) > 0)) ? 0.5 : 1,
                              fontSize: '14px', fontWeight: 600,
                            }}
                          >
                            המשך לשליחת ההסכם
                          </button>
                        </div>
                      </div>
                    );
                  })()
                ) : dealSummaryForm.iformsUrl ? (
                  <div>
                    {/* iForms Link */}
                    <div style={{
                      background: '#f4f6f9',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>
                        קישור לחתימה דיגיטלית:
                      </div>
                      <div style={{
                        background: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #e5e5e5',
                        wordBreak: 'break-all',
                        fontSize: '12px',
                        marginBottom: '12px',
                        direction: 'ltr'
                      }}>
                        {dealSummaryForm.iformsUrl}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(dealSummaryForm.iformsUrl);
                            alert('הקישור הועתק!');
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#0176d3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          העתק קישור
                        </button>
                        <button
                          onClick={() => window.open(dealSummaryForm.iformsUrl, '_blank')}
                          style={{
                            padding: '8px 16px',
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          פתח קישור
                        </button>
                        <button
                          onClick={createNewDealSummaryLink}
                          disabled={dealSummaryLoading}
                          style={{
                            padding: '8px 16px',
                            background: dealSummaryLoading ? '#ccc' : '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: dealSummaryLoading ? 'not-allowed' : 'pointer'
                          }}
                          title="יצירת קישור iForms חדש שיחליף את הקישור הקיים"
                        >
                          {dealSummaryLoading ? 'יוצר...' : 'צור קישור חדש'}
                        </button>
                      </div>
                    </div>

                    {/* Send to Customer - ABOVE preview */}
                    <div style={{
                      background: '#f4f6f9',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '14px' }}>
                        שליחת לינק ללקוח
                      </div>

                      {/* Step 0: Choose channel */}
                      {(!dealSummarySendMode || dealSummarySendMode === 'choosing') && (
                        <div>
                          <div style={{ marginBottom: '10px', color: '#706e6b', fontSize: '13px' }}>
                            בחר ערוץ שליחה:
                          </div>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => selectDealSummaryChannel('whatsapp')}
                              style={{ padding: '10px 20px', background: '#25D366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                            >
                              WhatsApp
                            </button>
                            <button
                              onClick={() => selectDealSummaryChannel('sms')}
                              style={{ padding: '10px 20px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                            >
                              SMS
                            </button>
                            <button
                              onClick={() => selectDealSummaryChannel('email')}
                              style={{ padding: '10px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                            >
                              Email
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 1: Confirm contact info — radio list of all
                          available Account phone/email slots. Owner spec
                          2026-05-15: show ALL contact options at once
                          (instead of just the precedence-picked one) so
                          the rep doesn't get stuck on "no phone found"
                          when the number lives in a different slot. */}
                      {dealSummarySendMode === 'confirm' && (() => {
                        const options = getDealSummaryContactOptions(dealSummarySendChannel);
                        const anyValue = options.some(o => o.value);
                        return (
                          <div>
                            <div style={{ marginBottom: '10px', color: '#3e3e3c', fontSize: '13px', fontWeight: '600' }}>
                              {dealSummarySendChannel === 'email' ? 'בחר כתובת מייל לשליחה:' : 'בחר מספר טלפון לשליחה:'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px', alignItems: 'stretch' }}>
                              {options.map((opt) => {
                                const isSelected = dealSummaryContactField === opt.field;
                                const isEmpty = !opt.value;
                                return (
                                  <label
                                    key={opt.field}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '8px 12px', borderRadius: '6px',
                                      border: `1px solid ${isSelected ? '#0176d3' : '#d8dde6'}`,
                                      background: isSelected ? '#eef4fb' : '#fff',
                                      cursor: isEmpty ? 'default' : 'pointer',
                                      opacity: isEmpty ? 0.7 : 1,
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name="ds-contact-option"
                                      checked={isSelected}
                                      disabled={isEmpty}
                                      onChange={() => {
                                        setDealSummaryContactField(opt.field);
                                        setDealSummaryContactValue(opt.value);
                                      }}
                                    />
                                    <div style={{ textAlign: 'right', flex: 1 }}>
                                      <div style={{ fontSize: '12px', color: '#54698d' }}>{opt.label}</div>
                                      <div style={{ fontSize: '14px', direction: 'ltr', textAlign: 'right', color: isEmpty ? '#a0a0a0' : '#16325c', fontWeight: 500 }}>
                                        {opt.value || '— ריק —'}
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                            {!anyValue && (
                              <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '6px', color: '#e65100', fontSize: '12px' }}>
                                {dealSummarySendChannel === 'email' ? 'לא נמצא מייל ללקוח — לחץ "הוסף / ערוך" להוספה' : 'לא נמצא מספר טלפון ללקוח — לחץ "הוסף / ערוך" להוספה'}
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => setDealSummarySendMode('choosing')}
                                style={{ padding: '8px 14px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                              >
                                חזרה
                              </button>
                              <button
                                onClick={() => {
                                  // Open the editing screen pre-filled with the currently
                                  // selected slot's value, and default the save-target
                                  // radio to that same slot.
                                  setDealSummaryEditValue(dealSummaryContactValue || '');
                                  setDealSummaryEditField(dealSummaryContactField || options[0]?.field || null);
                                  setDealSummarySendMode('editing');
                                }}
                                style={{ padding: '8px 14px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#0176d3', fontWeight: 500 }}
                              >
                                ✏️ הוסף / ערוך
                              </button>
                              {dealSummaryContactValue && (
                                <button
                                  onClick={sendDealSummaryLink}
                                  style={{
                                    padding: '8px 20px',
                                    background: dealSummarySendChannel === 'whatsapp' ? '#25D366' : dealSummarySendChannel === 'sms' ? '#0ea5e9' : '#7c3aed',
                                    color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
                                  }}
                                >
                                  שלח {dealSummarySendChannel === 'whatsapp' ? 'בווטסאפ' : dealSummarySendChannel === 'sms' ? 'ב-SMS' : 'במייל'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Step 1b: Edit / add contact info. Owner spec
                          2026-05-15: rep chooses WHICH Account slot to
                          save the new value into (PersonMobilePhone /
                          Phone / Another_Phone__c, or PersonEmail).
                          Pre-selected to whatever was selected in the
                          confirm step. */}
                      {dealSummarySendMode === 'editing' && (() => {
                        const options = getDealSummaryContactOptions(dealSummarySendChannel);
                        return (
                          <div>
                            <div style={{ marginBottom: '10px', color: '#3e3e3c', fontSize: '13px', fontWeight: '600' }}>
                              {dealSummarySendChannel === 'email' ? 'הזן כתובת מייל:' : 'הזן מספר טלפון:'}
                            </div>
                            <input
                              type={dealSummarySendChannel === 'email' ? 'email' : 'tel'}
                              value={dealSummaryEditValue}
                              onChange={(e) => setDealSummaryEditValue(e.target.value)}
                              placeholder={dealSummarySendChannel === 'email' ? 'email@example.com' : '050-1234567'}
                              style={{
                                width: '100%', maxWidth: '300px', padding: '10px 12px', border: '1px solid #d8dde6', borderRadius: '6px',
                                fontSize: '15px', direction: 'ltr', textAlign: 'center', marginBottom: '12px', outline: 'none'
                              }}
                              autoFocus
                            />

                            {/* Save-target radios — only for phone (email has 1 slot today) */}
                            {dealSummarySendChannel !== 'email' && options.length > 1 && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '6px', color: '#54698d', fontSize: '12px' }}>
                                  שמור כ:
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'stretch' }}>
                                  {options.map((opt) => {
                                    const isSelected = dealSummaryEditField === opt.field;
                                    return (
                                      <label
                                        key={opt.field}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: '8px',
                                          padding: '6px 10px', borderRadius: '4px',
                                          border: `1px solid ${isSelected ? '#0176d3' : '#e8eaed'}`,
                                          background: isSelected ? '#eef4fb' : '#fff',
                                          cursor: 'pointer', fontSize: '12px',
                                        }}
                                      >
                                        <input
                                          type="radio"
                                          name="ds-edit-target"
                                          checked={isSelected}
                                          onChange={() => setDealSummaryEditField(opt.field)}
                                        />
                                        <span style={{ flex: 1, textAlign: 'right' }}>
                                          {opt.label}
                                          {opt.value && (
                                            <span style={{ marginInlineStart: '6px', fontSize: '11px', color: '#a0a0a0', direction: 'ltr' }}>
                                              (קיים: {opt.value})
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                onClick={() => setDealSummarySendMode('confirm')}
                                style={{ padding: '8px 16px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                              >
                                ביטול
                              </button>
                              <button
                                onClick={() => {
                                  const v = dealSummaryEditValue.trim();
                                  if (!v) return;
                                  const targetField = dealSummaryEditField
                                    || (dealSummarySendChannel === 'email' ? 'PersonEmail' : 'PersonMobilePhone');
                                  setDealSummaryContactValue(v);
                                  setDealSummaryContactField(targetField);
                                  saveDealSummaryContactToAccount(v, targetField);
                                  setDealSummarySendMode('confirm');
                                }}
                                disabled={!dealSummaryEditValue.trim()}
                                style={{
                                  padding: '8px 20px', background: dealSummaryEditValue.trim() ? '#0176d3' : '#e0e0e0',
                                  color: 'white', border: 'none', borderRadius: '6px', cursor: dealSummaryEditValue.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 'bold'
                                }}
                              >
                                שמור
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Sending... */}
                      {dealSummarySendMode === 'sending' && (
                        <div style={{ padding: '12px', color: '#706e6b' }}>
                          שולח {dealSummarySendChannel === 'whatsapp' ? 'בווטסאפ' : dealSummarySendChannel === 'sms' ? 'ב-SMS' : 'במייל'}...
                        </div>
                      )}

                      {/* Success */}
                      {dealSummarySendMode === 'sent' && dealSummarySendResult?.success && (
                        <div>
                          <div style={{ padding: '12px', background: '#e8f5e9', borderRadius: '6px', color: '#2e7d32', fontWeight: 'bold', marginBottom: '8px' }}>
                            {dealSummarySendResult.message}
                          </div>
                          <button
                            onClick={() => { setDealSummarySendMode('choosing'); setDealSummarySendResult(null); }}
                            style={{ padding: '6px 16px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                          >
                            שלח שוב
                          </button>
                        </div>
                      )}

                      {/* Error */}
                      {dealSummarySendMode === 'error' && (
                        <div>
                          <div style={{ padding: '12px', background: '#fce4ec', borderRadius: '6px', color: '#c23934', marginBottom: '8px' }}>
                            {dealSummarySendResult?.message || 'שגיאה בשליחה'}
                          </div>
                          <button
                            onClick={() => { setDealSummarySendMode('confirm'); setDealSummarySendResult(null); }}
                            style={{ padding: '6px 16px', background: '#f4f6f9', border: '1px solid #d8dde6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                          >
                            נסה שוב
                          </button>
                        </div>
                      )}
                    </div>

                    {/* OTP Signing Section — owner-spec 2026-05-25:
                        temporarily hidden. All OTP state, handlers,
                        and the rendering tree are intentionally
                        preserved so this can be flipped back on by
                        changing the `false &&` guard below to `true`. */}
                    {false && (
                    <div style={{
                      background: otpStep === 'verified' ? '#e8f5e9' : '#fff8e1',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      textAlign: 'center',
                      border: otpStep === 'verified' ? '1px solid #a5d6a7' : '1px solid #ffe082'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '14px', color: otpStep === 'verified' ? '#2e7d32' : '#f57f17' }}>
                        {otpStep === 'verified' ? 'המסמך נחתם בהצלחה!' : 'חתימה באמצעות קוד OTP'}
                      </div>

                      {/* Step: Choose method */}
                      {otpStep === 'choose' && (
                        <div>
                          <div style={{ marginBottom: '8px' }}>
                            <label style={{ fontSize: '13px', color: '#3e3e3c', fontWeight: '600' }}>מספר טלפון:</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginTop: '4px' }}>
                              <input
                                type="tel"
                                value={otpPhone || getDealSummaryCustomerInfo().phone}
                                onChange={(e) => setOtpPhone(e.target.value)}
                                placeholder="050-1234567"
                                style={{
                                  padding: '8px 12px', border: '1px solid #d8dde6', borderRadius: '6px',
                                  fontSize: '15px', direction: 'ltr', textAlign: 'center', width: '200px', outline: 'none'
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: '8px', color: '#706e6b', fontSize: '12px' }}>
                            בחר אופן שליחת קוד אימות:
                          </div>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => sendOtp('whatsapp')}
                              disabled={!(otpPhone || getDealSummaryCustomerInfo().phone)}
                              style={{
                                padding: '10px 20px', background: '#25D366', color: 'white', border: 'none',
                                borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
                                opacity: (otpPhone || getDealSummaryCustomerInfo().phone) ? 1 : 0.5
                              }}
                            >
                              WhatsApp
                            </button>
                            <button
                              onClick={() => sendOtp('voice')}
                              disabled={!(otpPhone || getDealSummaryCustomerInfo().phone)}
                              style={{
                                padding: '10px 20px', background: '#1565c0', color: 'white', border: 'none',
                                borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
                                opacity: (otpPhone || getDealSummaryCustomerInfo().phone) ? 1 : 0.5
                              }}
                            >
                              שיחה קולית
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step: Sending... */}
                      {otpStep === 'sending' && (
                        <div style={{ padding: '12px', color: '#706e6b' }}>
                          {otpMethod === 'whatsapp' ? 'שולח קוד בווטסאפ...' : 'מחייג ללקוח...'}
                        </div>
                      )}

                      {/* Step: Code sent — enter code */}
                      {otpStep === 'sent' && (
                        <div>
                          <div style={{
                            padding: '8px 12px', background: '#e3f2fd', borderRadius: '6px',
                            color: '#1565c0', fontSize: '13px', marginBottom: '12px'
                          }}>
                            קוד נשלח {otpMethod === 'whatsapp' ? 'בWhatsApp' : 'בשיחה קולית'} ל-{otpPhone || getDealSummaryCustomerInfo().phone}
                            {otpTimer > 0 && <span style={{ marginRight: '8px', fontWeight: 'bold' }}>({formatOtpTimer(otpTimer)})</span>}
                          </div>

                          {otpError && (
                            <div style={{ padding: '8px', background: '#fce4ec', borderRadius: '4px', color: '#c23934', fontSize: '13px', marginBottom: '8px' }}>
                              {otpError}
                            </div>
                          )}

                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '13px', color: '#3e3e3c', fontWeight: '600' }}>הזן קוד (6 ספרות):</label>
                            <div style={{ marginTop: '4px' }}>
                              <input
                                type="text"
                                maxLength={6}
                                value={otpCode}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, '');
                                  setOtpCode(val);
                                  setOtpError('');
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && otpCode.length === 6) verifyOtp(); }}
                                placeholder="______"
                                autoFocus
                                style={{
                                  padding: '12px', border: '2px solid #1565c0', borderRadius: '8px',
                                  fontSize: '24px', direction: 'ltr', textAlign: 'center', width: '160px',
                                  letterSpacing: '8px', fontWeight: 'bold', outline: 'none'
                                }}
                              />
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={verifyOtp}
                              disabled={otpCode.length !== 6}
                              style={{
                                padding: '10px 24px', background: otpCode.length === 6 ? '#4caf50' : '#e0e0e0',
                                color: 'white', border: 'none', borderRadius: '6px',
                                cursor: otpCode.length === 6 ? 'pointer' : 'not-allowed',
                                fontSize: '14px', fontWeight: 'bold'
                              }}
                            >
                              חתום
                            </button>
                            <button
                              onClick={() => sendOtp(otpMethod)}
                              disabled={otpTimer > 540}
                              style={{
                                padding: '10px 16px', background: '#f4f6f9', border: '1px solid #d8dde6',
                                borderRadius: '6px', cursor: otpTimer > 540 ? 'not-allowed' : 'pointer',
                                fontSize: '13px', opacity: otpTimer > 540 ? 0.5 : 1
                              }}
                            >
                              שלח שוב
                            </button>
                            <button
                              onClick={resetOtpState}
                              style={{
                                padding: '10px 16px', background: '#f4f6f9', border: '1px solid #d8dde6',
                                borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
                              }}
                            >
                              חזרה
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step: Verifying... */}
                      {otpStep === 'verifying' && (
                        <div style={{ padding: '12px', color: '#706e6b' }}>
                          מאמת קוד...
                        </div>
                      )}

                      {/* Step: Verified / Signed */}
                      {otpStep === 'verified' && otpSignature && (
                        <div>
                          <div style={{
                            padding: '16px', background: '#fff', borderRadius: '8px',
                            border: '1px solid #a5d6a7', textAlign: 'right', marginBottom: '12px',
                            fontSize: '13px', lineHeight: '1.8'
                          }}>
                            <div><strong>שיטת אימות:</strong> {otpSignature.method === 'whatsapp' ? 'WhatsApp' : 'שיחת טלפון'}</div>
                            <div><strong>מספר טלפון:</strong> <span dir="ltr">{otpSignature.phone}</span></div>
                            <div><strong>קוד אימות:</strong> <span dir="ltr">{otpSignature.code}</span></div>
                            <div><strong>תאריך:</strong> {new Date(otpSignature.timestamp).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}</div>
                            {otpSignature.agent && <div><strong>נציג:</strong> {otpSignature.agent}</div>}
                          </div>
                        </div>
                      )}

                      {/* Step: Error */}
                      {otpStep === 'error' && (
                        <div>
                          <div style={{ padding: '10px', background: '#fce4ec', borderRadius: '6px', color: '#c23934', fontSize: '13px', marginBottom: '8px' }}>
                            {otpError || 'שגיאה'}
                          </div>
                          <button
                            onClick={resetOtpState}
                            style={{
                              padding: '8px 16px', background: '#f4f6f9', border: '1px solid #d8dde6',
                              borderRadius: '4px', cursor: 'pointer', fontSize: '13px'
                            }}
                          >
                            נסה שוב
                          </button>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Document preview iframe removed per owner request — many
                        iForms templates load slowly inside an iframe and the
                        operator already has the "פתח קישור" button to view the
                        document in a clean new tab. */}

                    {/* Signed document link */}
                    {dealSummaryForm.signedDocLink && (
                      <div style={{
                        background: '#e8f5e9',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2e7d32' }}>
                          מסמך חתום:
                        </div>
                        <button
                          onClick={() => window.open(`${API_BASE}${dealSummaryForm.signedDocLink}`, '_blank')}
                          style={{
                            padding: '8px 16px',
                            background: '#2e7d32',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          הורד מסמך חתום (PDF)
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>X</div>
                    <p style={{ color: '#c23934' }}>שגיאה ביצירת סיכום העסקה</p>
                  </div>
                )}
              </div>

              <footer style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                <button
                  onClick={() => setShowDealSummaryModal(false)}
                  style={{
                    padding: '8px 24px',
                    background: '#f4f6f9',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  סגור
                </button>
              </footer>
            </div>
          </div>
        </>
      )}

      {/* Work Order Automation Modal - יצירת הזמנת עבודה */}
      {showWorkTypeModal && (
        <>
          <div
            className="slds-backdrop slds-backdrop_open"
            onClick={() => !workOrderCreating && handleCancelWorkOrderFlow()}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9000 }}
          />
          <div
            style={{
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9001,
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent'
            }}
          >
            <div
              style={{
                width: '600px',
                maxWidth: '90vw',
                maxHeight: '80vh',
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Header */}
              <header style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #0176d3 0%, #0b5cab 100%)',
                color: 'white'
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                    יצירת הזמנת עבודה
                  </h2>
                  <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                    {workTypeModalStep === 'workType' && 'בחר סוג עבודה'}
                    {workTypeModalStep === 'technicianRequired' && 'שירות - דורש שיבוץ?'}
                    {workTypeModalStep === 'assetSelect' && 'בחר נכס'}
                    {workTypeModalStep === 'caseCreate' && 'פתיחת מקרה שירות'}
                    {workTypeModalStep === 'productItem' && 'בחר פריט מוצר מהמחסן'}
                    {workTypeModalStep === 'serialized' && 'בחר מספר סיריאלי'}
                    {workTypeModalStep === 'creating' && 'יוצר הזמנת עבודה...'}
                    {workTypeModalStep === 'success' && 'הזמנת עבודה נוצרה בהצלחה!'}
                    {workTypeModalStep === 'noTechWarrantyConfirm' && 'אישור יצירת נכס וחוזה שירות'}
                    {workTypeModalStep === 'noTechWarrantyNoPayment' && 'אין תשלום מאומת'}
                  </div>
                </div>
                <button
                  onClick={handleCancelWorkOrderFlow}
                  disabled={workOrderCreating}
                  style={{
                    fontSize: '28px',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    opacity: workOrderCreating ? 0.5 : 1
                  }}
                >
                  ×
                </button>
              </header>

              {/* Content */}
              <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                {/* Error Message - hide on asset/case steps (they handle their own) */}
                {workOrderError && workTypeModalStep !== 'assetSelect' && workTypeModalStep !== 'caseCreate' && (
                  <div style={{
                    background: '#ffebee',
                    border: '1px solid #ef5350',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '18px', color: '#c62828', fontWeight: 'bold' }}>!</span>
                    <div style={{ color: '#c62828' }}>{typeof workOrderError === 'string' ? workOrderError : (workOrderError?.message || JSON.stringify(workOrderError))}</div>
                  </div>
                )}

                {/* Loading State */}
                {workTypeModalStep === 'loading' && (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      border: '4px solid #e5e5e5',
                      borderTop: '4px solid #0176d3',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 20px'
                    }} />
                    <p style={{ color: '#706e6b', fontSize: '16px' }}>טוען סוגי עבודה...</p>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  </div>
                )}

                {/* 2026-10-09 (dealer P2-B): "מסירה ללקוח משווק" — Q1 invoice, Q2 delivery note,
                    then the address questions (BusinessDeliveryCloseStep). */}
                {workTypeModalStep === 'bdInvoice' && (
                  <div style={{ direction: 'rtl' }}>
                    <p style={{ fontSize: '16px', fontWeight: 600, margin: '8px 0 14px' }}>להפיק חשבונית מס למשווק?</p>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <button className="slds-button slds-button_brand"
                              onClick={() => { setBdIssueInvoice(true); setWorkTypeModalStep('bdDeliveryNote'); }}>✓ כן, להפיק</button>
                      <button className="slds-button slds-button_neutral" onClick={() => setBdIssueInvoice(false)}>✗ לא</button>
                    </div>
                    {bdIssueInvoice === false && (
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>סיבה לאי הפקת החשבונית *</label>
                        <textarea value={bdInvoiceSkipReason} onChange={e => setBdInvoiceSkipReason(e.target.value)}
                                  aria-label="סיבה לאי הפקת החשבונית" rows={2}
                                  style={{ width: '100%', padding: '8px', border: '1px solid #c9c9c9', borderRadius: '5px', boxSizing: 'border-box' }} />
                        <button className="slds-button slds-button_brand" style={{ marginTop: '10px' }}
                                disabled={!bdInvoiceSkipReason.trim()}
                                onClick={() => setWorkTypeModalStep('bdDeliveryNote')}>הבא →</button>
                      </div>
                    )}
                  </div>
                )}

                {workTypeModalStep === 'bdDeliveryNote' && (
                  <div style={{ direction: 'rtl' }}>
                    <p style={{ fontSize: '16px', fontWeight: 600, margin: '8px 0 14px' }}>להפיק תעודת משלוח?</p>
                    <p style={{ fontSize: '12px', color: '#706e6b', marginTop: 0 }}>תעודת משלוח אחת לכל כתובת מסירה, על שם המשווק, ברווחית.</p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button className="slds-button slds-button_brand"
                              onClick={() => { setBdIssueNotes(true); setWorkTypeModalStep('bdAddress'); }}>✓ כן, להפיק</button>
                      <button className="slds-button slds-button_neutral"
                              onClick={() => { setBdIssueNotes(false); setWorkTypeModalStep('bdAddress'); }}>✗ לא</button>
                    </div>
                  </div>
                )}

                {workTypeModalStep === 'bdAddress' && (
                  <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#706e6b' }}>טוען...</div>}>
                    <BusinessDeliveryCloseStep
                      account={closeFlowAccount}
                      lines={deliverableLines(relatedRecords['OpportunityLineItem'] || [],
                        workOrderDataCache.current.productNames || {}, workOrderDataCache.current.pbeToProduct || {})}
                      onSubmit={handleBusinessDeliverySubmit}
                      onBack={() => setWorkTypeModalStep('bdDeliveryNote')}
                      submitting={bdSubmitting}
                    />
                  </Suspense>
                )}

                {/* Multi-address splitter — runs after work type pick
                    when serial device count >= 2 (institutional flow).
                    Handles Q1/Q1a/Q2 + drag-drop cubes internally and
                    POSTs the result via handleMultiAddressSubmit. */}
                {workTypeModalStep === 'multiAddressClose' && (
                  <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#706e6b' }}>טוען...</div>}>
                    <MultiAddressCloseStep
                      account={closeFlowAccount}
                      opp={currentRecord}
                      workType={selectedWorkType}
                      lineItems={relatedRecords['OpportunityLineItem'] || []}
                      pbeToProduct={workOrderDataCache.current.pbeToProduct || {}}
                      serializableProductIds={workOrderDataCache.current.serializableProductIds || new Set()}
                      productNames={workOrderDataCache.current.productNames || {}}
                      availableSerials={workOrderDataCache.current.serializedProducts || []}
                      onCancel={() => setShowWorkTypeModal(false)}
                      onSubmit={handleMultiAddressSubmit}
                      submitting={multiAddressSubmitting}
                    />
                  </Suspense>
                )}

                {/* Step 1: Work Type Selection */}
                {workTypeModalStep === 'workType' && (
                  <div>
                    <p style={{ marginBottom: '20px', color: '#706e6b' }}>
                      בחר את סוג העבודה להזמנת העבודה החדשה:
                    </p>
                    {/* 2026-05-20: highlight switched from a hard-coded
                        "התקנה (טכנאי מכירה)" default to the dynamic
                        recommendedWorkType (name contains שדרוג/שידרוג,
                        any serialized line-item product, or fallback to
                        שירות). The recommended option shows a "מומלץ"
                        badge; picking any other option triggers a
                        confirm in handleWorkTypeSelect. */}
                    <div style={{ display: 'grid', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                      {availableWorkTypes.map(wt => {
                        const isRecommended = recommendedWorkType && wt.Name === recommendedWorkType;
                        return (
                        <button
                          key={wt.Id}
                          onClick={() => handleWorkTypeSelect(wt)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '16px 20px',
                            background: isRecommended
                              ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                              : '#f8f9fa',
                            border: isRecommended
                              ? '2px solid #1976d2'
                              : '1px solid #e5e5e5',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            textAlign: 'right',
                            transition: 'all 0.2s',
                            width: '100%'
                          }}
                          onMouseOver={e => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <span style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: isRecommended
                              ? '#1976d2'
                              : '#757575',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px'
                          }}>
                            {wt.Name === 'שירות' ? 'S' : 'W'}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              fontSize: '15px',
                              color: isRecommended ? '#1976d2' : wt.Name === 'שירות' ? '#0d9488' : '#1a1a1a',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}>
                              {wt.Name}
                              {isRecommended && (
                                <span style={{
                                  fontSize: '11px', fontWeight: 700, color: '#fff',
                                  background: '#1976d2', padding: '2px 8px',
                                  borderRadius: '10px',
                                }}>מומלץ</span>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', color: '#706e6b' }}>
                              משך: {wt.EstimatedDuration || 60} {wt.DurationType === 'Hours' ? 'שעות' : 'דקות'}
                            </div>
                          </div>
                          <span style={{ fontSize: '20px', color: '#bbb' }}>←</span>
                        </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step: Technician Required? (שירות only) */}
                {workTypeModalStep === 'technicianRequired' && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>👷</div>
                    <h3 style={{ marginBottom: '8px', color: '#1a1a1a', fontSize: '18px' }}>דורש שיבוץ טכנאי?</h3>
                    <p style={{ color: '#706e6b', fontSize: '14px', marginBottom: '28px' }}>
                      האם יש צורך בשיבוץ טכנאי לביצוע השירות?
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '20px' }}>
                      <button
                        onClick={() => handleTechnicianRequired(true)}
                        style={{
                          padding: '14px 32px', background: '#1976d2', color: 'white',
                          border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600',
                          cursor: 'pointer', transition: 'all 0.2s',
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = '#1565c0'; }}
                        onMouseOut={e => { e.currentTarget.style.background = '#1976d2'; }}
                      >
                        כן, דורש שיבוץ
                      </button>
                      <button
                        onClick={() => handleTechnicianRequired(false)}
                        style={{
                          padding: '14px 32px', background: '#f5f5f5', color: '#333',
                          border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', fontWeight: '600',
                          cursor: 'pointer', transition: 'all 0.2s',
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = '#e0e0e0'; }}
                        onMouseOut={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                      >
                        לא, ללא שיבוץ
                      </button>
                    </div>
                    <button
                      onClick={() => { setSelectedWorkType(null); setWorkTypeModalStep('workType'); }}
                      style={{
                        background: 'none', border: 'none', color: '#1976d2',
                        cursor: 'pointer', fontSize: '14px', padding: '8px'
                      }}
                    >
                      ← חזור לבחירת סוג עבודה
                    </button>
                  </div>
                )}

                {/* Step: No-Tech Warranty — confirm SC creation + pick existing Asset */}
                {workTypeModalStep === 'noTechWarrantyConfirm' && noTechWarrantyPrecheck && (
                  <div style={{ padding: '20px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🛡️</div>
                      <h3 style={{ marginBottom: '8px', color: '#1a1a1a', fontSize: '18px' }}>
                        האם ליצור חוזה שירות ל-3 שנים?
                      </h3>
                    </div>
                    <div style={{
                      background: '#f9fafb', border: '1px solid #e5e7eb',
                      borderRadius: '8px', padding: '16px', marginBottom: '20px',
                      fontSize: '14px', color: '#1f2937', lineHeight: '1.8'
                    }}>
                      <div><strong>מוצר אחריות:</strong> {noTechWarrantyPrecheck.warranty_product_name}</div>
                      <div><strong>תאריך תשלום מאומת אחרון:</strong> {
                        noTechWarrantyPrecheck.last_payment_date
                          ? new Date(noTechWarrantyPrecheck.last_payment_date)
                              .toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '-'
                      }</div>
                      <div><strong>משך החוזה:</strong> 36 חודש</div>
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px',
                                      fontWeight: '600', color: '#1f2937' }}>
                        בחר נכס שאליו ישוייך חוזה השירות:
                      </label>
                      {noTechWarrantyAssetsLoading ? (
                        <div style={{ color: '#6b7280', fontSize: '14px', padding: '12px' }}>טוען נכסים...</div>
                      ) : noTechWarrantyAssets.length === 0 ? (
                        <div style={{
                          background: '#fffbeb', border: '1px solid #fcd34d',
                          color: '#92400e', padding: '12px', borderRadius: '6px', fontSize: '13px'
                        }}>
                          לא נמצאו נכסים פעילים ללקוח. לא ניתן ליצור חוזה שירות ללא נכס.
                        </div>
                      ) : (
                        <div style={{
                          maxHeight: '220px', overflowY: 'auto',
                          border: '1px solid #e5e7eb', borderRadius: '6px'
                        }}>
                          {noTechWarrantyAssets.map(a => (
                            <label key={a.Id} style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 12px', cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6',
                              background: noTechWarrantySelectedAssetId === a.Id ? '#eff6ff' : 'white',
                            }}>
                              <input
                                type="radio"
                                name="noTechWarrantyAsset"
                                value={a.Id}
                                checked={noTechWarrantySelectedAssetId === a.Id}
                                onChange={() => setNoTechWarrantySelectedAssetId(a.Id)}
                              />
                              <div style={{ flex: 1, fontSize: '13px' }}>
                                <div style={{ fontWeight: '600', color: '#111827' }}>{a.Name || '(ללא שם)'}</div>
                                <div style={{ color: '#6b7280', marginTop: '2px' }}>
                                  {a.SerialNumber ? `מס׳ סיריאלי: ${a.SerialNumber}` : ''}
                                  {a.SerialNumber && a.Status ? ' · ' : ''}
                                  {a.Status ? `סטטוס: ${a.Status}` : ''}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {workOrderError && (
                      <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca',
                        color: '#dc2626', padding: '12px', borderRadius: '6px',
                        marginBottom: '16px', fontSize: '13px'
                      }}>
                        {workOrderError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      <button
                        onClick={confirmNoTechWarrantyAndClose}
                        disabled={workOrderCreating || !noTechWarrantySelectedAssetId
                                  || noTechWarrantyAssets.length === 0}
                        style={{
                          padding: '12px 28px',
                          background: (!noTechWarrantySelectedAssetId || noTechWarrantyAssets.length === 0)
                            ? '#9ca3af' : '#16a34a',
                          color: 'white', border: 'none', borderRadius: '8px',
                          fontSize: '15px', fontWeight: '600',
                          cursor: (workOrderCreating || !noTechWarrantySelectedAssetId
                                   || noTechWarrantyAssets.length === 0) ? 'not-allowed' : 'pointer',
                          opacity: workOrderCreating ? 0.6 : 1,
                        }}
                      >כן, ליצור חוזה שירות</button>
                      <button
                        onClick={async () => {
                          // 2026-05-29: don't null out the precheck — Task 3's
                          // tax-invoice prompt (run inside proceedAfterNoTech...)
                          // still needs the has_verified_payment / has_line_items
                          // / has_serialized_product flags from it.
                          setNoTechWarrantySelectedAssetId('');
                          setWorkOrderError(null);
                          await proceedAfterNoTechDecisionOrClose();
                        }}
                        disabled={workOrderCreating}
                        style={{
                          padding: '12px 28px', background: '#f5f5f5', color: '#333',
                          border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                          cursor: workOrderCreating ? 'wait' : 'pointer',
                        }}
                      >לא, רק לסגור הזדמנות</button>
                    </div>
                  </div>
                )}

                {/* Step: No-Tech Warranty — blocked because no verified payment */}
                {workTypeModalStep === 'noTechWarrantyNoPayment' && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: '#fef2f2', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px'
                    }}>⚠️</div>
                    <h3 style={{ marginBottom: '12px', color: '#dc2626', fontSize: '18px' }}>
                      אין תשלום מאומת
                    </h3>
                    <p style={{ color: '#374151', fontSize: '14px', marginBottom: '28px', lineHeight: '1.7' }}>
                      מכיוון שאין תשלום מאומת לא נוצר נכס וחוזה שירות ללקוח.<br/>
                      נא לאמת את התשלום ולבצע שוב סגירה בהצלחה
                    </p>
                    <button
                      onClick={() => {
                        setShowWorkTypeModal(false);
                        setPendingStageChange(null);
                        setNoTechWarrantyPrecheck(null);
                        setWorkOrderError(null);
                      }}
                      style={{
                        padding: '12px 28px', background: '#1976d2', color: 'white',
                        border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >סגור</button>
                  </div>
                )}

                {/* 2026-05-29 Step: Task 3 — confirm tax-invoice issuance on
                    no-tech שירות close with verified payment + line items +
                    no serialized OLI. "Yes" issues immediately; "No" stores
                    the user's request + reason on the Opp. */}
                {workTypeModalStep === 'noTechInvoiceConfirm' && (
                  <div style={{ padding: '20px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🧾</div>
                      <h3 style={{ marginBottom: '8px', color: '#1a1a1a', fontSize: '18px' }}>
                        המערכת תנפיק חשבונית מס ללקוח
                      </h3>
                      <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
                        האם להנפיק את חשבונית המס כעת?
                      </p>
                    </div>

                    {noTechInvoiceDecisionNo && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{
                          background: '#fffbeb', border: '1px solid #fcd34d',
                          color: '#92400e', padding: '10px 12px', borderRadius: '6px',
                          marginBottom: '12px', fontSize: '13px', lineHeight: '1.6'
                        }}>
                          המערכת תסמן שלא הונפקה חשבונית מס לבקשת המשתמש. נא להזין סיבה:
                        </div>
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          fontSize: '14px', color: '#1f2937', marginBottom: '10px',
                          fontWeight: '600'
                        }}>
                          <input
                            type="checkbox"
                            checked={true}
                            readOnly
                            style={{ width: '16px', height: '16px', cursor: 'default' }}
                          />
                          לא הונפקה חשבונית מס לבקשת המשתמש
                        </label>
                        <label style={{
                          display: 'block', marginBottom: '6px', fontSize: '13px',
                          fontWeight: '600', color: '#1f2937'
                        }}>
                          סיבה <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <textarea
                          value={noTechInvoiceReason}
                          onChange={e => setNoTechInvoiceReason(e.target.value)}
                          placeholder="הזן את הסיבה לאי-הנפקת חשבונית מס"
                          rows={3}
                          disabled={noTechInvoiceSaving}
                          style={{
                            width: '100%', padding: '10px 12px',
                            border: '1px solid #d1d5db', borderRadius: '6px',
                            fontSize: '14px', fontFamily: 'inherit',
                            direction: 'rtl', resize: 'vertical',
                            background: noTechInvoiceSaving ? '#f3f4f6' : 'white',
                          }}
                        />
                      </div>
                    )}

                    {workOrderError && (
                      <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca',
                        color: '#dc2626', padding: '12px', borderRadius: '6px',
                        marginBottom: '16px', fontSize: '13px'
                      }}>
                        {workOrderError}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      {!noTechInvoiceDecisionNo ? (
                        <>
                          <button
                            onClick={confirmNoTechIssueInvoiceAndClose}
                            disabled={noTechInvoiceSaving || workOrderCreating}
                            style={{
                              padding: '12px 28px', background: '#16a34a',
                              color: 'white', border: 'none', borderRadius: '8px',
                              fontSize: '15px', fontWeight: '600',
                              cursor: (noTechInvoiceSaving || workOrderCreating) ? 'wait' : 'pointer',
                              opacity: (noTechInvoiceSaving || workOrderCreating) ? 0.6 : 1,
                            }}
                          >{noTechInvoiceSaving ? 'מנפיק...' : 'כן, להנפיק חשבונית מס'}</button>
                          <button
                            onClick={() => {
                              setWorkOrderError(null);
                              setNoTechInvoiceDecisionNo(true);
                            }}
                            disabled={noTechInvoiceSaving || workOrderCreating}
                            style={{
                              padding: '12px 28px', background: '#f5f5f5',
                              color: '#333', border: '1px solid #ddd',
                              borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                              cursor: (noTechInvoiceSaving || workOrderCreating) ? 'wait' : 'pointer',
                            }}
                          >לא, לא להנפיק</button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={confirmNoTechSkipInvoiceAndClose}
                            disabled={noTechInvoiceSaving || workOrderCreating || !noTechInvoiceReason.trim()}
                            style={{
                              padding: '12px 28px',
                              background: !noTechInvoiceReason.trim() ? '#9ca3af' : '#dc2626',
                              color: 'white', border: 'none', borderRadius: '8px',
                              fontSize: '15px', fontWeight: '600',
                              cursor: (noTechInvoiceSaving || !noTechInvoiceReason.trim())
                                ? 'not-allowed' : 'pointer',
                              opacity: noTechInvoiceSaving ? 0.6 : 1,
                            }}
                          >{noTechInvoiceSaving ? 'שומר...' : 'שמור וסגור הזדמנות'}</button>
                          <button
                            onClick={() => {
                              setWorkOrderError(null);
                              setNoTechInvoiceDecisionNo(false);
                              setNoTechInvoiceReason('');
                            }}
                            disabled={noTechInvoiceSaving}
                            style={{
                              padding: '12px 28px', background: '#f5f5f5',
                              color: '#333', border: '1px solid #ddd',
                              borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                              cursor: noTechInvoiceSaving ? 'wait' : 'pointer',
                            }}
                          >← חזור</button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Step: Serial Product Error (שירות only) */}
                {workTypeModalStep === 'serialProductError' && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: '#fef2f2', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px'
                    }}>⚠️</div>
                    <h3 style={{ marginBottom: '12px', color: '#dc2626', fontSize: '18px' }}>
                      יש מוצר סיריאלי בהזדמנות
                    </h3>
                    <p style={{ color: '#706e6b', fontSize: '14px', marginBottom: '28px', lineHeight: '1.6' }}>
                      נא לבחור סוג עבודה תואם (התקנה / שידרוג)<br/>
                      כדי לבחור מוצר סיריאלי מהמחסן
                    </p>
                    <button
                      onClick={() => { setSelectedWorkType(null); setWorkTypeModalStep('workType'); }}
                      style={{
                        padding: '12px 28px', background: '#1976d2', color: 'white',
                        border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseOver={e => { e.currentTarget.style.background = '#1565c0'; }}
                      onMouseOut={e => { e.currentTarget.style.background = '#1976d2'; }}
                    >
                      ← חזור לבחירת סוג עבודה
                    </button>
                  </div>
                )}

                {/* Step: Asset Selection (שירות) */}
                {workTypeModalStep === 'assetSelect' && (
                  <div>
                    <p style={{ marginBottom: '16px', color: '#706e6b', fontSize: '14px' }}>
                      בחר נכס של הלקוח לפתיחת מקרה שירות:
                    </p>
                    {loadingServiceAssets ? (
                      <div style={{ textAlign: 'center', padding: '30px' }}>⏳ טוען נכסים...</div>
                    ) : serviceAssets.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px', background: '#f5f5f5', borderRadius: '8px' }}>
                        <p style={{ fontSize: '15px', marginBottom: '16px' }}>אין נכסים ללקוח זה</p>
                        <button
                          onClick={() => setShowCreateAssetInWOFlow(true)}
                          style={{ padding: '10px 24px', background: '#1976d2', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
                        >
                          ➕ צור נכס חדש
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                          {serviceAssets.map(asset => (
                            <div
                              key={asset.Id}
                              onClick={() => setSelectedServiceAsset(asset)}
                              style={{
                                padding: '12px 16px',
                                border: selectedServiceAsset?.Id === asset.Id ? '2px solid #1976d2' : '1px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                background: selectedServiceAsset?.Id === asset.Id ? '#e3f2fd' : '#fff',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ fontWeight: 600, fontSize: '14px' }}>
                                {selectedServiceAsset?.Id === asset.Id && '✓ '}{asset.Name || 'ללא שם'}
                              </div>
                              <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                {asset.Product2?.Name || asset.Product2Id || ''}
                                {asset.SerialNumber && ` | מס"ד: ${asset.SerialNumber}`}
                                {asset.Status && ` | ${asset.Status}`}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => setShowCreateAssetInWOFlow(true)}
                          style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                        >
                          ➕ צור נכס חדש
                        </button>
                      </>
                    )}
                    {workOrderError && (
                      <div style={{ marginTop: '16px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#7f1d1d', fontSize: '14px', lineHeight: 1.6 }}>
                        ⚠ לא ניתן לפתוח מקרה: {workOrderError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
                      <button
                        onClick={() => { setSelectedServiceAsset(null); setWorkTypeModalStep('technicianRequired'); }}
                        style={{ padding: '10px 24px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
                      >
                        ← חזור
                      </button>
                      <button
                        onClick={async () => {
                          if (!selectedServiceAsset) return;
                          setWorkOrderError(null);
                          // 2026-10-27 (owner): the case-creation block — strict identification with coordinates.
                          const gate = await checkAccountGate(API_BASE, currentRecord?.AccountId, { requireStrictId: true });
                          if (!gate.ok) { setWorkOrderError(gate.message); return; }
                          setWorkTypeModalStep('caseCreate');
                        }}
                        disabled={!selectedServiceAsset}
                        style={{
                          padding: '10px 24px', background: selectedServiceAsset ? '#1976d2' : '#ccc', color: 'white',
                          border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                          cursor: selectedServiceAsset ? 'pointer' : 'not-allowed'
                        }}
                      >
                        הבא - פתיחת מקרה →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step: Case Creation (שירות) */}
                {workTypeModalStep === 'caseCreate' && (
                  <div>
                    <div style={{ background: '#e3f2fd', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
                      נכס: <strong>{selectedServiceAsset?.Name || ''}</strong>
                      {selectedServiceAsset?.SerialNumber && ` (מס"ד: ${selectedServiceAsset.SerialNumber})`}
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>סוג טיפול (סיבה 1) *</label>
                      <select
                        value={serviceCaseData.Reason_1__c}
                        onChange={(e) => setServiceCaseData(prev => ({ ...prev, Reason_1__c: e.target.value, Reason_2__c: '' }))}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
                      >
                        <option value="">-- בחר סוג טיפול --</option>
                        {SERVICE_REASON_1.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>

                    {serviceCaseData.Reason_1__c && SERVICE_REASON_2[serviceCaseData.Reason_1__c] && (
                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>סיבה משנית (סיבה 2)</label>
                        <select
                          value={serviceCaseData.Reason_2__c}
                          onChange={(e) => setServiceCaseData(prev => ({ ...prev, Reason_2__c: e.target.value }))}
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
                        >
                          <option value="">-- בחר סיבה משנית --</option>
                          {SERVICE_REASON_2[serviceCaseData.Reason_1__c].map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>תאריך החלפת סנן אחרונה *</label>
                      <input
                        type="date"
                        value={serviceCaseData.DateOfLastFilterReplacement}
                        onChange={(e) => setServiceCaseData(prev => ({ ...prev, DateOfLastFilterReplacement: e.target.value }))}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
                      />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>תיאור</label>
                      <textarea
                        value={serviceCaseData.Description}
                        onChange={(e) => setServiceCaseData(prev => ({ ...prev, Description: e.target.value }))}
                        placeholder="תיאור הבעיה או הבקשה..."
                        rows={3}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', resize: 'vertical' }}
                      />
                    </div>

                    {workOrderError && (
                      <div style={{ color: '#dc2626', background: '#fef2f2', padding: '10px', borderRadius: '6px', marginBottom: '14px', fontSize: '13px' }}>
                        {typeof workOrderError === 'string' ? workOrderError : (workOrderError?.message || JSON.stringify(workOrderError))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
                      <button
                        onClick={() => { setWorkOrderError(null); setWorkTypeModalStep('assetSelect'); }}
                        style={{ padding: '10px 24px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
                      >
                        ← חזור
                      </button>
                      <button
                        onClick={() => { setWorkOrderError(null); createServiceCaseAndWorkOrder(); }}
                        disabled={!serviceCaseData.Reason_1__c || workOrderCreating}
                        style={{
                          padding: '10px 24px',
                          background: serviceCaseData.Reason_1__c && !workOrderCreating ? '#2e7d32' : '#ccc',
                          color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                          cursor: serviceCaseData.Reason_1__c && !workOrderCreating ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {workOrderCreating ? 'יוצר...' : '🔧 צור מקרה + הזמנת עבודה'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Product Item Selection */}
                {workTypeModalStep === 'productItem' && (
                  <div>
                    <div style={{
                      background: '#e3f2fd',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#1976d2' }}>סוג עבודה: {selectedWorkType?.Name}</div>
                        <div style={{ fontSize: '13px', color: '#1565c0' }}>
                          {completedSerialSelections.length > 0
                            ? 'בחר פריט נוסף או לחץ ליצור הזמנת עבודה'
                            : 'בחר פריט מוצר מהמחסן הראשי'}
                        </div>
                      </div>
                    </div>

                    {/* Accumulated selections summary */}
                    {completedSerialSelections.length > 0 && (
                      <div style={{
                        background: '#e8f5e9',
                        border: '1px solid #81c784',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontWeight: '600', color: '#2e7d32', marginBottom: '8px', fontSize: '14px' }}>
                          ✓ נבחרו {completedSerialSelections.length} מוצרים סיריאליים:
                        </div>
                        <div style={{ display: 'grid', gap: '6px' }}>
                          {completedSerialSelections.map((s, idx) => (
                            <div key={`${s.productItem?.Id || idx}-${s.serial?.Id || idx}`} style={{
                              fontSize: '13px',
                              color: '#1b5e20',
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '8px',
                              background: '#fff',
                              padding: '6px 10px',
                              borderRadius: '4px'
                            }}>
                              <span>{s.productItem?.Product2Name || s.productItem?.ProductItemNumber || 'מוצר'}</span>
                              <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                {s.serial?.SerialNumber || s.serial?.Name || s.serial?.Id || ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(() => {
                      // Filter out ProductItems whose serial has already been picked
                      const completedIds = new Set(
                        completedSerialSelections.map(s => s.productItem?.Id).filter(Boolean)
                      );
                      const remainingItems = (availableProductItems || []).filter(pi => !completedIds.has(pi.Id));

                      if (remainingItems.length > 0) {
                        return (
                          <div style={{ display: 'grid', gap: '12px', maxHeight: '350px', overflowY: 'auto' }}>
                            {remainingItems.map(pi => (
                              <button
                                key={pi.Id}
                                onClick={() => handleProductItemSelect(pi)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '16px',
                                  padding: '16px 20px',
                                  background: '#fff',
                                  border: '1px solid #e5e5e5',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  textAlign: 'right',
                                  transition: 'all 0.2s',
                                  width: '100%'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = '#f5f5f5';
                                  e.currentTarget.style.borderColor = '#1976d2';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = '#fff';
                                  e.currentTarget.style.borderColor = '#e5e5e5';
                                }}
                              >
                                <span style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '18px'
                                }}>
                                  P
                                </span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', fontSize: '15px' }}>
                                    {pi.Product2Name}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#706e6b' }}>
                                    מזהה: {pi.ProductItemNumber} | כמות: {pi.QuantityOnHand || 0} | <span style={{ color: pi.availableSerializedCount > 0 ? '#4caf50' : '#f44336', fontWeight: '600' }}>פנויים: {pi.availableSerializedCount || 0}</span>
                                  </div>
                                </div>
                                <span style={{ fontSize: '20px', color: '#bbb' }}>←</span>
                              </button>
                            ))}
                          </div>
                        );
                      } else if (completedSerialSelections.length === 0) {
                        return (
                          <div style={{ textAlign: 'center', padding: '40px', color: '#706e6b' }}>
                            <span style={{ fontSize: '48px', color: '#bbb' }}>-</span>
                            <p style={{ marginTop: '16px' }}>לא נמצאו פריטי מוצר תואמים במחסן הראשי</p>
                          </div>
                        );
                      }
                      return (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#2e7d32', fontSize: '14px' }}>
                          כל המוצרים הסיריאליים הדרושים נבחרו. לחץ "צור הזמנת עבודה" כדי להמשיך.
                        </div>
                      );
                    })()}

                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                      <button
                        onClick={() => {
                          setWorkTypeModalStep('workType');
                          setSelectedWorkType(null);
                          setCompletedSerialSelections([]);
                          setWorkOrderError(null);
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#f4f6f9',
                          border: '1px solid #d8dde6',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        ← חזור לבחירת סוג עבודה
                      </button>
                      {completedSerialSelections.length > 0 && (
                        <button
                          onClick={handleCreateWorkOrderFromAccumulated}
                          disabled={workOrderCreating}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            background: workOrderCreating
                              ? '#e0e0e0'
                              : 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: workOrderCreating ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '15px'
                          }}
                        >
                          ✓ צור הזמנת עבודה ({completedSerialSelections.length})
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Serialized Product Selection */}
                {workTypeModalStep === 'serialized' && (
                  <div>
                    <div style={{
                      background: '#e8f5e9',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#2e7d32' }}>
                          מוצר: {selectedProductItem?.Product2Name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#388e3c' }}>בחר מספר סיריאלי פנוי</div>
                      </div>
                    </div>

                    {Object.entries(availableSerializedProducts).map(([productId, serials]) => (
                      <div key={productId} style={{ marginBottom: '20px' }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '12px',
                          color: '#1a1a1a',
                          borderBottom: '2px solid #1976d2',
                          paddingBottom: '8px'
                        }}>
                          מוצרים סיריאליים זמינים ({serials.length})
                        </h4>
                        <div style={{
                          display: 'grid',
                          gap: '8px',
                          maxHeight: '250px',
                          overflowY: 'auto'
                        }}>
                          {serials.map(sp => (
                            <label
                              key={sp.Id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 16px',
                                background: selectedSerializedProducts[productId]?.Id === sp.Id
                                  ? '#e3f2fd'
                                  : '#fff',
                                border: selectedSerializedProducts[productId]?.Id === sp.Id
                                  ? '2px solid #1976d2'
                                  : '1px solid #e5e5e5',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <input
                                type="radio"
                                name={`serial-${productId}`}
                                checked={selectedSerializedProducts[productId]?.Id === sp.Id}
                                onChange={() => handleSerializedProductSelect(productId, sp)}
                                style={{ width: '18px', height: '18px' }}
                              />
                              <span style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: '#ff9800',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px'
                              }}>
                                #
                              </span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500' }}>{sp.Name || sp.SerialNumber || 'ללא שם'}</div>
                                <div style={{ fontSize: '12px', color: '#706e6b' }}>
                                  סיריאלי: {sp.SerialNumber || 'N/A'}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                      <button
                        onClick={() => {
                          setWorkTypeModalStep('productItem');
                          setSelectedProductItem(null);
                          setAvailableSerializedProducts({});
                          setSelectedSerializedProducts({});
                          setWorkOrderError(null);
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#f4f6f9',
                          border: '1px solid #d8dde6',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        ← חזור
                      </button>
                      {(() => {
                        // Compute whether there are more productItems that will still need a serial
                        // after this confirmation (to adjust button label)
                        const completedIds = new Set(
                          completedSerialSelections.map(s => s.productItem?.Id).filter(Boolean)
                        );
                        if (selectedProductItem?.Id) completedIds.add(selectedProductItem.Id);
                        const remainingAfter = (availableProductItems || [])
                          .filter(pi => !completedIds.has(pi.Id)).length;
                        const hasMore = remainingAfter > 0;
                        const hasSelection = Object.keys(selectedSerializedProducts).length > 0;
                        return (
                          <button
                            onClick={handleConfirmSerializedProducts}
                            disabled={!hasSelection}
                            style={{
                              flex: 1,
                              padding: '12px 24px',
                              background: hasSelection
                                ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)'
                                : '#e0e0e0',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: hasSelection ? 'pointer' : 'not-allowed',
                              fontWeight: '600',
                              fontSize: '15px'
                            }}
                          >
                            {hasMore ? '✓ אשר ועבור למוצר הבא' : '✓ אשר ויצור הזמנת עבודה'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Creating Step — owner-spec 2026-06-09: replaced the
                    pulsing W avatar with a live progress bar +
                    state-driven checklist. Each row reflects whether
                    that phase is pending (○), running (⏳), done (✓),
                    or errored (✗). The progress bar fills proportionally
                    to phases done out of total. */}
                {workTypeModalStep === 'creating' && (() => {
                  const phases = [
                    { key: 'workOrder', label: 'יוצר הזמנת עבודה' },
                    { key: 'products',  label: 'יוצר מוצרים נדרשים' },
                    { key: 'sa',        label: 'יוצר פעילות שירות' },
                  ];
                  const totals = phases.length;
                  const doneCount = phases.filter(p => workOrderProgress[p.key] === 'done').length;
                  const pct = Math.round((doneCount / totals) * 100);
                  const renderIcon = (state) => {
                    if (state === 'done')    return <span style={{ color: '#4caf50', fontWeight: 700 }}>✓</span>;
                    if (state === 'error')   return <span style={{ color: '#c23934', fontWeight: 700 }}>✗</span>;
                    if (state === 'running') return <span style={{ color: '#ff9800', animation: 'pulse 1s infinite', display: 'inline-block', minWidth: 14 }}>⏳</span>;
                    return <span style={{ color: '#bbb' }}>○</span>;
                  };
                  return (
                    <div style={{ padding: '36px 32px', direction: 'rtl' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', textAlign: 'center' }}>
                        יוצר רשומות…
                      </h3>
                      <p style={{ color: '#706e6b', margin: '0 0 20px', textAlign: 'center', fontSize: 13 }}>
                        הזמנת עבודה, מוצרים נדרשים ופעילות שירות
                      </p>
                      {/* Progress bar */}
                      <div style={{
                        height: 10,
                        background: '#e5e7eb',
                        borderRadius: 999,
                        overflow: 'hidden',
                        marginBottom: 6,
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: 'linear-gradient(90deg, #0176d3 0%, #4caf50 100%)',
                          transition: 'width 0.25s ease',
                        }} />
                      </div>
                      <div style={{ textAlign: 'left', fontSize: 12, color: '#706e6b', marginBottom: 18 }}>
                        {doneCount} / {totals} ({pct}%)
                      </div>
                      {/* Checklist */}
                      <div style={{
                        padding: '14px 16px',
                        background: '#f8fafc',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}>
                        {phases.map(ph => (
                          <div key={ph.key} style={{
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 14,
                            opacity: workOrderProgress[ph.key] === 'pending' ? 0.6 : 1,
                          }}>
                            {renderIcon(workOrderProgress[ph.key])}
                            <span>{ph.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Success Step */}
                {workTypeModalStep === 'success' && workOrderResult && (
                  <div style={{ textAlign: 'center', padding: '30px' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      margin: '0 auto 20px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)'
                    }}>
                      <span style={{ fontSize: '40px' }}>✓</span>
                    </div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#2e7d32' }}>
                      הזמנת עבודה ופעילות שירות נוצרו בהצלחה!
                    </h3>
                    <p style={{ color: '#706e6b', margin: '0 0 24px', fontSize: '14px' }}>
                      כל הרשומות נוצרו בהצלחה
                    </p>

                    {/* Products Required Section */}
                    {workOrderResult.productsRequired?.length > 0 && (
                      <div style={{
                        background: '#fff8e1',
                        border: '2px solid #ffc107',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px',
                        textAlign: 'right'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '12px',
                          borderBottom: '1px solid #ffe082',
                          paddingBottom: '10px'
                        }}>
                          <div style={{ fontWeight: '700', fontSize: '16px', color: '#f57c00' }}>
                            מוצרים נדרשים לפגישה
                          </div>
                        </div>
                        <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {workOrderResult.productsRequired.map((product, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 12px',
                              background: idx % 2 === 0 ? '#fffde7' : '#fff8e1',
                              borderRadius: '6px',
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontWeight: '600', color: '#e65100' }}>
                                כמות: {product.quantity}
                              </span>
                              <span style={{ color: '#5d4037' }}>{product.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Navigation Buttons */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                      {/* Work Order Button */}
                      <button
                        onClick={() => {
                          const woId = workOrderResult?.workOrderId;
                          console.log('[WorkOrder] Navigating to WorkOrder:', woId);
                          if (woId) {
                            window.location.href = `/object/WorkOrder/${woId}`;
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '14px 20px',
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '14px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                        }}
                      >
                        <span>מעבר להזמנת עבודה</span>
                        <span style={{ fontSize: '12px', opacity: 0.9 }}>#{workOrderResult.workOrderNumber}</span>
                      </button>

                      {/* Service Appointment Button */}
                      {workOrderResult.serviceAppointmentId && (
                        <button
                          onClick={() => {
                            const saId = workOrderResult?.serviceAppointmentId;
                            console.log('[WorkOrder] Navigating to ServiceAppointment:', saId);
                            if (saId) {
                              window.location.href = `/object/ServiceAppointment/${saId}`;
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '14px 20px',
                            background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                          }}
                        >
                          <span>מעבר לפעילות שירות</span>
                          <span style={{ fontSize: '12px', opacity: 0.9 }}>#{workOrderResult.serviceAppointmentNumber}</span>
                        </button>
                      )}
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={() => {
                        setShowWorkTypeModal(false);
                        setWorkOrderResult(null);
                      }}
                      style={{
                        padding: '10px 32px',
                        background: '#f4f6f9',
                        border: '1px solid #d8dde6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      סגור
                    </button>
                  </div>
                )}
              </div>

              {/* Footer */}
              <footer style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f8f9fa'
              }}>
                <button
                  onClick={handleCancelWorkOrderFlow}
                  disabled={workOrderCreating}
                  style={{
                    padding: '10px 24px',
                    background: '#fff',
                    border: '1px solid #d8dde6',
                    borderRadius: '4px',
                    cursor: workOrderCreating ? 'not-allowed' : 'pointer',
                    opacity: workOrderCreating ? 0.5 : 1
                  }}
                >
                  ביטול
                </button>
              </footer>
            </div>
          </div>
        </>
      )}

      {/* Line Item Action Menu Dropdown */}
      {lineItemActionMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
            onClick={() => setLineItemActionMenu(null)}
          />
          {/* Dropdown menu */}
          <div
            style={{
              position: 'fixed',
              left: lineItemActionMenu.x,
              top: lineItemActionMenu.y,
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              zIndex: 9999,
              minWidth: '120px',
              overflow: 'hidden'
            }}
          >
            {/* Edit button */}
            <button
              onClick={async () => {
                const record = lineItemActionMenu.record;
                const menuObjName = lineItemActionMenu.objectName || 'OpportunityLineItem';
                if (menuObjName === 'OpportunityLineItem') {
                  // Inline edit for OLI
                  setEditingLineItem({
                    ...record,
                    editQuantity: record.Quantity || 1,
                    editUnitPrice: record.UnitPrice || record.TotalPrice || 0,
                    selectedProductId: record.Product2Id || null
                  });
                  setShowLineItemModal(true);
                  setLineItemPriceError('');
                  loadLineItemPricePermission();
                  await loadProductsForLineItemEdit();
                } else {
                  // Open record in new tab for all other objects
                  window.open(`/object/${menuObjName}/${record.Id}`, '_blank');
                }
                setLineItemActionMenu(null);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'white',
                textAlign: 'right',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.target.style.background = 'white'}
            >
              ✏️ עריכה
            </button>
            {/* Delete button — visibility gate per object type +
                profile. Owner-spec evolution:
                  - #804 (2026-05-31): hidden for non-admin/non-accounting/
                    non-institutional on Payment/Case/etc. related records.
                  - #806 (2026-06-08): OpportunityLineItem stays visible
                    to all (no money/audit blast radius).
                  - 2026-06-10: ProductRequired visible to everyone EXCEPT
                    מוקד מכירה (sales_center) and מוקד שירות (service_center).
                    Reps in those profiles cannot accidentally remove a PR
                    line; the button just isn't in the dropdown for them. */}
            {(() => {
              const menuObjName = lineItemActionMenu.objectName || 'OpportunityLineItem';
              const isOLI = menuObjName === 'OpportunityLineItem';
              const isProductRequired = menuObjName === 'ProductRequired';
              const isCenterRep =
                permHasProfile(currentUser, 'sales_center') ||
                permHasProfile(currentUser, 'service_center');
              if (isOLI) return true;
              if (isProductRequired) return !isCenterRep;
              return isAdminUser || isAccountingUserForDelete || isInstitutionalUser;
            })() && (
              <button
                onClick={async () => {
                  const record = lineItemActionMenu.record;
                  const menuObjName = lineItemActionMenu.objectName || 'OpportunityLineItem';
                  // Defense-in-depth gate. The button is hidden for
                  // unauthorized users above, but if a click somehow lands
                  // here (e.g. menu state desync) we still refuse.
                  const isOLI = menuObjName === 'OpportunityLineItem';
                  const isProductRequired = menuObjName === 'ProductRequired';
                  const isCenterRep =
                    permHasProfile(currentUser, 'sales_center') ||
                    permHasProfile(currentUser, 'service_center');
                  if (isProductRequired && isCenterRep) {
                    setLineItemActionMenu(null);
                    return;
                  }
                  if (!isOLI && !isProductRequired && !isAdminUser && !isAccountingUserForDelete && !isInstitutionalUser) {
                    setLineItemActionMenu(null);
                    return;
                  }
                  if (!window.confirm(`האם למחוק רשומה זו?`)) {
                    setLineItemActionMenu(null);
                    return;
                  }
                  try {
                    if (menuObjName === 'OpportunityLineItem') {
                      handleDeleteLineItem(record);
                    } else {
                      const res = await fetch(`${API_BASE}/api/objects/${menuObjName}/records/${record.Id}`,
                        { method: 'DELETE', headers: deleteHeaders('MANUAL_DELETE') });
                      if (res.ok) {
                        setRelatedRecords(prev => ({
                          ...prev,
                          [menuObjName]: (prev[menuObjName] || []).filter(r => r.Id !== record.Id)
                        }));
                        // 2026-06-17 owner-spec: keep the badge in sync + bust the
                        // module-level cache so F5/navigate-back doesn't resurrect
                        // the deleted row (see _invalidateRelatedCacheOnDelete).
                        setRelatedCounts(prev => ({
                          ...prev,
                          [menuObjName]: Math.max(0, (prev[menuObjName] ?? (relatedRecords?.[menuObjName]?.length || 1)) - 1)
                        }));
                        _invalidateRelatedCacheOnDelete(object.api_name, currentRecord?.Id, menuObjName, record.Id);
                        // Recalculate opportunity amounts if Payment deleted
                        if (menuObjName === 'Payment__c') {
                          await recalculateOpportunityAmounts();
                        }
                      } else {
                        const errData = await res.json().catch(() => ({}));
                        alert(recordAccessErrorMessage(errData.detail) || 'שגיאה במחיקת הרשומה');
                      }
                    }
                  } catch (err) {
                    alert('שגיאה במחיקה: ' + err.message);
                  }
                  setLineItemActionMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  borderTop: '1px solid #eee',
                  background: 'white',
                  textAlign: 'right',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#dc3545',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#fff5f5'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                🗑️ מחיקה
              </button>
            )}
          </div>
        </>
      )}

      {/* Edit Line Item Modal */}
      {showLineItemModal && editingLineItem && (
        <>
          {/* Modal Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 10000
            }}
            onClick={() => {
              setShowLineItemModal(false);
              setEditingLineItem(null);
            }}
          />
          {/* Modal Content */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              zIndex: 10001,
              width: '400px',
              maxWidth: '90vw',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e5e5e5',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, color: 'white', fontSize: '16px' }}>
                ✏️ עריכת פריט הזדמנות
              </h3>
              <button
                onClick={() => {
                  setShowLineItemModal(false);
                  setEditingLineItem(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Product Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  מוצר
                </label>
                {loadingLineItemProducts ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                    טוען מוצרים...
                  </div>
                ) : lineItemProducts.length > 0 ? (
                  <select
                    value={editingLineItem.selectedProductId || ''}
                    onChange={(e) => {
                      const selectedProduct = lineItemProducts.find(p => p.Id === e.target.value);
                      setEditingLineItem(prev => ({
                        ...prev,
                        selectedProductId: e.target.value,
                        editUnitPrice: selectedProduct?.unitPrice || prev.editUnitPrice
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white'
                    }}
                  >
                    <option value="">בחר מוצר...</option>
                    {lineItemProducts.map(product => (
                      <option key={product.Id} value={product.Id}>
                        {product.Name} {product.ProductCode ? `(${product.ProductCode})` : ''} - ₪{product.unitPrice?.toLocaleString('he-IL')}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div
                    style={{
                      padding: '10px 12px',
                      background: '#f5f5f5',
                      borderRadius: '6px',
                      color: '#666'
                    }}
                  >
                    {editingLineItem.Name || editingLineItem.Product2?.Name || editingLineItem._display_name || 'מוצר'}
                  </div>
                )}
              </div>
              {/* Quantity */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  כמות
                </label>
                <input
                  type="number"
                  min="1"
                  value={editingLineItem.editQuantity}
                  onChange={(e) => setEditingLineItem(prev => ({
                    ...prev,
                    editQuantity: parseFloat(e.target.value) || 1
                  }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    direction: 'ltr',
                    textAlign: 'left'
                  }}
                />
              </div>
              {/* Unit Price — 2026-06-01 owner-spec price-range gate */}
              {(() => {
                const productId = editingLineItem.selectedProductId
                  || editingLineItem.Product2Id
                  || editingLineItem.Product2?.Id
                  || '';
                const perm = lineItemPricePermission;
                // PR after #804: institutional users get free-edit too.
                // Owner-spec 2026-06-16: accounting joins free-edit set.
                // 2026-06-16: ALSO derive isFree from currentUser to avoid
                // the race window where the modal renders before the
                // permission fetch resolves. Owner-flagged: a real admin
                // saw the locked state + error before the fetch landed.
                const currentUserIsFree = (
                  currentUser?.is_admin === true ||
                  ['Admin', 'accounting', 'הנהלת חשבונות', 'institutional', 'מוסדי'].includes(currentUser?.profile)
                );
                const isFree = currentUserIsFree || perm.is_admin || perm.is_technician || perm.is_institutional || perm.is_accounting || perm.is_price_unrestricted || perm.is_validation_exempt;
                const r = !isFree && productId ? perm.product_ranges?.[productId] : null;
                const isLocked = !isFree && !r;
                const range = isFree ? null : r;
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                      מחיר ליחידה (₪)
                    </label>
                    <input
                      type="number"
                      min={range ? range.min : 0}
                      max={range && range.max ? range.max : undefined}
                      step="0.01"
                      value={editingLineItem.editUnitPrice}
                      readOnly={isLocked}
                      onClick={() => {
                        if (isLocked) {
                          setLineItemPriceError('אין לך הרשאה לשנות את המחיר. צור קשר עם מנהל המערכת.');
                        }
                      }}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        if (!isFree && range) {
                          const min = Number(range.min);
                          const max = Number(range.max);
                          if (v < min || v > max) {
                            setLineItemPriceError('המחיר חייב להיות בטווח ₪' + min + ' – ₪' + max);
                            // Still update the value so the user sees what they typed;
                            // save button will block if still out of range.
                          } else {
                            setLineItemPriceError('');
                          }
                        } else {
                          setLineItemPriceError('');
                        }
                        setEditingLineItem(prev => ({ ...prev, editUnitPrice: v }));
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid ' + (lineItemPriceError ? '#c23934' : '#ddd'),
                        borderRadius: '6px',
                        fontSize: '14px',
                        direction: 'ltr',
                        textAlign: 'left',
                        background: isLocked ? '#f0f0f0' : '#fff',
                        cursor: isLocked ? 'not-allowed' : 'text',
                      }}
                    />
                    {range && !isFree && !isLocked && (
                      <div style={{ fontSize: 11, color: '#706e6b', marginTop: 4, direction: 'rtl' }}>
                        {'טווח: ₪' + range.min + ' – ₪' + range.max}
                      </div>
                    )}
                    {approvedUnitPrice(discountSummary, editingLineItem.Id) !== null && (
                      <div style={{ fontSize: 11, color: '#1d6b3f', marginTop: 4, direction: 'rtl' }}>
                        {'💲 מחיר מאושר בהנחת מנהל: ₪' + approvedUnitPrice(discountSummary, editingLineItem.Id)}
                      </div>
                    )}
                    {lineItemPriceError && (
                      <div style={{ fontSize: 12, color: '#c23934', marginTop: 6, fontWeight: 600, direction: 'rtl' }}>
                        {lineItemPriceError}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Total */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333' }}>
                  סה"כ
                </label>
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#e8f5e9',
                    borderRadius: '6px',
                    color: '#2e7d32',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  ₪{((editingLineItem.editQuantity || 1) * (editingLineItem.editUnitPrice || 0)).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            {/* Footer */}
            <div
              style={{
                padding: '16px 20px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '10px'
              }}
            >
              <button
                onClick={() => handleSaveLineItem()}
                disabled={lineItemSaving}
                style={{
                  padding: '10px 24px',
                  background: lineItemSaving ? '#999' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: lineItemSaving ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {lineItemSaving ? 'שומר...' : 'שמור שינויים'}
              </button>
              <button
                onClick={() => {
                  setShowLineItemModal(false);
                  setEditingLineItem(null);
                  setLineItemProducts([]);
                }}
                disabled={lineItemSaving}
                style={{
                  padding: '10px 24px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: lineItemSaving ? 'not-allowed' : 'pointer'
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        </>
      )}

      {/* Related-record hover card — owner-spec 2026-05-25. Shows the
          most useful identifying fields without forcing a navigation:
          Account, Owner, CreatedBy, CreatedDate. Positioned at the
          mouse coordinates captured on hover-enter; clamped to the
          viewport via translate so it never spills off-screen. */}
      {relatedHover && (() => {
        const r = relatedHover.rec || {};
        const fmtDate = (s) => {
          if (!s) return '—';
          let ds = s;
          if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ds)) ds = ds.replace(' ', 'T') + 'Z';
          const d = new Date(ds);
          if (Number.isNaN(d.getTime())) return s;
          return d.toLocaleString('he-IL', {
            timeZone: 'Asia/Jerusalem',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          });
        };
        const accountName =
          r.AccountId__Name || r.Account__Name || r.AccountName || '';
        const ownerName =
          r.OwnerId__Name || r.Owner__Name || r.OwnerName || '';
        const createdBy = r.CreatedByName || r.CreatedById__Name || '';
        const created = fmtDate(r.CreatedDate);
        // Position the card to the right of the cursor when there's
        // room, otherwise flip to the left. Vertical clamp via max.
        const CARD_W = 320;
        const PAD = 12;
        const flipLeft = relatedHover.x + CARD_W + PAD > window.innerWidth;
        const left = flipLeft
          ? Math.max(8, relatedHover.x - CARD_W - PAD)
          : relatedHover.x + PAD;
        const top = Math.min(
          window.innerHeight - 220,
          Math.max(8, relatedHover.y - 20),
        );
        return (
          <div
            onMouseEnter={() => { if (_relatedHoverTimerRef.current) clearTimeout(_relatedHoverTimerRef.current); }}
            onMouseLeave={() => setRelatedHover(null)}
            style={{
              position: 'fixed', top, left, width: `${CARD_W}px`,
              background: '#fff',
              border: '1px solid #d8dde6',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              padding: '12px 14px',
              fontSize: '13px',
              zIndex: 99999,
              direction: 'rtl',
              fontFamily: '"Assistant", "Heebo", system-ui, sans-serif',
              pointerEvents: 'auto',
            }}
          >
            <div style={{
              fontWeight: 700,
              fontSize: '14px',
              color: '#16325c',
              borderBottom: '1px solid #f3f3f7',
              paddingBottom: '8px',
              marginBottom: '10px',
            }}>
              {r.Name || r.Subject || r.CaseNumber || r.AppointmentNumber || r.WorkOrderNumber || relatedHover.objectName}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: '6px', columnGap: '10px' }}>
              {accountName && (<>
                <div style={{ color: '#706e6b' }}>שם חשבון</div>
                <div style={{ color: '#181818' }}>{accountName}</div>
              </>)}
              {ownerName && (<>
                <div style={{ color: '#706e6b' }}>בעלים</div>
                <div style={{ color: '#181818' }}>{ownerName}</div>
              </>)}
              {createdBy && (<>
                <div style={{ color: '#706e6b' }}>נוצר על ידי</div>
                <div style={{ color: '#181818' }}>{createdBy}</div>
              </>)}
              <div style={{ color: '#706e6b' }}>תאריך יצירה</div>
              <div style={{ color: '#181818' }}>{created}</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default App;

